import React, { useState, useEffect, useMemo, useRef } from "react";
import * as XLSX from "xlsx";

// Mobile/iPad-Erkennung: globale Helper
const isMobileDevice = (()=>{try{return /iPad|iPhone|iPod|Android/i.test(navigator.userAgent)||window.innerWidth<=900;}catch(e){return false;}})();

// ─── CONSTANTS & CONFIG ───
const STORAGE_KEYS = {
  users: "crm_users",
  leads: "crm_leads",
  calls: "crm_calls",
  appointments: "crm_appointments",
  finances: "crm_finances",
  session: "crm_session",
  checklist: "crm_checklist",
  cutter_projects: "crm_cutter_projects",
  cutter_invoices: "crm_cutter_invoices",
  cutter_goals: "crm_cutter_goals",
  service_catalog: "crm_service_catalog",
};

// Lead Pipeline (Cold Calling)
const LEAD_STATUSES = [
  { id: "new", label: "Neu", color: "#3B82F6", bg: "#1e3a5f" },
  { id: "no_answer", label: "Keinen erreicht", color: "#94A3B8", bg: "#2d3748" },
  { id: "email_sent", label: "E-Mail", color: "#F59E0B", bg: "#4a3520" },
  { id: "follow_up", label: "Nachfassen", color: "#F97316", bg: "#4a2d1a" },
  { id: "appointment", label: "Termin vereinbart", color: "#8B5CF6", bg: "#3b2d5e" },
  { id: "invalid", label: "Falsche Nr./Website", color: "#6B7280", bg: "#374151" },
  { id: "lost", label: "Verloren", color: "#EF4444", bg: "#4a1c1c" },
];

// Customer statuses (after conversion)
const CUSTOMER_STATUSES = [
  { id: "offer_sent", label: "Angebot abgegeben", color: "#06B6D4", bg: "#1a3a40" },
  { id: "closed", label: "Verkauf abgeschlossen", color: "#10B981", bg: "#1a3d2e" },
  { id: "active_client", label: "Laufender Kunde", color: "#22C55E", bg: "#14532d" },
];

const ALL_STATUSES = [...LEAD_STATUSES, ...CUSTOMER_STATUSES];

// ─── Kunden-Dienstleistungen (Module im Kundenportal) ───
// Jede angekreuzte Dienstleistung schaltet im Kunden-Dashboard einen eigenen Bereich frei.
// Erweiterbar: einfach einen weiteren Eintrag ergänzen.
const CLIENT_SERVICES = [
  {id:"meta_ads", label:"Meta Ads", icon:"target", desc:"Ad Creatives, Kampagnen-Links und Ergebnisse"},
  {id:"social_media", label:"Social Media Marketing", icon:"chart", desc:"Content, Redaktionspläne und Tabellen"},
  {id:"landingpage", label:"Landingpage", icon:"edit", desc:"Test- und Live-Link, Projektstand"},
  {id:"website", label:"Webseite", icon:"edit", desc:"Test- und Live-Link, Projektstand"},
];
const serviceById = (id) => CLIENT_SERVICES.find(s=>s.id===id);

// ─── Schema pro Dienstleistung: welche Eintrags-Typen, welche Felder, welche Status ───
// Jeder Typ: {id, label, fields:[...], statuses:[{id,label,color}]}
// Mögliche Felder: title, url, url2, status, notes (jeweils mit eigenem Label)
// ─── Spalten der Landingpage-Lead-Tabelle ───
// type: text | date | number | yesno | select ; key = Feldname
const YESNO = [{v:"",l:"—"},{v:"ja",l:"Ja"},{v:"nein",l:"Nein"}];
const LANDINGPAGE_LEAD_COLUMNS = [
  {key:"receivedAt", label:"Datum", type:"datetime", readOnly:true, w:120},
  {key:"source", label:"Anfrage über", type:"select", options:[{v:"",l:"—"},{v:"Instagram",l:"Instagram"},{v:"Facebook",l:"Facebook"}], w:120},
  {key:"kw", label:"KW", type:"number", w:60},
  {key:"monthLabel", label:"Monat", type:"text", w:90},
  {key:"status", label:"Status", type:"text", w:160},
  {key:"lastName", label:"Nachname", type:"text", w:130},
  {key:"firstName", label:"Vorname", type:"text", w:130},
  {key:"salutation", label:"Anrede", type:"text", w:90},
  {key:"phone", label:"Telefon", type:"text", w:140},
  {key:"email", label:"E-Mail", type:"text", w:190},
  {key:"callAttempts", label:"Wählversuche", type:"number", w:90},
  {key:"dialed", label:"Angewählt", type:"text", w:100},
  {key:"validNumber", label:"Valide Nummer", type:"yesno", w:110},
  {key:"reached", label:"Erreicht", type:"yesno", w:90},
  {key:"isOwner", label:"Eigentümer", type:"yesno", w:100},
  {key:"appointmentSet", label:"Terminierung", type:"yesno", w:100},
  {key:"followupUseful", label:"Follow Up sinnvoll", type:"yesno", w:120},
  {key:"followupDate", label:"Follow Up am", type:"date", w:130},
  {key:"firstAppointment", label:"Ersttermin am", type:"date", w:130},
  {key:"onsite", label:"Vor Ort", type:"select", options:[{v:"",l:"—"},{v:"Show",l:"Show"},{v:"No Show",l:"No Show"}], w:100},
  {key:"valuation", label:"Bewertung", type:"yesno", w:90},
  {key:"contract", label:"Auftrag", type:"yesno", w:90},
  {key:"offerPrice", label:"Angebotspreis", type:"text", w:120},
  {key:"location", label:"PLZ/Ort", type:"text", w:130},
  {key:"propertyType", label:"Immobilienart", type:"text", w:140},
  {key:"wantsToSell", label:"Will verkaufen", type:"yesno", w:110},
  {key:"notes", label:"Notiz", type:"text", w:180},
];

const SERVICE_SCHEMAS = {
  meta_ads: {
    sectionLabel: "Meta Ads",
    types: [
      { id:"creative", label:"Ad Creative", addLabel:"Creative hinzufügen",
        fields:{ title:"Bezeichnung", url:"Link zur Datei / zum Creative", status:true, notes:"Notiz" },
        statuses:[{id:"aktiv",label:"Aktiv",color:"success"},{id:"pausiert",label:"Pausiert",color:"warning"},{id:"in_pruefung",label:"In Prüfung",color:"info"},{id:"abgelehnt",label:"Abgelehnt",color:"danger"}] },
      { id:"campaign", label:"Kampagne", addLabel:"Kampagne hinzufügen",
        fields:{ title:"Kampagnenname", url:"Link (Ads Manager / Bericht)", status:true, notes:"Notiz" },
        statuses:[{id:"aktiv",label:"Aktiv",color:"success"},{id:"pausiert",label:"Pausiert",color:"warning"},{id:"beendet",label:"Beendet",color:"muted"}] },
    ],
  },
  social_media: {
    sectionLabel: "Social Media Marketing",
    types: [
      { id:"plan", label:"Redaktionsplan / Tabelle", addLabel:"Tabelle hinzufügen",
        fields:{ title:"Bezeichnung", url:"Link zur Tabelle", notes:"Notiz" } },
      { id:"content", label:"Content", addLabel:"Content hinzufügen",
        fields:{ title:"Bezeichnung", url:"Link", status:true, notes:"Notiz" },
        statuses:[{id:"idee",label:"Idee",color:"muted"},{id:"in_arbeit",label:"In Arbeit",color:"info"},{id:"freigabe",label:"Zur Freigabe",color:"warning"},{id:"geplant",label:"Geplant",color:"accent"},{id:"veroeffentlicht",label:"Veröffentlicht",color:"success"}] },
    ],
  },
  landingpage: {
    sectionLabel: "Landingpage",
    types: [
      { id:"page", label:"Landingpage", addLabel:"Landingpage hinzufügen",
        fields:{ title:"Bezeichnung", url:"Test-Link", url2:"Live-Link", status:true, notes:"Notiz" },
        statuses:[{id:"in_arbeit",label:"In Arbeit",color:"info"},{id:"review",label:"Zur Abnahme",color:"warning"},{id:"live",label:"Live",color:"success"}] },
    ],
  },
  website: {
    sectionLabel: "Webseite",
    types: [
      { id:"site", label:"Webseite", addLabel:"Webseite hinzufügen",
        fields:{ title:"Bezeichnung", url:"Test-Link", url2:"Live-Link", status:true, notes:"Notiz" },
        statuses:[{id:"in_arbeit",label:"In Arbeit",color:"info"},{id:"review",label:"Zur Abnahme",color:"warning"},{id:"live",label:"Live",color:"success"}] },
    ],
  },
};


// ─── Calendly Integration ───
// Hier deinen Calendly Discovery Call Link eintragen
const CALENDLY_URL = "https://calendly.com/kontakt-rf-media/lass-uns-30-minuten-ueber-dein-marketing-sprechen";

// ─── API + DATA LAYER ───
const API_BASE = '/api';

// Auth-Token (wird beim Login gesetzt, bei jedem Request mitgeschickt)
const authToken = {
  get: () => { try { return localStorage.getItem('crm_token') || ''; } catch { return ''; } },
  set: (t) => { try { localStorage.setItem('crm_token', t || ''); } catch {} },
  clear: () => { try { localStorage.removeItem('crm_token'); } catch {} },
};

// Baut die Header inkl. Token
const authHeaders = (extra = {}) => {
  const t = authToken.get();
  return t ? { ...extra, 'Authorization': `Bearer ${t}` } : extra;
};

// Bei abgelaufener/ungültiger Session sauber abmelden und zum Login
let _onAuthLost = null;
const handleAuthLost = () => {
  authToken.clear();
  try { localStorage.removeItem('crm_session'); } catch {}
  if (_onAuthLost) _onAuthLost();
  else window.location.reload();
};

const _parseRes = async (res) => {
  // Antwort erst als Text lesen, dann JSON versuchen. So werfen wir bei
  // nicht-JSON-Antworten (z.B. PHP-Fehlern) keine kryptische Browser-Meldung,
  // sondern geben den echten Serverinhalt im Klartext zurück.
  const text = await res.text();
  try { return JSON.parse(text); }
  catch (_) {
    const snippet = (text || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 300);
    return { error: snippet ? ('Serverfehler: ' + snippet) : ('Serverfehler (Status ' + res.status + ')') };
  }
};
// fetch mit hartem Zeitlimit, damit eine Anfrage nie endlos hängt
const _fetchT = (url, opts = {}, ms = 25000) => {
  const c = new AbortController();
  const t = setTimeout(() => c.abort(), ms);
  return fetch(url, { ...opts, signal: c.signal }).finally(() => clearTimeout(t));
};
const _netErr = (e) => {
  if (e && e.name === 'AbortError') return { error: 'Zeitüberschreitung: der Server hat nicht geantwortet. Meist ist die Verbindung zum Mailserver blockiert.' };
  return { error: 'Netzwerkfehler: ' + (e && e.message ? e.message : e) };
};
const api = {
  get: async (endpoint, params = '') => {
    try {
      const res = await _fetchT(`${API_BASE}/${endpoint}${params ? '?' + params : ''}`, { headers: authHeaders() });
      if (res.status === 401) { handleAuthLost(); return []; }
      const d = await _parseRes(res);
      return (d && d.error) ? [] : d;
    } catch (e) { return []; }
  },
  post: async (endpoint, data) => {
    try {
      const res = await _fetchT(`${API_BASE}/${endpoint}`, { method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }), body: JSON.stringify(data) });
      if (res.status === 401) { handleAuthLost(); return { error: 'auth' }; }
      return await _parseRes(res);
    } catch (e) { return _netErr(e); }
  },
  put: async (endpoint, data) => {
    try {
      const res = await _fetchT(`${API_BASE}/${endpoint}`, { method: 'PUT', headers: authHeaders({ 'Content-Type': 'application/json' }), body: JSON.stringify(data) });
      if (res.status === 401) { handleAuthLost(); return { error: 'auth' }; }
      return await _parseRes(res);
    } catch (e) { return _netErr(e); }
  },
  del: async (endpoint, data) => {
    try {
      const res = await _fetchT(`${API_BASE}/${endpoint}`, { method: 'DELETE', headers: authHeaders({ 'Content-Type': 'application/json' }), body: JSON.stringify(data) });
      if (res.status === 401) { handleAuthLost(); return { error: 'auth' }; }
      return await _parseRes(res);
    } catch (e) { return _netErr(e); }
  },
};

// Session (stays in localStorage)
const session = {
  get: () => { try { return JSON.parse(localStorage.getItem('crm_session')); } catch { return null; } },
  set: (v) => { localStorage.setItem('crm_session', JSON.stringify(v)); },
  clear: () => { localStorage.removeItem('crm_session'); },
};

// Globale App-Einstellungen (serverseitig in app_settings gespeichert, pro User über key-Suffix)
// Feature-Toggle: Anwesenheit/Zeiterfassung. Default an.
let _attendanceEnabled = true;
const appSettings = {
  // Lädt eine Einstellung; Fallback bei Fehler/leer
  getBool: async (key, fallback) => {
    try {
      const res = await api.get(`settings/${key}`);
      if(res && res.value !== null && res.value !== undefined) return !!res.value;
    } catch(e){}
    return fallback;
  },
  setBool: async (key, val, userId) => {
    try { await api.post(`settings/${key}`, {value: !!val, userId: userId||null}); return true; } catch(e){ return false; }
  },
};
// Persönliches Zeiterfassungs-Setting eines Users laden.
// Vertriebler ("member") haben es immer an (nicht abschaltbar, für besseres Tracking).
const loadAttendanceSetting = async (user) => {
  if(!user) return true;
  if(user.role === "client") return false; // Kunden haben mit Zeiterfassung nichts zu tun
  if(user.role === "member") return true;
  return await appSettings.getBool(`attendance_enabled_${user.id}`, true);
};

// Darstellung (Modus und Farbe) vom Server holen. Sie haengt am Nutzer, nicht am
// Browser, deshalb wird bei Abweichung einmalig neu geladen. Das sessionStorage-Flag
// verhindert, dass daraus eine Schleife wird.
const loadDarstellung = async (user) => {
  if(!user) return;
  try{
    const r = await api.get(`settings/darstellung_${user.id}`);
    const v = r && r.value;
    if(!v || typeof v !== "object") return;
    const modus = v.modus === "light" || v.modus === "dark" ? v.modus : _theme;
    const pal = PALETTEN.find(p=>p.id===v.palette) ? v.palette : _palette;
    if(modus === _theme && pal === _palette) return;
    if(sessionStorage.getItem("crm_darstellung_sync")) return;
    sessionStorage.setItem("crm_darstellung_sync","1");
    localStorage.setItem("crm_theme", modus);
    localStorage.setItem("crm_palette", pal);
    window.location.reload();
  }catch(e){}
};
const darstellungAnwenden = async (userId, modus, palette) => {
  localStorage.setItem("crm_theme", modus);
  localStorage.setItem("crm_palette", palette);
  // Das Flag verhindert, dass loadDarstellung nach dem Neuladen den alten
  // Serverstand zurueckspielt, bevor der neue dort angekommen ist.
  try{ sessionStorage.setItem("crm_darstellung_sync","1"); }catch(e){}
  if(userId){
    // Erst schreiben, dann neu laden. Ohne das Warten bricht der Reload den
    // Request ab und der alte Wert steht weiter auf dem Server.
    try{ await api.post(`settings/darstellung_${userId}`, {value:{modus,palette}, userId}); }catch(e){}
  }
  window.location.reload();
};

// Lead-Sperre eines Users laden (z.B. ausscheidende Mitarbeiter).
// Admins werden nie gesperrt.
let _leadsBlocked = false;
const loadLeadsBlocked = async (user) => {
  if(!user || user.role === "admin") return false;
  return await appSettings.getBool(`leads_blocked_${user.id}`, false);
};

// Cached data store - loads from API, provides sync interface for components
// Components still use store.get/store.set but data flows through API
const _cache = {};
const store = {
  get: (key) => _cache[key] || [],
  set: (key, val) => { _cache[key] = val; },
};

// Load all data from API into cache (leads loaded separately with pagination)
const loadAllData = async () => {
  const [users, calls, appts, finances, checklist, cutterP, cutterI, cutterG, serviceCat] = await Promise.all([
    api.get('users'), api.get('calls'), api.get('appointments'),
    api.get('finances'), api.get('checklist'), api.get('cutter-projects'), api.get('cutter-invoices'), api.get('cutter-goals'),
    api.get('service-catalog'),
  ]);
  _cache[STORAGE_KEYS.users] = users || [];
  _cache[STORAGE_KEYS.leads] = _cache[STORAGE_KEYS.leads] || []; // Keep existing, don't reload
  _cache[STORAGE_KEYS.calls] = calls || [];
  _cache[STORAGE_KEYS.appointments] = appts || [];
  _cache[STORAGE_KEYS.finances] = (finances || []).map(f => ({...f, amount: parseFloat(f.amount)||0}));
  _cache[STORAGE_KEYS.checklist] = (checklist || []).map(c => ({...c, amount: parseFloat(c.amount)||0}));
  _cache[STORAGE_KEYS.cutter_projects] = cutterP || [];
  _cache[STORAGE_KEYS.cutter_invoices] = (cutterI || []).map(i => ({...i, amount: parseFloat(i.amount)||0}));
  _cache[STORAGE_KEYS.cutter_goals] = cutterG || [];
  _cache[STORAGE_KEYS.service_catalog] = serviceCat || [];
};

// Refresh a single data type from API
const refreshData = async (key) => {
  const map = {
    [STORAGE_KEYS.users]: 'users',
    [STORAGE_KEYS.leads]: 'leads',
    [STORAGE_KEYS.calls]: 'calls',
    [STORAGE_KEYS.appointments]: 'appointments',
    [STORAGE_KEYS.finances]: 'finances',
    [STORAGE_KEYS.checklist]: 'checklist',
    [STORAGE_KEYS.cutter_projects]: 'cutter-projects',
    [STORAGE_KEYS.cutter_invoices]: 'cutter-invoices',
    [STORAGE_KEYS.cutter_goals]: 'cutter-goals',
    [STORAGE_KEYS.service_catalog]: 'service-catalog',
  };
  if (map[key]) {
    const data = await api.get(map[key]);
    _cache[key] = (data || []).map(d => {
      if (d.amount !== undefined) d.amount = parseFloat(d.amount)||0;
      return d;
    });
  }
};

// ─── ICONS ───
const Icon = ({ name, size = 20 }) => {
  const icons = {
    dashboard: <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />,
    mail: <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />,
    leads: <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />,
    phone: <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />,
    calendar: <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z" />,
    money: <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" />,
    upload: <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z" />,
    logout: <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />,
    add: <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />,
    close: <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />,
    delete: <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />,
    check: <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />,
    team: <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />,
    chart: <path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z" />,
    list: <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z" />,
    cut: <path d="M9.64 7.64c.23-.5.36-1.05.36-1.64 0-2.21-1.79-4-4-4S2 3.79 2 6s1.79 4 4 4c.59 0 1.14-.13 1.64-.36L10 12l-2.36 2.36C7.14 14.13 6.59 14 6 14c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4c0-.59-.13-1.14-.36-1.64L12 14l7 7h3v-1L9.64 7.64zM6 8c-1.1 0-2-.89-2-2s.9-2 2-2 2 .89 2 2-.9 2-2 2zm0 12c-1.1 0-2-.89-2-2s.9-2 2-2 2 .89 2 2-.9 2-2 2zm6-7.5c-.28 0-.5-.22-.5-.5s.22-.5.5-.5.5.22.5.5-.22.5-.5.5zM19 3l-6 6 2 2 7-7V3z" />,
    menu: <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />,
    ai: <path d="M21 10.5h-1.5V9h-2v1.5H16V9h-2v1.5h-1.5v2H14V15h2v-2.5h1.5V15h2v-2.5H21v-2zM8 13c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />,
    target: <path d="M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10 10-4.49 10-10S17.51 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3-8c0 1.66-1.34 3-3 3s-3-1.34-3-3 1.34-3 3-3 3 1.34 3 3z" />,
    clipboard: <path d="M19 2h-4.18C14.4.84 13.3 0 12 0c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm7 18H5V4h2v3h10V4h2v16z" />,
    edit: <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />,
    search: <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />,
  };
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">{icons[name] || null}</svg>);
};

// ─── CSV PARSER (handles multiline fields, BOM, semicolons, tabs) ───
const parseFullCSV = (text) => {
  // Strip any BOM characters (can be doubled)
  while (text.charCodeAt(0) === 0xFEFF || text.charCodeAt(0) === 0xEF) {
    text = text.slice(1);
  }
  text = text.replace(/^\ufeff+/, '');
  
  // Detect delimiter from first line (only outside quotes)
  let firstLine = '';
  let inQ = false;
  for (let i = 0; i < text.length && (inQ || (text[i] !== '\n' && text[i] !== '\r')); i++) {
    if (text[i] === '"') inQ = !inQ;
    if (!inQ) firstLine += text[i];
  }
  const commas = (firstLine.match(/,/g) || []).length;
  const semis = (firstLine.match(/;/g) || []).length;
  const tabs = (firstLine.match(/\t/g) || []).length;
  let delim = ",";
  if (semis > commas && semis > tabs) delim = ";";
  else if (tabs > commas && tabs > semis) delim = "\t";

  // Parse fields properly handling quoted multiline values
  const rows = [];
  let currentRow = [];
  let field = "";
  let inQuotes = false;
  
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++; // skip escaped quote
        } else {
          inQuotes = false; // end of quoted field
        }
      } else {
        field += c; // include everything inside quotes, including newlines
      }
    } else {
      if (c === '"') {
        inQuotes = true;
      } else if (c === delim) {
        currentRow.push(field.trim());
        field = "";
      } else if (c === '\r') {
        if (text[i + 1] === '\n') i++; // skip \r\n
        currentRow.push(field.trim());
        if (currentRow.some(x => x)) rows.push(currentRow);
        currentRow = [];
        field = "";
      } else if (c === '\n') {
        currentRow.push(field.trim());
        if (currentRow.some(x => x)) rows.push(currentRow);
        currentRow = [];
        field = "";
      } else {
        field += c;
      }
    }
  }
  // Last field/row
  currentRow.push(field.trim());
  if (currentRow.some(x => x)) rows.push(currentRow);
  
  if (rows.length < 2) return [];
  
  // Clean headers
  const headers = rows[0].map(h => h.replace(/^\ufeff+/, '').replace(/^"+|"+$/g, '').trim());
  
  return rows.slice(1).map(row => {
    const obj = {};
    headers.forEach((h, i) => {
      if (h) obj[h] = (row[i] || "").replace(/^"+|"+$/g, '').trim();
    });
    return obj;
  });
};

// ─── MAPPINGS ───
const clean = v => (!v || v === 'None' || v === 'none' || v === 'null') ? '' : String(v).trim();

const mapOutscraperToLead = (row) => {
  // Detect Notion format (has "Phase" or "Tier Rating" or "Setter Analysis" columns)
  const isNotion = row["Phase"] !== undefined || row["Tier Rating"] !== undefined || row["Setter Analysis"] !== undefined;
  // Detect Dripify (LinkedIn) format
  const isDripify = row["LinkedIn email"] !== undefined || row["Linkedin url"] !== undefined || row["Linkedin public url"] !== undefined;
  // Detect Apify (Google Maps scrape) format: hat "title" + "totalScore"/"categoryName"/"reviewsCount"
  const isApify = row["title"] !== undefined && (row["totalScore"] !== undefined || row["categoryName"] !== undefined || row["reviewsCount"] !== undefined);

  if (isApify) {
    // Kategorien sammeln (categories/0 ... categories/9 + categoryName)
    const cats = [];
    if(clean(row["categoryName"])) cats.push(clean(row["categoryName"]));
    for(let i=0;i<10;i++){ const c = clean(row[`categories/${i}`]); if(c && !cats.includes(c)) cats.push(c); }
    const street = clean(row["street"]);
    const city = clean(row["city"]);
    const state = clean(row["state"]);
    const country = clean(row["countryCode"]);
    const mapsUrl = clean(row["url"]);
    // Notiz mit Google-Maps-Link + Bewertung
    const noteParts = [];
    const score = clean(row["totalScore"]);
    const revCount = clean(row["reviewsCount"]);
    if(score) noteParts.push(`Google-Bewertung: ${score}${revCount?` (${revCount} Rezensionen)`:""}`);
    if(mapsUrl) noteParts.push(`Google Maps: ${mapsUrl}`);
    return {
      id: `lead-${Date.now()}-${Math.random().toString(36).slice(2,8)}`,
      company: clean(row["title"]) || "",
      contactName: "",
      contactTitle: "",
      email: clean(row["email"]) || "",
      phone: clean(row["phone"]) || "",
      website: clean(row["website"]) || "",
      address: [street, city, state].filter(Boolean).join(", "),
      city: city || "",
      country: country || "",
      category: cats.join(", "),
      rating: score || "",
      reviews: revCount || "",
      source: "Apify (Google Maps)",
      query: cats[0] || "",
      status: "new",
      assignedTo: null,
      notes: noteParts.length ? [{text: noteParts.join("\n"), date: new Date().toISOString(), by: "Apify Import"}] : [],
      callCount: 0,
      lastContact: null,
      created: new Date().toISOString(),
    };
  }

  if (isDripify) {
    const firstName = clean(row["First name"]);
    const lastName = clean(row["Last name"]);
    const fullName = `${firstName} ${lastName}`.trim();
    // Email: erste vorhandene aus LinkedIn / Corporate / Manually added
    const email = clean(row["LinkedIn email"]) || clean(row["Corporate email"]) || clean(row["Manually added email"]) || "";
    const lastAction = clean(row["Last action"]);
    const lastActionTime = clean(row["Time of last action"]);
    // Action-Map zu Status
    const actionStatusMap = {
      "MESSAGE_SENT": "contacted",
      "MESSAGE_REPLIED": "contacted",
      "PROFILE_VIEWED": "new",
      "CONNECTION_SENT": "contacted",
      "CONNECTION_ACCEPTED": "contacted",
      "FOLLOW_UP_SENT": "contacted",
    };
    // Notiz aus LinkedIn-Action erstellen
    const noteParts = [];
    if(lastAction) {
      let dateStr = "";
      if(lastActionTime) {
        try {
          const ts = parseInt(lastActionTime);
          if(!isNaN(ts)) dateStr = ` am ${new Date(ts).toLocaleDateString("de")}`;
        } catch(e){}
      }
      noteParts.push(`LinkedIn: ${lastAction.replace(/_/g," ").toLowerCase()}${dateStr}`);
    }
    return {
      id: `lead-${Date.now()}-${Math.random().toString(36).slice(2,8)}`,
      company: clean(row["Company"]) || "",
      contactName: fullName,
      contactTitle: clean(row["Title"]) || "",
      positionTitle: clean(row["Title"]) || "",
      email,
      phone: clean(row["Phone number"]) || "",
      website: clean(row["Company website"]) || "",
      address: "",
      city: clean(row["City"]) || "",
      country: clean(row["Country"]) || "",
      category: "",
      rating: "",
      reviews: "",
      source: "Dripify (LinkedIn)",
      query: clean(row["Location"]) || "",
      status: actionStatusMap[lastAction] || "new",
      assignedTo: null,
      linkedinUrl: clean(row["Linkedin public url"]) || clean(row["Linkedin url"]) || "",
      notes: noteParts.length ? [{text: noteParts.join("\n"), date: new Date().toISOString(), by: "Dripify Import"}] : [],
      callCount: 0,
      lastContact: null,
      created: new Date().toISOString(),
    };
  }

  if (isNotion) {
    const statusMap = {"Success":"appointment","Follow-Up":"contacted","Lost":"lost","Invalid Number":"lost","Invalid Website":"lost","N/A":"new","N/A + Voicemail":"contacted"};
    const p1 = clean(row["Phase 1 Status"]);
    const tier = clean(row["Tier Rating"]);
    const phase = clean(row["Phase"]);
    const setter = clean(row["Setter Analysis"]);
    const hours = clean(row["Working Hours"]);
    const notesField = clean(row["Notes"]);
    const appt = clean(row["Phase 1 Appointment"]);
    
    // Build notes text from all Notion fields
    const noteParts = [];
    if(tier) noteParts.push(`Tier: ${tier}`);
    if(phase) noteParts.push(`Phase: ${phase}`);
    if(p1) noteParts.push(`Status: ${p1}`);
    if(appt) noteParts.push(`Termin: ${appt}`);
    if(setter) noteParts.push(`Analyse: ${setter.slice(0,500)}`);
    if(hours) noteParts.push(`Öffnungszeiten: ${hours.slice(0,200)}`);
    if(notesField) noteParts.push(`Notizen: ${notesField.slice(0,500)}`);
    
    return {
      id: `lead-${Date.now()}-${Math.random().toString(36).slice(2,8)}`,
      company: clean(row["Company"]) || "",
      contactName: "",
      contactTitle: "",
      email: clean(row["E-Mail"]) || clean(row["Email"]) || "",
      phone: clean(row["Phone"]) || "",
      website: clean(row["Website"]) || clean(row["website"]) || clean(row["site"]) || clean(row["Web"]) || clean(row["URL"]) || clean(row["url"]) || clean(row["Homepage"]) || "",
      address: "",
      city: "",
      category: clean(row["Category"]) || "",
      rating: tier,
      reviews: "",
      source: "Notion Import",
      query: phase,
      status: statusMap[p1] || "new",
      assignedTo: null,
      notes: noteParts.length ? [{text: noteParts.join("\n"), date: new Date().toISOString(), by: "Notion Import"}] : [],
      callCount: 0,
      lastContact: null,
      created: new Date().toISOString(),
    };
  }
  
  // Default: Outscraper format
  return {
    id: `lead-${Date.now()}-${Math.random().toString(36).slice(2,8)}`,
    company: clean(row.name) || clean(row.company_name) || "",
    contactName: clean(row.full_name) || `${clean(row.first_name)} ${clean(row.last_name)}`.trim(),
    contactTitle: clean(row.title),
    email: clean(row.email),
    phone: clean(row.phone) || clean(row.company_phone) || clean(row.contact_phone),
    website: clean(row.website) || clean(row.site) || clean(row.url) || clean(row.homepage) || clean(row.web) || "",
    address: clean(row.address) || `${clean(row.street)} ${clean(row.postal_code)} ${clean(row.city)}`.trim(),
    city: clean(row.city),
    category: clean(row.category) || clean(row.subtypes),
    rating: clean(row.rating),
    reviews: clean(row.reviews),
    source: clean(row.source) || "Outscraper",
    query: clean(row.query),
    status: "new",
    assignedTo: null,
    notes: [],
    callCount: 0,
    lastContact: null,
    created: new Date().toISOString(),
  };
};

const mapBankCSV = (row) => {
  // Try all known German bank column names for date
  const dateStr = row["Bezahldatum"] || row["Buchungstag"] || row["Valutadatum"] || row["Datum"] || row["Buchungsdatum"] || row["Date"] || row["Wertstellung"] || row["Belegdatum"] || "";

  // Description: kombiniere Name + vollen Verwendungszweck
  const desc = row["Beschreibung"] || row["Verwendungszweck"] || row["Buchungstext"] || row["Description"] || "";
  const name = row["Name"] || row["Auftraggeber/Empfänger"] || row["Empfänger/Zahlungspflichtiger"] || row["Beguenstigter/Zahlungspflichtiger"] || "";
  // Verwendungszweck: alle Zeilen zu einer string-Reihe — für besseres Matching
  const fullDesc = desc.replace(/\n+/g, " | ").trim();
  const displayDesc = fullDesc ? (name ? `${name} — ${fullDesc}` : fullDesc) : name;
  
  // Amount: try all known column names
  let amtStr = row["Betrag"] || row["Betrag (EUR)"] || row["Amount"] || row["Umsatz"] || row["Betrag in EUR"] || row["Soll/Haben"] || "0";
  // German number format: 1.234,56 → 1234.56 and -29,13 → -29.13
  amtStr = amtStr.replace(/\s/g, ""); // remove spaces
  // Handle 1.234,56 format: dots are thousands separators, comma is decimal
  amtStr = amtStr.replace(/\.(?=\d{3})/g, ""); // remove thousand dots
  amtStr = amtStr.replace(",", "."); // comma to decimal point
  amtStr = amtStr.replace(/[^0-9.\-]/g, ""); // remove anything else
  const amount = parseFloat(amtStr) || 0;
  
  // Parse date DD.MM.YYYY → YYYY-MM-DD
  let parsedDate = "";
  const dm = dateStr.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/);
  if (dm) {
    parsedDate = `${dm[3]}-${dm[2].padStart(2,'0')}-${dm[1].padStart(2,'0')}`;
  } else {
    // Try YYYY-MM-DD or other ISO formats
    const iso = dateStr.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (iso) parsedDate = dateStr.slice(0, 10);
    else parsedDate = new Date().toISOString().slice(0, 10);
  }
  
  // Skip zero-amount rows (like "Rechnungsabschluss")
  if (amount === 0) return null;
  
  return {
    id: `fin-${Date.now()}-${Math.random().toString(36).slice(2,8)}`,
    type: amount >= 0 ? "income" : "expense",
    amount: Math.abs(amount),
    description: displayDesc,
    date: parsedDate,
    category: "",
    source: "bank-import",
    created: new Date().toISOString(),
  };
};

// ─── THEME ───
// Smart Euro formatter: 109.694 → "109,7K €", 1.234 → "1.234 €"
const fmtEuro = (v) => {
  const n = typeof v === 'string' ? parseFloat(v.replace(/\./g,'').replace(',','.')) : v;
  if(isNaN(n)) return '0 €';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if(abs >= 1000000) return `${sign}${(abs/1000000).toFixed(1).replace('.',',')}M €`;
  if(abs >= 10000) return `${sign}${(abs/1000).toFixed(1).replace('.',',')}K €`;
  return `${sign}${abs.toLocaleString("de")} €`;
};

// Datum als deutsches Format anzeigen — ohne Timezone-Bug.
// "2026-05-14" wird korrekt als 14.5.2026 angezeigt (nicht als 13.5. wegen UTC-Parsing).
// Frist in Klartext. Wird im Cutter-Bereich an mehreren Stellen gebraucht,
// deshalb hier oben und nicht doppelt weiter unten.
const rfTageBis = (d) => {
  if(!d || d==="0000-00-00") return null;
  const dt = new Date(d); if(isNaN(dt)) return null;
  const heute = new Date(); heute.setHours(0,0,0,0); dt.setHours(0,0,0,0);
  return Math.round((dt-heute)/86400000);
};
const rfFristText = (d, fertig) => {
  const t = rfTageBis(d);
  const dt = d && d!=="0000-00-00" ? new Date(d) : null;
  const datum = dt && !isNaN(dt) ? dt.toLocaleDateString("de",{weekday:"short",day:"numeric",month:"short"}) : null;
  if(fertig) return {haupt: datum || "—", sub:"", spaet:false};
  if(t===null) return {haupt:"ohne Frist", sub:"", spaet:false};
  if(t<0) return {haupt:`${-t} ${-t===1?"Tag":"Tage"} drüber`, sub:datum, spaet:true};
  if(t===0) return {haupt:"heute", sub:datum, spaet:false};
  if(t===1) return {haupt:"morgen", sub:datum, spaet:false};
  return {haupt:`in ${t} Tagen`, sub:datum, spaet:false};
};

const fmtDate = (val) => {
  if(!val) return "";
  // ISO date strings ("2026-05-14") manuell parsen statt new Date() — vermeidet UTC-Verschiebung
  if(typeof val === "string"){
    const m = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if(m){
      const [,y,mo,d] = m;
      // Erstelle Datum als lokale Zeit (Mittag), umgehe Timezone-Probleme
      const dt = new Date(parseInt(y), parseInt(mo)-1, parseInt(d), 12, 0, 0);
      return dt.toLocaleDateString("de");
    }
  }
  return new Date(val).toLocaleDateString("de");
};
// Datum als YYYY-MM-DD für Vergleiche, lokal-bezogen
const dateOnly = (val) => {
  if(!val) return null;
  if(typeof val === "string"){
    const m = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if(m) return new Date(parseInt(m[1]), parseInt(m[2])-1, parseInt(m[3]), 12, 0, 0);
  }
  return new Date(val);
};
// Heutiges Datum im LOKALEN-Format "YYYY-MM-DD" (kein UTC-Verschub!)
// todayLocal() ist BUGGY — gibt UTC zurück, was Abends/Nachts in Berlin den falschen Tag liefert.
const todayLocal = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth()+1).padStart(2,"0");
  const dd = String(d.getDate()).padStart(2,"0");
  return `${yyyy}-${mm}-${dd}`;
};
// Monatsschlüssel "YYYY-MM" für ein gegebenes Date-Objekt — LOKAL, nicht UTC.
// monthKey(d) ist BUGGY: new Date(2025, 11, 1).toISOString() → "2025-11-30T23:00:00.000Z"
// → slice gibt "2025-11" zurück statt "2025-12". Dieser Bug verschiebt ALLE Chart-Balken um einen Monat.
const monthKey = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth()+1).padStart(2,"0");
  return `${yyyy}-${mm}`;
};
// Sekunden → "HH:MM:SS" (für Timer)
const fmtDuration = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds||0));
  const h = Math.floor(s/3600);
  const m = Math.floor((s%3600)/60);
  const sec = s%60;
  return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}`;
};
// Sekunden → "3h 25m" (kompakt, für Übersichten)
const fmtDurationShort = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds||0));
  const h = Math.floor(s/3600);
  const m = Math.floor((s%3600)/60);
  if(h>0) return `${h}h ${m}m`;
  return `${m}m`;
};
// Mini-SVG-Icons (12px) für Meta-Zeilen statt Emojis
const IconCut = () => (<svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style={{flexShrink:0}}><path d="M9.64 7.64c.23-.5.36-1.05.36-1.64 0-2.21-1.79-4-4-4S2 3.79 2 6s1.79 4 4 4c.59 0 1.14-.13 1.64-.36L10 12l-2.36 2.36C7.14 14.13 6.59 14 6 14c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4c0-.59-.13-1.14-.36-1.64L12 14l7 7h3v-1L9.64 7.64zM6 8c-1.1 0-2-.89-2-2s.9-2 2-2 2 .89 2 2-.9 2-2 2zm0 12c-1.1 0-2-.89-2-2s.9-2 2-2 2 .89 2 2-.9 2-2 2zm6-7.5c-.28 0-.5-.22-.5-.5s.22-.5.5-.5.5.22.5.5-.22.5-.5.5zM19 3l-6 6 2 2 7-7V3z"/></svg>);
const IconCal = () => (<svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style={{flexShrink:0}}><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z"/></svg>);
const IconClock = () => (<svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style={{flexShrink:0}}><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>);
const IconPlay = () => (<svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style={{flexShrink:0}}><path d="M8 5v14l11-7z"/></svg>);
const IconPause = () => (<svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style={{flexShrink:0}}><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>);
const IconStop = () => (<svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style={{flexShrink:0}}><path d="M6 6h12v12H6z"/></svg>);
// Parst einen MySQL-DATETIME-String ("2026-06-17 12:47:20") als LOKALE Zeit (kein UTC-Shift).
const parseServerTime = (str) => {
  if(!str) return null;
  const m = str.match(/(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
  if(!m) return new Date(str);
  return new Date(+m[1], +m[2]-1, +m[3], +m[4], +m[5], +m[6]);
};
// Berechnet live (clientseitig) die aktuelle Arbeits- und Pausenzeit aus den Zeitstempeln.
// Läuft korrekt weiter auch wenn die Seite lange zu war, weil aus clock_in + jetzt gerechnet wird.
const liveAttendance = (session) => {
  if(!session || !session.clockIn) return { work: 0, currentBreak: 0, totalBreak: session?.breakSeconds||0 };
  const now = new Date();
  const clockIn = parseServerTime(session.clockIn);
  const clockOut = session.clockOut ? parseServerTime(session.clockOut) : null;
  const totalBreakBase = session.totalBreakSeconds != null ? session.totalBreakSeconds : (session.breakSeconds||0);
  let currentBreak = 0;
  if(session.status === "break" && session.breakStartedAt){
    currentBreak = Math.max(0, Math.floor((now - parseServerTime(session.breakStartedAt))/1000));
  }
  const totalBreak = totalBreakBase + currentBreak;
  let work = 0;
  if(clockIn){
    const end = clockOut || now;
    work = Math.max(0, Math.floor((end - clockIn)/1000) - totalBreak);
  }
  return { work, currentBreak, totalBreak };
};
// ═══ FARBSYSTEM ═══
// Eine ruhige Grundfarbe plus eine waehlbare Akzentfarbe. Die Namen der Werte
// bleiben wie vorher (accent, success, danger ...), damit alle Stellen im Code
// unveraendert weiterlaufen. Geaendert hat sich nur, woher die Farben kommen.
const NEUTRAL = {
  kalt: {
    light: { bg:"#f4f5f7", bgCard:"#ffffff", bgCard2:"#f8f9fb", bgHover:"#eef0f4", bgInput:"#f7f8fa", bgSidebar:"#fbfbfc",
             border:"#e3e6eb", borderStrong:"#d3d8e0", text:"#141b26", textMuted:"#5a6472", textDim:"#8d96a3" },
    dark:  { bg:"#0d1117", bgCard:"#151b23", bgCard2:"#1a212b", bgHover:"#1e2631", bgInput:"#111820", bgSidebar:"#0b0f15",
             border:"#232c38", borderStrong:"#2f3a49", text:"#e6eaf0", textMuted:"#9aa5b4", textDim:"#697585" },
  },
  warm: {
    light: { bg:"#f6f6f5", bgCard:"#ffffff", bgCard2:"#faf9f8", bgHover:"#f0efed", bgInput:"#f7f7f6", bgSidebar:"#fbfbfa",
             border:"#e6e4e1", borderStrong:"#d6d3cf", text:"#1a1918", textMuted:"#5e5b57", textDim:"#8f8b85" },
    dark:  { bg:"#0f0f0e", bgCard:"#181817", bgCard2:"#1d1d1c", bgHover:"#232322", bgInput:"#141413", bgSidebar:"#0c0c0b",
             border:"#272725", borderStrong:"#343431", text:"#eceae7", textMuted:"#a09c96", textDim:"#6f6b66" },
  },
};
// Jede Palette kennt drei Toene: den Akzent, einen fuer Gutes und einen fuer
// Kritisches. Mehr Farben gibt es im System nicht, alles andere ist neutral.
const PALETTEN = [
  { id:"tinte",   name:"Tinte",   info:"Ruhiges Navy",     basis:"kalt",
    light:"#1d2b3e", dark:"#4d76b0", kritLight:"#b0342a", kritDark:"#d4503f" },
  { id:"rot",     name:"Signal",  info:"Das Rot aus dem Logo",        basis:"warm",
    light:"#b0342a", dark:"#d4503f", posLight:"#33404f", posDark:"#8ba3bd", kritLight:"#8f2a22", kritDark:"#e0685a" },
  { id:"kupfer",  name:"Kupfer",  info:"Warm und gedeckt",                basis:"warm",
    light:"#96551f", dark:"#c07c42", kritLight:"#a33226", kritDark:"#d4503f" },
  { id:"petrol",  name:"Petrol",  info:"Kühl, aber nicht grell",  basis:"kalt",
    light:"#17595e", dark:"#2f8a90", kritLight:"#a33226", kritDark:"#d4503f" },
  { id:"violett", name:"Violett", info:"Dezent und ruhig",  basis:"kalt",
    light:"#4a3d7d", dark:"#7b6bc4", kritLight:"#a33226", kritDark:"#d4503f" },
];
const baueTheme = (palId, modus) => {
  const p = PALETTEN.find(x=>x.id===palId) || PALETTEN[0];
  const n = NEUTRAL[p.basis][modus];
  const acc = p[modus];
  const dunkel = modus === "dark";
  // Gutes laeuft im Akzent, Kritisches im Signalton, Warnungen bleiben neutral
  // und fallen ueber Form und Platz auf statt ueber eine dritte Farbe.
  const pos = (dunkel ? p.posDark : p.posLight) || acc;
  const krit = dunkel ? p.kritDark : p.kritLight;
  return {
    ...n,
    borderFocus: acc,
    accent: acc, accentGlow: acc+"1c",
    success: pos, successGlow: pos+"1c",
    warning: n.textMuted, warningGlow: n.textMuted+"1c",
    danger: krit, dangerGlow: krit+"1c",
    info: pos, infoGlow: pos+"1c",
    // Diagrammfarben: Hauptwert im Akzent, Nebenwert neutral, Signal im Kritischton
    chart1: acc, chart2: dunkel ? n.borderStrong : n.textDim, chart3: krit,
    shadow: dunkel ? "0 1px 2px rgba(0,0,0,0.30)" : "0 1px 2px rgba(16,24,40,0.05)",
    shadowLg: dunkel ? "0 12px 40px rgba(0,0,0,0.55)" : "0 8px 30px rgba(16,24,40,0.10)",
    logoFilter: dunkel ? "none" : "invert(1)",
    amb: `radial-gradient(900px 520px at 88% -12%, ${acc}0f, transparent 62%)`,
    glassBg: dunkel ? n.bgCard+"e6" : n.bgCard+"e6",
    glassBgSide: dunkel ? n.bgSidebar+"e6" : n.bgSidebar+"e6",
    glassBlur: "blur(18px) saturate(1.1)",
    glassBorder: n.border,
    glassHi: "none",
  };
};
let _theme = localStorage.getItem('crm_theme') || 'dark';
let _palette = localStorage.getItem('crm_palette') || 'tinte';
if(!PALETTEN.find(p=>p.id===_palette)) _palette = 'tinte';
let T = baueTheme(_palette, _theme);
// Die Pipeline-Status hatten frueher jeder eine eigene bunte Farbe. Jetzt laufen
// sie in einer Abstufung des Akzents, Verlorenes im Signalton, Totes in Grau.
const statusFarbenSetzen = () => {
  const stufe = {
    new: T.accent+"cc", no_answer: T.textDim, email_sent: T.accent+"88",
    follow_up: T.accent+"dd", appointment: T.accent, invalid: T.textDim+"99",
    lost: T.danger, offer_sent: T.accent+"ee", closed: T.accent, active_client: T.success,
  };
  [...LEAD_STATUSES, ...CUSTOMER_STATUSES].forEach(st => {
    if(stufe[st.id]) { st.color = stufe[st.id]; st.bg = stufe[st.id].slice(0,7)+"22"; }
  });
};
statusFarbenSetzen();
// Set body class for CSS variables
document.body.className = `theme-${_theme}`;
// Die Werte aus index.css (Seitenhintergrund, Scrollbalken, Pfeil in Auswahlfeldern)
// an die gewaehlte Palette angleichen, sonst blitzt am Rand noch die alte Farbe durch.
(() => {
  const pfeil = (farbe) => `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3E%3Cpath d='M1 1L5 5L9 1' stroke='${encodeURIComponent(farbe)}' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`;
  const s = document.documentElement.style;
  s.setProperty('--bg', T.bg);
  s.setProperty('--text', T.text);
  s.setProperty('--scrollbar', T.borderStrong);
  s.setProperty('--scrollbar-hover', T.textDim);
  s.setProperty('--sel-pfeil', pfeil(T.textDim));
  s.setProperty('--sel-pfeil-aktiv', pfeil(T.text));
  s.setProperty('--sel-ring', T.border);
  s.setProperty('--sel-ring-fokus', T.accent+"55");
  s.setProperty('--sel-opt-bg', T.bgCard);
  s.setProperty('--sel-opt-text', T.text);
  s.setProperty('--kachel-rahmen', T.accent);
  document.body.style.background = T.bg;
})();

// ─── UI COMPONENTS ───
const Badge = ({children,color=T.accent}) => (<span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 10px",borderRadius:7,fontSize:12,fontWeight:600,color,background:`${color}1f`}}>{children}</span>);

const Button = ({children,variant="primary",size="md",onClick,disabled,style:s,icon,type="button"}) => {
  const base = {display:"inline-flex",alignItems:"center",gap:6,border:"none",borderRadius:9,cursor:disabled?"not-allowed":"pointer",fontWeight:600,fontSize:size==="sm"?12:14,padding:size==="sm"?"7px 13px":"10px 20px",transition:"all 0.15s",opacity:disabled?0.5:1};
  const vars = {primary:{background:T.accent,color:"#fff",boxShadow:T.shadow},secondary:{background:T.bgCard,color:T.text,border:`1px solid ${T.border}`},ghost:{background:"transparent",color:T.textMuted},danger:{background:T.accentGlow,color:T.danger,border:`1px solid ${T.danger}33`},success:{background:T.successGlow,color:T.success}};
  return (<button type={type} onClick={onClick} disabled={disabled} style={{...base,...vars[variant],...s}}>{icon&&<Icon name={icon} size={size==="sm"?14:16}/>}{children}</button>);
};

const Input = ({label,...props}) => (
  <div style={{display:"flex",flexDirection:"column",gap:4}}>
    {label&&<label style={{fontSize:12,color:T.textMuted,fontWeight:500}}>{label}</label>}
    <input {...props} style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 14px",color:T.text,fontSize:14,outline:"none",...props.style}}
      onFocus={e=>{e.target.style.borderColor=T.borderFocus;props.onFocus?.(e);}} onBlur={e=>{e.target.style.borderColor=T.border;props.onBlur?.(e);}} />
  </div>
);

const Select = ({label,options,...props}) => (
  <div style={{display:"flex",flexDirection:"column",gap:4}}>
    {label&&<label style={{fontSize:12,color:T.textMuted,fontWeight:500}}>{label}</label>}
    <select {...props} style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 14px",color:T.text,fontSize:14,outline:"none",...props.style}}>
      {options.map(o=>(<option key={o.value} value={o.value}>{o.label}</option>))}
    </select>
  </div>
);

const Modal = ({open,onClose,title,children,width=600}) => {
  // Hintergrund nicht mitscrollen lassen, solange das Fenster offen ist
  useEffect(()=>{
    if(!open) return;
    const vorher = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return ()=>{ document.body.style.overflow = vorher; };
  },[open]);
  if(!open) return null;
  return (<div className="app-modal-backdrop" style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,backdropFilter:"blur(6px)",padding:20}} onClick={onClose}>
    <div onClick={e=>e.stopPropagation()} className="app-modal" style={{background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:14,width:"100%",maxWidth:width,maxHeight:"100%",display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:`${T.glassHi}, ${T.shadowLg}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,padding:"18px 22px",borderBottom:`1px solid ${T.border}`,flexShrink:0}}>
        <h3 style={{fontSize:17,fontWeight:700,letterSpacing:"-0.01em",minWidth:0,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{title}</h3>
        <button onClick={onClose} aria-label="Schließen" style={{background:T.bgCard2,border:`1px solid ${T.border}`,color:T.textMuted,cursor:"pointer",width:32,height:32,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><Icon name="close"/></button>
      </div>
      <div className="app-modal-body" style={{overflowY:"auto",overscrollBehavior:"contain",WebkitOverflowScrolling:"touch",padding:"20px 22px"}}>{children}</div>
    </div>
  </div>);
};

const StatCard = ({label,value,sub,icon,color=T.accent,hideable=false,featured=false,trend=null}) => {
  const blurKey = `crm_blur_${label}`;
  const [hidden,setHidden] = useState(()=>{ try{return localStorage.getItem(blurKey)==="1";}catch{return false;} });
  const toggleHide = () => { const next=!hidden; setHidden(next); try{if(next)localStorage.setItem(blurKey,"1");else localStorage.removeItem(blurKey);}catch{} };
  return (
  <div style={{background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:12,padding:"20px 22px",flex:1,minWidth:160,boxShadow:T.glassHi,position:"relative",overflow:"hidden"}}>
    {featured&&<div style={{position:"absolute",left:0,top:0,bottom:0,width:3,background:T.accent}}/>}
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
      <div style={{minWidth:0,flex:1}}>
        <div style={{display:"flex",alignItems:"center",gap:6}}>
          <span style={{fontSize:12,color:T.textMuted,fontWeight:500}}>{label}</span>
          {hideable&&<button onClick={toggleHide} style={{background:"none",border:"none",cursor:"pointer",color:T.textDim,padding:0,opacity:0.5}} title={hidden?"Einblenden":"Ausblenden"}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">{hidden
              ?<path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
              :<path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
            }</svg>
          </button>}
        </div>
        <div className="stat-value" style={{fontSize:"clamp(20px, 5vw, 27px)",fontWeight:600,letterSpacing:"-0.02em",color:T.text,fontFamily:"'JetBrains Mono',monospace",marginTop:13,filter:hidden?"blur(12px)":"none",transition:"filter 0.2s",userSelect:hidden?"none":"auto",wordBreak:"break-word",lineHeight:1}}>{value}</div>
        {trend&&<div style={{display:"inline-flex",alignItems:"center",gap:4,marginTop:10,fontSize:12,fontWeight:600,padding:"3px 8px",borderRadius:6,color:trend.dir==="up"?T.success:T.textDim,background:trend.dir==="up"?T.successGlow:T.bgHover}}>{trend.dir==="up"?"↑":""} {trend.text}</div>}
        {sub&&!trend&&<div style={{fontSize:12,color:T.textDim,marginTop:8,filter:hidden?"blur(8px)":"none",transition:"filter 0.2s"}}>{sub}</div>}
      </div>
      {icon&&!featured&&<div style={{color:`${color}55`,marginTop:2,flexShrink:0}}><Icon name={icon} size={22}/></div>}
    </div>
  </div>);
};

const Empty = ({icon,title,desc}) => (<div style={{textAlign:"center",padding:"60px 20px",color:T.textDim}}><div style={{marginBottom:12,opacity:0.4}}><Icon name={icon} size={48}/></div><div style={{fontSize:16,fontWeight:600,color:T.textMuted,marginBottom:6}}>{title}</div><div style={{fontSize:13}}>{desc}</div></div>);

// ─── CHARTS ───
// Glatte Bezier-Kurve durch Punkte (Catmull-Rom)
const _smoothPath = (points) => {
  if(points.length<2) return points.length?`M ${points[0].x} ${points[0].y}`:"";
  let p = `M ${points[0].x} ${points[0].y}`;
  for(let i=0;i<points.length-1;i++){
    const p0=points[i-1]||points[i], p1=points[i], p2=points[i+1], p3=points[i+2]||p2;
    const c1x=p1.x+(p2.x-p0.x)/6, c1y=p1.y+(p2.y-p0.y)/6, c2x=p2.x-(p3.x-p1.x)/6, c2y=p2.y-(p3.y-p1.y)/6;
    p += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
  }
  return p;
};
// Liniendiagramm: data = [{label, value}], fmt = Funktion für Werte-Anzeige
const LineChart = ({data, color, fmt=(v)=>v, height=210}) => {
  if(!data || data.length===0) return <div style={{color:T.textDim,fontSize:13,textAlign:"center",padding:30}}>Keine Daten</div>;
  const col = color || T.accent;
  const gradId = "lg-"+Math.random().toString(36).slice(2,8);
  const W=560,H=height,padL=14,padR=14,padT=22,padB=30, innerW=W-padL-padR, innerH=H-padT-padB;
  const vals = data.map(d=>d.value||0);
  let minV=Math.min(...vals), maxV=Math.max(...vals);
  if(minV===maxV){ maxV=maxV||1; minV=0; } else { minV=minV*0.85; maxV=maxV*1.05; }
  const x=i=>padL+(i/Math.max(1,data.length-1))*innerW, y=v=>padT+innerH-((v-minV)/(maxV-minV||1))*innerH;
  const pts=data.map((d,i)=>({x:x(i),y:y(d.value||0),...d}));
  const line=_smoothPath(pts);
  const area=line+` L ${pts[pts.length-1].x} ${padT+innerH} L ${pts[0].x} ${padT+innerH} Z`;
  const grid=[0,1,2,3].map(i=>{const gy=padT+(i/3)*innerH; return <line key={i} x1={padL} y1={gy} x2={W-padR} y2={gy} stroke={T.border} strokeWidth="1" opacity="0.6"/>;});
  return (<svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:"auto",display:"block",overflow:"visible"}}>
    <defs><linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={col} stopOpacity="0.18"/><stop offset="100%" stopColor={col} stopOpacity="0"/></linearGradient></defs>
    {grid}
    <path d={area} fill={`url(#${gradId})`}/>
    <path d={line} fill="none" stroke={col} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    {pts.map((pt,i)=>{const last=i===pts.length-1; return <g key={i}>
      <circle cx={pt.x} cy={pt.y} r={last?5.5:3.5} fill={last?col:T.bgCard} stroke={col} strokeWidth="2.5"/>
      <text x={pt.x} y={H-9} textAnchor="middle" fontSize="11" fontWeight="600" fill={T.textDim} fontFamily="DM Sans">{(pt.label||"").toUpperCase()}</text>
      {last&&<text x={pt.x} y={pt.y-13} textAnchor="middle" fontSize="12" fontWeight="600" fill={col} fontFamily="JetBrains Mono">{fmt(pt.value)}</text>}
    </g>;})}
  </svg>);
};
// Balkendiagramm: data = [{label, value}], fmt für Werte
const BarChart = ({data, color, fmt=(v)=>v, height=170, highlightLast=false}) => {
  if(!data || data.length===0) return <div style={{color:T.textDim,fontSize:13,textAlign:"center",padding:30}}>Keine Daten</div>;
  const col = color || T.accent;
  const maxV = Math.max(...data.map(d=>d.value||0), 1);
  return (<div style={{display:"flex",alignItems:"flex-end",gap:14,height,paddingTop:8}}>
    {data.map((d,i)=>{
      const val = d.value||0;
      const h = val>0 ? Math.max(4, (val/maxV)*(height-44)) : 4;
      const isHi = highlightLast ? i===data.length-1 : true;
      return (<div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:7,justifyContent:"flex-end",height:"100%"}}>
        <div style={{fontSize:12,fontWeight:600,color:val>0?(isHi?col:T.textMuted):T.textDim,fontFamily:"'JetBrains Mono',monospace"}}>{val>0?fmt(val):"—"}</div>
        <div style={{width:"100%",maxWidth:48,height:h,background:col,borderRadius:"8px 8px 0 0",opacity:val>0?(highlightLast?(isHi?1:0.35):Math.max(0.4,1-i*0.12)):0.15,transition:"height 0.3s"}}/>
        <div style={{fontSize:11,color:T.textDim,fontWeight:600,textAlign:"center"}}>{d.label}</div>
      </div>);
    })}
  </div>);
};
// Karten-Container mit Titel
const ChartCard = ({title, subtitle, total, children, style:s}) => (
  <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,padding:24,boxShadow:T.shadow,...s}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
      <div>
        <div style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em"}}>{title}</div>
        {subtitle&&<div style={{fontSize:13,color:T.textMuted,marginTop:3}}>{subtitle}</div>}
      </div>
      {total&&<div style={{textAlign:"right"}}><div style={{fontSize:22,fontWeight:600,letterSpacing:"-0.02em",fontFamily:"'JetBrains Mono',monospace"}}>{total.value}</div>{total.cap&&<div style={{fontSize:11,color:T.textDim,textTransform:"uppercase",letterSpacing:"0.05em"}}>{total.cap}</div>}</div>}
    </div>
    {children}
  </div>
);

// ─── AVATAR ───
const getAvatarKey = (userId) => `crm_avatar_${userId}`;

const Avatar = ({userId,name,size=34,editable=false}) => {
  const [img,setImg] = useState(null);
  useEffect(()=>{const users=store.get(STORAGE_KEYS.users)||[];const u=users.find(x=>x.id===userId);if(u?.avatar)setImg(u.avatar);},[userId]);
  const ref = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if(!file) return;
    if(file.size > 5000000) { alert("Bild zu groß (max 5MB)"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      // Resize to small square
      const imgEl = new window.Image();
      imgEl.onload = () => {
        const canvas = document.createElement("canvas");
        const s = Math.min(imgEl.width, imgEl.height);
        const sx = (imgEl.width - s) / 2;
        const sy = (imgEl.height - s) / 2;
        canvas.width = 128; canvas.height = 128;
        canvas.getContext("2d").drawImage(imgEl, sx, sy, s, s, 0, 0, 128, 128);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
        api.post("update-avatar",{userId,avatar:dataUrl});
        setImg(dataUrl);
      };
      imgEl.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const baseStyle = {width:size,height:size,borderRadius:"50%",flexShrink:0,overflow:"hidden",position:"relative",cursor:editable?"pointer":"default"};

  if(img) {
    return (<div style={baseStyle} onClick={()=>editable&&ref.current?.click()}>
      <img src={img} alt={name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
      {editable&&<div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"center",justifyContent:"center",opacity:0,transition:"opacity 0.2s"}} onMouseEnter={e=>e.currentTarget.style.opacity=1} onMouseLeave={e=>e.currentTarget.style.opacity=0}><Icon name="edit" size={size*0.35}/></div>}
      {editable&&<input ref={ref} type="file" accept="image/*" onChange={handleFile} style={{display:"none"}}/>}
    </div>);
  }

  return (<div style={{...baseStyle,background:`${T.accent}33`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.4,fontWeight:700,color:T.accent}} onClick={()=>editable&&ref.current?.click()}>
    {name?.[0]?.toUpperCase()||"?"}
    {editable&&<div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"center",justifyContent:"center",opacity:0,transition:"opacity 0.2s"}} onMouseEnter={e=>e.currentTarget.style.opacity=1} onMouseLeave={e=>e.currentTarget.style.opacity=0}><Icon name="edit" size={size*0.35}/></div>}
    {editable&&<input ref={ref} type="file" accept="image/*" onChange={handleFile} style={{display:"none"}}/>}
  </div>);
};

const FileDrop = ({onData,label,sublabel}) => {
  const ref = useRef(null);
  const handle = f => {
    if(!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (ext === 'xlsx' || ext === 'xls') {
      // Handle Excel files
      const r = new FileReader();
      r.onload = e => {
        try {
          const wb = XLSX.read(e.target.result, {type:'array'});
          const ws = wb.Sheets[wb.SheetNames[0]];
          const data = XLSX.utils.sheet_to_json(ws, {defval:''});
          onData(data);
        } catch(err) { alert('Datei konnte nicht gelesen werden: '+err.message); }
      };
      r.readAsArrayBuffer(f);
    } else {
      // Handle CSV/TSV
      const r = new FileReader();
      r.onload = e => { try { onData(parseFullCSV(e.target.result)); } catch(err){ alert('CSV konnte nicht gelesen werden: '+err.message); } };
      r.readAsText(f, "utf-8");
    }
  };
  const openPicker = (e) => { e?.stopPropagation?.(); if(ref.current){ ref.current.value=""; ref.current.click(); } };
  return (<div onClick={openPicker} style={{background:T.bgCard,border:`2px dashed ${T.border}`,borderRadius:12,padding:"40px 20px",textAlign:"center",cursor:"pointer"}}
    onDragOver={e=>{e.preventDefault();e.currentTarget.style.borderColor=T.accent;}} onDragLeave={e=>{e.currentTarget.style.borderColor=T.border;}}
    onDrop={e=>{e.preventDefault();e.currentTarget.style.borderColor=T.border;handle(e.dataTransfer.files?.[0]);}}>
    <div style={{color:T.textDim,marginBottom:8}}><Icon name="upload" size={36}/></div>
    <div style={{fontSize:14,fontWeight:600,color:T.textMuted,marginBottom:4}}>{label}</div>
    {sublabel&&<div style={{fontSize:12,color:T.textDim,marginBottom:14}}>{sublabel}</div>}
    <button type="button" onClick={openPicker} style={{background:T.accent,color:"#fff",border:"none",borderRadius:8,padding:"8px 18px",fontSize:13,fontWeight:600,cursor:"pointer"}}>Datei auswählen</button>
    <input ref={ref} type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" onChange={e=>handle(e.target.files?.[0])} style={{display:"none"}}/>
  </div>);
};

// ─── GROWTH CHART ───
const GrowthChart = ({finances}) => {
  // Monatswerte einsammeln
  const allMonths = {};
  finances.forEach(f => {
    const m = (f.date||"").slice(0,7);
    if (!m || m.length < 7) return;
    if (!allMonths[m]) allMonths[m] = {income:0, expense:0};
    if (f.type === "income") allMonths[m].income += f.amount;
    else allMonths[m].expense += f.amount;
  });
  const sorted = Object.keys(allMonths).sort();
  const ersterMonat = sorted[0] || monthKey(new Date());

  const defFrom = () => { const d=new Date(); d.setMonth(d.getMonth()-5); return monthKey(d); };
  const [fromMonth,setFromMonth] = useState(defFrom());
  const [toMonth,setToMonth] = useState(monthKey(new Date()));
  const [preset,setPreset] = useState("6");
  const [hover,setHover] = useState(null);

  const applyPreset = (p) => {
    setPreset(p);
    setToMonth(monthKey(new Date()));
    // "Alle" faengt beim ersten echten Datensatz an, nicht im Jahr 2000
    if (p === "all") { setFromMonth(ersterMonat); }
    else { const from = new Date(); from.setMonth(from.getMonth() - (parseInt(p)-1)); setFromMonth(monthKey(from)); }
  };

  if (sorted.length < 1) return <div style={{color:T.textDim,fontSize:13,padding:30,textAlign:"center"}}>Noch keine Finanzdaten vorhanden. Füge Einnahmen/Ausgaben hinzu oder importiere eine Bank-CSV.</div>;

  const fillMonths = (from, to) => {
    const result = [];
    const [fy, fm] = from.split("-").map(Number);
    const [ty, tm] = to.split("-").map(Number);
    if(!fy || !ty) return result;
    let y = fy, m = fm, schutz = 0;
    while ((y < ty || (y === ty && m <= tm)) && schutz++ < 1200) {
      result.push(`${y}-${String(m).padStart(2,"0")}`);
      m++; if (m > 12) { m = 1; y++; }
    }
    return result;
  };

  // Nie weiter zurueck als der erste Datensatz, sonst entsteht eine riesige leere Flaeche
  const vonEff = fromMonth < ersterMonat ? ersterMonat : fromMonth;
  const rohKeys = fillMonths(vonEff, toMonth);
  const roh = rohKeys.map(k => ({k, income:allMonths[k]?.income||0, expense:allMonths[k]?.expense||0}));

  // Bei langen Zeitraeumen auf Quartal oder Jahr zusammenfassen,
  // sonst wird die Achse unlesbar und die Balken verschwinden
  const kurz = (k) => { const [y,m]=k.split("-").map(Number); return ["Jan","Feb","März","Apr","Mai","Juni","Juli","Aug","Sept","Okt","Nov","Dez"][m-1]+" "+String(y).slice(2); };
  let stufe = "Monat", data = [];
  if (roh.length <= 26) {
    data = roh.map(r => ({label:kurz(r.k), income:r.income, expense:r.expense}));
  } else if (roh.length <= 60) {
    stufe = "Quartal";
    roh.forEach(r => {
      const [y,m] = r.k.split("-").map(Number); const q = Math.ceil(m/3); const id = y+"Q"+q;
      const last = data[data.length-1];
      if(!last || last.id !== id) data.push({id, label:"Q"+q+" "+String(y).slice(2), income:r.income, expense:r.expense});
      else { last.income += r.income; last.expense += r.expense; }
    });
  } else {
    stufe = "Jahr";
    roh.forEach(r => {
      const y = r.k.slice(0,4);
      const last = data[data.length-1];
      if(!last || last.id !== y) data.push({id:y, label:y, income:r.income, expense:r.expense});
      else { last.income += r.income; last.expense += r.expense; }
    });
  }
  if(data.length === 0) data = [{label:kurz(toMonth), income:0, expense:0}];


  const sumI = data.reduce((s,d)=>s+d.income,0);
  const sumA = data.reduce((s,d)=>s+d.expense,0);
  const fmt = v => { const a=Math.abs(v); return a>=1000 ? (v/1000).toFixed(a>=10000?0:1).replace(".",",")+"k" : Math.round(v).toString(); };
  const euro = v => v.toLocaleString("de",{maximumFractionDigits:0})+" €";

  const W=760, H=206, L=48, R=16, PT=14, PB=28;
  const chB = H - PT - PB;
  const cw = W-L-R;
  const maxBar = Math.max(...data.map(d=>Math.max(d.income,d.expense)), 1);
  const yB = v => PT + chB * (1 - v/maxBar);
  const xStep = cw/data.length;
  const xc = i => L + xStep*(i+0.5);
  const bw = Math.max(2, Math.min(14, xStep*0.32));

  const yTicks = Array.from({length:5}, (_,i)=> maxBar*i/4);
  const jeder = Math.ceil(data.length/8);
  const presets = [{v:"3",l:"3M"},{v:"6",l:"6M"},{v:"12",l:"1J"},{v:"24",l:"2J"},{v:"all",l:"Alle"}];
  const h = hover!==null && data[hover] ? data[hover] : null;

  return (<div>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:14}}>
      <div style={{display:"flex",gap:3,background:T.bgInput,padding:3,borderRadius:9,border:`1px solid ${T.border}`}}>
        {presets.map(r=>(<button key={r.v} onClick={()=>applyPreset(r.v)} style={{padding:"5px 12px",borderRadius:6,border:"none",fontSize:12,fontWeight:600,background:preset===r.v?T.accent:"transparent",color:preset===r.v?"#fff":T.textDim,cursor:"pointer",transition:"all 0.12s"}}>{r.l}</button>))}
      </div>
      <div style={{display:"flex",gap:8,alignItems:"center"}}>
        <input type="month" value={fromMonth} onChange={e=>{setFromMonth(e.target.value);setPreset("");}} style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"5px 9px",color:T.text,fontSize:12,outline:"none"}}/>
        <span style={{color:T.textDim,fontSize:12}}>bis</span>
        <input type="month" value={toMonth} onChange={e=>{setToMonth(e.target.value);setPreset("");}} style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"5px 9px",color:T.text,fontSize:12,outline:"none"}}/>
      </div>
    </div>

    <div style={{display:"flex",gap:26,marginBottom:10,flexWrap:"wrap",alignItems:"flex-end"}}>
      {[{l:"Umsatz",v:sumI,c:T.text},{l:"Ausgaben",v:sumA,c:T.textMuted},{l:"Gewinn",v:sumI-sumA,c:(sumI-sumA)>=0?T.text:T.danger}].map((x,i)=>(
        <div key={i}>
          <div style={{fontSize:10.5,color:T.textDim,marginBottom:2,letterSpacing:"0.05em",textTransform:"uppercase"}}>{x.l}</div>
          <div style={{color:x.c,fontWeight:500,fontSize:17,fontFamily:"'JetBrains Mono',monospace",letterSpacing:"-0.02em"}}>{euro(x.v)}</div>
        </div>))}
      <div style={{marginLeft:"auto",fontSize:11.5,color:T.textDim}}>{stufe}swerte · {data.length} {data.length===1?"Punkt":"Punkte"}</div>
    </div>

    <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:"auto",display:"block",overflow:"visible"}} onMouseLeave={()=>setHover(null)}>
      {yTicks.map((v,i)=>(<g key={"y"+i}>
        <line x1={L} y1={yB(v)} x2={W-R} y2={yB(v)} stroke={T.border} strokeWidth="1"/>
        <text x={L-8} y={yB(v)+3.5} textAnchor="end" fill={T.textDim} fontSize="10" fontFamily="'JetBrains Mono',monospace">{fmt(v)}</text>
      </g>))}

      {data.map((d,i)=>(<g key={"b"+i} opacity={hover===null||hover===i?1:0.45}>
        <rect x={xc(i)-bw-1} y={yB(d.income)} width={bw} height={Math.max(1,yB(0)-yB(d.income))} rx="2" fill={T.chart1}/>
        <rect x={xc(i)+1} y={yB(d.expense)} width={bw} height={Math.max(1,yB(0)-yB(d.expense))} rx="2" fill={T.chart2}/>
      </g>))}


      {data.map((d,i)=>((i%jeder===0 || i===data.length-1) && !(i%jeder!==0 && i>data.length-jeder)) ? (
        <text key={"x"+i} x={xc(i)} y={H-8} textAnchor="middle" fill={T.textDim} fontSize="10.5">{d.label}</text>
      ) : null)}

      {h && (()=>{
        // Beschrifteter Kasten, damit klar ist welche Zahl wozu gehoert.
        // Er springt auf die andere Seite, wenn rechts kein Platz mehr ist.
        const bW=200, bH=76, x0=xc(hover);
        const links = x0 + 14 + bW > W - R;
        const bx = Math.max(L, links ? x0 - 14 - bW : x0 + 14);
        const zeilen = [
          ["Umsatz", euro(h.income), T.chart1, T.text],
          ["Ausgaben", euro(h.expense), T.chart2, T.text],
          ["Gewinn", euro(h.income-h.expense), null, (h.income-h.expense)<0?T.danger:T.text],
        ];
        return (<g style={{pointerEvents:"none"}}>
          <line x1={x0} y1={PT} x2={x0} y2={PT+chB} stroke={T.borderStrong} strokeWidth="1"/>
          <rect x={bx} y={PT+4} width={bW} height={bH} rx="9" fill={T.bgCard} stroke={T.border} strokeWidth="1"/>
          <text x={bx+12} y={PT+22} fill={T.text} fontSize="11.5" fontWeight="600">{h.label}</text>
          {zeilen.map((z,i)=>(<g key={i}>
            {z[2] && <rect x={bx+12} y={PT+34+i*16} width={8} height={3} rx="1.5" fill={z[2]}/>}
            <text x={bx+24} y={PT+39+i*16} fill={T.textDim} fontSize="10.5">{z[0]}</text>
            <text x={bx+bW-12} y={PT+39+i*16} textAnchor="end" fill={z[3]} fontSize="10.5" fontFamily="'JetBrains Mono',monospace">{z[1]}</text>
          </g>))}
        </g>);
      })()}
      {data.map((d,i)=>(<rect key={"h"+i} x={L+xStep*i} y={PT} width={xStep} height={chB} fill="transparent" onMouseEnter={()=>setHover(i)}/>))}
    </svg>

    <div style={{display:"flex",gap:18,flexWrap:"wrap",marginTop:10,fontSize:11.5,color:T.textMuted}}>
      {[[T.chart1,"Umsatz"],[T.chart2,"Ausgaben"]].map(([c,l])=>(
        <span key={l} style={{display:"inline-flex",alignItems:"center",gap:6}}><i style={{width:13,height:3,borderRadius:2,background:c,display:"inline-block"}}/>{l}</span>))}
    </div>
  </div>);
};

// ─── Rechtliche Links (Impressum, Datenschutz, AGB) ───
const LegalLinks = ({style}) => (
  <div style={{display:"flex",justifyContent:"center",alignItems:"center",gap:14,flexWrap:"wrap",fontSize:11,...style}}>
    <a href="https://rf-media.de/impressum" target="_blank" rel="noopener noreferrer" style={{color:T.textDim,textDecoration:"none"}}>Impressum</a>
    <span style={{color:T.border}}>·</span>
    <a href="https://rf-media.de/datenschutz" target="_blank" rel="noopener noreferrer" style={{color:T.textDim,textDecoration:"none"}}>Datenschutz</a>
    <span style={{color:T.border}}>·</span>
    <a href="https://rf-media.de/agb" target="_blank" rel="noopener noreferrer" style={{color:T.textDim,textDecoration:"none"}}>AGB</a>
    <span style={{color:T.border}}>·</span>
    <a href="#" onClick={(e)=>{e.preventDefault(); if(window.CCM && window.CCM.openWidget) window.CCM.openWidget();}} style={{color:T.textDim,textDecoration:"none",cursor:"pointer"}}>Cookie-Einstellungen</a>
  </div>
);

// ─── LOGIN ───
const LoginScreen = ({onLogin}) => {
  const [u,setU]=useState(""); const [p,setP]=useState(""); const [err,setErr]=useState(""); const [loading,setLoading]=useState(false);
  const [mode,setMode]=useState("staff"); // "staff" = Mitarbeiter, "client" = Kunde
  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true); setErr("");
    const res = await api.post('login', {username:u, password:p});
    setLoading(false);
    if(res.user) { if(res.token) authToken.set(res.token); session.set({userId:res.user.id}); onLogin(res.user); }
    else if(res.error && res.error.indexOf('Fehlversuche')>=0) { setErr("Zu viele Fehlversuche. Bitte kurz warten."); }
    else { setErr("Benutzername oder Passwort falsch"); }
  };
  const isClient = mode==="client";
  return (<div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:T.bg,padding:16}}>
    <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:14,padding:"36px 28px",width:400,maxWidth:"100%"}}>
      <div style={{textAlign:"center",marginBottom:28}}>
        <img src="/logo.png" alt="R&F Media" style={{height:60,marginBottom:8,filter:T.logoFilter}}/>
        <div style={{color:T.textDim,fontSize:14}}>{isClient?"Kundenportal":"CRM System"}</div>
      </div>

      {/* Umschalter: Mitarbeiter / Kunde */}
      <div style={{display:"flex",gap:4,background:T.bgInput,padding:4,borderRadius:11,border:`1px solid ${T.border}`,marginBottom:24}}>
        <button type="button" onClick={()=>{setMode("staff");setErr("");}} style={{flex:1,padding:"9px 0",borderRadius:8,border:"none",fontSize:13.5,fontWeight:600,cursor:"pointer",background:!isClient?T.accent:"transparent",color:!isClient?"#fff":T.textDim,transition:"all 0.15s"}}>Mitarbeiter</button>
        <button type="button" onClick={()=>{setMode("client");setErr("");}} style={{flex:1,padding:"9px 0",borderRadius:8,border:"none",fontSize:13.5,fontWeight:600,cursor:"pointer",background:isClient?T.accent:"transparent",color:isClient?"#fff":T.textDim,transition:"all 0.15s"}}>Kunde</button>
      </div>

      {isClient && <div style={{background:T.accentGlow,border:`1px solid ${T.accent}33`,borderRadius:10,padding:"11px 14px",marginBottom:20,fontSize:13,color:T.text,lineHeight:1.5}}>
        Willkommen in deinem Kundenportal. Melde dich mit den Zugangsdaten an, die du von uns bekommen hast.
      </div>}

      <form onSubmit={submit} style={{display:"flex",flexDirection:"column",gap:16}}>
        <Input label="Benutzername" value={u} onChange={e=>setU(e.target.value)}/>
        <Input label="Passwort" type="password" value={p} onChange={e=>setP(e.target.value)}/>
        {err&&<div style={{color:T.danger,fontSize:13,textAlign:"center"}}>{err}</div>}
        <Button type="submit" style={{width:"100%",justifyContent:"center",marginTop:8,padding:"12px 20px"}}>{isClient?"Zum Kundenportal":"Anmelden"}</Button>
      </form>
      <div style={{textAlign:"center",marginTop:24,fontSize:11,color:T.textDim}}>R&F Media UG</div>
      <LegalLinks style={{marginTop:10}}/>
    </div>
  </div>);
};

// ─── SIDEBAR ───
const Sidebar = ({currentPage,setPage,user,onLogout,isOpen,setIsOpen,onUserUpdate,attendanceOn=true,leadsBlocked=false}) => {
  const ap=[{id:"dashboard",label:"Dashboard",icon:"dashboard"},{id:"leads",label:"Leads",icon:"leads"},{id:"email",label:"E-Mail",icon:"mail"},{id:"calls",label:"Anrufe",icon:"phone"},{id:"appointments",label:"Termine",icon:"calendar"},{id:"whiteboard",label:"Whiteboard",icon:"edit"},{id:"cutter",label:"Cutter",icon:"cut"},{id:"clients",label:"Kunden",icon:"team"},{id:"finances",label:"Finanzen",icon:"money"},{id:"sevdesk",label:"Buchhaltung",icon:"clipboard"},{id:"services",label:"Dienstleistungen",icon:"clipboard"},{id:"checklist",label:"Monatsplanung",icon:"list"},{id:"payroll",label:"Lohn & Gehalt",icon:"money"},{id:"team",label:"Team",icon:"team"}];
  const mp=[{id:"member-dashboard",label:"Mein Bereich",icon:"dashboard"},{id:"leads",label:"Meine Leads",icon:"leads"},{id:"email",label:"E-Mail",icon:"mail"},{id:"calls",label:"Anrufe",icon:"phone"},{id:"appointments",label:"Termine",icon:"calendar"},{id:"whiteboard",label:"Whiteboard",icon:"edit"}];
  const cp=[{id:"cutter-dashboard",label:"Mein Bereich",icon:"dashboard"},{id:"cutter-projects",label:"Meine Projekte",icon:"cut"},{id:"cutter-invoices",label:"Rechnungen",icon:"money"},{id:"cutter-goals",label:"Meine Ziele",icon:"chart"},{id:"whiteboard",label:"Whiteboard",icon:"edit"}];
  let pages=user.role==="admin"?ap:user.role==="cutter"?cp:mp;
  // Gesperrter Vertriebler: Leads komplett aus dem Menü entfernen
  if(leadsBlocked && user.role!=="admin") pages = pages.filter(p=>p.id!=="leads");
  const handleNav=(id)=>{setPage(id);setIsOpen(false);};

  const [showPw,setShowPw]=useState(false);
  const [pwForm,setPwForm]=useState({current:"",newPw:"",confirm:""});
  const [pwMsg,setPwMsg]=useState(null);

  const changePw=async()=>{
    setPwMsg(null);
    if(!pwForm.newPw||!pwForm.confirm){setPwMsg({type:"error",text:"Bitte alle Felder ausfüllen"});return;}
    if(pwForm.newPw.length<4){setPwMsg({type:"error",text:"Mindestens 4 Zeichen"});return;}
    if(pwForm.newPw!==pwForm.confirm){setPwMsg({type:"error",text:"Passwörter stimmen nicht überein"});return;}
    const res = await api.post('change-password', {userId:user.id, current:pwForm.current, newPassword:pwForm.newPw});
    if(res.error){setPwMsg({type:"error",text:res.error});return;}
    if(onUserUpdate)onUserUpdate({...user,pwChanged:true});
    setPwMsg({type:"success",text:"Passwort geändert!"});
    setPwForm({current:"",newPw:"",confirm:""});
    setTimeout(()=>{setShowPw(false);setPwMsg(null);},1500);
  };

  return (<>
    <div className={`sidebar-overlay${isOpen?" open":""}`} onClick={()=>setIsOpen(false)}/>
    <div className={`sidebar${isOpen?" open":""}`} style={{width:240,background:T.glassBgSide,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,borderRight:`1px solid ${T.glassBorder}`,display:"flex",flexDirection:"column",height:"100vh",position:"fixed",left:0,top:0,zIndex:100}}>
      <div style={{padding:"22px 20px 18px",display:"flex",alignItems:"center",justifyContent:"center"}}><img src="/logo.png" alt="R&F Media" style={{height:38,opacity:0.95,filter:T.logoFilter}}/></div>
      <nav style={{flex:1,padding:"8px 12px",display:"flex",flexDirection:"column",gap:2,overflowY:"auto"}}>
        {pages.map(pg=>{
          const active = currentPage===pg.id;
          return (<button key={pg.id} onClick={()=>handleNav(pg.id)} style={{position:"relative",display:"flex",alignItems:"center",gap:11,padding:"9px 12px",borderRadius:9,border:"none",background:active?T.accentGlow:"transparent",color:active?T.accent:T.textMuted,cursor:"pointer",fontSize:14,fontWeight:active?600:500,textAlign:"left",transition:"all 0.12s"}}
            onMouseEnter={e=>{if(!active){e.currentTarget.style.background=T.bgHover;e.currentTarget.style.color=T.text;}}}
            onMouseLeave={e=>{if(!active){e.currentTarget.style.background="transparent";e.currentTarget.style.color=T.textMuted;}}}>
            <Icon name={pg.icon} size={18}/>{pg.label}
          </button>);
        })}
      </nav>
      {attendanceOn && <div style={{padding:"0 16px"}}>
        <AttendanceWidget user={user}/>
      </div>}
      <div style={{padding:16,borderTop:`1px solid ${T.border}`}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
          <Avatar userId={user.id} name={user.name} size={36} editable={true}/>
          <div><div style={{fontSize:13,fontWeight:600}}>{user.name}</div><div style={{fontSize:11,color:T.textDim}}>{user.role==="admin"?"Admin":user.role==="cutter"?"Cutter":"Vertriebler"}</div></div>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:2}}>
          <button onClick={()=>handleNav("settings")} style={{display:"flex",alignItems:"center",gap:8,width:"100%",padding:"7px 12px",borderRadius:8,border:"none",background:currentPage==="settings"?T.accentGlow:"transparent",color:currentPage==="settings"?T.accent:T.textDim,cursor:"pointer",fontSize:12,fontWeight:currentPage==="settings"?600:400}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/></svg>
            Einstellungen
          </button>
          <button onClick={()=>{setShowPw(true);setPwMsg(null);setPwForm({current:"",newPw:"",confirm:""});}} style={{display:"flex",alignItems:"center",gap:8,width:"100%",padding:"7px 12px",borderRadius:8,border:"none",background:"transparent",color:T.textDim,cursor:"pointer",fontSize:12}}><Icon name="edit" size={14}/>Passwort ändern</button>
          <button onClick={()=>{const next=_theme==='dark'?'light':'dark';darstellungAnwenden(user&&user.id,next,_palette);}} style={{display:"flex",alignItems:"center",gap:8,width:"100%",padding:"7px 12px",borderRadius:8,border:"none",background:"transparent",color:T.textDim,cursor:"pointer",fontSize:12}}><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">{_theme==='dark'?<path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41L5.99 4.58zm12.37 12.37c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41l-1.06-1.06zm1.06-10.96c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06zM7.05 18.36c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06z"/>:<path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-.46-.04-.92-.1-1.36-.98 1.37-2.58 2.26-4.4 2.26-2.98 0-5.4-2.42-5.4-5.4 0-1.81.89-3.42 2.26-4.4-.44-.06-.9-.1-1.36-.1z"/>}</svg>{_theme==='dark'?'Heller Modus':'Dunkler Modus'}</button>
          <button onClick={onLogout} style={{display:"flex",alignItems:"center",gap:8,width:"100%",padding:"7px 12px",borderRadius:8,border:"none",background:"transparent",color:T.textDim,cursor:"pointer",fontSize:12}}><Icon name="logout" size={14}/>Abmelden</button>
        </div>
      </div>
    </div>
    <Modal open={showPw} onClose={()=>setShowPw(false)} title="Passwort ändern" width={400}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        {user.pwChanged&&<Input label="Aktuelles Passwort" type="password" value={pwForm.current} onChange={e=>setPwForm({...pwForm,current:e.target.value})} placeholder="Dein aktuelles Passwort"/>}
        <Input label="Neues Passwort" type="password" value={pwForm.newPw} onChange={e=>setPwForm({...pwForm,newPw:e.target.value})} placeholder="Neues Passwort wählen"/>
        <Input label="Passwort bestätigen" type="password" value={pwForm.confirm} onChange={e=>setPwForm({...pwForm,confirm:e.target.value})} placeholder="Nochmal eingeben"/>
        {pwMsg&&<div style={{fontSize:13,textAlign:"center",color:pwMsg.type==="error"?T.danger:T.success}}>{pwMsg.text}</div>}
        <Button icon="check" onClick={changePw} style={{marginTop:4}}>Passwort speichern</Button>
      </div>
    </Modal>
  </>);
};

// ─── MEMBER DASHBOARD ───
const MemberDashboard = ({user,reload,dv}) => {
  const [leadStats,setLeadStats]=useState({total:0,byStatus:{}});
  useEffect(()=>{api.get("lead-stats",`userId=${user.id}`).then(s=>{if(s.total!==undefined)setLeadStats(s);});},[dv]);
  const allLeads = {length:leadStats.total};
  // Calls direkt aus API laden — reaktiv auf dv
  const [allCalls,setAllCalls]=useState([]);
  useEffect(()=>{api.get("calls").then(c=>setAllCalls(c||[]));},[dv]);
  const calls = allCalls.filter(c=>c.userId===user.id);
  const allAppts = store.get(STORAGE_KEYS.appointments)||[];
  const myAppts = allAppts.filter(a=>a.userId===user.id);

  // Provision tracking
  const provKey = `crm_provisions_${user.id}`;
  const [provisions,setProvisions] = useState([]);
  useEffect(()=>{api.get("provisions","userId="+user.id).then(p=>setProvisions(p||[]));},[]);
  const saveProvisions = p => {setProvisions(p);};

  // Personal storage keys
  const goalsKey = `crm_goals_${user.id}`;
  const notesKey = `crm_notes_${user.id}`;
  const [goals,setGoals] = useState([]);
  const [notes,setNotes] = useState([]);
  useEffect(()=>{api.get("personal-goals","userId="+user.id).then(g=>setGoals(g||[]));api.get("personal-notes","userId="+user.id).then(n=>setNotes(n||[]));},[]);
  const [goalForm,setGoalForm] = useState({text:"",target:""});
  const [noteText,setNoteText] = useState("");

  const saveGoals = g => {setGoals(g);};
  const saveNotes = n => {setNotes(n);};

  // Mark appointment as showed up / no-show
  const markAppointment = async(apptId, showed) => {
    const appt = myAppts.find(a=>a.id===apptId);
    await api.post("provisions",{apptId,showed,userId:user.id,userName:user.name,leadCompany:appt?.leadCompany||"",date:appt?.date||""});
    const updated = await api.get("provisions","userId="+user.id);
    setProvisions(updated||[]);
  };

  // Stats
  const todayCalls = calls.filter(c=>new Date(c.date).toDateString()===new Date().toDateString()).length;
  const weekCalls = calls.filter(c=>{const d=new Date(c.date);const n=new Date();const s=new Date(n);s.setDate(n.getDate()-n.getDay()+1);s.setHours(0,0,0,0);return d>=s;}).length;
  const monthCalls = calls.filter(c=>monthKey(new Date(c.date))===monthKey(new Date())).length;
  const monthAppts = myAppts.filter(a=>monthKey(new Date(a.date))===monthKey(new Date()));
  const upcomingAppts = myAppts.filter(a=>new Date(a.date)>=new Date()).sort((a,b)=>new Date(a.date)-new Date(b.date));
  const pastAppts = myAppts.filter(a=>new Date(a.date)<new Date()).sort((a,b)=>new Date(b.date)-new Date(a.date));

  // Provision stats
  const monthProvisions = provisions.filter(p=>(p.date||"").startsWith(monthKey(new Date())));
  const monthShowed = monthProvisions.filter(p=>p.showed).length;
  const monthNoShow = monthProvisions.filter(p=>!p.showed).length;
  const monthProvisionTotal = monthShowed * 10;
  const allTimeShowed = provisions.filter(p=>p.showed).length;
  const allTimeProvision = allTimeShowed * 10;
  const showRate = monthProvisions.length > 0 ? Math.round((monthShowed / monthProvisions.length) * 100) : 0;

  const statusCounts = ALL_STATUSES.map(s=>({...s,count:leadStats.byStatus[s.id]||0}));

  // Provision chart data (last 6 months)
  const chartMonths = [];
  for(let i=5;i>=0;i--){const d=new Date();d.setMonth(d.getMonth()-i);chartMonths.push(monthKey(d));}
  const chartData = chartMonths.map(m=>{
    const mAppts = myAppts.filter(a=>(a.date||"").startsWith(m)).length;
    const mShowed = provisions.filter(p=>(p.date||"").startsWith(m)&&p.showed).length;
    const mNoShow = provisions.filter(p=>(p.date||"").startsWith(m)&&!p.showed).length;
    return {month:m,label:new Date(m+"-01").toLocaleDateString("de",{month:"short"}),total:mAppts,showed:mShowed,noShow:mNoShow};
  });
  const chartMax = Math.max(...chartData.map(d=>Math.max(d.total,d.showed+d.noShow)),1);

  // Goal/Note functions
  const addGoal = async() => {if(!goalForm.text)return;await api.post('personal-goals',{userId:user.id,text:goalForm.text,target:parseInt(goalForm.target)||0});const g=await api.get('personal-goals','userId='+user.id);setGoals(g||[]);setGoalForm({text:'',target:''});};
  const toggleGoal = async(id) => {const g=goals.find(x=>x.id===id);await api.put('personal-goals',{id,userId:user.id,current:g?.current||0,done:!g?.done});const u=await api.get('personal-goals','userId='+user.id);setGoals(u||[]);};
  const updateGoalProgress = async(id,val) => {await api.put('personal-goals',{id,userId:user.id,current:parseInt(val)||0,done:false});const u=await api.get('personal-goals','userId='+user.id);setGoals(u||[]);};
  const deleteGoal = async(id) => {await api.del('personal-goals',{id,userId:user.id});const u=await api.get('personal-goals','userId='+user.id);setGoals(u||[]);};
  const addNote = async() => {if(!noteText.trim())return;await api.post('personal-notes',{userId:user.id,text:noteText});const n=await api.get('personal-notes','userId='+user.id);setNotes(n||[]);setNoteText('');};
  const deleteNote = async(id) => {await api.del('personal-notes',{id,userId:user.id});const n=await api.get('personal-notes','userId='+user.id);setNotes(n||[]);};

  const currentMonth = new Date().toLocaleDateString("de",{month:"long",year:"numeric"});

  return (<div>
    {(()=>{ const hh=new Date().getHours(); const g=hh<11?"Morgen":hh<18?"Tag":"Abend";
      const dlong=new Date().toLocaleDateString("de",{weekday:"long",day:"numeric",month:"long"});
      const earningsData=chartMonths.map(m=>{const showed=provisions.filter(p=>(p.date||"").startsWith(m)&&p.showed).length;return {label:new Date(m+"-01").toLocaleDateString("de",{month:"short"}),value:showed*10};});
      return (<>
    <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:22}}><Avatar userId={user.id} name={user.name} size={48} editable={true}/><div><h1 style={{fontSize:24,fontWeight:700,letterSpacing:"-0.02em"}}>Guten {g}, {(user.name||"").split(" ")[0]}</h1><p style={{color:T.textDim,fontSize:13,marginTop:3}}>{dlong} · Dein persönlicher Bereich</p></div></div>

    {/* HERO: Provisions-Verlauf + Heldenzahlen */}
    <div className="dash-hero" style={{display:"grid",gridTemplateColumns:"1fr 300px",gap:16,marginBottom:16}}>
      <div style={{background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:12,padding:"18px 22px 10px",boxShadow:T.glassHi}}>
        <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:"0.1em",color:T.textDim,fontWeight:600,marginBottom:8}}>Provisions-Verdienst · 6 Monate</div>
        <LineChart color={T.success} data={earningsData} fmt={(v)=>v+" €"}/>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        <StatCard label="Provision (Monat)" value={fmtEuro(monthProvisionTotal)} hideable featured color={T.success}/>
        <StatCard label="Show-Rate (Monat)" value={showRate+" %"}/>
      </div>
    </div>

    {/* Kompakte Kennzahlen */}
    <div className="stats-row" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:22}}>
      <StatCard label="Alle Leads" value={allLeads.length} icon="leads"/>
      <StatCard label="Anrufe heute" value={todayCalls} icon="phone"/>
      <StatCard label="Anrufe (Monat)" value={monthCalls} icon="phone"/>
      <StatCard label="Termine (Monat)" value={monthAppts.length} icon="calendar"/>
    </div>
      </>);
    })()}
    <div style={{marginBottom:28}}>
      <TasksWidget user={user}/>
    </div>

    {/* Termin-Performance Chart */}
    <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:24,marginBottom:24}}>
      <h3 style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em",marginBottom:4,display:"flex",alignItems:"center",gap:8}}><Icon name="chart" size={16}/> <span>Termin-Performance</span></h3>
      <p style={{fontSize:12,color:T.textDim,marginBottom:16}}>Show-Rate: {showRate}% · Provision gesamt: {allTimeProvision} € ({allTimeShowed} Termine)</p>
      <div style={{display:"flex",alignItems:"end",gap:12,height:140}}>
        {chartData.map((d,i)=>(<div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
          <div style={{fontSize:10,color:T.textDim,fontFamily:"'JetBrains Mono',monospace"}}>{d.showed+d.noShow>0?d.showed:""}</div>
          <div style={{width:"100%",display:"flex",flexDirection:"column",justifyContent:"flex-end",height:100,gap:1}}>
            {d.noShow>0&&<div style={{height:`${(d.noShow/chartMax)*100}%`,background:T.danger,borderRadius:"4px 4px 0 0",minHeight:d.noShow?4:0}}/>}
            {d.showed>0&&<div style={{height:`${(d.showed/chartMax)*100}%`,background:T.success,borderRadius:d.noShow?"0 0 4px 4px":"4px",minHeight:d.showed?4:0}}/>}
            {d.showed===0&&d.noShow===0&&<div style={{height:`${(d.total/chartMax)*100}%`,background:`${T.border}`,borderRadius:4,minHeight:d.total?4:0}}/>}
          </div>
          <div style={{fontSize:10,color:T.textDim}}>{d.label}</div>
        </div>))}
      </div>
      <div style={{display:"flex",gap:16,marginTop:12,justifyContent:"center"}}>
        <div style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:10,height:10,borderRadius:2,background:T.success}}/><span style={{fontSize:11,color:T.textDim}}>Erschienen</span></div>
        <div style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:10,height:10,borderRadius:2,background:T.danger}}/><span style={{fontSize:11,color:T.textDim}}>Nicht erschienen</span></div>
        <div style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:10,height:10,borderRadius:2,background:T.border}}/><span style={{fontSize:11,color:T.textDim}}>Offen</span></div>
      </div>
    </div>

    {/* Past Appointments - Mark as showed/no-show */}
    <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:24,marginBottom:24}}>
      <h3 style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em",marginBottom:14,display:"flex",alignItems:"center",gap:8}}><Icon name="check" size={16}/> <span>Termine bewerten</span></h3>
      <p style={{fontSize:12,color:T.textDim,marginBottom:12}}>Markiere ob der Kunde zum Termin erschienen ist (= 10 € Provision)</p>
      {pastAppts.length===0?<div style={{color:T.textDim,fontSize:13}}>Keine vergangenen Termine</div>:
        pastAppts.slice(0,15).map(a=>{
          const prov = provisions.find(p=>p.apptId===a.id);
          return (<div key={a.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${T.border}`}}>
            <div>
              <div style={{fontSize:13,fontWeight:600}}>{a.title}</div>
              <div style={{fontSize:11,color:T.textDim}}>{a.leadCompany} · {fmtDate(a.date)} {a.time}</div>
            </div>
            <div style={{display:"flex",gap:6}}>
              {prov?<Badge color={prov.showed?T.success:T.danger}>{prov.showed?"Erschienen":"Nicht erschienen"}</Badge>:(<>
                <button onClick={()=>markAppointment(a.id,true)} style={{padding:"4px 12px",borderRadius:6,border:`1px solid ${T.success}`,background:`${T.success}22`,color:T.success,fontSize:12,fontWeight:600,cursor:"pointer"}}>Erschienen</button>
                <button onClick={()=>markAppointment(a.id,false)} style={{padding:"4px 12px",borderRadius:6,border:`1px solid ${T.danger}`,background:`${T.danger}22`,color:T.danger,fontSize:12,fontWeight:600,cursor:"pointer"}}>Nicht ersch.</button>
              </>)}
            </div>
          </div>);
        })}
    </div>

    {/* Lead Pipeline mini */}
    <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:24,marginBottom:24}}>
      <h3 style={{fontSize:16,fontWeight:600,marginBottom:16,letterSpacing:"-0.01em"}}>Lead-Pipeline</h3>
      <div style={{display:"flex",flexDirection:"column",gap:2}}>
        {(()=>{ const pmax=Math.max(...statusCounts.map(s=>s.count),1); return statusCounts.map(s=>(
          <div key={s.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:`1px solid ${T.border}`}}>
            <span style={{fontSize:13,fontWeight:500,flex:1,minWidth:0}}>{s.label}</span>
            <span style={{flex:2,height:7,background:T.bgHover,borderRadius:4,overflow:"hidden"}}><span style={{display:"block",height:"100%",width:`${Math.max(3,(s.count/pmax)*100)}%`,background:s.color,borderRadius:4,transition:"width 0.3s"}}/></span>
            <span style={{fontSize:14,fontWeight:600,minWidth:48,textAlign:"right",fontFamily:"'JetBrains Mono',monospace",color:s.color}}>{s.count.toLocaleString("de")}</span>
          </div>));
        })()}
      </div>
    </div>

    <div className="grid-2col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:24}}>
      {/* Goals */}
      <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:24}}>
        <h3 style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em",marginBottom:14,display:"flex",alignItems:"center",gap:8}}><Icon name="target" size={16}/> <span>Meine Ziele</span></h3>
        <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap",alignItems:"end"}}>
          <Input placeholder="z.B. 50 Anrufe pro Woche" value={goalForm.text} onChange={e=>setGoalForm({...goalForm,text:e.target.value})} style={{flex:1,minWidth:140}} />
          <Input placeholder="Ziel" type="number" value={goalForm.target} onChange={e=>setGoalForm({...goalForm,target:e.target.value})} style={{width:70}} />
          <Button icon="add" size="sm" onClick={addGoal}>Ziel</Button>
        </div>
        {goals.length===0?<div style={{color:T.textDim,fontSize:13,textAlign:"center",padding:16}}>Setze dir Ziele für diesen Monat!</div>:
          goals.map(g=>{
            const pct = g.target>0?Math.min(100,Math.round((g.current/g.target)*100)):g.done?100:0;
            return (<div key={g.id} style={{padding:"12px 0",borderBottom:`1px solid ${T.border}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <button onClick={()=>toggleGoal(g.id)} style={{width:20,height:20,borderRadius:6,border:`2px solid ${g.done?T.success:T.border}`,background:g.done?T.success:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:"#fff"}}>{g.done&&<Icon name="check" size={12}/>}</button>
                  <span style={{fontSize:13,fontWeight:600,textDecoration:g.done?"line-through":"none",opacity:g.done?0.5:1}}>{g.text}</span>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  {g.target>0&&<div style={{display:"flex",alignItems:"center",gap:4}}>
                    <input type="number" value={g.current} onChange={e=>updateGoalProgress(g.id,e.target.value)} style={{width:45,background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:4,padding:"2px 6px",color:T.text,fontSize:12,textAlign:"center",outline:"none"}}/>
                    <span style={{fontSize:12,color:T.textDim}}>/ {g.target}</span>
                  </div>}
                  <button onClick={()=>deleteGoal(g.id)} style={{background:"none",border:"none",color:T.textDim,cursor:"pointer"}}><Icon name="delete" size={14}/></button>
                </div>
              </div>
              {g.target>0&&<div style={{height:6,background:T.bgInput,borderRadius:3,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${pct}%`,background:pct>=100?T.success:pct>=50?T.warning:T.accent,borderRadius:3,transition:"width 0.3s"}}/>
              </div>}
            </div>);
          })}
      </div>

      {/* Private Notes */}
      <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:24}}>
        <h3 style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em",marginBottom:14,display:"flex",alignItems:"center",gap:8}}><Icon name="clipboard" size={16}/> <span>Meine Notizen</span></h3>
        <div style={{display:"flex",gap:8,marginBottom:16}}>
          <textarea value={noteText} onChange={e=>setNoteText(e.target.value)} placeholder="Notiz schreiben... (nur du siehst das)" onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();addNote();}}} style={{flex:1,background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 14px",color:T.text,fontSize:13,outline:"none",resize:"vertical",minHeight:60,fontFamily:"inherit"}}/>
        </div>
        <Button icon="add" size="sm" onClick={addNote} style={{marginBottom:16}}>Notiz speichern</Button>
        {notes.length===0?<div style={{color:T.textDim,fontSize:13,textAlign:"center",padding:16}}>Noch keine Notizen</div>:
          <div style={{maxHeight:300,overflow:"auto"}}>
            {notes.map(n=>(<div key={n.id} style={{padding:"10px 14px",background:T.bgInput,borderRadius:8,marginBottom:6,borderLeft:`3px solid ${T.info}`}}>
              <div style={{fontSize:13,whiteSpace:"pre-wrap"}}>{n.text}</div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:6}}>
                <div style={{fontSize:11,color:T.textDim}}>{new Date(n.date).toLocaleString("de")}</div>
                <button onClick={()=>deleteNote(n.id)} style={{background:"none",border:"none",color:T.textDim,cursor:"pointer"}}><Icon name="delete" size={13}/></button>
              </div>
            </div>))}
          </div>}
      </div>
    </div>

    {/* Upcoming Appointments */}
    <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:24}}>
      <h3 style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em",marginBottom:14,display:"flex",alignItems:"center",gap:8}}><Icon name="calendar" size={16}/> <span>Nächste Termine</span></h3>
      {upcomingAppts.length===0?<div style={{color:T.textDim,fontSize:13}}>Keine anstehenden Termine</div>:
        upcomingAppts.slice(0,8).map(a=>(<div key={a.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${T.border}`}}>
          <div><div style={{fontSize:13,fontWeight:600}}>{a.title}</div><div style={{fontSize:11,color:T.textDim}}>{a.leadCompany}</div></div>
          <div style={{fontSize:13,fontWeight:600,fontFamily:"'JetBrains Mono',monospace",color:T.accent}}>{fmtDate(a.date)} {a.time}</div>
        </div>))}
    </div>

    {/* Meine Verträge */}
    <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:24,marginTop:24}}>
      <h3 style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em",marginBottom:14,display:"flex",alignItems:"center",gap:8}}><Icon name="clipboard" size={16}/> <span>Meine Verträge</span></h3>
      <ContractsManager targetUserId={user.id} requester={user}/>
    </div>
  </div>);
};

// ─── DASHBOARD ───

// Umsatz-Auswertung mit Jahresfilter
const RevenueAnalysis = ({finances}) => {
  const [year,setYear] = useState(new Date().getFullYear());

  // Alle Jahre aus den Finanzdaten extrahieren
  const availableYears = (()=>{
    const ys = new Set();
    finances.forEach(f=>{const m=(f.date||"").slice(0,4);if(m&&m.length===4)ys.add(parseInt(m));});
    ys.add(2026); // Standard immer mit dabei
    ys.add(new Date().getFullYear()); // Aktuelles Jahr immer mit dabei
    return Array.from(ys).sort((a,b)=>b-a);
  })();

  // Aggregation pro Monat (nur Einnahmen) — gefiltert nach Jahr
  const byMonth = {};
  finances.forEach(f=>{
    const m=(f.date||"").slice(0,7);
    if(!m||m.length<7) return;
    if(parseInt(m.slice(0,4))!==year) return;
    if(!byMonth[m]) byMonth[m]=0;
    if(f.type==="income") byMonth[m]+=Number(f.amount)||0;
  });
  const months = Object.keys(byMonth).sort();

  // Aktueller Monat (Trend nur sinnvoll wenn das gewählte Jahr das aktuelle Jahr ist)
  const today = new Date();
  const isCurrentYear = year === today.getFullYear();
  const cm = monthKey(today);
  const lm = (()=>{const d=new Date();d.setMonth(d.getMonth()-1);return monthKey(d);})();
  const curRev = isCurrentYear ? (byMonth[cm]||0) : 0;
  const lastRev = isCurrentYear ? (byMonth[lm]||0) : 0;
  const trend = lastRev!==0 ? Math.round(((curRev-lastRev)/Math.abs(lastRev))*100) : 0;

  // Bester / Schwächster / Durchschnitt im gewählten Jahr
  const revenues = months.map(m=>({m,r:byMonth[m]}));
  const best = revenues.reduce((a,b)=>b.r>a.r?b:a,{m:"",r:-Infinity});
  const worst = revenues.reduce((a,b)=>b.r<a.r?b:a,{m:"",r:Infinity});
  const avgRev = revenues.length>0 ? revenues.reduce((s,x)=>s+x.r,0)/revenues.length : 0;
  const yearTotal = revenues.reduce((s,x)=>s+x.r,0);
  const fmtM = m=>m?new Date(m+"-01").toLocaleDateString("de",{month:"short"}):"-";

  return (<div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:24,display:"flex",flexDirection:"column"}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,gap:8}}>
      <h3 style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em"}}>Umsatz-Auswertung</h3>
      <select value={year} onChange={e=>setYear(parseInt(e.target.value))} style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:6,padding:"4px 8px",color:T.text,fontSize:12,outline:"none",cursor:"pointer"}}>
        {availableYears.map(y=>(<option key={y} value={y}>{y}</option>))}
      </select>
    </div>
    <div style={{display:"flex",flexDirection:"column",gap:14,flex:1}}>
      {isCurrentYear ? (
        <div>
          <div style={{fontSize:11,color:T.textDim,textTransform:"uppercase",fontWeight:600,letterSpacing:0.4,marginBottom:4}}>Aktueller Monat</div>
          <div style={{fontSize:22,fontWeight:800,fontFamily:"'JetBrains Mono',monospace",color:T.success}}>{Math.round(curRev).toLocaleString("de")} €</div>
          {trend!==0&&<div style={{fontSize:11,color:trend>=0?T.success:T.danger,marginTop:2}}>{trend>=0?"▲":"▼"} {Math.abs(trend)}% vs. Vormonat</div>}
        </div>
      ) : (
        <div>
          <div style={{fontSize:11,color:T.textDim,textTransform:"uppercase",fontWeight:600,letterSpacing:0.4,marginBottom:4}}>Jahresumsatz {year}</div>
          <div style={{fontSize:22,fontWeight:800,fontFamily:"'JetBrains Mono',monospace",color:T.success}}>{Math.round(yearTotal).toLocaleString("de")} €</div>
        </div>
      )}
      <div style={{height:1,background:T.border}}/>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
        <div>
          <div style={{fontSize:11,color:T.textDim,textTransform:"uppercase",fontWeight:600,letterSpacing:0.4}}>Bester Monat</div>
          <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>{fmtM(best.m)} {year}</div>
        </div>
        <div style={{fontSize:14,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:T.success}}>{best.r>-Infinity?Math.round(best.r).toLocaleString("de")+" €":"-"}</div>
      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
        <div>
          <div style={{fontSize:11,color:T.textDim,textTransform:"uppercase",fontWeight:600,letterSpacing:0.4}}>Schwächster Monat</div>
          <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>{fmtM(worst.m)} {year}</div>
        </div>
        <div style={{fontSize:14,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:T.textMuted}}>{worst.r<Infinity?Math.round(worst.r).toLocaleString("de")+" €":"-"}</div>
      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
        <div>
          <div style={{fontSize:11,color:T.textDim,textTransform:"uppercase",fontWeight:600,letterSpacing:0.4}}>Ø Umsatz</div>
          <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>{months.length} Monate</div>
        </div>
        <div style={{fontSize:14,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:T.success}}>{Math.round(avgRev).toLocaleString("de")} €</div>
      </div>
    </div>
  </div>);
};

// ─── WOCHENPLAN (fester Tagesablauf Mo–Fr) ───
const DAY_SCHEDULE = [
  {from:"09:00", to:"09:30", title:"Tagesplanung", color:"info"},
  {from:"09:30", to:"13:30", title:"Vertrieb", note:"bis 12 Uhr Akquise, danach DC's & SC's", color:"accent"},
  {from:"13:30", to:"14:15", title:"Pause", note:"45 Min", color:"muted"},
  {from:"14:15", to:"17:45", title:"Fulfillment / DC's & SC's", note:"Janik Call 17:00 mitnehmen", color:"success"},
  {from:"17:30", to:"17:45", title:"Tagesauswertung", note:"kurz", color:"warning"},
];
const WEEKDAYS = ["Mo","Di","Mi","Do","Fr"];

const WeekScheduleCard = () => {
  // Aktueller Wochentag (0=So..6=Sa) → unser Index Mo=0..Fr=4, sonst -1 (Wochenende)
  const jsDay = new Date().getDay();
  const todayIdx = jsDay>=1 && jsDay<=5 ? jsDay-1 : -1;
  const [selectedDay,setSelectedDay] = useState(todayIdx>=0?todayIdx:0);
  const colorOf = (c) => c==="accent"?T.accent:c==="success"?T.success:c==="warning"?T.warning:c==="info"?T.info:T.textDim;
  const isWeekend = todayIdx===-1;

  return (<div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,padding:24,boxShadow:T.shadow,marginBottom:24}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18,flexWrap:"wrap",gap:10}}>
      <div>
        <h3 style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em"}}>Tagesplan</h3>
        <div style={{fontSize:13,color:T.textMuted,marginTop:3}}>Arbeitstag 9:00 bis ca. 17:45 Uhr, 45 Min Pause</div>
      </div>
      {/* Wochentag-Wähler */}
      <div style={{display:"flex",gap:3,background:T.bgInput,padding:4,borderRadius:10,border:`1px solid ${T.border}`}}>
        {WEEKDAYS.map((d,i)=>(
          <button key={d} onClick={()=>setSelectedDay(i)} style={{padding:"6px 13px",borderRadius:7,border:"none",fontSize:13,fontWeight:600,background:selectedDay===i?T.accent:"transparent",color:selectedDay===i?"#fff":(i===todayIdx?T.accent:T.textDim),cursor:"pointer",transition:"all 0.12s",position:"relative"}}>
            {d}
            {i===todayIdx && selectedDay!==i && <span style={{position:"absolute",bottom:2,left:"50%",transform:"translateX(-50%)",width:4,height:4,borderRadius:"50%",background:T.accent}}/>}
          </button>
        ))}
      </div>
    </div>

    {isWeekend && <div style={{fontSize:13,color:T.textDim,background:T.bgInput,borderRadius:10,padding:"10px 14px",marginBottom:16}}>Heute ist Wochenende. Der Tagesplan gilt von Montag bis Freitag.</div>}

    {/* Zeitstrahl */}
    <div style={{display:"flex",flexDirection:"column",gap:0}}>
      {DAY_SCHEDULE.map((block,i)=>{
        const col = colorOf(block.color);
        return (<div key={i} style={{display:"flex",gap:14,alignItems:"stretch"}}>
          {/* Zeit-Spalte */}
          <div style={{minWidth:104,flexShrink:0,textAlign:"right",paddingTop:12}}>
            <div style={{fontSize:13,fontWeight:600,fontFamily:"'JetBrains Mono',monospace",color:T.text}}>{block.from}</div>
            <div style={{fontSize:11,color:T.textDim,fontFamily:"'JetBrains Mono',monospace"}}>{block.to}</div>
          </div>
          {/* Linie + Punkt */}
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",flexShrink:0}}>
            <div style={{width:13,height:13,borderRadius:"50%",background:col,marginTop:13,flexShrink:0,boxShadow:`0 0 0 4px ${col}22`}}/>
            {i<DAY_SCHEDULE.length-1 && <div style={{width:2,flex:1,background:T.border,minHeight:24}}/>}
          </div>
          {/* Inhalt */}
          <div style={{flex:1,paddingBottom:i<DAY_SCHEDULE.length-1?16:0,paddingTop:8}}>
            <div style={{background:T.bgInput,border:`1px solid ${T.border}`,borderLeft:`3px solid ${col}`,borderRadius:10,padding:"10px 14px"}}>
              <div style={{fontSize:14,fontWeight:600,color:T.text}}>{block.title}</div>
              {block.note && <div style={{fontSize:12,color:T.textMuted,marginTop:2}}>{block.note}</div>}
            </div>
          </div>
        </div>);
      })}
    </div>
  </div>);
};

// ═══════════════════════════════════════
// GEMEINSAME NOTIZEN (Apple-Notizen-Stil) — fürs Dashboard
// Liste links (Titel + erste Zeile), Editor rechts, Einladen pro Notiz.
// ═══════════════════════════════════════
const NotesWidget = ({user}) => {
  const [notes,setNotes] = useState([]);
  const [loading,setLoading] = useState(true);
  const [activeId,setActiveId] = useState(null);
  const [title,setTitle] = useState("");
  const [bodyText,setBodyText] = useState("");
  const [showShare,setShowShare] = useState(false);
  const [shareMembers,setShareMembers] = useState([]);
  const [allUsers,setAllUsers] = useState([]);
  const saveTimer = useRef(null);
  const lastLoaded = useRef(null);

  const load = async (keepActive=true) => {
    const d = await api.get("notes", `userId=${user.id}`);
    const list = Array.isArray(d)?d:[];
    setNotes(list);
    setLoading(false);
    if(!keepActive && list.length>0 && !activeId){ openNote(list[0]); }
    return list;
  };
  useEffect(()=>{ load(false); }, [user.id]);

  // Sanfter Auto-Refresh alle 8s, damit man Änderungen anderer sieht (ohne den eigenen Editor zu stören)
  useEffect(()=>{
    const iv = setInterval(async ()=>{
      const d = await api.get("notes", `userId=${user.id}`);
      if(Array.isArray(d)) setNotes(prev=>{
        // nur Liste aktualisieren, aktive Notiz im Editor nicht überschreiben während man tippt
        return d;
      });
    }, 8000);
    return ()=>clearInterval(iv);
  }, [user.id]);

  const activeNote = notes.find(n=>n.id===activeId);

  const openNote = (n) => {
    setActiveId(n.id);
    setTitle(n.title||"");
    setBodyText(n.body||"");
  };

  const createNote = async () => {
    const r = await api.post("notes", {userId:user.id, title:"", body:"", visibility:"private"});
    if(r.id){
      const list = await load();
      const fresh = list.find(n=>n.id===r.id);
      if(fresh) openNote(fresh);
      else { setActiveId(r.id); setTitle(""); setBodyText(""); }
    }
  };

  // Editor-Änderungen mit kurzem Debounce speichern
  const scheduleSave = (newTitle,newBody) => {
    if(saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async ()=>{
      await api.put("notes", {userId:user.id, id:activeId, title:newTitle, body:newBody});
      // Liste lokal aktualisieren (Titel/Vorschau)
      setNotes(prev=>prev.map(n=>n.id===activeId?{...n,title:newTitle,body:newBody,updatedAt:new Date().toISOString()}:n));
    }, 700);
  };
  const onTitle = (v) => { setTitle(v); scheduleSave(v, bodyText); };
  const onBody = (v) => { setBodyText(v); scheduleSave(title, v); };

  const delNote = async () => {
    if(!activeNote) return;
    if(!confirm("Diese Notiz wirklich löschen?")) return;
    await api.del("notes", {userId:user.id, id:activeId});
    setActiveId(null); setTitle(""); setBodyText("");
    load();
  };

  const changeVisibility = async (vis) => {
    await api.put("notes", {userId:user.id, id:activeId, visibility:vis});
    setNotes(prev=>prev.map(n=>n.id===activeId?{...n,visibility:vis}:n));
    if(vis==="shared") openShareModal();
  };

  const openShareModal = async () => {
    const [members, users] = await Promise.all([
      api.get("note-shares", `userId=${user.id}&noteId=${activeId}`),
      api.get("users",""),
    ]);
    setShareMembers(Array.isArray(members)?members:[]);
    setAllUsers((Array.isArray(users)?users:[]).filter(u=>u.id!==user.id));
    setShowShare(true);
  };
  const inviteToNote = async (targetId) => {
    await api.post("note-shares", {userId:user.id, noteId:activeId, targetUserId:targetId});
    const members = await api.get("note-shares", `userId=${user.id}&noteId=${activeId}`);
    setShareMembers(Array.isArray(members)?members:[]);
    setNotes(prev=>prev.map(n=>n.id===activeId?{...n,shareCount:(n.shareCount||0)+1}:n));
  };
  const removeFromNote = async (targetId) => {
    await api.del("note-shares", {userId:user.id, noteId:activeId, targetUserId:targetId});
    const members = await api.get("note-shares", `userId=${user.id}&noteId=${activeId}`);
    setShareMembers(Array.isArray(members)?members:[]);
  };

  const preview = (n) => {
    const firstLine = (n.body||"").split("\n").find(l=>l.trim()) || "";
    return firstLine.slice(0,60) || "Keine weiteren Inhalte";
  };
  const visLabel = {private:"Privat", all:"Ganzes Team", shared:"Eingeladene"};
  const visIcon = (v) => v==="all"
    ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
    : v==="shared"
    ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
    : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;

  return (<div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,overflow:"hidden",display:"flex",flexDirection:"column",minHeight:360}}>
    <div style={{padding:"16px 20px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
      <div style={{display:"flex",alignItems:"center",gap:9}}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        <h3 style={{fontSize:16,fontWeight:700}}>Gemeinsame Notizen</h3>
      </div>
      <Button size="sm" icon="add" onClick={createNote}>Neue Notiz</Button>
    </div>

    <div className="notes-wrap" style={{display:"flex",flex:1,minHeight:300}}>
      {/* Liste */}
      <div className="notes-list" style={{width:260,borderRight:`1px solid ${T.border}`,overflowY:"auto",flexShrink:0}}>
        {loading
          ? <div style={{padding:20,color:T.textDim,fontSize:13}}>Lädt...</div>
          : notes.length===0
            ? <div style={{padding:"30px 20px",color:T.textDim,fontSize:13,textAlign:"center"}}>Noch keine Notizen. Leg die erste an.</div>
            : notes.map(n=>(
                <button key={n.id} onClick={()=>openNote(n)} style={{display:"block",width:"100%",textAlign:"left",padding:"12px 16px",background:n.id===activeId?T.bgInput:"transparent",border:"none",borderBottom:`1px solid ${T.border}`,cursor:"pointer"}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                    <span style={{fontSize:14,fontWeight:600,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{n.title||"Ohne Titel"}</span>
                    <span style={{color:T.textDim,flexShrink:0}} title={visLabel[n.visibility]}>{visIcon(n.visibility)}</span>
                  </div>
                  <div style={{fontSize:12,color:T.textDim,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{preview(n)}</div>
                  {!n.isOwner && <div style={{fontSize:11,color:T.textMuted,marginTop:3}}>von {n.ownerName}</div>}
                </button>
              ))}
      </div>

      {/* Editor */}
      <div className="notes-editor" style={{flex:1,display:"flex",flexDirection:"column",minWidth:0}}>
        {!activeNote
          ? <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:T.textDim,fontSize:14,padding:20,textAlign:"center"}}>Wähle links eine Notiz oder leg eine neue an.</div>
          : <>
              <div style={{padding:"12px 18px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  {activeNote.isOwner ? (
                    <select value={activeNote.visibility} onChange={e=>changeVisibility(e.target.value)} style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"5px 9px",color:T.text,fontSize:12,cursor:"pointer"}}>
                      <option value="private">Privat</option>
                      <option value="all">Ganzes Team</option>
                      <option value="shared">Einzelne einladen</option>
                    </select>
                  ) : (
                    <span style={{fontSize:12,color:T.textDim,display:"inline-flex",alignItems:"center",gap:5}}>{visIcon(activeNote.visibility)} {visLabel[activeNote.visibility]} · von {activeNote.ownerName}</span>
                  )}
                  {activeNote.isOwner && activeNote.visibility==="shared" && <button onClick={openShareModal} style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"5px 10px",color:T.text,fontSize:12,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:5}}><Icon name="team" size={13}/> Einladen{activeNote.shareCount>0?` (${activeNote.shareCount})`:""}</button>}
                </div>
                {activeNote.isOwner && <button onClick={delNote} title="Löschen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4}}><Icon name="delete" size={16}/></button>}
              </div>
              <input value={title} onChange={e=>onTitle(e.target.value)} placeholder="Titel" style={{padding:"14px 18px 6px",background:"transparent",border:"none",outline:"none",color:T.text,fontSize:18,fontWeight:700}}/>
              <textarea value={bodyText} onChange={e=>onBody(e.target.value)} placeholder="Schreib hier..." style={{flex:1,minHeight:160,padding:"4px 18px 18px",background:"transparent",border:"none",outline:"none",color:T.text,fontSize:14,lineHeight:1.6,resize:"none",fontFamily:"inherit"}}/>
            </>}
      </div>
    </div>

    {/* Einladen-Modal */}
    <Modal open={showShare} onClose={()=>setShowShare(false)} title="Zur Notiz einladen" width={460}>
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:T.textDim,textTransform:"uppercase",letterSpacing:0.4,marginBottom:10}}>Eingeladen ({shareMembers.length})</div>
          {shareMembers.length===0
            ? <div style={{fontSize:13,color:T.textDim}}>Noch niemand eingeladen.</div>
            : <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {shareMembers.map(m=>(<div key={m.user_id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8}}>
                  <div style={{flex:1,fontSize:13,fontWeight:600}}>{m.name}</div>
                  <button onClick={()=>removeFromNote(m.user_id)} style={{background:"none",border:`1px solid ${T.border}`,borderRadius:6,padding:"4px 10px",color:T.danger,cursor:"pointer",fontSize:11,fontWeight:600}}>Entfernen</button>
                </div>))}
              </div>}
        </div>
        <div style={{height:1,background:T.border}}/>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:T.textDim,textTransform:"uppercase",letterSpacing:0.4,marginBottom:10}}>Person einladen</div>
          {(() => {
            const already = new Set(shareMembers.map(m=>m.user_id));
            const invitable = allUsers.filter(u=>!already.has(u.id));
            if(invitable.length===0) return <div style={{fontSize:13,color:T.textDim}}>Alle verfügbaren Personen sind eingeladen.</div>;
            return (<div style={{display:"flex",flexDirection:"column",gap:6}}>
              {invitable.map(u=>(<div key={u.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8}}>
                <div style={{flex:1,fontSize:13,fontWeight:600}}>{u.name}</div>
                <Button size="sm" variant="secondary" icon="add" onClick={()=>inviteToNote(u.id)}>Einladen</Button>
              </div>))}
            </div>);
          })()}
        </div>
      </div>
    </Modal>
  </div>);
};

// ═══════════════════════════════════════
// TAGES- & WOCHEN-CHECKLISTE (fürs Dashboard)
// Umschalter Tag/Woche, abhaken, Sichtbarkeit (privat/alle/einzelne) wie beim Whiteboard
// ═══════════════════════════════════════
const TasksWidget = ({user}) => {
  const [scope,setScope] = useState("day");   // day | week
  const [tasks,setTasks] = useState([]);
  const [loading,setLoading] = useState(true);
  const [newText,setNewText] = useState("");
  const [newVis,setNewVis] = useState("private");
  const [shareFor,setShareFor] = useState(null);  // Aufgabe, für die das Einladen offen ist
  const [shareMembers,setShareMembers] = useState([]);
  const [allUsers,setAllUsers] = useState([]);
  const [confirmDel,setConfirmDel] = useState(null);

  const load = async (sc=scope) => {
    setLoading(true);
    try { const d = await api.get("task-items", `userId=${user.id}&scope=${sc}`); setTasks(Array.isArray(d)?d:[]); } catch(e){}
    setLoading(false);
  };
  useEffect(()=>{ load(scope); }, [scope, user.id]);
  // sanfter Abgleich alle 12s, damit geteilte Aufgaben aktuell bleiben
  useEffect(()=>{
    const iv = setInterval(async ()=>{
      try { const d = await api.get("task-items", `userId=${user.id}&scope=${scope}`); if(Array.isArray(d)) setTasks(d); } catch(e){}
    }, 12000);
    return ()=>clearInterval(iv);
  }, [scope, user.id]);

  const addTask = async () => {
    if(!newText.trim()) return;
    await api.post("task-items", {userId:user.id, scope, text:newText.trim(), visibility:newVis, sortOrder:tasks.length});
    setNewText(""); load();
  };
  const toggle = async (t) => {
    setTasks(prev=>prev.map(x=>x.id===t.id?{...x,done:!x.done}:x));
    await api.put("task-items", {userId:user.id, id:t.id, done:!t.done});
    load();
  };
  const changeVis = async (t, vis) => {
    await api.put("task-items", {userId:user.id, id:t.id, visibility:vis});
    setTasks(prev=>prev.map(x=>x.id===t.id?{...x,visibility:vis}:x));
    if(vis==="shared") openShare(t);
  };
  const doDelete = async () => {
    if(!confirmDel) return;
    await api.del("task-items", {userId:user.id, id:confirmDel.id});
    setConfirmDel(null); load();
  };
  const openShare = async (t) => {
    const [members, users] = await Promise.all([
      api.get("task-shares", `userId=${user.id}&taskId=${t.id}`),
      api.get("users",""),
    ]);
    setShareMembers(Array.isArray(members)?members:[]);
    setAllUsers((Array.isArray(users)?users:[]).filter(u=>u.id!==user.id && u.role!=="client"));
    setShareFor(t);
  };
  const invite = async (targetId) => {
    await api.post("task-shares", {userId:user.id, taskId:shareFor.id, targetUserId:targetId});
    const m = await api.get("task-shares", `userId=${user.id}&taskId=${shareFor.id}`);
    setShareMembers(Array.isArray(m)?m:[]);
  };
  const uninvite = async (targetId) => {
    await api.del("task-shares", {userId:user.id, taskId:shareFor.id, targetUserId:targetId});
    const m = await api.get("task-shares", `userId=${user.id}&taskId=${shareFor.id}`);
    setShareMembers(Array.isArray(m)?m:[]);
  };

  const open = tasks.filter(t=>!t.done);
  const done = tasks.filter(t=>t.done);
  const visIcon = (v) => v==="all"
    ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
    : v==="shared"
    ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
    : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
  const visLabel = {private:"Privat", all:"Ganzes Team", shared:"Eingeladene"};

  const TaskRow = ({t}) => (
    <div style={{display:"flex",alignItems:"flex-start",gap:10,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
      <button onClick={()=>toggle(t)} title={t.done?"Wieder offen":"Erledigt"} style={{flexShrink:0,marginTop:1,width:22,height:22,borderRadius:6,border:`2px solid ${t.done?T.success:T.borderStrong}`,background:t.done?T.success:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
        {t.done&&<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
      </button>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:14,lineHeight:1.4,color:t.done?T.textDim:T.text,textDecoration:t.done?"line-through":"none"}}>{t.text}</div>
        <div style={{display:"flex",alignItems:"center",gap:6,marginTop:3,flexWrap:"wrap"}}>
          {t.isOwner ? (
            <select value={t.visibility} onChange={e=>changeVis(t,e.target.value)} style={{background:"transparent",border:"none",color:T.textDim,fontSize:11,cursor:"pointer",padding:0}}>
              <option value="private">Privat</option>
              <option value="all">Ganzes Team</option>
              <option value="shared">Einzelne einladen</option>
            </select>
          ) : (
            <span style={{fontSize:11,color:T.textDim,display:"inline-flex",alignItems:"center",gap:4}}>{visIcon(t.visibility)} von {t.ownerName}</span>
          )}
          {t.isOwner && t.visibility==="shared" && <button onClick={()=>openShare(t)} style={{background:"none",border:"none",color:T.info,fontSize:11,cursor:"pointer",padding:0,display:"inline-flex",alignItems:"center",gap:3}}><Icon name="team" size={11}/> Einladen{t.shareCount>0?` (${t.shareCount})`:""}</button>}
        </div>
      </div>
      {t.isOwner && <button onClick={()=>setConfirmDel({id:t.id,text:t.text})} title="Löschen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:3,flexShrink:0}}><Icon name="delete" size={14}/></button>}
    </div>
  );

  return (<div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,overflow:"hidden"}}>
    <div style={{padding:"16px 20px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
      <div style={{display:"flex",alignItems:"center",gap:9}}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
        <h3 style={{fontSize:16,fontWeight:700}}>Checkliste</h3>
      </div>
      {/* Umschalter Tag / Woche */}
      <div style={{display:"flex",background:T.bgInput,borderRadius:9,padding:3,border:`1px solid ${T.border}`}}>
        {[["day","Tag"],["week","Woche"]].map(([v,l])=>(
          <button key={v} onClick={()=>setScope(v)} style={{padding:"6px 16px",borderRadius:7,border:"none",background:scope===v?T.accent:"transparent",color:scope===v?"#fff":T.textMuted,fontSize:13,fontWeight:600,cursor:"pointer"}}>{l}</button>
        ))}
      </div>
    </div>

    <div style={{padding:"16px 20px"}}>
      {/* Neue Aufgabe */}
      <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
        <input value={newText} onChange={e=>setNewText(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")addTask();}} placeholder={scope==="day"?"Neue Tagesaufgabe...":"Neue Wochenaufgabe..."} style={{flex:1,minWidth:180,background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 13px",color:T.text,fontSize:14,outline:"none"}}/>
        <select value={newVis} onChange={e=>setNewVis(e.target.value)} title="Sichtbarkeit" style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 12px",color:T.text,fontSize:13,cursor:"pointer"}}>
          <option value="private">Privat</option>
          <option value="all">Ganzes Team</option>
          <option value="shared">Einzelne einladen</option>
        </select>
        <Button icon="add" onClick={addTask}>Hinzufügen</Button>
      </div>

      {loading ? <div style={{color:T.textDim,fontSize:13,padding:10}}>Lädt...</div> :
       tasks.length===0 ? <div style={{color:T.textDim,fontSize:14,padding:"24px",textAlign:"center"}}>{scope==="day"?"Noch keine Tagesaufgaben. Schreib die erste rein.":"Noch keine Wochenaufgaben. Schreib die erste rein."}</div> :
       <div>
        {open.map(t=><TaskRow key={t.id} t={t}/>)}
        {done.length>0 && <div style={{marginTop:14}}>
          <div style={{fontSize:11,fontWeight:600,color:T.textDim,textTransform:"uppercase",letterSpacing:0.4,marginBottom:4}}>Erledigt ({done.length})</div>
          {done.map(t=><TaskRow key={t.id} t={t}/>)}
        </div>}
       </div>}
    </div>

    {/* Einladen-Modal */}
    <Modal open={!!shareFor} onClose={()=>setShareFor(null)} title="Zur Aufgabe einladen" width={460}>
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:T.textDim,textTransform:"uppercase",letterSpacing:0.4,marginBottom:10}}>Eingeladen ({shareMembers.length})</div>
          {shareMembers.length===0 ? <div style={{fontSize:13,color:T.textDim}}>Noch niemand eingeladen.</div> :
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {shareMembers.map(m=>(<div key={m.user_id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8}}>
                <div style={{flex:1,fontSize:13,fontWeight:600}}>{m.name}</div>
                <button onClick={()=>uninvite(m.user_id)} style={{background:"none",border:`1px solid ${T.border}`,borderRadius:6,padding:"4px 10px",color:T.danger,cursor:"pointer",fontSize:11,fontWeight:600}}>Entfernen</button>
              </div>))}
            </div>}
        </div>
        <div style={{height:1,background:T.border}}/>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:T.textDim,textTransform:"uppercase",letterSpacing:0.4,marginBottom:10}}>Person einladen</div>
          {(() => {
            const already = new Set(shareMembers.map(m=>m.user_id));
            const inv = allUsers.filter(u=>!already.has(u.id));
            if(inv.length===0) return <div style={{fontSize:13,color:T.textDim}}>Alle verfügbaren Personen sind eingeladen.</div>;
            return (<div style={{display:"flex",flexDirection:"column",gap:6}}>
              {inv.map(u=>(<div key={u.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8}}>
                <div style={{flex:1,fontSize:13,fontWeight:600}}>{u.name}</div>
                <Button size="sm" variant="secondary" icon="add" onClick={()=>invite(u.id)}>Einladen</Button>
              </div>))}
            </div>);
          })()}
        </div>
      </div>
    </Modal>

    {/* Löschen mit Bestätigung */}
    <Modal open={!!confirmDel} onClose={()=>setConfirmDel(null)} title="Aufgabe löschen?" width={400}>
      {confirmDel && <div>
        <p style={{fontSize:14,color:T.textMuted,lineHeight:1.5,marginBottom:20}}>Die Aufgabe "{confirmDel.text?.slice(0,60)}" wird gelöscht.</p>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <Button variant="secondary" onClick={()=>setConfirmDel(null)}>Abbrechen</Button>
          <Button variant="danger" icon="delete" onClick={doDelete}>Ja, löschen</Button>
        </div>
      </div>}
    </Modal>
  </div>);
};

// ═══ DASHBOARD-BAUKASTEN ═══
// Jede Kachel kennt ihre Breite (Spalten von 12) und eine Hoehenstufe.
// Die Reihenfolge kommt aus dem Layout des Nutzers, gespeichert in app_settings.
const DASH_BREITEN = [3,4,6,8,12];
const DASH_HOEHEN = ["s","m","l","xl"];
const DASH_MINH = {s:0, m:230, l:350, xl:500};
const DASH_NAMEN = {
  chart:"Wachstumskurve", umsatz:"Umsatz gesamt", gewinn:"Gewinn nach Ausgaben",
  leads:"Offene Leads", anrufe:"Anrufe heute", termine7:"Termine (7 Tage)", abschluesse:"Abschlüsse gesamt",
  aufgaben:"Checkliste", woche:"Wochenplan", analyse:"Umsatz-Analyse", notizen:"Notizen",
  pipeline:"Lead-Pipeline", achtung:"Braucht Aufmerksamkeit", termine:"Anstehende Termine",
  vertriebler:"Vertriebler Performance",
};
// Die Standardanordnung ist so gewaehlt, dass jede Reihe auf zwoelf Spalten aufgeht
const DASH_STD = [
  {id:"chart",w:8,h:"l"}, {id:"umsatz",w:4,h:"s"},
  {id:"gewinn",w:4,h:"s"}, {id:"leads",w:4,h:"s"}, {id:"anrufe",w:4,h:"s"},
  {id:"termine7",w:6,h:"s"}, {id:"abschluesse",w:6,h:"s"},
  {id:"aufgaben",w:12,h:"m"}, {id:"woche",w:12,h:"m"}, {id:"analyse",w:12,h:"l"}, {id:"notizen",w:12,h:"m"},
  {id:"achtung",w:6,h:"m"}, {id:"termine",w:6,h:"m"},
  {id:"pipeline",w:12,h:"m"}, {id:"vertriebler",w:12,h:"m"},
];
// Layout aus dem Speicher mit dem Code abgleichen: unbekannte Kacheln raus,
// neu dazugekommene hinten anhaengen, damit nach einem Update nichts fehlt.
const dashBereinigen = (l) => {
  if(!Array.isArray(l)) return DASH_STD.map(k=>({...k}));
  const sauber = l.filter(k=>k && DASH_NAMEN[k.id]).map(k=>({
    id:k.id,
    w:DASH_BREITEN.includes(k.w)?k.w:6,
    h:DASH_HOEHEN.includes(k.h)?k.h:"m",
    aus:!!k.aus,
  }));
  DASH_STD.forEach(s=>{ if(!sauber.find(k=>k.id===s.id)) sauber.push({...s}); });
  return sauber;
};

const Dashboard = ({user,reload,dv}) => {
  const [leadStats,setLeadStats]=useState({total:0,byStatus:{}});
  const calls=store.get(STORAGE_KEYS.calls)||[];
  const appts=store.get(STORAGE_KEYS.appointments)||[];
  const fin=store.get(STORAGE_KEYS.finances)||[];
  const [provisions_all,setProvisionsAll]=useState([]);
  const [recentLeads,setRecentLeads]=useState([]);
  useEffect(()=>{api.get("provisions").then(p=>setProvisionsAll(p||[]));api.get("lead-stats").then(s=>{if(s.total!==undefined)setLeadStats(s);});api.get("leads",`page=1&limit=5&userId=${user.id}&userRole=${user.role}`).then(r=>{if(r.leads)setRecentLeads(r.leads);});},[dv]);
  const leads={length:leadStats.total};
  // Bereinigter Umsatz: ohne Stammkapital, Konto-Überträge, Gutschriften und R+F-Eigenbuchungen
  const rev = fin.filter(f => {
    if(f.type !== "income") return false;
    if(f.category === "konto-uebertrag" || f.category === "stammkapital" || f.category === "gutschrift") return false;
    if(/r\+?f\s?media|\brf\s?media\b/i.test(f.description||"")) return false;
    return true;
  }).reduce((s,f)=>s+Number(f.amount), 0);
  const exp=fin.filter(f=>f.type==="expense").reduce((s,f)=>s+Number(f.amount),0);
  const todayCalls=calls.filter(c=>new Date(c.date).toDateString()===new Date().toDateString()).length;
  const weekAppts=appts.filter(a=>{const d=new Date(a.date);const n=new Date();return d>=n&&d<=new Date(n.getTime()+7*86400000);}).length;
  const sc=ALL_STATUSES.map(s=>({...s,count:leadStats.byStatus[s.id]||0}));
  const closed=(leadStats.byStatus["closed"]||0)+(leadStats.byStatus["active_client"]||0);

  // ── Layout ──
  const lKey = `crm_dash_${user.id}`;
  const [layout,setLayout] = useState(()=>{ try{ const s=localStorage.getItem(lKey); return dashBereinigen(s?JSON.parse(s):null); }catch{ return dashBereinigen(null); } });
  const [bearbeiten,setBearbeiten] = useState(false);
  const [menuOffen,setMenuOffen] = useState(false);
  const [zieht,setZieht] = useState(null);
  const [ueber,setUeber] = useState(null);
  const menuRef = useRef(null);

  useEffect(()=>{
    api.get(`settings/dashboard_${user.id}`).then(r=>{
      if(r && Array.isArray(r.value) && r.value.length) setLayout(dashBereinigen(r.value));
    }).catch(()=>{});
  },[user.id]);

  useEffect(()=>{
    if(!menuOffen) return;
    const zu = (e)=>{ if(menuRef.current && !menuRef.current.contains(e.target)) setMenuOffen(false); };
    document.addEventListener("mousedown",zu);
    return ()=>document.removeEventListener("mousedown",zu);
  },[menuOffen]);

  const merken = (next) => {
    setLayout(next);
    try{ localStorage.setItem(lKey, JSON.stringify(next)); }catch{}
  };
  const sichern = (next) => {
    const l = next || layout;
    api.post(`settings/dashboard_${user.id}`, {value:l, userId:user.id}).catch(()=>{});
  };
  const aendern = (id, feld, richtung) => {
    const liste = feld==="w" ? DASH_BREITEN : DASH_HOEHEN;
    merken(layout.map(k=>{
      if(k.id!==id) return k;
      const i = liste.indexOf(k[feld]);
      const neu = liste[Math.min(liste.length-1, Math.max(0, i+richtung))];
      return {...k, [feld]:neu};
    }));
  };
  const ausblenden = (id) => merken(layout.map(k=>k.id===id?{...k,aus:true}:k));
  const einblenden = (id) => { const n=layout.map(k=>k.id===id?{...k,aus:false}:k); merken(n); sichern(n); };
  const zuruecksetzen = () => { const n=DASH_STD.map(k=>({...k})); merken(n); sichern(n); setMenuOffen(false); };
  const fallenlassen = (zielId) => {
    if(!zieht || zieht===zielId) return;
    const von = layout.findIndex(k=>k.id===zieht), zu = layout.findIndex(k=>k.id===zielId);
    if(von<0||zu<0) return;
    const next=[...layout]; const [m]=next.splice(von,1); next.splice(zu,0,m);
    merken(next); setZieht(null); setUeber(null);
  };
  const fertig = () => { setBearbeiten(false); sichern(); };

  const versteckt = layout.filter(k=>k.aus);

  const inhalt = (id) => {
    if(id==="chart") return (<div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,padding:"16px 20px 14px",boxShadow:T.shadow}}>
      <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:"0.1em",color:T.textDim,fontWeight:600,marginBottom:10}}>Wachstumskurve</div>
      <GrowthChart finances={fin}/>
    </div>);
    if(id==="umsatz") return <StatCard label="Umsatz gesamt" value={fmtEuro(rev)} hideable featured/>;
    if(id==="gewinn") return <StatCard label="Gewinn nach Ausgaben" value={fmtEuro(rev-exp)} hideable color={rev-exp>=0?T.success:T.danger}/>;
    if(id==="leads") return <StatCard label="Offene Leads" value={leads.length} icon="leads"/>;
    if(id==="anrufe") return <StatCard label="Anrufe heute" value={todayCalls} icon="phone"/>;
    if(id==="termine7") return <StatCard label="Termine (7 Tage)" value={weekAppts} icon="calendar"/>;
    if(id==="abschluesse") return <StatCard label="Abschlüsse gesamt" value={closed} icon="check"/>;
    if(id==="aufgaben") return <TasksWidget user={user}/>;
    if(id==="woche") return <WeekScheduleCard/>;
    if(id==="analyse") return <RevenueAnalysis finances={fin}/>;
    if(id==="notizen") return <NotesWidget user={user}/>;
    if(id==="pipeline") return (<div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,padding:"20px 22px",boxShadow:T.shadow}}>
      <h3 style={{fontSize:15,fontWeight:600,marginBottom:14,letterSpacing:"-0.01em"}}>Lead-Pipeline</h3>
      <div style={{display:"flex",flexDirection:"column",gap:2}}>
        {(()=>{ const pmax=Math.max(...sc.map(s=>s.count),1); return sc.map(s=>(
          <div key={s.id} style={{display:"flex",alignItems:"center",gap:12,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
            <span style={{fontSize:13,fontWeight:500,flex:1,minWidth:0}}>{s.label}</span>
            <span style={{flex:2,height:6,background:T.bgHover,borderRadius:3,overflow:"hidden"}}><span style={{display:"block",height:"100%",width:`${Math.max(3,(s.count/pmax)*100)}%`,background:T.accent,borderRadius:3,transition:"width 0.3s"}}/></span>
            <span style={{fontSize:13.5,fontWeight:600,minWidth:54,textAlign:"right",fontFamily:"'JetBrains Mono',monospace",color:T.text}}>{s.count.toLocaleString("de")}</span>
          </div>));
        })()}
      </div>
    </div>);
    if(id==="achtung") return (<div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,overflow:"hidden"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"15px 20px",borderBottom:`1px solid ${T.border}`}}><h3 style={{fontSize:14.5,fontWeight:600,letterSpacing:"-0.01em"}}>Braucht Aufmerksamkeit</h3></div>
      {(()=>{
        const now=new Date(); const in48=new Date(now.getTime()+48*3600000);
        const reason={follow_up:{why:"Nachfassen fällig",tag:"nachfassen",w:5,hot:true},offer_sent:{why:"Angebot offen, kein Abschluss",tag:"nachhaken",w:5,hot:true},email_sent:{why:"E-Mail raus, keine Antwort",tag:"nachfassen",w:3},new:{why:"Neuer Lead, noch nicht kontaktiert",tag:"anrufen",w:4},appointment:{why:"Termin steht an",tag:"vorbereiten",w:2}};
        let att=[];
        appts.filter(a=>{const d=new Date(a.date);return d>=now&&d<=in48;}).forEach(a=>att.push({key:"a"+a.id,name:a.leadCompany||a.title,why:`Termin ${fmtDate(a.date)} ${a.time||""}`,tag:"vorbereiten",w:9,hot:true}));
        recentLeads.forEach(l=>{const r=reason[l.status];if(r)att.push({key:"l"+l.id,name:l.company,why:r.why,tag:r.tag,w:r.w,hot:!!r.hot});});
        att.sort((a,b)=>b.w-a.w); att=att.slice(0,5);
        if(att.length===0) return <div style={{padding:"22px 20px",color:T.textDim,fontSize:13}}>Gerade nichts Dringendes. Sauber.</div>;
        return att.map((a,i)=>(
          <div key={a.key} style={{display:"flex",alignItems:"center",gap:12,padding:"11px 20px",borderBottom:i<att.length-1?`1px solid ${T.border}`:"none"}}>
            <span style={{width:22,height:22,borderRadius:6,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",background:a.hot?T.accentGlow:T.bgHover,color:a.hot?T.accent:T.textMuted}}>{i+1}</span>
            <div style={{flex:1,minWidth:0}}><div style={{fontSize:13.5,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{a.name}</div><div style={{fontSize:12,color:T.textDim,marginTop:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{a.why}</div></div>
            <span style={{fontSize:11,fontWeight:600,color:T.textMuted,whiteSpace:"nowrap"}}>{a.tag}</span>
          </div>));
      })()}
    </div>);
    if(id==="termine") return (<div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,overflow:"hidden"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"15px 20px",borderBottom:`1px solid ${T.border}`}}><h3 style={{fontSize:14.5,fontWeight:600,letterSpacing:"-0.01em"}}>Anstehende Termine</h3></div>
      {appts.filter(a=>new Date(a.date)>=new Date()).length===0?<div style={{padding:"22px 20px",color:T.textDim,fontSize:13}}>Keine Termine</div>:appts.filter(a=>new Date(a.date)>=new Date()).sort((a,b)=>new Date(a.date)-new Date(b.date)).slice(0,5).map((a,i,arr)=>(<div key={a.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,padding:"11px 20px",borderBottom:i<arr.length-1?`1px solid ${T.border}`:"none"}}><div style={{minWidth:0}}><div style={{fontSize:13.5,fontWeight:600}}>{a.title}</div><div style={{fontSize:12,color:T.textDim}}>{a.leadCompany}</div></div><div style={{fontSize:12,color:T.textMuted,fontFamily:"'JetBrains Mono',monospace",whiteSpace:"nowrap"}}>{fmtDate(a.date)} {a.time}</div></div>))}
    </div>);
    if(id==="vertriebler") return (()=>{
      const allUsers = store.get(STORAGE_KEYS.users)||[];
      const members = allUsers.filter(u=>u.role!=="cutter");
      if(members.length===0) return <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:"20px 22px",color:T.textDim,fontSize:13}}>Noch keine Vertriebler angelegt.</div>;
      const cm = monthKey(new Date());
      const memberStats = members.map(m=>{
        const prov = provisions_all.filter(p=>p.userId===m.id)||[];
        const monthProv = prov.filter(p=>(p.date||"").startsWith(cm));
        const showed = monthProv.filter(p=>p.showed).length;
        const noShow = monthProv.filter(p=>!p.showed).length;
        const mCalls = (store.get(STORAGE_KEYS.calls)||[]).filter(c=>c.userId===m.id&&(c.date||"").startsWith(cm)).length;
        const mAppts = (store.get(STORAGE_KEYS.appointments)||[]).filter(a=>a.userId===m.id&&(a.date||"").startsWith(cm)).length;
        return {...m,showed,noShow,total:showed+noShow,provision:showed*10,calls:mCalls,appts:mAppts,rate:showed+noShow>0?Math.round(showed/(showed+noShow)*100):0};
      });
      return (<div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:"20px 22px"}}>
        <h3 style={{fontSize:15,fontWeight:600,letterSpacing:"-0.01em",marginBottom:14,display:"flex",alignItems:"center",gap:8}}><Icon name="team" size={16}/> <span>Vertriebler Performance</span> ({new Date().toLocaleDateString("de",{month:"long"})})</h3>
        <div className="table-wrap" style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse",minWidth:500}}>
          <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>{["Vertriebler","Anrufe","Termine","Erschienen","No-Show","Rate","Provision"].map(h=>(<th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:11,fontWeight:600,color:T.textDim,textTransform:"uppercase"}}>{h}</th>))}</tr></thead>
          <tbody>{memberStats.map(m=>(<tr key={m.id} style={{borderBottom:`1px solid ${T.border}`}}>
            <td style={{padding:"10px 14px"}}><div style={{display:"flex",alignItems:"center",gap:8}}><Avatar userId={m.id} name={m.name} size={28}/><span style={{fontSize:13,fontWeight:600}}>{m.name}</span></div></td>
            <td style={{padding:"10px 14px",fontSize:13,fontFamily:"'JetBrains Mono',monospace"}}>{m.calls}</td>
            <td style={{padding:"10px 14px",fontSize:13,fontFamily:"'JetBrains Mono',monospace"}}>{m.appts}</td>
            <td style={{padding:"10px 14px",fontSize:13,fontWeight:600,color:T.text,fontFamily:"'JetBrains Mono',monospace"}}>{m.showed}</td>
            <td style={{padding:"10px 14px",fontSize:13,color:T.textMuted,fontFamily:"'JetBrains Mono',monospace"}}>{m.noShow}</td>
            <td style={{padding:"10px 14px",fontSize:13,fontWeight:600,color:m.rate>=70?T.text:m.rate>=40?T.textMuted:T.danger,fontFamily:"'JetBrains Mono',monospace"}}>{m.rate}%</td>
            <td style={{padding:"10px 14px",fontSize:14,fontWeight:600,color:T.text,fontFamily:"'JetBrains Mono',monospace"}}>{m.provision} €</td>
          </tr>))}</tbody>
        </table></div>
      </div>);
    })();
    return null;
  };

  const knopf = {border:`1px solid ${T.border}`,background:T.bgCard,color:T.textMuted,borderRadius:6,cursor:"pointer",fontSize:11,lineHeight:1,padding:"4px 6px",fontWeight:600};
  const hh=new Date().getHours(); const g=hh<11?"Morgen":hh<18?"Tag":"Abend";
  const dlong=new Date().toLocaleDateString("de",{weekday:"long",day:"numeric",month:"long"});

  return (<div>
    <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:20}}>
      <Avatar userId={user.id} name={user.name} size={48} editable={true}/>
      <div style={{minWidth:0}}>
        <h1 style={{fontSize:24,fontWeight:700,letterSpacing:"-0.02em"}}>Guten {g}, {(user.name||"").split(" ")[0]}</h1>
        <p style={{color:T.textDim,fontSize:13,marginTop:3}}>{dlong}</p>
      </div>
      <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:10}}>
        {bearbeiten && <button onClick={fertig} style={{border:"none",background:T.accent,color:"#fff",borderRadius:8,padding:"7px 15px",fontSize:13,fontWeight:600,cursor:"pointer"}}>Fertig</button>}
        <div ref={menuRef} style={{position:"relative"}}>
          <button onClick={()=>setMenuOffen(o=>!o)} title="Dashboard" style={{width:32,height:32,borderRadius:8,border:`1px solid ${T.border}`,background:T.bgCard,color:T.textMuted,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="12" cy="19" r="1.8"/></svg>
          </button>
          {menuOffen && <div style={{position:"absolute",right:0,top:38,zIndex:60,minWidth:236,background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:10,boxShadow:T.shadowLg,padding:6}}>
            <button onClick={()=>{setBearbeiten(b=>{const n=!b; if(!n) sichern(); return n;});setMenuOffen(false);}} style={{width:"100%",textAlign:"left",border:"none",background:"transparent",color:T.text,fontSize:13,padding:"9px 11px",borderRadius:7,cursor:"pointer",whiteSpace:"normal",lineHeight:1.35}}>{bearbeiten?"Anpassen beenden":"Dashboard anpassen"}</button>
            {versteckt.length>0 && <>
              <div style={{borderTop:`1px solid ${T.border}`,margin:"5px 0"}}/>
              <div style={{fontSize:11,color:T.textDim,padding:"4px 11px 5px",fontWeight:600}}>Ausgeblendet</div>
              {versteckt.map(k=>(<button key={k.id} onClick={()=>einblenden(k.id)} style={{width:"100%",textAlign:"left",border:"none",background:"transparent",color:T.textMuted,fontSize:13,padding:"8px 11px",borderRadius:7,cursor:"pointer",display:"flex",alignItems:"center",gap:8,whiteSpace:"normal",lineHeight:1.35}}>
                <span style={{color:T.accent,fontWeight:700}}>+</span>{DASH_NAMEN[k.id]}
              </button>))}
            </>}
            <div style={{borderTop:`1px solid ${T.border}`,margin:"5px 0"}}/>
            <button onClick={zuruecksetzen} style={{width:"100%",textAlign:"left",border:"none",background:"transparent",color:T.textMuted,fontSize:13,padding:"9px 11px",borderRadius:7,cursor:"pointer",whiteSpace:"normal",lineHeight:1.35}}>Standard wiederherstellen</button>
          </div>}
        </div>
      </div>
    </div>

    {bearbeiten && <div style={{background:T.accentGlow,border:`1px solid ${T.accent}44`,borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:12.5,color:T.textMuted}}>
      Kacheln lassen sich ziehen und über die Knöpfe oben rechts breiter, schmaler, höher oder niedriger machen. Mit dem Kreuz verschwindet eine Kachel, zurück holst du sie über das Menü.
    </div>}

    <div className="dash-grid" style={{display:"grid",gridTemplateColumns:"repeat(12,1fr)",gap:16,alignItems:"stretch"}}>
      {layout.filter(k=>!k.aus).map(k=>(
        <div key={k.id}
          className={"dash-kachel"+(bearbeiten?" bearb":"")+(zieht===k.id?" zieht":"")+(ueber===k.id?" ueber":"")}
          draggable={bearbeiten}
          onDragStart={()=>setZieht(k.id)}
          onDragEnd={()=>{setZieht(null);setUeber(null);}}
          onDragOver={e=>{ if(!bearbeiten)return; e.preventDefault(); if(zieht&&zieht!==k.id) setUeber(k.id); }}
          onDragLeave={()=>{ if(ueber===k.id) setUeber(null); }}
          onDrop={e=>{ if(!bearbeiten)return; e.preventDefault(); fallenlassen(k.id); }}
          style={{gridColumn:`span ${k.w}`,minHeight:DASH_MINH[k.h]||undefined,position:"relative"}}>
          {bearbeiten && <div className="kachel-werkzeug" style={{position:"absolute",top:8,right:8,zIndex:5,display:"flex",gap:3,background:T.bgCard2,border:`1px solid ${T.border}`,borderRadius:8,padding:3,boxShadow:T.shadow}}>
            <button onClick={()=>aendern(k.id,"w",-1)} style={knopf} title="schmaler">◀</button>
            <button onClick={()=>aendern(k.id,"w",1)} style={knopf} title="breiter">▶</button>
            <button onClick={()=>aendern(k.id,"h",-1)} style={knopf} title="niedriger">▲</button>
            <button onClick={()=>aendern(k.id,"h",1)} style={knopf} title="höher">▼</button>
            <button onClick={()=>ausblenden(k.id)} style={{...knopf,color:T.danger}} title="ausblenden">✕</button>
          </div>}
          {inhalt(k.id)}
        </div>))}
    </div>
  </div>);
};

// ─── LEADS ───
const LeadsPage = ({user,reload,dv}) => {
  const [leads,setLeads]=useState([]);
  const [totalLeads,setTotalLeads]=useState(0);
  const [currentPage,setCurrentPage]=useState(1);
  const [totalPages,setTotalPages]=useState(1);
  const [search,setSearch]=useState("");
  const [searchInput,setSearchInput]=useState("");
  const [filter,setFilter]=useState("all");
  const [websiteFirst,setWebsiteFirst]=useState(false);
  const [sel,setSel]=useState(null);
  const [showD,setShowD]=useState(false);
  const [showDeleteConfirm,setShowDeleteConfirm]=useState(null);
  const [showImport,setShowImport]=useState(false);
  const [showAddLead,setShowAddLead]=useState(false);
  const [preview,setPreview]=useState(null);
  const [importPrivate,setImportPrivate]=useState(false); // false=Allen, true=nur mir
  const [importResult,setImportResult]=useState(null);
  const [leadForm,setLeadForm]=useState({company:"",contactName:"",contactTitle:"",email:"",phone:"",website:"",address:"",city:"",category:""});
  const [loading,setLoading]=useState(false);
  const [statusCounts,setStatusCounts]=useState({});
  const [showMoreMenu,setShowMoreMenu]=useState(false);
  const [showStatusMenu,setShowStatusMenu]=useState(false);
  // Lead-Stats für Status-Counts laden
  useEffect(()=>{api.get("lead-stats",`userId=${user.id}`).then(s=>{if(s.byStatus)setStatusCounts(s.byStatus);});},[dv, leads.length]);
  // Drag & Drop State (Pointer Events — funktioniert Desktop + Touch)
  const [draggedId,setDraggedId]=useState(null);
  const [dragOverStatus,setDragOverStatus]=useState(null);
  const [dragPos,setDragPos]=useState(null); // {x,y} — Position des Drag-Ghosts
  const [draggedLead,setDraggedLead]=useState(null); // Snapshot des Leads für Ghost-Render
  const dragStartRef = useRef(null); // {leadId, startX, startY, longPressTimer}
  const users=store.get(STORAGE_KEYS.users)||[];

  // Load leads from API with pagination
  const loadLeads = async(page=1,searchQ="",statusF="all") => {
    setLoading(true);
    const params = `page=${page}&limit=50&userId=${user.id}&userRole=${user.role}${searchQ?`&search=${encodeURIComponent(searchQ)}`:""}${statusF&&statusF!=="all"?`&status=${statusF}`:""}`;
    const res = await api.get("leads",params);
    if(res.leads){setLeads(res.leads);setTotalLeads(res.total);setTotalPages(res.pages);setCurrentPage(res.page);}
    setLoading(false);
  };

  useEffect(()=>{loadLeads(1,search,filter);},[]);

  // Search with debounce
  useEffect(()=>{const t=setTimeout(()=>{setSearch(searchInput);loadLeads(1,searchInput,filter);},400);return()=>clearTimeout(t);},[searchInput]);
  useEffect(()=>{loadLeads(1,search,filter);},[filter]);

  // Auto-Refresh alle 20s — synchronisiert Status-Änderungen über Geräte hinweg
  // Refresh nicht wenn Lead-Detail offen (würde laufende Edits stören)
  useEffect(()=>{
    const iv = setInterval(()=>{
      if(showD) return; // nicht refreshen wenn Detail offen
      loadLeads(currentPage, search, filter);
    }, 20000);
    return ()=>clearInterval(iv);
  },[currentPage, search, filter, showD]);

  // Hilfsfunktion: hat der Lead eine "echte" Website?
  const hasWebsite = (l) => {
    const w = (l.website||"").trim();
    if(!w) return false;
    // Schließe Platzhalter aus
    if(/^(n\/?a|none|kein|keine|—|-)$/i.test(w)) return false;
    return true;
  };
  // Wenn websiteFirst aktiv: erst Leads mit Website, dann ohne
  const fl = websiteFirst
    ? [...leads].sort((a,b)=>{const A=hasWebsite(a)?0:1, B=hasWebsite(b)?0:1; return A-B;})
    : leads;

  // Open lead detail - load notes from API
  const openLead = async(lead) => {
    const full = await api.get(`lead/${lead.id}`,`userId=${user.id}`);
    if(full && !full.error){setSel(full);}else{setSel(lead);}
    setShowD(true);
  };

  const upd=async(updated)=>{await api.put("leads",updated);setSel(updated);loadLeads(currentPage,search,filter);};

  // Drag & Drop: Lead per Drop in neue Status-Spalte verschieben
  const moveLead = async(leadId,newStatus) => {
    const lead = leads.find(l=>l.id===leadId);
    if(!lead||lead.status===newStatus) return;
    const oldStatus = lead.status;
    // Optimistic update — UI sofort aktualisieren
    setLeads(prev=>prev.map(l=>l.id===leadId?{...l,status:newStatus}:l));
    // API call im Hintergrund
    try{
      const res = await api.put("leads",{...lead,status:newStatus});
      if(res&&res.error){
        // Rollback bei Fehler
        setLeads(prev=>prev.map(l=>l.id===leadId?{...l,status:oldStatus}:l));
      }
    }catch(e){
      setLeads(prev=>prev.map(l=>l.id===leadId?{...l,status:oldStatus}:l));
    }
  };

  // Pointer Events: einheitliches Drag & Drop für Desktop + Touch (iPad/iPhone)
  const onCardPointerDown = (e,lead) => {
    // Nur primäre Maustaste / Touch
    if(e.button!==undefined && e.button!==0) return;
    // Klicks auf interne Links (tel:, http) nicht abfangen
    if(e.target.closest("a")) return;
    const startX = e.clientX, startY = e.clientY;
    // Long-Press auf 600ms erhöht — ermöglicht dem User natürlich zu scrollen
    const timer = setTimeout(()=>{
      // Long-Press erreicht → Drag-Modus aktivieren
      setDraggedId(lead.id);
      setDraggedLead(lead);
      setDragPos({x:startX,y:startY});
      // Haptisches Feedback auf mobilen Geräten
      if(navigator.vibrate) try{navigator.vibrate(30);}catch(_){}
    }, 600);
    dragStartRef.current = {leadId:lead.id, startX, startY, timer, moved:false};
  };

  const onPointerMove = (e) => {
    const ds = dragStartRef.current;
    if(!ds) return;
    // Vor Long-Press: Schon bei minimalster Bewegung Drag abbrechen — User wollte scrollen
    if(!draggedId){
      const dx = Math.abs(e.clientX-ds.startX), dy = Math.abs(e.clientY-ds.startY);
      if(dx>5||dy>5){clearTimeout(ds.timer);dragStartRef.current=null;}
      return;
    }
    // Drag aktiv: Position aktualisieren + Drop-Target erkennen
    e.preventDefault();
    setDragPos({x:e.clientX,y:e.clientY});
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const col = el?.closest("[data-status-col]");
    const status = col?.getAttribute("data-status-col");
    setDragOverStatus(status||null);
  };

  const onPointerUp = (e) => {
    const ds = dragStartRef.current;
    if(ds) clearTimeout(ds.timer);
    dragStartRef.current = null;
    if(draggedId && dragOverStatus){
      moveLead(draggedId, dragOverStatus);
    }
    setDraggedId(null);
    setDraggedLead(null);
    setDragPos(null);
    setDragOverStatus(null);
  };

  // Globale Pointer-Listener nur aktiv während eines Drags
  useEffect(()=>{
    if(!dragStartRef.current && !draggedId) return;
    window.addEventListener("pointermove", onPointerMove, {passive:false});
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    return ()=>{
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
    };
  },[draggedId,dragOverStatus]);

  const logCall=async(lead)=>{
    // Anruf-Eintrag anlegen, bei Netzfehler einmal erneut versuchen (Handy-Verbindung wackelt manchmal)
    const post = () => api.post("calls",{leadId:lead.id,leadCompany:lead.company,userId:user.id,userName:user.name});
    try {
      await post();
    } catch(e){
      try { await new Promise(r=>setTimeout(r,800)); await post(); } catch(e2){ /* Call-Log fehlgeschlagen */ }
    }
    try { await upd({...lead,callCount:(lead.callCount||0)+1,lastContact:new Date().toISOString()}); } catch(e){}
    try { await reload(STORAGE_KEYS.calls); } catch(e){}
  };
  // Anruf IMMER sofort loggen und parallel den Wähler öffnen.
  // Kein Warten mehr auf einen App-Wechsel (das war unzuverlässig).
  const callAndLog=(lead)=>{
    logCall(lead); // sofort zählen, jedes Mal (läuft im Hintergrund)
    if(lead.phone){
      const tel = String(lead.phone).replace(/[^\d+]/g,"");
      if(tel){
        // kurzer Moment, damit der Log-Request sicher rausgeht, bevor der Wähler die Seite verlässt
        setTimeout(()=>{ window.location.href = `tel:${tel}`; }, 150);
      }
    }
  };

  const deleteLead = async(id) => {await api.del("leads",{id});loadLeads(currentPage,search,filter);};
  const deleteAllLeads = async() => {await api.del("leads",{id:"ALL"});loadLeads(1,search,filter);setShowDeleteConfirm(null);};
  const deleteFilteredLeads = async() => {for(const l of fl){await api.del("leads",{id:l.id});}loadLeads(1,search,filter);setShowDeleteConfirm(null);};

  // Add single lead
  const addSingleLead = async() => {
    if(!leadForm.company&&!leadForm.contactName){return;}
    const newLead = {
      id:`lead-${Date.now()}-${Math.random().toString(36).slice(2,8)}`,
      company:leadForm.company, contactName:leadForm.contactName, contactTitle:leadForm.contactTitle,
      email:leadForm.email, phone:leadForm.phone, website:leadForm.website,
      address:leadForm.address, city:leadForm.city, category:leadForm.category,
      rating:"", reviews:"", source:"Manuell", query:"",
      status:"new", assignedTo:null, notes:[], callCount:0, lastContact:null,
      created:new Date().toISOString(),
    };
    await api.post("leads",newLead);
    loadLeads(1,search,filter);
    setLeadForm({company:"",contactName:"",contactTitle:"",email:"",phone:"",website:"",address:"",city:"",category:""});
    setShowAddLead(false);
  };

  // Import functions
  const findDuplicates = (newLeads, existingLeads) => {
    const ep = new Set(existingLeads.map(l=>l.phone).filter(p=>p&&p.length>3));
    const ek = new Set(existingLeads.map(l=>`${(l.company||'').toLowerCase().trim()}|${(l.city||'').toLowerCase().trim()}`).filter(k=>k!=='|'));
    const unique=[], dupes=[];
    const np=new Set(), nk=new Set();
    newLeads.forEach(lead=>{
      const phone=(lead.phone||'').trim();
      const key=`${(lead.company||'').toLowerCase().trim()}|${(lead.city||'').toLowerCase().trim()}`;
      const isDupe=(phone&&phone.length>3&&(ep.has(phone)||np.has(phone)))||(key!=='|'&&(ek.has(key)||nk.has(key)));
      if(isDupe){dupes.push(lead);}else{unique.push(lead);if(phone)np.add(phone);if(key!=='|')nk.add(key);}
    });
    return {unique,dupes};
  };
  const doImport=async()=>{
    if(!preview?.length)return;
    const allNew=preview.map(mapOutscraperToLead);
    const{unique,dupes}=findDuplicates(allNew,leads);
    const privateTo = importPrivate ? user.id : null;
    // privateToUser auch in jedem einzelnen Lead setzen falls privat
    const leadsForUpload = unique.map(l => ({...l, privateToUser: privateTo}));
    await api.post("leads-bulk",{leads:leadsForUpload, privateToUser: privateTo});
    loadLeads(1,search,filter);
    setImportResult({count:unique.length,dupes:dupes.length,total:0,private:importPrivate});
    setPreview(null);
    setImportPrivate(false);
  };
  const [dedupRunning,setDedupRunning] = useState(false);
  const deduplicateExisting = async () => {
    if(!confirm("Duplikate werden aus der gesamten Datenbank entfernt (alle Seiten).\n\nBehalten wird pro Duplikat-Gruppe der wertvollste Lead:\n• Bearbeitete Status werden IMMER bevorzugt (egal welcher)\n• Bei gleichem Status: Lead mit Notizen\n• Bei gleichem Status & ohne Notizen: zuletzt erstellter Lead\n\n→ \"Neu\"-Leads werden zuerst gelöscht, bearbeitete bleiben erhalten.\n\nFortfahren?")) return;
    setDedupRunning(true);
    try {
      const res = await api.post("leads-deduplicate", {});
      await loadLeads(currentPage, search, filter);
      const msg = res.deleted > 0
        ? `✓ ${res.deleted} Duplikate aus ${res.groupsFound || "?"} Gruppen entfernt. ${res.remaining} Leads verbleiben.`
        : "Keine Duplikate gefunden — alles sauber!";
      alert(msg);
    } catch(e){
      alert("Fehler beim Bereinigen: " + e.message);
    }
    setDedupRunning(false);
  };

  const [viewMode,setViewMode]=useState(localStorage.getItem('crm_lead_view')||"table");
  const setView=(v)=>{setViewMode(v);localStorage.setItem('crm_lead_view',v);};

  return (<div>
    {/* ═══ HEADER — kompakte 1-Zeilen-Variante ═══ */}
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,gap:12,flexWrap:"wrap"}}>
      <div style={{display:"flex",alignItems:"baseline",gap:10}}>
        <h1 style={{fontSize:isMobileDevice?20:24,fontWeight:800,margin:0}}>{user.role==="admin"?"Alle Leads":"Meine Leads"}</h1>
        <span style={{fontSize:13,color:T.textDim}}>· {totalLeads}{loading?" (laden...)":""}</span>
      </div>
      <div style={{display:"flex",gap:6,alignItems:"center",position:"relative"}}>
        <Button icon="add" size="sm" onClick={()=>setShowAddLead(true)}>{isMobileDevice?"":"Lead anlegen"}</Button>
        <button onClick={()=>setShowMoreMenu(!showMoreMenu)} title="Mehr Aktionen" style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"7px 10px",color:T.text,cursor:"pointer",fontSize:14,fontWeight:600,lineHeight:1}}>⋯</button>
        {showMoreMenu&&<>
          <div onClick={()=>setShowMoreMenu(false)} style={{position:"fixed",inset:0,zIndex:998}}/>
          <div style={{position:"absolute",top:"100%",right:0,marginTop:6,background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:10,padding:6,zIndex:999,minWidth:220,boxShadow:"0 8px 24px rgba(0,0,0,0.3)"}}>
            <button onClick={()=>{setShowImport(!showImport);setImportResult(null);setShowMoreMenu(false);}} style={{width:"100%",textAlign:"left",padding:"10px 12px",background:"none",border:"none",color:T.text,cursor:"pointer",fontSize:13,borderRadius:6,display:"flex",alignItems:"center",gap:10}} onMouseEnter={e=>e.currentTarget.style.background=T.bgHover} onMouseLeave={e=>e.currentTarget.style.background="none"}><Icon name="upload" size={14}/> Importieren</button>
            {leads.length>0&&<button onClick={()=>{deduplicateExisting();setShowMoreMenu(false);}} disabled={dedupRunning} style={{width:"100%",textAlign:"left",padding:"10px 12px",background:"none",border:"none",color:T.text,cursor:dedupRunning?"wait":"pointer",fontSize:13,borderRadius:6,display:"flex",alignItems:"center",gap:10,opacity:dedupRunning?0.5:1}} onMouseEnter={e=>{if(!dedupRunning)e.currentTarget.style.background=T.bgHover;}} onMouseLeave={e=>e.currentTarget.style.background="none"}><Icon name="delete" size={14}/> {dedupRunning?"Bereinige...":"Duplikate bereinigen"}</button>}
            {user.role==="admin"&&(search||filter!=="all")&&fl.length>0&&<button onClick={()=>{setShowDeleteConfirm('filtered');setShowMoreMenu(false);}} style={{width:"100%",textAlign:"left",padding:"10px 12px",background:"none",border:"none",color:T.danger,cursor:"pointer",fontSize:13,borderRadius:6,display:"flex",alignItems:"center",gap:10}} onMouseEnter={e=>e.currentTarget.style.background=`${T.danger}15`} onMouseLeave={e=>e.currentTarget.style.background="none"}><Icon name="delete" size={14}/> Gefilterte löschen ({fl.length})</button>}
            {user.role==="admin"&&leads.length>0&&<button onClick={()=>{setShowDeleteConfirm('all');setShowMoreMenu(false);}} style={{width:"100%",textAlign:"left",padding:"10px 12px",background:"none",border:"none",color:T.danger,cursor:"pointer",fontSize:13,borderRadius:6,display:"flex",alignItems:"center",gap:10}} onMouseEnter={e=>e.currentTarget.style.background=`${T.danger}15`} onMouseLeave={e=>e.currentTarget.style.background="none"}><Icon name="delete" size={14}/> Alle löschen ({leads.length})</button>}
          </div>
        </>}
      </div>
    </div>

    {/* Add Single Lead Modal */}
    <Modal open={showAddLead} onClose={()=>setShowAddLead(false)} title="Neuen Lead anlegen" width={560}>
      <div className="grid-2col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
        <Input label="Firma *" value={leadForm.company} onChange={e=>setLeadForm({...leadForm,company:e.target.value})} placeholder="z.B. Müller GmbH"/>
        <Input label="Kontaktperson" value={leadForm.contactName} onChange={e=>setLeadForm({...leadForm,contactName:e.target.value})} placeholder="z.B. Max Müller"/>
        <Input label="Position/Titel" value={leadForm.contactTitle} onChange={e=>setLeadForm({...leadForm,contactTitle:e.target.value})} placeholder="z.B. Geschäftsführer"/>
        <Input label="Telefon" value={leadForm.phone} onChange={e=>setLeadForm({...leadForm,phone:e.target.value})} placeholder="+49 30 123456"/>
        <Input label="E-Mail" value={leadForm.email} onChange={e=>setLeadForm({...leadForm,email:e.target.value})} placeholder="max@mueller.de"/>
        <Input label="Website" value={leadForm.website} onChange={e=>setLeadForm({...leadForm,website:e.target.value})} placeholder="www.mueller.de"/>
        <Input label="Stadt" value={leadForm.city} onChange={e=>setLeadForm({...leadForm,city:e.target.value})} placeholder="Berlin"/>
        <Input label="Kategorie/Branche" value={leadForm.category} onChange={e=>setLeadForm({...leadForm,category:e.target.value})} placeholder="z.B. Immobilien"/>
        <div style={{gridColumn:"1/-1"}}><Input label="Adresse" value={leadForm.address} onChange={e=>setLeadForm({...leadForm,address:e.target.value})} placeholder="Straße, PLZ, Ort" style={{width:"100%"}}/></div>
      </div>
      <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:20}}>
        <Button variant="secondary" onClick={()=>setShowAddLead(false)}>Abbrechen</Button>
        <Button icon="add" onClick={addSingleLead}>Lead anlegen</Button>
      </div>
    </Modal>

    {/* Import Area */}
    {showImport&&<div style={{marginBottom:24}}>
      <FileDrop onData={rows=>{if(rows?.length){setPreview(rows);setImportResult(null);}}} label="Leads importieren (CSV oder XLSX)" sublabel="Outscraper, Notion, Dripify (LinkedIn) oder andere Formate — Duplikate werden automatisch erkannt"/>
      {importResult&&<div style={{background:`${T.success}15`,border:`1px solid ${T.success}40`,borderRadius:12,padding:18,marginTop:16}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{color:T.success}}><Icon name="check" size={24}/></div>
          <div>
            {importResult.dedup
              ? <><div style={{fontSize:14,fontWeight:600,color:T.success}}>{importResult.dupes} Duplikate entfernt!</div><div style={{fontSize:12,color:T.textDim}}>{importResult.total} Leads verbleiben</div></>
              : <><div style={{fontSize:14,fontWeight:600,color:T.success}}>{importResult.count} neue Leads importiert!</div>
                {importResult.private&&<div style={{fontSize:12,color:T.accent,fontWeight:600}}>🔒 Nur für dich sichtbar</div>}
                {importResult.dupes>0&&<div style={{fontSize:12,color:T.warning}}>{importResult.dupes} Duplikate übersprungen</div>}
                <div style={{fontSize:12,color:T.textDim}}>Gesamt: {importResult.total} Leads</div></>}
          </div>
        </div>
      </div>}
      {preview&&preview.length>0&&<div style={{marginTop:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:12}}>
          <div>
            <div style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em"}}>Vorschau — {preview.length} Einträge</div>
            {(()=>{const m0=mapOutscraperToLead(preview[0]||{});return m0.source&&<div style={{fontSize:12,color:T.textDim,marginTop:2}}>Erkanntes Format: {m0.source}</div>;})()}
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}><Button variant="secondary" onClick={()=>{setPreview(null);setImportPrivate(false);}}>Abbrechen</Button><Button icon="upload" onClick={doImport}>{preview.length} Leads importieren</Button></div>
        </div>

        {/* Privatsphäre-Toggle */}
        <div style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:12,padding:14,marginBottom:14}}>
          <div style={{fontSize:13,fontWeight:700,marginBottom:10}}>Wer soll diese Leads sehen?</div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            <button onClick={()=>setImportPrivate(false)} style={{flex:1,minWidth:200,padding:"12px 16px",background:!importPrivate?T.success:T.bgCard,color:!importPrivate?"#fff":T.text,border:`2px solid ${!importPrivate?T.success:T.border}`,borderRadius:10,fontSize:13,fontWeight:600,cursor:"pointer",textAlign:"left"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:16}}>👥</span>
                <div>
                  <div>Alle im Team</div>
                  <div style={{fontSize:11,fontWeight:400,opacity:0.8,marginTop:2}}>Standard — alle im Team sehen die Leads</div>
                </div>
              </div>
            </button>
            <button onClick={()=>setImportPrivate(true)} style={{flex:1,minWidth:200,padding:"12px 16px",background:importPrivate?T.accent:T.bgCard,color:importPrivate?"#fff":T.text,border:`2px solid ${importPrivate?T.accent:T.border}`,borderRadius:10,fontSize:13,fontWeight:600,cursor:"pointer",textAlign:"left"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:16}}>🔒</span>
                <div>
                  <div>Nur ich</div>
                  <div style={{fontSize:11,fontWeight:400,opacity:0.8,marginTop:2}}>Niemand sonst sieht diese Leads — auch keine Admins</div>
                </div>
              </div>
            </button>
          </div>
        </div>

        <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,overflow:"auto",maxHeight:300}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}><thead><tr style={{borderBottom:`1px solid ${T.border}`}}>{["Firma","Kontakt","Telefon","E-Mail","Stadt"].map(h=>(<th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:11,fontWeight:600,color:T.textDim,textTransform:"uppercase",position:"sticky",top:0,background:T.bgCard}}>{h}</th>))}</tr></thead>
          <tbody>{preview.slice(0,30).map((row,i)=>{const m=mapOutscraperToLead(row);return(<tr key={i} style={{borderBottom:`1px solid ${T.border}`}}><td style={{padding:"10px 14px",fontSize:13,fontWeight:600}}>{m.company}</td><td style={{padding:"10px 14px",fontSize:13}}>{m.contactName}</td><td style={{padding:"10px 14px",fontSize:13,fontFamily:"'JetBrains Mono',monospace"}}>{m.phone}</td><td style={{padding:"10px 14px",fontSize:13}}>{m.email}</td><td style={{padding:"10px 14px",fontSize:13}}>{m.city}</td></tr>);})}</tbody></table>
          {preview.length>30&&<div style={{padding:14,textAlign:"center",fontSize:12,color:T.textDim}}>... und {preview.length-30} weitere</div>}
        </div>
      </div>}
    </div>}

    {/* Delete Confirmation */}
    <Modal open={!!showDeleteConfirm} onClose={()=>setShowDeleteConfirm(null)} title="Leads löschen?" width={440}>
      <div style={{fontSize:14,color:T.textMuted,marginBottom:20}}>
        {showDeleteConfirm==='all'
          ? `Willst du wirklich alle ${leads.length} Leads unwiderruflich löschen?`
          : `Willst du wirklich die ${fl.length} gefilterten Leads löschen?`}
      </div>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
        <Button variant="secondary" onClick={()=>setShowDeleteConfirm(null)}>Abbrechen</Button>
        <Button variant="danger" icon="delete" onClick={showDeleteConfirm==='all'?deleteAllLeads:deleteFilteredLeads}>Ja, löschen</Button>
      </div>
    </Modal>
    {/* ═══ FILTER-BAR: Suche + View-Toggle ═══ */}
    <div style={{display:"flex",gap:10,marginBottom:10,flexWrap:"wrap",alignItems:"center"}}>
      <div style={{flex:1,minWidth:200}}><Input placeholder="Suche nach Firma, Kontakt, Stadt..." value={searchInput} onChange={e=>setSearchInput(e.target.value)} style={{width:"100%"}}/></div>
      <div style={{display:"flex",gap:4,background:T.bgInput,borderRadius:8,padding:2}}>
        <button onClick={()=>setView("table")} style={{padding:"6px 12px",borderRadius:6,border:"none",background:viewMode==="table"?T.accent:"transparent",color:viewMode==="table"?"#fff":T.textDim,cursor:"pointer",fontSize:12,fontWeight:600}}>Tabelle</button>
        <button onClick={()=>setView("board")} style={{padding:"6px 12px",borderRadius:6,border:"none",background:viewMode==="board"?T.accent:"transparent",color:viewMode==="board"?"#fff":T.textDim,cursor:"pointer",fontSize:12,fontWeight:600}}>Board</button>
      </div>
    </div>

    {/* ═══ STATUS-DROPDOWN + WEBSITE-TOGGLE ═══ */}
    <div style={{display:"flex",gap:8,marginBottom:18,flexWrap:"wrap",alignItems:"center"}}>
      {/* Status-Dropdown */}
      <div style={{position:"relative"}}>
        {(()=>{
          const activeStatus = filter==="all" ? null : ALL_STATUSES.find(s=>s.id===filter);
          const activeColor = activeStatus?.color || T.accent;
          const activeLabel = activeStatus?.label || "Alle Status";
          const activeCount = filter==="all" ? totalLeads : (statusCounts[filter]||0);
          return (<button onClick={()=>setShowStatusMenu(!showStatusMenu)} style={{padding:"7px 14px",borderRadius:14,border:`1px solid ${filter!=="all"?activeColor:T.border}`,background:filter!=="all"?`${activeColor}22`:T.bgInput,color:filter!=="all"?activeColor:T.text,cursor:"pointer",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:8}}>
            <span>{activeLabel}</span>
            <span style={{opacity:0.7,fontWeight:500}}>({activeCount})</span>
            <span style={{fontSize:9,marginLeft:2}}>▼</span>
          </button>);
        })()}
        {showStatusMenu&&<>
          <div onClick={()=>setShowStatusMenu(false)} style={{position:"fixed",inset:0,zIndex:998}}/>
          <div style={{position:"absolute",top:"100%",left:0,marginTop:6,background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:10,padding:6,zIndex:999,minWidth:240,maxHeight:380,overflowY:"auto",boxShadow:"0 8px 24px rgba(0,0,0,0.3)"}}>
            <button onClick={()=>{setFilter("all");setShowStatusMenu(false);}} style={{width:"100%",textAlign:"left",padding:"9px 12px",background:filter==="all"?T.bgHover:"none",border:"none",color:T.text,cursor:"pointer",fontSize:13,borderRadius:6,display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,fontWeight:filter==="all"?600:400}}><span>Alle Status</span><span style={{color:T.textDim,fontSize:11,fontFamily:"'JetBrains Mono',monospace"}}>{totalLeads}</span></button>
            <div style={{height:1,background:T.border,margin:"4px 6px"}}/>
            {ALL_STATUSES.map(s=>{
              const cnt = statusCounts[s.id]||0;
              const active = filter===s.id;
              return (<button key={s.id} onClick={()=>{setFilter(s.id);setShowStatusMenu(false);}} style={{width:"100%",textAlign:"left",padding:"9px 12px",background:active?`${s.color}22`:"none",border:"none",color:active?s.color:T.text,cursor:"pointer",fontSize:13,borderRadius:6,display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,fontWeight:active?600:400}} onMouseEnter={e=>{if(!active)e.currentTarget.style.background=T.bgHover;}} onMouseLeave={e=>{if(!active)e.currentTarget.style.background="none";}}>
                <span style={{display:"flex",alignItems:"center",gap:8}}><span style={{width:8,height:8,borderRadius:"50%",background:s.color,display:"inline-block"}}/>{s.label}</span>
                <span style={{color:active?s.color:T.textDim,fontSize:11,fontFamily:"'JetBrains Mono',monospace"}}>{cnt}</span>
              </button>);
            })}
          </div>
        </>}
      </div>

      {/* Website-Toggle */}
      <button onClick={()=>setWebsiteFirst(!websiteFirst)} title="Leads mit Website nach oben sortieren" style={{padding:"7px 14px",borderRadius:14,border:`1px solid ${websiteFirst?T.info:T.border}`,background:websiteFirst?`${T.info}22`:T.bgInput,color:websiteFirst?T.info:T.text,cursor:"pointer",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:6}}>🌐 Mit Website {websiteFirst&&"✓"}</button>

      {/* Reset-Button wenn ein Filter aktiv ist */}
      {(filter!=="all"||websiteFirst||search)&&<button onClick={()=>{setFilter("all");setWebsiteFirst(false);setSearchInput("");}} style={{padding:"7px 12px",borderRadius:14,border:"none",background:"transparent",color:T.textDim,cursor:"pointer",fontSize:12,textDecoration:"underline"}}>Zurücksetzen</button>}
    </div>

    {/* ═══ BOARD VIEW (Notion-Style Kanban mit Drag & Drop, Touch + Desktop) ═══ */}
    {viewMode==="board"&&<>
      {draggedId&&<div style={{position:"fixed",top:0,left:0,right:0,padding:"10px 16px",background:T.accent,color:"#fff",fontSize:13,fontWeight:600,textAlign:"center",zIndex:1500,boxShadow:"0 4px 12px rgba(0,0,0,0.3)"}}>Lege auf der Ziel-Spalte ab — oder lass los um abzubrechen</div>}
      <div style={{display:"flex",gap:12,overflowX:"auto",paddingBottom:16,touchAction:draggedId?"none":"auto"}}>
        {ALL_STATUSES.map(status=>{ const statusLeads=fl.filter(l=>l.status===status.id); const isDropTarget=dragOverStatus===status.id; return (
          <div key={status.id} data-status-col={status.id}
            style={{minWidth:260,maxWidth:300,flex:"0 0 280px",background:isDropTarget?T.accentGlow:T.bgCard,border:`2px solid ${isDropTarget?status.color:T.border}`,borderRadius:12,display:"flex",flexDirection:"column",maxHeight:"70vh",transition:"all 0.15s"}}>
            <div data-status-col={status.id} style={{padding:"12px 16px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{width:8,height:8,borderRadius:"50%",background:status.color}}/>
                <span style={{fontSize:13,fontWeight:700,color:T.text}}>{status.label}</span>
              </div>
              <span style={{fontSize:11,color:T.textDim,background:T.bgInput,padding:"2px 8px",borderRadius:10}}>{statusLeads.length}</span>
            </div>
            <div data-status-col={status.id} style={{flex:1,overflowY:"auto",padding:8,display:"flex",flexDirection:"column",gap:6,minHeight:60}}>
              {statusLeads.length===0?<div data-status-col={status.id} style={{padding:16,textAlign:"center",fontSize:12,color:isDropTarget?T.accent:T.textDim,fontWeight:isDropTarget?600:400}}>{isDropTarget?"Hier ablegen":"Keine Leads"}</div>:
              statusLeads.map(l=>(<div key={l.id} data-status-col={status.id}
                onPointerDown={e=>onCardPointerDown(e,l)}
                onClick={e=>{if(!draggedId&&!dragStartRef.current?.moved)openLead(l);}}
                style={{background:T.bgHover,border:`1px solid ${T.border}`,borderRadius:10,padding:12,cursor:draggedId===l.id?"grabbing":"grab",transition:"opacity 0.15s",opacity:draggedId===l.id?0.3:1,userSelect:"none",touchAction:draggedId?"none":"pan-y",WebkitUserSelect:"none",WebkitTouchCallout:"none"}}>
                <div data-status-col={status.id} style={{fontSize:13,fontWeight:700,marginBottom:4,color:T.text,pointerEvents:"none"}}>{l.company||"Unbekannt"}</div>
                {l.contactName&&<div data-status-col={status.id} style={{fontSize:12,color:T.textMuted,marginBottom:4,pointerEvents:"none"}}>{l.contactName}</div>}
                <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:6}}>
                  {l.phone&&<a href={`tel:${l.phone}`} onClick={e=>e.stopPropagation()} style={{fontSize:11,color:T.info,textDecoration:"none",display:"flex",alignItems:"center",gap:3}}><Icon name="phone" size={10}/>{l.phone}</a>}
                  {l.city&&<span data-status-col={status.id} style={{fontSize:11,color:T.textDim,pointerEvents:"none"}}>{l.city}</span>}
                </div>
                {l.website&&<div style={{marginTop:4}}><a href={l.website.startsWith("http")?l.website:`https://${l.website}`} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()} style={{fontSize:11,color:T.info,textDecoration:"none"}}>{l.website.replace(/^https?:\/\//,'').replace(/\/$/,'').slice(0,30)}</a></div>}
              </div>))}
            </div>
          </div>
        );})}
      </div>
      {/* Drag-Ghost: folgt dem Finger/Cursor */}
      {draggedId&&draggedLead&&dragPos&&<div style={{position:"fixed",left:dragPos.x-130,top:dragPos.y-30,width:240,background:T.bgCard,border:`2px solid ${T.accent}`,borderRadius:10,padding:12,pointerEvents:"none",zIndex:2000,boxShadow:"0 12px 30px rgba(0,0,0,0.5)",transform:"rotate(-2deg)"}}>
        <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>{draggedLead.company||"Unbekannt"}</div>
        {draggedLead.contactName&&<div style={{fontSize:12,color:T.textMuted}}>{draggedLead.contactName}</div>}
      </div>}
    </>}

    {/* ═══ TABLE VIEW ═══ */}
    {viewMode==="table"&&<div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,overflow:"auto",WebkitOverflowScrolling:"touch"}}>
      {fl.length===0?<Empty icon="leads" title="Keine Leads" desc="Importiere Leads über CSV Import"/>:(
        <div className="table-wrap" style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>{["Firma","Telefon","Website","Stadt","Status",""].map(h=>(<th key={h} style={{padding:"12px 16px",textAlign:"left",fontSize:11,fontWeight:600,color:T.textDim,textTransform:"uppercase"}}>{h}</th>))}</tr></thead>
          <tbody>{fl.map(l=>(<tr key={l.id} style={{borderBottom:`1px solid ${T.border}`,cursor:"pointer"}} onClick={()=>openLead(l)}>
            <td style={{padding:"12px 16px"}}><div style={{fontWeight:600,fontSize:13,display:"flex",alignItems:"center",gap:6}}>{l.privateToUser===user.id&&<span title="Nur für dich sichtbar" style={{fontSize:11}}>🔒</span>}{l.company}</div><div style={{fontSize:11,color:T.textDim}}>{l.category}</div></td>
            <td style={{padding:"12px 16px",fontSize:13,fontFamily:"'JetBrains Mono',monospace",whiteSpace:"nowrap"}}>{l.phone?<a href={`tel:${l.phone}`} onClick={e=>e.stopPropagation()} style={{color:T.info,textDecoration:"none"}}>{l.phone}</a>:<span style={{color:T.textDim}}>—</span>}</td>
            <td style={{padding:"12px 16px",fontSize:12,maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} onClick={e=>e.stopPropagation()}>{l.website ? <a href={l.website.startsWith("http")?l.website:`https://${l.website}`} target="_blank" rel="noopener noreferrer" style={{color:T.info,textDecoration:"none"}}>{l.website.replace(/^https?:\/\//,'').replace(/\/$/,'')}</a> : <span style={{color:T.textDim}}>—</span>}</td>
            <td style={{padding:"12px 16px",fontSize:13}}>{l.city}</td>
            <td style={{padding:"12px 16px"}}><Badge color={ALL_STATUSES.find(s=>s.id===l.status)?.color}>{ALL_STATUSES.find(s=>s.id===l.status)?.label}</Badge></td>
            <td style={{padding:"12px 16px"}} onClick={e=>e.stopPropagation()}>
              <div style={{display:"flex",gap:4}}>
                <Button variant="ghost" size="sm" icon="phone" onClick={()=>callAndLog(l)}>Anruf</Button>
                <button onClick={()=>deleteLead(l.id)} style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4,borderRadius:4}} title="Lead löschen"><Icon name="delete" size={15}/></button>
              </div>
            </td>
          </tr>))}</tbody>
        </table></div>)}
    </div>}
    {/* Pagination */}
    {totalPages>1&&<div style={{display:"flex",justifyContent:"center",alignItems:"center",gap:8,marginTop:16,marginBottom:16}}>
      <button onClick={()=>loadLeads(currentPage-1,search,filter)} disabled={currentPage<=1} style={{padding:"6px 14px",borderRadius:8,border:`1px solid ${T.border}`,background:currentPage<=1?"transparent":T.bgCard,color:currentPage<=1?T.textDim:T.text,cursor:currentPage<=1?"default":"pointer",fontSize:13}}>Zurück</button>
      <span style={{fontSize:13,color:T.textDim}}>Seite {currentPage} von {totalPages} ({totalLeads} Leads)</span>
      <button onClick={()=>loadLeads(currentPage+1,search,filter)} disabled={currentPage>=totalPages} style={{padding:"6px 14px",borderRadius:8,border:`1px solid ${T.border}`,background:currentPage>=totalPages?"transparent":T.bgCard,color:currentPage>=totalPages?T.textDim:T.text,cursor:currentPage>=totalPages?"default":"pointer",fontSize:13}}>Weiter</button>
    </div>}
    <Modal open={showD} onClose={()=>setShowD(false)} title={sel?.company||"Lead Details"} width={820}>
      {sel&&<LeadDetail lead={sel} user={user} users={users} onUpdate={upd} onLogCall={()=>callAndLog(sel)} onDelete={()=>{deleteLead(sel.id);setShowD(false);}} reload={reload}/>}
    </Modal>
  </div>);
};

// ─── LEAD DETAIL (Tabbed) ───

// Lead-Daten als kopierbare Felder (für Calendly-Modal)
const CopyableLeadData = ({lead}) => {
  const [copied,setCopied] = useState("");
  const copy = (val,key) => {
    if(!val||val==="—") return;
    if(navigator.clipboard&&navigator.clipboard.writeText){
      navigator.clipboard.writeText(val).then(()=>{setCopied(key);setTimeout(()=>setCopied(""),1200);}).catch(()=>{});
    } else {
      // Fallback für ältere Browser/Mobile
      const ta=document.createElement("textarea");ta.value=val;ta.style.position="fixed";ta.style.opacity="0";document.body.appendChild(ta);ta.select();
      try{document.execCommand("copy");setCopied(key);setTimeout(()=>setCopied(""),1200);}catch(e){}
      document.body.removeChild(ta);
    }
  };
  const fields = [
    ["Firma",lead.company,"company"],
    ["Kontakt",lead.contactName,"name"],
    ["Vorname",(lead.contactName||"").split(" ")[0]||"","firstname"],
    ["E-Mail",lead.email,"email"],
    ["Telefon",lead.phone,"phone"],
    ["Stadt",lead.city,"city"],
    ["Website",lead.website,"website"],
  ].filter(([_,v])=>v&&v!=="—");
  return (<div>
    <div style={{fontSize:13,fontWeight:700,color:T.textMuted,marginBottom:10}}>Lead-Daten (Klick zum Kopieren)</div>
    <div style={{display:"flex",flexDirection:"column",gap:6}}>
      {fields.map(([label,val,key])=>(<button key={key} onClick={()=>copy(val,key)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,padding:"8px 12px",background:copied===key?`${T.success}22`:T.bgInput,border:`1px solid ${copied===key?T.success:T.border}`,borderRadius:8,cursor:"pointer",textAlign:"left",transition:"all 0.15s",width:"100%"}} onMouseEnter={e=>{if(copied!==key)e.currentTarget.style.borderColor=T.accent;}} onMouseLeave={e=>{if(copied!==key)e.currentTarget.style.borderColor=T.border;}}>
        <div style={{minWidth:0,flex:1}}>
          <div style={{fontSize:10,color:T.textDim,textTransform:"uppercase",fontWeight:600,letterSpacing:0.4,marginBottom:2}}>{label}</div>
          <div style={{fontSize:13,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontFamily:label==="Telefon"||label==="E-Mail"?"'JetBrains Mono',monospace":"inherit"}}>{val}</div>
        </div>
        <div style={{fontSize:11,color:copied===key?T.success:T.textDim,flexShrink:0,fontWeight:600}}>{copied===key?"✓ Kopiert":"Kopieren"}</div>
      </button>))}
    </div>
  </div>);
};

const DISCOVERY_SCRIPT = [
  {section:"1. Einstieg",text:`Hallo [Name], schön, dass du dir die Zeit genommen hast. Gehts dir gut?
(Smalltalk max. 30 Sek., dann gleich zur Sache)

„Super. Ganz kurz zum Ablauf: Heute klären wir drei Dinge: Wo steht ihr, wo wollt ihr hin, und ob wir die richtigen dafür sind. Am Ende weißt du, ob ein nächster Schritt Sinn ergibt — und wir auch. Passt?"`,questions:[]},
  {section:"2. Agenda / Gesprächsrahmen",text:`Ich habe ca. 30 Minuten eingeplant, je nachdem, wie tief wir einsteigen. Ich stelle dir ein paar Fragen zu eurem aktuellen Online-Auftritt und Zielen – und gebe dir gerne schon erste Einschätzungen mit, wenn sinnvoll. Klingt das gut?`,questions:[]},
  {section:"3. Vorstellung des Unternehmens (Lead)",text:`Ich hab mir im Vorfeld natürlich schon einen Überblick verschafft über dich und dein Unternehmen, ich hör das allerdings immer gern nochmal von den Menschen selber, deswegen: Stell dich gern einmal mit eigenen Worten vor [Name]. Wer bist du? Was machst du?

Bevor wir tiefer einsteigen — bist du derjenige, der am Ende entscheidet, ob ihr mit einer Agentur zusammenarbeitet, oder müssen da noch andere mit an den Tisch?

Ich entscheide: "Perfekt, das macht es einfacher."
Gemeinsam mit Partner XY: "Verstanden, dann denk schon mal mit, dass wir XY beim nächsten Termin dabei haben — sonst wiederholen wir's."
Mein Chef entscheidet: "Verstehe — dann würde ich vorschlagen, dass wir den tiefen Fachteil heute gemeinsam machen, und für den nächsten Termin deinen Chef direkt dazu holen. Sonst müssen wir alles doppelt erklären. Macht das Sinn?"`,questions:["Wer ist bei euch für den Online-Auftritt verantwortlich?","Wie viele Leute seid ihr im Team?","An wen verkauft ihr da hauptsächlich? (Wenn nicht schon offensichtlich)","Wie läuft Social Media bei euch aktuell — und wie zufrieden bist du damit?"]},
  {section:"4. Social-Media-Analyse",text:`[Name] Ich würd mir mit dir gemeinsam euren Auftritt anschauen, und dir direkt sagen, was ich sehe — was funktioniert und was nicht. Bist du damit einverstanden?

Analyse-Check (2–3 Minuten):
• Posting-Frequenz und Konsistenz
• Qualität der Inhalte (Bilder, Captions, CTAs)
• Bio / Profiloptimierung
• Engagement-Rate (Kommentare, nicht nur Likes)
• Eine konkrete Sache, die sie richtig machen (Lob zuerst)
• Zwei bis drei konkrete Lücken

"Kurz zusammengefasst: Ihr habt die Basics, aber der Auftritt generiert gerade keine Anfragen.

Account läuft okay: "Ihr habt die Basics — was fehlt, ist Strategie. Das lässt sich strukturieren."
Account läuft schlecht: "Ihr habt Präsenz, aber sie arbeitet nicht für euch. Das lässt sich drehen."
Account läuft kaum: "Ihr seid quasi bei null — das ist ehrlich gesagt ein Vorteil, weil wir direkt richtig aufbauen können."
Account nicht vorhanden: "Ich hab nach Ihrem Social Media gesucht und nichts gefunden — stimmt das, oder hab ich was übersehen?"`,questions:["Notizen zur Analyse:"]},
  {section:"5. Vorschlag & nächster Schritt",text:`„Was ich bisher gehört habe, klingt gut — ich sehe mehrere Stellen, wo wir euch nach vorne bringen könnten. Die Frage ist jetzt: Kannst du dir vorstellen, mit uns zusammenzuarbeiten, wenn die Zahlen und das Angebot passen?

Super. Dann lass uns einen zweiten Termin machen, in dem ich dir ein konkretes Angebot vorstelle. Nimm bitte [Chef] mit dazu, damit wir nicht doppelt erklären müssen. Passt [Wochentag] [Uhrzeit]?

→ Termin fixieren.

„Super, dann schicke ich dir im Anschluss die Terminbestätigung mit Google-Meet-Link. Ich freue mich auf unser nächstes Gespräch!"`,questions:[]},
];

// Sales-Call Skript (zweiter Termin)
const SALES_SCRIPT = [
  {section:"1. Einstieg",text:`Hey, grüße dich! Alles gut bei dir?
Freut mich zu allererst mal, dass das Ganze mit unserem Termin so gut geklappt hat. :-)

Was ist in den letzten Tagen passiert seit wir das letzte mal gesprochen haben?
Konntest du schon in die Fallstudien reinschauen? :)`,questions:[]},
  {section:"Option 1: Wenn Lead Fallstudien NICHT angesehen hat",text:`"Name, ich bin auch Unternehmer, ich verstehe das total… Gerade in so Phasen wenn viel los ist können so Sachen passieren.

Entschuldige dass ich jetzt hier so nachhake, aber hast du es dir nicht angeschaut, weil es einfach untergegangen ist, oder weil du einfach keine Priorität auf (Problem aus Discoverycall einfügen) hast.

Wenn keine Priorität:
"Gut mein Lieber, dann danke für deine Zeit - wenn das Thema für dich keine Wichtigkeit aktuell hat, dann macht es ja wenig Sinn für uns Beide, dass wir uns jetzt hier die Zeit nehmen und anschauen wie die ideale Lösung ausschauen könnte. Richtig? (Ja, abholen)
Dann sage ich danke für deine Zeit und ich wünsche dir auf deinem weiteren Wege alles Gute und sollte sich an der Situation etwas ändern, melde dich gerne!"

Wenn keine Zeit:
"Ok, du wirst mir zustimmen, wenn einem etwas wichtig ist, dann findet man immer einen Weg, das Ganze auch wirklich umzusetzen?
Schau, mir ist sehr wichtig, dass du dir die Fallstudien anschaust. Warum?
Weil du dann mich besser kennenlernst, meine Dienstleistung besser kennenlernen darfst und… Wie soll eine Zusammenarbeit funktionieren, wenn ich sage, schau dir bitte die Fallstudien an und du schaust sie dir nicht an, weil du keine Zeit hast?
Dann wird es wahrscheinlich auch nicht funktionieren, dass wir das Ziel erreichen, und das ist mir wichtig. Deswegen würde ich dich darum bitten, dass wir einen neuen Termin vereinbaren, du schaust dir bis dahin die Fallstudien an. Passt das so für dich?"`,questions:[]},
  {section:"Option 2: Lead hat Fallstudien angeschaut — Agenda",text:`Kurz mal zum Ablauf von unserem heutigen Call:
Wir beide haben letztes Mal ja schon gesprochen, was dich aktuell bei der Thematik beschäftigt und wo es hingehen soll. Heute würde ich mit dir mal genau ins Detail gehen. Das heißt, wir schauen uns nochmal detailliert an, was die Herausforderungen sind. Dann schauen wir uns an:
1. Kann ich dabei überhaupt helfen,
2. Will ich dabei helfen
3. und vor allem das Wichtigste: willst du, dass ich dir dabei helfe.

Anschließend sprechen wir durch, was du haben willst. Wenn ich das liefern kann, werde ich dir ein konkretes Angebot machen und dann gehen wir das gemeinsam an, oder eben nicht. Das heißt, wir treffen eine klare Entscheidung am Ende des Gespräches: ja, wir gehen die Dinge gemeinsam an, oder eben nicht.
Passt das für dich?

"Ich wollte mir das Ganze einfach mal anhören:"
→ Bist du nur hier, um dich zu informieren, oder bist du hier, um eine nachhaltige Entscheidung zu treffen für dich und dein Vorankommen?
Wenn informieren - kein Closing Call durchführen; klassischer „no sayer\u201c. (genauere Infos im YouTube Video)`,questions:[]},
  {section:"IST-Situation",text:`Perfekt, dann gehen wir das mal gemeinsam an…`,questions:[
    "1. Was ist aktuell, ich meine, wir haben diese Punkte im letzten Gespräch schon teilweise angeschnitten, die größte Herausforderung aktuell im Bereich Onlineauftritt? (Schmerzlevel 1)",
    "2. Warum ist es dir so wichtig, XYZ zu lösen? (umgedrehte Psychologie nutzen hier; Wichtigkeit des Problems runterstufen) (Schmerzlevel 1)",
    "3. Was verändert sich dann? (Desire steigern)",
    "4. Wie lange hast du das Problem schon? (Schmerzlevel 2)",
    "5. Was macht das mit dir? (Schmerzlevel 2)",
    "6. Ok, und was glaubst du, was für Auswirkungen das hat, wenn du jetzt im Prinzip genau so weitermachst wie bisher? (Schmerzlevel 3)"
  ]},
  {section:"Wunsch-Situation",text:`Ok. Dann weiß ich jetzt, wie deine aktuelle Situation aussieht... lass uns jetzt mal über die nächste Zeit sprechen :)

(Im B2C hier Future Pacing einbauen. Dazu gerne 1 zu 1 Skriptcheck buchen)`,questions:[
    "Welche konkreten Ziele möchtest du in der Zusammenarbeit mit mir innerhalb der nächsten 3-6 Monate erreichen? Erzähl!"
  ]},
  {section:"Bedarfsanalyse",text:`Ok, dann weiß ich jetzt, wo du stehst und wo du hin möchtest! :-)

Ich werde dir jetzt noch ein paar Fragen stellen, um genauer herauszufinden, was du haben magst und wie für dich die ideale Zusammenarbeit aussieht:`,questions:[
    "1. Wie muss für dich eine Zusammenarbeit zwischen uns beiden aufgebaut sein, damit du ganz genau weißt, das ist das Richtige, damit fühlst du dich gut und das wird auch wirklich funktionieren?",
    "2. Wie müsste eine Zusammenarbeit für dich aussehen, damit du wirklich für dich sicherstellen kannst, das ist das Richtige, damit fühlst du dich gut und damit werden wir auch wirklich zu 100% das dir gesetzte Ziel erreichen?",
    "3. Was möchtest du alles in den Bereichen XYZ umgesetzt bekommen, damit du sicher sein kannst, dass du dein gesetztes Ziel erreichst?",
    "4. Was brauchst du noch, damit du weißt, dass du damit dein gesetztes Ziel auch wirklich erreichst?",
    "5. Ok, sehr gut, und wie müsste die Zusammenarbeit zwischen uns Beiden aussehen, damit du ganz genau weißt, das ist das Richtige, damit fühlst du dich gut und das wird auch wirklich funktionieren?"
  ]},
  {section:"Commitment & Lückencheck",text:`Commitment jetzt abholen und herausfinden, ob wir alle Punkte haben für die maßgeschneiderte Lösung:`,questions:[
    "1. Gibt es abgesehen von den Punkten noch irgendetwas, wo du sagst: „hmm… das fehlt, damit ich mein Ziel erreichen kann!\u201c Gibt's da noch etwas?",
    "2. Gut... dann nehmen wir mal an, du bekommst genau das und es läuft genauso ab, bist du dann davon überzeugt, dass wir gemeinsam dein gesetztes Ziel erreichen?"
  ]},
  {section:"Produktpräsentation",text:`Ok, sehr schön, dann lass uns loslegen :)

Also: Mir ist es sehr wichtig, immer intensiv mit meinen Kunden zusammenarbeiten zu können.
Das heißt, Individualität und Erreichbarkeit stehen bei uns immer an erster Stelle. Aus diesem Grund habe ich auch meine Dienstleistung auf folgende 3 Säulen aufgebaut:

1. Säule: Social Media Betreuung
Paket und Dienstleistung vorstellen:
- Ideenfindung und Skripten
- 1-3 Drehtage vor Ort
- Videoschnitt
- Posten mit SEO optimierten Captions
- Storys
- Communitymanagement (Kommentare, Nachrichten, Anfragen)

2. Säule: Support
Hier sprechen wir wirklich von der eigentlichen Betreuung bzw. dem Support. (Vorname) du sagst, du hast Bock da drauf, das Ganze mit mir anzugehen, dann haben wir beide von nun an alle 2 Wochen ein Updatecall.

In diesen Meetings schauen wir uns dann an:
1. Was läuft gut?
2. Was läuft schlecht?
3. Was sind die nächsten Schritte oder Planung von Drehtagen.

Dann im darauf folgenden Termin können wir uns wieder anschauen:
1. Was läuft gut?
2. Was läuft schlecht?
3. Was sind die nächsten Schritte, damit wir auch wirklich Ziel X erreichen können?

In diesen Meetings können wir auch konkret Dinge ausarbeiten wie beispielsweise (alles aus Bedarfsanalyse hier einsetzen was geht).

Zusätzlich zu den zweiwöchigen Meetings hast du auch meine Handynummer. Unter dieser Nummer kannst du jeder Zeit bei Notfällen einfach durchrufen. Das heißt: wenn es außerhalb der Meetings etwas gibt, wo du sagst hier brauchst Rat oder Unterstützung - nimm den Hörer in die Hand und ruf durch. Sollte ich in einem Meeting sein, so wie jetzt gerade rufe ich nach dem Meeting durch. Ich habe hierzu immer einen Puffer zwischen meinen Calls. :)

Neben der telefonischen Erreichbarkeit haben wir auch einen WhatsApp Support. Du kannst gerne abseits der Meetings auch einfach diesen nutzen. Hier kommt an Werktagen immer eine Antwort innerhalb von 30 Minuten und wie schon erwähnt. Am einfachsten einfach durchrufen.

3. Säule: Unsere Garantie
Wie läuft es jetzt mit der Garantie ab? Du hast innerhalb von 6 Monaten über 1 Mio + Aufrufe. Wenn nicht arbeiten wir solange kostenlos weiter bis wir das Ergebnis erreicht haben.

Hast du noch Fragen zur Zusammenarbeit?
(Sollten hier Fragen von deinem Gegenüber kommen - Dringlich das YouTube Video zum Skript anschauen)`,questions:[]},
  {section:"Commitment",text:`Sehr schön. Jetzt, nach all dem, was du gehört hast und auch ganz genau weißt, wie die Zusammenarbeit mit uns aussieht.

Bist du der Überzeugung, dass du mit mir gemeinsam und dieser Art der Zusammenarbeit, dein gesetztes Ziel erreichst?
(Ja abwarten)
Was macht dich dabei so sicher?

Okay. Sehr schön. Folgendes: Wie du siehst, ist uns Individualität extrem wichtig und wir können in dem Ausmaß offensichtlicherweise keine 10.000 Leute betreuen. Das geht nicht.

Deswegen stelle ich dir folgende Frage:

Warum sollten wir ausgerechnet mit dir zusammenarbeiten? Also, was macht dich deiner Meinung nach zum perfekten Teilnehmer für uns? (Antwort abwarten. Frage nicht stellen bei grünen Persönlichkeitstypen. Im B2B nur stellen, wenn du mit dem Entscheider im Call sitzt)`,questions:["Antwort: Was macht dich zum perfekten Teilnehmer?"]},
  {section:"Möglichkeiten A oder B",text:`(Vorname des Gegenübers), bevor wir über die Investition sprechen… es gibt zwei Möglichkeiten, wie das Gespräch hier ausgehen kann:

Möglichkeit A: Du sagst „Hey XYZ, das schaut cool aus. Das passt zu mir und wir gehen das gemeinsam an.\u201c Dann sage ich „hey, herzlichen Glückwunsch - lass uns einen Onboarding-Termin vereinbaren und uns das gemeinsam angehen.\u201c Das ist die Möglichkeit A.

Oder

Möglichkeit B: Du sagst, „Ramon oder Felix, ich fühle mich nicht wohl, ich möchte das Ganze nicht angehen. Das respektiere ich und sage dann danke für deine Zeit.\u201c

Passt das soweit, dass wir bei einer dieser beiden Möglichkeiten bleiben und nicht den Arzt, die Zahnfee oder sonst wen fragen ob wir das gemeinsam angehen?`,questions:[]},
  {section:"Preispitch",text:`Danach Preispitch - völlig trocken:

Dann kommen wir zum letzten Punkt. Preislich ist das Ganze bei gerade mal X netto einmalig oder bei X netto auf Y Raten - geht das bei dir?`,questions:["Reaktion / Notiz:"]},
];

const LeadDetail = ({lead,user,users,onUpdate,onLogCall,onDelete,reload}) => {
  const [tab,setTab]=useState("info");
  const [note,setNote]=useState("");
  const [at,setAt]=useState("");const [ad,setAd]=useState("");const [atm,setAtm]=useState("");const [showA,setShowA]=useState(false);const [apptEmail,setApptEmail]=useState(lead.email||"");
  const [showCalendly,setShowCalendly]=useState(false);
  const [editingLead,setEditingLead]=useState(false);
  const [editForm,setEditForm]=useState({});
  const [calendlyAutoSaved,setCalendlyAutoSaved]=useState(false);

  // Calendly Event-Listener: wenn Termin im iframe gebucht wird, automatisch im CRM speichern
  useEffect(()=>{
    if(!showCalendly) return;
    const handler = async(e)=>{
      // Nur Calendly-Events akzeptieren
      if(!e.data || typeof e.data !== "object") return;
      if(typeof e.data.event !== "string" || !e.data.event.startsWith("calendly")) return;

      if(e.data.event === "calendly.event_scheduled"){
        // Termindaten kommen oft im payload, aber das Datumsformat ist nicht garantiert.
        // Wir nehmen das aktuelle Datum/Zeit als Fallback wenn nichts mitkommt.
        const payload = e.data.payload || {};
        const eventUri = payload.event?.uri || payload.invitee?.uri || "";

        // Versuche das Datum aus dem payload zu lesen — Calendly v2 schickt invitee.uri,
        // das echte Datum müssen wir oft erst nachladen. Pragmatisch: Standard-Discovery-Call,
        // User trägt Datum/Zeit nochmal ein wenn's stimmt.
        try {
          // Auto-fill: Titel ist Discovery Call, Datum/Zeit aus Payload falls da
          const startTime = payload.event?.start_time || payload.scheduled_event?.start_time;
          let dateStr = "", timeStr = "";
          if(startTime){
            const d = new Date(startTime);
            // Lokales Datum ohne Timezone-Verschiebung
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth()+1).padStart(2,"0");
            const dd = String(d.getDate()).padStart(2,"0");
            const hh = String(d.getHours()).padStart(2,"0");
            const mi = String(d.getMinutes()).padStart(2,"0");
            dateStr = `${yyyy}-${mm}-${dd}`;
            timeStr = `${hh}:${mi}`;
          }
          // Auto-Save ins CRM
          if(dateStr && timeStr){
            const res = await api.post("appointments",{
              leadId: lead.id,
              leadCompany: lead.company,
              title: "Discovery Call",
              date: dateStr,
              time: timeStr,
              userId: user.id,
              userName: user.name
            });
            if(res?.success){
              setCalendlyAutoSaved(true);
              if(reload) await reload(STORAGE_KEYS.appointments);
              // Nach 2 Sek Modal automatisch schließen
              setTimeout(()=>{setShowCalendly(false);setCalendlyAutoSaved(false);}, 2500);
            }
          } else {
            // Fallback: nur Titel/Felder vorbefüllen, User muss manuell speichern
            setAt("Discovery Call");
          }
        } catch(err){
          setAt("Discovery Call");
        }
      }
    };
    window.addEventListener("message", handler);
    return ()=>window.removeEventListener("message", handler);
  },[showCalendly, lead.id]);

  // Lead-Notizen aus DB neu laden
  const reloadNotes = async () => {
    try {
      const notes = await api.get("lead-notes", `leadId=${lead.id}`);
      if(Array.isArray(notes)) onUpdate({...lead, notes});
    } catch(e){ /* reloadNotes */ }
  };

  const addNote = async () => {
    if(!note.trim()) return;
    await api.post("lead-notes", {leadId:lead.id, text:note, by:user.name});
    setNote("");
    await reloadNotes();
  };

  const deleteNote = async (noteId) => {
    if(!confirm("Diese Notiz wirklich löschen?")) return;
    try {
      await api.del("lead-notes", {id:noteId, userId:user.id, userName:user.name});
      await reloadNotes();
    } catch(e){
      alert("Notiz konnte nicht gelöscht werden. Du kannst nur eigene Notizen löschen.");
    }
  };

  // Auto-Refresh: alle 15 Sek. neue Notizen von Kollegen abholen
  useEffect(() => {
    const t = setInterval(reloadNotes, 15000);
    return () => clearInterval(t);
  }, [lead.id]);

  const [apptStatus,setApptStatus]=useState("");
  const addAppt=async()=>{
    if(!at||!ad||!atm){setApptStatus("missing");setTimeout(()=>setApptStatus(""),2500);return false;}
    setApptStatus("saving");
    try{
      const res = await api.post("appointments",{leadId:lead.id,leadCompany:lead.company,title:at,date:ad,time:atm,userId:user.id,userName:user.name});
      if(res&&res.error){setApptStatus("error");setTimeout(()=>setApptStatus(""),3000);return false;}
      if(reload) await reload(STORAGE_KEYS.appointments);
      setApptStatus("success");
      setTimeout(()=>{setApptStatus("");setAt("");setAd("");setAtm("");setApptEmail("");setShowA(false);setShowCalendly(false);},900);
      return true;
    }catch(e){setApptStatus("error");setTimeout(()=>setApptStatus(""),3000);return false;}
  };

  // Discovery call data (stored in lead.discovery JSON)
  const disc = lead.discovery ? (typeof lead.discovery==="string"?JSON.parse(lead.discovery):lead.discovery) : {};
  const updateDisc = (key,val) => {const d={...disc,[key]:val};onUpdate({...lead,discovery:JSON.stringify(d)});};

  // Angebot/Kunde data (stored in lead.deal JSON)
  const deal = lead.deal ? (typeof lead.deal==="string"?JSON.parse(lead.deal):lead.deal) : {};
  const updateDeal = (key,val) => {const d={...deal,[key]:val};onUpdate({...lead,deal:JSON.stringify(d)});};

  const tabs=[{id:"info",label:"Info"},{id:"discovery",label:"Discovery"},{id:"sales",label:"Sales Call"},{id:"deal",label:"Angebot"}];

  // Editierbare Skripte:
  // Hierarchie: Lead-Override (in disc._script_discovery) > Globaler Default (in DB) > Eingebauter Default (DISCOVERY_SCRIPT)
  const [globalDiscovery,setGlobalDiscovery] = useState(null);
  const [globalSales,setGlobalSales] = useState(null);
  const [savingGlobal,setSavingGlobal] = useState(false);
  // Lade globale Skripte beim Mount
  useEffect(()=>{
    api.get("settings/script_discovery").then(r=>{ if(r&&r.value)setGlobalDiscovery(r.value); }).catch(()=>{});
    api.get("settings/script_sales").then(r=>{ if(r&&r.value)setGlobalSales(r.value); }).catch(()=>{});
  },[]);
  const discoveryScript = disc._script_discovery || globalDiscovery || DISCOVERY_SCRIPT;
  const salesScript = disc._script_sales || globalSales || SALES_SCRIPT;
  const [editingScriptKey,setEditingScriptKey] = useState(null);
  const [editingScriptValue,setEditingScriptValue] = useState("");

  const startEditScript = (which, sectionIndex, field, qi=null, currentValue="") => {
    setEditingScriptKey(`${which}_${sectionIndex}_${field}${qi!==null?`_q${qi}`:""}`);
    setEditingScriptValue(currentValue);
  };

  // mode = "lead" (nur diesen Lead) oder "global" (alle Leads)
  const saveScriptEdit = async (mode="lead") => {
    if(!editingScriptKey) return;
    const [which, sectionIndexStr, ...rest] = editingScriptKey.split("_");
    const sectionIndex = parseInt(sectionIndexStr);
    const field = rest[0];
    const qi = rest[1]?.startsWith("q")?parseInt(rest[1].slice(1)):null;
    const baseScript = which==="discovery"?discoveryScript:salesScript;
    const cloned = baseScript.map(s=>({...s, questions:[...s.questions]}));
    if(field==="section") cloned[sectionIndex].section = editingScriptValue;
    else if(field==="text") cloned[sectionIndex].text = editingScriptValue;
    else if(field==="q" && qi!==null) cloned[sectionIndex].questions[qi] = editingScriptValue;

    if(mode==="global"){
      // Global speichern: in DB ablegen + lokalen State updaten
      setSavingGlobal(true);
      const settingKey = which==="discovery"?"settings/script_discovery":"settings/script_sales";
      try {
        await api.put(settingKey, {value: cloned, userId: user.id});
        if(which==="discovery") setGlobalDiscovery(cloned); else setGlobalSales(cloned);
        // WICHTIG: Falls dieser Lead ein Override hatte, das Override löschen damit der neue globale Default greift
        const overrideKey = which==="discovery"?"_script_discovery":"_script_sales";
        if(disc[overrideKey]){
          const newDisc = {...disc};
          delete newDisc[overrideKey];
          onUpdate({...lead, discovery: JSON.stringify(newDisc)});
        }
      } catch(e){ alert("Fehler beim globalen Speichern: "+e.message); }
      setSavingGlobal(false);
    } else {
      // Nur für diesen Lead speichern
      const targetKey = which==="discovery"?"_script_discovery":"_script_sales";
      const newDisc = {...disc, [targetKey]: cloned};
      onUpdate({...lead, discovery: JSON.stringify(newDisc)});
    }
    setEditingScriptKey(null);
    setEditingScriptValue("");
  };

  const cancelScriptEdit = () => { setEditingScriptKey(null); setEditingScriptValue(""); };

  // Reset auf eingebauten Default (löscht Lead-Override UND optional globalen Default)
  const resetScript = (which, scope="lead") => {
    if(scope==="lead"){
      if(!confirm(`${which==="discovery"?"Discovery":"Sales"}-Skript für diesen Lead auf den globalen Standard zurücksetzen? Deine Antworten bleiben erhalten.`)) return;
      const targetKey = which==="discovery"?"_script_discovery":"_script_sales";
      const newDisc = {...disc};
      delete newDisc[targetKey];
      onUpdate({...lead, discovery: JSON.stringify(newDisc)});
    } else {
      if(!confirm(`Globalen ${which==="discovery"?"Discovery":"Sales"}-Default löschen? Alle Leads die das eingebaute Skript verwenden, bekommen wieder den ursprünglichen Skript-Text.`)) return;
      const settingKey = which==="discovery"?"settings/script_discovery":"settings/script_sales";
      api.del(settingKey, {}).then(()=>{
        if(which==="discovery") setGlobalDiscovery(null); else setGlobalSales(null);
      });
    }
  };

  const isAdmin = user.role === "admin";

  return (<div style={{display:"flex",flexDirection:"column",gap:0}}>
    {/* Tabs */}
    <div style={{display:"flex",gap:0,borderBottom:`2px solid ${T.border}`,marginBottom:20,overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
      {tabs.map(t=>(<button key={t.id} onClick={()=>setTab(t.id)} style={{padding:"10px 20px",fontSize:13,fontWeight:tab===t.id?700:400,color:tab===t.id?T.accent:T.textDim,background:"none",border:"none",borderBottom:tab===t.id?`2px solid ${T.accent}`:"2px solid transparent",cursor:"pointer",marginBottom:-2,whiteSpace:"nowrap",flexShrink:0}}>{t.label}</button>))}
    </div>

    {/* ═══ TAB: INFO ═══ */}
    {tab==="info"&&<div style={{display:"flex",flexDirection:"column",gap:20}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
        <div style={{fontSize:14,fontWeight:700}}>Lead-Informationen</div>
        {!editingLead ? (
          <Button size="sm" variant="secondary" icon="edit" onClick={()=>{setEditingLead(true);setEditForm({company:lead.company||"",contactName:lead.contactName||"",contactTitle:lead.contactTitle||lead.positionTitle||"",phone:lead.phone||"",email:lead.email||"",website:lead.website||"",linkedinUrl:lead.linkedinUrl||"",address:lead.address||"",city:lead.city||"",country:lead.country||"",category:lead.category||""});}}>Bearbeiten</Button>
        ) : (
          <div style={{display:"flex",gap:8}}>
            <Button size="sm" variant="ghost" onClick={()=>setEditingLead(false)}>Abbrechen</Button>
            <Button size="sm" icon="check" onClick={async()=>{
              await onUpdate({...lead, ...editForm, positionTitle:editForm.contactTitle});
              setEditingLead(false);
            }}>Speichern</Button>
          </div>
        )}
      </div>

      {!editingLead ? (
        <div className="grid-2col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          {[
            ["Firma",lead.company],
            ["Kontakt",lead.contactName+(lead.contactTitle||lead.positionTitle?` (${lead.contactTitle||lead.positionTitle})`:"")],
            ["Telefon",lead.phone||"—"],
            ["E-Mail",lead.email||"—"],
            ["Website",lead.website||"—"],
            ["LinkedIn",lead.linkedinUrl||"—"],
            ["Adresse",lead.address||"—"],
            ["Stadt",lead.city||"—"],
            ["Land",lead.country||"—"],
            ["Kategorie",lead.category||"—"],
          ].filter(([l,v])=>v && v!=="—" || ["Firma","Kontakt","Telefon","E-Mail","Website"].includes(l)).map(([l,v],i)=>{
            const isLink = l==="Website" && v && v!=="—";
            const isLinkedIn = l==="LinkedIn" && v && v!=="—";
            const isEmail = l==="E-Mail" && v && v!=="—";
            const isPhone = l==="Telefon" && v && v!=="—";
            return (<div key={i}><div style={{fontSize:11,color:T.textDim,textTransform:"uppercase",marginBottom:4}}>{l}</div>
              {isLink ? <a href={v.startsWith("http")?v:`https://${v}`} target="_blank" rel="noopener noreferrer" style={{fontSize:14,color:T.info,textDecoration:"none",wordBreak:"break-all"}}>{v}</a>
              : isLinkedIn ? <a href={v.startsWith("http")?v:`https://${v}`} target="_blank" rel="noopener noreferrer" style={{fontSize:14,color:T.info,textDecoration:"none",wordBreak:"break-all"}}>↗ Profil ansehen</a>
              : isEmail ? <a href={`mailto:${v}`} style={{fontSize:14,color:T.info,textDecoration:"none"}}>{v}</a>
              : isPhone ? <a href={`tel:${v}`} style={{fontSize:14,color:T.info,textDecoration:"none",fontFamily:"'JetBrains Mono',monospace"}}>{v}</a>
              : <div style={{fontSize:14}}>{v}</div>}
            </div>);
          })}
        </div>
      ) : (
        <div className="grid-2col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          <Input label="Firma" value={editForm.company} onChange={e=>setEditForm({...editForm,company:e.target.value})}/>
          <Input label="Ansprechpartner" value={editForm.contactName} onChange={e=>setEditForm({...editForm,contactName:e.target.value})}/>
          <Input label="Position" value={editForm.contactTitle} onChange={e=>setEditForm({...editForm,contactTitle:e.target.value})}/>
          <Input label="Telefon" value={editForm.phone} onChange={e=>setEditForm({...editForm,phone:e.target.value})}/>
          <Input label="E-Mail" value={editForm.email} onChange={e=>setEditForm({...editForm,email:e.target.value})} placeholder="kontakt@firma.de"/>
          <Input label="Website" value={editForm.website} onChange={e=>setEditForm({...editForm,website:e.target.value})} placeholder="firma.de"/>
          <Input label="LinkedIn" value={editForm.linkedinUrl} onChange={e=>setEditForm({...editForm,linkedinUrl:e.target.value})} placeholder="linkedin.com/in/..."/>
          <Input label="Adresse" value={editForm.address} onChange={e=>setEditForm({...editForm,address:e.target.value})}/>
          <Input label="Stadt" value={editForm.city} onChange={e=>setEditForm({...editForm,city:e.target.value})}/>
          <Input label="Land" value={editForm.country} onChange={e=>setEditForm({...editForm,country:e.target.value})}/>
          <Input label="Kategorie/Branche" value={editForm.category} onChange={e=>setEditForm({...editForm,category:e.target.value})}/>
        </div>
      )}

      <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
        <Select label="Status" options={ALL_STATUSES.map(s=>({value:s.id,label:s.label}))} value={lead.status} onChange={e=>onUpdate({...lead,status:e.target.value})}/>
        {user.role==="admin"&&<Select label="Zugewiesen an" options={[{value:"",label:"Nicht zugewiesen"},...users.map(u=>({value:u.id,label:u.name}))]} value={lead.assignedTo||""} onChange={e=>onUpdate({...lead,assignedTo:e.target.value||null})}/>}
        <Select label="Sichtbarkeit" options={[{value:"",label:"👥 Alle im Team"},{value:user.id,label:"🔒 Nur ich"}]} value={lead.privateToUser===user.id?user.id:""} onChange={e=>onUpdate({...lead,privateToUser:e.target.value||null})}/>
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        <Button icon="phone" onClick={onLogCall}>Anruf loggen</Button>
        <Button variant="secondary" icon="calendar" onClick={()=>setShowCalendly(true)}>Calendly Termin buchen</Button>
        <Button variant="secondary" icon="calendar" onClick={()=>setShowA(!showA)}>Termin im CRM eintragen</Button>
        <Button variant="danger" icon="delete" onClick={onDelete}>Lead löschen</Button>
      </div>
      {showA&&<div style={{background:T.bgInput,borderRadius:12,padding:16,display:"flex",gap:12,flexWrap:"wrap",alignItems:"end"}}>
        <Input label="Titel" value={at} onChange={e=>setAt(e.target.value)} placeholder="z.B. Erstgespräch"/>
        <Input label="Datum" type="date" value={ad} onChange={e=>setAd(e.target.value)}/>
        <Input label="Uhrzeit" type="time" value={atm} onChange={e=>setAtm(e.target.value)}/>
        <Input label="E-Mail (Notiz)" value={apptEmail} onChange={e=>setApptEmail(e.target.value)} placeholder="kunde@email.de"/>
        <Button icon="check" onClick={addAppt} disabled={apptStatus==="saving"}>{apptStatus==="saving"?"Speichert...":"Speichern"}</Button>
        {apptStatus==="success"&&<span style={{fontSize:12,color:T.success,fontWeight:600}}>✓ Termin gespeichert!</span>}
        {apptStatus==="missing"&&<span style={{fontSize:12,color:T.warning}}>Bitte Titel, Datum und Uhrzeit ausfüllen</span>}
        {apptStatus==="error"&&<span style={{fontSize:12,color:T.danger}}>Fehler beim Speichern</span>}
      </div>}
      {/* ─── Calendly Modal ─── */}
      <Modal open={showCalendly} onClose={()=>{setShowCalendly(false);setCalendlyAutoSaved(false);}} title="Termin in Calendly buchen" width={isMobileDevice?540:1180}>
        {calendlyAutoSaved && <div style={{background:`${T.success}22`,border:`1px solid ${T.success}`,borderRadius:10,padding:14,marginBottom:14,display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:18}}>✓</span>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:T.success}}>Termin erkannt und im CRM gespeichert!</div>
            <div style={{fontSize:11,color:T.textDim,marginTop:2}}>Modal schließt sich automatisch...</div>
          </div>
        </div>}
        {isMobileDevice ? (
          // Mobile: vertikal gestapelt — Lead-Daten oben, Calendly iframe Mitte, Termin-Form unten
          <div style={{display:"flex",flexDirection:"column",gap:14,maxHeight:"75vh",overflowY:"auto",paddingRight:2}}>
            <CopyableLeadData lead={lead}/>

            <div style={{height:1,background:T.border}}/>

            {/* Calendly im iframe — funktioniert auf den meisten mobilen Browsern */}
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <div style={{fontSize:13,fontWeight:700,color:T.textMuted}}>Calendly</div>
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:T.accent,textDecoration:"none",fontWeight:600}}>↗ In neuem Tab öffnen</a>
              </div>
              <div style={{borderRadius:10,overflow:"hidden",border:`1px solid ${T.border}`,background:"#fff"}}>
                <iframe src={`${CALENDLY_URL}?embed_domain=${window.location.hostname}&embed_type=Inline&hide_gdpr_banner=1`} style={{width:"100%",height:580,border:"none",display:"block"}} title="Calendly"/>
              </div>
            </div>

            <div style={{height:1,background:T.border}}/>

            <div style={{fontSize:13,fontWeight:700,color:T.textMuted}}>Termindaten eintragen:</div>
            <Input label="Titel" value={at} onChange={e=>setAt(e.target.value)} placeholder="z.B. Discovery Call"/>
            <div style={{display:"flex",gap:10}}>
              <div style={{flex:1}}><Input label="Datum" type="date" value={ad} onChange={e=>setAd(e.target.value)}/></div>
              <div style={{flex:1}}><Input label="Uhrzeit" type="time" value={atm} onChange={e=>setAtm(e.target.value)}/></div>
            </div>
            <Input label="E-Mail des Kunden" value={apptEmail} onChange={e=>setApptEmail(e.target.value)} placeholder="kunde@email.de"/>
            <Button icon="check" onClick={addAppt} disabled={apptStatus==="saving"}>{apptStatus==="saving"?"Speichert...":apptStatus==="success"?"✓ Gespeichert!":"Termin speichern"}</Button>
            {apptStatus==="missing"&&<div style={{fontSize:12,color:T.warning,textAlign:"center"}}>Bitte Titel, Datum und Uhrzeit ausfüllen</div>}
            {apptStatus==="error"&&<div style={{fontSize:12,color:T.danger,textAlign:"center"}}>Fehler beim Speichern, bitte erneut versuchen</div>}
          </div>
        ) : (
          // Desktop: Zwei-Spalten — links Lead-Daten zum Copy-Pasten, rechts Calendly iframe
          <div style={{display:"grid",gridTemplateColumns:"320px 1fr",gap:16}}>
            {/* Linke Spalte: Lead-Daten */}
            <div style={{display:"flex",flexDirection:"column",gap:14,maxHeight:620,overflowY:"auto",paddingRight:4}}>
              <CopyableLeadData lead={lead}/>
              <div style={{height:1,background:T.border}}/>
              <div style={{fontSize:13,fontWeight:700,color:T.textMuted}}>Nach dem Buchen eintragen:</div>
              <Input label="Titel" value={at} onChange={e=>setAt(e.target.value)} placeholder="Discovery Call"/>
              <Input label="Datum" type="date" value={ad} onChange={e=>setAd(e.target.value)}/>
              <Input label="Uhrzeit" type="time" value={atm} onChange={e=>setAtm(e.target.value)}/>
              <Input label="E-Mail des Kunden" value={apptEmail} onChange={e=>setApptEmail(e.target.value)} placeholder="kunde@email.de"/>
              <Button icon="check" onClick={addAppt} disabled={apptStatus==="saving"}>{apptStatus==="saving"?"Speichert...":apptStatus==="success"?"✓ Gespeichert!":"Termin im CRM speichern"}</Button>
              {apptStatus==="missing"&&<div style={{fontSize:12,color:T.warning,textAlign:"center"}}>Bitte Titel, Datum und Uhrzeit ausfüllen</div>}
              {apptStatus==="error"&&<div style={{fontSize:12,color:T.danger,textAlign:"center"}}>Fehler beim Speichern, bitte erneut versuchen</div>}
            </div>
            {/* Rechte Spalte: Calendly iframe */}
            <div style={{borderRadius:10,overflow:"hidden",border:`1px solid ${T.border}`,background:"#fff"}}>
              <iframe src={`${CALENDLY_URL}?embed_domain=${window.location.hostname}&embed_type=Inline&hide_gdpr_banner=1`} style={{width:"100%",height:620,border:"none",display:"block"}} title="Calendly"/>
            </div>
          </div>
        )}
      </Modal>
      <div>
        <div style={{fontSize:14,fontWeight:700,marginBottom:10,display:"flex",alignItems:"center",gap:8}}>Notizen <span style={{fontSize:11,color:T.textDim,fontWeight:400}}>· für alle sichtbar</span></div>
        <div style={{display:"flex",gap:8,marginBottom:12}}><input value={note} onChange={e=>setNote(e.target.value)} placeholder="Notiz hinzufügen..." onKeyDown={e=>e.key==="Enter"&&addNote()} style={{flex:1,background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 14px",color:T.text,fontSize:13,outline:"none"}}/><Button icon="add" onClick={addNote} size="sm">Hinzufügen</Button></div>
        {(lead.notes||[]).length===0 ? <div style={{fontSize:12,color:T.textDim,padding:"10px 0"}}>Noch keine Notizen.</div> : (lead.notes||[]).slice().sort((a,b)=>new Date(b.date||b.created_at)-new Date(a.date||a.created_at)).map((n,i)=>{
          const author = n.by || n.created_by || "Unbekannt";
          const dateStr = n.date || n.created_at;
          const isMine = author === user.name;
          // Farbe aus Username generieren (deterministisch)
          const colors = [T.accent, T.accent+"bb", T.accent+"88", T.chart2, "#5f8a8c", "#9a7b52", "#7d6b96", "#95647a"];
          const colorIdx = author.split("").reduce((a,c)=>a+c.charCodeAt(0),0) % colors.length;
          const authorColor = colors[colorIdx];
          const initial = author.charAt(0).toUpperCase();
          return (<div key={n.id||i} style={{padding:"12px 14px",background:T.bgInput,borderRadius:10,marginBottom:8,borderLeft:`3px solid ${authorColor}`}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
              <div style={{width:28,height:28,borderRadius:"50%",background:authorColor,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:12,flexShrink:0}}>{initial}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:700,color:authorColor}}>{author}{isMine&&<span style={{color:T.textDim,fontWeight:400,fontSize:11,marginLeft:6}}>(Du)</span>}</div>
                <div style={{fontSize:10,color:T.textDim}}>{dateStr ? new Date(dateStr).toLocaleString("de", {dateStyle:"short", timeStyle:"short"}) : ""}</div>
              </div>
              {isMine && n.id && <button onClick={()=>deleteNote(n.id)} title="Eigene Notiz löschen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4,opacity:0.5}} onMouseEnter={e=>e.currentTarget.style.opacity=1} onMouseLeave={e=>e.currentTarget.style.opacity=0.5}><Icon name="delete" size={14}/></button>}
            </div>
            <div style={{fontSize:13,whiteSpace:"pre-wrap",lineHeight:1.5,paddingLeft:38}}>{n.text}</div>
          </div>);
        })}
      </div>
    </div>}

    {/* ═══ TAB: DISCOVERY CALL ═══ */}
    {tab==="discovery"&&<div style={{display:"flex",flexDirection:"column",gap:20}}>
      {/* Quick Info Header */}
      <div style={{background:T.bgInput,borderRadius:12,padding:16}}>
        <div className="resp-grid-3">
          <div><div style={{fontSize:11,color:T.textDim,textTransform:"uppercase",marginBottom:4}}>Firma</div><div style={{fontSize:14,fontWeight:600}}>{lead.company}</div></div>
          <div><div style={{fontSize:11,color:T.textDim,textTransform:"uppercase",marginBottom:4}}>Kontakt</div><div style={{fontSize:14}}>{lead.contactName||"—"}</div></div>
          <div><div style={{fontSize:11,color:T.textDim,textTransform:"uppercase",marginBottom:4}}>Telefon</div><div style={{fontSize:14,fontFamily:"'JetBrains Mono',monospace"}}>{lead.phone||"—"}</div></div>
        </div>
      </div>

      {/* Key Discovery Fields */}
      <div className="resp-grid-2">
        <Input label="Mitarbeiteranzahl" value={disc.employees||""} onChange={e=>updateDisc("employees",e.target.value)} placeholder="z.B. 15"/>
        <Input label="Entscheider (Name + Position)" value={disc.decision_maker||""} onChange={e=>updateDisc("decision_maker",e.target.value)} placeholder="z.B. Max Müller, GF"/>
        <Input label="Aktueller Online-Auftritt (1-10)" value={disc.online_rating||""} onChange={e=>updateDisc("online_rating",e.target.value)} placeholder="z.B. 4"/>
        <Input label="Budget-Rahmen" value={disc.budget||""} onChange={e=>updateDisc("budget",e.target.value)} placeholder="z.B. 1.500-3.000€/mtl."/>
        <Input label="Zeitrahmen / Dringlichkeit" value={disc.timeline||""} onChange={e=>updateDisc("timeline",e.target.value)} placeholder="z.B. Sofort / Q2 2026"/>
        <Input label="Nächster Schritt" value={disc.next_step||""} onChange={e=>updateDisc("next_step",e.target.value)} placeholder="z.B. Angebotstermin 15.04."/>
      </div>

      {/* Discovery Script */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:8,gap:8,flexWrap:"wrap"}}>
        <div style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em"}}>Discovery-Call Leitfaden</div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {disc._script_discovery && <Button size="sm" variant="ghost" onClick={()=>resetScript("discovery","lead")}>Lead zurücksetzen</Button>}
          {isAdmin && globalDiscovery && <Button size="sm" variant="ghost" onClick={()=>resetScript("discovery","global")}>Globalen Default löschen</Button>}
        </div>
      </div>
      {discoveryScript.map((s,si)=>(<div key={si} style={{background:T.bgInput,borderRadius:12,padding:16,marginBottom:8}}>
        {/* Section Header */}
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
          {editingScriptKey===`discovery_${si}_section` ? (
            <>
              <input autoFocus value={editingScriptValue} onChange={e=>setEditingScriptValue(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")saveScriptEdit("lead");if(e.key==="Escape")cancelScriptEdit();}} style={{flex:1,fontSize:13,fontWeight:700,color:T.accent,background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:6,padding:"6px 10px",outline:"none"}}/>
              <button onClick={()=>saveScriptEdit("lead")} title="Nur für diesen Lead speichern" style={{background:T.success,color:"#fff",border:"none",borderRadius:6,padding:"4px 10px",cursor:"pointer",fontSize:12,fontWeight:600}}>✓ Lead</button>
              {isAdmin&&<button onClick={()=>saveScriptEdit("global")} disabled={savingGlobal} title="Für ALLE Leads als Standard übernehmen" style={{background:T.accent,color:"#fff",border:"none",borderRadius:6,padding:"4px 10px",cursor:"pointer",fontSize:12,fontWeight:600,opacity:savingGlobal?0.5:1}}>★ Alle</button>}
              <button onClick={cancelScriptEdit} style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",fontSize:14}}>✕</button>
            </>
          ) : (
            <>
              <div style={{flex:1,fontSize:15,fontWeight:700,color:T.accent}}>{s.section}</div>
              <button onClick={()=>startEditScript("discovery",si,"section",null,s.section)} title="Überschrift bearbeiten" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4,fontSize:12}}><Icon name="edit" size={13}/></button>
            </>
          )}
        </div>
        {/* Section Text */}
        {s.text && (editingScriptKey===`discovery_${si}_text` ? (
          <div style={{marginBottom:10}}>
            <textarea autoFocus value={editingScriptValue} onChange={e=>setEditingScriptValue(e.target.value)} rows={Math.max(4, editingScriptValue.split("\n").length)} style={{width:"100%",fontSize:12,color:T.text,background:T.bgCard,border:`1px solid ${T.accent}`,borderRadius:8,padding:"8px 12px",outline:"none",lineHeight:1.6,fontFamily:"inherit",resize:"vertical"}}/>
            <div style={{display:"flex",gap:6,marginTop:6,justifyContent:"flex-end"}}>
              <button onClick={cancelScriptEdit} style={{background:"none",border:`1px solid ${T.border}`,color:T.text,borderRadius:6,padding:"4px 12px",cursor:"pointer",fontSize:12}}>Abbrechen</button>
              <button onClick={()=>saveScriptEdit("lead")} style={{background:T.success,color:"#fff",border:"none",borderRadius:6,padding:"4px 12px",cursor:"pointer",fontSize:12,fontWeight:600}}>Nur für diesen Lead</button>
              {isAdmin&&<button onClick={()=>saveScriptEdit("global")} disabled={savingGlobal} style={{background:T.accent,color:"#fff",border:"none",borderRadius:6,padding:"4px 12px",cursor:"pointer",fontSize:12,fontWeight:600,opacity:savingGlobal?0.5:1}}>{savingGlobal?"Speichert...":"Für alle übernehmen"}</button>}
            </div>
          </div>
        ) : (
          <div style={{position:"relative",fontSize:16,color:T.text,padding:"14px 38px 14px 16px",background:T.bgCard,borderRadius:8,marginBottom:10,whiteSpace:"pre-wrap",lineHeight:1.7,borderLeft:`3px solid ${T.accent}`}}>
            {s.text}
            <button onClick={()=>startEditScript("discovery",si,"text",null,s.text)} title="Text bearbeiten" style={{position:"absolute",top:8,right:8,background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4,fontSize:12}}><Icon name="edit" size={14}/></button>
          </div>
        ))}
        {/* Questions */}
        {s.questions.length>0 && <div>{s.questions.map((q,qi)=>(<div key={qi} style={{marginBottom:10}}>
          {editingScriptKey===`discovery_${si}_q_q${qi}` ? (
            <div style={{marginBottom:6}}>
              <input autoFocus value={editingScriptValue} onChange={e=>setEditingScriptValue(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")saveScriptEdit("lead");if(e.key==="Escape")cancelScriptEdit();}} style={{width:"100%",fontSize:12,color:T.textMuted,background:T.bgCard,border:`1px solid ${T.accent}`,borderRadius:6,padding:"6px 10px",outline:"none"}}/>
              <div style={{display:"flex",gap:6,marginTop:4,justifyContent:"flex-end"}}>
                <button onClick={cancelScriptEdit} style={{background:"none",border:`1px solid ${T.border}`,color:T.text,borderRadius:6,padding:"3px 10px",cursor:"pointer",fontSize:11}}>Abbrechen</button>
                <button onClick={()=>saveScriptEdit("lead")} style={{background:T.success,color:"#fff",border:"none",borderRadius:6,padding:"3px 10px",cursor:"pointer",fontSize:11,fontWeight:600}}>Nur Lead</button>
                {isAdmin&&<button onClick={()=>saveScriptEdit("global")} disabled={savingGlobal} style={{background:T.accent,color:"#fff",border:"none",borderRadius:6,padding:"3px 10px",cursor:"pointer",fontSize:11,fontWeight:600,opacity:savingGlobal?0.5:1}}>Alle</button>}
              </div>
            </div>
          ) : (
            <div style={{display:"flex",alignItems:"flex-start",gap:6,fontSize:14,fontWeight:500,color:T.text,padding:"8px 8px 8px 12px",borderLeft:`2px solid ${T.accent}`,marginBottom:6}}>
              <div style={{flex:1}}>→ {q}</div>
              <button onClick={()=>startEditScript("discovery",si,"q",qi,q)} title="Frage bearbeiten" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:2,fontSize:12,flexShrink:0}}><Icon name="edit" size={12}/></button>
            </div>
          )}
          <textarea value={disc[`s${si}_q${qi}`]||""} onChange={e=>updateDisc(`s${si}_q${qi}`,e.target.value)} placeholder="Antwort eintragen..." rows={2} style={{width:"100%",background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 12px",color:T.text,fontSize:13,outline:"none",resize:"vertical",fontFamily:"inherit"}}/>
        </div>))}</div>}
        {s.questions.length===0&&<textarea value={disc[`section_${si}`]||""} onChange={e=>updateDisc(`section_${si}`,e.target.value)} placeholder="Notizen hier eintragen..." rows={2} style={{width:"100%",background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 12px",color:T.text,fontSize:13,outline:"none",resize:"vertical",fontFamily:"inherit"}}/>}
      </div>))}

      {/* General Discovery Notes */}
      <div>
        <div style={{fontSize:13,fontWeight:700,marginBottom:8}}>Zusammenfassung / Gesamteindruck</div>
        <textarea value={disc.summary||""} onChange={e=>updateDisc("summary",e.target.value)} placeholder="Gesamteindruck nach dem Gespräch..." rows={4} style={{width:"100%",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 14px",color:T.text,fontSize:13,outline:"none",resize:"vertical",fontFamily:"inherit"}}/>
      </div>
    </div>}

    {/* ═══ TAB: SALES CALL ═══ */}
    {tab==="sales"&&<div style={{display:"flex",flexDirection:"column",gap:20}}>
      <div style={{background:T.bgInput,borderRadius:12,padding:16}}>
        <div className="resp-grid-3">
          <div><div style={{fontSize:11,color:T.textDim,textTransform:"uppercase",marginBottom:4}}>Firma</div><div style={{fontSize:14,fontWeight:600}}>{lead.company}</div></div>
          <div><div style={{fontSize:11,color:T.textDim,textTransform:"uppercase",marginBottom:4}}>Kontakt</div><div style={{fontSize:14}}>{lead.contactName||"—"}</div></div>
          <div><div style={{fontSize:11,color:T.textDim,textTransform:"uppercase",marginBottom:4}}>Status</div><Badge color={ALL_STATUSES.find(s=>s.id===lead.status)?.color}>{ALL_STATUSES.find(s=>s.id===lead.status)?.label}</Badge></div>
        </div>
      </div>

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:8,gap:8,flexWrap:"wrap"}}>
        <div style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em"}}>Sales-Call Leitfaden</div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {disc._script_sales && <Button size="sm" variant="ghost" onClick={()=>resetScript("sales","lead")}>Lead zurücksetzen</Button>}
          {isAdmin && globalSales && <Button size="sm" variant="ghost" onClick={()=>resetScript("sales","global")}>Globalen Default löschen</Button>}
        </div>
      </div>
      {salesScript.map((s,si)=>(<div key={si} style={{background:T.bgInput,borderRadius:12,padding:16,marginBottom:8}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
          {editingScriptKey===`sales_${si}_section` ? (
            <>
              <input autoFocus value={editingScriptValue} onChange={e=>setEditingScriptValue(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")saveScriptEdit("lead");if(e.key==="Escape")cancelScriptEdit();}} style={{flex:1,fontSize:13,fontWeight:700,color:T.accent,background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:6,padding:"6px 10px",outline:"none"}}/>
              <button onClick={()=>saveScriptEdit("lead")} title="Nur für diesen Lead speichern" style={{background:T.success,color:"#fff",border:"none",borderRadius:6,padding:"4px 10px",cursor:"pointer",fontSize:12,fontWeight:600}}>✓ Lead</button>
              {isAdmin&&<button onClick={()=>saveScriptEdit("global")} disabled={savingGlobal} title="Für ALLE Leads als Standard übernehmen" style={{background:T.accent,color:"#fff",border:"none",borderRadius:6,padding:"4px 10px",cursor:"pointer",fontSize:12,fontWeight:600,opacity:savingGlobal?0.5:1}}>★ Alle</button>}
              <button onClick={cancelScriptEdit} style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",fontSize:14}}>✕</button>
            </>
          ) : (
            <>
              <div style={{flex:1,fontSize:15,fontWeight:700,color:T.accent}}>{s.section}</div>
              <button onClick={()=>startEditScript("sales",si,"section",null,s.section)} title="Überschrift bearbeiten" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4,fontSize:12}}><Icon name="edit" size={13}/></button>
            </>
          )}
        </div>
        {s.text && (editingScriptKey===`sales_${si}_text` ? (
          <div style={{marginBottom:10}}>
            <textarea autoFocus value={editingScriptValue} onChange={e=>setEditingScriptValue(e.target.value)} rows={Math.max(4, editingScriptValue.split("\n").length)} style={{width:"100%",fontSize:12,color:T.text,background:T.bgCard,border:`1px solid ${T.accent}`,borderRadius:8,padding:"8px 12px",outline:"none",lineHeight:1.6,fontFamily:"inherit",resize:"vertical"}}/>
            <div style={{display:"flex",gap:6,marginTop:6,justifyContent:"flex-end"}}>
              <button onClick={cancelScriptEdit} style={{background:"none",border:`1px solid ${T.border}`,color:T.text,borderRadius:6,padding:"4px 12px",cursor:"pointer",fontSize:12}}>Abbrechen</button>
              <button onClick={()=>saveScriptEdit("lead")} style={{background:T.success,color:"#fff",border:"none",borderRadius:6,padding:"4px 12px",cursor:"pointer",fontSize:12,fontWeight:600}}>Nur für diesen Lead</button>
              {isAdmin&&<button onClick={()=>saveScriptEdit("global")} disabled={savingGlobal} style={{background:T.accent,color:"#fff",border:"none",borderRadius:6,padding:"4px 12px",cursor:"pointer",fontSize:12,fontWeight:600,opacity:savingGlobal?0.5:1}}>{savingGlobal?"Speichert...":"Für alle übernehmen"}</button>}
            </div>
          </div>
        ) : (
          <div style={{position:"relative",fontSize:16,color:T.text,padding:"14px 38px 14px 16px",background:T.bgCard,borderRadius:8,marginBottom:10,whiteSpace:"pre-wrap",lineHeight:1.7,borderLeft:`3px solid ${T.accent}`}}>
            {s.text}
            <button onClick={()=>startEditScript("sales",si,"text",null,s.text)} title="Text bearbeiten" style={{position:"absolute",top:8,right:8,background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4,fontSize:12}}><Icon name="edit" size={14}/></button>
          </div>
        ))}
        {s.questions.length>0 && <div>{s.questions.map((q,qi)=>(<div key={qi} style={{marginBottom:10}}>
          {editingScriptKey===`sales_${si}_q_q${qi}` ? (
            <div style={{marginBottom:6}}>
              <input autoFocus value={editingScriptValue} onChange={e=>setEditingScriptValue(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")saveScriptEdit("lead");if(e.key==="Escape")cancelScriptEdit();}} style={{width:"100%",fontSize:12,color:T.textMuted,background:T.bgCard,border:`1px solid ${T.accent}`,borderRadius:6,padding:"6px 10px",outline:"none"}}/>
              <div style={{display:"flex",gap:6,marginTop:4,justifyContent:"flex-end"}}>
                <button onClick={cancelScriptEdit} style={{background:"none",border:`1px solid ${T.border}`,color:T.text,borderRadius:6,padding:"3px 10px",cursor:"pointer",fontSize:11}}>Abbrechen</button>
                <button onClick={()=>saveScriptEdit("lead")} style={{background:T.success,color:"#fff",border:"none",borderRadius:6,padding:"3px 10px",cursor:"pointer",fontSize:11,fontWeight:600}}>Nur Lead</button>
                {isAdmin&&<button onClick={()=>saveScriptEdit("global")} disabled={savingGlobal} style={{background:T.accent,color:"#fff",border:"none",borderRadius:6,padding:"3px 10px",cursor:"pointer",fontSize:11,fontWeight:600,opacity:savingGlobal?0.5:1}}>Alle</button>}
              </div>
            </div>
          ) : (
            <div style={{display:"flex",alignItems:"flex-start",gap:6,fontSize:14,fontWeight:500,color:T.text,padding:"8px 8px 8px 12px",borderLeft:`2px solid ${T.accent}`,marginBottom:6}}>
              <div style={{flex:1}}>→ {q}</div>
              <button onClick={()=>startEditScript("sales",si,"q",qi,q)} title="Frage bearbeiten" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:2,fontSize:12,flexShrink:0}}><Icon name="edit" size={12}/></button>
            </div>
          )}
          <textarea value={disc[`sales_s${si}_q${qi}`]||""} onChange={e=>updateDisc(`sales_s${si}_q${qi}`,e.target.value)} placeholder="Antwort eintragen..." rows={2} style={{width:"100%",background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 12px",color:T.text,fontSize:13,outline:"none",resize:"vertical",fontFamily:"inherit"}}/>
        </div>))}</div>}
        {s.questions.length===0&&<textarea value={disc[`sales_section_${si}`]||""} onChange={e=>updateDisc(`sales_section_${si}`,e.target.value)} placeholder="Notizen hier eintragen..." rows={2} style={{width:"100%",background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 12px",color:T.text,fontSize:13,outline:"none",resize:"vertical",fontFamily:"inherit"}}/>}
      </div>))}

      <div>
        <div style={{fontSize:13,fontWeight:700,marginBottom:8}}>Zusammenfassung Sales-Call</div>
        <textarea value={disc.sales_summary||""} onChange={e=>updateDisc("sales_summary",e.target.value)} placeholder="Notizen, Entscheidung, nächste Schritte..." rows={4} style={{width:"100%",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 14px",color:T.text,fontSize:13,outline:"none",resize:"vertical",fontFamily:"inherit"}}/>
      </div>
    </div>}

    {/* ═══ TAB: ANGEBOT / KUNDE ═══ */}
    {tab==="deal"&&<div style={{display:"flex",flexDirection:"column",gap:20}}>
      <div style={{background:T.bgInput,borderRadius:12,padding:16}}>
        <div className="resp-grid-2">
          <div><div style={{fontSize:11,color:T.textDim,textTransform:"uppercase",marginBottom:4}}>Firma</div><div style={{fontSize:14,fontWeight:600}}>{lead.company}</div></div>
          <div><div style={{fontSize:11,color:T.textDim,textTransform:"uppercase",marginBottom:4}}>Status</div><Badge color={ALL_STATUSES.find(s=>s.id===lead.status)?.color}>{ALL_STATUSES.find(s=>s.id===lead.status)?.label}</Badge></div>
        </div>
      </div>

      {/* Angebot abgegeben — Ja/Nein Toggle */}
      <div>
        <div style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em",marginBottom:12}}>Angebot abgegeben?</div>
        <div style={{display:"flex",gap:10}}>
          <button onClick={()=>updateDeal("offer_sent","yes")} style={{flex:1,padding:"14px 18px",background:deal.offer_sent==="yes"?T.success:T.bgInput,color:deal.offer_sent==="yes"?"#fff":T.text,border:`2px solid ${deal.offer_sent==="yes"?T.success:T.border}`,borderRadius:12,fontSize:14,fontWeight:700,cursor:"pointer",transition:"all 0.15s"}}>✓ Ja</button>
          <button onClick={()=>updateDeal("offer_sent","no")} style={{flex:1,padding:"14px 18px",background:deal.offer_sent==="no"?T.danger:T.bgInput,color:deal.offer_sent==="no"?"#fff":T.text,border:`2px solid ${deal.offer_sent==="no"?T.danger:T.border}`,borderRadius:12,fontSize:14,fontWeight:700,cursor:"pointer",transition:"all 0.15s"}}>✕ Nein</button>
        </div>
        {deal.offer_sent==="yes" && (<div className="resp-grid-2" style={{marginTop:14}}>
          <Input label="Datum Angebotsversand" type="date" value={deal.offer_sent_date||""} onChange={e=>updateDeal("offer_sent_date",e.target.value)}/>
          <Input label="Angebotssumme (€)" type="number" value={deal.total_amount||""} onChange={e=>updateDeal("total_amount",e.target.value)} placeholder="z.B. 3000"/>
        </div>)}
        <textarea value={deal.offer_notes||""} onChange={e=>updateDeal("offer_notes",e.target.value)} placeholder="Kurze Notiz zum Angebot (optional)" rows={2} style={{width:"100%",marginTop:14,background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 14px",color:T.text,fontSize:13,outline:"none",resize:"vertical",fontFamily:"inherit"}}/>
      </div>

      {/* Wenn Kunde — weitere Details */}
      {(lead.status==="closed_won"||lead.status==="active_customer") && (<>
        <div style={{height:1,background:T.border}}/>
        <div style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em"}}>Vertragsdetails</div>
        <div className="resp-grid-2">
          <div style={{gridColumn:"1/-1"}}><Input label="Leistung / Paket" value={deal.service||""} onChange={e=>updateDeal("service",e.target.value)} placeholder="z.B. SM Betreuung 6 Monate"/></div>
          <Select label="Zahlungsart" options={[{value:"",label:"Auswählen"},{value:"einmalig",label:"Einmalzahlung"},{value:"monatlich",label:"Ratenzahlung monatlich"},{value:"quartal",label:"Ratenzahlung quartalsweise"}]} value={deal.payment_type||""} onChange={e=>updateDeal("payment_type",e.target.value)}/>
          <Input label="Monatlicher Betrag (€)" type="number" value={deal.monthly_amount||""} onChange={e=>updateDeal("monthly_amount",e.target.value)} placeholder="z.B. 500"/>
          <Input label="Laufzeit (Monate)" type="number" value={deal.duration||""} onChange={e=>updateDeal("duration",e.target.value)} placeholder="z.B. 6"/>
        </div>

        <div style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em",marginTop:8}}>Rechnungen & Zahlungen</div>
        <div className="resp-grid-2">
          <Input label="Rechnung gestellt am" type="date" value={deal.invoice_date||""} onChange={e=>updateDeal("invoice_date",e.target.value)}/>
          <Input label="Bezahlt am" type="date" value={deal.paid_date||""} onChange={e=>updateDeal("paid_date",e.target.value)}/>
          <Input label="Nächste Folgerechnung" type="date" value={deal.next_invoice||""} onChange={e=>updateDeal("next_invoice",e.target.value)}/>
          <Input label="Betrag Folgerechnung (€)" type="number" value={deal.next_amount||""} onChange={e=>updateDeal("next_amount",e.target.value)} placeholder="Laut Vertrag"/>
        </div>

        <div style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em",marginTop:8}}>Provision</div>
        <div className="resp-grid-2">
          <Input label="Provisions-Betrag fix (€/mtl.)" type="number" value={deal.provision_fix||""} onChange={e=>updateDeal("provision_fix",e.target.value)} placeholder="z.B. 300"/>
          <Input label="Provisions-Satz (%)" type="number" value={deal.provision_percent||""} onChange={e=>updateDeal("provision_percent",e.target.value)} placeholder="z.B. 10"/>
          <div style={{gridColumn:"1/-1"}}><Input label="Provision Notizen" value={deal.provision_notes||""} onChange={e=>updateDeal("provision_notes",e.target.value)} placeholder="z.B. Jonny bekommt 10% von jedem Sale monatlich"/></div>
        </div>
      </>)}
    </div>}
  </div>);
};

// ─── CALLS ───
const CallsPage = ({user,reload,dv}) => {
  const calls=store.get(STORAGE_KEYS.calls)||[];
  const f=user.role==="admin"?calls:calls.filter(c=>c.userId===user.id);
  const today=f.filter(c=>new Date(c.date).toDateString()===new Date().toDateString()).length;
  return (<div>
    <h1 style={{fontSize:26,fontWeight:700,letterSpacing:"-0.02em",marginBottom:24}}>Anrufe</h1>
    <div style={{display:"flex",gap:16,marginBottom:24,flexWrap:"wrap"}}><StatCard label="Heute" value={today} icon="phone" color={T.accent}/><StatCard label="Gesamt" value={f.length} icon="chart" color={T.success}/></div>
    <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,overflow:"auto",WebkitOverflowScrolling:"touch"}}>
      {f.length===0?<Empty icon="phone" title="Keine Anrufe" desc="Logge Anrufe über die Lead-Seite"/>:(
        <table style={{width:"100%",borderCollapse:"collapse"}}><thead><tr style={{borderBottom:`1px solid ${T.border}`}}>{["Datum","Firma","Vertriebler"].map(h=>(<th key={h} style={{padding:"12px 16px",textAlign:"left",fontSize:11,fontWeight:600,color:T.textDim,textTransform:"uppercase"}}>{h}</th>))}</tr></thead>
        <tbody>{f.slice().reverse().map(c=>(<tr key={c.id} style={{borderBottom:`1px solid ${T.border}`}}><td style={{padding:"12px 16px",fontSize:13,fontFamily:"'JetBrains Mono',monospace"}}>{new Date(c.date).toLocaleString("de")}</td><td style={{padding:"12px 16px",fontSize:13,fontWeight:600}}>{c.leadCompany}</td><td style={{padding:"12px 16px",fontSize:13}}>{c.userName}</td></tr>))}</tbody></table>)}
    </div>
  </div>);
};

// ─── APPOINTMENTS ───
const AppointmentsPage = ({user,reload,dv}) => {
  const [appts,setAppts]=useState(store.get(STORAGE_KEYS.appointments)||[]);
  useEffect(()=>{setAppts(store.get(STORAGE_KEYS.appointments)||[]);},[dv]);
  const f=user.role==="admin"?appts:appts.filter(a=>a.userId===user.id);
  const up=f.filter(a=>new Date(a.date)>=new Date()).sort((a,b)=>new Date(a.date)-new Date(b.date));
  const past=f.filter(a=>new Date(a.date)<new Date()).sort((a,b)=>new Date(b.date)-new Date(a.date));
  const del=async(id)=>{await api.del("appointments",{id});await reload(STORAGE_KEYS.appointments);setAppts(store.get(STORAGE_KEYS.appointments));};
  const renderL=(list,label)=>(<div style={{marginBottom:24}}><h3 style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em",marginBottom:12,color:T.textMuted}}>{label}</h3>
    {list.length===0?<div style={{color:T.textDim,fontSize:13,padding:16}}>Keine Termine</div>:list.map(a=>(<div key={a.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 18px",background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:10,marginBottom:8}}>
      <div><div style={{fontWeight:600,fontSize:14}}>{a.title}</div><div style={{fontSize:12,color:T.textDim,marginTop:2}}>{a.leadCompany} — {a.userName}</div></div>
      <div style={{display:"flex",alignItems:"center",gap:14}}><div style={{textAlign:"right"}}><div style={{fontSize:13,fontWeight:600,fontFamily:"'JetBrains Mono',monospace",color:T.accent}}>{fmtDate(a.date)}</div><div style={{fontSize:12,color:T.textDim}}>{a.time}</div></div><button onClick={()=>del(a.id)} style={{background:"none",border:"none",color:T.textDim,cursor:"pointer"}}><Icon name="delete" size={16}/></button></div>
    </div>))}</div>);
  return (<div><h1 style={{fontSize:26,fontWeight:700,letterSpacing:"-0.02em",marginBottom:24}}>Termine</h1>{renderL(up,`Anstehend (${up.length})`)}{renderL(past,`Vergangen (${past.length})`)}</div>);
};

// ─── FINANCES ───
// ═══ FINANCE-KATEGORIEN ═══
// Standardkategorien mit Farben + Erkennungs-Patterns
const FINANCE_CATEGORIES = [
  {id:"stammkapital",     label:"Stammkapital",         color:"#6d6a94", patterns:[/stammkapital/i, /\beinlage\b/i, /gesellschafter.*einlag/i]},
  {id:"steuerkonto",      label:"Steuerkonto-Übertrag", color:"#a05046", patterns:[/steuerkonto/i, /steuer.?rücklage/i, /steuerrücklage/i, /steuer.?konto/i, /rücklage.*steuer/i]},
  {id:"steuern",          label:"Steuern & Finanzamt",  color:"#a05046", patterns:[/finanzamt/i, /\bsteuer\b/i, /umsatzsteuer/i, /\bust\b/i, /körperschaft/i, /gewerbesteuer/i, /kfz.steuer/i, /kapitalertragsteuer/i, /kammer/i, /\bihk\b/i]},
  {id:"gf-entnahme",      label:"GF-Entnahme/Vorschuss",color:"#7d6b96", patterns:[/felix griska/i, /ramon dachner/i, /\bramon\b/i, /\bfelix\b/i, /gehaltsvorschuss/i, /\bgf\b.*entnahme/i, /geschäftsführer/i]},
  {id:"konto-uebertrag",  label:"Konto-Übertrag",       color:"#5b7fa6", patterns:[/r\+?f\s?media/i, /\brf\s?media\b/i, /eigene buchung/i, /\bumbuchung\b/i, /übertrag/i, /interne buchung/i]},
  {id:"gutschrift",       label:"Gutschrift / Erstattung", color:"#6d8f7a", patterns:[/gutschrift/i, /erstattung/i, /\brückerstattung\b/i, /\brueckerstattung\b/i, /\brefund\b/i, /\bstorno\b/i, /\bretoure\b/i, /rückzahlung/i, /rueckzahlung/i, /\breversal\b/i]},
  {id:"coaching-beratung",label:"Coaching & Beratung",  color:"#9a7b52", patterns:[/janik\s?bickel/i, /\bbickel\b/i, /bickel\s?advisory/i, /coaching/i, /\bcoach\b/i, /beratungs?honorar/i, /\bunternehmens?beratung\b/i]},
  {id:"bank-gebuehr",     label:"Bankgebühren",         color:"#7f8a93", patterns:[/kontoführung/i, /kontogebühr/i, /bankgebühr/i, /\bentgelt\b/i, /jahresgebühr/i, /buchungspost/i, /\bvisa\b.*gebühr/i, /mastercard.*gebühr/i]},
  {id:"versicherung",     label:"Versicherung",         color:"#5f8a8c", patterns:[/versicherung/i, /\bvers\.\b/i, /\ballianz\b/i, /\bhuk\b/i, /\baxa\b/i, /generali/i, /\bergo\b/i, /barmenia/i, /\bbgw\b/i, /berufsgenossenschaft/i, /hiscox/i]},
  {id:"miete",            label:"Miete & Nebenkosten",  color:"#8a7f5a", patterns:[/\bmiete\b/i, /nebenkosten/i, /\bnk\b.*abrechnung/i, /\bstrom\b/i, /\bgas\b/i, /heizung/i, /\bwasser\b/i, /\bmüll\b/i, /vermieter/i, /hausverwaltung/i]},
  {id:"telekom",          label:"Telefon & Internet",   color:"#5b7fa6", patterns:[/telekom/i, /vodafone/i, /\b1und1\b/i, /\b1&1\b/i, /\bo2\b/i, /telefonica/i, /\binternet\b/i, /\bhandy\b/i, /\bsim\b/i, /freenet/i]},
  {id:"hosting",          label:"Hosting & Domains",    color:"#4f7d80", patterns:[/münnich/i, /muennich/i, /all-?inkl/i, /\bhetzner\b/i, /\bstrato\b/i, /godaddy/i, /\bdomain\b/i, /webhost/i, /\bvps\b/i, /\bserver\b.*miete/i, /netcup/i, /ionos/i]},
  {id:"software",         label:"Software & SaaS",      color:"#5f8a72", patterns:[/microsoft/i, /office365/i, /google.*workspace/i, /google.*cloud/i, /\badobe\b/i, /\bcanva\b/i, /\bnotion\b/i, /\bslack\b/i, /\bzoom\b/i, /\bopenai\b/i, /anthropic/i, /\bclaude\b/i, /\bgithub\b/i, /\baws\b/i, /netlify/i, /vercel/i, /\bfigma\b/i, /jetbrains/i, /\bjira\b/i, /asana/i, /monday\.com/i, /linkedin.*premium/i, /sales.*navigator/i, /dripify/i, /apollo\.io/i, /hubspot/i, /salesforce/i, /pipedrive/i, /seobility/i, /sistrix/i, /\bahrefs\b/i, /semrush/i]},
  {id:"reise-transport",  label:"Reise & Transport",    color:"#96704e", patterns:[/\bdb\b.*vertrieb/i, /deutsche bahn/i, /\bbvg\b/i, /\bvbb\b/i, /flixbus/i, /\bsbb\b/i, /öbb/i, /\buber\b/i, /\bbolt\b/i, /freenow/i, /\btaxi\b/i, /lufthansa/i, /eurowings/i, /ryanair/i, /easyjet/i, /booking\.com/i, /airbnb/i, /\bhotel\b/i, /\btanken\b/i, /\baral\b/i, /shell.*energie/i, /\besso\b/i, /\bjet\b.*tankstelle/i, /\btotal\b.*tank/i, /parkgebühr/i, /\bparking\b/i]},
  {id:"essen-bewirtung",  label:"Bewirtung & Verpflegung", color:"#9a8352", patterns:[/\brestaurant\b/i, /\bcafé\b/i, /\bcafe\b/i, /lieferando/i, /\bwolt\b/i, /uber.*eats/i, /\bedeka\b/i, /\brewe\b/i, /\baldi\b/i, /\blidl\b/i, /\bnetto\b/i, /\bpenny\b/i, /kaufland/i, /backerei/i, /bäckerei/i, /\bbistro\b/i, /\bkantine\b/i, /mcdonald/i, /\bburger\b.*king/i]},
  {id:"buero-material",   label:"Büromaterial & Versand",color:"#7d6b96", patterns:[/\bamazon\b/i, /\bibay\b/i, /\bebay\b/i, /\bbüro\b/i, /\bbuero\b/i, /\botto\b/i, /mediamarkt/i, /\bsaturn\b/i, /\bobi\b/i, /bauhaus/i, /\bikea\b/i, /\bdpd\b/i, /\bdhl\b/i, /\bhermes\b/i, /\bups\b/i, /\bgls\b/i, /\bporto\b/i]},
  {id:"marketing",        label:"Marketing & Werbung",  color:"#95647a", patterns:[/google.*ads/i, /google.*adwords/i, /facebook.*ads/i, /\bmeta\b.*ads/i, /instagram.*ads/i, /tiktok.*ads/i, /linkedin.*ads/i, /\bseo\b/i, /printful/i, /vistaprint/i, /\bagentur\b.*ads/i]},
  {id:"freelancer",       label:"Freelancer & Honorare",color:"#4f8189", patterns:[/dustin urban/i, /\bdustin\b/i, /\bhonorar\b/i, /freelancer/i, /freelance/i, /\bcutter\b/i, /\beditor\b/i]},
  {id:"einnahme-kunde",   label:"Kundenzahlung",        color:"#4f8464", patterns:[/rechnung/i, /\brg\b\.\s/i, /\binv\b/i, /kundenzahlung/i]},
  {id:"paypal",           label:"PayPal (zu klären)",   color:"#8a7f5a", patterns:[/paypal\s+europe/i, /paypal\s+\(europe\)/i, /paypal sarl/i, /paypal\s+s\.a\.r\.l/i]},
  {id:"sonstiges",        label:"Sonstiges",            color:"#7a736c", patterns:[]},
];

// Erkennt Kategorie automatisch anhand der Beschreibung
const detectFinanceCategory = (description, type) => {
  const text = (description||"").toLowerCase();
  for(const cat of FINANCE_CATEGORIES){
    if(cat.id==="sonstiges") continue;
    for(const pattern of cat.patterns){
      if(pattern.test(text)) {
        if(cat.id==="einnahme-kunde" && type!=="income") continue;
        return cat.id;
      }
    }
  }
  return "";
};

const getFinanceCategoryInfo = (catId) => FINANCE_CATEGORIES.find(c=>c.id===catId) || null;

const FinancesPage = ({reload,dv}) => {
  const [fin,setFin]=useState(store.get(STORAGE_KEYS.finances)||[]);
  const [showAdd,setShowAdd]=useState(false);
  const [showBank,setShowBank]=useState(false);
  const [showMoreMenu,setShowFinMoreMenu]=useState(false);
  const [bankPrev,setBankPrev]=useState(null);
  const [bankRes,setBankRes]=useState(null);
  const [showDelConfirm,setShowDelConfirm]=useState(null);
  const [aiLoading,setAiLoading]=useState(false);
  const [aiResult,setAiResult]=useState(null);
  const [autoCatLoading,setAutoCatLoading]=useState(false);
  const [autoCatResult,setAutoCatResult]=useState(null);
  const [categoryFilter,setCategoryFilter]=useState("all"); // "all", "uncategorized", or category-id
  const [editingCatId,setEditingCatId]=useState(null);
  const [searchQuery,setSearchQuery]=useState(""); // Buchungssuche
  const [selectedIds,setSelectedIds]=useState([]); // Mehrfachauswahl für Bulk-Aktionen
  const [form,setForm]=useState({type:"income",amount:"",description:"",date:todayLocal(),category:""});

  // Mehrere Buchungen auf einmal kategorisieren (Bulk)
  const bulkCategorize = async (newCategory) => {
    if(selectedIds.length===0) return;
    const updates = selectedIds.map(id=>({id, category:newCategory}));
    await api.post("finances-categorize", {updates});
    await reload(STORAGE_KEYS.finances);
    setFin(store.get(STORAGE_KEYS.finances));
    setSelectedIds([]);
  };
  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);

  // ─── AUTO-KATEGORISIERUNG ───
  // Geht durch alle Buchungen ohne Kategorie und versucht sie via Pattern zu erkennen
  const runAutoCategorize = async (onlyUncategorized=true) => {
    setAutoCatLoading(true);
    setAutoCatResult(null);
    try {
      const candidates = onlyUncategorized
        ? fin.filter(f => !f.category || f.category.trim()==="")
        : fin;
      const updates = [];
      for(const f of candidates){
        const detected = detectFinanceCategory(f.description||"", f.type);
        if(detected) updates.push({id: f.id, category: detected});
      }
      if(updates.length > 0){
        await api.post("finances-categorize", {updates});
      }
      await reload(STORAGE_KEYS.finances);
      setFin(store.get(STORAGE_KEYS.finances));
      setAutoCatResult({total:candidates.length, matched:updates.length});
    } catch(e){
      setAutoCatResult({error:e.message});
    } finally {
      setAutoCatLoading(false);
    }
  };

  // Einzelne Buchung kategorisieren
  const updateCategory = async (id, newCategory) => {
    const f = fin.find(x=>x.id===id);
    if(!f) return;
    await api.post("finances-categorize", {updates:[{id, category:newCategory}]});
    await reload(STORAGE_KEYS.finances);
    setFin(store.get(STORAGE_KEYS.finances));
    setEditingCatId(null);
  };

  // ─── KI-KATEGORISIERUNG (für die Reste die Pattern nicht erkannt haben) ───
  const [aiCatLoading,setAiCatLoading]=useState(false);
  const [aiCatProgress,setAiCatProgress]=useState(null); // {done, total}
  const [aiCatResult,setAiCatResult]=useState(null);
  const [showTopClients,setShowTopClients]=useState(false);

  const runAiCategorize = async () => {
    const candidates = fin.filter(f => !f.category || f.category.trim()==="");
    if(candidates.length===0){
      setAiCatResult({error:"Keine unkategorisierten Buchungen — alles bereits sortiert!"});
      return;
    }
    setAiCatLoading(true);
    setAiCatResult(null);
    setAiCatProgress({done:0, total:candidates.length});

    const categoryList = FINANCE_CATEGORIES.map(c=>`${c.id} (${c.label})`).join(", ");
    const BATCH_SIZE = 40; // 40 Transaktionen pro API-Call
    const allUpdates = [];
    let totalMatched = 0;

    try {
      for(let i=0; i<candidates.length; i+=BATCH_SIZE){
        const batch = candidates.slice(i, i+BATCH_SIZE);
        const txList = batch.map(f => `${f.id}|${f.type==="income"?"E":"A"}|${f.amount.toFixed(2)}€|${f.description}`).join("\n");

        const prompt = `Du bist ein Buchhaltungs-Assistent für eine deutsche Digitalagentur (R&F Media UG). Kategorisiere folgende Banktransaktionen.

VERFÜGBARE KATEGORIEN (id):
${categoryList}

REGELN:
- Antworte NUR mit einem JSON-Array: [{"id":"<transaction-id>","category":"<category-id>"}, ...]
- KEIN Text drumrum, KEIN Markdown-Codeblock, NUR das JSON-Array
- Wenn unsicher → "sonstiges"
- "E" = Einnahme, "A" = Ausgabe
- "einnahme-kunde" nur bei Einnahmen
- Bei Übertrag/Eigenbuchung zwischen eigenen Konten → "konto-uebertrag"

TRANSAKTIONEN (Format: id|E/A|Betrag|Beschreibung):
${txList}`;

        const response = await fetch("/api/anthropic-proxy", {
          method: "POST",
          headers: authHeaders({"Content-Type": "application/json"}),
          body: JSON.stringify({
            model: "claude-sonnet-4-20250514",
            max_tokens: 4000,
            messages: [{role:"user", content: prompt}]
          })
        });
        const data = await response.json();
        const text = data.content?.[0]?.text || "";
        // JSON aus der Antwort extrahieren
        const jsonMatch = text.match(/\[[\s\S]*\]/);
        if(jsonMatch){
          try {
            const parsed = JSON.parse(jsonMatch[0]);
            const validCatIds = FINANCE_CATEGORIES.map(c=>c.id);
            const updates = parsed
              .filter(p => p.id && p.category && validCatIds.includes(p.category) && p.category!=="sonstiges")
              .map(p => ({id: p.id, category: p.category}));
            allUpdates.push(...updates);
            totalMatched += updates.length;
          } catch(e){ /* JSON-Parse-Fehler */ }
        }
        setAiCatProgress({done: Math.min(i+BATCH_SIZE, candidates.length), total: candidates.length});
      }

      // Bulk-Speichern
      if(allUpdates.length > 0){
        await api.post("finances-categorize", {updates: allUpdates});
        await reload(STORAGE_KEYS.finances);
        setFin(store.get(STORAGE_KEYS.finances));
      }
      setAiCatResult({total: candidates.length, matched: totalMatched});
    } catch(e){
      setAiCatResult({error: e.message});
    } finally {
      setAiCatLoading(false);
      setAiCatProgress(null);
    }
  };

  const add=async()=>{if(!form.amount||!form.description)return;await api.post("finances",{...form,amount:parseFloat(form.amount),date:form.date||todayLocal()});await reload(STORAGE_KEYS.finances);setFin(store.get(STORAGE_KEYS.finances));setForm({type:"income",amount:"",description:"",date:todayLocal(),category:""});setShowAdd(false);};
  const del=async(id)=>{await api.del("finances",{id});await reload(STORAGE_KEYS.finances);setFin(store.get(STORAGE_KEYS.finances));};
  const handleBank=rows=>{if(!rows?.length)return;setBankPrev(rows.map(mapBankCSV).filter(f=>f && f.amount>0));setBankRes(null);};
  const importBank=async()=>{if(!bankPrev?.length)return;await api.post("finances-bulk",{items:bankPrev});await reload(STORAGE_KEYS.finances);setFin(store.get(STORAGE_KEYS.finances));setBankRes({count:bankPrev.length});setBankPrev(null);};

  // ─── KI ANALYSE ───
  const runAiAnalysis = async () => {
    if(fin.length===0) return;
    setAiLoading(true);
    setAiResult(null);
    try {
      // Aggregierte Statistiken über ALLE Transaktionen (nicht nur die letzten 200)
      const totalIncome = fin.filter(f=>f.type==="income").reduce((s,f)=>s+f.amount,0);
      const totalExpense = fin.filter(f=>f.type==="expense").reduce((s,f)=>s+f.amount,0);

      // Datum-Range
      const dates = fin.map(f=>new Date(f.date)).sort((a,b)=>a-b);
      const dateFrom = dates[0]?.toLocaleDateString("de") || "?";
      const dateTo = dates[dates.length-1]?.toLocaleDateString("de") || "?";

      // Aggregation pro Kategorie
      const byCategory = {};
      for(const f of fin){
        const catId = f.category || "(ohne Kategorie)";
        if(!byCategory[catId]) byCategory[catId] = {income:0, incomeCount:0, expense:0, expenseCount:0};
        if(f.type==="income"){ byCategory[catId].income += f.amount; byCategory[catId].incomeCount++; }
        else { byCategory[catId].expense += f.amount; byCategory[catId].expenseCount++; }
      }
      const catSummary = Object.entries(byCategory)
        .map(([id,d])=>{
          const catLabel = getFinanceCategoryInfo(id)?.label || id;
          const parts = [];
          if(d.income>0) parts.push(`+${d.income.toFixed(2)}€ aus ${d.incomeCount} Einnahmen`);
          if(d.expense>0) parts.push(`-${d.expense.toFixed(2)}€ aus ${d.expenseCount} Ausgaben`);
          return `- ${catLabel}: ${parts.join(", ")}`;
        }).join("\n");

      // Top-Empfänger/Absender — Schlüssel ist der "Hauptname" (vor dem ersten —)
      const cleanName = (desc) => {
        let n = (desc||"").split("—")[0].split("|")[0].trim();
        // Normalisierung: entferne Rechtsformen, Zusätze
        n = n.replace(/\b(GmbH|AG|UG|e\.K\.|KG|OHG|GbR|haftungsbeschränkt|gemeinnützig|& Co\. KG|GmbH & Co\. KG)\b/gi, "").trim();
        n = n.replace(/\s+/g," ").trim();
        return n.toUpperCase();
      };
      const byPartner = {};
      for(const f of fin){
        const key = cleanName(f.description) || "(unbekannt)";
        if(!byPartner[key]) byPartner[key] = {income:0, expense:0, count:0, raw:f.description};
        if(f.type==="income") byPartner[key].income += f.amount;
        else byPartner[key].expense += f.amount;
        byPartner[key].count++;
      }
      // Top 25 Einnahme-Quellen
      const topClients = Object.entries(byPartner)
        .filter(([_,d])=>d.income>0)
        .sort((a,b)=>b[1].income-a[1].income)
        .slice(0,25)
        .map(([name,d])=>`- ${name}: ${d.income.toFixed(2)}€ (${d.count} Buchungen)`)
        .join("\n");
      // Top 25 Ausgabe-Empfänger
      const topVendors = Object.entries(byPartner)
        .filter(([_,d])=>d.expense>0)
        .sort((a,b)=>b[1].expense-a[1].expense)
        .slice(0,25)
        .map(([name,d])=>`- ${name}: ${d.expense.toFixed(2)}€ (${d.count} Buchungen)`)
        .join("\n");

      // Sample der größten 30 Einzeltransaktionen für Kontext
      const bigOnes = fin.slice().sort((a,b)=>b.amount-a.amount).slice(0,30)
        .map(f=>`${f.date} | ${f.type==="income"?"+":"-"}${f.amount.toFixed(2)}€ | ${f.description.slice(0,120)}`)
        .join("\n");

      // Steuerkonto-Übertragungen suchen
      const taxAccountTx = fin.filter(f=>/steuerkonto|steuer.?rücklage|steuerrücklage/i.test(f.description||""))
        .map(f=>`${f.date} | ${f.type==="income"?"+":"-"}${f.amount.toFixed(2)}€ | ${f.description.slice(0,120)}`)
        .join("\n") || "(keine eindeutigen Steuerkonto-Übertragungen erkannt)";

      const response = await fetch("/api/anthropic-proxy", {
        method: "POST",
        headers: authHeaders({"Content-Type": "application/json"}),
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 3000,
          messages: [{
            role: "user",
            content: `Du bist ein BUCHHALTUNGS-ASSISTENT für die R&F Media UG (haftungsbeschränkt) — eine deutsche Digitalagentur. Du wirst eine Analyse aller Banktransaktionen erstellen.

KONTEXT — DAS MUSST DU WISSEN:
- **Inhaber/Geschäftsführer**: Felix Griska und Ramon Dachner. Auszahlungen an diese Personen sind ENTWEDER monatliches Gehalt (1.240€ pro Person), Gehaltsvorschüsse oder Geschäftsführer-Entnahmen für private Auslagen die später per SevDesk-Beleg gegengebucht werden. KEINE "Privatentnahmen" pauschal labeln.
- **Janik Bickel / Bickel Advisory**: Das ist UNSER COACH (Geschäftscoaching, Beratungshonorar). KEIN Durchlaufposten, sondern eine echte Geschäftsausgabe.
- **Dustin Urban**: Freelancer (Cutter/Editor). Honorar.
- **R+F Media UG / RF Media** als Empfänger oder Absender: Das sind Übertragungen zwischen unseren EIGENEN Konten (z.B. Geschäftskonto ↔ Steuerkonto, Geschäftskonto ↔ Rücklagenkonto). Das sind KEINE Einnahmen oder Ausgaben — sondern interne Konto-Überträge.
- **Steuerkonto**: Wir haben ein separates Konto wo wir Steuern zurücklegen. Übertragungen dorthin sind keine echten Ausgaben sondern Rücklagen.
- **Stammkapital**: 25.000€ wurde in 2-4 Tranchen eingezahlt (z.B. 2.500€, 12.500€). Das ist KEIN Umsatz.

WAS DU NICHT TUN SOLLST:
- KEINE Lifestyle-Empfehlungen ("zu viele Restaurant-Besuche") — wir sind erwachsen
- KEINE Vermutungen darüber wann der Geschäftsführer essen darf
- KEINE Weisheiten zu Ausgabe-Reduzierung — fokussiere dich auf BUCHHALTERISCHE Sauberkeit

WAS DU TUN SOLLST: Analysiere präzise und wertfrei für die Buchhaltung.

═══════════════════════════════════════
DATEN

Zeitraum: ${dateFrom} bis ${dateTo}
Anzahl Transaktionen: ${fin.length}
Einnahmen gesamt: ${totalIncome.toFixed(2)}€
Ausgaben gesamt: ${totalExpense.toFixed(2)}€
Saldo: ${(totalIncome-totalExpense).toFixed(2)}€

KATEGORIEN-VERTEILUNG (über alle Buchungen):
${catSummary}

TOP 25 EINNAHME-QUELLEN (potenzielle Kunden):
${topClients}

TOP 25 AUSGABE-EMPFÄNGER:
${topVendors}

GRÖSSTE 30 EINZELTRANSAKTIONEN:
${bigOnes}

GEFUNDENE STEUERKONTO-ÜBERTRAGUNGEN:
${taxAccountTx}

═══════════════════════════════════════
DEINE ANALYSE — bitte in dieser Struktur:

## 💰 ECHTER GESCHÄFTSUMSATZ
Berechne den bereinigten Umsatz: Gesamteinnahmen MINUS Stammkapital MINUS interne Kontoüberträge (R+F Media als Absender) MINUS Erstattungen. Sei mathematisch präzise mit Zahlen aus den Daten oben.

## 🏆 TOP-KUNDEN
Liste die ECHTEN Kunden (keine internen Überträge!) mit ihrer GESAMT-Einnahmensumme. Wenn ein Name in mehreren Schreibweisen auftaucht (z.B. "ALARM-BERATUNG SCHMIDT" und "ALARMBERATUNG SCHMIDT GMBH"), addiere die zusammen. Mindestens die Top 8.

## 💼 STEUERKONTO-CHECK
Wurden Steuerrücklagen aufs Steuerkonto übertragen? Wenn ja: wie oft, gesamt wie viel? Wenn nein: hinweisen dass keine eindeutigen Überträge erkannt wurden — kann sein dass sie als "RF Media" o.ä. gebucht sind.

## 🔍 KATEGORIE-AUFFÄLLIGKEITEN
NUR buchhalterische Auffälligkeiten:
- Welche Kategorien haben besonders viele "ohne Kategorie"-Buchungen?
- Welche PayPal-Buchungen sollten manuell zugeordnet werden? (Wie viele Stück, welche Summe insgesamt)
- Doppelt erkannte Transaktionen?
- Auffällige Beträge (sehr hoch oder ungerade Beträge)?

## 🔄 WIEDERKEHRENDE KOSTEN
Erkenne Abos. Liste mit Anbieter, Monatskosten und seit wann. Hochrechnung pro Jahr.

## ✅ EMPFEHLUNGEN FÜR BUCHHALTUNG
2-3 konkrete buchhalterische Empfehlungen (NICHT was wir weniger ausgeben sollen). Beispiel: "X Buchungen sind 'ohne Kategorie' — auto-kategorisieren" oder "Y PayPal-Zahlungen müssen einzeln zugeordnet werden" oder "Z hat keinen Beleg im SevDesk".

Antwort auf Deutsch. Sei präzise, sachlich, nicht moralisierend.`
          }]
        })
      });

      const data = await response.json();
      if(data.content?.[0]?.text) {
        setAiResult(data.content[0].text);
      } else if(data.error) {
        setAiResult(`Fehler: ${data.error.message || "API Fehler"}`);
      }
    } catch(err) {
      setAiResult(`Fehler bei der Analyse: ${err.message}`);
    }
    setAiLoading(false);
  };

  const bulkDelete = async(type) => {
    if(type==="all")await api.del("finances",{id:"ALL"});
    else if(type==="bank")await api.del("finances",{id:"BANK"});
    await reload(STORAGE_KEYS.finances);setFin(store.get(STORAGE_KEYS.finances));
    setShowDelConfirm(null);
  };

  const bankCount = fin.filter(f => f.source === 'bank-import').length;
  const incomeCount = fin.filter(f => f.type === 'income').length;
  const expenseCount = fin.filter(f => f.type === 'expense').length;
  const ti=fin.filter(f=>f.type==="income").reduce((s,f)=>s+f.amount,0);
  const te=fin.filter(f=>f.type==="expense").reduce((s,f)=>s+f.amount,0);

  // ─── TOP-KUNDEN aggregation ───
  // Normalisiert Namen: "ALARM-BERATUNG SCHMIDT GmbH" und "Alarmberatung Schmidt" → gleicher Kunde
  const normalizeName = (desc) => {
    if(!desc || typeof desc !== "string") return "";
    try {
      let n = desc.split("—")[0].split("|")[0].trim();
      n = n.replace(/\b(GmbH|AG|UG|e\.K\.|KG|OHG|GbR|haftungsbeschränkt|gemeinnützig|& Co\. KG|GmbH & Co\. KG|mbH)\b/gi, "");
      n = n.replace(/[-_/\\.,;:()]/g," ");
      n = n.replace(/\s+/g," ").trim().toUpperCase();
      return n;
    } catch(e){ return ""; }
  };
  // Aggregiere alle Einnahmen pro Kunde — IGNORIERE interne Konto-Überträge + Stammkapital
  let topClients = [];
  let cleanTotalIncome = 0;
  try {
    const map = {};
    for(const f of (fin||[])){
      if(!f || f.type!=="income") continue;
      if(f.category==="konto-uebertrag" || f.category==="stammkapital" || f.category==="gutschrift") continue;
      const desc = f.description || "";
      if(/r\+?f\s?media|\brf\s?media\b/i.test(desc)) continue;
      const key = normalizeName(desc) || "(unbekannt)";
      if(!map[key]) map[key] = {name:key, total:0, count:0, lastDate:null, firstDate:null};
      map[key].total += (typeof f.amount === "number" ? f.amount : 0);
      map[key].count++;
      if(f.date){
        if(!map[key].lastDate || new Date(f.date) > new Date(map[key].lastDate)) map[key].lastDate = f.date;
        if(!map[key].firstDate || new Date(f.date) < new Date(map[key].firstDate)) map[key].firstDate = f.date;
      }
    }
    topClients = Object.values(map).sort((a,b)=>b.total-a.total);
    cleanTotalIncome = topClients.reduce((s,c)=>s+c.total, 0);
  } catch(e){
  }

  const delLabels = {
    all: `Willst du wirklich alle ${fin.length} Einträge unwiderruflich löschen?`,
    income: `Willst du wirklich alle ${incomeCount} Einnahmen löschen?`,
    expense: `Willst du wirklich alle ${expenseCount} Ausgaben löschen?`,
    bank: `Willst du wirklich alle ${bankCount} Bank-Import-Einträge löschen?`,
  };

  return (<div>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24,flexWrap:"wrap",gap:12}}>
      <h1 style={{fontSize:26,fontWeight:700,letterSpacing:"-0.02em"}}>Finanzen</h1>
      <div style={{display:"flex",gap:8,alignItems:"center",position:"relative"}}>
        <Button icon="add" size="sm" onClick={()=>setShowAdd(true)}>Eintrag</Button>
        <Button variant="secondary" icon="upload" size="sm" onClick={()=>{setShowBank(true);setBankPrev(null);setBankRes(null);}}>Bank-CSV</Button>
        <button onClick={()=>setShowFinMoreMenu(!showMoreMenu)} title="Mehr Aktionen" style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"7px 11px",color:T.text,cursor:"pointer",fontSize:16,fontWeight:600,letterSpacing:"-0.01em",lineHeight:1}}>⋯</button>
        {showMoreMenu&&<>
          <div onClick={()=>setShowFinMoreMenu(false)} style={{position:"fixed",inset:0,zIndex:998}}/>
          <div style={{position:"absolute",top:"100%",right:0,marginTop:6,background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:10,padding:6,zIndex:999,minWidth:240,boxShadow:"0 8px 24px rgba(0,0,0,0.3)"}}>
            {fin.length>0&&<button onClick={()=>{runAiAnalysis();setShowFinMoreMenu(false);}} disabled={aiLoading} style={{width:"100%",textAlign:"left",padding:"10px 12px",background:"none",border:"none",color:T.text,cursor:"pointer",fontSize:13,borderRadius:6,display:"flex",alignItems:"center",gap:10}} onMouseEnter={e=>e.currentTarget.style.background=T.bgHover} onMouseLeave={e=>e.currentTarget.style.background="none"}><Icon name="chart" size={14}/> {aiLoading?"Analyse läuft...":"KI-Analyse (Bericht)"}</button>}
            {fin.length>0&&<button onClick={()=>{runAutoCategorize(true);setShowFinMoreMenu(false);}} disabled={autoCatLoading} style={{width:"100%",textAlign:"left",padding:"10px 12px",background:"none",border:"none",color:T.text,cursor:"pointer",fontSize:13,borderRadius:6,display:"flex",alignItems:"center",gap:10}} onMouseEnter={e=>e.currentTarget.style.background=T.bgHover} onMouseLeave={e=>e.currentTarget.style.background="none"}><Icon name="check" size={14}/> {autoCatLoading?"Sortiere...":"Auto-Kategorisieren"}</button>}
            {fin.length>0&&<button onClick={()=>{runAiCategorize();setShowFinMoreMenu(false);}} disabled={aiCatLoading} style={{width:"100%",textAlign:"left",padding:"10px 12px",background:"none",border:"none",color:T.text,cursor:"pointer",fontSize:13,borderRadius:6,display:"flex",alignItems:"center",gap:10}} onMouseEnter={e=>e.currentTarget.style.background=T.bgHover} onMouseLeave={e=>e.currentTarget.style.background="none"}><Icon name="chart" size={14}/> {aiCatLoading?(aiCatProgress?`KI: ${aiCatProgress.done}/${aiCatProgress.total}`:"KI denkt nach..."):"🤖 KI-Kategorisieren"}</button>}
            {(fin.length>0||bankCount>0)&&<div style={{height:1,background:T.border,margin:"4px 6px"}}/>}
            {bankCount>0&&<button onClick={()=>{setShowDelConfirm('bank');setShowFinMoreMenu(false);}} style={{width:"100%",textAlign:"left",padding:"10px 12px",background:"none",border:"none",color:T.danger,cursor:"pointer",fontSize:13,borderRadius:6,display:"flex",alignItems:"center",gap:10}} onMouseEnter={e=>e.currentTarget.style.background=`${T.danger}15`} onMouseLeave={e=>e.currentTarget.style.background="none"}><Icon name="delete" size={14}/> Bank-Importe löschen ({bankCount})</button>}
            {fin.length>0&&<button onClick={()=>{setShowDelConfirm('all');setShowFinMoreMenu(false);}} style={{width:"100%",textAlign:"left",padding:"10px 12px",background:"none",border:"none",color:T.danger,cursor:"pointer",fontSize:13,borderRadius:6,display:"flex",alignItems:"center",gap:10}} onMouseEnter={e=>e.currentTarget.style.background=`${T.danger}15`} onMouseLeave={e=>e.currentTarget.style.background="none"}><Icon name="delete" size={14}/> Alle löschen ({fin.length})</button>}
          </div>
        </>}
      </div>
    </div>
    {/* Delete Confirmation */}
    <Modal open={!!showDelConfirm} onClose={()=>setShowDelConfirm(null)} title="Einträge löschen?" width={440}>
      <div style={{fontSize:14,color:T.textMuted,marginBottom:20}}>{delLabels[showDelConfirm]}</div>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
        <Button variant="secondary" onClick={()=>setShowDelConfirm(null)}>Abbrechen</Button>
        <Button variant="danger" icon="delete" onClick={()=>bulkDelete(showDelConfirm)}>Ja, löschen</Button>
      </div>
    </Modal>
    <div style={{display:"flex",gap:16,marginBottom:24,flexWrap:"wrap"}}>
      <StatCard label="Umsatz (alle Eingänge)" value={fmtEuro(ti)} icon="money" color={T.success} hideable/>
      <StatCard label="Bereinigter Umsatz" value={fmtEuro(cleanTotalIncome)} sub="Ohne interne Übertragungen" icon="money" color={T.info} hideable/>
      <StatCard label="Ausgaben" value={fmtEuro(te)} icon="money" color={T.danger} hideable/>
      <StatCard label="Gewinn" value={fmtEuro(ti-te)} icon="chart" color={ti-te>=0?T.success:T.danger} hideable/>
    </div>

    {/* Top-Kunden Dashboard */}
    {topClients.length>0&&<div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:20,marginBottom:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
        <h3 style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em",display:"flex",alignItems:"center",gap:8}}>🏆 Top-Kunden ({topClients.length} insgesamt)</h3>
        <button onClick={()=>setShowTopClients(!showTopClients)} style={{background:"none",border:`1px solid ${T.border}`,borderRadius:8,padding:"4px 10px",color:T.textMuted,cursor:"pointer",fontSize:11}}>{showTopClients?"weniger":"alle anzeigen"}</button>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:6}}>
        {(showTopClients?topClients:topClients.slice(0,10)).map((c,i)=>{
          const pct = cleanTotalIncome>0 ? (c.total/cleanTotalIncome*100) : 0;
          return (<div key={c.name} style={{display:"flex",alignItems:"center",gap:12,padding:"8px 0",borderBottom:i<topClients.length-1?`1px solid ${T.border}`:"none"}}>
            <div style={{minWidth:24,fontSize:12,color:T.textDim,fontWeight:600,textAlign:"right"}}>{i+1}</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.name}</div>
              <div style={{fontSize:11,color:T.textDim,marginTop:2}}>{c.count} Zahlung{c.count>1?"en":""}{c.firstDate&&c.lastDate&&c.firstDate!==c.lastDate?` · ${new Date(c.firstDate).toLocaleDateString("de")} → ${new Date(c.lastDate).toLocaleDateString("de")}`:c.lastDate?` · ${new Date(c.lastDate).toLocaleDateString("de")}`:""}</div>
              {/* Bar */}
              <div style={{marginTop:6,height:4,background:T.bg,borderRadius:2,overflow:"hidden"}}>
                <div style={{width:`${pct}%`,height:"100%",background:T.success,borderRadius:2}}/>
              </div>
            </div>
            <div style={{textAlign:"right",minWidth:120}}>
              <div style={{fontSize:14,fontWeight:700,color:T.success,fontFamily:"'JetBrains Mono',monospace"}}>{c.total.toLocaleString("de",{maximumFractionDigits:0})} €</div>
              <div style={{fontSize:10,color:T.textDim}}>{pct.toFixed(1)}% des Umsatzes</div>
            </div>
          </div>);
        })}
      </div>
    </div>}
    {/* AI Analysis Results */}
    {aiLoading&&<div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:24,marginBottom:24,textAlign:"center"}}>
      <div style={{marginBottom:8,color:T.accent}}><Icon name="ai" size={32}/></div>
      <div style={{fontSize:14,fontWeight:600,color:T.accent}}>KI analysiert eure Finanzdaten...</div>
      <div style={{fontSize:12,color:T.textDim,marginTop:4}}>Das dauert ein paar Sekunden</div>
    </div>}
    {aiResult&&<div style={{background:T.bgCard,border:`1px solid ${T.accent}44`,borderRadius:12,padding:24,marginBottom:24,boxShadow:T.shadow}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <h3 style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em",display:"flex",alignItems:"center",gap:8}}><Icon name="ai" size={16}/> <span>KI-Finanzanalyse</span></h3>
        <button onClick={()=>setAiResult(null)} style={{background:"none",border:"none",color:T.textDim,cursor:"pointer"}}><Icon name="close" size={18}/></button>
      </div>
      <div style={{fontSize:13,lineHeight:1.7,color:T.text,whiteSpace:"pre-wrap"}}>{aiResult}</div>
    </div>}
    <Modal open={showBank} onClose={()=>{setShowBank(false);setBankPrev(null);setBankRes(null);}} title="Bank-CSV importieren" width={bankPrev&&bankPrev.length>0?760:520}>
      <FileDrop onData={handleBank} label="Bank-Datei hier hochladen (CSV oder XLSX)" sublabel="Sparkasse, Volksbank, DKB, ING, Commerzbank, N26..."/>
      {bankRes&&<div style={{background:`${T.success}15`,border:`1px solid ${T.success}40`,borderRadius:12,padding:18,marginTop:16,display:"flex",alignItems:"center",gap:12}}><div style={{color:T.success}}><Icon name="check" size={24}/></div><div style={{fontSize:14,fontWeight:600,color:T.success}}>{bankRes.count} Transaktionen importiert!</div></div>}
      {bankPrev&&bankPrev.length>0&&<div style={{marginTop:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}><div style={{fontSize:14,fontWeight:700}}>Vorschau — {bankPrev.length} Transaktionen</div><div style={{display:"flex",gap:8}}><Button variant="secondary" size="sm" onClick={()=>setBankPrev(null)}>Zurück</Button><Button icon="upload" size="sm" onClick={importBank}>{bankPrev.length} importieren</Button></div></div>
        <div style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:12,overflow:"auto",maxHeight:340}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}><thead><tr style={{borderBottom:`1px solid ${T.border}`}}>{["Datum","Typ","Beschreibung","Betrag"].map(h=>(<th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:11,fontWeight:600,color:T.textDim,textTransform:"uppercase",position:"sticky",top:0,background:T.bgInput}}>{h}</th>))}</tr></thead>
          <tbody>{bankPrev.slice(0,40).map((f,i)=>(<tr key={i} style={{borderBottom:`1px solid ${T.border}`}}><td style={{padding:"10px 14px",fontSize:13,fontFamily:"'JetBrains Mono',monospace"}}>{f.date}</td><td style={{padding:"10px 14px"}}><Badge color={f.type==="income"?T.success:T.danger}>{f.type==="income"?"Einnahme":"Ausgabe"}</Badge></td><td style={{padding:"10px 14px",fontSize:13,maxWidth:300,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.description}</td><td style={{padding:"10px 14px",fontSize:14,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:f.type==="income"?T.success:T.danger}}>{f.type==="income"?"+":"-"}{f.amount.toLocaleString("de")} €</td></tr>))}</tbody></table>
        </div>
      </div>}
    </Modal>
    <Modal open={showAdd} onClose={()=>setShowAdd(false)} title="Finanzeintrag" width={480}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <Select label="Typ" options={[{value:"income",label:"Einnahme"},{value:"expense",label:"Ausgabe"}]} value={form.type} onChange={e=>setForm({...form,type:e.target.value})}/>
        <Input label="Betrag (€)" type="number" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} placeholder="0.00"/>
        <Input label="Beschreibung" value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="z.B. Website-Projekt"/>
        <Input label="Kategorie" value={form.category} onChange={e=>setForm({...form,category:e.target.value})} placeholder="z.B. Projektarbeit"/>
        <Input label="Datum" type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})}/>
        <Button icon="check" onClick={add} style={{marginTop:8}}>Speichern</Button>
      </div>
    </Modal>

    {/* Auto-Kategorisierungs-Ergebnis */}
    {autoCatResult&&!autoCatResult.error&&<div style={{background:`${T.success}15`,border:`1px solid ${T.success}40`,borderRadius:12,padding:14,marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
      <div style={{fontSize:13}}>
        <span style={{color:T.success,fontWeight:600}}>✓ {autoCatResult.matched} von {autoCatResult.total} Buchungen automatisch kategorisiert</span>
        {autoCatResult.total - autoCatResult.matched > 0 && <span style={{color:T.textDim,marginLeft:8}}>— {autoCatResult.total - autoCatResult.matched} nicht erkannt (KI-Kategorisierung versuchen oder manuell zuordnen)</span>}
      </div>
      <button onClick={()=>setAutoCatResult(null)} style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",fontSize:18}}>×</button>
    </div>}
    {autoCatResult?.error&&<div style={{background:`${T.danger}15`,border:`1px solid ${T.danger}40`,borderRadius:12,padding:14,marginBottom:16,fontSize:13,color:T.danger}}>Fehler: {autoCatResult.error}</div>}

    {/* KI-Kategorisierungs-Ergebnis */}
    {aiCatResult&&!aiCatResult.error&&<div style={{background:`${T.info}15`,border:`1px solid ${T.info}40`,borderRadius:12,padding:14,marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
      <div style={{fontSize:13}}>
        <span style={{color:T.info,fontWeight:600}}>🤖 KI hat {aiCatResult.matched} von {aiCatResult.total} Buchungen kategorisiert</span>
        {aiCatResult.total - aiCatResult.matched > 0 && <span style={{color:T.textDim,marginLeft:8}}>— {aiCatResult.total - aiCatResult.matched} bleiben unter "Sonstiges" oder unsortiert</span>}
      </div>
      <button onClick={()=>setAiCatResult(null)} style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",fontSize:18}}>×</button>
    </div>}
    {aiCatResult?.error&&<div style={{background:`${T.warning}15`,border:`1px solid ${T.warning}40`,borderRadius:12,padding:14,marginBottom:16,fontSize:13,color:T.warning,display:"flex",justifyContent:"space-between",gap:12}}>
      <span>{aiCatResult.error}</span>
      <button onClick={()=>setAiCatResult(null)} style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",fontSize:18}}>×</button>
    </div>}
    {aiCatLoading&&aiCatProgress&&<div style={{background:`${T.info}15`,border:`1px solid ${T.info}40`,borderRadius:12,padding:14,marginBottom:16,fontSize:13,color:T.info,display:"flex",alignItems:"center",gap:12}}>
      <span style={{display:"inline-block",width:16,height:16,border:`2px solid ${T.info}`,borderTopColor:"transparent",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
      <span>🤖 KI analysiert Buchungen — {aiCatProgress.done} / {aiCatProgress.total}</span>
    </div>}

    {/* Kategorie-Filter */}
    {fin.length>0&&<div style={{display:"flex",gap:6,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
      <button onClick={()=>setCategoryFilter("all")} style={{padding:"5px 12px",borderRadius:12,border:`1px solid ${categoryFilter==="all"?T.accent:T.border}`,background:categoryFilter==="all"?T.accentGlow:"transparent",color:categoryFilter==="all"?T.accent:T.textMuted,cursor:"pointer",fontSize:11,fontWeight:600}}>Alle ({fin.length})</button>
      {(()=>{
        const uncatCount = fin.filter(f=>!f.category||f.category.trim()==="").length;
        return uncatCount>0&&<button onClick={()=>setCategoryFilter("uncategorized")} style={{padding:"5px 12px",borderRadius:12,border:`1px solid ${categoryFilter==="uncategorized"?T.warning:T.border}`,background:categoryFilter==="uncategorized"?`${T.warning}22`:"transparent",color:categoryFilter==="uncategorized"?T.warning:T.textMuted,cursor:"pointer",fontSize:11,fontWeight:600}}>Ohne Kategorie ({uncatCount})</button>;
      })()}
      {FINANCE_CATEGORIES.map(cat=>{
        const count = fin.filter(f=>f.category===cat.id).length;
        if(count===0) return null;
        const active = categoryFilter===cat.id;
        return (<button key={cat.id} onClick={()=>setCategoryFilter(cat.id)} style={{padding:"5px 12px",borderRadius:12,border:`1px solid ${active?cat.color:T.border}`,background:active?`${cat.color}22`:"transparent",color:active?cat.color:T.textMuted,cursor:"pointer",fontSize:11,fontWeight:active?600:400}}>{cat.label} ({count})</button>);
      })}
    </div>}

    {/* Suchfeld */}
    {fin.length>0&&<div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
      <div style={{position:"relative",flex:1,minWidth:240}}>
        <input value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} placeholder="Buchung suchen (Beschreibung, Betrag, Datum)..." style={{width:"100%",padding:"10px 14px 10px 38px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:10,color:T.text,fontSize:13,outline:"none"}}/>
        <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:T.textDim}}><Icon name="search" size={16}/></span>
        {searchQuery&&<button onClick={()=>setSearchQuery("")} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:T.textDim,cursor:"pointer",fontSize:16}}>×</button>}
      </div>
    </div>}

    {/* Bulk-Aktionsleiste — erscheint wenn etwas ausgewählt ist */}
    {selectedIds.length>0&&<div style={{display:"flex",gap:10,marginBottom:14,padding:"12px 16px",background:T.accentGlow,border:`1px solid ${T.accent}44`,borderRadius:10,alignItems:"center",flexWrap:"wrap"}}>
      <span style={{fontSize:13,fontWeight:700,color:T.accent}}>{selectedIds.length} ausgewählt</span>
      <div style={{flex:1}}/>
      <span style={{fontSize:12,color:T.textMuted}}>Als Kategorie setzen:</span>
      <button onClick={()=>bulkCategorize("konto-uebertrag")} style={{padding:"6px 14px",background:T.accent,border:"none",borderRadius:8,color:"#fff",fontSize:12,fontWeight:600,cursor:"pointer"}}>↔ Konto-Übertrag</button>
      <button onClick={()=>bulkCategorize("gutschrift")} style={{padding:"6px 14px",background:"#7c3aed",border:"none",borderRadius:8,color:"#fff",fontSize:12,fontWeight:600,cursor:"pointer"}}>↩ Gutschrift</button>
      <select onChange={e=>{if(e.target.value)bulkCategorize(e.target.value);}} defaultValue="" style={{padding:"6px 10px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,fontSize:12,cursor:"pointer"}}>
        <option value="">Andere Kategorie…</option>
        {FINANCE_CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
      </select>
      <button onClick={()=>setSelectedIds([])} style={{padding:"6px 12px",background:"none",border:`1px solid ${T.border}`,borderRadius:8,color:T.textMuted,fontSize:12,cursor:"pointer"}}>Auswahl aufheben</button>
    </div>}

    <div className="table-wrap" style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,overflow:"auto",WebkitOverflowScrolling:"touch"}}>
      {fin.length===0?<Empty icon="money" title="Keine Einträge" desc="Eintrag hinzufügen oder Bank-CSV importieren"/>:(()=>{
        // Filter anwenden (Kategorie + Suche)
        const q = searchQuery.trim().toLowerCase();
        const filtered = fin.filter(f=>{
          // Kategorie-Filter
          if(categoryFilter==="uncategorized"){ if(f.category && f.category.trim()!=="") return false; }
          else if(categoryFilter!=="all"){ if(f.category!==categoryFilter) return false; }
          // Suche
          if(q){
            const hay = `${f.description||""} ${f.amount||""} ${fmtDate(f.date)||""} ${f.date||""}`.toLowerCase();
            if(!hay.includes(q)) return false;
          }
          return true;
        });
        const allSelected = filtered.length>0 && filtered.every(f=>selectedIds.includes(f.id));
        const toggleAll = ()=>{ if(allSelected) setSelectedIds([]); else setSelectedIds(filtered.map(f=>f.id)); };
        return (<table style={{width:"100%",minWidth:820,borderCollapse:"collapse"}}>
          <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>
            <th style={{padding:"12px 16px",width:32}}><input type="checkbox" checked={allSelected} onChange={toggleAll} style={{cursor:"pointer"}}/></th>
            {["Datum","Typ","Beschreibung","Kategorie","Betrag",""].map(h=>(<th key={h} style={{padding:"12px 16px",textAlign:"left",fontSize:11,fontWeight:600,color:T.textDim,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>))}
          </tr></thead>
          <tbody>{filtered.slice().sort((a,b)=>new Date(b.date)-new Date(a.date)).map(f=>{
            const catInfo = getFinanceCategoryInfo(f.category);
            const isSel = selectedIds.includes(f.id);
            return (<tr key={f.id} style={{borderBottom:`1px solid ${T.border}`,background:isSel?T.accentGlow:"transparent"}}>
              <td style={{padding:"12px 16px"}}><input type="checkbox" checked={isSel} onChange={()=>toggleSelect(f.id)} style={{cursor:"pointer"}}/></td>
              <td style={{padding:"12px 16px",fontSize:13,fontFamily:"'JetBrains Mono',monospace",whiteSpace:"nowrap"}}>{fmtDate(f.date)}</td>
              <td style={{padding:"12px 16px",whiteSpace:"nowrap"}}><Badge color={f.type==="income"?T.success:T.danger}>{f.type==="income"?"Einnahme":"Ausgabe"}</Badge></td>
              <td style={{padding:"12px 16px",fontSize:13,minWidth:200,maxWidth:380}}>{f.description}</td>
              <td style={{padding:"12px 16px",whiteSpace:"nowrap"}}>
                {editingCatId===f.id ? (
                  <select autoFocus value={f.category||""} onChange={e=>updateCategory(f.id, e.target.value)} onBlur={()=>setEditingCatId(null)} style={{padding:"4px 8px",fontSize:12,background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:6,color:T.text,cursor:"pointer"}}>
                    <option value="">— Keine —</option>
                    {FINANCE_CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                ) : (
                  <button onClick={()=>setEditingCatId(f.id)} style={{padding:"3px 10px",borderRadius:7,border:`1px solid ${catInfo?catInfo.color+"40":T.border}`,background:catInfo?`${catInfo.color}15`:"transparent",color:catInfo?catInfo.color:T.textDim,cursor:"pointer",fontSize:11,fontWeight:600,whiteSpace:"nowrap"}}>{catInfo?catInfo.label:"+ Kategorie"}</button>
                )}
              </td>
              <td style={{padding:"12px 16px",fontSize:14,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:f.type==="income"?T.success:T.danger,whiteSpace:"nowrap",textAlign:"right"}}>{f.type==="income"?"+":"-"}{f.amount.toLocaleString("de")} €</td>
              <td style={{padding:"12px 16px"}}><button onClick={()=>del(f.id)} style={{background:"none",border:"none",color:T.textDim,cursor:"pointer"}}><Icon name="delete" size={16}/></button></td>
            </tr>);
          })}</tbody></table>);
      })()}
    </div>
  </div>);
};

// ─── SEVDESK ───
const SevDeskPage = () => {
  const [status,setStatus]=useState(null);      // {verbunden, version, konten}
  const [statusFehler,setStatusFehler]=useState(null);
  const [konten,setKonten]=useState([]);         // Buchungskonten
  const [zahlungen,setZahlungen]=useState([]);   // offene Bankzahlungen
  const [belege,setBelege]=useState([]);         // hochgeladene Belege mit Auslesung
  const [ladenBasis,setLadenBasis]=useState(true);
  const [alleZeigen,setAlleZeigen]=useState({}); // je Beleg: volle Zahlungsliste zeigen

  // Grunddaten laden: Verbindung, Konten, offene Zahlungen
  const init=async()=>{
    setLadenBasis(true); setStatusFehler(null); setStatus(null);
    let s=null, code=0;
    try{
      const res=await fetch(`${API_BASE}/sevdesk-status`,{headers:authHeaders()});
      code=res.status;
      s=await res.json().catch(()=>null);
    }catch(e){ setStatusFehler("Verbindung zum Server fehlgeschlagen. Bist du online?"); setLadenBasis(false); return; }
    // Erfolg nur, wenn wirklich verbunden. api schluckt Fehler sonst still.
    if(!s || !s.verbunden){
      const msg = (s&&s.error) ? s.error
        : (code===404 ? "Der Server kennt den Buchhaltungs-Bereich noch nicht. Bitte die neue index.php aus dem Paket in den Ordner api hochladen (die alte überschreiben)."
        : "sevdesk ist noch nicht verbunden.");
      setStatusFehler(msg); setLadenBasis(false); return;
    }
    setStatus(s);
    const [k,z]=await Promise.all([api.get("sevdesk-accounting-types"), api.get("sevdesk-open-transactions")]);
    if(k&&k.konten) setKonten(k.konten||[]);
    if(z&&z.zahlungen) setZahlungen(z.zahlungen||[]);
    setLadenBasis(false);
  };
  useEffect(()=>{ init(); },[]);

  // Kandidaten-Zahlungen für einen Betrag: gleiche Summe (centgenau), Ausgaben zuerst
  const kandidaten=(brutto)=>{
    const b=Math.round((Number(brutto)||0)*100);
    return zahlungen.filter(z=>Math.round(z.betrag*100)===b);
  };
  // Konto per Namen grob vorschlagen (Claude-Kategorie gegen Kontennamen)
  const kontoVorschlag=(kategorie)=>{
    if(!kategorie||!konten.length) return "";
    const kl=String(kategorie).toLowerCase();
    const t=konten.find(k=>String(k.name).toLowerCase().includes(kl)||kl.includes(String(k.name).toLowerCase().split(" ")[0]));
    return t?t.id:"";
  };

  // Eine Datei auslesen lassen
  const lesen=async(beleg)=>{
    try{
      const istPdf=beleg.fileName.toLowerCase().endsWith(".pdf");
      const block=istPdf
        ? {type:"document",source:{type:"base64",media_type:"application/pdf",data:beleg.fileBase64}}
        : {type:"image",source:{type:"base64",media_type:beleg.fileName.toLowerCase().endsWith(".png")?"image/png":"image/jpeg",data:beleg.fileBase64}};
      const prompt="Das ist ein Beleg oder eine Rechnung. Lies die Kopfdaten aus und gib NUR ein JSON-Objekt zurück, ohne Text drumherum, ohne Markdown: {\"supplierName\": string (Lieferant/Firma), \"date\": \"YYYY-MM-DD\" (Belegdatum), \"gross\": number (Brutto-Gesamtbetrag als Zahl, Punkt als Dezimaltrenner), \"taxRate\": number (19, 7 oder 0), \"invoiceNumber\": string (Rechnungsnummer, sonst leer), \"category\": string (kurze deutsche Ausgaben-Kategorie, z.B. Bürobedarf, Software, Bewirtung)}. Wenn ein Wert fehlt, nutze leeren String oder 0.";
      const resp=await fetch("/api/anthropic-proxy",{method:"POST",headers:authHeaders({"Content-Type":"application/json"}),body:JSON.stringify({
        model:"claude-sonnet-4-20250514", max_tokens:800,
        messages:[{role:"user",content:[block,{type:"text",text:prompt}]}]
      })});
      const data=await resp.json();
      if(data.error){ const em=(typeof data.error==="string")?data.error:(data.error.message||JSON.stringify(data.error)); return {...beleg,status:"fehler",fehler:em}; }
      let txt=(data.content||[]).filter(c=>c.type==="text").map(c=>c.text).join("").trim().replace(/```json/gi,"").replace(/```/g,"").trim();
      let d={}; try{ d=JSON.parse(txt); }catch(e){ return {...beleg,status:"fehler",fehler:"Beleg ließ sich nicht sauber auslesen."}; }
      const brutto=Number(d.gross)||0;
      const kand=kandidaten(brutto);
      return {...beleg, status:"ok",
        extract:{supplierName:d.supplierName||"",date:d.date||"",gross:brutto,taxRate:Number(d.taxRate)||19,invoiceNumber:d.invoiceNumber||"",category:d.category||""},
        matchId: kand.length ? kand[0].id : "",
        kontoTypId: kontoVorschlag(d.category),
      };
    }catch(e){ return {...beleg,status:"fehler",fehler:"Beim Auslesen ist etwas schiefgelaufen."}; }
  };

  // Dateien aufnehmen und nacheinander auslesen
  const dateienRein=async(fileList)=>{
    const files=Array.from(fileList||[]);
    for(const f of files){
      if(!/\.(pdf|jpg|jpeg|png)$/i.test(f.name)){ continue; }
      if(f.size>12000000){ setBelege(b=>[...b,{id:"x"+Math.random().toString(36).slice(2),fileName:f.name,status:"fehler",fehler:"Datei zu groß (max 12 MB)."}]); continue; }
      const b64=await new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(String(r.result).split(",")[1]);r.onerror=rej;r.readAsDataURL(f);});
      const id="b"+Math.random().toString(36).slice(2);
      const beleg={id,fileName:f.name,fileBase64:b64,status:"liest"};
      setBelege(b=>[...b,beleg]);
      const fertig=await lesen(beleg);
      setBelege(b=>b.map(x=>x.id===id?fertig:x));
    }
  };

  const setzeFeld=(id,feld,wert)=>setBelege(b=>b.map(x=>{
    if(x.id!==id)return x;
    if(feld==="matchId"||feld==="kontoTypId")return {...x,[feld]:wert};
    return {...x,extract:{...x.extract,[feld]:wert}};
  }));
  const entfernen=(id)=>setBelege(b=>b.filter(x=>x.id!==id));

  // Einen Beleg wirklich in sevdesk buchen
  const buchen=async(beleg)=>{
    const z=zahlungen.find(t=>t.id===beleg.matchId);
    if(!z){ setBelege(b=>b.map(x=>x.id===beleg.id?{...x,fehler:"Bitte zuerst eine Zahlung zuordnen."}:x)); return; }
    if(!beleg.kontoTypId){ setBelege(b=>b.map(x=>x.id===beleg.id?{...x,fehler:"Bitte ein Buchungskonto wählen."}:x)); return; }
    setBelege(b=>b.map(x=>x.id===beleg.id?{...x,status:"bucht",fehler:null}:x));
    const r=await api.post("sevdesk-book-voucher",{
      supplierName:beleg.extract.supplierName,
      voucherDate:beleg.extract.date,
      description:beleg.extract.invoiceNumber,
      taxRate:beleg.extract.taxRate,
      gross:beleg.extract.gross,
      accountingTypeId:beleg.kontoTypId,
      transactionId:z.id,
      accountId:z.kontoId,
      fileBase64:beleg.fileBase64,
      fileName:beleg.fileName,
    });
    if(r&&r.success){
      // Zahlung ist weg vom Tisch, aus der offenen Liste nehmen
      setZahlungen(list=>list.filter(t=>t.id!==z.id));
      setBelege(b=>b.map(x=>x.id===beleg.id?{...x,status:"gebucht",voucherId:r.voucherId,fehler:null}:x));
    }else{
      const msg=(r&&r.error)?r.error:"Buchen fehlgeschlagen.";
      const detail=r&&r.detail?(" ("+(typeof r.detail==="string"?r.detail:JSON.stringify(r.detail)).slice(0,200)+")"):"";
      setBelege(b=>b.map(x=>x.id===beleg.id?{...x,status:"ok",fehler:msg+detail}:x));
    }
  };

  const zForm=(z)=>`${z.datum} · ${rfEuroGenau(z.betrag)} · ${z.name||z.zweck||"—"}`;

  return (<div>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,flexWrap:"wrap",gap:12}}>
      <h1 style={{fontSize:26,fontWeight:700,letterSpacing:"-0.02em"}}>Buchhaltung</h1>
      {status&&status.verbunden&&<span style={{display:"inline-flex",alignItems:"center",gap:7,fontSize:12.5,color:T.success,background:`${T.success}18`,border:`1px solid ${T.success}55`,borderRadius:20,padding:"5px 12px",fontWeight:600}}>
        <span style={{width:8,height:8,borderRadius:"50%",background:T.success}}/> mit sevdesk verbunden{status.version?` · System ${status.version}`:""}
      </span>}
    </div>
    <p style={{color:T.textMuted,fontSize:14,marginBottom:22,lineHeight:1.5}}>Wirf deine Belege rein. Das System liest sie aus, sucht die passende offene Zahlung und schlägt ein Buchungskonto vor. Gebucht wird erst, wenn du bei jedem Beleg auf Buchen drückst.</p>

    {ladenBasis&&<div style={{color:T.textDim,fontSize:14,padding:"20px 0"}}>Verbindung zu sevdesk wird geprüft...</div>}

    {statusFehler&&<div style={{background:`${T.danger}14`,border:`1px solid ${T.danger}55`,borderRadius:12,padding:"16px 18px",marginBottom:20}}>
      <div style={{fontWeight:600,color:T.danger,marginBottom:5,fontSize:14}}>Keine Verbindung zu sevdesk</div>
      <div style={{fontSize:13,color:T.textMuted,lineHeight:1.6}}>{statusFehler} Trag deinen sevdesk-Token in die secrets.php auf dem Server ein (SEVDESK_API_TOKEN), dann lädt dieser Bereich die offenen Zahlungen.</div>
      <button onClick={init} style={{marginTop:12,padding:"8px 14px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,fontSize:13,fontWeight:600,cursor:"pointer"}}>Nochmal versuchen</button>
    </div>}

    {status&&status.verbunden&&<>
      <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:20,alignItems:"center"}}>
        <label style={{display:"inline-flex",alignItems:"center",gap:8,padding:"11px 18px",background:T.accent,color:"#fff",borderRadius:10,fontSize:14,fontWeight:600,cursor:"pointer"}}>
          <Icon name="upload" size={16}/> Belege hochladen
          <input type="file" accept="application/pdf,image/jpeg,image/png" multiple onChange={e=>{dateienRein(e.target.files);e.target.value="";}} style={{display:"none"}}/>
        </label>
        <span style={{fontSize:13,color:T.textDim}}>{zahlungen.length} offene Zahlungen · {konten.length} Buchungskonten</span>
      </div>

      {belege.length===0
        ? <div style={{border:`1.5px dashed ${T.border}`,borderRadius:14,padding:"40px 20px",textAlign:"center",color:T.textDim,fontSize:14}}>Noch keine Belege geladen. Lad zum Testen erst einen einzelnen hoch.</div>
        : <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {belege.map(beleg=>{
              const kand=beleg.extract?kandidaten(beleg.extract.gross):[];
              const zeigeAlle=alleZeigen[beleg.id];
              const auswahl=zeigeAlle?zahlungen:kand;
              return (
              <div key={beleg.id} style={{background:T.bgCard,border:`1px solid ${beleg.status==="gebucht"?T.success+"66":T.border}`,borderRadius:12,padding:18,boxShadow:T.shadow}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,marginBottom:beleg.status==="ok"||beleg.status==="gebucht"?14:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
                    <Icon name="clipboard" size={16}/>
                    <span style={{fontSize:13.5,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{beleg.fileName}</span>
                  </div>
                  {beleg.status==="liest"&&<span style={{fontSize:12.5,color:T.info}}>wird ausgelesen...</span>}
                  {beleg.status==="bucht"&&<span style={{fontSize:12.5,color:T.info}}>wird gebucht...</span>}
                  {beleg.status==="gebucht"&&<span style={{fontSize:12.5,color:T.success,fontWeight:600,display:"inline-flex",alignItems:"center",gap:5}}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.success} strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg> gebucht</span>}
                  {(beleg.status==="ok"||beleg.status==="fehler")&&<button onClick={()=>entfernen(beleg.id)} title="Entfernen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:2,display:"flex",flexShrink:0}}><Icon name="close" size={15}/></button>}
                </div>

                {beleg.status==="fehler"&&<div style={{fontSize:12.5,color:T.danger,marginTop:8}}>{beleg.fehler}</div>}

                {(beleg.status==="ok"||beleg.status==="bucht"||beleg.status==="gebucht")&&beleg.extract&&<>
                  {/* Ausgelesene Daten */}
                  <div className="beleg-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
                    <div><div style={{fontSize:11,color:T.textDim,fontWeight:600,marginBottom:4}}>LIEFERANT</div>
                      <input value={beleg.extract.supplierName} disabled={beleg.status!=="ok"} onChange={e=>setzeFeld(beleg.id,"supplierName",e.target.value)} style={{width:"100%",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 11px",color:T.text,fontSize:13,outline:"none"}}/></div>
                    <div><div style={{fontSize:11,color:T.textDim,fontWeight:600,marginBottom:4}}>DATUM</div>
                      <input value={beleg.extract.date} disabled={beleg.status!=="ok"} onChange={e=>setzeFeld(beleg.id,"date",e.target.value)} placeholder="YYYY-MM-DD" style={{width:"100%",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 11px",color:T.text,fontSize:13,outline:"none",fontFamily:"'JetBrains Mono',monospace"}}/></div>
                    <div><div style={{fontSize:11,color:T.textDim,fontWeight:600,marginBottom:4}}>BRUTTOBETRAG</div>
                      <input value={beleg.extract.gross} disabled={beleg.status!=="ok"} onChange={e=>setzeFeld(beleg.id,"gross",parseFloat(String(e.target.value).replace(",","."))||0)} style={{width:"100%",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 11px",color:T.text,fontSize:13,outline:"none",fontFamily:"'JetBrains Mono',monospace"}}/></div>
                    <div><div style={{fontSize:11,color:T.textDim,fontWeight:600,marginBottom:4}}>STEUERSATZ</div>
                      <select value={beleg.extract.taxRate} disabled={beleg.status!=="ok"} onChange={e=>setzeFeld(beleg.id,"taxRate",Number(e.target.value))} style={{width:"100%",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 11px",color:T.text,fontSize:13,outline:"none"}}>
                        <option value={19}>19%</option><option value={7}>7%</option><option value={0}>0%</option>
                      </select></div>
                    <div style={{gridColumn:"1 / -1"}}><div style={{fontSize:11,color:T.textDim,fontWeight:600,marginBottom:4}}>BELEGNUMMER (für sevdesk)</div>
                      <input value={beleg.extract.invoiceNumber} disabled={beleg.status!=="ok"} onChange={e=>setzeFeld(beleg.id,"invoiceNumber",e.target.value)} placeholder={`leer lassen = "Beleg ${beleg.extract.date||"Datum"}"`} style={{width:"100%",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 11px",color:T.text,fontSize:13,outline:"none"}}/></div>
                  </div>

                  {/* Zahlung zuordnen */}
                  <div style={{marginBottom:14}}>
                    <div style={{fontSize:11,color:T.textDim,fontWeight:600,marginBottom:4}}>ZUGEORDNETE ZAHLUNG</div>
                    {auswahl.length===0
                      ? <div style={{fontSize:12.5,color:T.warning,background:`${T.warning}14`,border:`1px solid ${T.warning}44`,borderRadius:8,padding:"9px 12px"}}>Keine offene Zahlung über {rfEuroGenau(beleg.extract.gross)} gefunden. <button onClick={()=>setAlleZeigen(a=>({...a,[beleg.id]:true}))} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",textDecoration:"underline",fontFamily:"inherit",fontSize:12.5}}>Alle Zahlungen zeigen</button></div>
                      : <select value={beleg.matchId} disabled={beleg.status!=="ok"} onChange={e=>setzeFeld(beleg.id,"matchId",e.target.value)} style={{width:"100%",background:T.bgInput,border:`1px solid ${beleg.matchId?T.border:T.warning+"88"}`,borderRadius:8,padding:"9px 11px",color:T.text,fontSize:13,outline:"none"}}>
                          <option value="">— keine Zuordnung —</option>
                          {auswahl.map(z=>(<option key={z.id} value={z.id}>{zForm(z)}</option>))}
                        </select>}
                    {kand.length>0&&!zeigeAlle&&<button onClick={()=>setAlleZeigen(a=>({...a,[beleg.id]:true}))} style={{marginTop:6,background:"none",border:"none",color:T.textDim,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>andere Zahlung wählen</button>}
                  </div>

                  {/* Buchungskonto */}
                  <div style={{marginBottom:14}}>
                    <div style={{fontSize:11,color:T.textDim,fontWeight:600,marginBottom:4}}>BUCHUNGSKONTO {beleg.extract.category?`· Vorschlag aus „${beleg.extract.category}“`:""}</div>
                    <select value={beleg.kontoTypId} disabled={beleg.status!=="ok"} onChange={e=>setzeFeld(beleg.id,"kontoTypId",e.target.value)} style={{width:"100%",background:T.bgInput,border:`1px solid ${beleg.kontoTypId?T.border:T.warning+"88"}`,borderRadius:8,padding:"9px 11px",color:T.text,fontSize:13,outline:"none"}}>
                      <option value="">— Konto wählen —</option>
                      {konten.map(k=>(<option key={k.id} value={k.id}>{k.nummer?`${k.nummer} · `:""}{k.name}</option>))}
                    </select>
                  </div>

                  {beleg.fehler&&beleg.status==="ok"&&<div style={{fontSize:12.5,color:T.danger,marginBottom:12,lineHeight:1.5}}>{beleg.fehler}</div>}

                  {beleg.status==="ok"&&<div style={{display:"flex",justifyContent:"flex-end"}}>
                    <Button onClick={()=>buchen(beleg)} disabled={!beleg.matchId||!beleg.kontoTypId}>In sevdesk buchen</Button>
                  </div>}
                  {beleg.status==="gebucht"&&<div style={{fontSize:12.5,color:T.success}}>Beleg angelegt und auf die Zahlung gebucht. Du kannst ihn in sevdesk kontrollieren.</div>}
                </>}
              </div>);
            })}
          </div>}
    </>}
  </div>);
};

// ─── CHECKLIST / MONATSPLANUNG ───
// ═══════════════════════════════════════
// MONATSPLANUNG-BAUSTEIN: Umsatzkurve (Soll vs Ist, 12 Monate) mit Grafik
// ═══════════════════════════════════════
const MONTH_LABELS = ["Jan","Feb","Mär","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Dez"];
const RevenueCurveModule = () => {
  const nowYear = new Date().getFullYear();
  const [year,setYear] = useState(nowYear);
  const [rows,setRows] = useState([]); // [{month,soll,ist}]
  const [loading,setLoading] = useState(true);
  const saveTimers = useRef({});

  const load = (y) => {
    setLoading(true);
    api.get("planning-revenue", `year=${y}`)
      .then(d=>{ setRows(d.rows||[]); setLoading(false); })
      .catch(()=>setLoading(false));
  };
  useEffect(()=>{ load(year); }, [year]);

  const updateCell = (month, field, value) => {
    setRows(prev=>prev.map(r=>r.month===month?{...r,[field]:value}:r));
    // debounced speichern pro Monat
    const key = `${month}`;
    if(saveTimers.current[key]) clearTimeout(saveTimers.current[key]);
    saveTimers.current[key] = setTimeout(async ()=>{
      const row = (rows.find(r=>r.month===month))||{month,soll:0,ist:0};
      const merged = {...row,[field]:value};
      await api.put("planning-revenue", {year, month, soll:parseFloat(merged.soll)||0, ist:parseFloat(merged.ist)||0});
    }, 600);
  };

  const yearOptions = [];
  for(let y=nowYear-2;y<=nowYear+2;y++) yearOptions.push({value:y,label:String(y)});

  // Grafik-Geometrie
  const W=720, H=320, PAD_L=64, PAD_R=120, PAD_T=24, PAD_B=36;
  const maxVal = Math.max(1, ...rows.map(r=>Math.max(parseFloat(r.soll)||0, parseFloat(r.ist)||0)));
  const niceMax = (()=>{ const p=Math.pow(10,Math.floor(Math.log10(maxVal))); const n=Math.ceil(maxVal/p)*p; return n||1; })();
  const x = (m) => PAD_L + (W-PAD_L-PAD_R) * ((m-1)/11);
  const y = (v) => PAD_T + (H-PAD_T-PAD_B) * (1 - (v/niceMax));
  const linePath = (field) => rows.map((r,i)=>`${i===0?"M":"L"} ${x(r.month).toFixed(1)} ${y(parseFloat(r[field])||0).toFixed(1)}`).join(" ");
  const gridLines = 5;

  return (<div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:24,marginBottom:24}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18,flexWrap:"wrap",gap:12}}>
      <div>
        <h3 style={{fontSize:18,fontWeight:700,letterSpacing:"-0.01em"}}>Umsatzkurve Soll gegen Ist</h3>
        <p style={{fontSize:13,color:T.textMuted,marginTop:2}}>Trag pro Monat den Soll- und Ist-Umsatz ein. Die Kurve aktualisiert sich automatisch.</p>
      </div>
      <Select options={yearOptions} value={year} onChange={e=>setYear(parseInt(e.target.value))}/>
    </div>

    {/* Grafik */}
    <div className="table-wrap" style={{marginBottom:20}}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",minWidth:560,height:"auto"}}>
        {/* Gitterlinien + Y-Beschriftung */}
        {Array.from({length:gridLines+1}).map((_,i)=>{
          const val = niceMax*(1-i/gridLines);
          const yy = PAD_T + (H-PAD_T-PAD_B)*(i/gridLines);
          return (<g key={i}>
            <line x1={PAD_L} y1={yy} x2={W-PAD_R} y2={yy} stroke={T.border} strokeWidth="1"/>
            <text x={PAD_L-8} y={yy+4} textAnchor="end" fill={T.textDim} fontSize="11">{Math.round(val).toLocaleString("de")}</text>
          </g>);
        })}
        {/* X-Beschriftung */}
        {rows.map(r=>(<text key={r.month} x={x(r.month)} y={H-PAD_B+18} textAnchor="middle" fill={T.textDim} fontSize="11">{MONTH_LABELS[r.month-1]}</text>))}
        {/* Linien */}
        <path d={linePath("soll")} fill="none" stroke={T.info} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"/>
        <path d={linePath("ist")} fill="none" stroke={T.accent} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"/>
        {/* Punkte */}
        {rows.map(r=>(<circle key={"s"+r.month} cx={x(r.month)} cy={y(parseFloat(r.soll)||0)} r="3" fill={T.info}/>))}
        {rows.map(r=>(<circle key={"i"+r.month} cx={x(r.month)} cy={y(parseFloat(r.ist)||0)} r="3" fill={T.accent}/>))}
        {/* Legende */}
        <circle cx={W-PAD_R+20} cy={PAD_T+6} r="5" fill={T.info}/><text x={W-PAD_R+30} y={PAD_T+10} fill={T.textMuted} fontSize="12" fontWeight="600">Soll</text>
        <circle cx={W-PAD_R+20} cy={PAD_T+28} r="5" fill={T.accent}/><text x={W-PAD_R+30} y={PAD_T+32} fill={T.textMuted} fontSize="12" fontWeight="600">Ist</text>
      </svg>
    </div>

    {/* Tabelle */}
    {loading ? <div style={{color:T.textDim,fontSize:13,padding:10}}>Lädt...</div> :
    <div className="table-wrap">
      <table style={{width:"100%",borderCollapse:"collapse",minWidth:520}}>
        <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>
          {["Monat","Soll Umsatz (€)","Ist Umsatz (€)"].map(h=>(<th key={h} style={{padding:"10px 14px",textAlign:h==="Monat"?"left":"right",fontSize:11,fontWeight:600,color:T.textDim,textTransform:"uppercase"}}>{h}</th>))}
        </tr></thead>
        <tbody>
          {rows.map(r=>(
            <tr key={r.month} style={{borderBottom:`1px solid ${T.border}`}}>
              <td style={{padding:"8px 14px",fontSize:13,fontWeight:600}}>{MONTH_LABELS[r.month-1]}</td>
              <td style={{padding:"6px 14px",textAlign:"right"}}>
                <input type="number" value={r.soll} onChange={e=>updateCell(r.month,"soll",e.target.value)} style={{width:120,textAlign:"right",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:6,padding:"6px 10px",color:T.text,fontSize:13,fontFamily:"'JetBrains Mono',monospace"}}/>
              </td>
              <td style={{padding:"6px 14px",textAlign:"right"}}>
                <input type="number" value={r.ist} onChange={e=>updateCell(r.month,"ist",e.target.value)} style={{width:120,textAlign:"right",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:6,padding:"6px 10px",color:T.text,fontSize:13,fontFamily:"'JetBrains Mono',monospace"}}/>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>}
  </div>);
};

// ═══════════════════════════════════════
// MONATSPLANUNG-BAUSTEIN: Kostenaufstellung (3 Bereiche, gruppiert, editierbar, Upload)
// ═══════════════════════════════════════
const COST_SECTIONS = [
  {id:"fixkosten", title:"Fixkostenübersicht", paidLabel:"Betrag bezahlt", amtLabel:"Betrag"},
  {id:"umsatz", title:"Umsatzrechnung", paidLabel:"Betrag netto bezahlt", amtLabel:"Betrag netto"},
  {id:"offene", title:"Offene Rechnung / Schulden", paidLabel:"Betrag netto bezahlt", amtLabel:"Betrag netto"},
];
const cellInput = (w) => ({width:w,background:"transparent",border:`1px solid transparent`,borderRadius:6,padding:"6px 8px",color:T.text,fontSize:13,outline:"none"});
const CostSection = ({section, fileImport}) => {
  const [rows,setRows] = useState([]);
  const [loading,setLoading] = useState(true);
  const [addingGrp,setAddingGrp] = useState(null);     // Gruppe zu der eine Zeile kommt
  const [newGrpName,setNewGrpName] = useState("");
  const [showAddGrp,setShowAddGrp] = useState(false);
  const [confirmDel,setConfirmDel] = useState(null);    // {kind:"row"|"group", ...}
  const fileRef = useRef(null);
  const [importMsg,setImportMsg] = useState(null);
  const saveTimers = useRef({});

  const load = () => {
    setLoading(true);
    api.get("planning-costs", `section=${section.id}`)
      .then(d=>{ setRows(Array.isArray(d)?d:[]); setLoading(false); })
      .catch(()=>setLoading(false));
  };
  useEffect(()=>{ load(); }, [section.id]);

  // nach Gruppe ordnen, Reihenfolge erhalten
  const groups = (()=>{
    const map = new Map();
    rows.forEach(r=>{ if(!map.has(r.grp)) map.set(r.grp,[]); map.get(r.grp).push(r); });
    return Array.from(map.entries());
  })();

  const addGroup = () => {
    if(!newGrpName.trim()){ alert("Bitte einen Namen eingeben."); return; }
    setShowAddGrp(false);
    setAddingGrp(newGrpName.trim());
    addRow(newGrpName.trim());
    setNewGrpName("");
  };
  const addRow = async (grp) => {
    const grpOrder = groups.findIndex(([g])=>g===grp);
    await api.post("planning-costs", {section:section.id, grp, label:"", paymentMethod:grp, description:"", amountPaid:null, amount:null, grpOrder: grpOrder>=0?grpOrder:groups.length, sortOrder: (groups.find(([g])=>g===grp)?.[1].length)||0});
    load();
  };
  const updateRow = (id, field, value) => {
    setRows(prev=>prev.map(r=>r.id===id?{...r,[field]:value}:r));
    const key = id+field;
    if(saveTimers.current[key]) clearTimeout(saveTimers.current[key]);
    saveTimers.current[key] = setTimeout(()=>{ api.put("planning-costs", {id, [field]:value}); }, 600);
  };
  const doDelete = async () => {
    if(!confirmDel) return;
    if(confirmDel.kind==="row") await api.del("planning-costs", {id:confirmDel.id});
    else if(confirmDel.kind==="group") await api.del("planning-costs", {section:section.id, deleteGroup:confirmDel.grp});
    setConfirmDel(null); load();
  };

  const sum = (list, field) => list.reduce((s,r)=>s+(parseFloat(r[field])||0),0);
  const fmt = (n) => n.toLocaleString("de",{minimumFractionDigits:2,maximumFractionDigits:2});

  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value=""; if(!file) return;
    setImportMsg(null);
    try {
      const payload = await fileImport(file);
      if(!payload || payload.length===0){ setImportMsg({ok:false,text:"Keine gültigen Zeilen gefunden."}); return; }
      const res = await api.post("planning-costs", {section:section.id, rows:payload});
      setImportMsg({ok:true,text:`${res.imported||0} Zeilen importiert.`});
      load();
    } catch(err){ setImportMsg({ok:false,text:"Datei konnte nicht gelesen werden. Bitte CSV oder Excel."}); }
  };

  return (<div style={{marginBottom:28}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
      <h3 style={{fontSize:16,fontWeight:700}}>{section.title}</h3>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onFile} style={{display:"none"}}/>
        <Button icon="upload" size="sm" variant="secondary" onClick={()=>fileRef.current?.click()}>Tabelle hochladen</Button>
        <Button icon="add" size="sm" variant="secondary" onClick={()=>{setNewGrpName("");setShowAddGrp(true);}}>Gruppe</Button>
      </div>
    </div>
    {importMsg && <div style={{marginBottom:12,padding:"8px 12px",borderRadius:8,fontSize:13,background:importMsg.ok?T.success+"22":T.danger+"22",color:importMsg.ok?T.success:T.danger}}>{importMsg.text}</div>}

    {loading ? <div style={{color:T.textDim,fontSize:13,padding:10}}>Lädt...</div> :
     groups.length===0 ? <div style={{color:T.textDim,fontSize:13,padding:"18px",textAlign:"center",border:`1px dashed ${T.border}`,borderRadius:12}}>Noch keine Einträge. Leg eine Gruppe an oder lade eine Tabelle hoch.</div> :
    <div className="table-wrap">
      <table style={{width:"100%",borderCollapse:"collapse",minWidth:760}}>
        <thead><tr style={{borderBottom:`2px solid ${T.border}`}}>
          {["Posten","Zahlungsweise","Beschreibung",section.paidLabel,section.amtLabel,""].map((h,i)=>(<th key={i} style={{padding:"10px 12px",textAlign:i>=3&&i<=4?"right":"left",fontSize:11,fontWeight:600,color:T.textDim,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>))}
        </tr></thead>
        <tbody>
          {groups.map(([grp,list])=>(
            <React.Fragment key={grp}>
              {/* Gruppen-Kopf mit Summen */}
              <tr style={{background:T.bgInput}}>
                <td colSpan={3} style={{padding:"8px 12px",fontWeight:700,fontSize:13}}>
                  <span style={{display:"inline-flex",alignItems:"center",gap:8}}>
                    {grp}
                    <button onClick={()=>addRow(grp)} title="Zeile hinzufügen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:2}}><Icon name="add" size={14}/></button>
                    <button onClick={()=>setConfirmDel({kind:"group",grp})} title="Gruppe löschen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:2}}><Icon name="delete" size={13}/></button>
                  </span>
                </td>
                <td style={{padding:"8px 12px",textAlign:"right",fontWeight:700,fontSize:13,fontFamily:"'JetBrains Mono',monospace"}}>{fmt(sum(list,"amountPaid"))}</td>
                <td style={{padding:"8px 12px",textAlign:"right",fontWeight:700,fontSize:13,fontFamily:"'JetBrains Mono',monospace"}}>{fmt(sum(list,"amount"))}</td>
                <td></td>
              </tr>
              {/* Zeilen */}
              {list.map(r=>(
                <tr key={r.id} style={{borderBottom:`1px solid ${T.border}`}}>
                  <td style={{padding:"4px 8px"}}><input value={r.label||""} onChange={e=>updateRow(r.id,"label",e.target.value)} placeholder="Posten" style={cellInput(160)}/></td>
                  <td style={{padding:"4px 8px"}}><input value={r.paymentMethod||""} onChange={e=>updateRow(r.id,"paymentMethod",e.target.value)} placeholder={grp} style={cellInput(120)}/></td>
                  <td style={{padding:"4px 8px"}}><input value={r.description||""} onChange={e=>updateRow(r.id,"description",e.target.value)} placeholder="Beschreibung" style={cellInput(180)}/></td>
                  <td style={{padding:"4px 8px",textAlign:"right"}}><input type="number" value={r.amountPaid??""} onChange={e=>updateRow(r.id,"amountPaid",e.target.value)} placeholder="—" style={{...cellInput(90),textAlign:"right",fontFamily:"'JetBrains Mono',monospace"}}/></td>
                  <td style={{padding:"4px 8px",textAlign:"right"}}><input type="number" value={r.amount??""} onChange={e=>updateRow(r.id,"amount",e.target.value)} placeholder="—" style={{...cellInput(90),textAlign:"right",fontFamily:"'JetBrains Mono',monospace"}}/></td>
                  <td style={{padding:"4px 8px",textAlign:"center"}}><button onClick={()=>setConfirmDel({kind:"row",id:r.id})} title="Zeile löschen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:3}}><Icon name="delete" size={14}/></button></td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>}

    {/* Gruppe anlegen */}
    <Modal open={showAddGrp} onClose={()=>setShowAddGrp(false)} title="Neue Gruppe" width={420}>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <Input label="Name der Gruppe" value={newGrpName} onChange={e=>setNewGrpName(e.target.value)} placeholder="z.B. Monatlich, Jährlich, Einmalig"/>
        <Button icon="check" onClick={addGroup}>Anlegen</Button>
      </div>
    </Modal>

    {/* Löschen mit Bestätigung */}
    <Modal open={!!confirmDel} onClose={()=>setConfirmDel(null)} title="Wirklich löschen?" width={420}>
      {confirmDel && <div>
        <p style={{fontSize:14,color:T.textMuted,lineHeight:1.5,marginBottom:20}}>
          {confirmDel.kind==="row" ? "Diese Zeile wird gelöscht." : `Die ganze Gruppe "${confirmDel.grp}" wird mit allen Zeilen gelöscht.`} Das lässt sich nicht rückgängig machen.
        </p>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <Button variant="secondary" onClick={()=>setConfirmDel(null)}>Abbrechen</Button>
          <Button variant="danger" icon="delete" onClick={doDelete}>Ja, löschen</Button>
        </div>
      </div>}
    </Modal>
  </div>);
};

const CostListModule = () => {
  // gemeinsamer CSV/Excel-Parser für die Kostentabellen
  const parseCostFile = (file) => new Promise((resolve,reject)=>{
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, {type:"array"});
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(sheet, {defval:""});
        const pick = (row, names) => {
          const keys = Object.keys(row);
          for(const n of names){ const k=keys.find(k=>k.trim().toLowerCase()===n.toLowerCase()); if(k!==undefined) return String(row[k]??"").trim(); }
          return "";
        };
        const num = (s) => { if(s==="") return null; const n=parseFloat(String(s).replace(/\./g,"").replace(",",".").replace(/[^\d.-]/g,"")); return isNaN(n)?null:n; };
        const out = raw.map(r=>({
          grp: pick(r,["Gruppe","Zahlungsweise","grp","Kategorie"]) || "Monatlich",
          label: pick(r,["Posten","Fixkosten","Name","Bezeichnung","label"]),
          paymentMethod: pick(r,["Zahlungsweise","Methode"]),
          description: pick(r,["Beschreibung","Notiz","description"]),
          amountPaid: num(pick(r,["Betrag bezahlt","Betrag netto bezahlt","Bezahlt","amountPaid"])),
          amount: num(pick(r,["Betrag","Betrag netto","Summe","amount"])),
        })).filter(r=>r.label || r.description || r.amount!==null);
        resolve(out);
      } catch(err){ reject(err); }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });

  return (<div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:24,marginBottom:24}}>
    <h2 style={{fontSize:20,fontWeight:700,letterSpacing:"-0.01em",marginBottom:6}}>Kostenaufstellung</h2>
    <p style={{fontSize:13,color:T.textMuted,marginBottom:20}}>Fixkosten, Umsatzrechnung und offene Rechnungen. Alles direkt editierbar, oder per Tabelle hochladen.</p>
    {COST_SECTIONS.map(s=>(<CostSection key={s.id} section={s} fileImport={parseCostFile}/>))}
  </div>);
};

const ChecklistPage = ({reload,dv}) => {
  const [items,setItems]=useState(store.get(STORAGE_KEYS.checklist)||[]);
  const [form,setForm]=useState({type:"income",description:"",amount:"",dueDate:""});
  const [month,setMonth]=useState(monthKey(new Date()));
  const save=u=>{setItems(u);};
  const addItem=async()=>{if(!form.description||!form.amount)return;await api.post('checklist',{...form,amount:parseFloat(form.amount)});await reload(STORAGE_KEYS.checklist);setItems(store.get(STORAGE_KEYS.checklist));setForm({type:'income',description:'',amount:'',dueDate:''});};
  const toggle=async(id)=>{const i=items.find(x=>x.id===id);await api.put('checklist',{id,done:!i?.done});await reload(STORAGE_KEYS.checklist);setItems(store.get(STORAGE_KEYS.checklist));};
  const del=async(id)=>{await api.del('checklist',{id});await reload(STORAGE_KEYS.checklist);setItems(store.get(STORAGE_KEYS.checklist));};

  const f=items.filter(i=>(i.dueDate||"").startsWith(month));
  const pi=f.filter(i=>i.type==="income").reduce((s,i)=>s+i.amount,0);
  const pe=f.filter(i=>i.type==="expense").reduce((s,i)=>s+i.amount,0);
  const di=f.filter(i=>i.type==="income"&&i.done).reduce((s,i)=>s+i.amount,0);
  const de2=f.filter(i=>i.type==="expense"&&i.done).reduce((s,i)=>s+i.amount,0);

  const mo=[];for(let m=-2;m<=6;m++){const d=new Date();d.setMonth(d.getMonth()+m);mo.push({value:monthKey(d),label:d.toLocaleDateString("de",{month:"long",year:"numeric"})});}

  const renderSection=(type,label,color)=>{
    const list=f.filter(i=>i.type===type);
    return (<div style={{marginBottom:24}}>
      <h3 style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em",marginBottom:12,color}}>{label}</h3>
      {list.length===0?<div style={{color:T.textDim,fontSize:13,padding:16,background:T.bgCard,borderRadius:10,border:`1px solid ${T.border}`}}>Keine Einträge</div>:
        list.map(item=>(<div key={item.id} style={{display:"flex",alignItems:"center",gap:14,padding:"12px 18px",background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:10,marginBottom:6}}>
          <button onClick={()=>toggle(item.id)} style={{width:22,height:22,borderRadius:6,border:`2px solid ${item.done?T.success:T.border}`,background:item.done?T.success:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:"#fff"}}>{item.done&&<Icon name="check" size={14}/>}</button>
          <div style={{flex:1,textDecoration:item.done?"line-through":"none",opacity:item.done?0.6:1}}>
            <div style={{fontSize:13,fontWeight:600}}>{item.description}</div>
            {item.dueDate&&<div style={{fontSize:11,color:T.textDim}}>Fällig: {new Date(item.dueDate).toLocaleDateString("de")}</div>}
          </div>
          <div style={{fontSize:14,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color}}>{type==="income"?"+":"-"}{item.amount.toLocaleString("de")} €</div>
          <button onClick={()=>del(item.id)} style={{background:"none",border:"none",color:T.textDim,cursor:"pointer"}}><Icon name="delete" size={16}/></button>
        </div>))}
    </div>);
  };

  return (<div>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24,flexWrap:"wrap",gap:12}}>
      <h1 style={{fontSize:26,fontWeight:700,letterSpacing:"-0.02em"}}>Monatsplanung</h1>
      <Select options={mo} value={month} onChange={e=>setMonth(e.target.value)}/>
    </div>
    <div style={{display:"flex",gap:16,marginBottom:24,flexWrap:"wrap"}}>
      <StatCard label="Geplante Einnahmen" value={fmtEuro(pi)} sub={`Erledigt: ${fmtEuro(di)}`} icon="money" color={T.success} hideable/>
      <StatCard label="Geplante Ausgaben" value={fmtEuro(pe)} sub={`Erledigt: ${fmtEuro(de2)}`} icon="money" color={T.danger} hideable/>
      <StatCard label="Geplanter Überschuss" value={fmtEuro(pi-pe)} icon="chart" color={pi-pe>=0?T.success:T.danger} hideable/>
    </div>
    <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:20,marginBottom:24}}>
      <div style={{fontSize:14,fontWeight:700,marginBottom:14}}>Neuer Eintrag</div>
      <div style={{display:"flex",gap:12,flexWrap:"wrap",alignItems:"end"}}>
        <Select label="Typ" options={[{value:"income",label:"Einnahme"},{value:"expense",label:"Ausgabe"}]} value={form.type} onChange={e=>setForm({...form,type:e.target.value})}/>
        <Input label="Beschreibung" value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="z.B. Website Müller" style={{minWidth:200}}/>
        <Input label="Betrag (€)" type="number" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} placeholder="0.00" style={{width:120}}/>
        <Input label="Fällig am" type="date" value={form.dueDate} onChange={e=>setForm({...form,dueDate:e.target.value})}/>
        <Button icon="add" onClick={addItem}>Hinzufügen</Button>
      </div>
    </div>
    {renderSection("income","Erwartete Einnahmen",T.success)}
    {renderSection("expense","Geplante Ausgaben",T.danger)}

    {/* Umsatzkurve + Kostenaufstellung (aus der Numbers-Planung ins CRM geholt) */}
    <div style={{marginTop:32,paddingTop:8,borderTop:`1px solid ${T.border}`}}>
      <RevenueCurveModule/>
      <CostListModule/>
    </div>
  </div>);
};

// ─── MITARBEITER-VERTRÄGE ───
// Zeigt die Verträge eines Users. isAdmin=true → Upload + Löschen möglich.
// requester = der eingeloggte User (für serverseitige Zugriffsprüfung).
const ContractsManager = ({targetUserId, requester, compact}) => {
  const [contracts,setContracts] = useState([]);
  const [loading,setLoading] = useState(true);
  const [uploading,setUploading] = useState(false);
  const [title,setTitle] = useState("");
  const [pendingFile,setPendingFile] = useState(null);
  const fileRef = useRef(null);
  const isAdmin = requester?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const list = await api.get("user-contracts", `userId=${targetUserId}&requesterId=${requester.id}`);
      setContracts(Array.isArray(list) ? list : []);
    } catch(e){ setContracts([]); }
    setLoading(false);
  };
  useEffect(()=>{ if(targetUserId && requester?.id) load(); }, [targetUserId, requester?.id]);

  const doUpload = async () => {
    if(!pendingFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("userId", targetUserId);
      fd.append("requesterId", requester.id);
      fd.append("title", title || pendingFile.name);
      fd.append("file", pendingFile);
      const res = await fetch("/api/user-contract-upload", {method:"POST", headers:authHeaders(), body:fd});
      const data = await res.json();
      if(!res.ok){ alert(data.error || "Upload fehlgeschlagen"); }
      else { setTitle(""); setPendingFile(null); if(fileRef.current) fileRef.current.value=""; await load(); }
    } catch(e){ alert("Upload-Fehler: "+e.message); }
    setUploading(false);
  };

  const doDelete = async (id) => {
    if(!confirm("Diesen Vertrag wirklich löschen?")) return;
    try {
      const res = await api.del("user-contracts", {id, requesterId: requester.id});
      if(res.error){ alert(res.error); return; }
      await load();
    } catch(e){ alert("Fehler: "+e.message); }
  };

  const fmtSize = (b) => b>1024*1024 ? `${(b/1024/1024).toFixed(1)} MB` : `${Math.round(b/1024)} KB`;

  return (<div>
    {!loading && contracts.length===0 && <div style={{fontSize:13,color:T.textDim,padding:"8px 0"}}>Noch keine Verträge hinterlegt.</div>}
    {contracts.map(c=>(
      <div key={c.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:T.bgInput,borderRadius:8,marginBottom:6}}>
        <span style={{color:T.textDim,display:"flex"}}><Icon name="clipboard" size={17}/></span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:13,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.title}</div>
          <div style={{fontSize:11,color:T.textDim,marginTop:2}}>{c.fileName} · {fmtSize(c.fileSize)} · {new Date(c.createdAt).toLocaleDateString("de")}</div>
        </div>
        <a href={`/api/user-contract-file/${c.id}?requesterId=${requester.id}`} target="_blank" rel="noopener noreferrer" style={{color:T.info,textDecoration:"none",fontSize:12,fontWeight:600,whiteSpace:"nowrap"}}>Ansehen</a>
        {isAdmin && <button onClick={()=>doDelete(c.id)} title="Löschen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:2}}><Icon name="delete" size={15}/></button>}
      </div>
    ))}
    {/* Upload nur für Admins */}
    {isAdmin && <div style={{marginTop:10,padding:12,background:T.bgCard,border:`1px dashed ${T.border}`,borderRadius:10}}>
      <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Bezeichnung (z.B. Arbeitsvertrag)" style={{width:"100%",padding:"8px 10px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,fontSize:12,outline:"none",marginBottom:8,boxSizing:"border-box"}}/>
      <input ref={fileRef} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" style={{display:"none"}} onChange={e=>setPendingFile(e.target.files?.[0]||null)}/>
      <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
        <button onClick={()=>fileRef.current?.click()} style={{padding:"7px 12px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,fontSize:12,cursor:"pointer"}}>Datei wählen</button>
        {pendingFile && <span style={{fontSize:12,color:T.text,flex:1,minWidth:0,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",display:"inline-flex",alignItems:"center",gap:4}}><Icon name="upload" size={12}/> {pendingFile.name}</span>}
        {pendingFile && <button onClick={doUpload} disabled={uploading} style={{padding:"7px 14px",background:T.accent,border:"none",borderRadius:8,color:"#fff",fontSize:12,fontWeight:600,cursor:"pointer"}}>{uploading?"Lädt...":"Hochladen"}</button>}
      </div>
      <div style={{fontSize:10,color:T.textDim,marginTop:6}}>PDF, JPG, PNG oder WebP · max 20 MB</div>
    </div>}
  </div>);
};

// ─── TEAM ───
// ═══════════════════════════════════════
// LOHN & GEHALT — Lohnabrechnungen der Angestellten verwalten (nur Admin)
// ═══════════════════════════════════════
const MONTH_NAMES = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
const PayrollPage = ({user}) => {
  // Nur Admins dürfen Lohn & Gehalt sehen
  if(user && user.role !== "admin"){
    return (<div style={{maxWidth:520,margin:"60px auto",textAlign:"center"}}>
      <h2 style={{fontSize:20,fontWeight:700,marginBottom:8}}>Kein Zugriff</h2>
      <p style={{color:T.textMuted,fontSize:14,lineHeight:1.6}}>Dieser Bereich ist nur für die Geschäftsführung.</p>
    </div>);
  }
  const [staff,setStaff] = useState([]);
  const [slips,setSlips] = useState([]);
  const [loading,setLoading] = useState(true);
  const [showAdd,setShowAdd] = useState(false);
  const nowY = new Date().getFullYear();
  const [form,setForm] = useState({userId:"",year:nowY,month:new Date().getMonth()+1,gross:"",net:"",note:"",fileName:"",fileData:"",fileType:""});
  const [confirmDel,setConfirmDel] = useState(null);
  const [filterUser,setFilterUser] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const us = store.get(STORAGE_KEYS.users)||[];
      setStaff(us.filter(u=>u.role!=="client"));
      const s = await api.get("payslips","");
      setSlips(Array.isArray(s)?s:[]);
    } catch(e){}
    setLoading(false);
  };
  useEffect(()=>{ load(); }, []);

  const nameOf = (uid) => (staff.find(u=>u.id===uid)?.name) || "—";

  const onPickFile = (e) => {
    const file = e.target.files?.[0]; e.target.value=""; if(!file) return;
    if(file.size > 8*1024*1024){ alert("Die Datei ist zu groß (max 8 MB)."); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result).split(",")[1] || "";
      setForm(f=>({...f, fileName:file.name, fileData:b64, fileType:file.type||"application/octet-stream"}));
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if(!form.userId){ alert("Bitte eine Person wählen."); return; }
    await api.post("payslips", {
      userId:form.userId, year:parseInt(form.year), month:parseInt(form.month),
      gross:form.gross, net:form.net, note:form.note,
      fileName:form.fileName||null, fileData:form.fileData||null, fileType:form.fileType||null,
      createdBy:user.id,
    });
    setShowAdd(false);
    setForm({userId:"",year:nowY,month:new Date().getMonth()+1,gross:"",net:"",note:"",fileName:"",fileData:"",fileType:""});
    load();
  };

  const download = async (slip) => {
    try {
      const d = await api.get("payslips", `id=${slip.id}&withFile=1`);
      if(!d.fileData){ alert("Zu dieser Abrechnung ist keine Datei hinterlegt."); return; }
      const bin = atob(d.fileData);
      const bytes = new Uint8Array(bin.length);
      for(let i=0;i<bin.length;i++) bytes[i]=bin.charCodeAt(i);
      const blob = new Blob([bytes], {type:d.fileType||"application/octet-stream"});
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = d.fileName||"Lohnabrechnung"; document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch(e){ alert("Download fehlgeschlagen."); }
  };

  const doDelete = async () => {
    if(!confirmDel) return;
    await api.del("payslips", {id:confirmDel.id});
    setConfirmDel(null); load();
  };

  const fmtEuro = (n) => n!=null ? n.toLocaleString("de",{minimumFractionDigits:2,maximumFractionDigits:2})+" €" : "—";
  const shown = filterUser ? slips.filter(s=>s.userId===filterUser) : slips;
  // nach Person gruppieren
  const byUser = (()=>{ const m=new Map(); shown.forEach(s=>{ if(!m.has(s.userId)) m.set(s.userId,[]); m.get(s.userId).push(s); }); return Array.from(m.entries()); })();

  return (<div style={{maxWidth:1100,margin:"0 auto"}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24,flexWrap:"wrap",gap:12}}>
      <div>
        <h1 style={{fontSize:26,fontWeight:700,letterSpacing:"-0.02em"}}>Lohn & Gehalt</h1>
        <p style={{color:T.textDim,fontSize:15,marginTop:6}}>Lohnabrechnungen für euch als Angestellte hinterlegen und verwalten. Nur für Admins sichtbar.</p>
      </div>
      <Button icon="add" onClick={()=>{setForm({userId:"",year:nowY,month:new Date().getMonth()+1,gross:"",net:"",note:"",fileName:"",fileData:"",fileType:""});setShowAdd(true);}}>Abrechnung hinzufügen</Button>
    </div>

    {/* Filter */}
    <div style={{display:"flex",gap:8,marginBottom:20,flexWrap:"wrap"}}>
      <button onClick={()=>setFilterUser("")} style={{padding:"7px 14px",borderRadius:9,border:`1px solid ${!filterUser?T.accent:T.border}`,background:!filterUser?T.accentGlow:T.bgInput,color:!filterUser?T.accent:T.text,fontSize:13,fontWeight:600,cursor:"pointer"}}>Alle</button>
      {staff.map(u=>(<button key={u.id} onClick={()=>setFilterUser(u.id)} style={{padding:"7px 14px",borderRadius:9,border:`1px solid ${filterUser===u.id?T.accent:T.border}`,background:filterUser===u.id?T.accentGlow:T.bgInput,color:filterUser===u.id?T.accent:T.text,fontSize:13,fontWeight:600,cursor:"pointer"}}>{u.name}</button>))}
    </div>

    {loading ? <div style={{color:T.textDim,fontSize:14,padding:20}}>Lädt...</div> :
     byUser.length===0 ? <div style={{color:T.textDim,fontSize:14,padding:"40px",textAlign:"center",border:`1px dashed ${T.border}`,borderRadius:12}}>Noch keine Lohnabrechnungen hinterlegt.</div> :
     <div style={{display:"flex",flexDirection:"column",gap:24}}>
      {byUser.map(([uid,list])=>(
        <div key={uid} style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,overflow:"hidden"}}>
          <div style={{padding:"14px 20px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:10}}>
            <Avatar userId={uid} name={nameOf(uid)} size={32} editable={false}/>
            <h3 style={{fontSize:16,fontWeight:700}}>{nameOf(uid)}</h3>
            <span style={{fontSize:12,color:T.textDim,marginLeft:"auto"}}>{list.length} {list.length===1?"Abrechnung":"Abrechnungen"}</span>
          </div>
          <div className="table-wrap">
            <table style={{width:"100%",borderCollapse:"collapse",minWidth:640}}>
              <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>
                {["Zeitraum","Brutto","Netto","Notiz","Datei",""].map((h,i)=>(<th key={i} style={{padding:"10px 16px",textAlign:i>=1&&i<=2?"right":"left",fontSize:11,fontWeight:600,color:T.textDim,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>))}
              </tr></thead>
              <tbody>
                {list.map(s=>(
                  <tr key={s.id} style={{borderBottom:`1px solid ${T.border}`}}>
                    <td style={{padding:"12px 16px",fontSize:13,fontWeight:600}}>{MONTH_NAMES[s.month-1]} {s.year}</td>
                    <td style={{padding:"12px 16px",textAlign:"right",fontSize:13,fontFamily:"'JetBrains Mono',monospace"}}>{fmtEuro(s.gross)}</td>
                    <td style={{padding:"12px 16px",textAlign:"right",fontSize:13,fontWeight:600,fontFamily:"'JetBrains Mono',monospace",color:T.success}}>{fmtEuro(s.net)}</td>
                    <td style={{padding:"12px 16px",fontSize:12,color:T.textMuted}}>{s.note||"—"}</td>
                    <td style={{padding:"12px 16px"}}>
                      {s.hasFile
                        ? <button onClick={()=>download(s)} style={{display:"inline-flex",alignItems:"center",gap:6,background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"6px 11px",color:T.accent,fontSize:12,fontWeight:600,cursor:"pointer"}}><Icon name="upload" size={13}/> {s.fileName?.slice(0,18)||"Öffnen"}</button>
                        : <span style={{fontSize:12,color:T.textDim}}>keine Datei</span>}
                    </td>
                    <td style={{padding:"12px 16px",textAlign:"right"}}>
                      <button onClick={()=>setConfirmDel({id:s.id,label:`${MONTH_NAMES[s.month-1]} ${s.year}`,name:nameOf(uid)})} title="Löschen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4}}><Icon name="delete" size={15}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>}

    {/* Abrechnung hinzufügen */}
    <Modal open={showAdd} onClose={()=>setShowAdd(false)} title="Lohnabrechnung hinzufügen" width={520}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div>
          <div style={{fontSize:13,fontWeight:600,color:T.textMuted,marginBottom:6}}>Person</div>
          <select value={form.userId} onChange={e=>setForm({...form,userId:e.target.value})} style={{width:"100%",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:14,cursor:"pointer"}}>
            <option value="">Bitte wählen…</option>
            {staff.map(u=>(<option key={u.id} value={u.id}>{u.name}</option>))}
          </select>
        </div>
        <div className="grid-2col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <div>
            <div style={{fontSize:13,fontWeight:600,color:T.textMuted,marginBottom:6}}>Monat</div>
            <select value={form.month} onChange={e=>setForm({...form,month:e.target.value})} style={{width:"100%",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:14,cursor:"pointer"}}>
              {MONTH_NAMES.map((m,i)=>(<option key={i} value={i+1}>{m}</option>))}
            </select>
          </div>
          <div>
            <div style={{fontSize:13,fontWeight:600,color:T.textMuted,marginBottom:6}}>Jahr</div>
            <input type="number" value={form.year} onChange={e=>setForm({...form,year:e.target.value})} style={{width:"100%",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:14,fontFamily:"'JetBrains Mono',monospace"}}/>
          </div>
        </div>
        <div className="grid-2col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <Input label="Brutto (€)" value={form.gross} onChange={e=>setForm({...form,gross:e.target.value})} placeholder="z.B. 3000"/>
          <Input label="Netto (€)" value={form.net} onChange={e=>setForm({...form,net:e.target.value})} placeholder="z.B. 2100"/>
        </div>
        <Input label="Notiz (optional)" value={form.note} onChange={e=>setForm({...form,note:e.target.value})} placeholder="z.B. inkl. Bonus"/>
        <div>
          <div style={{fontSize:13,fontWeight:600,color:T.textMuted,marginBottom:6}}>Abrechnung als Datei (PDF)</div>
          <input ref={fileRef} type="file" accept=".pdf,image/*" onChange={onPickFile} style={{display:"none"}}/>
          <button onClick={()=>fileRef.current?.click()} style={{display:"inline-flex",alignItems:"center",gap:8,background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 14px",color:T.text,fontSize:13,cursor:"pointer"}}><Icon name="upload" size={15}/> {form.fileName||"Datei wählen"}</button>
          {form.fileName && <button onClick={()=>setForm({...form,fileName:"",fileData:"",fileType:""})} style={{marginLeft:8,background:"none",border:"none",color:T.danger,fontSize:12,cursor:"pointer"}}>entfernen</button>}
        </div>
        <Button icon="check" onClick={save}>Speichern</Button>
      </div>
    </Modal>

    {/* Löschen mit Bestätigung */}
    <Modal open={!!confirmDel} onClose={()=>setConfirmDel(null)} title="Wirklich löschen?" width={420}>
      {confirmDel && <div>
        <p style={{fontSize:14,color:T.textMuted,lineHeight:1.5,marginBottom:20}}>Die Abrechnung "{confirmDel.label}" von {confirmDel.name} wird endgültig gelöscht, samt hinterlegter Datei. Das lässt sich nicht rückgängig machen.</p>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <Button variant="secondary" onClick={()=>setConfirmDel(null)}>Abbrechen</Button>
          <Button variant="danger" icon="delete" onClick={doDelete}>Ja, löschen</Button>
        </div>
      </div>}
    </Modal>
  </div>);
};

const TeamPage = ({user,reload,dv}) => {
  // Nur Admins dürfen die Team-Übersicht (inkl. Anwesenheits-Tabelle) sehen
  if(user && user.role !== "admin"){
    return (<div style={{padding:40,textAlign:"center",color:T.textDim}}>
      <div style={{marginBottom:8}}><Icon name="team" size={32}/></div>
      <div style={{fontSize:15,fontWeight:600}}>Kein Zugriff</div>
      <div style={{fontSize:13,marginTop:4}}>Diese Seite ist nur für Admins.</div>
    </div>);
  }
  const [users,setUsers]=useState((store.get(STORAGE_KEYS.users)||[]).filter(u=>u.role!=="client"));
  useEffect(()=>{setUsers((store.get(STORAGE_KEYS.users)||[]).filter(u=>u.role!=="client"));},[dv]);
  const [showAdd,setShowAdd]=useState(false);
  const [confirmDelete,setConfirmDelete]=useState(null); // user object oder null
  const [form,setForm]=useState({name:"",username:"",password:"",role:"member",provision_per_appointment:"10",provision_percent:"0"});
  const [leadStats,setLeadStats]=useState({total:0,byStatus:{}});
  useEffect(()=>{api.get("lead-stats").then(s=>{if(s.total!==undefined)setLeadStats(s);});},[dv]);
  const leads={length:leadStats.total,filter:()=>[]};const calls=store.get(STORAGE_KEYS.calls)||[];

  // Live-Anwesenheit aller User laden + alle 20s aktualisieren + lokaler Sekundentakt
  const [attendance,setAttendance] = useState([]);
  const [, setAttTick] = useState(0);
  const loadAttendance = async () => {
    try { const a = await api.get("attendance"); setAttendance(Array.isArray(a)?a:[]); } catch(e){}
  };
  useEffect(()=>{ loadAttendance(); const t=setInterval(loadAttendance, 20000); return ()=>clearInterval(t); }, []);
  useEffect(()=>{ const t=setInterval(()=>setAttTick(x=>x+1), 1000); return ()=>clearInterval(t); }, []);
  const attFor = (uid) => attendance.find(a=>a.userId===uid);
  const [contractsOpenFor,setContractsOpenFor] = useState(null); // userId dessen Verträge aufgeklappt sind
  const [menuOpenFor,setMenuOpenFor] = useState(null); // userId dessen 3-Punkte-Menü offen ist

  // Lead-Sperre pro User (z.B. ausscheidende Mitarbeiter)
  const [leadBlocks,setLeadBlocks] = useState({}); // {userId: true/false}
  const [blockSaving,setBlockSaving] = useState(null); // userId der gerade gespeichert wird
  useEffect(()=>{
    // Sperr-Status aller Nicht-Admin-User laden
    const us = (store.get(STORAGE_KEYS.users)||[]).filter(u=>u.role!=="admin");
    Promise.all(us.map(u=>appSettings.getBool(`leads_blocked_${u.id}`, false).then(v=>[u.id,v])))
      .then(pairs=>{ const m={}; pairs.forEach(([id,v])=>m[id]=v); setLeadBlocks(m); });
  }, [dv]);
  const toggleLeadBlock = async (u) => {
    const next = !leadBlocks[u.id];
    if(next && !confirm(`Leads für ${u.name} wirklich sperren? Die Person sieht dann keine Leads mehr, kann aber weiter ihr Dashboard nutzen.`)) return;
    setBlockSaving(u.id);
    const ok = await appSettings.setBool(`leads_blocked_${u.id}`, next, user.id);
    setBlockSaving(null);
    if(ok) setLeadBlocks(prev=>({...prev,[u.id]:next}));
    else alert("Konnte die Sperre nicht speichern. Bitte erneut versuchen.");
  };

  // Detail-Tabelle: Anwesenheit für einen wählbaren Tag
  const [tableDate,setTableDate] = useState(todayLocal());
  const [tableRows,setTableRows] = useState([]);
  const loadTable = async (date) => {
    try { const a = await api.get("attendance", `date=${date}`); setTableRows(Array.isArray(a)?a:[]); } catch(e){ setTableRows([]); }
  };
  useEffect(()=>{ loadTable(tableDate); }, [tableDate]);
  // Wenn heute gewählt ist, alle 20s aktualisieren (live)
  useEffect(()=>{
    if(tableDate !== todayLocal()) return;
    const t = setInterval(()=>loadTable(tableDate), 20000);
    return ()=>clearInterval(t);
  }, [tableDate]);
  const isToday = tableDate === todayLocal();

  // Admin steuert die Anwesenheit eines Users (ein-/ausstempeln, Pause)
  const adminAttAction = async (targetUserId, action) => {
    try {
      await api.post("attendance", {userId: targetUserId, action});
      window.dispatchEvent(new CustomEvent("attendance-changed", {detail:{userId:targetUserId}}));
      await loadTable(tableDate);
      await loadAttendance();
    } catch(e){ alert("Aktion fehlgeschlagen: "+e.message); }
  };

  // ── Zeit nachtragen (falls Einstempeln vergessen) ──
  const [showManual,setShowManual] = useState(false);
  const [manual,setManual] = useState({userId:"", date:todayLocal(), start:"09:00", end:"17:00", breakMinutes:"0"});
  const saveManual = async () => {
    if(!manual.userId){ alert("Bitte eine Person wählen."); return; }
    if(!manual.start || !manual.end){ alert("Bitte Start und Ende eintragen."); return; }
    if(manual.end <= manual.start){ alert("Das Ende muss nach dem Start liegen."); return; }
    try {
      await api.post("attendance-manual", {userId:manual.userId, date:manual.date, start:manual.start, end:manual.end, breakMinutes:parseInt(manual.breakMinutes)||0});
      setShowManual(false);
      await loadTable(tableDate);
      await loadWeek(weekRef);
      await loadAttendance();
    } catch(e){ alert("Konnte nicht gespeichert werden: "+e.message); }
  };

  // ── Wochenübersicht: wer wie viel pro Woche ──
  const [weekRef,setWeekRef] = useState(todayLocal());
  const [week,setWeek] = useState(null);
  const loadWeek = async (ref) => {
    try { const d = await api.get("attendance-week", `week=${ref}`); setWeek(d); } catch(e){ setWeek(null); }
  };
  useEffect(()=>{ loadWeek(weekRef); }, [weekRef]);
  const shiftWeek = (dir) => {
    const d = new Date(weekRef); d.setDate(d.getDate()+dir*7);
    setWeekRef(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`);
  };
  const fmtHours = (sec) => { const h=Math.floor(sec/3600); const m=Math.floor((sec%3600)/60); return `${h}:${String(m).padStart(2,"0")} h`; };
  const weekDates = (()=>{ if(!week) return []; const out=[]; const d=new Date(week.monday+"T00:00:00"); for(let i=0;i<7;i++){ out.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`); d.setDate(d.getDate()+1); } return out; })();

  const addUser=async()=>{if(!form.name||!form.username||!form.password)return;if(users.find(u=>u.username===form.username))return;await api.post("users",form);await reload(STORAGE_KEYS.users);setUsers(store.get(STORAGE_KEYS.users));setForm({name:"",username:"",password:"",role:"member",provision_per_appointment:"10",provision_percent:"0"});setShowAdd(false);};
  const delUser=async(id)=>{if(id==="admin-1")return;await api.del("users",{id});await reload(STORAGE_KEYS.users);setUsers(store.get(STORAGE_KEYS.users));};

  // Anwesenheits-Status-Anzeige für eine User-Karte
  const AttBadge = ({uid}) => {
    const a = attFor(uid);
    const status = a?.status || "offline";
    const color = status==="working"?T.success:status==="break"?T.warning:T.textDim;
    const label = status==="working"?"Online":status==="break"?"Pause":(a?.clockOut?"Feierabend":"Offline");
    // Live-Zeit aus Zeitstempeln (tickt jede Sekunde mit, da der Sekunden-Timer den Re-Render auslöst)
    let extra = "";
    if(a){
      const { work, currentBreak } = liveAttendance(a);
      if(status==="working") extra = fmtDurationShort(work);
      else if(status==="break") extra = "Pause "+fmtDurationShort(currentBreak);
    }
    return (<div style={{display:"flex",alignItems:"center",gap:6,marginTop:6}}>
      <span style={{width:8,height:8,borderRadius:"50%",background:color,display:"inline-block",boxShadow:status==="working"?`0 0 6px ${T.success}`:"none"}}/>
      <span style={{fontSize:11,fontWeight:600,color}}>{label}</span>
      {extra && <span style={{fontSize:11,color:T.textDim,fontFamily:"'JetBrains Mono',monospace"}}>· {extra}</span>}
    </div>);
  };

  return (<div>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24,flexWrap:"wrap",gap:12}}><h1 style={{fontSize:26,fontWeight:700,letterSpacing:"-0.02em"}}>Team</h1><Button icon="add" onClick={()=>setShowAdd(true)}>Vertriebler</Button></div>
    <Modal open={showAdd} onClose={()=>setShowAdd(false)} title="Neuer Vertriebler" width={480}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <Input label="Name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Max Mustermann"/>
        <Input label="Benutzername" value={form.username} onChange={e=>setForm({...form,username:e.target.value})} placeholder="max"/>
        <Input label="Passwort" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} placeholder="Sicheres Passwort"/>
        <Select label="Rolle" options={[{value:"member",label:"Vertriebler"},{value:"cutter",label:"Cutter"},{value:"admin",label:"Admin"}]} value={form.role} onChange={e=>setForm({...form,role:e.target.value})}/>
        {form.role!=="admin"&&<>
          <Input label="Provision pro Termin (€)" type="number" value={form.provision_per_appointment} onChange={e=>setForm({...form,provision_per_appointment:e.target.value})} placeholder="z.B. 10"/>
          <Input label="Provision pro Sale (%)" type="number" value={form.provision_percent} onChange={e=>setForm({...form,provision_percent:e.target.value})} placeholder="z.B. 10"/>
        </>}
        <Button icon="check" onClick={addUser} style={{marginTop:8}}>Hinzufügen</Button>
      </div>
    </Modal>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(min(100%, 280px),1fr))",gap:16}}>
      {users.map(u=>{
        const ul=leads.filter(l=>l.assignedTo===u.id).length;
        const uc=calls.filter(c=>c.userId===u.id).length;
        const cutterProjects=(store.get(STORAGE_KEYS.cutter_projects)||[]).length;
        const cutterOpen=(store.get(STORAGE_KEYS.cutter_projects)||[]).filter(p=>p.status!=="done"&&p.status!=="delivered").length;
        const provAppt = u.provision_per_appointment||10;
        const provPct = u.provision_percent||0;
        return (
        <div key={u.id} style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:22,position:"relative"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}><Avatar userId={u.id} name={u.name} size={44} editable={false}/><div><div style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em"}}>{u.name}</div><div style={{fontSize:12,color:T.textDim}}>@{u.username}</div><AttBadge uid={u.id}/></div></div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <Badge color={u.role==="admin"?T.accent:u.role==="cutter"?T.warning:T.info}>{u.role==="admin"?"Admin":u.role==="cutter"?"Cutter":"Vertriebler"}</Badge>
              {/* 3-Punkte-Menü (nicht für den eigenen Admin-Account) */}
              {u.id!=="admin-1" && <div style={{position:"relative"}}>
                <button onClick={()=>setMenuOpenFor(menuOpenFor===u.id?null:u.id)} title="Optionen" style={{width:30,height:30,borderRadius:8,border:`1px solid ${T.border}`,background:menuOpenFor===u.id?T.bgHover:"transparent",color:T.textMuted,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0}}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
                </button>
                {menuOpenFor===u.id && <>
                  <div onClick={()=>setMenuOpenFor(null)} style={{position:"fixed",inset:0,zIndex:40}}/>
                  <div style={{position:"absolute",top:36,right:0,minWidth:210,background:T.bgCard,border:`1px solid ${T.borderStrong}`,borderRadius:12,boxShadow:T.shadowLg,zIndex:41,overflow:"hidden",padding:6}}>
                    {/* Verträge */}
                    <button onClick={()=>{setContractsOpenFor(contractsOpenFor===u.id?null:u.id);setMenuOpenFor(null);}} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"9px 12px",background:"none",border:"none",borderRadius:8,color:T.text,cursor:"pointer",fontSize:13,textAlign:"left"}}>
                      <Icon name="clipboard" size={15}/> Verträge {contractsOpenFor===u.id?"ausblenden":"anzeigen"}
                    </button>
                    {/* Lead-Sperre (nur Vertriebler) */}
                    {u.role==="member" && <button onClick={()=>{toggleLeadBlock(u);setMenuOpenFor(null);}} disabled={blockSaving===u.id} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"9px 12px",background:"none",border:"none",borderRadius:8,color:leadBlocks[u.id]?T.success:T.warning,cursor:blockSaving===u.id?"wait":"pointer",fontSize:13,textAlign:"left"}}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      {leadBlocks[u.id]?"Leads wieder freigeben":"Leads sperren"}
                    </button>}
                    <div style={{height:1,background:T.border,margin:"6px 0"}}/>
                    {/* Entfernen */}
                    <button onClick={()=>{setConfirmDelete(u);setMenuOpenFor(null);}} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"9px 12px",background:"none",border:"none",borderRadius:8,color:T.danger,cursor:"pointer",fontSize:13,textAlign:"left"}}>
                      <Icon name="delete" size={15}/> Aus dem Team entfernen
                    </button>
                  </div>
                </>}
              </div>}
            </div>
          </div>
          {/* Lead-Sperre-Hinweis */}
          {u.role==="member" && leadBlocks[u.id] && <div style={{display:"flex",alignItems:"center",gap:8,background:T.warningGlow,border:`1px solid ${T.warning}33`,borderRadius:9,padding:"7px 11px",marginBottom:14,fontSize:12,color:T.warning,fontWeight:600}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            Lead-Zugriff gesperrt
          </div>}
          {u.role==="cutter"
            ?<div style={{display:"flex",gap:20}}><div><div style={{fontSize:20,fontWeight:700,fontFamily:"'JetBrains Mono',monospace"}}>{cutterProjects}</div><div style={{fontSize:11,color:T.textDim}}>Projekte</div></div><div><div style={{fontSize:20,fontWeight:700,fontFamily:"'JetBrains Mono',monospace"}}>{cutterOpen}</div><div style={{fontSize:11,color:T.textDim}}>Offen</div></div></div>
            :<div style={{display:"flex",gap:16}}><div><div style={{fontSize:20,fontWeight:700,fontFamily:"'JetBrains Mono',monospace"}}>{ul}</div><div style={{fontSize:11,color:T.textDim}}>Leads</div></div><div><div style={{fontSize:20,fontWeight:700,fontFamily:"'JetBrains Mono',monospace"}}>{uc}</div><div style={{fontSize:11,color:T.textDim}}>Anrufe</div></div>
            {u.role!=="admin"&&<div><div style={{fontSize:14,fontWeight:600,fontFamily:"'JetBrains Mono',monospace",color:T.success}}>{provAppt}€/T</div><div style={{fontSize:11,color:T.textDim}}>Provision</div></div>}
            {u.role!=="admin"&&provPct>0&&<div><div style={{fontSize:14,fontWeight:600,fontFamily:"'JetBrains Mono',monospace",color:T.success}}>{provPct}%</div><div style={{fontSize:11,color:T.textDim}}>Sale</div></div>}
            </div>}
          {/* Verträge (aufklappbar über das Menü) */}
          {contractsOpenFor===u.id && <div style={{marginTop:14,borderTop:`1px solid ${T.border}`,paddingTop:12}}>
            <div style={{fontSize:13,fontWeight:600,color:T.textMuted,marginBottom:10,display:"flex",alignItems:"center",gap:8}}><Icon name="clipboard" size={14}/> Verträge</div>
            <ContractsManager targetUserId={u.id} requester={user}/>
          </div>}
        </div>);})}
    </div>

    {(()=>{
      // Zeit-Helper: "09:03" aus Server-Timestamp
      const fmtClock = (ts) => { if(!ts) return "—"; const d=parseServerTime(ts); return d?d.toLocaleTimeString("de",{hour:"2-digit",minute:"2-digit"}):"—"; };
      // Für heute: alle User zeigen (auch nicht eingestempelte), damit Admin sie einstempeln kann.
      // Für vergangene Tage: nur die, die an dem Tag eine Session hatten.
      const sessionByUser = {};
      tableRows.forEach(r=>{ sessionByUser[r.userId]=r; });
      const rowsToShow = isToday
        ? users.map(u=>{ const s=sessionByUser[u.id]; return s ? {...s, userName:u.name, userRole:u.role} : {userId:u.id, userName:u.name, userRole:u.role, status:"offline", clockIn:null, clockOut:null}; })
        : tableRows.filter(r=>r.clockIn);
      // Summen für die Fußzeile
      const totalWork = rowsToShow.reduce((s,r)=>s+(liveAttendance(r).work||0), 0);
      const totalBreak = rowsToShow.reduce((s,r)=>s+(liveAttendance(r).totalBreak||0), 0);
      const present = rowsToShow.filter(r=>r.clockIn).length;
      return (<div style={{marginTop:32}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:12}}>
          <h2 style={{fontSize:19,fontWeight:700,letterSpacing:"-0.01em",display:"flex",alignItems:"center",gap:8}}><Icon name="calendar" size={18}/> Anwesenheit {isToday?"heute":""}</h2>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <Button size="sm" variant="secondary" icon="add" onClick={()=>{setManual({userId:"",date:tableDate,start:"09:00",end:"17:00",breakMinutes:"0"});setShowManual(true);}}>Zeit nachtragen</Button>
            <button onClick={()=>{const d=new Date(tableDate);d.setDate(d.getDate()-1);setTableDate(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`);}} style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"7px 11px",color:T.text,cursor:"pointer",fontSize:14}}>‹</button>
            <input type="date" value={tableDate} max={todayLocal()} onChange={e=>setTableDate(e.target.value||todayLocal())} style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"7px 10px",color:T.text,fontSize:13,outline:"none",fontFamily:"'JetBrains Mono',monospace"}}/>
            <button disabled={isToday} onClick={()=>{const d=new Date(tableDate);d.setDate(d.getDate()+1);const n=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;if(n<=todayLocal())setTableDate(n);}} style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"7px 11px",color:isToday?T.textDim:T.text,cursor:isToday?"default":"pointer",fontSize:14,opacity:isToday?0.4:1}}>›</button>
            {!isToday&&<button onClick={()=>setTableDate(todayLocal())} style={{background:T.accentGlow,border:`1px solid ${T.accent}44`,borderRadius:8,padding:"7px 12px",color:T.accent,cursor:"pointer",fontSize:12,fontWeight:600}}>Heute</button>}
          </div>
        </div>
        {isToday&&<p style={{fontSize:12,color:T.textDim,marginBottom:12,marginTop:-6}}>Als Admin kannst du Mitarbeiter direkt ein- und ausstempeln.</p>}
        <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,overflow:"auto",WebkitOverflowScrolling:"touch"}}>
          {rowsToShow.length===0
            ? <div style={{padding:30,textAlign:"center",color:T.textDim,fontSize:14}}>Niemand war an diesem Tag eingestempelt.</div>
            : <table style={{width:"100%",borderCollapse:"collapse",minWidth:isToday?820:720}}>
            <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>{["Name","Status","Eingestempelt","Feierabend","Pause gesamt","Arbeitszeit"].concat(isToday?["Steuerung"]:[]).map(h=>(<th key={h} style={{padding:"12px 16px",textAlign:"left",fontSize:11,fontWeight:600,color:T.textDim,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>))}</tr></thead>
            <tbody>
              {rowsToShow.map(r=>{
                const { work, totalBreak:tb } = liveAttendance(r);
                const status = r.status||"offline";
                const color = status==="working"?T.success:status==="break"?T.warning:T.textDim;
                const label = status==="working"?"Arbeitet":status==="break"?"Pause":(r.clockOut?"Feierabend":"Offline");
                const isWorking=status==="working", isBreak=status==="break", isOffline=status==="offline";
                return (<tr key={r.userId} style={{borderBottom:`1px solid ${T.border}`}}>
                  <td style={{padding:"12px 16px",fontSize:13,fontWeight:600}}>{r.userName||"—"}</td>
                  <td style={{padding:"12px 16px"}}><div style={{display:"flex",alignItems:"center",gap:6}}><span style={{width:8,height:8,borderRadius:"50%",background:color,display:"inline-block"}}/><span style={{fontSize:12,fontWeight:600,color}}>{label}</span></div></td>
                  <td style={{padding:"12px 16px",fontSize:13,fontFamily:"'JetBrains Mono',monospace"}}>{fmtClock(r.clockIn)}</td>
                  <td style={{padding:"12px 16px",fontSize:13,fontFamily:"'JetBrains Mono',monospace",color:r.clockOut?T.text:T.textDim}}>{r.clockOut?fmtClock(r.clockOut):(isWorking||isBreak?"läuft…":"—")}</td>
                  <td style={{padding:"12px 16px",fontSize:13,fontFamily:"'JetBrains Mono',monospace",color:tb>0?T.warning:T.textDim}}>{tb>0?fmtDurationShort(tb):"—"}</td>
                  <td style={{padding:"12px 16px",fontSize:14,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:T.success}}>{fmtDurationShort(work)}</td>
                  {isToday&&<td style={{padding:"10px 16px"}}>
                    <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                      {isOffline&&<button onClick={()=>adminAttAction(r.userId,"clock_in")} style={{padding:"5px 10px",background:T.success,border:"none",borderRadius:7,color:"#fff",fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:4}}><IconPlay/> {r.clockOut?"Weiter":"Einstempeln"}</button>}
                      {isWorking&&<button onClick={()=>adminAttAction(r.userId,"break_start")} style={{padding:"5px 10px",background:T.warningGlow,border:`1px solid ${T.warning}`,borderRadius:7,color:T.warning,fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:4}}><IconPause/> Pause</button>}
                      {isBreak&&<button onClick={()=>adminAttAction(r.userId,"break_end")} style={{padding:"5px 10px",background:T.success,border:"none",borderRadius:7,color:"#fff",fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:4}}><IconPlay/> Weiter</button>}
                      {(isWorking||isBreak)&&<button onClick={()=>{if(confirm(`${r.userName} jetzt ausstempeln (Feierabend)?`))adminAttAction(r.userId,"clock_out");}} style={{padding:"5px 10px",background:`${T.danger}15`,border:`1px solid ${T.danger}40`,borderRadius:7,color:T.danger,fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:4}}><IconStop/> Feierabend</button>}
                    </div>
                  </td>}
                </tr>);
              })}
            </tbody>
            <tfoot><tr style={{borderTop:`2px solid ${T.border}`,background:T.bgInput}}>
              <td style={{padding:"12px 16px",fontSize:12,fontWeight:700,color:T.textMuted}}>{present} {present===1?"Person":"Personen"} aktiv</td>
              <td/><td/><td/>
              <td style={{padding:"12px 16px",fontSize:13,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:T.warning}}>{fmtDurationShort(totalBreak)}</td>
              <td style={{padding:"12px 16px",fontSize:14,fontWeight:800,fontFamily:"'JetBrains Mono',monospace",color:T.success}}>{fmtDurationShort(totalWork)}</td>
              {isToday&&<td/>}
            </tr></tfoot>
          </table>}
        </div>
      </div>);
    })()}

    {/* ── Wochenübersicht: wer wie viel gearbeitet hat ── */}
    {_attendanceEnabled && week && (()=>{
      const fmtDM = (iso) => { const d=new Date(iso+"T00:00:00"); return `${String(d.getDate()).padStart(2,"0")}.${String(d.getMonth()+1).padStart(2,"0")}.`; };
      return (<div style={{marginTop:32}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:12}}>
          <h2 style={{fontSize:19,fontWeight:700,letterSpacing:"-0.01em",display:"flex",alignItems:"center",gap:8}}><Icon name="chart" size={18}/> Wochenübersicht</h2>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <button onClick={()=>shiftWeek(-1)} style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"7px 11px",color:T.text,cursor:"pointer",fontSize:14}}>‹</button>
            <span style={{fontSize:13,fontWeight:600,color:T.textMuted,fontFamily:"'JetBrains Mono',monospace"}}>{fmtDM(week.monday)} – {fmtDM(week.sunday)}</span>
            <button onClick={()=>shiftWeek(1)} style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"7px 11px",color:T.text,cursor:"pointer",fontSize:14}}>›</button>
            <button onClick={()=>setWeekRef(todayLocal())} style={{background:T.accentGlow,border:`1px solid ${T.accent}44`,borderRadius:8,padding:"7px 12px",color:T.accent,cursor:"pointer",fontSize:12,fontWeight:600}}>Diese Woche</button>
          </div>
        </div>
        <div className="table-wrap" style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow}}>
          {week.users.length===0
            ? <div style={{padding:30,textAlign:"center",color:T.textDim,fontSize:14}}>In dieser Woche wurde noch keine Zeit erfasst.</div>
            : <table style={{width:"100%",borderCollapse:"collapse",minWidth:720}}>
              <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>
                <th style={{padding:"12px 16px",textAlign:"left",fontSize:11,fontWeight:600,color:T.textDim,textTransform:"uppercase"}}>Name</th>
                {["Mo","Di","Mi","Do","Fr","Sa","So"].map((d,i)=>(<th key={d} style={{padding:"12px 10px",textAlign:"center",fontSize:11,fontWeight:600,color:T.textDim,textTransform:"uppercase"}}>{d}<div style={{fontSize:9,color:T.textDim,fontWeight:400}}>{weekDates[i]?fmtDM(weekDates[i]):""}</div></th>))}
                <th style={{padding:"12px 16px",textAlign:"right",fontSize:11,fontWeight:600,color:T.textDim,textTransform:"uppercase"}}>Summe</th>
              </tr></thead>
              <tbody>
                {week.users.map(u=>(
                  <tr key={u.userId} style={{borderBottom:`1px solid ${T.border}`}}>
                    <td style={{padding:"12px 16px",fontSize:13,fontWeight:600}}>{u.userName}</td>
                    {weekDates.map(dt=>{
                      const day = u.days[dt];
                      const sec = day?day.workSeconds:0;
                      return (<td key={dt} style={{padding:"12px 10px",textAlign:"center",fontSize:12,fontFamily:"'JetBrains Mono',monospace",color:sec>0?T.text:T.textDim}}>{sec>0?fmtHours(sec):"—"}</td>);
                    })}
                    <td style={{padding:"12px 16px",textAlign:"right",fontSize:14,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:T.success}}>{fmtHours(u.totalSeconds)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr style={{borderTop:`2px solid ${T.border}`,background:T.bgInput}}>
                <td style={{padding:"12px 16px",fontSize:12,fontWeight:700,color:T.textMuted}}>Gesamt</td>
                {weekDates.map(dt=>{
                  const daySum = week.users.reduce((s,u)=>s+(u.days[dt]?u.days[dt].workSeconds:0),0);
                  return (<td key={dt} style={{padding:"12px 10px",textAlign:"center",fontSize:12,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:daySum>0?T.textMuted:T.textDim}}>{daySum>0?fmtHours(daySum):"—"}</td>);
                })}
                <td style={{padding:"12px 16px",textAlign:"right",fontSize:14,fontWeight:800,fontFamily:"'JetBrains Mono',monospace",color:T.success}}>{fmtHours(week.users.reduce((s,u)=>s+u.totalSeconds,0))}</td>
              </tr></tfoot>
            </table>}
        </div>
      </div>);
    })()}

    {/* Zeit nachtragen Modal */}
    <Modal open={showManual} onClose={()=>setShowManual(false)} title="Arbeitszeit nachtragen" width={480}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <p style={{fontSize:13,color:T.textMuted,lineHeight:1.5}}>Falls das Einstempeln vergessen wurde, kannst du hier die gearbeitete Zeit von Hand eintragen. Sie landet dann in der Tages- und Wochenübersicht.</p>
        <div>
          <div style={{fontSize:13,fontWeight:600,color:T.textMuted,marginBottom:6}}>Person</div>
          <select value={manual.userId} onChange={e=>setManual({...manual,userId:e.target.value})} style={{width:"100%",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:14,cursor:"pointer"}}>
            <option value="">Bitte wählen…</option>
            {users.filter(u=>u.role!=="client").map(u=>(<option key={u.id} value={u.id}>{u.name}</option>))}
          </select>
        </div>
        <div>
          <div style={{fontSize:13,fontWeight:600,color:T.textMuted,marginBottom:6}}>Datum</div>
          <input type="date" value={manual.date} max={todayLocal()} onChange={e=>setManual({...manual,date:e.target.value})} style={{width:"100%",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:14,fontFamily:"'JetBrains Mono',monospace"}}/>
        </div>
        <div className="grid-3col" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
          <div>
            <div style={{fontSize:13,fontWeight:600,color:T.textMuted,marginBottom:6}}>Von</div>
            <input type="time" value={manual.start} onChange={e=>setManual({...manual,start:e.target.value})} style={{width:"100%",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:14,fontFamily:"'JetBrains Mono',monospace"}}/>
          </div>
          <div>
            <div style={{fontSize:13,fontWeight:600,color:T.textMuted,marginBottom:6}}>Bis</div>
            <input type="time" value={manual.end} onChange={e=>setManual({...manual,end:e.target.value})} style={{width:"100%",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:14,fontFamily:"'JetBrains Mono',monospace"}}/>
          </div>
          <div>
            <div style={{fontSize:13,fontWeight:600,color:T.textMuted,marginBottom:6}}>Pause (Min)</div>
            <input type="number" value={manual.breakMinutes} onChange={e=>setManual({...manual,breakMinutes:e.target.value})} style={{width:"100%",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:14,fontFamily:"'JetBrains Mono',monospace"}}/>
          </div>
        </div>
        <div style={{fontSize:12,color:T.textDim}}>Hinweis: Gibt es an dem Tag schon eine Erfassung für die Person, wird sie durch diese Angabe ersetzt.</div>
        <Button icon="check" onClick={saveManual}>Speichern</Button>
      </div>
    </Modal>

    {/* Bestätigungs-Modal Vertriebler löschen */}
    <Modal open={!!confirmDelete} onClose={()=>setConfirmDelete(null)} title="Vertriebler wirklich entfernen?" width={460}>
      {confirmDelete && (<div>
        <div style={{display:"flex",alignItems:"center",gap:12,padding:14,background:T.bgInput,borderRadius:10,marginBottom:16}}>
          <Avatar userId={confirmDelete.id} name={confirmDelete.name} size={44} editable={false}/>
          <div>
            <div style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em"}}>{confirmDelete.name}</div>
            <div style={{fontSize:12,color:T.textDim}}>@{confirmDelete.username} · {confirmDelete.role==="admin"?"Admin":confirmDelete.role==="cutter"?"Cutter":"Vertriebler"}</div>
          </div>
        </div>
        <div style={{fontSize:14,color:T.textMuted,lineHeight:1.6,marginBottom:20}}>
          Bist du sicher, dass du <strong style={{color:T.text}}>{confirmDelete.name}</strong> entfernen möchtest? Diese Aktion kann nicht rückgängig gemacht werden. Zugewiesene Leads, geloggte Anrufe und Termine bleiben erhalten, sind aber nicht mehr personalisiert zuordenbar.
        </div>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <Button variant="secondary" onClick={()=>setConfirmDelete(null)}>Abbrechen</Button>
          <Button variant="danger" icon="delete" onClick={async()=>{await delUser(confirmDelete.id);setConfirmDelete(null);}}>Ja, entfernen</Button>
        </div>
      </div>)}
    </Modal>
  </div>);
};

// ─── CUTTER DASHBOARD (for cutter role) ───
const CutterDashboard = ({user,reload,dv}) => {
  const allProjects = store.get(STORAGE_KEYS.cutter_projects)||[];
  // Cutter sieht nur die ihm zugewiesenen Projekte (oder selbst angelegte ohne Zuweisung)
  const projects = allProjects.filter(p => p.assignedTo===user.id || (!p.assignedTo && p.createdBy===user.id));
  const invoices = store.get(STORAGE_KEYS.cutter_invoices)||[];
  const myInvoices = invoices.filter(i => i.createdBy === user.id);
  const goalsKey = `crm_cutter_personal_goals_${user.id}`;
  const notesKey = `crm_cutter_personal_notes_${user.id}`;
  const [goals,setGoals] = useState([]);
  const [notes,setNotes] = useState([]);
  useEffect(()=>{api.get("personal-goals","userId="+user.id).then(g=>setGoals(g||[]));api.get("personal-notes","userId="+user.id).then(n=>setNotes(n||[]));},[]);
  const [goalForm,setGoalForm] = useState({text:"",target:""});
  const [noteText,setNoteText] = useState("");

  const saveGoals = g=>{setGoals(g);};
  const saveNotes = n=>{setNotes(n);};

  const openProjects = projects.filter(p=>p.status!=="done"&&p.status!=="delivered").length;
  const inProgress = projects.filter(p=>p.status==="in_progress").length;
  const doneThisMonth = projects.filter(p=>p.status==="done"&&(p.completedAt||"").startsWith(monthKey(new Date()))).length;
  const openInvoiceTotal = myInvoices.filter(i=>i.status==="sent"||i.status==="overdue").reduce((s,i)=>s+i.amount,0);
  const overdueCount = myInvoices.filter(i=>(i.status==="sent"||i.status==="overdue")&&i.dueDate&&new Date(i.dueDate)<new Date()).length;
  const paidThisMonth = myInvoices.filter(i=>i.status==="paid"&&(i.paidAt||i.created_at||"").startsWith(monthKey(new Date()))).reduce((s,i)=>s+i.amount,0);

  // Chart: Zahlungen aus den echten Teilzahlungen (cutter_invoice_payments) — pro Monat aggregiert.
  // Das ist die einzige saubere Quelle für "wann wurde wieviel gezahlt".
  const [myPayments,setMyPayments] = useState([]);
  useEffect(()=>{
    api.get("cutter-invoice-payments", `cutterId=${user.id}`).then(p=>setMyPayments(Array.isArray(p)?p:[])).catch(()=>{});
  }, [user.id, dv, invoices.length]);
  const monthlyEarnings = (()=>{
    const now = new Date();
    const months = [];
    for(let i=5;i>=0;i--){
      const d = new Date(now.getFullYear(), now.getMonth()-i, 1);
      const key = monthKey(d);
      // Alle Teilzahlungen in diesem Monat aufsummieren
      let total = myPayments.filter(p => (p.paidAt||"").startsWith(key)).reduce((s,p)=>s+(p.amount||0), 0);
      // Fallback: Wenn keine Teilzahlungs-Datensätze existieren (alte Rechnungen die direkt auf "paid" gesetzt wurden ohne Teilzahlungen),
      // nutze paidAt der Rechnung. paidAt ist DATETIME "2026-05-19 13:23:00" — der Vergleich startsWith("2026-05") funktioniert trotzdem.
      if (myPayments.length === 0) {
        total = myInvoices.filter(inv => inv.status==="paid" && (inv.paidAt||"").startsWith(key)).reduce((s,inv)=>s+(inv.amount||0), 0);
      }
      months.push({key, label: d.toLocaleDateString("de",{month:"short"}), total});
    }
    return months;
  })();
  const maxMonth = Math.max(...monthlyEarnings.map(m=>m.total), 1);

  const addGoal=async()=>{if(!goalForm.text)return;await api.post('personal-goals',{userId:user.id,text:goalForm.text,target:parseInt(goalForm.target)||0});const g=await api.get('personal-goals','userId='+user.id);setGoals(g||[]);setGoalForm({text:'',target:''});};
  const toggleGoal=async(id)=>{const g=goals.find(x=>x.id===id);await api.put('personal-goals',{id,userId:user.id,current:g?.current||0,done:!g?.done});const u=await api.get('personal-goals','userId='+user.id);setGoals(u||[]);};
  const updateGoalProgress=async(id,val)=>{await api.put('personal-goals',{id,userId:user.id,current:parseInt(val)||0,done:false});const u=await api.get('personal-goals','userId='+user.id);setGoals(u||[]);};
  const deleteGoal=async(id)=>{await api.del('personal-goals',{id,userId:user.id});const u=await api.get('personal-goals','userId='+user.id);setGoals(u||[]);};
  const addNote=async()=>{if(!noteText.trim())return;await api.post('personal-notes',{userId:user.id,text:noteText});const n=await api.get('personal-notes','userId='+user.id);setNotes(n||[]);setNoteText('');};
  const deleteNote=async(id)=>{await api.del('personal-notes',{id,userId:user.id});const n=await api.get('personal-notes','userId='+user.id);setNotes(n||[]);};

  return (<div>
    {(()=>{ const hh=new Date().getHours(); const g=hh<11?"Morgen":hh<18?"Tag":"Abend";
      const dlong=new Date().toLocaleDateString("de",{weekday:"long",day:"numeric",month:"long"});
      return (<>
    <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:22}}><Avatar userId={user.id} name={user.name} size={48} editable={true}/><div><h1 style={{fontSize:24,fontWeight:700,letterSpacing:"-0.02em"}}>Guten {g}, {(user.name||"").split(" ")[0]}</h1><p style={{color:T.textDim,fontSize:13,marginTop:3}}>{dlong} · Dein Cutter-Bereich</p></div></div>

    {/* HERO: Einnahmen-Verlauf + Heldenzahlen */}
    <div className="dash-hero" style={{display:"grid",gridTemplateColumns:"1fr 300px",gap:16,marginBottom:16}}>
      <div style={{background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:12,padding:"18px 22px 10px",boxShadow:T.glassHi}}>
        <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:"0.1em",color:T.textDim,fontWeight:600,marginBottom:8}}>Einnahmen · 6 Monate</div>
        <LineChart color={T.success} data={monthlyEarnings.map(m=>({label:m.label, value:m.total}))} fmt={(v)=>fmtEuro(v)}/>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        <StatCard label="Bezahlt (Monat)" value={fmtEuro(paidThisMonth)} hideable featured color={T.success}/>
        <StatCard label="Offene Rechnungen" value={fmtEuro(openInvoiceTotal)} hideable color={openInvoiceTotal>0?T.danger:T.success}/>
      </div>
    </div>

    {/* Kompakte Kennzahlen */}
    <div className="stats-row" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:22}}>
      <StatCard label="Offene Projekte" value={openProjects} icon="cut"/>
      <StatCard label="In Arbeit" value={inProgress} icon="cut"/>
      <StatCard label="Fertig (Monat)" value={doneThisMonth} icon="check"/>
      <StatCard label="Überfällig" value={overdueCount} icon="money"/>
    </div>
      </>);
    })()}

    <div style={{marginBottom:24}}>
      <TasksWidget user={user}/>
    </div>

    {/* Aufmerksamkeit + Deadlines nebeneinander */}
    <div className="grid-2col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:24,alignItems:"start"}}>
      <div style={{background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:12,boxShadow:T.glassHi,overflow:"hidden"}}>
        <div style={{padding:"16px 20px",borderBottom:`1px solid ${T.border}`}}><h3 style={{fontSize:15,fontWeight:600,letterSpacing:"-0.01em"}}>Braucht Aufmerksamkeit</h3></div>
        {(()=>{
          const now=new Date(); const in48=new Date(now.getTime()+2*86400000); let att=[];
          projects.filter(p=>p.deadline&&p.status!=="done"&&p.status!=="delivered"&&new Date(p.deadline)<now).forEach(p=>att.push({key:"d"+p.id,name:p.title,why:`Deadline überfällig (${new Date(p.deadline).toLocaleDateString("de")})`,tag:"dringend",w:9,hot:true}));
          myInvoices.filter(i=>(i.status==="sent"||i.status==="overdue")&&i.dueDate&&new Date(i.dueDate)<now).forEach(i=>att.push({key:"i"+i.id,name:i.client||"Offene Rechnung",why:"Rechnung überfällig",tag:"zahlung",w:7,hot:true}));
          projects.filter(p=>p.deadline&&p.status!=="done"&&p.status!=="delivered"&&new Date(p.deadline)>=now&&new Date(p.deadline)<=in48).forEach(p=>att.push({key:"s"+p.id,name:p.title,why:"Deadline in Kürze",tag:"bald",w:6,hot:false}));
          att.sort((a,b)=>b.w-a.w); att=att.slice(0,5);
          if(att.length===0) return <div style={{padding:"22px 20px",color:T.textDim,fontSize:13}}>Alles im grünen Bereich.</div>;
          return att.map((a,i)=>(
            <div key={a.key} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 20px",borderBottom:i<att.length-1?`1px solid ${T.border}`:"none"}}>
              <span style={{width:22,height:22,borderRadius:6,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",background:a.hot?T.accentGlow:T.bgHover,color:a.hot?T.accent:T.textMuted}}>{i+1}</span>
              <div style={{flex:1,minWidth:0}}><div style={{fontSize:13.5,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{a.name}</div><div style={{fontSize:12,color:T.textDim,marginTop:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{a.why}</div></div>
              <span style={{fontSize:11,fontWeight:600,color:T.textMuted,whiteSpace:"nowrap"}}>{a.tag}</span>
            </div>));
        })()}
      </div>

      {/* Upcoming deadlines */}
      <div style={{background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:12,boxShadow:T.glassHi,padding:24}}>
        <h3 style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em",marginBottom:14,display:"flex",alignItems:"center",gap:8}}><Icon name="calendar" size={16}/> <span>Nächste Deadlines</span></h3>
        {projects.filter(p=>p.deadline&&p.status!=="done"&&p.status!=="delivered").sort((a,b)=>new Date(a.deadline)-new Date(b.deadline)).length===0
          ?<div style={{color:T.textDim,fontSize:13}}>Keine offenen Deadlines</div>
          :projects.filter(p=>p.deadline&&p.status!=="done"&&p.status!=="delivered").sort((a,b)=>new Date(a.deadline)-new Date(b.deadline)).slice(0,6).map(p=>{
            const overdue=new Date(p.deadline)<new Date();
            return (<div key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${T.border}`}}>
              <div style={{minWidth:0,flex:1}}><div style={{fontSize:13,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.title}</div><div style={{fontSize:11,color:T.textDim}}>{p.client} — <Badge color={PROJECT_STATUSES.find(s=>s.id===p.status)?.color}>{PROJECT_STATUSES.find(s=>s.id===p.status)?.label}</Badge></div></div>
              <div style={{fontSize:13,fontWeight:600,fontFamily:"'JetBrains Mono',monospace",color:overdue?T.danger:T.accent,flexShrink:0,marginLeft:10}}>{new Date(p.deadline).toLocaleDateString("de")}</div>
            </div>);
          })}
      </div>
    </div>

    <div className="grid-2col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:24}}>
      {/* Goals */}
      <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:24}}>
        <h3 style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em",marginBottom:14,display:"flex",alignItems:"center",gap:8}}><Icon name="target" size={16}/> <span>Meine Ziele</span></h3>
        <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap",alignItems:"end"}}>
          <Input placeholder="z.B. 10 Reels schneiden" value={goalForm.text} onChange={e=>setGoalForm({...goalForm,text:e.target.value})} style={{flex:1,minWidth:140}}/>
          <Input placeholder="Ziel" type="number" value={goalForm.target} onChange={e=>setGoalForm({...goalForm,target:e.target.value})} style={{width:70}}/>
          <Button icon="add" size="sm" onClick={addGoal}>Ziel</Button>
        </div>
        {goals.length===0?<div style={{color:T.textDim,fontSize:13,textAlign:"center",padding:16}}>Setze dir Ziele!</div>:
          goals.map(g=>{
            const pct=g.target>0?Math.min(100,Math.round((g.current/g.target)*100)):g.done?100:0;
            return (<div key={g.id} style={{padding:"10px 0",borderBottom:`1px solid ${T.border}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <button onClick={()=>toggleGoal(g.id)} style={{width:20,height:20,borderRadius:6,border:`2px solid ${g.done?T.success:T.border}`,background:g.done?T.success:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:"#fff"}}>{g.done&&<Icon name="check" size={12}/>}</button>
                  <span style={{fontSize:13,fontWeight:600,textDecoration:g.done?"line-through":"none",opacity:g.done?0.5:1}}>{g.text}</span>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  {g.target>0&&<><input type="number" value={g.current} onChange={e=>updateGoalProgress(g.id,e.target.value)} style={{width:40,background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:4,padding:"2px 4px",color:T.text,fontSize:11,textAlign:"center",outline:"none"}}/><span style={{fontSize:11,color:T.textDim}}>/{g.target}</span></>}
                  <button onClick={()=>deleteGoal(g.id)} style={{background:"none",border:"none",color:T.textDim,cursor:"pointer"}}><Icon name="delete" size={13}/></button>
                </div>
              </div>
              {g.target>0&&<div style={{height:5,background:T.bgInput,borderRadius:3,overflow:"hidden"}}><div style={{height:"100%",width:`${pct}%`,background:pct>=100?T.success:pct>=50?T.warning:T.accent,borderRadius:3,transition:"width 0.3s"}}/></div>}
            </div>);
          })}
      </div>

      {/* Notes */}
      <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:24}}>
        <h3 style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em",marginBottom:14,display:"flex",alignItems:"center",gap:8}}><Icon name="clipboard" size={16}/> <span>Meine Notizen</span></h3>
        <div style={{display:"flex",gap:8,marginBottom:12}}>
          <textarea value={noteText} onChange={e=>setNoteText(e.target.value)} placeholder="Nur du siehst das..." onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();addNote();}}} style={{flex:1,background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 14px",color:T.text,fontSize:13,outline:"none",resize:"vertical",minHeight:50,fontFamily:"inherit"}}/>
        </div>
        <Button icon="add" size="sm" onClick={addNote} style={{marginBottom:12}}>Speichern</Button>
        {notes.length===0?<div style={{color:T.textDim,fontSize:13,textAlign:"center",padding:10}}>Noch keine Notizen</div>:
          <div style={{maxHeight:250,overflow:"auto"}}>{notes.map(n=>(<div key={n.id} style={{padding:"8px 12px",background:T.bgInput,borderRadius:8,marginBottom:6,borderLeft:`3px solid ${T.warning}`}}>
            <div style={{fontSize:13,whiteSpace:"pre-wrap"}}>{n.text}</div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:4}}>
              <div style={{fontSize:11,color:T.textDim}}>{new Date(n.date).toLocaleString("de")}</div>
              <button onClick={()=>deleteNote(n.id)} style={{background:"none",border:"none",color:T.textDim,cursor:"pointer"}}><Icon name="delete" size={13}/></button>
            </div>
          </div>))}</div>}
      </div>
    </div>

    {/* Meine Verträge */}
    <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:24,marginTop:24}}>
      <h3 style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em",marginBottom:14,display:"flex",alignItems:"center",gap:8}}><Icon name="clipboard" size={16}/> <span>Meine Verträge</span></h3>
      <ContractsManager targetUserId={user.id} requester={user}/>
    </div>
  </div>);
};

// Der Cutter schlägt einen Preis vor. Entschieden wird bei den Admins.
const PreisVorschlagModal = ({video, onClose, onGesendet}) => {
  const [betrag,setBetrag]=useState("");
  const [grund,setGrund]=useState("");
  const [sendet,setSendet]=useState(false);
  const [fehler,setFehler]=useState(null);
  useEffect(()=>{ if(video){ setBetrag(""); setGrund(""); setFehler(null); } },[video]);
  const senden=async()=>{
    const b=parseFloat(String(betrag).replace(",","."));
    if(!b||b<=0){ setFehler("Bitte einen Betrag eintragen."); return; }
    setSendet(true);
    const r=await api.post('cutter-price-requests',{projectId:video.id,proposedPrice:b,reason:grund.trim()});
    setSendet(false);
    if(r&&r.error){ setFehler(r.error); return; }
    onGesendet&&onGesendet();
  };
  return (<Modal open={!!video} onClose={onClose} title="Preis vorschlagen" width={440}>
    {video&&<div>
      <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>{video.title}</div>
      <div style={{fontSize:12.5,color:T.textDim,marginBottom:16}}>{video.client}</div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:T.bgInput,borderRadius:10,padding:"12px 15px",marginBottom:16}}>
        <span style={{fontSize:13,color:T.textMuted}}>Festgelegter Preis</span>
        <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:16,fontWeight:700}}>{video.price!=null?rfEuroGenau(video.price):"noch keiner"}</span>
      </div>
      <Input label="Mein Vorschlag (€)" value={betrag} onChange={e=>setBetrag(e.target.value)} placeholder="z.B. 45"/>
      <div style={{marginTop:12}}>
        <div style={{fontSize:12,fontWeight:600,color:T.textMuted,marginBottom:5}}>Kurz warum</div>
        <textarea value={grund} onChange={e=>setGrund(e.target.value)} rows={3} placeholder="z.B. Rohmaterial sind 40 Minuten aus drei Wohnungen, dazu Drohne."
          style={{boxSizing:"border-box",width:"100%",padding:"10px 12px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:9,color:T.text,fontSize:13,fontFamily:"inherit",resize:"vertical",outline:"none"}}/>
      </div>
      {fehler&&<div style={{fontSize:12.5,color:T.danger,marginTop:10}}>{fehler}</div>}
      <Button onClick={senden} disabled={sendet} style={{marginTop:16,width:"100%"}}>{sendet?"Wird gesendet...":"Vorschlag senden"}</Button>
      <div style={{fontSize:12,color:T.textDim,marginTop:10,lineHeight:1.5}}>Der Preis ändert sich erst, wenn Felix oder Ramon zustimmt.</div>
    </div>}
  </Modal>);
};

// ─── CUTTER PROJECTS VIEW (for cutter role) — interaktiver Contentplan ───
const CutterProjectsView = ({user,reload,dv}) => {
  const [projects,setProjects] = useState(store.get(STORAGE_KEYS.cutter_projects)||[]);
  useEffect(()=>{setProjects(store.get(STORAGE_KEYS.cutter_projects)||[]);},[dv]);
  const [showAdd,setShowAdd] = useState(false);
  const [pForm,setPForm] = useState({client:"",title:"",type:"",deadline:"",postDate:"",rawLink:"",notes:"",status:"open"});
  const [clientFilter,setClientFilter] = useState("all");
  const [statusFilter,setStatusFilter] = useState("all"); // all | open | done
  const [search,setSearch] = useState("");
  const [collapsed,setCollapsed] = useState({}); // pro Kunde eingeklappt
  const [restZeigen,setRestZeigen] = useState({}); // pro Kunde: spaetere bzw. fertige einblenden
  const [editLinkId,setEditLinkId] = useState(null);
  const [linkDraft,setLinkDraft] = useState("");
  const [editProject,setEditProject] = useState(null); // Video das gerade bearbeitet wird
  const [meinGuthaben,setMeinGuthaben] = useState(null);
  useEffect(()=>{ api.get('cutter-balance').then(r=>{ if(r&&r.cutter&&r.cutter.length) setMeinGuthaben(r.cutter[0]); }); },[dv]);
  const [notizen,setNotizen] = useState([]);
  const [notizAuf,setNotizAuf] = useState({});
  const ladeNotizen = () => { api.get('cutter-project-notes').then(r=>{ if(Array.isArray(r)) setNotizen(r); }); };
  const [meineVorschlaege,setMeineVorschlaege] = useState([]);
  const [preisFenster,setPreisFenster] = useState(null);  // Video fuer das gerade vorgeschlagen wird
  const [mehrAuf,setMehrAuf] = useState(null);            // offenes Dreipunkt-Menue
  useEffect(()=>{
    if(!mehrAuf) return;
    const zu=()=>setMehrAuf(null);
    document.addEventListener("mousedown",zu);
    return ()=>document.removeEventListener("mousedown",zu);
  },[mehrAuf]);
  const ladeMeineVorschlaege = () => { api.get('cutter-price-requests').then(r=>{ if(r&&r.vorschlaege) setMeineVorschlaege(r.vorschlaege); }); };
  useEffect(()=>{ ladeNotizen(); ladeMeineVorschlaege(); },[]);

  const isDone = (p) => p.status==="done"||p.status==="delivered";
  const normLink = (l) => { l=(l||"").trim(); if(!l) return ""; if(!/^https?:\/\//i.test(l)) return "https://"+l; return l; };

  const addProject=async()=>{
    if(!pForm.client||!pForm.title)return;
    await api.post('cutter-projects',{...pForm,rawLink:normLink(pForm.rawLink),createdBy:user.id,assignedTo:user.id});
    await reload(STORAGE_KEYS.cutter_projects);setProjects(store.get(STORAGE_KEYS.cutter_projects));
    setPForm({client:'',title:'',type:'',deadline:'',postDate:'',rawLink:'',notes:'',status:'open'});setShowAdd(false);
  };
  const patchProject=async(id,changes)=>{
    const p=projects.find(x=>x.id===id); if(!p) return;
    const merged={...p,...changes};
    // optimistisch updaten
    setProjects(prev=>prev.map(x=>x.id===id?merged:x));
    await api.put('cutter-projects',{id,client:merged.client,title:merged.title,type:merged.type,deadline:merged.deadline,notes:merged.notes,status:merged.status,rawLink:merged.rawLink,postDate:merged.postDate,assignedTo:merged.assignedTo,price:merged.price});
    await reload(STORAGE_KEYS.cutter_projects);setProjects(store.get(STORAGE_KEYS.cutter_projects));
  };
  const saveEdit=async()=>{
    if(!editProject||!editProject.client||!editProject.title)return;
    await patchProject(editProject.id,{client:editProject.client,title:editProject.title,type:editProject.type,deadline:editProject.deadline||null,postDate:editProject.postDate||null,rawLink:normLink(editProject.rawLink),notes:editProject.notes,status:editProject.status});
    setEditProject(null);
  };
  const toggleDone=(p)=>patchProject(p.id,{status:isDone(p)?"open":"done"});
  const delProject=async(id)=>{if(!confirm("Dieses Video wirklich löschen?"))return;await api.del('cutter-projects',{id});await reload(STORAGE_KEYS.cutter_projects);setProjects(store.get(STORAGE_KEYS.cutter_projects));};
  const saveLink=(p)=>{patchProject(p.id,{rawLink:normLink(linkDraft)});setEditLinkId(null);setLinkDraft("");};

  // Kundenliste für Filter
  const clients = Array.from(new Set(projects.map(p=>p.client).filter(Boolean))).sort();

  // Filtern
  const q = search.trim().toLowerCase();
  const filtered = projects.filter(p=>{
    // Cutter sieht nur die ihm zugewiesenen Videos (oder die er selbst angelegt hat und die niemandem zugewiesen sind)
    const mine = p.assignedTo===user.id || (!p.assignedTo && p.createdBy===user.id);
    if(!mine) return false;
    if(clientFilter!=="all" && p.client!==clientFilter) return false;
    if(statusFilter==="open" && isDone(p)) return false;
    if(statusFilter==="done" && !isDone(p)) return false;
    if(q && !(`${p.title} ${p.client} ${p.notes||""}`.toLowerCase().includes(q))) return false;
    return true;
  });

  // Nach Kunde gruppieren
  const groups = {};
  filtered.forEach(p=>{ const k=p.client||"(Ohne Kunde)"; if(!groups[k])groups[k]=[]; groups[k].push(p); });
  const groupNames = Object.keys(groups).sort();
  // Innerhalb Gruppe: offen zuerst, dann nach Postdatum/Deadline
  groupNames.forEach(k=>groups[k].sort((a,b)=>{
    if(isDone(a)!==isDone(b)) return isDone(a)?1:-1;
    const da=a.postDate||a.deadline||"9999", db=b.postDate||b.deadline||"9999";
    return da<db?-1:da>db?1:0;
  }));

  const totalCount = filtered.length;
  const doneCount = filtered.filter(isDone).length;

  const fmtD = (d) => { if(!d||d==="0000-00-00") return null; const dt=new Date(d); return isNaN(dt)?null:dt.toLocaleDateString("de",{day:"2-digit",month:"2-digit"}); };

  const TYPE_LABELS = {reel:"Reel",video:"Video",story:"Story",image:"Bild",animation:"Animation",other:"Sonstiges"};

  return (<div>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,flexWrap:"wrap",gap:12}}>
      <h1 style={{fontSize:26,fontWeight:700,letterSpacing:"-0.02em"}}>Content & Projekte</h1>
      <Button icon="add" size="sm" onClick={()=>setShowAdd(true)}>Video hinzufügen</Button>
    </div>
    <p style={{color:T.textMuted,fontSize:14,marginBottom:22}}>Deine zugewiesenen Videos.</p>

    {/* Preis vorschlagen. Aendern darf der Cutter nichts, nur vorschlagen. */}
    <PreisVorschlagModal video={preisFenster} onClose={()=>setPreisFenster(null)} onGesendet={()=>{setPreisFenster(null);ladeMeineVorschlaege();}}/>

    {/* ─── TAGESBLOCK: das Erste was der Cutter morgens sieht ─── */}
    {(()=>{
      const heute0=new Date(); heute0.setHours(0,0,0,0);
      const tageBis=(d)=>{ if(!d||d==="0000-00-00") return null; const dt=new Date(d); if(isNaN(dt)) return null; dt.setHours(0,0,0,0); return Math.round((dt-heute0)/86400000); };
      const meine=projects.filter(p=>(p.assignedTo===user.id||(!p.assignedTo&&p.createdBy===user.id))&&!isDone(p));
      const sortD=(a,b)=>{const da=a.deadline||a.postDate||"9999",db=b.deadline||b.postDate||"9999";return da<db?-1:da>db?1:0;};
      const ueber=meine.filter(p=>{const t=tageBis(p.deadline);return t!==null&&t<0;}).sort(sortD);
      const heuteF=meine.filter(p=>tageBis(p.deadline)===0).sort(sortD);
      const bald=meine.filter(p=>{const t=tageBis(p.deadline);return t!==null&&t>0&&t<=7;}).sort(sortD);
      const jetzt=[...ueber,...heuteF].slice(0,4);
      const anzahlHeute=ueber.length+heuteF.length;
      return (<div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:16,padding:"20px 22px",marginBottom:16,color:T.text,boxShadow:T.shadow}}>
        <div className="cut-tag-kopf" style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:18,flexWrap:"wrap",marginBottom:16}}>
          <div>
            <div style={{fontSize:12,fontWeight:500,color:T.textDim,marginBottom:3}}>{new Date().toLocaleDateString("de",{weekday:"long",day:"2-digit",month:"long"})}</div>
            <div style={{fontSize:19,fontWeight:600}}>{anzahlHeute===0?"Heute ist nichts fällig":anzahlHeute===1?"Heute ist 1 Video dran":`Heute sind ${anzahlHeute} Videos dran`}</div>
          </div>
          {meinGuthaben&&<div style={{textAlign:"right"}}>
            <div style={{fontSize:12,color:T.textDim,marginBottom:3}}>{meinGuthaben.rest<0?"bekommst du noch":"noch abzuarbeiten"}</div>
            <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:21,fontWeight:600,color:meinGuthaben.rest<0?T.success:T.text}}>{rfEuroGenau(Math.abs(meinGuthaben.rest))}</div>
          </div>}
        </div>
        <div className="cut-tag-kacheln" style={{display:"flex",gap:9,flexWrap:"wrap"}}>
          {[[ueber.length,"überfällig",T.danger],[heuteF.length,"heute fällig",T.text],[bald.length,"diese Woche",T.text],[meine.length,"offen insgesamt",T.text]].map(([wert,text,farbe],i)=>(
            <div key={i} style={{flex:"1 1 110px",minWidth:0,background:T.bgCard2,border:`1px solid ${T.border}`,borderRadius:11,padding:"12px 14px"}}>
              <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:18,fontWeight:600,color:farbe}}>{wert}</div>
              <div style={{fontSize:11.5,color:T.textDim,marginTop:2}}>{text}</div>
            </div>
          ))}
        </div>
        {jetzt.length>0&&<div style={{marginTop:15,paddingTop:14,borderTop:`1px solid ${T.border}`,display:"flex",flexDirection:"column",gap:8}}>
          {jetzt.map(p=>{ const t=tageBis(p.deadline);
            return (<div key={p.id} className="cut-tag-zeile" style={{display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:13,fontWeight:500,flex:"1 1 auto",minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.title}</span>
              <span className="cut-nur-breit" style={{fontSize:11.5,color:T.textDim,flexShrink:0}}>{p.client}</span>
              <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:11.5,fontWeight:500,color:t<0?T.danger:T.textMuted,flexShrink:0,whiteSpace:"nowrap"}}>{rfFristText(p.deadline,false).haupt}</span>
              {p.price!=null&&<span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:11.5,fontWeight:500,color:T.textDim,flexShrink:0}}>{rfEuroGenau(p.price)}</span>}
            </div>);
          })}
        </div>}
      </div>);
    })()}

    {/* Fortschritt gesamt */}
    {totalCount>0&&<div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:"16px 20px",marginBottom:18}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <span style={{fontSize:13,fontWeight:600,color:T.textMuted}}>Fortschritt {clientFilter!=="all"?`· ${clientFilter}`:"gesamt"}</span>
        <span style={{fontSize:13,fontWeight:700,fontFamily:"'JetBrains Mono',monospace"}}>{doneCount} / {totalCount} fertig</span>
      </div>
      <div style={{height:8,background:T.bgHover,borderRadius:4,overflow:"hidden"}}>
        <div style={{height:"100%",width:`${totalCount>0?(doneCount/totalCount*100):0}%`,background:T.success,borderRadius:4,transition:"width 0.3s"}}/>
      </div>
    </div>}

    {/* Filterleiste */}
    <div style={{display:"flex",gap:10,marginBottom:18,flexWrap:"wrap",alignItems:"center"}}>
      <div style={{position:"relative",flex:1,minWidth:200}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Video oder Kunde suchen..." style={{width:"100%",padding:"9px 14px 9px 36px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:10,color:T.text,fontSize:13,outline:"none"}}/>
        <span style={{position:"absolute",left:11,top:"50%",transform:"translateY(-50%)",color:T.textDim}}><Icon name="search" size={15}/></span>
      </div>
      <select value={clientFilter} onChange={e=>setClientFilter(e.target.value)} style={{padding:"9px 12px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:10,color:T.text,fontSize:13,cursor:"pointer",outline:"none"}}>
        <option value="all">Alle Kunden</option>
        {clients.map(c=><option key={c} value={c}>{c}</option>)}
      </select>
      <div style={{display:"flex",gap:3,background:T.bgInput,padding:4,borderRadius:10,border:`1px solid ${T.border}`}}>
        {[{v:"all",l:"Alle"},{v:"open",l:"Offen"},{v:"done",l:"Fertig"}].map(f=>(
          <button key={f.v} onClick={()=>setStatusFilter(f.v)} style={{padding:"6px 14px",borderRadius:7,border:"none",fontSize:13,fontWeight:600,background:statusFilter===f.v?T.accent:"transparent",color:statusFilter===f.v?"#fff":T.textDim,cursor:"pointer",transition:"all 0.12s"}}>{f.l}</button>
        ))}
      </div>
    </div>

    {/* Gruppen pro Kunde */}
    {groupNames.length===0
      ? <Empty icon="cut" title="Keine Videos" desc={projects.length===0?"Füge dein erstes Video hinzu":"Keine Treffer für diesen Filter"}/>
      : groupNames.map(gName=>{
        const items = groups[gName];
        const gDone = items.filter(isDone).length;
        const isCol = collapsed[gName];
        // Oben nur was diese Woche ansteht, der Rest liegt hinter den Knoepfen unten
        const jetztListe = items.filter(p=>{const t=rfTageBis(p.deadline); return !isDone(p)&&t!==null&&t<=7;});
        const spaeterListe = items.filter(p=>{const t=rfTageBis(p.deadline); return !isDone(p)&&(t===null||t>7);});
        const fertigeListe = items.filter(isDone);
        const restAuf = !!restZeigen[gName];
        const fertigAuf = statusFilter==="done" ? restZeigen[gName+"__fertig"]!==false : !!restZeigen[gName+"__fertig"];
        return (<div key={gName} style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,marginBottom:16,overflow:"hidden"}}>
          {/* Kunden-Header */}
          <div onClick={()=>setCollapsed(c=>({...c,[gName]:!c[gName]}))} style={{display:"flex",alignItems:"center",gap:12,padding:"15px 20px",cursor:"pointer",borderBottom:isCol?"none":`1px solid ${T.border}`,background:T.bgCard2}}>
            <span style={{color:T.textDim,fontSize:12,transform:isCol?"rotate(-90deg)":"none",transition:"transform 0.15s"}}>▼</span>
            <span style={{fontSize:15,fontWeight:600,flex:1}}>{gName}</span>
            <span style={{fontSize:12,color:T.textDim,fontFamily:"'JetBrains Mono',monospace"}}>{gDone}/{items.length}</span>
            <span style={{width:96,height:4,background:T.bgHover,borderRadius:2,overflow:"hidden"}}><span style={{display:"block",height:"100%",width:`${items.length>0?gDone/items.length*100:0}%`,background:T.accent,borderRadius:2}}/></span>
          </div>
          {/* Video-Zeilen */}
          {!isCol && (()=>{
            const zeigeListe = [...jetztListe, ...(restAuf?spaeterListe:[]), ...(fertigAuf?fertigeListe:[])];
            return (<>
            {zeigeListe.length===0 && <div style={{padding:"15px 20px",fontSize:13,color:T.textDim}}>Diese Woche nichts fällig.</div>}
            {zeigeListe.map(p=>{
            const done = isDone(p);
            const overdue = p.deadline && !done && new Date(p.deadline)<new Date();
            const meineNotizen=notizen.filter(n=>n.projectId===p.id);
            const offeneNotizen=meineNotizen.filter(n=>!n.done).length;
            const notizOffen=!!notizAuf[p.id];
            return (<div key={p.id}>
            <div className="proj-row" style={{position:"relative",display:"flex",alignItems:"center",gap:14,padding:"12px 20px",borderBottom:notizOffen?"none":`1px solid ${T.border}`,background:"transparent"}}>
              {overdue&&<span style={{position:"absolute",left:0,top:0,bottom:0,width:2,background:T.danger}}/>}
              {/* Checkbox */}
              <button onClick={()=>toggleDone(p)} title={done?"Als offen markieren":"Als fertig markieren"} style={{width:18,height:18,flexShrink:0,borderRadius:6,border:`1.5px solid ${done?T.accent:T.borderStrong}`,background:done?T.accent:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0,transition:"all 0.15s"}}>
                {done&&<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
              </button>
              {/* Titel + Meta */}
              <div className="proj-main" style={{flex:1,minWidth:0}}>
                <div className="proj-title" style={{fontSize:14,fontWeight:500,color:done?T.textDim:T.text,textDecoration:done?"line-through":"none",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.title}</div>
                <div style={{display:"flex",alignItems:"center",gap:9,marginTop:3,flexWrap:"wrap",fontSize:11.5,color:T.textDim}}>
                  {p.type&&<span>{TYPE_LABELS[p.type]||p.type}</span>}
                  <span>{p.price!=null?rfEuroGenau(p.price):"kein Preis"}</span>
                  {fmtD(p.postDate)&&<span>Post {fmtD(p.postDate)}</span>}
                  {(()=>{
                    const vs=meineVorschlaege.find(v=>v.projectId===p.id&&(v.status==="offen"||v.status==="gegenvorschlag"));
                    if(!vs) return null;
                    return <span style={{color:T.accent,fontWeight:600}}>{vs.status==="offen"?"Vorschlag läuft":"Antwort da"}</span>;
                  })()}
                  {p.notes&&<span style={{fontStyle:"italic"}}>{p.notes}</span>}
                </div>
              </div>
              {/* Frist in Worten, gleiche Sprache wie in der Adminliste */}
              {(()=>{ const fr=rfFristText(p.deadline,done); return (
                <span className="cut-frist" style={{width:112,flexShrink:0,textAlign:"right"}}>
                  <span style={{display:"block",fontSize:13,fontWeight:500,color:fr.spaet?T.danger:done?T.textDim:T.text}}>{fr.haupt}</span>
                  {fr.sub&&<span className="cut-nur-breit" style={{display:"block",fontSize:11.5,color:T.textDim,marginTop:2}}>{fr.sub}</span>}
                </span>); })()}
              {/* Steuerelemente: auf schmalen Screens in zweite, umbrechende Reihe (CSS proj-controls) */}
              <div className="proj-controls" style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",flexShrink:0}}>
              {/* Rohvideo-Link */}
              <div style={{flexShrink:0}}>
                {editLinkId===p.id ? (
                  <div style={{display:"flex",gap:4,alignItems:"center"}}>
                    <input autoFocus value={linkDraft} onChange={e=>setLinkDraft(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")saveLink(p);if(e.key==="Escape"){setEditLinkId(null);setLinkDraft("");}}} placeholder="Drive-Link..." style={{width:160,padding:"6px 9px",background:T.bgInput,border:`1px solid ${T.accent}`,borderRadius:7,color:T.text,fontSize:12,outline:"none"}}/>
                    <button onClick={()=>saveLink(p)} style={{padding:"6px 9px",background:T.accent,border:"none",borderRadius:7,color:"#fff",fontSize:12,fontWeight:600,cursor:"pointer"}}>OK</button>
                  </div>
                ) : p.rawLink ? (
                  <div style={{display:"flex",gap:4,alignItems:"center"}}>
                    <a href={p.rawLink} target="_blank" rel="noopener noreferrer" style={{display:"inline-flex",alignItems:"center",gap:5,padding:"6px 11px",background:T.accentGlow,border:`1px solid ${T.accent}33`,borderRadius:7,color:T.accent,fontSize:12,fontWeight:600,textDecoration:"none"}}><IconPlay/> Rohvideo</a>
                    <button onClick={()=>{setEditLinkId(p.id);setLinkDraft(p.rawLink);}} title="Link ändern" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:3}}><Icon name="edit" size={13}/></button>
                  </div>
                ) : (
                  <button onClick={()=>{setEditLinkId(p.id);setLinkDraft("");}} style={{padding:"6px 11px",background:"transparent",border:`1px dashed ${T.border}`,borderRadius:7,color:T.textDim,fontSize:12,cursor:"pointer"}}>+ Link</button>
                )}
              </div>
              {/* Status-Dropdown */}
              <select value={p.status} onChange={e=>patchProject(p.id,{status:e.target.value})} style={{flexShrink:0,background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:7,padding:"6px 8px",color:PROJECT_STATUSES.find(s=>s.id===p.status)?.color||T.text,fontSize:12,fontWeight:600,outline:"none",cursor:"pointer"}}>
                {PROJECT_STATUSES.map(s=>(<option key={s.id} value={s.id}>{s.label}</option>))}
              </select>
              {/* Bearbeiten */}
              <button onClick={()=>setEditProject({...p,deadline:p.deadline&&p.deadline!=="0000-00-00"?p.deadline.slice(0,10):"",postDate:p.postDate&&p.postDate!=="0000-00-00"?p.postDate.slice(0,10):"",rawLink:p.rawLink||"",notes:p.notes||""})} style={{flexShrink:0,background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:3}} title="Bearbeiten"><Icon name="edit" size={15}/></button>
              {/* Notizen zum Abhaken */}
              <button onClick={()=>setNotizAuf(a=>({...a,[p.id]:!a[p.id]}))} title="Notizen zu diesem Video"
                style={{display:"inline-flex",alignItems:"center",gap:5,padding:"6px 10px",borderRadius:7,border:`1px solid ${offeneNotizen>0?T.accent+"55":T.border}`,background:offeneNotizen>0?T.accentGlow:"transparent",color:offeneNotizen>0?T.accent:T.textDim,fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 11 12 14 20 6"/><path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9"/></svg>
                {meineNotizen.length>0?`${meineNotizen.length-offeneNotizen}/${meineNotizen.length}`:"Notiz"}
              </button>
              {/* Mehr: seltene Aktionen liegen hier drin, nicht offen in der Zeile */}
              <div onMouseDown={e=>e.stopPropagation()} style={{position:"relative",flexShrink:0}}>
                <button onClick={()=>setMehrAuf(m=>m===p.id?null:p.id)} aria-label="Weitere Aktionen" title="Mehr"
                  style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:"3px 5px",lineHeight:0,borderRadius:6}}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="19" cy="12" r="1.8"/></svg>
                </button>
                {mehrAuf===p.id&&<div style={{position:"absolute",right:0,top:"calc(100% + 5px)",zIndex:40,minWidth:196,background:T.bgCard,border:`1px solid ${T.borderStrong}`,borderRadius:10,boxShadow:T.shadowLg,padding:5,display:"flex",flexDirection:"column"}}>
                  {(()=>{
                    const vs=meineVorschlaege.find(v=>v.projectId===p.id&&(v.status==="offen"||v.status==="gegenvorschlag"));
                    if(vs) return (<div style={{padding:"9px 11px"}}>
                      <div style={{fontSize:12.5,color:T.textDim,lineHeight:1.45}}>{vs.status==="offen"?"Dein Preisvorschlag läuft noch.":"Es liegt eine Antwort für dich bereit."}</div>
                      <button onClick={()=>{setMehrAuf(null);window.dispatchEvent(new Event("rf-nachrichten"));}} style={{marginTop:6,background:"none",border:"none",padding:0,fontFamily:"inherit",fontSize:12.5,fontWeight:600,color:T.accent,cursor:"pointer",textAlign:"left"}}>Nachrichten öffnen</button>
                    </div>);
                    return <button onClick={()=>{setMehrAuf(null);setPreisFenster(p);}} style={{textAlign:"left",background:"none",border:"none",padding:"9px 11px",borderRadius:7,fontFamily:"inherit",fontSize:13,color:T.text,cursor:"pointer"}}>Preis vorschlagen</button>;
                  })()}
                  <button onClick={()=>{setMehrAuf(null);delProject(p.id);}} style={{textAlign:"left",background:"none",border:"none",padding:"9px 11px",borderRadius:7,fontFamily:"inherit",fontSize:13,color:T.danger,cursor:"pointer"}}>Video löschen</button>
                </div>}
              </div>
              </div>
            </div>
            {notizOffen&&<NotizListe projectId={p.id} notizen={notizen} onAendern={ladeNotizen}/>}
            </div>);
          })}
          {(spaeterListe.length>0||fertigeListe.length>0)&&<div style={{display:"flex",gap:18,flexWrap:"wrap",padding:"11px 20px",borderTop:`1px solid ${T.border}`}}>
            {spaeterListe.length>0&&<button onClick={()=>setRestZeigen(r=>({...r,[gName]:!restAuf}))} style={{background:"none",border:"none",padding:0,fontFamily:"inherit",fontSize:12,fontWeight:600,color:T.textMuted,cursor:"pointer"}}>{restAuf?"Später ausblenden":`Später fällig · ${spaeterListe.length}`}</button>}
            {fertigeListe.length>0&&<button onClick={()=>setRestZeigen(r=>({...r,[gName+"__fertig"]:!fertigAuf}))} style={{background:"none",border:"none",padding:0,fontFamily:"inherit",fontSize:12,fontWeight:600,color:T.textDim,cursor:"pointer"}}>{fertigAuf?"Fertige ausblenden":`Fertig · ${fertigeListe.length}`}</button>}
          </div>}
            </>);
          })()}
        </div>);
      })}

    {/* Bearbeiten-Modal */}
    <Modal open={!!editProject} onClose={()=>setEditProject(null)} title="Video bearbeiten" width={520}>
      {editProject&&<div style={{display:"flex",flexDirection:"column",gap:14}}>
        <Input label="Kunde" value={editProject.client} onChange={e=>setEditProject({...editProject,client:e.target.value})} list="cutter-clients-edit"/>
        <datalist id="cutter-clients-edit">{clients.map(c=><option key={c} value={c}/>)}</datalist>
        <Input label="Titel des Videos" value={editProject.title} onChange={e=>setEditProject({...editProject,title:e.target.value})}/>
        <Select label="Art" options={[{value:"",label:"— Auswählen —"},{value:"reel",label:"Reel"},{value:"video",label:"Video"},{value:"story",label:"Story"},{value:"image",label:"Bildbearbeitung"},{value:"animation",label:"Animation"},{value:"other",label:"Sonstiges"}]} value={editProject.type||""} onChange={e=>setEditProject({...editProject,type:e.target.value})}/>
        <div style={{display:"flex",gap:12}}>
          <div style={{flex:1}}><Input label="Postdatum" type="date" value={editProject.postDate||""} onChange={e=>setEditProject({...editProject,postDate:e.target.value})}/></div>
          <div style={{flex:1}}><Input label="Deadline (Schnitt)" type="date" value={editProject.deadline||""} onChange={e=>setEditProject({...editProject,deadline:e.target.value})}/></div>
        </div>
        <Input label="Rohvideo-Link (Google Drive)" value={editProject.rawLink||""} onChange={e=>setEditProject({...editProject,rawLink:e.target.value})} placeholder="https://drive.google.com/..."/>
        <Input label="Notizen" value={editProject.notes||""} onChange={e=>setEditProject({...editProject,notes:e.target.value})}/>
        <Select label="Status" options={PROJECT_STATUSES.map(s=>({value:s.id,label:s.label}))} value={editProject.status} onChange={e=>setEditProject({...editProject,status:e.target.value})}/>
        <Button icon="check" onClick={saveEdit} style={{marginTop:8}}>Speichern</Button>
      </div>}
    </Modal>

    {/* Hinzufügen-Modal */}
    <Modal open={showAdd} onClose={()=>setShowAdd(false)} title="Video hinzufügen" width={520}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <Input label="Kunde" value={pForm.client} onChange={e=>setPForm({...pForm,client:e.target.value})} placeholder="z.B. Fleck Immobilien" list="cutter-clients"/>
        <datalist id="cutter-clients">{clients.map(c=><option key={c} value={c}/>)}</datalist>
        <Input label="Titel des Videos" value={pForm.title} onChange={e=>setPForm({...pForm,title:e.target.value})} placeholder="z.B. 5 Gründe für eine Alarmanlage"/>
        <Select label="Art" options={[{value:"",label:"— Auswählen —"},{value:"reel",label:"Reel"},{value:"video",label:"Video"},{value:"story",label:"Story"},{value:"image",label:"Bildbearbeitung"},{value:"animation",label:"Animation"},{value:"other",label:"Sonstiges"}]} value={pForm.type} onChange={e=>setPForm({...pForm,type:e.target.value})}/>
        <div style={{display:"flex",gap:12}}>
          <div style={{flex:1}}><Input label="Postdatum" type="date" value={pForm.postDate} onChange={e=>setPForm({...pForm,postDate:e.target.value})}/></div>
          <div style={{flex:1}}><Input label="Deadline (Schnitt)" type="date" value={pForm.deadline} onChange={e=>setPForm({...pForm,deadline:e.target.value})}/></div>
        </div>
        <Input label="Rohvideo-Link (Google Drive)" value={pForm.rawLink} onChange={e=>setPForm({...pForm,rawLink:e.target.value})} placeholder="https://drive.google.com/..."/>
        <Input label="Notizen" value={pForm.notes} onChange={e=>setPForm({...pForm,notes:e.target.value})} placeholder="Briefing, Hinweise..."/>
        <Button icon="check" onClick={addProject} style={{marginTop:8}}>Hinzufügen</Button>
      </div>
    </Modal>
  </div>);
};

// ─── CUTTER INVOICES VIEW (for cutter role) ───
// ─── Teilzahlungs-Modal für Cutter-Rechnungen ───
// Zeigt eine Liste aller Teilzahlungen, erlaubt hinzufügen + löschen.
// Wird sowohl von Cutter als auch Admin genutzt.
const PaymentsModal = ({invoice, user, onClose, onChanged}) => {
  const [payments,setPayments] = useState([]);
  const [loading,setLoading] = useState(false);
  const [form,setForm] = useState({amount:"", paidAt: todayLocal(), note:""});

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await api.get("cutter-invoice-payments", `invoiceId=${invoice.id}`);
      setPayments(Array.isArray(list) ? list : []);
    } catch(e){ /* ignore */ }
    setLoading(false);
  };
  useEffect(()=>{ if(invoice?.id) refresh(); }, [invoice?.id]);

  const totalPaid = payments.reduce((s,p)=>s+(p.amount||0), 0);
  const remaining = Math.max(0, invoice.amount - totalPaid);
  const pct = invoice.amount > 0 ? Math.min(100, (totalPaid/invoice.amount)*100) : 0;

  const addPayment = async () => {
    const amt = parseFloat(form.amount);
    if(!amt || amt <= 0) return;
    if(amt > remaining + 0.01) {
      if(!confirm(`Der Betrag (${amt.toFixed(2)} €) übersteigt den Restbetrag (${remaining.toFixed(2)} €). Trotzdem fortfahren?`)) return;
    }
    // Datum-Plausibilität: warnen wenn mehr als 6 Monate in der Zukunft
    if(form.paidAt) {
      const paidD = new Date(form.paidAt);
      const now = new Date();
      const monthsAhead = (paidD.getFullYear() - now.getFullYear()) * 12 + (paidD.getMonth() - now.getMonth());
      if(monthsAhead > 6) {
        if(!confirm(`Das eingetragene Datum (${paidD.toLocaleDateString("de")}) liegt mehr als 6 Monate in der Zukunft. Ist das wirklich korrekt? Möglicherweise hast du im Datums-Picker das falsche Jahr ausgewählt.`)) return;
      }
    }
    await api.post("cutter-invoice-payments", {
      invoiceId: invoice.id,
      amount: amt,
      paidAt: form.paidAt,
      note: form.note,
      paidBy: user?.id || null
    });
    setForm({amount:"", paidAt: todayLocal(), note:""});
    await refresh();
    if(onChanged) onChanged();
  };

  const deletePayment = async (id) => {
    if(!confirm("Diese Teilzahlung wirklich löschen?")) return;
    await api.del("cutter-invoice-payments", {id});
    await refresh();
    if(onChanged) onChanged();
  };

  return (<Modal open={!!invoice} onClose={onClose} title={`Teilzahlungen: ${invoice.client}`} width={620}>
    {/* Übersicht: Soll/Ist/Rest */}
    <div style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:12,padding:16,marginBottom:18}}>
      <div className="grid-3col" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>
        <div><div style={{fontSize:11,color:T.textDim,textTransform:"uppercase",marginBottom:4}}>Rechnung</div><div style={{fontSize:16,fontWeight:700,fontFamily:"'JetBrains Mono',monospace"}}>{invoice.amount.toLocaleString("de")} €</div></div>
        <div><div style={{fontSize:11,color:T.textDim,textTransform:"uppercase",marginBottom:4}}>Bezahlt</div><div style={{fontSize:16,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:T.success}}>{totalPaid.toLocaleString("de",{minimumFractionDigits:2,maximumFractionDigits:2})} €</div></div>
        <div><div style={{fontSize:11,color:T.textDim,textTransform:"uppercase",marginBottom:4}}>Offen</div><div style={{fontSize:16,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:remaining>0?T.warning:T.success}}>{remaining.toLocaleString("de",{minimumFractionDigits:2,maximumFractionDigits:2})} €</div></div>
      </div>
      {/* Fortschritts-Balken */}
      <div style={{height:8,background:T.bgCard,borderRadius:4,overflow:"hidden"}}>
        <div style={{height:"100%",width:`${pct}%`,background:remaining<=0?T.success:T.accent,borderRadius:4,transition:"width 0.3s"}}/>
      </div>
      <div style={{fontSize:11,color:T.textDim,marginTop:6,textAlign:"right"}}>{pct.toFixed(1)}% bezahlt</div>
    </div>

    {/* Neue Teilzahlung */}
    {remaining > 0.01 && <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,padding:14,marginBottom:18}}>
      <div style={{fontSize:13,fontWeight:700,marginBottom:10}}>Neue Teilzahlung erfassen</div>
      <div className="grid-2col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
        <Input label="Betrag (€)" type="number" step="0.01" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} placeholder={remaining.toFixed(2)}/>
        <div>
          <Input label="Datum" type="date" value={form.paidAt} onChange={e=>setForm({...form,paidAt:e.target.value})}/>
          {form.paidAt && <div style={{fontSize:11,color:T.textDim,marginTop:4}}>→ {new Date(form.paidAt).toLocaleDateString("de",{day:"numeric",month:"long",year:"numeric"})}</div>}
        </div>
      </div>
      <Input label="Notiz (optional)" value={form.note} onChange={e=>setForm({...form,note:e.target.value})} placeholder="z.B. Überweisung am 15.04."/>
      <div style={{display:"flex",gap:8,marginTop:12,justifyContent:"space-between",flexWrap:"wrap"}}>
        <button onClick={()=>setForm({...form, amount: remaining.toFixed(2)})} style={{padding:"6px 12px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:6,color:T.textMuted,fontSize:12,cursor:"pointer"}}>Restbetrag einsetzen</button>
        <Button icon="check" size="sm" onClick={addPayment}>Teilzahlung speichern</Button>
      </div>
    </div>}
    {remaining <= 0.01 && <div style={{background:`${T.success}15`,border:`1px solid ${T.success}40`,borderRadius:10,padding:12,marginBottom:18,display:"flex",alignItems:"center",gap:10}}>
      <span style={{fontSize:18}}>✓</span>
      <div style={{fontSize:13,color:T.success,fontWeight:600}}>Rechnung vollständig bezahlt</div>
    </div>}

    {/* Liste der bisherigen Teilzahlungen */}
    <div>
      <div style={{fontSize:13,fontWeight:700,marginBottom:10}}>Bisherige Zahlungen ({payments.length})</div>
      {loading && <div style={{color:T.textDim,fontSize:12}}>lädt...</div>}
      {!loading && payments.length===0 && <div style={{color:T.textDim,fontSize:13,padding:"12px 0"}}>Noch keine Teilzahlungen erfasst.</div>}
      {payments.map(p => (<div key={p.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",background:T.bgInput,borderRadius:8,marginBottom:6}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:14,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:T.success}}>{p.amount.toLocaleString("de",{minimumFractionDigits:2,maximumFractionDigits:2})} €</div>
          <div style={{fontSize:11,color:T.textDim,marginTop:2}}>{fmtDate(p.paidAt)}{p.paidByName?` · ${p.paidByName}`:""}{p.note?` · ${p.note}`:""}</div>
        </div>
        <button onClick={()=>deletePayment(p.id)} title="Teilzahlung löschen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4}}><Icon name="delete" size={14}/></button>
      </div>))}
    </div>
  </Modal>);
};

const CutterInvoicesView = ({user,reload,dv}) => {
  const [invoices,setInvoices] = useState(store.get(STORAGE_KEYS.cutter_invoices)||[]);
  const [showAdd,setShowAdd] = useState(false);
  const [iForm,setIForm] = useState({client:"",description:"",amount:"",dueDate:"",status:"draft"});
  const [pendingFile,setPendingFile] = useState(null);
  const [uploadingId,setUploadingId] = useState(null);
  const fileInputRef = useRef(null);

  // Nur eigene Rechnungen für den Cutter
  const myInvoices = invoices.filter(i => i.createdBy === user.id);

  const refreshInvoices = async () => { await reload(STORAGE_KEYS.cutter_invoices); setInvoices(store.get(STORAGE_KEYS.cutter_invoices)); };

  const addInvoice = async()=>{
    if(!iForm.client||!iForm.amount) return;
    const res = await api.post('cutter-invoices', {...iForm, amount:parseFloat(iForm.amount), createdBy:user.id});
    // Wenn Datei dabei: hochladen
    if(pendingFile && res?.id){
      const fd = new FormData();
      fd.append('id', res.id);
      fd.append('file', pendingFile);
      try { await fetch('/api/cutter-invoice-upload', {method:'POST', headers:authHeaders(), body:fd}); } catch(e){ /* ignore */ }
    }
    await refreshInvoices();
    setIForm({client:'',description:'',amount:'',dueDate:'',status:'draft'});
    setPendingFile(null);
    setShowAdd(false);
  };
  const updateStatus = async(id,status)=>{ await api.put('cutter-invoices',{id,status}); await refreshInvoices(); };
  const uploadFileToInvoice = async(invoiceId, file) => {
    setUploadingId(invoiceId);
    try {
      const fd = new FormData();
      fd.append('id', invoiceId);
      fd.append('file', file);
      const res = await fetch('/api/cutter-invoice-upload', {method:'POST', headers:authHeaders(), body:fd});
      const data = await res.json();
      if(!res.ok) { alert(data.error || 'Upload fehlgeschlagen'); return; }
      await refreshInvoices();
    } catch(e){ alert('Upload-Fehler: '+e.message); }
    setUploadingId(null);
  };

  // Status "paid" gilt als voll bezahlt (auch ohne erfasste Teilzahlung)
  const _effPaid = (i) => i.status==="paid" ? (i.amount||0) : Math.min(i.amount||0, i.paidTotal||0);
  const _effOpen = (i) => Math.max(0, (i.amount||0) - _effPaid(i));
  const openTotal = myInvoices.reduce((s,i)=>s+_effOpen(i), 0);
  const paidTotal = myInvoices.reduce((s,i)=>s+_effPaid(i), 0);

  // Modal-State für Teilzahlungen
  const [paymentInvoice,setPaymentInvoice] = useState(null);

  return (<div>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24,flexWrap:"wrap",gap:12}}>
      <h1 style={{fontSize:26,fontWeight:700,letterSpacing:"-0.02em"}}>Meine Rechnungen</h1>
      <Button icon="add" size="sm" onClick={()=>setShowAdd(true)}>Neue Rechnung</Button>
    </div>
    <div className="stats-row" style={{display:"flex",gap:16,flexWrap:"wrap",marginBottom:24}}>
      <StatCard label="Noch offen" value={fmtEuro(openTotal)} icon="money" color={T.warning}/>
      <StatCard label="Bezahlt (gesamt)" value={fmtEuro(paidTotal)} icon="money" color={T.success}/>
    </div>

    {paymentInvoice && <PaymentsModal invoice={paymentInvoice} user={user} onClose={()=>setPaymentInvoice(null)} onChanged={refreshInvoices}/>}

    <Modal open={showAdd} onClose={()=>{setShowAdd(false);setPendingFile(null);}} title="Neue Rechnung" width={480}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <Input label="Kunde" value={iForm.client} onChange={e=>setIForm({...iForm,client:e.target.value})} placeholder="z.B. R&F Media UG"/>
        <Input label="Beschreibung" value={iForm.description} onChange={e=>setIForm({...iForm,description:e.target.value})} placeholder="z.B. 3x Reels März"/>
        <Input label="Betrag (€)" type="number" value={iForm.amount} onChange={e=>setIForm({...iForm,amount:e.target.value})} placeholder="0.00"/>
        <Input label="Fällig am" type="date" value={iForm.dueDate} onChange={e=>setIForm({...iForm,dueDate:e.target.value})}/>
        <div>
          <div style={{fontSize:12,fontWeight:600,marginBottom:6,color:T.textDim}}>Rechnungs-Datei (PDF/Bild, max 20 MB)</div>
          <input ref={fileInputRef} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" style={{display:"none"}} onChange={e=>setPendingFile(e.target.files?.[0]||null)}/>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <Button variant="secondary" size="sm" icon="upload" onClick={()=>fileInputRef.current?.click()}>Datei wählen</Button>
            {pendingFile && <div style={{fontSize:12,color:T.text,display:"flex",alignItems:"center",gap:4}}><Icon name="upload" size={12}/> {pendingFile.name} ({(pendingFile.size/1024).toFixed(0)} KB)</div>}
          </div>
        </div>
        <Button icon="check" onClick={addInvoice} style={{marginTop:8}}>Anlegen</Button>
      </div>
    </Modal>

    <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,overflow:"auto",WebkitOverflowScrolling:"touch"}}>
      {myInvoices.length===0?<Empty icon="money" title="Keine Rechnungen" desc="Lege deine erste Rechnung an"/>:(
        <table style={{width:"100%",borderCollapse:"collapse",minWidth:880}}>
          <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>{["Kunde","Beschreibung","Betrag","Bezahlt","Offen","Fällig","Datei","Status",""].map(h=>(<th key={h} style={{padding:"12px 12px",textAlign:"left",fontSize:11,fontWeight:600,color:T.textDim,textTransform:"uppercase"}}>{h}</th>))}</tr></thead>
          <tbody>{myInvoices.slice().reverse().map(inv=>{
            const isOverdue=inv.status==="sent"&&inv.dueDate&&new Date(inv.dueDate)<new Date();
            const paid = _effPaid(inv);
            const rem = _effOpen(inv);
            return (<tr key={inv.id} style={{borderBottom:`1px solid ${T.border}`,background:isOverdue?`${T.danger}08`:"transparent"}}>
              <td style={{padding:"12px 12px",fontSize:13,fontWeight:600}}>{inv.client}</td>
              <td style={{padding:"12px 12px",fontSize:13}}>{inv.description}</td>
              <td style={{padding:"12px 12px",fontSize:14,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:inv.status==="paid"?T.success:T.text}}>{inv.amount.toLocaleString("de")} €</td>
              <td style={{padding:"12px 12px",fontSize:13,fontFamily:"'JetBrains Mono',monospace",color:paid>0?T.success:T.textDim}}>{paid>0?paid.toLocaleString("de",{minimumFractionDigits:2,maximumFractionDigits:2})+" €":"—"}</td>
              <td style={{padding:"12px 12px",fontSize:13,fontFamily:"'JetBrains Mono',monospace",color:rem>0?T.warning:T.success,fontWeight:700}}>{rem>0?rem.toLocaleString("de",{minimumFractionDigits:2,maximumFractionDigits:2})+" €":"✓"}</td>
              <td style={{padding:"12px 12px",fontSize:13,fontFamily:"'JetBrains Mono',monospace",color:isOverdue?T.danger:T.textMuted}}>{inv.dueDate?new Date(inv.dueDate).toLocaleDateString("de"):"—"}</td>
              <td style={{padding:"12px 12px",fontSize:12}}>
                {inv.hasFile ? (
                  <a href={`/api/cutter-invoice-file/${inv.id}`} target="_blank" rel="noopener noreferrer" style={{color:T.info,textDecoration:"none",display:"flex",alignItems:"center",gap:4}}><Icon name="upload" size={13}/> Ansehen</a>
                ) : (
                  <label style={{cursor:"pointer",color:T.textDim,fontSize:11,display:"inline-flex",alignItems:"center",gap:4}}>
                    <input type="file" accept="application/pdf,image/png,image/jpeg,image/webp" style={{display:"none"}} onChange={e=>{const f=e.target.files?.[0];if(f)uploadFileToInvoice(inv.id, f);}}/>
                    {uploadingId===inv.id ? "lädt..." : <><Icon name="upload" size={12}/> Datei hinzufügen</>}
                  </label>
                )}
              </td>
              <td style={{padding:"12px 12px"}}><select value={isOverdue?"overdue":inv.status} onChange={e=>updateStatus(inv.id,e.target.value)} style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:6,padding:"4px 8px",color:INVOICE_STATUSES.find(s=>s.id===(isOverdue?"overdue":inv.status))?.color||T.text,fontSize:12,fontWeight:600,outline:"none"}}>
                {INVOICE_STATUSES.map(s=>(<option key={s.id} value={s.id}>{s.label}</option>))}
              </select></td>
              <td style={{padding:"12px 12px"}}>
                <button onClick={()=>setPaymentInvoice(inv)} title="Teilzahlungen verwalten" style={{padding:"5px 10px",background:`${T.info}22`,border:`1px solid ${T.info}40`,borderRadius:6,color:T.info,fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:4}}><Icon name="money" size={13}/> Zahlungen</button>
              </td>
            </tr>);})}</tbody>
        </table>)}
    </div>
  </div>);
};

// ─── CUTTER GOALS VIEW (for cutter role) ───
const CutterGoalsView = ({user,reload,dv}) => {
  const [goals,setGoals] = useState([]);
  useEffect(()=>{api.get('personal-goals','userId='+user.id).then(g=>setGoals(g||[]));},[dv]);
  const [goalForm,setGoalForm] = useState({text:"",target:""});
  const addGoal=async()=>{if(!goalForm.text)return;await api.post('personal-goals',{userId:user.id,text:goalForm.text,target:parseInt(goalForm.target)||0});const g=await api.get('personal-goals','userId='+user.id);setGoals(g||[]);setGoalForm({text:"",target:""});};
  const toggleGoal=async(id)=>{const g=goals.find(x=>x.id===id);await api.put('personal-goals',{id,userId:user.id,current:g?.current||0,done:!g?.done});const u=await api.get('personal-goals','userId='+user.id);setGoals(u||[]);};
  const updateGoalProgress=async(id,val)=>{await api.put('personal-goals',{id,userId:user.id,current:parseInt(val)||0,done:false});const u=await api.get('personal-goals','userId='+user.id);setGoals(u||[]);};
  const deleteGoal=async(id)=>{await api.del('personal-goals',{id,userId:user.id});const u=await api.get('personal-goals','userId='+user.id);setGoals(u||[]);};
  const doneCount=goals.filter(g=>g.done).length;

  return (<div>
    <div style={{marginBottom:24}}><h1 style={{fontSize:26,fontWeight:700,letterSpacing:"-0.02em"}}>Meine Ziele</h1><p style={{color:T.textDim,fontSize:14,marginTop:4}}>{doneCount}/{goals.length} erledigt</p></div>
    <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:24}}>
      <div style={{display:"flex",gap:8,marginBottom:20,flexWrap:"wrap",alignItems:"end"}}>
        <Input placeholder="z.B. 10 Reels pro Monat" value={goalForm.text} onChange={e=>setGoalForm({...goalForm,text:e.target.value})} style={{flex:1,minWidth:200}}/>
        <Input placeholder="Ziel" type="number" value={goalForm.target} onChange={e=>setGoalForm({...goalForm,target:e.target.value})} style={{width:80}}/>
        <Button icon="add" onClick={addGoal}>Ziel setzen</Button>
      </div>
      {goals.length===0?<div style={{color:T.textDim,fontSize:13,textAlign:"center",padding:24}}>Setze dir Ziele und tracke deinen Fortschritt!</div>:
        goals.map(g=>{
          const pct=g.target>0?Math.min(100,Math.round((g.current/g.target)*100)):g.done?100:0;
          return (<div key={g.id} style={{padding:"16px 0",borderBottom:`1px solid ${T.border}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <button onClick={()=>toggleGoal(g.id)} style={{width:24,height:24,borderRadius:8,border:`2px solid ${g.done?T.success:T.border}`,background:g.done?T.success:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:"#fff"}}>{g.done&&<Icon name="check" size={14}/>}</button>
                <span style={{fontSize:15,fontWeight:600,textDecoration:g.done?"line-through":"none",opacity:g.done?0.5:1}}>{g.text}</span>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                {g.target>0&&<div style={{display:"flex",alignItems:"center",gap:4}}>
                  <input type="number" value={g.current} onChange={e=>updateGoalProgress(g.id,e.target.value)} style={{width:50,background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:6,padding:"4px 8px",color:T.text,fontSize:14,textAlign:"center",outline:"none"}}/>
                  <span style={{fontSize:14,color:T.textDim}}>/ {g.target}</span>
                  <span style={{fontSize:12,fontWeight:700,color:pct>=100?T.success:pct>=50?T.warning:T.textDim,marginLeft:4}}>{pct}%</span>
                </div>}
                <button onClick={()=>deleteGoal(g.id)} style={{background:"none",border:"none",color:T.textDim,cursor:"pointer"}}><Icon name="delete" size={16}/></button>
              </div>
            </div>
            {g.target>0&&<div style={{height:8,background:T.bgInput,borderRadius:4,overflow:"hidden"}}><div style={{height:"100%",width:`${pct}%`,background:pct>=100?T.success:pct>=50?T.warning:T.accent,borderRadius:4,transition:"width 0.3s"}}/></div>}
          </div>);
        })}
    </div>
  </div>);
};

// Notizen zu einem Video, einzeln abhakbar.
// Wird unter der Videozeile aufgeklappt und in Admin- wie Cutter-Ansicht genutzt.
const NotizListe = ({projectId, notizen, onAendern, flach}) => {
  const [neu,setNeu]=useState("");
  const meine=(notizen||[]).filter(n=>n.projectId===projectId);
  const anlegen=async()=>{
    const t=neu.trim(); if(!t)return;
    setNeu("");
    await api.post('cutter-project-notes',{projectId,text:t});
    onAendern&&onAendern();
  };
  const umschalten=async(n)=>{ await api.put('cutter-project-notes',{id:n.id,done:!n.done}); onAendern&&onAendern(); };
  const loeschen=async(n)=>{ await api.del('cutter-project-notes',{id:n.id}); onAendern&&onAendern(); };
  return (<div style={flach?{}:{padding:"10px 18px 12px 49px",background:T.bgCard2,borderBottom:`1px solid ${T.border}`}}>
    {meine.length>0&&<div style={{display:"flex",flexDirection:"column",gap:5,marginBottom:9}}>
      {meine.map(n=>(
        <div key={n.id} className="notiz-zeile" style={{display:"flex",alignItems:"flex-start",gap:9}}>
          <button onClick={()=>umschalten(n)} title={n.done?"Wieder offen":"Erledigt"} style={{width:17,height:17,marginTop:1,flexShrink:0,borderRadius:5,border:`2px solid ${n.done?T.success:T.borderStrong}`,background:n.done?T.success:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0}}>
            {n.done&&<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
          </button>
          <span style={{flex:1,fontSize:12.5,lineHeight:1.45,color:n.done?T.textDim:T.text,textDecoration:n.done?"line-through":"none"}}>{n.text}</span>
          <button className="notiz-weg" onClick={()=>loeschen(n)} title="Notiz entfernen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:1,display:"flex",flexShrink:0}}><Icon name="close" size={13}/></button>
        </div>
      ))}
    </div>}
    <div style={{display:"flex",gap:7,alignItems:"center"}}>
      <input value={neu} onChange={e=>setNeu(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")anlegen();}} placeholder="Notiz hinzufügen, z.B. Extraaudio beachten"
        style={{flex:1,minWidth:0,padding:"6px 10px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:7,color:T.text,fontSize:12.5,outline:"none"}}/>
      <button onClick={anlegen} disabled={!neu.trim()} style={{padding:"6px 12px",background:neu.trim()?T.accent:T.bgInput,border:`1px solid ${neu.trim()?T.accent:T.border}`,borderRadius:7,color:neu.trim()?"#fff":T.textDim,fontSize:12.5,fontWeight:600,cursor:neu.trim()?"pointer":"default",flexShrink:0}}>Hinzufügen</button>
    </div>
  </div>);
};

// Preisvorlagen je Videotyp. Damit wird der Preis beim Anlegen vorgeschlagen.
const PreisVorlagenModal = ({open,onClose,vorlagen,onSpeichern,onLoeschen,typLabels}) => {
  const TYPEN=[["reel","Reel"],["video","Video"],["story","Story"],["image","Bildbearbeitung"],["animation","Animation"],["other","Sonstiges"]];
  const [entwurf,setEntwurf]=useState({});
  useEffect(()=>{ if(open){ const e={}; TYPEN.forEach(([id])=>{ const v=vorlagen.find(x=>x.type===id); e[id]=v?String(v.price):""; }); setEntwurf(e); } },[open,vorlagen]);
  const speichern=async()=>{
    for(const [id] of TYPEN){
      const wert=(entwurf[id]||"").trim();
      const da=vorlagen.find(x=>x.type===id);
      if(wert===""){ if(da) await onLoeschen(da.id); continue; }
      await onSpeichern(id, parseFloat(wert.replace(",",".")) || 0);
    }
    onClose();
  };
  return (<Modal open={open} onClose={onClose} title="Preisvorlagen" width={460}>
    <p style={{fontSize:13.5,color:T.textMuted,lineHeight:1.6,marginTop:-2,marginBottom:16}}>Was ein Video je Art standardmäßig wert ist. Beim Anlegen wird der Preis vorgeschlagen und du kannst ihn bei aufwendigen Videos einzeln überschreiben. Leer lassen heißt kein Vorschlag.</p>
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      {TYPEN.map(([id,label])=>(
        <div key={id} style={{display:"flex",alignItems:"center",gap:12}}>
          <span style={{flex:1,fontSize:13.5,fontWeight:600}}>{label}</span>
          <input value={entwurf[id]||""} onChange={e=>setEntwurf(v=>({...v,[id]:e.target.value}))} placeholder="—"
            style={{width:110,background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 11px",color:T.text,fontSize:13,outline:"none",fontFamily:"'JetBrains Mono',monospace",textAlign:"right"}}/>
          <span style={{fontSize:13,color:T.textDim,width:14}}>€</span>
        </div>
      ))}
    </div>
    <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:18}}>
      <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
      <Button onClick={speichern}>Speichern</Button>
    </div>
  </Modal>);
};

// Vorschüsse eintragen. Fertige Videos ziehen ihren Preis vom Guthaben ab.
const VorschussModal = ({open,onClose,cutter,guthaben,onSpeichern,onNeuLaden}) => {
  const [form,setForm]=useState({cutterId:"",amount:"",date:"",note:""});
  const [liste,setListe]=useState([]);
  const laden=()=>{ api.get('cutter-advances').then(r=>{ if(Array.isArray(r)) setListe(r); }); };
  useEffect(()=>{ if(open){ setForm({cutterId:"",amount:"",date:new Date().toISOString().slice(0,10),note:""}); laden(); } },[open]);
  const speichern=async()=>{
    if(!form.cutterId||!form.amount)return;
    await onSpeichern(form.cutterId, parseFloat(String(form.amount).replace(",",".")), form.date, form.note);
    setForm(f=>({...f,amount:"",note:""})); laden();
  };
  const loeschen=async(id)=>{ await api.del('cutter-advances',{id}); laden(); onNeuLaden&&onNeuLaden(); };
  const nameVon=(id)=>cutter.find(u=>u.id===id)?.name||"—";
  return (<Modal open={open} onClose={onClose} title="Vorschüsse" width={560}>
    <p style={{fontSize:13.5,color:T.textMuted,lineHeight:1.6,marginTop:-2,marginBottom:16}}>Trag hier ein, was du einem Cutter im Voraus ausgezahlt hast. Jedes fertige Video zieht seinen Preis automatisch von diesem Guthaben ab.</p>
    <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"flex-end",marginBottom:18}}>
      <div style={{flex:"1 1 150px"}}>
        <Select label="Cutter" options={[{value:"",label:"— Auswählen —"}].concat(cutter.map(u=>({value:u.id,label:u.name})))} value={form.cutterId} onChange={e=>setForm({...form,cutterId:e.target.value})}/>
      </div>
      <div style={{width:110}}><Input label="Betrag (€)" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} placeholder="z.B. 500"/></div>
      <div style={{width:150}}><Input label="Datum" type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})}/></div>
      <Button onClick={speichern} disabled={!form.cutterId||!form.amount}>Eintragen</Button>
    </div>
    {liste.length===0
      ? <div style={{fontSize:13,color:T.textDim}}>Noch keine Vorschüsse eingetragen.</div>
      : <div style={{display:"flex",flexDirection:"column",border:`1px solid ${T.border}`,borderRadius:10,overflow:"hidden"}}>
          {liste.map((a,i)=>(
            <div key={a.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderBottom:i<liste.length-1?`1px solid ${T.border}`:"none",background:T.bgCard}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600}}>{nameVon(a.cutterId)}</div>
                <div style={{fontSize:11.5,color:T.textDim}}>{a.date||"ohne Datum"}{a.note?` · ${a.note}`:""}</div>
              </div>
              <span style={{fontSize:13.5,fontWeight:700,fontFamily:"'JetBrains Mono',monospace"}}>{rfEuroGenau(a.amount)}</span>
              <button onClick={()=>loeschen(a.id)} title="Entfernen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:3,display:"flex"}}><Icon name="delete" size={14}/></button>
            </div>
          ))}
        </div>}
  </Modal>);
};

// ═══════════════════════════════════════
// NACHRICHTENFENSTER
// Sitzt unten in der Ecke und ist von überall erreichbar.
// Hier laufen die Preisvorschläge der Cutter auf.
// ═══════════════════════════════════════
const NachrichtenFenster = ({user}) => {
  const istAdmin = user && user.role === "admin";
  const [auf,setAuf] = useState(false);
  const [reiter,setReiter] = useState("offen");
  const [liste,setListe] = useState([]);
  const [gegen,setGegen] = useState({});     // je Vorschlag der eingetippte Gegenbetrag
  const [notiz,setNotiz] = useState({});     // je Vorschlag die Begruendung des Admins
  const [laeuft,setLaeuft] = useState(null); // welcher Vorschlag gerade gespeichert wird
  const [fehler,setFehler] = useState(null);
  const [erlaubnis,setErlaubnis] = useState(typeof Notification!=="undefined" ? Notification.permission : "unsupported");
  const gesehen = useRef(null);              // IDs, die beim letzten Abruf schon da waren
  const titelRef = useRef(typeof document!=="undefined" ? document.title : "");

  const meldung = (titel, text) => {
    try{ if(typeof Notification!=="undefined" && Notification.permission==="granted") new Notification(titel,{body:text}); }catch(e){}
  };

  // Bewusst ohne api.get: das verschluckt Fehler und liefert ein leeres Array.
  // Hier soll im Zweifel dastehen, was der Server wirklich geantwortet hat.
  const holen = async () => {
    const res = await _fetchT(`${API_BASE}/cutter-price-requests`, { headers: authHeaders() });
    const roh = await res.text();
    let d = null;
    try { d = JSON.parse(roh); } catch(e) {}
    if(res.ok && d && Array.isArray(d.vorschlaege)) return d;
    const detail = d && d.error ? d.error : (roh||"").replace(/\s+/g," ").trim().slice(0,160);
    throw new Error(`HTTP ${res.status}${detail?": "+detail:""}`);
  };

  const laden = () => {
    holen().then(r=>{
      if(r && Array.isArray(r.vorschlaege)){
        setFehler(null);
        setListe(r.vorschlaege);
        // Alles, was jetzt bei mir auf dem Tisch liegt
        const meine = r.vorschlaege.filter(v=> istAdmin ? v.status==="offen" : v.status==="gegenvorschlag");
        const ids = meine.map(v=>v.id);
        if(gesehen.current){
          const neu = meine.filter(v=>!gesehen.current.includes(v.id));
          if(neu.length){
            const v = neu[0];
            meldung(
              istAdmin ? `Preisvorschlag von ${v.cutterName||"Cutter"}` : "Antwort auf deinen Preisvorschlag",
              neu.length>1 ? `${neu.length} neue Nachrichten im CRM` : `${v.title||"Video"}: ${rfEuroGenau(istAdmin?v.proposedPrice:v.counterPrice)}`
            );
          }
        }
        gesehen.current = ids;
      }
    }).catch(e=>setFehler(`Der Server meldet: ${e&&e.message?e.message:"keine Antwort"}`));
  };
  useEffect(()=>{
    laden();
    const t = setInterval(laden, 20000); // alle zwanzig Sekunden nachsehen
    const sicht = ()=>{ if(!document.hidden) laden(); };
    const oeffne = ()=>{ setAuf(true); laden(); };
    document.addEventListener("visibilitychange", sicht);
    window.addEventListener("rf-nachrichten", oeffne);
    return ()=>{ clearInterval(t); document.removeEventListener("visibilitychange", sicht); window.removeEventListener("rf-nachrichten", oeffne); };
  },[]);

  const offen = liste.filter(v=> istAdmin ? v.status==="offen" : v.status==="gegenvorschlag");
  // Zaehler auch im Browsertab, damit man es sieht ohne im CRM zu sein
  useEffect(()=>{
    const basis = titelRef.current || "R&F Media CRM";
    document.title = offen.length>0 ? `(${offen.length}) ${basis}` : basis;
    return ()=>{ document.title = basis; };
  },[offen.length]);
  const wartend = liste.filter(v=> istAdmin ? v.status==="gegenvorschlag" : v.status==="offen");
  const erledigt = liste.filter(v=>v.status==="angenommen"||v.status==="abgelehnt");

  const entscheiden = async(v, aktion, extra={}) => {
    setLaeuft(v.id);
    const r = await api.put('cutter-price-requests',{id:v.id,aktion,...extra});
    setLaeuft(null);
    if(r&&r.error){ alert(r.error); return; }
    setGegen(g=>({...g,[v.id]:""})); setNotiz(n=>({...n,[v.id]:""}));
    laden();
  };

  const euro = (n)=> n==null ? "—" : rfEuroGenau(n);
  const initialen = (name)=>!name?"—":name.split(" ").filter(Boolean).slice(0,2).map(t=>t[0]).join("").toUpperCase();
  const wann = (s)=>{
    if(!s) return "";
    const d=new Date(String(s).replace(" ","T"));
    if(isNaN(d)) return "";
    const min=Math.round((Date.now()-d.getTime())/60000);
    if(min<60) return `vor ${Math.max(1,min)} Min.`;
    if(min<60*24) return `vor ${Math.round(min/60)} Std.`;
    if(min<60*48) return "gestern";
    return d.toLocaleDateString("de",{day:"2-digit",month:"2-digit"});
  };

  const nachfragen = async()=>{
    try{ const p = await Notification.requestPermission(); setErlaubnis(p); }catch(e){}
  };

  return (<>
    {!auf&&<button onClick={()=>setAuf(true)} aria-label={`Nachrichten öffnen${offen.length?`, ${offen.length} neue`:""}`} className="nf-knopf"
      style={{position:"fixed",right:22,bottom:22,zIndex:900,width:52,height:52,borderRadius:"50%",background:T.text,border:"none",boxShadow:"0 8px 22px rgba(20,20,30,0.22)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke={T.bgCard} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
      {offen.length>0&&<span style={{position:"absolute",top:-2,right:-2,minWidth:20,height:20,boxSizing:"border-box",borderRadius:10,background:T.danger,color:"#fff",fontFamily:"'JetBrains Mono',monospace",fontSize:11,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",border:`2px solid ${T.bg}`,padding:"0 4px"}}>{offen.length}</span>}
    </button>}

    {auf&&<div className="nf-fenster" style={{position:"fixed",right:22,bottom:22,zIndex:900,width:396,maxWidth:"calc(100vw - 32px)",background:T.bgCard,border:`1px solid ${T.borderStrong}`,borderRadius:16,boxShadow:"0 18px 44px rgba(20,20,30,0.2)",overflow:"hidden",display:"flex",flexDirection:"column",maxHeight:"min(78vh, 680px)"}}>
      <div style={{display:"flex",alignItems:"center",gap:9,padding:"14px 17px",borderBottom:`1px solid ${T.border}`,flexShrink:0}}>
        <span style={{fontSize:15,fontWeight:700}}>Nachrichten</span>
        {offen.length>0&&<span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:11.5,fontWeight:700,color:"#fff",background:T.danger,borderRadius:20,padding:"2px 9px"}}>{offen.length}</span>}
        <button onClick={()=>setAuf(false)} aria-label="Fenster schließen" style={{marginLeft:"auto",background:"none",border:"none",padding:4,color:T.textDim,cursor:"pointer",display:"flex"}}><Icon name="close" size={16}/></button>
      </div>

      <div style={{display:"flex",gap:2,padding:"9px 13px 0",flexShrink:0}}>
        {[{v:"offen",l:`Offen${offen.length?` ${offen.length}`:""}`},{v:"erledigt",l:"Erledigt"}].map(r=>(
          <button key={r.v} onClick={()=>setReiter(r.v)} style={{background:reiter===r.v?T.bgInput:"none",border:"none",borderRadius:"8px 8px 0 0",padding:"8px 14px",fontFamily:"inherit",fontSize:12.5,fontWeight:reiter===r.v?700:500,color:reiter===r.v?T.text:T.textMuted,cursor:"pointer"}}>{r.l}</button>
        ))}
      </div>

      <div style={{overflowY:"auto",borderTop:`1px solid ${T.border}`,flex:"1 1 auto"}}>
        {fehler&&<div style={{margin:"12px 13px 0",padding:"10px 12px",background:`${T.danger}12`,border:`1px solid ${T.danger}44`,borderRadius:10,fontSize:12.5,color:T.text,lineHeight:1.5}}>{fehler}</div>}
        {erlaubnis==="default"&&<div style={{margin:"12px 13px 0",padding:"10px 12px",background:T.bgInput,borderRadius:10,fontSize:12.5,color:T.textMuted,lineHeight:1.5}}>
          Damit neue Nachrichten sofort aufpoppen, einmal erlauben.
          <button onClick={nachfragen} style={{display:"block",marginTop:7,background:T.accent,border:"none",borderRadius:7,padding:"7px 12px",fontFamily:"inherit",fontSize:12,fontWeight:600,color:"#fff",cursor:"pointer"}}>Benachrichtigungen erlauben</button>
        </div>}
        {reiter==="offen"&&<>
          {offen.length===0&&wartend.length===0&&<div style={{padding:"26px 18px",fontSize:13,color:T.textDim,textAlign:"center"}}>Nichts Offenes. Alles erledigt.</div>}

          {offen.map(v=>(
            <div key={v.id} style={{padding:"15px 17px",borderBottom:`1px solid ${T.border}`}}>
              <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:9}}>
                <span style={{width:27,height:27,borderRadius:"50%",background:T.infoGlow,color:T.info,fontSize:10.5,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{initialen(v.cutterName)}</span>
                <span style={{fontSize:13,fontWeight:700,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v.cutterName||"Cutter"}</span>
                <span style={{fontSize:11.5,color:T.textDim,flexShrink:0}}>{wann(v.createdAt)}</span>
                <span style={{marginLeft:"auto",fontSize:11,fontWeight:700,color:T.warning,background:`${T.warning}18`,borderRadius:5,padding:"3px 8px",flexShrink:0}}>Preis</span>
              </div>

              <div style={{background:T.bgInput,borderRadius:11,padding:"11px 13px",marginBottom:11}}>
                <div style={{fontSize:13,fontWeight:700,marginBottom:4}}>{v.title||"Video"}</div>
                <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:15,fontWeight:700,marginBottom:v.reason?6:0}}>
                  {istAdmin ? `${euro(v.oldPrice)} → ${euro(v.proposedPrice)}` : `Dein Vorschlag ${euro(v.proposedPrice)}, Gegenvorschlag ${euro(v.counterPrice)}`}
                </div>
                {v.reason&&<div style={{fontSize:12.5,color:T.textMuted,lineHeight:1.5}}><span style={{color:T.textDim}}>{istAdmin?"Seine Begründung: ":"Deine Begründung: "}</span>{v.reason}</div>}
                {!istAdmin&&v.adminNote&&<div style={{marginTop:8,paddingLeft:9,borderLeft:`2px solid ${T.accent}`,fontSize:12.5,color:T.text,lineHeight:1.5}}>
                  <span style={{display:"block",fontSize:11,color:T.textDim,marginBottom:2}}>Antwort von R&amp;F</span>{v.adminNote}
                </div>}
              </div>

              {istAdmin ? (<>
                <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:8}}>
                  <button disabled={laeuft===v.id} onClick={()=>entscheiden(v,"annehmen")} style={{flex:1,background:T.accent,border:"none",borderRadius:8,padding:"9px",fontFamily:"inherit",fontSize:12.5,fontWeight:600,color:"#fff",cursor:"pointer",opacity:laeuft===v.id?0.6:1}}>{euro(v.proposedPrice)} annehmen</button>
                  <button disabled={laeuft===v.id} onClick={()=>entscheiden(v,"ablehnen",{adminNote:(notiz[v.id]||"").trim()})} style={{background:"none",border:"none",padding:"0 6px",fontFamily:"inherit",fontSize:12.5,color:T.danger,textDecoration:"underline",cursor:"pointer"}}>Ablehnen</button>
                </div>
                <textarea value={notiz[v.id]||""} onChange={e=>setNotiz(n=>({...n,[v.id]:e.target.value}))} rows={2}
                  placeholder="Warum? Das liest der Cutter direkt bei sich."
                  style={{boxSizing:"border-box",width:"100%",resize:"vertical",padding:"9px 11px",marginBottom:7,background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,fontSize:12.5,fontFamily:"inherit",outline:"none",lineHeight:1.45}}/>
                <div style={{display:"flex",alignItems:"center",gap:7}}>
                  <input value={gegen[v.id]||""} onChange={e=>setGegen(g=>({...g,[v.id]:e.target.value}))} placeholder="€" aria-label="Gegenvorschlag in Euro"
                    style={{boxSizing:"border-box",width:72,height:34,padding:"0 11px",background:T.bgCard,border:`1px solid ${T.borderStrong}`,borderRadius:8,color:T.text,fontSize:13,fontWeight:600,outline:"none",fontFamily:"'JetBrains Mono',monospace"}}/>
                  <button disabled={!gegen[v.id]||laeuft===v.id} onClick={()=>entscheiden(v,"gegenvorschlag",{counterPrice:parseFloat(String(gegen[v.id]).replace(",",".")),adminNote:(notiz[v.id]||"").trim()})}
                    style={{flex:1,background:T.bgCard,border:`1px solid ${gegen[v.id]?T.borderStrong:T.border}`,borderRadius:8,padding:"9px",fontFamily:"inherit",fontSize:12.5,fontWeight:600,color:gegen[v.id]?T.text:T.textDim,cursor:gegen[v.id]?"pointer":"default"}}>Gegenvorschlag senden</button>
                </div>
              </>) : (<>
                <div style={{display:"flex",alignItems:"center",gap:7}}>
                  <button disabled={laeuft===v.id} onClick={()=>entscheiden(v,"gegenvorschlag-annehmen")} style={{flex:1,background:T.success,border:"none",borderRadius:8,padding:"9px",fontFamily:"inherit",fontSize:12.5,fontWeight:700,color:"#fff",cursor:"pointer"}}>{euro(v.counterPrice)} annehmen</button>
                  <button disabled={laeuft===v.id} onClick={()=>entscheiden(v,"gegenvorschlag-ablehnen")} style={{background:"none",border:"none",padding:"0 6px",fontFamily:"inherit",fontSize:12.5,color:T.textMuted,textDecoration:"underline",cursor:"pointer"}}>Ablehnen</button>
                </div>
              </>)}
            </div>
          ))}

          {wartend.map(v=>(
            <div key={v.id} style={{padding:"13px 17px",background:T.bgCard2,borderBottom:`1px solid ${T.border}`}}>
              <div style={{display:"flex",alignItems:"center",gap:9}}>
                <span style={{width:27,height:27,borderRadius:"50%",background:T.bgInput,color:T.textDim,fontSize:10.5,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{initialen(v.cutterName)}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12.5,fontWeight:700,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{v.title||"Video"}</div>
                  <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:11.5,color:T.textDim,marginTop:2}}>
                    {istAdmin ? `Du hast ${euro(v.counterPrice)} geboten` : `${euro(v.proposedPrice)} vorgeschlagen`}
                  </div>
                </div>
                <span style={{fontSize:11,fontWeight:700,color:T.textMuted,background:T.bgInput,borderRadius:5,padding:"4px 9px",flexShrink:0}}>{istAdmin?"wartet auf ihn":"wartet"}</span>
              </div>
            </div>
          ))}
        </>}

        {reiter==="erledigt"&&<>
          {erledigt.length===0
            ? <div style={{padding:"26px 18px",fontSize:13,color:T.textDim,textAlign:"center"}}>Noch nichts entschieden.</div>
            : erledigt.slice(0,30).map(v=>(
              <div key={v.id} style={{display:"flex",alignItems:"center",gap:11,padding:"11px 17px",borderBottom:`1px solid ${T.border}`}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:500,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{v.title||"Video"}</div>
                  <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:11.5,color:T.textDim,marginTop:2}}>{euro(v.oldPrice)} → {euro(v.counterPrice!=null?v.counterPrice:v.proposedPrice)}</div>
                </div>
                <span style={{fontSize:11,fontWeight:700,flexShrink:0,color:v.status==="angenommen"?T.success:T.danger,background:v.status==="angenommen"?`${T.success}16`:`${T.danger}16`,borderRadius:5,padding:"4px 9px"}}>{v.status==="angenommen"?"angenommen":"abgelehnt"}</span>
              </div>
            ))}
        </>}
      </div>
    </div>}
  </>);
};

// ─── CUTTER (admin view) ───
// Abstufung statt Buntheit: je weiter im Ablauf, desto kraeftiger der Akzent.
const PROJECT_STATUSES = [
  {id:"open",label:"Offen",color:T.textDim},
  {id:"in_progress",label:"In Arbeit",color:T.accent+"aa"},
  {id:"review",label:"Review",color:T.accent+"dd"},
  {id:"done",label:"Fertig",color:T.accent},
  {id:"delivered",label:"Gepostet",color:T.success},
];
const INVOICE_STATUSES = [
  {id:"draft",label:"Entwurf",color:T.textDim},
  {id:"sent",label:"Gesendet",color:T.accent+"bb"},
  {id:"paid",label:"Bezahlt",color:T.success},
  {id:"overdue",label:"Überfällig",color:T.danger},
];

const CutterPage = ({reload,dv}) => {
  const [tab,setTab] = useState("projects"); // projects | invoices | goals | overview
  const [projects,setProjects] = useState(store.get(STORAGE_KEYS.cutter_projects)||[]);
  const [invoices,setInvoices] = useState(store.get(STORAGE_KEYS.cutter_invoices)||[]);
  const [goals,setGoals] = useState(store.get(STORAGE_KEYS.cutter_goals)||[]);
  const [showAddProject,setShowAddProject] = useState(false);
  const [showAddInvoice,setShowAddInvoice] = useState(false);
  const [pForm,setPForm] = useState({client:"",title:"",type:"",deadline:"",postDate:"",rawLink:"",notes:"",status:"open",assignedTo:"",price:""});
  const [iForm,setIForm] = useState({client:"",description:"",amount:"",dueDate:"",status:"draft",createdBy:""});
  const [adminPendingFile,setAdminPendingFile] = useState(null);
  const adminFileInputRef = useRef(null);
  const [goalForm,setGoalForm] = useState({text:"",target:""});
  const [paymentInvoice,setPaymentInvoice] = useState(null);
  const [editInvoice,setEditInvoice] = useState(null);

  // Zeitraum-Filter für Pro-Cutter-Auswertung. Default: aktueller Monat
  const thisMonth = monthKey(new Date());
  const [filterMonth,setFilterMonth] = useState("all"); // "all" oder "YYYY-MM"
  // Contentplan-Filter (Projekte-Tab)
  const [projClientFilter,setProjClientFilter] = useState("all");
  const [projStatusFilter,setProjStatusFilter] = useState("all");
  const [projCollapsed,setProjCollapsed] = useState({});
  // Neue Ansicht: nach Kunde, nach Cutter oder nach Woche sortiert
  const [projView,setProjView] = useState("kunde");
  const [projTab,setProjTab] = useState(null);   // ausgewaehlter Kunde bzw. Cutter in der Reiterleiste
  const [unterAuf,setUnterAuf] = useState({});          // aufgeklappte Unterabschnitte
  const [preisVorlagen,setPreisVorlagen] = useState([]);
  const [guthaben,setGuthaben] = useState([]);
  const [zeigePreise,setZeigePreise] = useState(false); // Preisvorlagen-Fenster
  const [zeigeVorschuss,setZeigeVorschuss] = useState(false);
  const [projPreisEditId,setProjPreisEditId] = useState(null);
  const [projPreisDraft,setProjPreisDraft] = useState("");
  const [notizen,setNotizen] = useState([]);
  const [zeileAuf,setZeileAuf] = useState({});     // je Video: Detailbereich aufgeklappt
  const [geldAlle,setGeldAlle] = useState({});     // je Cutter: ganze Liste der fertigen Videos zeigen
  const [restZeigen,setRestZeigen] = useState({}); // je Gruppe: spaeter faellige einblenden
  const [projSuche,setProjSuche] = useState("");
  const [vorschlaege,setVorschlaege] = useState([]);
  const [projEditLinkId,setProjEditLinkId] = useState(null);
  const [projLinkDraft,setProjLinkDraft] = useState("");
  const [editProjectAdmin,setEditProjectAdmin] = useState(null);

  const users = store.get(STORAGE_KEYS.users)||[];
  const cutterUsers = users.filter(u => u.role === "cutter");

  // Preisvorlagen und Guthaben nachladen
  const ladeGeld = () => {
    api.get('cutter-price-templates').then(r=>{ if(Array.isArray(r)) setPreisVorlagen(r); });
    api.get('cutter-balance').then(r=>{ if(r&&r.cutter) setGuthaben(r.cutter); });
  };
  const ladeNotizen = () => { api.get('cutter-project-notes').then(r=>{ if(Array.isArray(r)) setNotizen(r); }); };
  const ladeVorschlaege = () => { api.get('cutter-price-requests').then(r=>{ if(r&&r.vorschlaege) setVorschlaege(r.vorschlaege); }); };
  useEffect(()=>{ ladeNotizen(); ladeVorschlaege(); },[]);
  useEffect(()=>{ ladeGeld(); },[]);
  const vorlagePreis = (typ) => { const v=preisVorlagen.find(x=>x.type===typ); return v?v.price:null; };
  const vorlageSpeichern = async(typ,preis)=>{
    if(!typ)return;
    const da=preisVorlagen.find(x=>x.type===typ);
    if(da) await api.put('cutter-price-templates',{id:da.id,type:typ,price:preis});
    else await api.post('cutter-price-templates',{type:typ,price:preis});
    ladeGeld();
  };
  const vorlageLoeschen = async(id)=>{ await api.del('cutter-price-templates',{id}); ladeGeld(); };
  const vorschussSpeichern = async(cutterId,betrag,datum,notiz)=>{
    if(!cutterId||!betrag)return;
    await api.post('cutter-advances',{cutterId,amount:betrag,date:datum||null,note:notiz||null});
    ladeGeld();
  };

  const saveP = u=>{setProjects(u);};
  const saveI = u=>{setInvoices(u);};
  const saveG = u=>{setGoals(u);};

  // Filter-Helper: prüft ob eine Rechnung im gewählten Zeitraum (teil-)bezahlt wurde
  // Pragmatisch: Wir nehmen paidAt (Datum der Vollbezahlung) oder created_at als Approximation
  const inFilterRange = (inv) => {
    if ((inv.paidTotal || 0) <= 0) return false;
    if (filterMonth === "all") return true;
    return (inv.paidAt || inv.created_at || "").startsWith(filterMonth);
  };

  // Gesamt-Stats über alle Cutter
  const openProjects = projects.filter(p=>p.status!=="done"&&p.status!=="delivered").length;
  const doneThisMonth = projects.filter(p=>p.status==="done"&&(p.completedAt||"").startsWith(thisMonth)).length;
  // Effektiv bezahlt: Status "paid" gilt als voll bezahlt (auch ohne erfasste Teilzahlung),
  // sonst zählen die tatsächlich erfassten Teilzahlungen.
  const effPaid = (i) => i.status === "paid" ? (i.amount||0) : Math.min(i.amount||0, i.paidTotal||0);
  const effOpen = (i) => Math.max(0, (i.amount||0) - effPaid(i));
  const openInvoices = invoices.filter(i => effOpen(i) > 0.01);
  const openInvoiceTotal = openInvoices.reduce((s,i)=>s+effOpen(i), 0);
  const paidInRange = invoices.filter(inFilterRange).reduce((s,i)=>s+effPaid(i), 0);
  const paidAllTime = invoices.reduce((s,i)=>s+effPaid(i), 0);

  // Aggregation pro Cutter im gewählten Zeitraum
  const perCutter = (()=>{
    const map = {};
    for(const inv of invoices){
      if(!inFilterRange(inv)) continue;
      const cId = inv.createdBy || "(unbekannt)";
      const cName = inv.createdByName || cutterUsers.find(u=>u.id===cId)?.name || "Unbekannt";
      if(!map[cId]) map[cId] = {id:cId, name:cName, total:0, count:0};
      map[cId].total += (inv.paidTotal || 0);
      map[cId].count++;
    }
    return Object.values(map).sort((a,b)=>b.total-a.total);
  })();

  // Monatsoptionen: letzte 12 Monate
  const monthOptions = (()=>{
    const arr = [{value:"all", label:"Gesamter Zeitraum"}];
    const now = new Date();
    for(let i=0;i<12;i++){
      const d = new Date(now.getFullYear(), now.getMonth()-i, 1);
      const k = monthKey(d);
      arr.push({value:k, label:d.toLocaleDateString("de",{month:"long",year:"numeric"})});
    }
    return arr;
  })();

  // Project CRUD
  const normLinkA = (l) => { l=(l||"").trim(); if(!l) return ""; if(!/^https?:\/\//i.test(l)) return "https://"+l; return l; };
  const addProject = async()=>{
    if(!pForm.client||!pForm.title)return;
    await api.post('cutter-projects',{...pForm,rawLink:normLinkA(pForm.rawLink),assignedTo:pForm.assignedTo||null});await reload(STORAGE_KEYS.cutter_projects);setProjects(store.get(STORAGE_KEYS.cutter_projects));
    setPForm({client:'',title:'',type:'',deadline:'',postDate:'',rawLink:'',notes:'',status:'open',assignedTo:'',price:''});setShowAddProject(false);ladeGeld();
  };
  const updateProjectStatus = async(id,status)=>{
    const p=projects.find(x=>x.id===id);
    setProjects(prev=>prev.map(x=>x.id===id?{...x,status}:x));
    await api.put('cutter-projects',{...p,id,status});await reload(STORAGE_KEYS.cutter_projects);setProjects(store.get(STORAGE_KEYS.cutter_projects));
    ladeGeld();
  };
  const patchProjectAdmin = async(id,changes)=>{
    const p=projects.find(x=>x.id===id); if(!p) return;
    const merged={...p,...changes};
    setProjects(prev=>prev.map(x=>x.id===id?merged:x));
    await api.put('cutter-projects',{id,client:merged.client,title:merged.title,type:merged.type,deadline:merged.deadline,notes:merged.notes,status:merged.status,rawLink:merged.rawLink,postDate:merged.postDate,assignedTo:merged.assignedTo,price:merged.price});
    await reload(STORAGE_KEYS.cutter_projects);setProjects(store.get(STORAGE_KEYS.cutter_projects));
    ladeGeld();
  };
  // Schnellzuweisung an einen Cutter (Dropdown in der Zeile)
  const assignProjectCutter = async(id, cutterId)=>{
    setProjects(prev=>prev.map(x=>x.id===id?{...x,assignedTo:cutterId||null}:x));
    await api.put('cutter-projects',{assignOnly:true, id, assignedTo:cutterId||null});
    await reload(STORAGE_KEYS.cutter_projects);setProjects(store.get(STORAGE_KEYS.cutter_projects));
  };
  const saveEditProjectAdmin = async()=>{
    if(!editProjectAdmin||!editProjectAdmin.client||!editProjectAdmin.title)return;
    const l=(editProjectAdmin.rawLink||"").trim(); const link=l&&!/^https?:\/\//i.test(l)?"https://"+l:l;
    await patchProjectAdmin(editProjectAdmin.id,{client:editProjectAdmin.client,title:editProjectAdmin.title,type:editProjectAdmin.type,deadline:editProjectAdmin.deadline||null,postDate:editProjectAdmin.postDate||null,rawLink:link,notes:editProjectAdmin.notes,status:editProjectAdmin.status,assignedTo:editProjectAdmin.assignedTo||null,price:(editProjectAdmin.price===''||editProjectAdmin.price===null||editProjectAdmin.price===undefined)?null:parseFloat(String(editProjectAdmin.price).replace(',','.'))});
    setEditProjectAdmin(null);
  };
  const deleteProject = async(id)=>{if(!confirm("Dieses Video wirklich löschen?"))return;await api.del('cutter-projects',{id});await reload(STORAGE_KEYS.cutter_projects);setProjects(store.get(STORAGE_KEYS.cutter_projects));};

  // Invoice CRUD
  const addInvoice = async()=>{
    if(!iForm.client||!iForm.amount)return;
    const res = await api.post('cutter-invoices',{
      ...iForm,
      amount:parseFloat(iForm.amount),
      createdBy: iForm.createdBy || null
    });
    // Datei mit hochladen falls vorhanden
    if(adminPendingFile && res?.id){
      const fd = new FormData();
      fd.append('id', res.id);
      fd.append('file', adminPendingFile);
      try { await fetch('/api/cutter-invoice-upload', {method:'POST', headers:authHeaders(), body:fd}); } catch(e){ /* ignore */ }
    }
    await reload(STORAGE_KEYS.cutter_invoices);setInvoices(store.get(STORAGE_KEYS.cutter_invoices));
    setIForm({client:'',description:'',amount:'',dueDate:'',status:'draft',createdBy:''});
    setAdminPendingFile(null);
    setShowAddInvoice(false);
  };
  // Cutter nachträglich zuweisen
  const assignCutter = async(invoiceId, cutterId) => {
    try {
      await api.put('cutter-invoices', {id: invoiceId, createdBy: cutterId || null});
      await reload(STORAGE_KEYS.cutter_invoices);
      setInvoices(store.get(STORAGE_KEYS.cutter_invoices));
    } catch(e){ alert("Fehler beim Zuweisen: "+e.message); }
  };
  const updateInvoiceStatus = async(id,status)=>{
    await api.put('cutter-invoices',{id,status});await reload(STORAGE_KEYS.cutter_invoices);setInvoices(store.get(STORAGE_KEYS.cutter_invoices));
  };
  const deleteInvoice = async(id)=>{if(!confirm("Diese Rechnung wirklich löschen?"))return;await api.del('cutter-invoices',{id});await reload(STORAGE_KEYS.cutter_invoices);setInvoices(store.get(STORAGE_KEYS.cutter_invoices));};
  const saveEditInvoice = async()=>{
    if(!editInvoice||!editInvoice.client)return;
    await api.put('cutter-invoices',{edit:true,id:editInvoice.id,client:editInvoice.client,description:editInvoice.description,amount:parseFloat(editInvoice.amount)||0,dueDate:editInvoice.dueDate||null,status:editInvoice.status,createdBy:editInvoice.createdBy||null});
    await reload(STORAGE_KEYS.cutter_invoices);setInvoices(store.get(STORAGE_KEYS.cutter_invoices));
    setEditInvoice(null);
  };

  // Goals
  const addGoal = async()=>{if(!goalForm.text)return;await api.post('cutter-goals',{text:goalForm.text,target:parseInt(goalForm.target)||0});await reload(STORAGE_KEYS.cutter_goals);setGoals(store.get(STORAGE_KEYS.cutter_goals));setGoalForm({text:'',target:''});};
  const toggleGoal = async(id)=>{const g=goals.find(x=>x.id===id);await api.put('cutter-goals',{id,current:g?.current||0,done:!g?.done});await reload(STORAGE_KEYS.cutter_goals);setGoals(store.get(STORAGE_KEYS.cutter_goals));};
  const updateGoalProgress = async(id,val)=>{await api.put('cutter-goals',{id,current:parseInt(val)||0,done:false});await reload(STORAGE_KEYS.cutter_goals);setGoals(store.get(STORAGE_KEYS.cutter_goals));};
  const deleteGoal = async(id)=>{await api.del('cutter-goals',{id});await reload(STORAGE_KEYS.cutter_goals);setGoals(store.get(STORAGE_KEYS.cutter_goals));};

  const tabs=[{id:"projects",label:"Arbeit"},{id:"geld",label:"Geld"},{id:"overview",label:"Übersicht"},{id:"open",label:"Offene Rechnungen"},{id:"invoices",label:"Rechnungen"},{id:"goals",label:"Ziele"}];

  return (<div>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24,flexWrap:"wrap",gap:12}}>
      <div><h1 style={{fontSize:26,fontWeight:700,letterSpacing:"-0.02em"}}>Cutter</h1><p style={{color:T.textDim,fontSize:14,marginTop:4}}>Projekte, Rechnungen & Auszahlungen</p></div>
      <div style={{display:"flex",gap:8}}>
        {tab==="projects"&&<Button icon="add" size="sm" onClick={()=>setShowAddProject(true)}>Projekt</Button>}
        {tab==="invoices"&&<Button icon="add" size="sm" onClick={()=>setShowAddInvoice(true)}>Rechnung</Button>}
      </div>
    </div>

    {/* Stats */}
    <div className="stats-row" style={{display:"flex",gap:16,flexWrap:"wrap",marginBottom:24}}>
      <StatCard label="Offene Projekte" value={openProjects} icon="cut" color={T.warning}/>
      <StatCard label="Fertig (Monat)" value={doneThisMonth} icon="check" color={T.success}/>
      <StatCard label="Offene Rechnungen" value={fmtEuro(openInvoiceTotal)} icon="money" color={T.danger}/>
      <StatCard label="Gezahlt gesamt" value={fmtEuro(paidAllTime)} icon="money" color={T.success}/>
    </div>

    {/* Tabs */}
    <div style={{display:"flex",gap:4,marginBottom:20,borderBottom:`1px solid ${T.border}`,paddingBottom:8,overflowX:"auto"}}>
      {tabs.map(t=>(<button key={t.id} onClick={()=>setTab(t.id)} style={{padding:"8px 20px",borderRadius:"8px 8px 0 0",border:"none",fontSize:14,fontWeight:tab===t.id?700:400,background:tab===t.id?T.accentGlow:"transparent",color:tab===t.id?T.accent:T.textMuted,cursor:"pointer",whiteSpace:"nowrap"}}>{t.label}</button>))}
    </div>

    {/* ─── ÜBERSICHT TAB: Pro-Cutter-Auswertung ─── */}
    {tab==="overview"&&<div>
      {/* Zeitraum-Filter */}
      <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:20,marginBottom:20}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap",marginBottom:18}}>
          <div>
            <div style={{fontSize:12,color:T.textDim,marginBottom:6}}>Zeitraum</div>
            <select value={filterMonth} onChange={e=>setFilterMonth(e.target.value)} style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 12px",color:T.text,fontSize:13,fontWeight:600,outline:"none",minWidth:200}}>
              {monthOptions.map(m=>(<option key={m.value} value={m.value}>{m.label}</option>))}
            </select>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:12,color:T.textDim,marginBottom:6}}>Gezahlt im gewählten Zeitraum</div>
            <div style={{fontSize:28,fontWeight:800,color:T.success,fontFamily:"'JetBrains Mono',monospace"}}>{fmtEuro(paidInRange)}</div>
          </div>
        </div>
      </div>

      {/* Pro-Cutter-Tabelle */}
      <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:20}}>
        <h3 style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em",marginBottom:14}}>Auszahlungen pro Cutter</h3>
        {perCutter.length===0?<div style={{color:T.textDim,fontSize:13,textAlign:"center",padding:20}}>Keine bezahlten Rechnungen im gewählten Zeitraum.</div>:
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {perCutter.map((c,i)=>{
              const pct = paidInRange>0 ? (c.total/paidInRange*100) : 0;
              return (<div key={c.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:i<perCutter.length-1?`1px solid ${T.border}`:"none"}}>
                <div style={{minWidth:24,fontSize:12,color:T.textDim,fontWeight:600,textAlign:"right"}}>{i+1}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:14,fontWeight:600}}>{c.name}</div>
                  <div style={{fontSize:11,color:T.textDim,marginTop:2}}>{c.count} bezahlte Rechnung{c.count>1?"en":""}</div>
                  <div style={{marginTop:6,height:5,background:T.bgInput,borderRadius:3,overflow:"hidden"}}>
                    <div style={{width:`${pct}%`,height:"100%",background:T.success,borderRadius:3}}/>
                  </div>
                </div>
                <div style={{textAlign:"right",minWidth:120}}>
                  <div style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em",color:T.success,fontFamily:"'JetBrains Mono',monospace"}}>{c.total.toLocaleString("de",{maximumFractionDigits:0})} €</div>
                  <div style={{fontSize:10,color:T.textDim}}>{pct.toFixed(1)}%</div>
                </div>
              </div>);
            })}
          </div>}
      </div>
    </div>}

    {/* ─── PROJECTS TAB ─── */}
    {/* ═══ ARBEIT ═══ */}
    {tab==="projects"&&(()=>{
      const isDoneP = (p)=>p.status==="done"||p.status==="delivered";
      const normLink = (l)=>{l=(l||"").trim();if(!l)return"";if(!/^https?:\/\//i.test(l))return"https://"+l;return l;};
      const clientsA = Array.from(new Set(projects.map(p=>p.client).filter(Boolean))).sort();
      const q = projSuche.trim().toLowerCase();
      const filteredA = projects.filter(p=>{
        if(projClientFilter!=="all"&&p.client!==projClientFilter)return false;
        if(projStatusFilter==="open"&&isDoneP(p))return false;
        if(projStatusFilter==="done"&&!isDoneP(p))return false;
        if(q && !(`${p.title} ${p.client} ${p.notes||""}`.toLowerCase().includes(q))) return false;
        return true;
      });
      const fmtD=(d)=>{ if(!d||d==="0000-00-00") return null; const dt=new Date(d); return isNaN(dt)?null:dt.toLocaleDateString("de",{day:"2-digit",month:"2-digit"}); };
      const TYPE_LABELS={reel:"Reel",video:"Video",story:"Story",image:"Bild",animation:"Animation",other:"Sonstiges"};
      const cutterNameById = (id)=>cutterUsers.find(u=>u.id===id)?.name;
      const initialen = (name)=>!name?"—":name.split(" ").filter(Boolean).slice(0,2).map(t=>t[0]).join("").toUpperCase();

      const heute0 = new Date(); heute0.setHours(0,0,0,0);
      const tageBis = (d)=>{ if(!d||d==="0000-00-00") return null; const dt=new Date(d); if(isNaN(dt)) return null; dt.setHours(0,0,0,0); return Math.round((dt-heute0)/86400000); };
      const fristText = rfFristText;
      const abschnittVon = (p)=>{
        if(isDoneP(p)) return "fertig";
        const t=tageBis(p.deadline);
        if(t===null) return "ohne";
        if(t<0) return "ueberfaellig";
        if(t===0) return "heute";
        if(t<=7) return "woche";
        if(t<=14) return "naechste";
        return "spaeter";
      };
      const ABSCHNITTE=[
        {id:"ueberfaellig",label:"Überfällig",color:T.danger},
        {id:"heute",label:"Heute fällig",color:T.warning},
        {id:"woche",label:"Diese Woche",color:T.accent},
        {id:"naechste",label:"Nächste Woche",color:T.info},
        {id:"spaeter",label:"Später",color:T.textDim},
        {id:"ohne",label:"Ohne Deadline",color:T.textDim},
        {id:"fertig",label:"Fertig",color:T.success},
      ];
      const KUNDENFARBEN=["#5b7fa6","#5f8a72","#9a7b52","#7d6b96","#95647a","#4f8189","#96704e","#6f8158"];
      const kundenFarbe=(name)=>{ const i=clientsA.indexOf(name); return KUNDENFARBEN[(i<0?0:i)%KUNDENFARBEN.length]; };
      const sortNachDatum=(a,b)=>{ const da=a.deadline||a.postDate||"9999",db=b.deadline||b.postDate||"9999"; return da<db?-1:da>db?1:0; };

      const offeneAlle = projects.filter(p=>!isDoneP(p));
      const ueberfaellig = offeneAlle.filter(p=>{const t=tageBis(p.deadline);return t!==null&&t<0;}).sort(sortNachDatum);
      const heuteFaellig = offeneAlle.filter(p=>tageBis(p.deadline)===0).sort(sortNachDatum);
      const dieseWoche = offeneAlle.filter(p=>{const t=tageBis(p.deadline);return t!==null&&t>0&&t<=7;}).sort(sortNachDatum);
      const agendaListe = [...ueberfaellig,...heuteFaellig,...dieseWoche].slice(0,3);

      return (<>
      {/* Hinzufügen */}
      <Modal open={showAddProject} onClose={()=>setShowAddProject(false)} title="Video anlegen" width={520}>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <Input label="Kunde" value={pForm.client} onChange={e=>setPForm({...pForm,client:e.target.value})} placeholder="z.B. Fleck" list="admin-cutter-clients"/>
          <datalist id="admin-cutter-clients">{clientsA.map(c=><option key={c} value={c}/>)}</datalist>
          <Input label="Titel des Videos" value={pForm.title} onChange={e=>setPForm({...pForm,title:e.target.value})} placeholder="z.B. Haustour Gutshof 2"/>
          <div className="cut-form-2" style={{display:"flex",gap:12}}>
            <div style={{flex:1}}>
              <Select label="Art" options={[{value:"",label:"— Auswählen —"},{value:"reel",label:"Reel"},{value:"video",label:"Video"},{value:"story",label:"Story"},{value:"image",label:"Bildbearbeitung"},{value:"animation",label:"Animation"},{value:"other",label:"Sonstiges"}]} value={pForm.type}
                onChange={e=>{const t=e.target.value; const vp=vorlagePreis(t); setPForm({...pForm,type:t,price:(pForm.price===""&&vp!==null)?String(vp):pForm.price});}}/>
            </div>
            <div style={{width:130}}><Input label="Preis (€)" value={pForm.price} onChange={e=>setPForm({...pForm,price:e.target.value})} placeholder={vorlagePreis(pForm.type)!==null?String(vorlagePreis(pForm.type)):"z.B. 25"}/></div>
          </div>
          <div className="cut-form-2" style={{display:"flex",gap:12}}>
            <div style={{flex:1}}><Input label="Postdatum" type="date" value={pForm.postDate} onChange={e=>setPForm({...pForm,postDate:e.target.value})}/></div>
            <div style={{flex:1}}><Input label="Schnitt bis" type="date" value={pForm.deadline} onChange={e=>setPForm({...pForm,deadline:e.target.value})}/></div>
          </div>
          <Input label="Rohvideo-Link" value={pForm.rawLink} onChange={e=>setPForm({...pForm,rawLink:e.target.value})} placeholder="https://drive.google.com/..."/>
          <Input label="Briefing" value={pForm.notes} onChange={e=>setPForm({...pForm,notes:e.target.value})} placeholder="Kurz worum es geht"/>
          <Select label="Cutter" options={[{value:"",label:"— Niemand —"}].concat(cutterUsers.map(u=>({value:u.id,label:u.name})))} value={pForm.assignedTo||""} onChange={e=>setPForm({...pForm,assignedTo:e.target.value})}/>
          <Button icon="check" onClick={addProject} style={{marginTop:8}}>Anlegen</Button>
        </div>
      </Modal>

      {/* ─── Agenda ─── */}
      <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:14,padding:"15px 18px",marginBottom:14,boxShadow:T.shadow}}>
        <div className="cut-agenda-kopf" style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap",marginBottom:agendaListe.length?13:0}}>
          <span style={{fontSize:14,fontWeight:700}}>{new Date().toLocaleDateString("de",{weekday:"long",day:"2-digit",month:"long"})}</span>
          <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
            {ueberfaellig.length>0&&<span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:12,fontWeight:700,color:T.danger,background:`${T.danger}16`,borderRadius:20,padding:"4px 11px"}}>{ueberfaellig.length} überfällig</span>}
            <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:12,fontWeight:700,color:T.warning,background:`${T.warning}16`,borderRadius:20,padding:"4px 11px"}}>{heuteFaellig.length} heute</span>
            <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:12,fontWeight:700,color:T.textMuted,background:T.bgInput,borderRadius:20,padding:"4px 11px"}}>{dieseWoche.length} diese Woche</span>
          </div>
        </div>
        {agendaListe.length===0
          ? <div style={{fontSize:13,color:T.textDim}}>Diese Woche ist nichts fällig.</div>
          : <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {agendaListe.map(p=>{ const t=tageBis(p.deadline); const cn=cutterNameById(p.assignedTo);
                return (<div key={p.id} className="cut-agenda-zeile" style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{width:8,height:8,borderRadius:"50%",background:kundenFarbe(p.client),flexShrink:0}}/>
                  <span className="cut-agenda-titel" style={{fontSize:13.5,fontWeight:500,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:"1 1 auto"}}>{p.title}</span>
                  <span className="cut-nur-breit" style={{fontSize:12.5,color:T.textDim,flexShrink:0}}>{p.client}</span>
                  <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:12.5,fontWeight:700,color:t<0?T.danger:T.textMuted,flexShrink:0,whiteSpace:"nowrap"}}>{rfFristText(p.deadline,false).haupt}</span>
                  <span className="cut-nur-breit" style={{fontSize:12.5,color:cn?T.text:T.textDim,flexShrink:0}}>{cn||"niemand"}</span>
                </div>);
              })}
            </div>}
      </div>

      {/* ─── Ansicht und Suche ─── */}
      <div className="cut-steuer" style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
        <div style={{display:"flex",gap:2,background:T.bgInput,padding:3,borderRadius:10,border:`1px solid ${T.border}`}}>
          {[{v:"kunde",l:"Kunde"},{v:"cutter",l:"Cutter"},{v:"woche",l:"Fälligkeit"}].map(f=>(
            <button key={f.v} onClick={()=>{setProjView(f.v);setProjTab(null);}} style={{padding:"7px 14px",borderRadius:8,border:"none",fontSize:12.5,fontWeight:projView===f.v?700:500,background:projView===f.v?T.bgCard:"transparent",color:projView===f.v?T.text:T.textDim,cursor:"pointer",boxShadow:projView===f.v?T.shadow:"none"}}>{f.l}</button>
          ))}
        </div>
        <input value={projSuche} onChange={e=>setProjSuche(e.target.value)} placeholder="Video oder Kunde suchen" style={{flex:"1 1 180px",minWidth:0,boxSizing:"border-box",height:38,padding:"0 13px",background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:10,color:T.text,fontSize:13.5,outline:"none"}}/>
        <button onClick={()=>setProjStatusFilter(projStatusFilter==="done"?"all":"done")} style={{padding:"9px 14px",background:projStatusFilter==="done"?T.accentGlow:T.bgCard,border:`1px solid ${projStatusFilter==="done"?T.accent:T.border}`,borderRadius:10,color:projStatusFilter==="done"?T.accent:T.textMuted,fontSize:12.5,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>Nur fertige</button>
      </div>

      {(()=>{
        // ── Eine Videozeile: schmal. Alles Weitere erscheint beim Aufklappen. ──
        const Zeile = (p, zeigeKunde)=>{
          const done=isDoneP(p); const t=tageBis(p.deadline); const overdue=t!==null&&t<0&&!done;
          const cn=cutterNameById(p.assignedTo);
          const meineNotizen=notizen.filter(n=>n.projectId===p.id);
          const offeneNotizen=meineNotizen.filter(n=>!n.done).length;
          const auf=!!zeileAuf[p.id];
          const st=PROJECT_STATUSES.find(s=>s.id===p.status)||PROJECT_STATUSES[0];
          const vorschlag=vorschlaege.find(v=>v.projectId===p.id&&(v.status==="offen"||v.status==="gegenvorschlag"));
          const fr=fristText(p.deadline, done);
          return (<div key={p.id}>
            <div className="cut-zeile" style={{position:"relative",display:"flex",alignItems:"center",gap:14,padding:"11px 18px",borderBottom:auf?"none":`1px solid ${T.border}`,background:auf?T.bgCard2:"transparent"}}>
              {overdue&&<span style={{position:"absolute",left:0,top:0,bottom:0,width:2,background:T.danger}}/>}
              <button onClick={()=>updateProjectStatus(p.id,done?"open":"done")} title={done?"Wieder offen":"Als fertig markieren"} style={{width:18,height:18,flexShrink:0,borderRadius:6,border:`1.5px solid ${done?T.accent:T.borderStrong}`,background:done?T.accent:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0}}>
                {done&&<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
              </button>
              <button onClick={()=>setZeileAuf(z=>z[p.id]?{}:{[p.id]:true})} className="cut-zeile-haupt" style={{flex:"1 1 auto",minWidth:0,display:"block",background:"none",border:"none",padding:0,cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                <span style={{display:"flex",alignItems:"center",gap:7,minWidth:0}}>
                  {zeigeKunde&&<span style={{width:7,height:7,borderRadius:"50%",background:kundenFarbe(p.client),flexShrink:0}}/>}
                  <span style={{fontSize:14,fontWeight:500,color:done?T.textDim:T.text,textDecoration:done?"line-through":"none",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.title}</span>
                  {offeneNotizen>0&&<span title={`${offeneNotizen} offene Notizen`} style={{flexShrink:0,fontSize:10.5,fontWeight:600,color:T.textMuted,background:T.bgHover,borderRadius:5,padding:"2px 6px"}}>{offeneNotizen}</span>}
                  {vorschlag&&<span title="Preisvorschlag läuft" style={{flexShrink:0,fontSize:10.5,fontWeight:600,color:T.accent,background:T.accentGlow,borderRadius:5,padding:"2px 6px"}}>Preis?</span>}
                </span>
                <span className="cut-meta" style={{display:"flex",gap:9,fontSize:11.5,color:T.textDim,marginTop:3,paddingLeft:zeigeKunde?14:0}}>
                  <span>{zeigeKunde?p.client:(TYPE_LABELS[p.type]||"—")}</span>
                  {zeigeKunde&&<span>{TYPE_LABELS[p.type]||"—"}</span>}
                  <span>{p.price!=null?rfEuroGenau(p.price):"kein Preis"}</span>
                </span>
              </button>
              <span className="cut-frist" style={{width:122,flexShrink:0,textAlign:"right"}}>
                <span style={{display:"block",fontSize:13,fontWeight:500,color:fr.spaet?T.danger:done?T.textDim:T.text}}>{fr.haupt}</span>
                {fr.sub&&<span className="cut-nur-breit" style={{display:"block",fontSize:11.5,color:T.textDim,marginTop:2}}>{fr.sub}</span>}
              </span>
              <span className="cut-nur-breit" style={{width:88,flexShrink:0,display:"flex",alignItems:"center",gap:7,fontSize:12,color:T.textMuted}}>
                <span style={{width:7,height:7,borderRadius:"50%",background:st.color,flexShrink:0}}/>{st.label}
              </span>
              <span title={cn||"niemand zugewiesen"} style={{width:26,height:26,borderRadius:"50%",flexShrink:0,background:T.bgHover,color:cn?T.textMuted:T.textDim,fontSize:10,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center"}}>{initialen(cn)}</span>
            </div>

            {auf&&<div className="cut-detail" style={{display:"flex",gap:24,padding:"4px 18px 16px 49px",background:T.bgCard2,borderBottom:`1px solid ${T.border}`}}>
              <div className="cut-detail-notizen" style={{flex:"1 1 320px",minWidth:0}}>
                <div style={{fontSize:10.5,fontWeight:700,letterSpacing:"0.05em",color:T.textDim,marginBottom:8}}>NOTIZEN</div>
                <NotizListe projectId={p.id} notizen={notizen} onAendern={ladeNotizen} flach/>
              </div>
              <div className="cut-detail-felder" style={{flex:"1 1 320px",minWidth:0}}>
                <div style={{fontSize:10.5,fontWeight:700,letterSpacing:"0.05em",color:T.textDim,marginBottom:8}}>DETAILS</div>
                <div className="cut-detail-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:11}}>
                  <div>
                    <div style={{fontSize:11,color:T.textMuted,marginBottom:4}}>Cutter</div>
                    <select value={p.assignedTo||""} onChange={e=>assignProjectCutter(p.id,e.target.value)} style={{boxSizing:"border-box",width:"100%",height:34,padding:"0 8px",background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,fontSize:12.5,outline:"none",cursor:"pointer"}}>
                      <option value="">Niemand</option>
                      {cutterUsers.map(u=>(<option key={u.id} value={u.id}>{u.name}</option>))}
                    </select>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:T.textMuted,marginBottom:4}}>Status</div>
                    <select value={p.status} onChange={e=>patchProjectAdmin(p.id,{status:e.target.value})} style={{boxSizing:"border-box",width:"100%",height:34,padding:"0 8px",background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,fontSize:12.5,outline:"none",cursor:"pointer"}}>
                      {PROJECT_STATUSES.map(s=>(<option key={s.id} value={s.id}>{s.label}</option>))}
                    </select>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:T.textMuted,marginBottom:4}}>Preis</div>
                    <input defaultValue={p.price!=null?String(p.price):""} placeholder={vorlagePreis(p.type)!=null?String(vorlagePreis(p.type)):"€"}
                      onBlur={e=>{const w=e.target.value.trim(); patchProjectAdmin(p.id,{price:w===""?null:parseFloat(w.replace(",","."))});}}
                      style={{boxSizing:"border-box",width:"100%",height:34,padding:"0 8px",background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,fontSize:12.5,outline:"none",fontFamily:"'JetBrains Mono',monospace"}}/>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:T.textMuted,marginBottom:4}}>Schnitt bis</div>
                    <input type="date" defaultValue={p.deadline&&p.deadline!=="0000-00-00"?p.deadline.slice(0,10):""} onBlur={e=>patchProjectAdmin(p.id,{deadline:e.target.value||null})} style={{boxSizing:"border-box",width:"100%",height:34,padding:"0 8px",background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,fontSize:12.5,outline:"none"}}/>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:T.textMuted,marginBottom:4}}>Post am</div>
                    <input type="date" defaultValue={p.postDate&&p.postDate!=="0000-00-00"?p.postDate.slice(0,10):""} onBlur={e=>patchProjectAdmin(p.id,{postDate:e.target.value||null})} style={{boxSizing:"border-box",width:"100%",height:34,padding:"0 8px",background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,fontSize:12.5,outline:"none"}}/>
                  </div>
                  <div style={{display:"flex",alignItems:"flex-end"}}>
                    {p.rawLink
                      ? <a href={p.rawLink} target="_blank" rel="noopener noreferrer" style={{boxSizing:"border-box",width:"100%",height:34,display:"flex",alignItems:"center",justifyContent:"center",gap:6,background:T.infoGlow,borderRadius:8,fontSize:12.5,fontWeight:700,color:T.info,textDecoration:"none"}}><IconPlay/> Rohvideo</a>
                      : <input placeholder="Rohvideo-Link" onBlur={e=>{const v=e.target.value.trim(); if(v)patchProjectAdmin(p.id,{rawLink:normLink(v)});}} style={{boxSizing:"border-box",width:"100%",height:34,padding:"0 8px",background:T.bgCard,border:`1px dashed ${T.borderStrong}`,borderRadius:8,color:T.text,fontSize:12.5,outline:"none"}}/>}
                  </div>
                </div>
                {p.notes&&<div style={{fontSize:12.5,color:T.textMuted,lineHeight:1.5,marginBottom:10}}>{p.notes}</div>}
                <div style={{display:"flex",gap:14,flexWrap:"wrap"}}>
                  <button onClick={()=>setEditProjectAdmin({...p,deadline:p.deadline&&p.deadline!=="0000-00-00"?p.deadline.slice(0,10):"",postDate:p.postDate&&p.postDate!=="0000-00-00"?p.postDate.slice(0,10):"",rawLink:p.rawLink||"",notes:p.notes||"",price:p.price??""})} style={{background:"none",border:"none",padding:0,fontFamily:"inherit",fontSize:12.5,color:T.textMuted,cursor:"pointer",textDecoration:"underline"}}>Briefing bearbeiten</button>
                  <button onClick={()=>deleteProject(p.id)} style={{background:"none",border:"none",padding:0,fontFamily:"inherit",fontSize:12.5,color:T.danger,cursor:"pointer",textDecoration:"underline"}}>Video löschen</button>
                </div>
              </div>
            </div>}
          </div>);
        };

        // Gruppen bilden
        let gruppen=[];
        if(projView==="kunde"){
          const g={}; filteredA.forEach(p=>{const k=p.client||"(Ohne Kunde)";(g[k]=g[k]||[]).push(p);});
          gruppen=Object.keys(g).sort().map(k=>({key:k,titel:k,farbe:kundenFarbe(k),items:g[k],zeigeKunde:false}));
        } else if(projView==="cutter"){
          const g={}; filteredA.forEach(p=>{const k=cutterNameById(p.assignedTo)||"Niemand zugewiesen";(g[k]=g[k]||[]).push(p);});
          gruppen=Object.keys(g).sort((a,b)=>a==="Niemand zugewiesen"?1:b==="Niemand zugewiesen"?-1:a.localeCompare(b))
            .map(k=>({key:k,titel:k,farbe:k==="Niemand zugewiesen"?T.textDim:T.info,items:g[k],zeigeKunde:true}));
        } else {
          const g={}; filteredA.forEach(p=>{const a=abschnittVon(p);(g[a]=g[a]||[]).push(p);});
          gruppen=ABSCHNITTE.filter(a=>g[a.id]&&g[a.id].length).map(a=>({key:a.id,titel:a.label,farbe:a.color,items:g[a.id],zeigeKunde:true,flach:true}));
        }

        if(gruppen.length===0) return <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow}}><Empty icon="cut" title="Keine Videos" desc="Leg ein neues Video an"/></div>;

        // ── Reiterleiste: ein Feld je Kunde bzw. Cutter, Klick springt hinein ──
        const mitReitern = projView!=="woche" && gruppen.length>1;
        const aktiv = mitReitern && gruppen.find(g=>g.key===projTab) ? projTab : null;
        const zaehle = (items)=>({
          offen: items.filter(p=>!isDoneP(p)).length,
          ueber: items.filter(p=>{const t=tageBis(p.deadline); return !isDoneP(p)&&t!==null&&t<0;}).length,
          bald: items.filter(p=>{const t=tageBis(p.deadline); return !isDoneP(p)&&t!==null&&t>=0&&t<=7;}).length,
        });
        const reiterStil = (an)=>({
          flexShrink:0, minWidth:132, textAlign:"left", cursor:"pointer", fontFamily:"inherit",
          padding:"9px 14px", borderRadius:11, whiteSpace:"nowrap",
          background: an?T.accent:T.bgCard, border:`1px solid ${an?T.accent:T.border}`,
          color: an?"#fff":T.text, boxShadow: an?"none":T.shadow, transition:"background .12s",
        });
        const gesamt = zaehle(filteredA);
        const Leiste = mitReitern ? (
          <div className="cut-reiter" style={{display:"flex",gap:9,overflowX:"auto",paddingBottom:6,marginBottom:14}}>
            <button onClick={()=>setProjTab(null)} style={reiterStil(!aktiv)}>
              <span style={{display:"block",fontSize:13.5,fontWeight:600}}>Alle</span>
              <span style={{display:"block",fontSize:11.5,marginTop:2,opacity:!aktiv?0.85:1,color:!aktiv?"#fff":T.textDim}}>{gesamt.offen} offen</span>
            </button>
            {gruppen.map(g=>{
              const an = aktiv===g.key; const z = zaehle(g.items);
              return (<button key={g.key} onClick={()=>setProjTab(an?null:g.key)} style={reiterStil(an)}>
                <span style={{display:"flex",alignItems:"center",gap:7}}>
                  <span style={{width:7,height:7,borderRadius:"50%",background:an?"#fff":g.farbe,flexShrink:0}}/>
                  <span style={{fontSize:13.5,fontWeight:600,maxWidth:150,overflow:"hidden",textOverflow:"ellipsis"}}>{g.titel}</span>
                </span>
                <span style={{display:"flex",alignItems:"center",gap:8,fontSize:11.5,marginTop:3,paddingLeft:14}}>
                  <span style={{opacity:an?0.85:1,color:an?"#fff":T.textDim}}>{z.offen} offen</span>
                  {z.ueber>0&&<span style={{color:an?"#fff":T.danger,fontWeight:600}}>{z.ueber} drüber</span>}
                </span>
              </button>);
            })}
          </div>
        ) : null;

        const sichtbar = aktiv ? gruppen.filter(g=>g.key===aktiv) : gruppen;

        return (<>{Leiste}{sichtbar.map(gr=>{
          const gDone=gr.items.filter(isDoneP).length;
          // Wer einen Kunden auswaehlt, will hineinsehen, also nicht zugeklappt.
          const isCol=aktiv?false:projCollapsed[gr.key];
          const offeneSumme=gr.items.filter(p=>!isDoneP(p)).reduce((s,p)=>s+(Number(p.price)||0),0);
          const grUeber=gr.items.filter(p=>{const t=tageBis(p.deadline);return !isDoneP(p)&&t!==null&&t<0;}).length;
          // Unterabschnitte
          // Drei Koerbe: was in dieser Woche ansteht, was spaeter kommt, was erledigt ist.
          // Eigene Zwischenkoepfe braucht es nicht mehr, die Frist steht in Worten in der Zeile.
          const jetztListe=gr.items.filter(p=>{const t=tageBis(p.deadline); return !isDoneP(p)&&t!==null&&t<=7;}).sort(sortNachDatum);
          const spaeterListe=gr.items.filter(p=>{const t=tageBis(p.deadline); return !isDoneP(p)&&(t===null||t>7);}).sort(sortNachDatum);
          const fertigeListe=gr.items.filter(isDoneP).sort(sortNachDatum);
          const restAuf=!!restZeigen[gr.key];
          // Wer gezielt nach fertigen Videos filtert, will sie auch sehen
          const fertigAuf=projStatusFilter==="done" ? restZeigen[gr.key+"__fertig"]!==false : !!restZeigen[gr.key+"__fertig"];

          return (<div key={gr.key} style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:14,boxShadow:T.shadow,marginBottom:13,overflow:"hidden"}}>
            <div onClick={()=>{ if(aktiv) setProjTab(null); else setProjCollapsed(c=>({...c,[gr.key]:!c[gr.key]})); }} title={aktiv?"Zurück zu allen":undefined} className="cut-gruppe-kopf" style={{display:"flex",alignItems:"center",gap:11,padding:"13px 18px",cursor:"pointer",borderBottom:isCol?"none":`1px solid ${T.border}`,background:T.bgCard2}}>
              <span style={{color:T.textDim,fontSize:11,transform:(!aktiv&&isCol)?"rotate(-90deg)":"none",transition:"transform 0.15s"}}>{aktiv?"←":"▼"}</span>
              <span style={{width:10,height:10,borderRadius:"50%",background:gr.farbe,flexShrink:0}}/>
              <span style={{fontSize:15,fontWeight:600,minWidth:0,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{gr.titel}</span>
              <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:12,color:T.textDim,flexShrink:0}}>{gDone} / {gr.items.length}</span>
              {grUeber>0&&<span style={{fontSize:11.5,fontWeight:600,color:T.danger,flexShrink:0}}>{grUeber} überfällig</span>}
              {offeneSumme>0&&<span className="cut-nur-breit" style={{marginLeft:"auto",fontFamily:"'JetBrains Mono',monospace",fontSize:12,color:T.textDim,whiteSpace:"nowrap"}}>{rfEuroGenau(offeneSumme)} offen</span>}
              <span className="cut-nur-breit" style={{width:96,height:4,background:T.bgHover,borderRadius:2,overflow:"hidden",flexShrink:0,marginLeft:offeneSumme>0?0:"auto"}}><span style={{display:"block",height:"100%",width:`${gr.items.length>0?gDone/gr.items.length*100:0}%`,background:T.accent,borderRadius:2}}/></span>
            </div>
            {!isCol && (gr.flach
              ? gr.items.sort(sortNachDatum).map(p=>Zeile(p,true))
              : <div>
                  {jetztListe.map(p=>Zeile(p,gr.zeigeKunde))}
                  {jetztListe.length===0&&<div style={{padding:"15px 18px",fontSize:13,color:T.textDim}}>Diese Woche nichts fällig.</div>}
                  {restAuf&&spaeterListe.map(p=>Zeile(p,gr.zeigeKunde))}
                  {fertigAuf&&fertigeListe.map(p=>Zeile(p,gr.zeigeKunde))}
                  {(spaeterListe.length>0||fertigeListe.length>0)&&<div style={{display:"flex",gap:18,flexWrap:"wrap",padding:"11px 18px",borderTop:`1px solid ${T.border}`}}>
                    {spaeterListe.length>0&&<button onClick={()=>setRestZeigen(r=>({...r,[gr.key]:!restAuf}))} style={{background:"none",border:"none",padding:0,fontFamily:"inherit",fontSize:12,fontWeight:600,color:T.textMuted,cursor:"pointer"}}>{restAuf?"Später ausblenden":`Später fällig · ${spaeterListe.length}`}</button>}
                    {fertigeListe.length>0&&<button onClick={()=>setRestZeigen(r=>({...r,[gr.key+"__fertig"]:!fertigAuf}))} style={{background:"none",border:"none",padding:0,fontFamily:"inherit",fontSize:12,fontWeight:600,color:T.textDim,cursor:"pointer"}}>{fertigAuf?"Fertige ausblenden":`Fertig · ${fertigeListe.length}`}</button>}
                  </div>}
                </div>)}
          </div>);
        })}</>);
      })()}

      {/* Bearbeiten-Fenster */}
      <Modal open={!!editProjectAdmin} onClose={()=>setEditProjectAdmin(null)} title="Video bearbeiten" width={520}>
        {editProjectAdmin&&<div style={{display:"flex",flexDirection:"column",gap:14}}>
          <Input label="Kunde" value={editProjectAdmin.client} onChange={e=>setEditProjectAdmin({...editProjectAdmin,client:e.target.value})} list="admin-cutter-clients-edit"/>
          <datalist id="admin-cutter-clients-edit">{clientsA.map(c=><option key={c} value={c}/>)}</datalist>
          <Input label="Titel des Videos" value={editProjectAdmin.title} onChange={e=>setEditProjectAdmin({...editProjectAdmin,title:e.target.value})}/>
          <div className="cut-form-2" style={{display:"flex",gap:12}}>
            <div style={{flex:1}}>
              <Select label="Art" options={[{value:"",label:"— Auswählen —"},{value:"reel",label:"Reel"},{value:"video",label:"Video"},{value:"story",label:"Story"},{value:"image",label:"Bildbearbeitung"},{value:"animation",label:"Animation"},{value:"other",label:"Sonstiges"}]} value={editProjectAdmin.type||""}
                onChange={e=>{const t=e.target.value; const vp=vorlagePreis(t); setEditProjectAdmin({...editProjectAdmin,type:t,price:(editProjectAdmin.price===null||editProjectAdmin.price===""||editProjectAdmin.price===undefined)&&vp!==null?String(vp):editProjectAdmin.price});}}/>
            </div>
            <div style={{width:130}}><Input label="Preis (€)" value={editProjectAdmin.price??""} onChange={e=>setEditProjectAdmin({...editProjectAdmin,price:e.target.value})}/></div>
          </div>
          <Input label="Briefing" value={editProjectAdmin.notes||""} onChange={e=>setEditProjectAdmin({...editProjectAdmin,notes:e.target.value})}/>
          <Input label="Rohvideo-Link" value={editProjectAdmin.rawLink||""} onChange={e=>setEditProjectAdmin({...editProjectAdmin,rawLink:e.target.value})}/>
          <Button icon="check" onClick={saveEditProjectAdmin} style={{marginTop:8}}>Speichern</Button>
        </div>}
      </Modal>
      </>);
    })()}

    {/* ═══ GELD ═══ */}
    {tab==="geld"&&(()=>{
      const istFertig=(p)=>p.status==="done"||p.status==="delivered";
      const fertigeVon=(cid)=>projects.filter(p=>p.assignedTo===cid&&istFertig(p)&&p.price!=null)
        .sort((a,b)=>{const da=a.deadline||a.postDate||"",db=b.deadline||b.postDate||"";return da<db?1:-1;});
      const fmtD=(d)=>{ if(!d||d==="0000-00-00") return "—"; const dt=new Date(d); return isNaN(dt)?"—":dt.toLocaleDateString("de",{day:"2-digit",month:"2-digit"}); };
      const initialen=(name)=>!name?"—":name.split(" ").filter(Boolean).slice(0,2).map(t=>t[0]).join("").toUpperCase();
      return (<>
        <PreisVorlagenModal open={zeigePreise} onClose={()=>setZeigePreise(false)} vorlagen={preisVorlagen} onSpeichern={vorlageSpeichern} onLoeschen={vorlageLoeschen}/>
        <VorschussModal open={zeigeVorschuss} onClose={()=>setZeigeVorschuss(false)} cutter={cutterUsers} onSpeichern={vorschussSpeichern} onNeuLaden={ladeGeld}/>

        <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap"}}>
          <Button onClick={()=>setZeigeVorschuss(true)}>Vorschuss eintragen</Button>
          <Button variant="secondary" onClick={()=>setZeigePreise(true)}>Preisvorlagen</Button>
        </div>
        <p style={{margin:"0 0 18px",fontSize:13.5,color:T.textMuted,lineHeight:1.55,maxWidth:760}}>Jeder Cutter bekommt vorab eine Summe. Sobald ein Video fertig ist, zieht sein Preis automatisch davon ab. Hier siehst du, wie viel Arbeit noch offen ist und ab wann du nachzahlen musst.</p>

        {guthaben.length===0
          ? <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow}}><Empty icon="money" title="Noch keine Cutter" desc="Sobald ein Nutzer die Rolle Cutter hat, erscheint er hier"/></div>
          : <div style={{display:"flex",flexDirection:"column",gap:16}}>
              <div className="cut-geld-karten" style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,280px),1fr))",gap:14}}>
                {guthaben.map(g=>{
                  const minus=g.rest<0;
                  const anteil=g.vorschuss>0?Math.min(100,Math.max(0,(g.verbraucht/g.vorschuss)*100)):(g.verbraucht>0?100:0);
                  return (<div key={g.cutterId} style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 20px",boxShadow:T.shadow}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
                      <span style={{width:32,height:32,borderRadius:"50%",background:minus?`${T.danger}18`:T.infoGlow,color:minus?T.danger:T.info,fontSize:11.5,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{initialen(g.name)}</span>
                      <span style={{fontSize:15.5,fontWeight:700,minWidth:0,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{g.name}</span>
                    </div>
                    <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:31,fontWeight:700,lineHeight:1,color:minus?T.danger:T.text}}>{rfEuroGenau(Math.abs(g.rest))}</div>
                    <div style={{fontSize:12.5,marginTop:6,color:minus?T.danger:T.textMuted,fontWeight:minus?700:400,lineHeight:1.45}}>{minus?"schuldest du ihm, Vorschuss ist aufgebraucht":"arbeitet er noch ab"}</div>
                    <div style={{height:7,background:T.bgHover,borderRadius:4,overflow:"hidden",margin:"15px 0 11px"}}><span style={{display:"block",height:"100%",width:`${anteil}%`,background:minus?T.danger:T.success}}/></div>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12.5,color:T.textMuted,gap:10}}>
                      <span>{rfEuroGenau(g.vorschuss)} Vorschuss</span>
                      <span style={{fontFamily:"'JetBrains Mono',monospace"}}>{rfEuroGenau(g.verbraucht)} abgearbeitet</span>
                    </div>
                    <div style={{borderTop:`1px solid ${T.border}`,marginTop:13,paddingTop:11,display:"flex",justifyContent:"space-between",fontSize:12.5,color:T.textDim,gap:10}}>
                      <span>{g.anzahlFertig} fertig</span>
                      <span>{g.anzahlOffen} offen, Wert {rfEuroGenau(g.offenWert)}</span>
                    </div>
                  </div>);
                })}
              </div>

              {/* Woraus sich das zusammensetzt */}
              {guthaben.map(g=>{
                const liste=fertigeVon(g.cutterId);
                if(!liste.length) return null;
                const alle=!!geldAlle[g.cutterId];
                const sicht=alle?liste:liste.slice(0,12);
                return (<div key={"l"+g.cutterId} style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:14,boxShadow:T.shadow,overflow:"hidden"}}>
                  <div style={{padding:"14px 18px",borderBottom:`1px solid ${T.border}`,fontSize:14.5,fontWeight:600}}>Fertige Videos von {g.name}</div>
                  {sicht.map((p,i)=>(
                    <div key={p.id} className="cut-geld-zeile" style={{display:"flex",alignItems:"center",gap:12,padding:"10px 18px",borderBottom:i<sicht.length-1?`1px solid ${T.border}`:"none"}}>
                      <span style={{flex:"1 1 auto",minWidth:0,fontSize:13.5,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.title}</span>
                      <span className="cut-nur-breit" style={{fontSize:12,color:T.textDim,width:90,flexShrink:0}}>{p.client}</span>
                      <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:12.5,color:T.textDim,width:52,textAlign:"right",flexShrink:0}}>{fmtD(p.deadline||p.postDate)}</span>
                      <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:13,fontWeight:600,width:74,textAlign:"right",flexShrink:0,whiteSpace:"nowrap"}}>{rfEuroGenau(p.price)}</span>
                    </div>
                  ))}
                  {liste.length>12&&<button onClick={()=>setGeldAlle(a=>({...a,[g.cutterId]:!alle}))} style={{width:"100%",background:"none",border:"none",borderTop:`1px solid ${T.border}`,padding:"11px 18px",fontFamily:"inherit",fontSize:12.5,fontWeight:600,color:T.textMuted,textAlign:"left",cursor:"pointer"}}>
                    {alle?"Weniger anzeigen":`Alle ${liste.length} anzeigen`}
                  </button>}
                  <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 18px",background:T.bgCard2,borderTop:`1px solid ${T.border}`}}>
                    <span style={{flex:1,fontSize:13,fontWeight:600}}>Summe ({liste.length} Videos)</span>
                    <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:14,fontWeight:600,whiteSpace:"nowrap"}}>{rfEuroGenau(g.verbraucht)}</span>
                  </div>
                </div>);
              })}
            </div>}
      </>);
    })()}

    {tab==="open"&&<div>
      <div style={{background:`${T.warning}11`,border:`1px solid ${T.warning}40`,borderRadius:12,padding:14,marginBottom:18,display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <div style={{fontSize:13,color:T.text}}>
          <strong>{openInvoices.length}</strong> Rechnung{openInvoices.length===1?"":"en"} mit offenem Restbetrag
        </div>
        <div style={{fontSize:20,fontWeight:800,color:T.warning,fontFamily:"'JetBrains Mono',monospace"}}>
          {openInvoiceTotal.toLocaleString("de",{minimumFractionDigits:2,maximumFractionDigits:2})} €
        </div>
      </div>
      <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,overflow:"auto",WebkitOverflowScrolling:"touch"}}>
        {openInvoices.length===0?<Empty icon="check" title="Alles bezahlt!" desc="Keine offenen Rechnungen mehr."/>:(
          <table style={{width:"100%",borderCollapse:"collapse",minWidth:900}}>
            <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>{["Cutter","Kunde","Beschreibung","Rechnung","Bezahlt","Noch offen","Fällig",""].map(h=>(<th key={h} style={{padding:"12px 12px",textAlign:"left",fontSize:11,fontWeight:600,color:T.textDim,textTransform:"uppercase"}}>{h}</th>))}</tr></thead>
            <tbody>{openInvoices.sort((a,b)=>{
              // Älteste/dringendste zuerst: nach dueDate, dann nach erstellt
              const dA = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
              const dB = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
              return dA-dB;
            }).map(inv=>{
              const isOverdue = inv.dueDate && new Date(inv.dueDate)<new Date();
              const cutterName = inv.createdByName || cutterUsers.find(u=>u.id===inv.createdBy)?.name || "—";
              const paid = effPaid(inv);
              const rem = effOpen(inv);
              return (<tr key={inv.id} style={{borderBottom:`1px solid ${T.border}`,background:isOverdue?`${T.danger}08`:"transparent"}}>
                <td style={{padding:"12px 12px",fontSize:13}}>
                  {inv.createdBy ? (
                    <span style={{fontWeight:600,color:T.info}}>{cutterName}</span>
                  ) : (
                    <select value="" onChange={e=>{if(e.target.value)assignCutter(inv.id, e.target.value);}} style={{background:`${T.warning}22`,border:`1px solid ${T.warning}`,borderRadius:6,padding:"4px 8px",color:T.warning,fontSize:11,fontWeight:600,outline:"none",cursor:"pointer"}}>
                      <option value="">Cutter zuweisen…</option>
                      {cutterUsers.map(u=>(<option key={u.id} value={u.id}>{u.name}</option>))}
                    </select>
                  )}
                </td>
                <td style={{padding:"12px 12px",fontSize:13,fontWeight:600}}>{inv.client}</td>
                <td style={{padding:"12px 12px",fontSize:13}}>{inv.description}</td>
                <td style={{padding:"12px 12px",fontSize:13,fontFamily:"'JetBrains Mono',monospace"}}>{inv.amount.toLocaleString("de")} €</td>
                <td style={{padding:"12px 12px",fontSize:13,fontFamily:"'JetBrains Mono',monospace",color:paid>0?T.success:T.textDim}}>{paid>0?paid.toLocaleString("de",{minimumFractionDigits:2,maximumFractionDigits:2})+" €":"—"}</td>
                <td style={{padding:"12px 12px",fontSize:14,fontFamily:"'JetBrains Mono',monospace",fontWeight:700,color:isOverdue?T.danger:T.warning}}>{rem.toLocaleString("de",{minimumFractionDigits:2,maximumFractionDigits:2})} €</td>
                <td style={{padding:"12px 12px",fontSize:12,fontFamily:"'JetBrains Mono',monospace",color:isOverdue?T.danger:T.textMuted}}>{inv.dueDate?new Date(inv.dueDate).toLocaleDateString("de"):"—"}{isOverdue?" ⚠️":""}</td>
                <td style={{padding:"12px 12px"}}>
                  <button onClick={()=>setPaymentInvoice(inv)} style={{padding:"6px 12px",background:T.success,border:"none",borderRadius:6,color:"#fff",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:5}}><Icon name="money" size={13}/> Zahlung erfassen</button>
                </td>
              </tr>);
            })}</tbody>
          </table>)}
      </div>
    </div>}

    {/* ─── INVOICES TAB ─── */}
    {tab==="invoices"&&<>
      <Modal open={showAddInvoice} onClose={()=>{setShowAddInvoice(false);setAdminPendingFile(null);}} title="Neue Cutter-Rechnung" width={520}>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <Select label="Cutter zuweisen" options={[{value:"",label:"— Cutter wählen —"},...cutterUsers.map(u=>({value:u.id,label:u.name}))]} value={iForm.createdBy} onChange={e=>setIForm({...iForm,createdBy:e.target.value})}/>
          <Input label="Kunde" value={iForm.client} onChange={e=>setIForm({...iForm,client:e.target.value})} placeholder="z.B. Fleck Immobilien"/>
          <Input label="Beschreibung" value={iForm.description} onChange={e=>setIForm({...iForm,description:e.target.value})} placeholder="z.B. 3x Reels März"/>
          <Input label="Betrag (€)" type="number" value={iForm.amount} onChange={e=>setIForm({...iForm,amount:e.target.value})} placeholder="0.00"/>
          <Input label="Fällig am" type="date" value={iForm.dueDate} onChange={e=>setIForm({...iForm,dueDate:e.target.value})}/>
          <Select label="Status" options={INVOICE_STATUSES.map(s=>({value:s.id,label:s.label}))} value={iForm.status} onChange={e=>setIForm({...iForm,status:e.target.value})}/>
          <div>
            <div style={{fontSize:12,fontWeight:600,marginBottom:6,color:T.textDim}}>Rechnungs-Datei (PDF/Bild, max 20 MB)</div>
            <input ref={adminFileInputRef} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" style={{display:"none"}} onChange={e=>setAdminPendingFile(e.target.files?.[0]||null)}/>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <Button variant="secondary" size="sm" icon="upload" onClick={()=>adminFileInputRef.current?.click()}>Datei wählen</Button>
              {adminPendingFile && <div style={{fontSize:12,color:T.text,display:"flex",alignItems:"center",gap:4}}><Icon name="upload" size={12}/> {adminPendingFile.name} ({(adminPendingFile.size/1024).toFixed(0)} KB)</div>}
            </div>
          </div>
          <Button icon="check" onClick={addInvoice} style={{marginTop:8}}>Rechnung anlegen</Button>
        </div>
      </Modal>
      <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,overflow:"auto",WebkitOverflowScrolling:"touch"}}>
        {invoices.length===0?<Empty icon="money" title="Keine Rechnungen" desc="Lege eine neue Cutter-Rechnung an"/>:(
          <table style={{width:"100%",borderCollapse:"collapse",minWidth:950}}>
            <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>{["Cutter","Kunde","Beschreibung","Betrag","Bezahlt","Offen","Fällig","Datei","Status","",""].map(h=>(<th key={h} style={{padding:"12px 12px",textAlign:"left",fontSize:11,fontWeight:600,color:T.textDim,textTransform:"uppercase"}}>{h}</th>))}</tr></thead>
            <tbody>{invoices.slice().reverse().map(inv=>{ 
              const isOverdue = inv.status==="sent"&&inv.dueDate&&new Date(inv.dueDate)<new Date();
              const cutterName = inv.createdByName || cutterUsers.find(u=>u.id===inv.createdBy)?.name || "—";
              const paid = inv.status==="paid" ? (inv.amount||0) : Math.min(inv.amount||0, inv.paidTotal||0);
              const rem = Math.max(0, (inv.amount||0) - paid);
              return (<tr key={inv.id} style={{borderBottom:`1px solid ${T.border}`,background:isOverdue?`${T.danger}08`:"transparent"}}>
              <td style={{padding:"12px 12px",fontSize:13}}>
                  {inv.createdBy ? (
                    <span style={{fontWeight:600,color:T.info}}>{cutterName}</span>
                  ) : (
                    <select value="" onChange={e=>{if(e.target.value)assignCutter(inv.id, e.target.value);}} style={{background:`${T.warning}22`,border:`1px solid ${T.warning}`,borderRadius:6,padding:"4px 8px",color:T.warning,fontSize:11,fontWeight:600,outline:"none",cursor:"pointer"}}>
                      <option value="">Cutter zuweisen…</option>
                      {cutterUsers.map(u=>(<option key={u.id} value={u.id}>{u.name}</option>))}
                    </select>
                  )}
                </td>
              <td style={{padding:"12px 12px",fontSize:13,fontWeight:600}}>{inv.client}</td>
              <td style={{padding:"12px 12px",fontSize:13}}>{inv.description}</td>
              <td style={{padding:"12px 12px",fontSize:14,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:inv.status==="paid"?T.success:T.text}}>{inv.amount.toLocaleString("de")} €</td>
              <td style={{padding:"12px 12px",fontSize:13,fontFamily:"'JetBrains Mono',monospace",color:paid>0?T.success:T.textDim}}>{paid>0?paid.toLocaleString("de",{minimumFractionDigits:2,maximumFractionDigits:2})+" €":"—"}</td>
              <td style={{padding:"12px 12px",fontSize:13,fontFamily:"'JetBrains Mono',monospace",color:rem>0?T.warning:T.success,fontWeight:700}}>{rem>0?rem.toLocaleString("de",{minimumFractionDigits:2,maximumFractionDigits:2})+" €":"✓"}</td>
              <td style={{padding:"12px 12px",fontSize:13,fontFamily:"'JetBrains Mono',monospace",color:isOverdue?T.danger:T.textMuted}}>{inv.dueDate?new Date(inv.dueDate).toLocaleDateString("de"):"—"}</td>
              <td style={{padding:"12px 12px",fontSize:12}}>
                {inv.hasFile ? <a href={`/api/cutter-invoice-file/${inv.id}`} target="_blank" rel="noopener noreferrer" style={{color:T.info,textDecoration:"none",display:"inline-flex",alignItems:"center",gap:4}}><Icon name="upload" size={13}/> Ansehen</a> : <span style={{color:T.textDim}}>—</span>}
              </td>
              <td style={{padding:"12px 12px"}}><select value={isOverdue&&inv.status==="sent"?"overdue":inv.status} onChange={e=>updateInvoiceStatus(inv.id,e.target.value)} style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:6,padding:"4px 8px",color:INVOICE_STATUSES.find(s=>s.id===(isOverdue&&inv.status==="sent"?"overdue":inv.status))?.color||T.text,fontSize:12,fontWeight:600,outline:"none"}}>
                {INVOICE_STATUSES.map(s=>(<option key={s.id} value={s.id}>{s.label}</option>))}
              </select></td>
              <td style={{padding:"12px 12px"}}>
                <button onClick={()=>setPaymentInvoice(inv)} title="Teilzahlungen verwalten" style={{padding:"5px 10px",background:`${T.info}22`,border:`1px solid ${T.info}40`,borderRadius:6,color:T.info,fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:4}}><Icon name="money" size={13}/> Zahlungen</button>
              </td>
              <td style={{padding:"12px 12px",whiteSpace:"nowrap"}}>
                <button onClick={()=>setEditInvoice({...inv,dueDate:inv.dueDate&&inv.dueDate!=="0000-00-00"?inv.dueDate.slice(0,10):"",amount:inv.amount,createdBy:inv.createdBy||""})} title="Rechnung bearbeiten" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:3}}><Icon name="edit" size={15}/></button>
                <button onClick={()=>deleteInvoice(inv.id)} title="Löschen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:3}}><Icon name="delete" size={15}/></button>
              </td>
            </tr>);})}</tbody>
          </table>)}
      </div>
    </>}

    {paymentInvoice && <PaymentsModal invoice={paymentInvoice} user={{id:null,name:"Admin"}} onClose={()=>setPaymentInvoice(null)} onChanged={async()=>{ await reload(STORAGE_KEYS.cutter_invoices); setInvoices(store.get(STORAGE_KEYS.cutter_invoices)); }}/>}

    {/* Rechnung bearbeiten */}
    <Modal open={!!editInvoice} onClose={()=>setEditInvoice(null)} title="Rechnung bearbeiten" width={520}>
      {editInvoice&&<div style={{display:"flex",flexDirection:"column",gap:14}}>
        <Input label="Kunde" value={editInvoice.client} onChange={e=>setEditInvoice({...editInvoice,client:e.target.value})}/>
        <Input label="Beschreibung" value={editInvoice.description||""} onChange={e=>setEditInvoice({...editInvoice,description:e.target.value})}/>
        <div style={{display:"flex",gap:12}}>
          <div style={{flex:1}}><Input label="Betrag (€)" type="number" value={editInvoice.amount} onChange={e=>setEditInvoice({...editInvoice,amount:e.target.value})}/></div>
          <div style={{flex:1}}><Input label="Fällig am" type="date" value={editInvoice.dueDate||""} onChange={e=>setEditInvoice({...editInvoice,dueDate:e.target.value})}/></div>
        </div>
        <Select label="Cutter" options={[{value:"",label:"— Niemand —"}].concat(cutterUsers.map(u=>({value:u.id,label:u.name})))} value={editInvoice.createdBy||""} onChange={e=>setEditInvoice({...editInvoice,createdBy:e.target.value})}/>
        <Select label="Status" options={INVOICE_STATUSES.filter(s=>s.id!=="overdue").map(s=>({value:s.id,label:s.label}))} value={editInvoice.status} onChange={e=>setEditInvoice({...editInvoice,status:e.target.value})}/>
        <Button icon="check" onClick={saveEditInvoice} style={{marginTop:8}}>Speichern</Button>
      </div>}
    </Modal>

    {/* ─── GOALS TAB ─── */}
    {tab==="goals"&&<div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:24}}>
      <div style={{display:"flex",gap:8,marginBottom:20,flexWrap:"wrap",alignItems:"end"}}>
        <Input placeholder="z.B. 10 Reels pro Monat" value={goalForm.text} onChange={e=>setGoalForm({...goalForm,text:e.target.value})} style={{flex:1,minWidth:200}}/>
        <Input placeholder="Ziel" type="number" value={goalForm.target} onChange={e=>setGoalForm({...goalForm,target:e.target.value})} style={{width:80}}/>
        <Button icon="add" onClick={addGoal}>Ziel setzen</Button>
      </div>
      {goals.length===0?<div style={{color:T.textDim,fontSize:13,textAlign:"center",padding:20}}>Noch keine Ziele gesetzt</div>:
        goals.map(g=>{
          const pct=g.target>0?Math.min(100,Math.round((g.current/g.target)*100)):g.done?100:0;
          return (<div key={g.id} style={{padding:"14px 0",borderBottom:`1px solid ${T.border}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <button onClick={()=>toggleGoal(g.id)} style={{width:22,height:22,borderRadius:6,border:`2px solid ${g.done?T.success:T.border}`,background:g.done?T.success:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:"#fff"}}>{g.done&&<Icon name="check" size={14}/>}</button>
                <span style={{fontSize:14,fontWeight:600,textDecoration:g.done?"line-through":"none",opacity:g.done?0.5:1}}>{g.text}</span>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                {g.target>0&&<div style={{display:"flex",alignItems:"center",gap:4}}>
                  <input type="number" value={g.current} onChange={e=>updateGoalProgress(g.id,e.target.value)} style={{width:50,background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:4,padding:"4px 8px",color:T.text,fontSize:13,textAlign:"center",outline:"none"}}/>
                  <span style={{fontSize:13,color:T.textDim}}>/ {g.target}</span>
                </div>}
                <button onClick={()=>deleteGoal(g.id)} style={{background:"none",border:"none",color:T.textDim,cursor:"pointer"}}><Icon name="delete" size={15}/></button>
              </div>
            </div>
            {g.target>0&&<div style={{height:8,background:T.bgInput,borderRadius:4,overflow:"hidden"}}>
              <div style={{height:"100%",width:`${pct}%`,background:pct>=100?T.success:pct>=50?T.warning:T.accent,borderRadius:4,transition:"width 0.3s"}}/>
            </div>}
          </div>);
        })}
    </div>}
  </div>);
};

// ─── WHITEBOARD (Mindmap / Planungstool mit mehreren Boards + Datei-Anhängen) ───
const NODE_COLORS = [
  {bg:"#3B82F6",fg:"#fff",name:"Blau"},
  {bg:"#10B981",fg:"#fff",name:"Grün"},
  {bg:"#F59E0B",fg:"#fff",name:"Orange"},
  {bg:"#EF4444",fg:"#fff",name:"Rot"},
  {bg:"#8B5CF6",fg:"#fff",name:"Lila"},
  {bg:"#EC4899",fg:"#fff",name:"Pink"},
  {bg:"#1F2937",fg:"#fff",name:"Schwarz"},
  {bg:"#FCD34D",fg:"#1a1a2e",name:"Gelb"},
];

// Pfad zwischen zwei Knoten:
// - Ohne mid: gerade Linie
// - Mit mid: zwei gerade Segmente mit leichter Rundung am Knickpunkt (mehr Miro-mäßig)
const edgePath = (a, b, mid) => {
  const ax = a.x + a.w/2, ay = a.y + a.h/2;
  const bx = b.x + b.w/2, by = b.y + b.h/2;
  if (!mid) {
    return `M ${ax} ${ay} L ${bx} ${by}`;
  }
  // Knick mit Abrundung: berechne Vektoren, kürze beide Segmente am Knickpunkt
  const v1x = mid.x - ax, v1y = mid.y - ay;
  const v2x = bx - mid.x, v2y = by - mid.y;
  const len1 = Math.hypot(v1x, v1y) || 1;
  const len2 = Math.hypot(v2x, v2y) || 1;
  const r = Math.min(20, len1 * 0.3, len2 * 0.3);
  const p1x = mid.x - (v1x/len1) * r, p1y = mid.y - (v1y/len1) * r;
  const p2x = mid.x + (v2x/len2) * r, p2y = mid.y + (v2y/len2) * r;
  return `M ${ax} ${ay} L ${p1x} ${p1y} Q ${mid.x} ${mid.y} ${p2x} ${p2y} L ${bx} ${by}`;
};

const WhiteboardPage = ({user}) => {
  // Boards-Liste & aktives Board
  const [boards,setBoards] = useState([]);              // own + shared kombiniert (flach)
  const [invites,setInvites] = useState([]);            // ausstehende Einladungen für mich
  const [boardMembers,setBoardMembers] = useState([]);  // Mitglieder des aktuellen Boards (wenn ich Owner bin)
  const [showShareModal,setShowShareModal] = useState(false);
  const [allUsers,setAllUsers] = useState([]);          // Alle User für die Einladung
  const [activeBoardId,setActiveBoardId] = useState(null);
  const [showBoardMenu,setShowBoardMenu] = useState(false);
  const [renameValue,setRenameValue] = useState("");
  const [showRename,setShowRename] = useState(false);
  const [showCreateBoard,setShowCreateBoard] = useState(false);
  const [createBoardName,setCreateBoardName] = useState("");
  const [confirmDeleteBoard,setConfirmDeleteBoard] = useState(null);
  const [confirmDeleteNode,setConfirmDeleteNode] = useState(null); // {kind:"node"|"edge", id}
  const [showAddLink,setShowAddLink] = useState(false);
  const [linkUrl,setLinkUrl] = useState("");
  const [linkLabel,setLinkLabel] = useState("");
  const [showAddImage,setShowAddImage] = useState(false);
  const [imageUrl,setImageUrl] = useState("");
  const [boardUpdatedAt,setBoardUpdatedAt] = useState(null); // für Auto-Refresh-Vergleich

  // Board-Inhalt
  const [nodes,setNodes] = useState([]);
  const [edges,setEdges] = useState([]); // {id, from, to, mid?:{x,y}}
  const [view,setView] = useState({x:0,y:0,zoom:1});
  const [loaded,setLoaded] = useState(false);
  const [savedAt,setSavedAt] = useState(null);
  const [saving,setSaving] = useState(false);

  // Interaction State
  const [selectedId,setSelectedId] = useState(null); // node id
  const [selectedEdgeId,setSelectedEdgeId] = useState(null);
  const [editingId,setEditingId] = useState(null);
  const [editText,setEditText] = useState("");
  const [draggingNode,setDraggingNode] = useState(null);
  const [resizingNode,setResizingNode] = useState(null); // {id, startW, startH, startX(screen), startY(screen)}
  const [draggingMid,setDraggingMid] = useState(null); // edge id für Mid-Drag
  const [panning,setPanning] = useState(null);
  const [connectFrom,setConnectFrom] = useState(null);
  const [connectMouse,setConnectMouse] = useState(null);
  const [showColorPicker,setShowColorPicker] = useState(null);
  const [showNodeDetail,setShowNodeDetail] = useState(null); // node id

  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const importInputRef = useRef(null);
  const saveTimerRef = useRef(null);
  const skipNextSaveRef = useRef(false);
  const deletedIdsRef = useRef([]); // gelöschte Knoten/Kanten für den Merge beim Speichern
  const editingChecklistRef = useRef(false); // true während in einer Checkliste getippt wird (pausiert Auto-Refresh)
  const editingChecklistTimer = useRef(null);
  const nodeActivityRef = useRef(false); // true kurz nach jeder Knoten-Änderung (verhindert Zurückspringen)
  const nodeActivityTimer = useRef(null);

  // ═══ Boards-Liste laden (own + shared + invites) ═══
  const loadBoards = async () => {
    const res = await api.get("boards","userId="+user.id);
    if(!res || res.error){ setBoards([]); setInvites([]); return; }
    const own = (res.own||[]).map(b=>({...b, access:"owner"}));
    const shared = (res.shared||[]).map(b=>({...b, access:"shared"}));
    const combined = [...own, ...shared];
    setBoards(combined);
    setInvites(res.invites||[]);
    if(combined.length === 0 && (res.invites||[]).length === 0){
      // Erstes Board automatisch erstellen
      const created = await api.post("boards",{userId:user.id, name:"Mein erstes Board"});
      if(created && created.id){
        const fresh = await api.get("boards","userId="+user.id);
        const allCombined = [...((fresh?.own||[]).map(b=>({...b,access:"owner"}))), ...((fresh?.shared||[]).map(b=>({...b,access:"shared"})))];
        setBoards(allCombined);
        setInvites(fresh?.invites||[]);
        setActiveBoardId(created.id);
      }
    } else if(!activeBoardId && combined.length>0){
      setActiveBoardId(combined[0].id);
    } else if(activeBoardId && !combined.find(b=>b.id===activeBoardId)){
      // Aktives Board existiert nicht mehr (z.B. Owner hat es gelöscht oder Share entzogen)
      setActiveBoardId(combined[0]?.id || null);
    }
  };
  useEffect(()=>{loadBoards();},[user.id]);

  // Alle User für die Einladung laden (nur wenn man am Teilen-Modal vorbei kommt)
  useEffect(()=>{
    if(!showShareModal) return;
    const cached = store.get(STORAGE_KEYS.users);
    if(cached && cached.length>0) setAllUsers(cached);
    else api.get("users").then(u=>setAllUsers(u||[])).catch(()=>{});
  },[showShareModal]);

  // Mitglieder des aktiven Boards laden (nur wenn ich Owner bin)
  const loadBoardMembers = async () => {
    if(!activeBoardId) return;
    const board = boards.find(b=>b.id===activeBoardId);
    if(!board || board.access !== "owner"){ setBoardMembers([]); return; }
    const list = await api.get("board-shares",`userId=${user.id}&boardId=${activeBoardId}`);
    setBoardMembers(Array.isArray(list)?list:[]);
  };
  useEffect(()=>{loadBoardMembers();},[activeBoardId, boards.length]);

  // ═══ Aktives Board laden bei Wechsel ═══
  useEffect(()=>{
    if(!activeBoardId) return;
    let cancelled = false;
    setLoaded(false);
    skipNextSaveRef.current = true;
    (async()=>{
      const res = await api.get("board/"+activeBoardId,"userId="+user.id);
      if(cancelled) return;
      if(res && !res.error){
        const d = res.data || {nodes:[],edges:[],view:{x:0,y:0,zoom:1}};
        setNodes(d.nodes||[]);
        setEdges(d.edges||[]);
        setView(d.view||{x:0,y:0,zoom:1});
        setBoardUpdatedAt(res.updated_at||null);
      } else {
        setNodes([]);setEdges([]);setView({x:0,y:0,zoom:1});
      }
      setSelectedId(null);setSelectedEdgeId(null);setEditingId(null);
      setLoaded(true);
    })();
    return ()=>{cancelled=true;};
  },[activeBoardId,user.id]);

  // ═══ Auto-Refresh für geteilte Boards (alle 5 Sek) ═══
  // Aktiv wenn das Board geteilt ist (egal ob ich Owner oder Eingeladener bin)
  useEffect(()=>{
    if(!activeBoardId || !loaded) return;
    const activeBoard = boards.find(b=>b.id===activeBoardId);
    if(!activeBoard) return;
    // Auto-Refresh aktiv für: alle "shared" Boards (immer) UND eigene Boards mit Mitgliedern
    const isShared = activeBoard.access==="shared" || (activeBoard.access==="owner" && boardMembers.length>0);
    if(!isShared) return;
    const iv = setInterval(async ()=>{
      // Nicht refreshen wenn User gerade was zieht / editiert / verbindet / Größe zieht / in Checkliste tippt
      if(draggingNode || editingId || connectFrom || draggingMid || panning || resizingNode || editingChecklistRef.current || nodeActivityRef.current) return;
      // Nicht refreshen während Save läuft
      if(saving) return;
      try {
        const res = await api.get("board/"+activeBoardId,"userId="+user.id);
        if(!res || res.error) return;
        // Nur updaten wenn updated_at neuer ist als unser letzter bekannter Stand
        if(res.updated_at && res.updated_at !== boardUpdatedAt){
          const d = res.data || {nodes:[],edges:[],view:{x:0,y:0,zoom:1}};
          skipNextSaveRef.current = true;
          // Server-Stand mit eigenen Knoten zusammenführen statt blind ersetzen:
          // pro Knoten gewinnt die neuere Version (_m). So bleiben eigene, noch nicht
          // gespeicherte Änderungen (z.B. ein frisch gesetztes Schloss) erhalten.
          const serverNodes = d.nodes||[];
          const justDeleted = new Set(deletedIdsRef.current);
          setNodes(localNodes=>{
            const byId = {};
            serverNodes.forEach(n=>{ if(n.id) byId[n.id]=n; });
            localNodes.forEach(ln=>{
              if(!ln.id) return;
              const sv = byId[ln.id];
              const lm = ln._m||0, sm = sv?._m||0;
              // eigene Version behalten wenn sie neuer ODER gleich alt ist (bei Gleichstand
              // gewinnt der lokale Stand — sonst springt die eigene Position kurz zurück).
              if(!sv || lm >= sm) byId[ln.id] = ln;
            });
            // lokal gerade gelöschte Knoten nicht wieder einblenden
            justDeleted.forEach(id=>{ delete byId[id]; });
            return Object.values(byId);
          });
          const serverEdges = d.edges||[];
          setEdges(localEdges=>{
            const byId = {};
            serverEdges.forEach(e=>{ if(e.id) byId[e.id]=e; });
            localEdges.forEach(le=>{
              if(!le.id) return;
              const sv = byId[le.id];
              const lm = le._m||0, sm = sv?._m||0;
              if(!sv || lm >= sm) byId[le.id] = le;
            });
            justDeleted.forEach(id=>{ delete byId[id]; });
            return Object.values(byId);
          });
          // view NICHT mit-syncen — sonst springt der eigene Pan/Zoom weg
          setBoardUpdatedAt(res.updated_at);
        }
      } catch(e){ /* Auto-Refresh failed */ }
    }, 5000);
    return ()=>clearInterval(iv);
  },[activeBoardId, loaded, boards, boardMembers, draggingNode, editingId, connectFrom, draggingMid, panning, resizingNode, saving, boardUpdatedAt, user.id]);

  // ═══ Auto-Refresh Boards-Liste (alle 15 Sek für neue Einladungen) ═══
  useEffect(()=>{
    const iv = setInterval(()=>{ loadBoards(); }, 15000);
    return ()=>clearInterval(iv);
  },[user.id, activeBoardId]);

  // ═══ Auto-Save (debounced) — mit serverseitigem Merge ═══
  useEffect(()=>{
    if(!loaded || !activeBoardId) return;
    if(skipNextSaveRef.current){skipNextSaveRef.current=false;return;}
    if(saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async()=>{
      setSaving(true);
      const sentDeleted = deletedIdsRef.current.slice();
      try {
        const res = await api.put("board/"+activeBoardId,{userId:user.id, data:{nodes,edges,view}, deletedIds:sentDeleted});
        if(res && res.error){
          setSaving(false);
          return;
        }
        // gesendete Löschungen sind verarbeitet → aus der Merker-Liste entfernen
        deletedIdsRef.current = deletedIdsRef.current.filter(id=>!sentDeleted.includes(id));
        // WICHTIG: den eigenen Stand NICHT durch den Server-Stand ersetzen.
        // Unser lokaler Stand ist der frischeste (wir haben ihn gerade geschickt).
        // Fremde Änderungen holt der Auto-Refresh und führt sie knotengenau zusammen,
        // ohne die eigene Position/Auswahl springen zu lassen.
        if(res.updated_at) setBoardUpdatedAt(res.updated_at);
        setSavedAt(new Date());
      } catch(e){
      }
      setSaving(false);
    },1200);
    return ()=>{ if(saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  },[nodes,edges,view,loaded,activeBoardId,user.id]);

  // ═══ Coordinate Helpers ═══
  const screenToBoard = (sx,sy) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if(!rect) return {x:0,y:0};
    return {x:(sx-rect.left-view.x)/view.zoom, y:(sy-rect.top-view.y)/view.zoom};
  };
  const centerOfView = () => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if(!rect) return {x:200,y:200};
    return {x:(rect.width/2 - view.x)/view.zoom, y:(rect.height/2 - view.y)/view.zoom};
  };

  // ═══ Node-Operationen ═══
  const addNode = (x,y,opts={}) => {
    const id = "n-"+Date.now()+"-"+Math.random().toString(36).slice(2,7);
    let w = 200, h = 70;
    if(opts.type==="sticky"){w=180;h=120;}
    if(opts.type==="textbox"){w=220;h=60;}
    if(opts.type==="checklist"){w=240;h=140;}
    if(opts.type==="image"){w=220;h=180;}
    if(opts.type==="link"){w=220;h=80;}
    if(opts.type==="file"){w=220;h=70;}
    const node = {
      id, x:x-w/2, y:y-h/2, w, h,
      text:opts.text||"Neue Notiz",
      color:opts.color??0,
      type:opts.type||"node",
      description:"",
      url:opts.url||"",
      imageSrc:opts.imageSrc||"",
      fileId:opts.fileId||"",
      fileName:opts.fileName||"",
      locked:false,
      // Text-Gestaltung
      fontSize:opts.fontSize||(opts.type==="textbox"?18:14),
      textColor:opts.textColor||"",
      bold:opts.bold||false,
      align:opts.align||"center",
      items:opts.type==="checklist"?(opts.items||[{id:"i-"+Date.now(),text:"",done:false}]):(opts.items||undefined),
      title:opts.title||"",
      _m:Date.now(),
    };
    setNodes(ns=>[...ns,node]);
    setSelectedId(id);
    if(opts.type==="node"||opts.type==="sticky"||opts.type==="textbox"||!opts.type){
      setEditingId(id);
      setEditText(node.text);
    }
    return id;
  };
  // Merkt sich kurz, dass gerade an Knoten gearbeitet wurde → Auto-Refresh hält sich zurück,
  // damit nach dem Loslassen/Speichern die eigene Position nicht zurückspringt.
  const markNodeActivity = () => {
    nodeActivityRef.current = true;
    if(nodeActivityTimer.current) clearTimeout(nodeActivityTimer.current);
    nodeActivityTimer.current = setTimeout(()=>{ nodeActivityRef.current = false; }, 2000);
  };
  const updateNode = (id,patch) => { markNodeActivity(); setNodes(ns=>ns.map(n=>n.id===id?{...n,...patch,_m:Date.now()}:n)); };

  // ═══ Checklisten-Punkte ═══
  // Merkt sich, dass gerade in einer Checkliste getippt wird → Auto-Refresh pausiert kurz
  const markChecklistActivity = () => {
    editingChecklistRef.current = true;
    if(editingChecklistTimer.current) clearTimeout(editingChecklistTimer.current);
    editingChecklistTimer.current = setTimeout(()=>{ editingChecklistRef.current = false; }, 2500);
  };
  const addCheckItem = (nodeId) => {
    markChecklistActivity();
    setNodes(ns=>ns.map(n=>n.id===nodeId?{...n,items:[...(n.items||[]),{id:"i-"+Date.now()+"-"+Math.random().toString(36).slice(2,5),text:"",done:false}],_m:Date.now()}:n));
  };
  const updateCheckItem = (nodeId,itemId,patch) => {
    markChecklistActivity();
    setNodes(ns=>ns.map(n=>n.id===nodeId?{...n,items:(n.items||[]).map(it=>it.id===itemId?{...it,...patch}:it),_m:Date.now()}:n));
  };
  const deleteCheckItem = (nodeId,itemId) => {
    markChecklistActivity();
    setNodes(ns=>ns.map(n=>n.id===nodeId?{...n,items:(n.items||[]).filter(it=>it.id!==itemId),_m:Date.now()}:n));
  };

  // Link-Knoten erstellen
  const addLinkNode = () => {
    if(!linkUrl.trim()) return;
    let url = linkUrl.trim();
    if(!/^https?:\/\//i.test(url)) url = "https://" + url;
    const c = centerOfView();
    addNode(c.x, c.y, {type:"link", url, text:linkLabel.trim() || url.replace(/^https?:\/\//,"").replace(/\/$/,"").slice(0,40)});
    setShowAddLink(false);
    setLinkUrl("");
    setLinkLabel("");
  };

  // Bild-Knoten via URL erstellen
  const addImageFromUrl = () => {
    if(!imageUrl.trim()) return;
    const c = centerOfView();
    addNode(c.x, c.y, {type:"image", imageSrc:imageUrl.trim(), text:""});
    setShowAddImage(false);
    setImageUrl("");
  };

  // Bild-Knoten via Datei-Upload (lokal als data-URL gespeichert — bleibt im Board-JSON)
  const imageFileInputRef = useRef(null);
  const onImageFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if(!file) return;
    if(!file.type.startsWith("image/")){ alert("Bitte eine Bild-Datei auswählen"); return; }
    if(file.size > 3*1024*1024){ alert("Bild zu groß (max 3 MB für Inline-Bilder). Für größere Dateien benutze Dokument-Upload."); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const c = centerOfView();
      addNode(c.x, c.y, {type:"image", imageSrc:reader.result, text:file.name});
      setShowAddImage(false);
    };
    reader.readAsDataURL(file);
  };

  // Standalone-Datei-Button: Datei hochladen → File-Knoten erzeugen
  const standaloneFileInputRef = useRef(null);
  const onStandaloneFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if(!file) return;
    if(file.size > 20*1024*1024){ alert("Datei zu groß (max 20 MB)"); return; }
    // Erst Knoten erzeugen damit wir eine nodeId haben
    const c = centerOfView();
    const nodeId = "n-"+Date.now()+"-"+Math.random().toString(36).slice(2,7);
    const w=220, h=70;
    const node = {id:nodeId, x:c.x-w/2, y:c.y-h/2, w, h, text:file.name, color:0, type:"file", description:"", url:"", imageSrc:"", fileId:"", fileName:file.name};
    setNodes(ns=>[...ns,node]);
    // Datei hochladen
    const fd = new FormData();
    fd.append("userId", user.id);
    fd.append("boardId", activeBoardId);
    fd.append("nodeId", nodeId);
    fd.append("file", file);
    try {
      const res = await fetch("/api/whiteboard-upload", {method:"POST", headers:authHeaders(), body:fd});
      const data = await res.json();
      if(data.id){
        // fileId in den Knoten schreiben
        setNodes(ns=>ns.map(n=>n.id===nodeId?{...n, fileId:data.id, fileName:file.name}:n));
      } else {
        alert("Upload fehlgeschlagen: "+(data.error||"Unbekannter Fehler"));
        setNodes(ns=>ns.filter(n=>n.id!==nodeId));
      }
    } catch(err){
      alert("Upload fehlgeschlagen: "+err.message);
      setNodes(ns=>ns.filter(n=>n.id!==nodeId));
    }
  };

  const deleteNode = (id) => {
    deletedIdsRef.current.push(id);
    // auch zugehörige Kanten als gelöscht vormerken
    edges.forEach(e=>{ if(e.from===id||e.to===id) deletedIdsRef.current.push(e.id); });
    setNodes(ns=>ns.filter(n=>n.id!==id));
    setEdges(es=>es.filter(e=>e.from!==id && e.to!==id));
    if(selectedId===id) setSelectedId(null);
    if(editingId===id) setEditingId(null);
  };

  // ═══ Edge-Operationen ═══
  const addEdge = (from,to) => {
    if(from===to) return;
    if(edges.some(e=>(e.from===from&&e.to===to)||(e.from===to&&e.to===from))) return;
    setEdges(es=>[...es,{id:"e-"+Date.now()+"-"+Math.random().toString(36).slice(2,5),from,to,mid:null,_m:Date.now()}]);
  };
  const updateEdge = (id,patch) => setEdges(es=>es.map(e=>e.id===id?{...e,...patch,_m:Date.now()}:e));
  const deleteEdge = (id) => {deletedIdsRef.current.push(id);setEdges(es=>es.filter(e=>e.id!==id));if(selectedEdgeId===id)setSelectedEdgeId(null);};
  const resetEdgeCurve = (id) => updateEdge(id,{mid:null});

  // ═══ Pointer Handlers ═══
  const onCanvasPointerDown = (e) => {
    if(e.target.closest("[data-node]") || e.target.closest("[data-control]") || e.target.closest("[data-edge]")) return;
    setSelectedId(null);
    setSelectedEdgeId(null);
    if(editingId) commitEdit();
    setPanning({startX:e.clientX, startY:e.clientY, viewX:view.x, viewY:view.y});
  };
  const onCanvasDoubleClick = (e) => {
    if(e.target.closest("[data-node]")||e.target.closest("[data-edge]")) return;
    const pt = screenToBoard(e.clientX, e.clientY);
    addNode(pt.x, pt.y);
  };
  const onNodePointerDown = (e,node) => {
    e.stopPropagation();
    if(e.target.closest("[data-control]")) return;
    if(editingId===node.id) return;
    setSelectedId(node.id);
    setSelectedEdgeId(null);
    if(node.locked) return; // gesperrte Knoten lassen sich nicht verschieben
    const pt = screenToBoard(e.clientX, e.clientY);
    setDraggingNode({id:node.id, offsetX:pt.x-node.x, offsetY:pt.y-node.y});
  };
  const startConnect = (e,nodeId) => {
    e.stopPropagation();
    setConnectFrom(nodeId);
    setConnectMouse(screenToBoard(e.clientX, e.clientY));
  };
  const startEdgeMidDrag = (e,edgeId) => {
    e.stopPropagation();
    setSelectedEdgeId(edgeId);
    setDraggingMid(edgeId);
  };

  // Globale Pointer-Listener für Pan/Drag (Maus + Touch über Pointer Events)
  useEffect(()=>{
    if(!draggingNode && !panning && !connectFrom && !draggingMid && !resizingNode) return;
    const onMove = (e) => {
      if(panning){
        setView(v=>({...v, x: panning.viewX + (e.clientX-panning.startX), y: panning.viewY + (e.clientY-panning.startY)}));
      } else if(resizingNode){
        // Größe ziehen — Differenz in Bildschirm-Pixeln durch Zoom teilen
        const dw = (e.clientX - resizingNode.startX)/view.zoom;
        const dh = (e.clientY - resizingNode.startY)/view.zoom;
        updateNode(resizingNode.id, {
          w: Math.max(80, resizingNode.startW + dw),
          h: Math.max(40, resizingNode.startH + dh),
        });
      } else if(draggingNode){
        const pt = screenToBoard(e.clientX, e.clientY);
        updateNode(draggingNode.id, {x: pt.x-draggingNode.offsetX, y: pt.y-draggingNode.offsetY});
      } else if(draggingMid){
        const pt = screenToBoard(e.clientX, e.clientY);
        updateEdge(draggingMid, {mid:{x:pt.x, y:pt.y}});
      } else if(connectFrom){
        setConnectMouse(screenToBoard(e.clientX, e.clientY));
      }
    };
    const onUp = (e) => {
      if(connectFrom){
        const el = document.elementFromPoint(e.clientX, e.clientY);
        const targetEl = el?.closest("[data-node]");
        const targetId = targetEl?.getAttribute("data-node");
        if(targetId && targetId !== connectFrom) addEdge(connectFrom, targetId);
      }
      setDraggingNode(null);
      setPanning(null);
      setConnectFrom(null);
      setConnectMouse(null);
      setDraggingMid(null);
      setResizingNode(null);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return ()=>{
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  },[draggingNode,panning,connectFrom,draggingMid,resizingNode,view]);

  // Zoom wie bei Miro: Mausrad und Trackpad zoomen sanft, gedämpft und begrenzt,
  // damit es bei kleinen Bewegungen nicht zu stark springt.
  const onWheel = (e) => {
    e.preventDefault();
    if(!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left, my = e.clientY - rect.top;

    // deltaY normalisieren (manche Browser liefern Zeilen statt Pixel)
    let dy = e.deltaY;
    if(e.deltaMode === 1) dy *= 16;        // Zeilen → ungefähre Pixel
    else if(e.deltaMode === 2) dy *= 100;  // Seiten → ungefähre Pixel

    // Trackpad-Pinch (kommt als wheel mit ctrlKey) zoomt feiner als das Mausrad
    const isPinch = e.ctrlKey || e.metaKey;
    const k = isPinch ? 0.015 : 0.004;

    // pro Event begrenzen, damit ein kräftiger Schwung nicht überschießt
    const clamped = Math.max(-50, Math.min(50, dy));
    const factor = Math.exp(-clamped * k);

    setView(v=>{
      const newZoom = Math.max(0.2, Math.min(3, v.zoom * factor));
      if(Math.abs(newZoom - v.zoom) < 0.0001) return v;
      return {x: mx - (mx - v.x) * (newZoom / v.zoom), y: my - (my - v.y) * (newZoom / v.zoom), zoom: newZoom};
    });
  };

  // Pinch-Zoom für Touch (additiv, Pan läuft über Pointer Events)
  const pinchRef = useRef(null);
  const onTouchStart = (e) => {
    if(e.touches.length === 2){
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchRef.current = {dist: Math.hypot(dx,dy), zoom: view.zoom};
    }
  };
  const onTouchMove = (e) => {
    if(e.touches.length === 2 && pinchRef.current){
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const newDist = Math.hypot(dx,dy);
      setView(v=>({...v, zoom: Math.max(0.2, Math.min(3, pinchRef.current.zoom * (newDist/pinchRef.current.dist)))}));
    }
  };
  const onTouchEnd = () => { pinchRef.current = null; };

  // Native Listener mit passive:false — sonst funktioniert preventDefault auf iOS nicht
  // (verhindert iOS-Crash mit weißem Bildschirm beim Zoomen)
  const handlersRef = useRef({});
  handlersRef.current.onTouchStart = onTouchStart;
  handlersRef.current.onTouchMove = onTouchMove;
  handlersRef.current.onTouchEnd = onTouchEnd;
  handlersRef.current.onWheel = onWheel;
  useEffect(()=>{
    const el = canvasRef.current;
    if(!el) return;
    const ts = (e) => handlersRef.current.onTouchStart(e);
    const tm = (e) => handlersRef.current.onTouchMove(e);
    const te = (e) => handlersRef.current.onTouchEnd(e);
    const wh = (e) => handlersRef.current.onWheel(e);
    el.addEventListener("touchstart", ts, {passive:false});
    el.addEventListener("touchmove", tm, {passive:false});
    el.addEventListener("touchend", te);
    el.addEventListener("touchcancel", te);
    el.addEventListener("wheel", wh, {passive:false});
    return ()=>{
      el.removeEventListener("touchstart", ts);
      el.removeEventListener("touchmove", tm);
      el.removeEventListener("touchend", te);
      el.removeEventListener("touchcancel", te);
      el.removeEventListener("wheel", wh);
    };
  },[loaded, activeBoardId, canvasRef.current]); // auch neu binden wenn der Canvas-DOM-Knoten wechselt


  const commitEdit = () => {
    if(editingId){
      updateNode(editingId, {text: editText});
      setEditingId(null);
    }
  };

  // Keyboard
  useEffect(()=>{
    const onKey = (e) => {
      if(editingId || showNodeDetail || showRename || showCreateBoard || confirmDeleteBoard || confirmDeleteNode || showAddLink || showAddImage) return;
      // Wenn gerade in einem Eingabefeld getippt wird (z.B. Checklisten-Punkt, Titel),
      // darf die Löschtaste NICHT den ganzen Knoten löschen.
      const tag = (e.target && e.target.tagName) ? e.target.tagName.toLowerCase() : "";
      const isTyping = tag==="input" || tag==="textarea" || (e.target && e.target.isContentEditable);
      if(isTyping) return;
      if((e.key==="Delete"||e.key==="Backspace") && selectedId){ e.preventDefault(); setConfirmDeleteNode({kind:"node", id:selectedId}); }
      else if((e.key==="Delete"||e.key==="Backspace") && selectedEdgeId){ e.preventDefault(); setConfirmDeleteNode({kind:"edge", id:selectedEdgeId}); }
      if(e.key==="Escape"){setEditingId(null);setSelectedId(null);setSelectedEdgeId(null);setShowColorPicker(null);}
    };
    window.addEventListener("keydown", onKey);
    return ()=>window.removeEventListener("keydown", onKey);
  },[selectedId,selectedEdgeId,editingId,showNodeDetail,showRename,showCreateBoard,confirmDeleteBoard,confirmDeleteNode,showAddLink,showAddImage]);

  // Lösch-Bestätigung ausführen
  const performConfirmedDelete = () => {
    if(!confirmDeleteNode) return;
    if(confirmDeleteNode.kind==="node") deleteNode(confirmDeleteNode.id);
    else if(confirmDeleteNode.kind==="edge") deleteEdge(confirmDeleteNode.id);
    setConfirmDeleteNode(null);
  };

  const fitToContent = () => {
    if(nodes.length===0){setView({x:0,y:0,zoom:1});return;}
    const minX = Math.min(...nodes.map(n=>n.x)), minY = Math.min(...nodes.map(n=>n.y));
    const maxX = Math.max(...nodes.map(n=>n.x+n.w)), maxY = Math.max(...nodes.map(n=>n.y+n.h));
    const rect = canvasRef.current?.getBoundingClientRect();
    if(!rect) return;
    const padding = 80;
    const zx = (rect.width - padding*2) / (maxX-minX || 1);
    const zy = (rect.height - padding*2) / (maxY-minY || 1);
    const z = Math.max(0.2, Math.min(1, Math.min(zx,zy)));
    setView({x: padding - minX*z, y: padding - minY*z, zoom: z});
  };

  // ═══ Boards verwalten ═══
  const createBoard = async () => {
    const name = createBoardName.trim() || "Neues Board";
    const res = await api.post("boards",{userId:user.id, name});
    if(res && res.id){
      await loadBoards();
      setActiveBoardId(res.id);
    }
    setShowCreateBoard(false);
    setCreateBoardName("");
    setShowBoardMenu(false);
  };
  const deleteBoardConfirmed = async (id) => {
    await api.del("boards",{userId:user.id, id});
    await loadBoards();
    if(activeBoardId===id) setActiveBoardId(null);
    setConfirmDeleteBoard(null);
    setShowBoardMenu(false);
  };
  const renameBoard = async () => {
    if(!renameValue.trim()) return;
    await api.put("boards",{userId:user.id, id:activeBoardId, name:renameValue.trim()});
    await loadBoards();
    setShowRename(false);
    setShowBoardMenu(false);
  };

  // ═══ Sharing-Funktionen ═══
  const inviteUser = async (invitedUserId) => {
    const res = await api.post("board-shares",{userId:user.id, boardId:activeBoardId, invitedUserId});
    if(res && res.error){ alert(res.error); return; }
    await loadBoardMembers();
  };
  const removeMember = async (shareId) => {
    if(!confirm("Mitglied wirklich aus diesem Board entfernen?")) return;
    await api.del("board-shares",{userId:user.id, shareId});
    await loadBoardMembers();
  };
  const respondInvite = async (shareId, accept) => {
    await api.post("board-invite-respond",{userId:user.id, shareId, accept});
    await loadBoards();
  };
  const leaveBoard = async (boardId) => {
    if(!confirm("Möchtest du dieses Board wirklich verlassen? Du verlierst dann den Zugriff.")) return;
    await api.post("board-leave",{userId:user.id, boardId});
    if(activeBoardId===boardId) setActiveBoardId(null);
    await loadBoards();
  };

  // ═══ Export / Import ═══
  const exportBoard = () => {
    const board = boards.find(b=>b.id===activeBoardId);
    const exportData = {
      version: 1,
      name: board?.name || "Board",
      exportedAt: new Date().toISOString(),
      data: {nodes, edges, view}
    };
    const blob = new Blob([JSON.stringify(exportData,null,2)], {type:"application/json"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `whiteboard-${(board?.name||"board").replace(/[^\w]/g,"_")}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setShowBoardMenu(false);
  };
  const importBoardFromFile = async (file) => {
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if(!parsed.data || !Array.isArray(parsed.data.nodes)) throw new Error("Ungültiges Format");
      // Neues Board mit den importierten Daten anlegen
      const res = await api.post("boards",{userId:user.id, name:(parsed.name||"Importiert")+" (Import)", data:parsed.data});
      if(res && res.id){
        const fresh = await api.get("boards","userId="+user.id);
        setBoards(fresh||[]);
        setActiveBoardId(res.id);
      }
    } catch(e) {
      alert("Import fehlgeschlagen: "+e.message);
    }
    setShowBoardMenu(false);
  };

  // ═══ Render ═══
  if(boards.length === 0 || !activeBoardId) {
    if(invites.length>0){
      return (<div style={{padding:40,maxWidth:600,margin:"0 auto"}}>
        <h2 style={{fontSize:20,fontWeight:700,marginBottom:8}}>Du hast Einladungen</h2>
        <p style={{fontSize:13,color:T.textDim,marginBottom:20}}>Du hast aktuell keine eigenen Boards, aber {invites.length} Einladung{invites.length>1?"en":""} zu Boards anderer User. Nimm eine an um loszulegen — oder erstelle ein eigenes Board.</p>
        <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:20}}>
          {invites.map(inv=>(<div key={inv.share_id} style={{display:"flex",alignItems:"center",gap:10,padding:14,background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:10,flexWrap:"wrap"}}>
            <div style={{flex:1,minWidth:200}}>
              <div style={{fontSize:14,fontWeight:700,color:T.text}}>"{inv.name}"</div>
              <div style={{fontSize:12,color:T.textDim,marginTop:2}}>von {inv.owner_name}</div>
            </div>
            <Button size="sm" icon="check" onClick={()=>respondInvite(inv.share_id, true)}>Annehmen</Button>
            <Button size="sm" variant="secondary" onClick={()=>respondInvite(inv.share_id, false)}>Ablehnen</Button>
          </div>))}
        </div>
        <Button icon="add" onClick={()=>{setCreateBoardName("");setShowCreateBoard(true);}}>Eigenes Board erstellen</Button>
        {/* Modals brauchen wir auch hier */}
        <Modal open={showCreateBoard} onClose={()=>setShowCreateBoard(false)} title="Neues Board erstellen" width={420}>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <Input label="Name" value={createBoardName} onChange={e=>setCreateBoardName(e.target.value)} placeholder="z.B. Marketing-Plan"/>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <Button variant="secondary" onClick={()=>setShowCreateBoard(false)}>Abbrechen</Button>
              <Button icon="check" onClick={createBoard}>Erstellen</Button>
            </div>
          </div>
        </Modal>
      </div>);
    }
    return <div style={{padding:40,textAlign:"center",color:T.textDim}}>Whiteboard wird vorbereitet...</div>;
  }
  if(!loaded) return <div style={{padding:40,textAlign:"center",color:T.textDim}}>Board wird geladen...</div>;

  const activeBoard = boards.find(b=>b.id===activeBoardId);
  const detailNode = showNodeDetail ? nodes.find(n=>n.id===showNodeDetail) : null;

  return (<div style={{display:"flex",flexDirection:"column",height:isMobileDevice?"calc(100vh - 72px)":"calc(100vh - 64px)",margin:isMobileDevice?"0 -12px -16px -12px":"-32px -36px"}}>
    {/* Toolbar */}
    <div style={{padding:isMobileDevice?"8px 10px":"10px 16px",background:T.bgCard,borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:isMobileDevice?6:8,flexWrap:"wrap",position:"relative"}}>
      {!isMobileDevice && <h2 style={{fontSize:16,fontWeight:700,marginRight:4}}>Whiteboard</h2>}

      {/* Board-Selector */}
      <div style={{position:"relative"}}>
        <button onClick={()=>setShowBoardMenu(!showBoardMenu)} style={{display:"flex",alignItems:"center",gap:6,padding:"6px 12px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,cursor:"pointer",fontSize:13,fontWeight:600,minWidth:isMobileDevice?100:160,maxWidth:isMobileDevice?140:300}}>
          {activeBoard?.access==="shared" && <span style={{fontSize:11}} title={`Geteilt von ${activeBoard.owner_name}`}>👥</span>}
          {activeBoard?.access==="owner" && boardMembers.some(m=>m.accepted) && <span style={{fontSize:11}} title="Du teilst dieses Board">👥</span>}
          <span style={{flex:1,textAlign:"left",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{activeBoard?.name||"Board"}</span>
          <span style={{fontSize:10}}>▼</span>
        </button>
        {showBoardMenu && (
          <div style={{position:"absolute",top:"100%",left:0,marginTop:4,background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:10,boxShadow:"0 8px 24px rgba(0,0,0,0.3)",zIndex:50,minWidth:300,maxHeight:480,overflowY:"auto"}}>
            {(() => {
              const ownBoards = boards.filter(b=>b.access==="owner");
              const sharedBoards = boards.filter(b=>b.access==="shared");
              return (<>
                {/* Eigene Boards */}
                <div style={{padding:8,borderBottom:`1px solid ${T.border}`}}>
                  <div style={{fontSize:11,color:T.textDim,textTransform:"uppercase",fontWeight:600,letterSpacing:0.5,padding:"4px 8px"}}>Meine Boards ({ownBoards.length})</div>
                  {ownBoards.map(b=>(<div key={b.id} onClick={()=>{setActiveBoardId(b.id);setShowBoardMenu(false);}} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:b.id===activeBoardId?T.accentGlow:"transparent",borderRadius:6,cursor:"pointer",marginBottom:2}} onMouseEnter={e=>{if(b.id!==activeBoardId)e.currentTarget.style.background=T.bgHover;}} onMouseLeave={e=>{if(b.id!==activeBoardId)e.currentTarget.style.background="transparent";}}>
                    <div style={{flex:1,fontSize:13,fontWeight:b.id===activeBoardId?700:500,color:b.id===activeBoardId?T.accent:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{b.name}</div>
                    {boards.length>1&&<button onClick={e=>{e.stopPropagation();setConfirmDeleteBoard(b.id);setShowBoardMenu(false);}} title="Löschen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4,fontSize:14}}>🗑</button>}
                  </div>))}
                </div>

                {/* Geteilte Boards */}
                {sharedBoards.length>0 && (<div style={{padding:8,borderBottom:`1px solid ${T.border}`}}>
                  <div style={{fontSize:11,color:T.textDim,textTransform:"uppercase",fontWeight:600,letterSpacing:0.5,padding:"4px 8px"}}>Geteilt mit mir ({sharedBoards.length})</div>
                  {sharedBoards.map(b=>(<div key={b.id} onClick={()=>{setActiveBoardId(b.id);setShowBoardMenu(false);}} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:b.id===activeBoardId?T.accentGlow:"transparent",borderRadius:6,cursor:"pointer",marginBottom:2}} onMouseEnter={e=>{if(b.id!==activeBoardId)e.currentTarget.style.background=T.bgHover;}} onMouseLeave={e=>{if(b.id!==activeBoardId)e.currentTarget.style.background="transparent";}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:b.id===activeBoardId?700:500,color:b.id===activeBoardId?T.accent:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{b.name}</div>
                      <div style={{fontSize:10,color:T.textDim,marginTop:1}}>von {b.owner_name}</div>
                    </div>
                    <button onClick={e=>{e.stopPropagation();leaveBoard(b.id);}} title="Verlassen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4,fontSize:13}}>✕</button>
                  </div>))}
                </div>)}

                {/* Aktionen */}
                <div style={{padding:8,display:"flex",flexDirection:"column",gap:4}}>
                  <button onClick={()=>{setCreateBoardName("");setShowCreateBoard(true);setShowBoardMenu(false);}} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:"transparent",border:"none",color:T.text,cursor:"pointer",fontSize:13,borderRadius:6,textAlign:"left"}} onMouseEnter={e=>e.currentTarget.style.background=T.bgHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>＋ Neues Board</button>
                  {activeBoard?.access==="owner" && <button onClick={()=>{setRenameValue(activeBoard?.name||"");setShowRename(true);setShowBoardMenu(false);}} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:"transparent",border:"none",color:T.text,cursor:"pointer",fontSize:13,borderRadius:6,textAlign:"left"}} onMouseEnter={e=>e.currentTarget.style.background=T.bgHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>✎ Aktuelles umbenennen</button>}
                  {activeBoard?.access==="owner" && <button onClick={()=>{setShowShareModal(true);setShowBoardMenu(false);}} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:"transparent",border:"none",color:T.text,cursor:"pointer",fontSize:13,borderRadius:6,textAlign:"left"}} onMouseEnter={e=>e.currentTarget.style.background=T.bgHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>👥 Teilen / Mitglieder verwalten</button>}
                  <button onClick={exportBoard} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:"transparent",border:"none",color:T.text,cursor:"pointer",fontSize:13,borderRadius:6,textAlign:"left"}} onMouseEnter={e=>e.currentTarget.style.background=T.bgHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>↓ Exportieren (JSON)</button>
                  <button onClick={()=>importInputRef.current?.click()} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:"transparent",border:"none",color:T.text,cursor:"pointer",fontSize:13,borderRadius:6,textAlign:"left"}} onMouseEnter={e=>e.currentTarget.style.background=T.bgHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>↑ Importieren (JSON)</button>
                  <input ref={importInputRef} type="file" accept="application/json" style={{display:"none"}} onChange={e=>{const f=e.target.files?.[0];if(f)importBoardFromFile(f);e.target.value="";}}/>
                </div>
              </>);
            })()}
          </div>
        )}
      </div>

      <div style={{width:1,height:24,background:T.border,margin:isMobileDevice?"0 2px":"0 4px"}}/>
      <Button size="sm" variant="secondary" icon="add" onClick={()=>{const c=centerOfView();addNode(c.x,c.y);}}>{isMobileDevice?"":"Knoten"}</Button>
      <Button size="sm" variant="secondary" icon="add" onClick={()=>{const c=centerOfView();addNode(c.x,c.y,{text:"Notiz",color:7,type:"sticky"});}}>{isMobileDevice?"📝":"Sticky"}</Button>
      <Button size="sm" variant="secondary" onClick={()=>{const c=centerOfView();addNode(c.x,c.y,{text:"Text",type:"textbox",color:0});}}>{isMobileDevice?<span style={{fontWeight:800,fontSize:15}}>T</span>:<span style={{display:"inline-flex",alignItems:"center",gap:5}}><span style={{fontWeight:800,fontSize:14}}>T</span>Text</span>}</Button>
      <Button size="sm" variant="secondary" onClick={()=>{const c=centerOfView();addNode(c.x,c.y,{type:"checklist",text:"",color:0,title:"Checkliste"});}}>{isMobileDevice
        ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
        : <span style={{display:"inline-flex",alignItems:"center",gap:5}}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>Checkliste</span>}</Button>
      <Button size="sm" variant="secondary" icon="add" onClick={()=>setShowAddImage(true)}>{isMobileDevice?"🖼":"Bild"}</Button>
      <Button size="sm" variant="secondary" icon="add" onClick={()=>{setLinkUrl("");setLinkLabel("");setShowAddLink(true);}}>{isMobileDevice?"🔗":"Link"}</Button>
      <Button size="sm" variant="secondary" icon="upload" onClick={()=>standaloneFileInputRef.current?.click()}>{isMobileDevice?"":"Datei"}</Button>
      <input ref={standaloneFileInputRef} type="file" style={{display:"none"}} onChange={onStandaloneFilePicked}/>
      <div style={{width:1,height:24,background:T.border,margin:isMobileDevice?"0 2px":"0 4px"}}/>
      <button onClick={()=>setView(v=>({...v,zoom:Math.max(0.2,v.zoom*0.85)}))} style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:6,padding:"4px 10px",color:T.text,cursor:"pointer",fontSize:14,fontWeight:600}}>−</button>
      {!isMobileDevice && <span style={{fontSize:12,color:T.textDim,fontFamily:"'JetBrains Mono',monospace",minWidth:50,textAlign:"center"}}>{Math.round(view.zoom*100)}%</span>}
      <button onClick={()=>setView(v=>({...v,zoom:Math.min(3,v.zoom*1.15)}))} style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:6,padding:"4px 10px",color:T.text,cursor:"pointer",fontSize:14,fontWeight:600}}>+</button>
      <Button size="sm" variant="ghost" onClick={fitToContent}>{isMobileDevice?"⊕":"Zentrieren"}</Button>
      <div style={{flex:1}}/>
      {!isMobileDevice && <span style={{fontSize:11,color:T.textDim,fontFamily:"'JetBrains Mono',monospace"}}>{saving?"Speichert...":savedAt?`✓ ${savedAt.toLocaleTimeString("de",{hour:"2-digit",minute:"2-digit"})}`:""}</span>}
    </div>

    {/* Einladungen-Banner */}
    {invites.length>0 && (
      <div style={{padding:"10px 16px",background:T.accentGlow,borderBottom:`1px solid ${T.accent}`,display:"flex",flexDirection:"column",gap:8}}>
        {invites.map(inv=>(<div key={inv.share_id} style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <span style={{fontSize:13,color:T.text,flex:1,minWidth:200}}>📩 <strong>{inv.owner_name}</strong> hat dich auf das Board <strong>"{inv.name}"</strong> eingeladen.</span>
          <Button size="sm" icon="check" onClick={()=>respondInvite(inv.share_id, true)}>Annehmen</Button>
          <Button size="sm" variant="secondary" onClick={()=>respondInvite(inv.share_id, false)}>Ablehnen</Button>
        </div>))}
      </div>
    )}

    {/* Hilfe */}
    {nodes.length===0 && (
      <div style={{position:"absolute",top:140,left:"50%",transform:"translateX(-50%)",background:T.bgCard,border:`1px dashed ${T.border}`,borderRadius:14,padding:"24px 32px",textAlign:"center",zIndex:5,maxWidth:440}}>
        <div style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em",marginBottom:6}}>{activeBoard?.name}</div>
        <div style={{fontSize:13,color:T.textDim,lineHeight:1.6}}>Doppelklick irgendwo → neuer Knoten<br/>Doppelklick auf Knoten → Detail-Ansicht öffnen<br/>Knoten verbinden über die Punkte am Rand<br/>Linien biegen über den mittleren Punkt</div>
      </div>
    )}

    {/* Canvas */}
    <div ref={canvasRef}
      onPointerDown={onCanvasPointerDown}
      onDoubleClick={onCanvasDoubleClick}
      style={{flex:1,position:"relative",overflow:"hidden",cursor:panning?"grabbing":"grab",background:_theme==="dark"?"#0d0d14":"#fafafd",backgroundImage:`radial-gradient(circle, ${T.border} 1px, transparent 1px)`,backgroundSize:`${20*view.zoom}px ${20*view.zoom}px`,backgroundPosition:`${view.x}px ${view.y}px`,touchAction:"none",userSelect:"none"}}>

      {/* SVG für Verbindungen — ÜBER allen Knoten als overlay, mit pointer-events nur auf paths */}
      <svg style={{position:"absolute",left:0,top:0,width:"100%",height:"100%",overflow:"visible",pointerEvents:"none"}}>
        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
            <path d="M 0 0 L 10 5 L 0 10 z" fill={T.textMuted}/>
          </marker>
          <marker id="arrow-selected" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
            <path d="M 0 0 L 10 5 L 0 10 z" fill={T.accent}/>
          </marker>
        </defs>
        <g transform={`translate(${view.x},${view.y}) scale(${view.zoom})`}>
          {edges.map(edge=>{
            const a = nodes.find(n=>n.id===edge.from);
            const b = nodes.find(n=>n.id===edge.to);
            if(!a||!b) return null;
            const isSel = selectedEdgeId===edge.id;
            const path = edgePath(a,b,edge.mid);
            // Mid-Position für Handle berechnen
            const ax = a.x+a.w/2, ay = a.y+a.h/2, bx = b.x+b.w/2, by = b.y+b.h/2;
            const cx = edge.mid?edge.mid.x:(ax+bx)/2, cy = edge.mid?edge.mid.y:(ay+by)/2;
            return (<g key={edge.id} data-edge>
              {/* Sichtbare Linie */}
              <path d={path} fill="none" stroke={isSel?T.accent:T.textMuted} strokeWidth={isSel?2.5:2} markerEnd={isSel?"url(#arrow-selected)":"url(#arrow)"} style={{transition:"stroke 0.15s"}}/>
              {/* Unsichtbarer dicker Stroke für leichteres Anklicken */}
              <path d={path} fill="none" stroke="transparent" strokeWidth={16} style={{cursor:"pointer",pointerEvents:"stroke"}} onClick={e=>{e.stopPropagation();setSelectedEdgeId(edge.id);setSelectedId(null);}}/>
              {/* Mid-Handle (nur sichtbar wenn Edge ausgewählt) */}
              {isSel && (
                <g style={{pointerEvents:"all"}}>
                  <circle cx={cx} cy={cy} r={9} fill={T.accent} stroke="#fff" strokeWidth={2} style={{cursor:"grab"}} onPointerDown={e=>startEdgeMidDrag(e,edge.id)}/>
                </g>
              )}
            </g>);
          })}
          {/* Vorschau-Linie beim Verbinden */}
          {connectFrom && connectMouse && (()=>{
            const a = nodes.find(n=>n.id===connectFrom);
            if(!a) return null;
            return <line x1={a.x+a.w/2} y1={a.y+a.h/2} x2={connectMouse.x} y2={connectMouse.y} stroke={T.accent} strokeWidth={2} strokeDasharray="6 4"/>;
          })()}
        </g>
      </svg>

      {/* Edge Toolbar (wenn Edge ausgewählt) */}
      {selectedEdgeId && (()=>{
        const edge = edges.find(e=>e.id===selectedEdgeId);
        if(!edge) return null;
        const a = nodes.find(n=>n.id===edge.from);
        const b = nodes.find(n=>n.id===edge.to);
        if(!a||!b) return null;
        const ax = a.x+a.w/2, ay = a.y+a.h/2, bx = b.x+b.w/2, by = b.y+b.h/2;
        const cx = edge.mid?edge.mid.x:(ax+bx)/2, cy = edge.mid?edge.mid.y:(ay+by)/2;
        const sx = cx*view.zoom + view.x, sy = cy*view.zoom + view.y;
        return (<div data-control style={{position:"absolute",left:sx-70,top:sy-50,display:"flex",gap:4,background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:8,padding:4,boxShadow:"0 4px 12px rgba(0,0,0,0.3)",zIndex:30}} onPointerDown={e=>e.stopPropagation()}>
          <button onClick={()=>resetEdgeCurve(edge.id)} title="Krümmung zurücksetzen" style={{background:"none",border:"none",cursor:"pointer",padding:"4px 8px",color:T.text,fontSize:12,borderRadius:4}}>↺</button>
          <button onClick={()=>deleteEdge(edge.id)} title="Verbindung löschen" style={{background:"none",border:"none",cursor:"pointer",padding:"4px 8px",color:T.danger,fontSize:14,borderRadius:4}}>🗑</button>
        </div>);
      })()}

      {/* Knoten — ÜBER der SVG-Schicht damit sie klickbar bleiben (SVG hat pointer-events:none) */}
      <div style={{position:"absolute",left:0,top:0,transform:`translate(${view.x}px, ${view.y}px) scale(${view.zoom})`,transformOrigin:"0 0",pointerEvents:"none"}}>
        {nodes.map(node=>{
          const c = NODE_COLORS[node.color]||NODE_COLORS[0];
          const isSelected = selectedId===node.id;
          const isEditing = editingId===node.id;
          const isSticky = node.type==="sticky";
          const isImage = node.type==="image";
          const isLink = node.type==="link";
          const isFile = node.type==="file";
          const isTextbox = node.type==="textbox";
          const isChecklist = node.type==="checklist";
          const hasFiles = node.fileCount>0;
          // Hintergrund je Typ
          const bg = isImage?"transparent":isTextbox?"transparent":isLink?T.bgCard:isFile?T.bgCard:c.bg;
          const baseFg = isImage?T.text:isTextbox?T.text:isLink?T.text:isFile?T.text:c.fg;
          const fg = node.textColor || baseFg; // eigene Textfarbe überschreibt
          const radius = isSticky?4:isImage?12:14;
          // Text-Gestaltung
          const nodeFontSize = node.fontSize || (isTextbox?18:14);
          const nodeBold = node.bold ? 800 : (isSticky?500:600);
          const nodeAlign = node.align || "center";
          const fileDownloadUrl = isFile && node.fileId ? `/api/whiteboard-file/${node.fileId}?userId=${user.id}` : "";
          return (<div key={node.id} data-node={node.id}
            onPointerDown={e=>onNodePointerDown(e,node)}
            onContextMenu={e=>{e.preventDefault();e.stopPropagation();setSelectedId(node.id);setShowNodeDetail(node.id);}}
            onDoubleClick={e=>{
              e.stopPropagation();
              // Link & File: direkt öffnen statt Detail
              if(isLink && node.url){window.open(node.url,"_blank","noopener,noreferrer");return;}
              if(isFile && fileDownloadUrl){window.open(fileDownloadUrl,"_blank");return;}
              if(isChecklist) return; // Checkliste hat eigene Eingabefelder
              // Bild & alle anderen Knoten: Text editieren
              setEditingId(node.id);
              setEditText(node.text||"");
              setSelectedId(node.id);
            }}
            style={{position:"absolute",left:node.x,top:node.y,width:node.w,minHeight:node.h,background:bg,color:fg,border:`2px solid ${isSelected?T.accent:isTextbox?(isSelected?T.accent:"transparent"):isLink||isFile?T.border:"transparent"}`,borderRadius:radius,padding:isImage?0:isTextbox?"8px 10px":isChecklist?"12px 14px":"14px 16px",fontSize:nodeFontSize,fontWeight:nodeBold,cursor:node.locked?"default":draggingNode?.id===node.id?"grabbing":"grab",boxShadow:isTextbox?(isSelected?"0 0 0 1px "+T.accent:"none"):isSelected?"0 8px 24px rgba(0,0,0,0.35)":isSticky?"3px 5px 14px rgba(0,0,0,0.25)":isImage?"0 6px 18px rgba(0,0,0,0.25)":"0 4px 12px rgba(0,0,0,0.18)",transform:isSticky?"rotate(-1.5deg)":"none",userSelect:"none",touchAction:"none",display:"flex",alignItems:isChecklist?"stretch":"center",justifyContent:isChecklist?"flex-start":nodeAlign==="left"?"flex-start":nodeAlign==="right"?"flex-end":"center",flexDirection:isChecklist?"column":"row",textAlign:isChecklist?"left":nodeAlign,wordBreak:"break-word",whiteSpace:"pre-wrap",pointerEvents:"auto",transition:"box-shadow 0.15s, border-color 0.15s",overflow:"visible"}}>
            {isChecklist ? (
              // Checklisten-Knoten
              <div style={{width:"100%",display:"flex",flexDirection:"column",gap:8}}>
                <input value={node.title||""} onChange={e=>{markChecklistActivity();updateNode(node.id,{title:e.target.value});}} onPointerDown={e=>e.stopPropagation()} placeholder="Titel" style={{background:"transparent",border:"none",outline:"none",color:fg,fontSize:15,fontWeight:700,fontFamily:"inherit",width:"100%"}}/>
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  {(node.items||[]).map(it=>(
                    <div key={it.id} style={{display:"flex",alignItems:"flex-start",gap:8}}>
                      <button onPointerDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation();updateCheckItem(node.id,it.id,{done:!it.done});}} style={{flexShrink:0,marginTop:2,width:18,height:18,borderRadius:5,border:`2px solid ${it.done?T.success:T.border}`,background:it.done?T.success:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0}}>
                        {it.done && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                      </button>
                      <input value={it.text} onChange={e=>updateCheckItem(node.id,it.id,{text:e.target.value})} onPointerDown={e=>e.stopPropagation()} onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();addCheckItem(node.id);}}} placeholder="Punkt..." style={{flex:1,minWidth:0,background:"transparent",border:"none",outline:"none",color:fg,fontSize:13,fontWeight:400,fontFamily:"inherit",textDecoration:it.done?"line-through":"none",opacity:it.done?0.6:1}}/>
                      <button onPointerDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation();deleteCheckItem(node.id,it.id);}} style={{flexShrink:0,background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:2,fontSize:14,lineHeight:1}}>×</button>
                    </div>
                  ))}
                </div>
                <button onPointerDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation();addCheckItem(node.id);}} style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:12,fontWeight:600,padding:"2px 0"}}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Punkt
                </button>
              </div>
            ) : isImage ? (
              // Bild-Knoten: Bild oben, editierbarer Name unten
              <div style={{width:"100%",height:"100%",display:"flex",flexDirection:"column",borderRadius:12,overflow:"hidden"}}>
                <div style={{flex:1,minHeight:0,background:_theme==="dark"?"#1a1a2e":"#f0f0f4",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
                  {node.imageSrc ? (
                    <img src={node.imageSrc} alt={node.text||"Bild"} draggable={false} style={{width:"100%",height:"100%",objectFit:"contain",display:"block",pointerEvents:"none"}}/>
                  ) : (
                    <div style={{padding:20,color:T.textDim,fontSize:13}}>Kein Bild</div>
                  )}
                </div>
                <div style={{padding:"8px 10px",background:T.bgCard,color:T.text,fontSize:12,fontWeight:600,textAlign:"center",borderTop:`1px solid ${T.border}`}}>
                  {isEditing ? (
                    <input autoFocus value={editText} onChange={e=>setEditText(e.target.value)} onBlur={commitEdit} onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();commitEdit();}}} style={{width:"100%",background:"transparent",border:"none",outline:"none",color:T.text,fontSize:12,fontWeight:600,fontFamily:"inherit",textAlign:"center"}}/>
                  ) : (
                    <div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{node.text||"Unbenannt"}</div>
                  )}
                </div>
              </div>
            ) : isLink ? (
              // Link-Knoten — als Button-artige Karte
              <div style={{display:"flex",alignItems:"center",gap:10,width:"100%"}}>
                <div style={{width:36,height:36,borderRadius:10,background:c.bg,color:c.fg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>🔗</div>
                <div style={{flex:1,minWidth:0,textAlign:"left"}}>
                  <div style={{fontSize:13,fontWeight:700,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{node.text||"Link"}</div>
                  <div style={{fontSize:11,color:T.textDim,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{(node.url||"").replace(/^https?:\/\//,"")}</div>
                </div>
                <div style={{fontSize:14,color:T.textDim,flexShrink:0}}>↗</div>
              </div>
            ) : isFile ? (
              // Datei-Knoten — als Download-Button
              <div style={{display:"flex",alignItems:"center",gap:10,width:"100%"}}>
                <div style={{width:36,height:36,borderRadius:10,background:c.bg,color:c.fg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg></div>
                <div style={{flex:1,minWidth:0,textAlign:"left"}}>
                  <div style={{fontSize:13,fontWeight:700,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{node.text||node.fileName||"Datei"}</div>
                  <div style={{fontSize:11,color:T.textDim}}>{node.fileId?"Klick zum Öffnen":"Lädt hoch..."}</div>
                </div>
                <div style={{fontSize:14,color:T.textDim,flexShrink:0}}>↓</div>
              </div>
            ) : isEditing ? (
              <textarea autoFocus value={editText} onChange={e=>setEditText(e.target.value)} onBlur={commitEdit} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();commitEdit();}}} style={{width:"100%",minHeight:30,background:"transparent",border:"none",outline:"none",color:fg,fontSize:nodeFontSize,fontWeight:nodeBold,fontFamily:"inherit",resize:"none",textAlign:nodeAlign}}/>
            ) : (
              <div style={{position:"relative",width:"100%"}}>
                <div style={{textAlign:nodeAlign}}>{node.text||(isTextbox?"Text":"(leer)")}</div>
                {node.description && <div style={{fontSize:11,opacity:0.85,marginTop:6,fontWeight:400,whiteSpace:"pre-wrap",maxHeight:60,overflow:"hidden",textOverflow:"ellipsis"}}>{node.description.slice(0,80)}{node.description.length>80?"...":""}</div>}
                {hasFiles && <div style={{position:"absolute",top:-6,right:-6,background:T.bgCard,color:T.text,fontSize:10,fontWeight:700,padding:"2px 6px",borderRadius:10,border:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:3}}><svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>{node.fileCount}</div>}
              </div>
            )}
            {/* Connection Handles */}
            {/* Sichtbares Schloss an gesperrten Knoten (Ecke oben links) */}
            {node.locked && <div data-control onPointerDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation();updateNode(node.id,{locked:false});}} title="Fixiert – klicken zum Entsperren" style={{position:"absolute",top:-9,left:-9,width:22,height:22,borderRadius:"50%",background:T.accent,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 2px 6px rgba(0,0,0,0.3)",zIndex:15,cursor:"pointer"}}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            </div>}
            {isSelected && !isEditing && (<>
              {[
                {pos:"right",style:{right:-8,top:"50%",transform:"translateY(-50%)"}},
                {pos:"left",style:{left:-8,top:"50%",transform:"translateY(-50%)"}},
                {pos:"top",style:{top:-8,left:"50%",transform:"translateX(-50%)"}},
                {pos:"bottom",style:{bottom:-8,left:"50%",transform:"translateX(-50%)"}}
              ].map(h=>(<div key={h.pos} data-control onPointerDown={e=>startConnect(e,node.id)} style={{position:"absolute",...h.style,width:14,height:14,borderRadius:"50%",background:T.accent,border:"2px solid #fff",cursor:"crosshair",touchAction:"none",zIndex:10}} title="Verbinden"/>))}
              {/* Resize-Griff unten rechts (nicht bei gesperrten Knoten) */}
              {!node.locked && <div data-control onPointerDown={e=>{e.stopPropagation();setResizingNode({id:node.id,startW:node.w,startH:node.h,startX:e.clientX,startY:e.clientY});}} title="Größe ziehen" style={{position:"absolute",right:-6,bottom:-6,width:16,height:16,borderRadius:4,background:T.bgCard,border:`2px solid ${T.accent}`,cursor:"nwse-resize",touchAction:"none",zIndex:12,display:"flex",alignItems:"center",justifyContent:"center"}}>
                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={T.accent} strokeWidth="2.5" strokeLinecap="round"><path d="M8 20h12M14 14h6M20 8v12"/></svg>
              </div>}
              {/* Action Toolbar */}
              <div data-control style={{position:"absolute",top:-44,left:"50%",transform:"translateX(-50%)",display:"flex",gap:2,background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:8,padding:4,boxShadow:"0 4px 12px rgba(0,0,0,0.3)",whiteSpace:"nowrap",zIndex:20}}>
                <button onPointerDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation();setShowNodeDetail(node.id);}} title="Notizen & Datei-Anhänge" style={{background:T.accent,color:"#fff",border:"none",cursor:"pointer",padding:"4px 10px",fontSize:11,fontWeight:600,borderRadius:4}}>📋 Notizen / Dateien</button>
                <button onPointerDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation();setShowColorPicker(showColorPicker===node.id?null:node.id);}} title="Farbe" style={{background:"none",border:"none",cursor:"pointer",padding:"4px 8px",color:T.text,fontSize:13,borderRadius:4}}>🎨</button>
                <button onPointerDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation();updateNode(node.id,{locked:!node.locked});}} title={node.locked?"Entsperren (verschiebbar machen)":"An dieser Stelle fixieren"} style={{background:node.locked?T.accent:"none",border:"none",cursor:"pointer",padding:"4px 8px",color:node.locked?"#fff":T.text,fontSize:13,borderRadius:4,display:"flex",alignItems:"center"}}>
                  {node.locked
                    ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>}
                </button>
                <button onPointerDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation();setEditingId(node.id);setEditText(node.text);}} title="Text editieren" style={{background:"none",border:"none",cursor:"pointer",padding:"4px 8px",color:T.text,fontSize:13,borderRadius:4}}>✎</button>
                <button onPointerDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation();setConfirmDeleteNode({kind:"node", id:node.id});}} title="Löschen" style={{background:"none",border:"none",cursor:"pointer",padding:"4px 8px",color:T.danger,fontSize:14,borderRadius:4}}>🗑</button>
              </div>
              {showColorPicker===node.id && (
                <div data-control style={{position:"absolute",top:-92,left:"50%",transform:"translateX(-50%)",display:"flex",gap:4,background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:8,padding:6,boxShadow:"0 4px 12px rgba(0,0,0,0.3)",zIndex:25}}>
                  {NODE_COLORS.map((col,i)=>(<button key={i} onPointerDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation();updateNode(node.id,{color:i});setShowColorPicker(null);}} title={col.name} style={{width:24,height:24,borderRadius:6,background:col.bg,border:node.color===i?`2px solid ${T.accent}`:"2px solid transparent",cursor:"pointer"}}/>))}
                </div>
              )}
              {/* Format-Leiste für Text (Knoten, Sticky, Textfeld) */}
              {(node.type==="node"||node.type==="sticky"||node.type==="textbox"||!node.type) && (
                <div data-control style={{position:"absolute",top:node.h+10,left:"50%",transform:"translateX(-50%)",display:"flex",alignItems:"center",gap:3,background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:8,padding:4,boxShadow:"0 4px 12px rgba(0,0,0,0.3)",whiteSpace:"nowrap",zIndex:20}}>
                  <button onPointerDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation();updateNode(node.id,{fontSize:Math.max(10,(node.fontSize||14)-2)});}} title="Kleiner" style={{background:"none",border:"none",cursor:"pointer",padding:"3px 7px",color:T.text,fontSize:13,fontWeight:700,borderRadius:4}}>A−</button>
                  <span style={{fontSize:11,color:T.textDim,minWidth:22,textAlign:"center",fontFamily:"monospace"}}>{node.fontSize||14}</span>
                  <button onPointerDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation();updateNode(node.id,{fontSize:Math.min(48,(node.fontSize||14)+2)});}} title="Größer" style={{background:"none",border:"none",cursor:"pointer",padding:"3px 7px",color:T.text,fontSize:15,fontWeight:700,borderRadius:4}}>A+</button>
                  <div style={{width:1,height:18,background:T.border,margin:"0 2px"}}/>
                  <button onPointerDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation();updateNode(node.id,{bold:!node.bold});}} title="Fett" style={{background:node.bold?T.accent:"none",color:node.bold?"#fff":T.text,border:"none",cursor:"pointer",padding:"3px 9px",fontSize:13,fontWeight:800,borderRadius:4}}>B</button>
                  <div style={{width:1,height:18,background:T.border,margin:"0 2px"}}/>
                  {[["left","M3 5h18M3 10h12M3 15h18M3 20h12"],["center","M3 5h18M6 10h12M3 15h18M6 20h12"],["right","M3 5h18M9 10h12M3 15h18M9 20h12"]].map(([al,d])=>(
                    <button key={al} onPointerDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation();updateNode(node.id,{align:al});}} title={al==="left"?"Linksbündig":al==="center"?"Zentriert":"Rechtsbündig"} style={{background:(node.align||"center")===al?T.accent:"none",border:"none",cursor:"pointer",padding:"4px 6px",borderRadius:4,display:"flex",alignItems:"center"}}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={(node.align||"center")===al?"#fff":T.text} strokeWidth="2" strokeLinecap="round"><path d={d}/></svg>
                    </button>
                  ))}
                  <div style={{width:1,height:18,background:T.border,margin:"0 2px"}}/>
                  {["",  "#e23b3b","#2f9e44","#1971c2","#f08c00","#9c36b5"].map((col,i)=>(
                    <button key={i} onPointerDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation();updateNode(node.id,{textColor:col});}} title={col?"Textfarbe":"Standardfarbe"} style={{width:18,height:18,borderRadius:"50%",background:col||"transparent",border:(node.textColor||"")===col?`2px solid ${T.accent}`:`1px solid ${T.border}`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,color:T.textDim}}>{col?"":"A"}</button>
                  ))}
                </div>
              )}
            </>)}
          </div>);
        })}
      </div>
    </div>

    {/* Node Detail Modal */}
    <Modal open={!!detailNode} onClose={()=>setShowNodeDetail(null)} title={detailNode?.text||"Knoten-Detail"} width={620}>
      {detailNode && <NodeDetailPanel user={user} boardId={activeBoardId} node={detailNode} onUpdate={patch=>updateNode(detailNode.id,patch)}/>}
    </Modal>

    {/* Share Modal — Mitgliederverwaltung */}
    <Modal open={showShareModal} onClose={()=>setShowShareModal(false)} title={`"${activeBoard?.name}" teilen`} width={520}>
      <div style={{display:"flex",flexDirection:"column",gap:18}}>
        {/* Aktuelle Mitglieder */}
        <div>
          <div style={{fontSize:12,fontWeight:600,color:T.textDim,textTransform:"uppercase",letterSpacing:0.4,marginBottom:10}}>Mitglieder ({boardMembers.length})</div>
          {boardMembers.length===0 ? (
            <div style={{fontSize:13,color:T.textDim,padding:"10px 0"}}>Noch niemand eingeladen — füge unten Personen hinzu.</div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {boardMembers.map(m=>(<div key={m.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8}}>
                <Avatar userId={m.user_id} name={m.name} size={32} editable={false}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:600,color:T.text}}>{m.name}</div>
                  <div style={{fontSize:11,color:m.accepted?T.success:T.warning}}>{m.accepted?"✓ Mitglied":"⏳ Einladung ausstehend"}</div>
                </div>
                <button onClick={()=>removeMember(m.id)} style={{background:"none",border:`1px solid ${T.border}`,borderRadius:6,padding:"4px 10px",color:T.danger,cursor:"pointer",fontSize:11,fontWeight:600}}>Entfernen</button>
              </div>))}
            </div>
          )}
        </div>

        <div style={{height:1,background:T.border}}/>

        {/* User einladen */}
        <div>
          <div style={{fontSize:12,fontWeight:600,color:T.textDim,textTransform:"uppercase",letterSpacing:0.4,marginBottom:10}}>Person einladen</div>
          {(() => {
            const alreadyInBoard = new Set(boardMembers.map(m=>m.user_id));
            alreadyInBoard.add(user.id);
            const invitable = allUsers.filter(u=>!alreadyInBoard.has(u.id));
            if(invitable.length===0) return <div style={{fontSize:13,color:T.textDim}}>Alle verfügbaren User sind bereits eingeladen.</div>;
            return (<div style={{display:"flex",flexDirection:"column",gap:6}}>
              {invitable.map(u=>(<div key={u.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8}}>
                <Avatar userId={u.id} name={u.name} size={32} editable={false}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:600,color:T.text}}>{u.name}</div>
                  <div style={{fontSize:11,color:T.textDim}}>{u.role==="admin"?"Admin":u.role==="cutter"?"Cutter":"Vertriebler"}</div>
                </div>
                <Button size="sm" variant="secondary" icon="add" onClick={()=>inviteUser(u.id)}>Einladen</Button>
              </div>))}
            </div>);
          })()}
        </div>
      </div>
    </Modal>

    {/* Rename Modal */}
    <Modal open={showRename} onClose={()=>setShowRename(false)} title="Board umbenennen" width={420}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <Input label="Neuer Name" value={renameValue} onChange={e=>setRenameValue(e.target.value)} placeholder="Board-Name"/>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <Button variant="secondary" onClick={()=>setShowRename(false)}>Abbrechen</Button>
          <Button icon="check" onClick={renameBoard}>Speichern</Button>
        </div>
      </div>
    </Modal>

    {/* Create Board Modal */}
    <Modal open={showCreateBoard} onClose={()=>setShowCreateBoard(false)} title="Neues Board erstellen" width={420}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <Input label="Name" value={createBoardName} onChange={e=>setCreateBoardName(e.target.value)} placeholder="z.B. Marketing-Plan"/>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <Button variant="secondary" onClick={()=>setShowCreateBoard(false)}>Abbrechen</Button>
          <Button icon="check" onClick={createBoard}>Erstellen</Button>
        </div>
      </div>
    </Modal>

    {/* Delete Board Confirm Modal */}
    <Modal open={!!confirmDeleteBoard} onClose={()=>setConfirmDeleteBoard(null)} title="Board löschen?" width={440}>
      <div style={{fontSize:14,color:T.textMuted,marginBottom:20,lineHeight:1.5}}>
        Willst du das Board "{boards.find(b=>b.id===confirmDeleteBoard)?.name}" wirklich löschen? Alle Knoten, Verbindungen und hochgeladenen Dateien werden unwiderruflich entfernt.
      </div>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
        <Button variant="secondary" onClick={()=>setConfirmDeleteBoard(null)}>Abbrechen</Button>
        <Button variant="danger" icon="delete" onClick={()=>deleteBoardConfirmed(confirmDeleteBoard)}>Ja, löschen</Button>
      </div>
    </Modal>

    {/* Knoten / Verbindung löschen — Sicherheitsabfrage */}
    <Modal open={!!confirmDeleteNode} onClose={()=>setConfirmDeleteNode(null)} title={confirmDeleteNode?.kind==="edge"?"Verbindung löschen?":"Element löschen?"} width={420}>
      <div style={{fontSize:14,color:T.textMuted,marginBottom:20,lineHeight:1.5}}>
        {confirmDeleteNode?.kind==="edge"
          ? "Willst du diese Verbindung wirklich löschen?"
          : "Willst du dieses Element wirklich löschen? Der Inhalt geht dabei verloren und lässt sich nicht zurückholen."}
      </div>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
        <Button variant="secondary" onClick={()=>setConfirmDeleteNode(null)}>Abbrechen</Button>
        <Button variant="danger" icon="delete" onClick={performConfirmedDelete}>Ja, löschen</Button>
      </div>
    </Modal>

    {/* Add Link Modal */}
    <Modal open={showAddLink} onClose={()=>setShowAddLink(false)} title="Link hinzufügen" width={460}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <Input label="URL" value={linkUrl} onChange={e=>setLinkUrl(e.target.value)} placeholder="https://..."/>
        <Input label="Beschriftung (optional)" value={linkLabel} onChange={e=>setLinkLabel(e.target.value)} placeholder="z.B. Projekt-Doku"/>
        <div style={{fontSize:12,color:T.textDim,lineHeight:1.5}}>Der Link wird als Bubble angezeigt. Doppelklick auf die Bubble öffnet den Link in einem neuen Tab.</div>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <Button variant="secondary" onClick={()=>setShowAddLink(false)}>Abbrechen</Button>
          <Button icon="check" onClick={addLinkNode}>Hinzufügen</Button>
        </div>
      </div>
    </Modal>

    {/* Add Image Modal */}
    <Modal open={showAddImage} onClose={()=>setShowAddImage(false)} title="Bild hinzufügen" width={460}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:T.textDim,textTransform:"uppercase",letterSpacing:0.4,marginBottom:8}}>Vom Computer hochladen</div>
          <Button variant="secondary" icon="upload" onClick={()=>imageFileInputRef.current?.click()}>Bild auswählen (max 3 MB)</Button>
          <input ref={imageFileInputRef} type="file" accept="image/*" style={{display:"none"}} onChange={onImageFilePicked}/>
        </div>
        <div style={{height:1,background:T.border}}/>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:T.textDim,textTransform:"uppercase",letterSpacing:0.4,marginBottom:8}}>Oder Bild-URL einfügen</div>
          <Input label="" value={imageUrl} onChange={e=>setImageUrl(e.target.value)} placeholder="https://example.com/bild.jpg"/>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:10}}>
            <Button variant="secondary" onClick={()=>setShowAddImage(false)}>Abbrechen</Button>
            <Button icon="check" onClick={addImageFromUrl} disabled={!imageUrl.trim()}>Hinzufügen</Button>
          </div>
        </div>
      </div>
    </Modal>
  </div>);
};

// ─── Node Detail Panel (Beschreibung + Datei-Anhänge) ───
const NodeDetailPanel = ({user, boardId, node, onUpdate}) => {
  const [files,setFiles] = useState([]);
  const [uploading,setUploading] = useState(false);
  const [uploadError,setUploadError] = useState("");
  const [description,setDescription] = useState(node.description||"");
  const [descSaved,setDescSaved] = useState(false);
  const fileInputRef = useRef(null);
  const descTimerRef = useRef(null);

  const loadFiles = async() => {
    const res = await api.get("whiteboard-files",`userId=${user.id}&nodeId=${node.id}`);
    setFiles(Array.isArray(res)?res:[]);
    // Aktualisiere fileCount im Knoten
    onUpdate({fileCount: Array.isArray(res)?res.length:0});
  };
  useEffect(()=>{loadFiles();},[node.id]);

  // Beschreibung debounced speichern
  useEffect(()=>{
    if(description===(node.description||"")) return;
    if(descTimerRef.current) clearTimeout(descTimerRef.current);
    descTimerRef.current = setTimeout(()=>{
      onUpdate({description});
      setDescSaved(true);
      setTimeout(()=>setDescSaved(false),1500);
    },800);
    return ()=>{ if(descTimerRef.current) clearTimeout(descTimerRef.current); };
  },[description]);

  const handleUpload = async(filesToUpload) => {
    setUploadError("");
    for(const f of filesToUpload){
      if(f.size > 20*1024*1024){
        setUploadError(`"${f.name}" ist zu groß (max 20 MB)`);
        continue;
      }
      setUploading(true);
      const fd = new FormData();
      fd.append("userId", user.id);
      fd.append("boardId", boardId);
      fd.append("nodeId", node.id);
      fd.append("file", f);
      try {
        const res = await fetch("/api/whiteboard-upload", {method:"POST", headers:authHeaders(), body:fd});
        const data = await res.json();
        if(data.error) setUploadError(data.error);
      } catch(e){
        setUploadError("Upload fehlgeschlagen: "+e.message);
      }
      setUploading(false);
    }
    loadFiles();
  };

  const onFilePick = (e) => {
    const list = Array.from(e.target.files||[]);
    if(list.length>0) handleUpload(list);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    const list = Array.from(e.dataTransfer.files||[]);
    if(list.length>0) handleUpload(list);
  };

  const deleteFile = async(id) => {
    if(!confirm("Datei wirklich löschen?")) return;
    await api.del("whiteboard-file",{userId:user.id, id});
    loadFiles();
  };

  const downloadUrl = (id) => `/api/whiteboard-file/${id}?userId=${user.id}`;
  const fmtSize = (b) => b<1024?b+" B":b<1024*1024?(b/1024).toFixed(1)+" KB":(b/1024/1024).toFixed(2)+" MB";
  const fileIcon = (mime,name) => {
    const ext = (name||"").split(".").pop()?.toLowerCase()||"";
    if(mime?.startsWith("image/")) return "🖼";
    if(mime?.includes("pdf")||ext==="pdf") return "📄";
    if(mime?.includes("word")||ext==="docx"||ext==="doc") return "📝";
    if(ext==="eml"||ext==="msg") return "📧";
    if(mime?.includes("zip")||ext==="zip") return "🗜";
    if(mime?.includes("excel")||ext==="xlsx"||ext==="csv") return "📊";
    return "📎";
  };

  return (<div style={{display:"flex",flexDirection:"column",gap:18}}>
    {/* Beschreibung */}
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
        <label style={{fontSize:12,fontWeight:600,color:T.textDim,textTransform:"uppercase",letterSpacing:0.4}}>Beschreibung</label>
        {descSaved && <span style={{fontSize:11,color:T.success}}>✓ Gespeichert</span>}
      </div>
      <textarea value={description} onChange={e=>setDescription(e.target.value)} placeholder="Notizen, Details, Aufgaben..." style={{width:"100%",minHeight:100,background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:10,padding:"12px 14px",color:T.text,fontSize:13,outline:"none",fontFamily:"inherit",resize:"vertical",lineHeight:1.5}}/>
    </div>

    {/* Datei-Bereich */}
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <label style={{fontSize:12,fontWeight:600,color:T.textDim,textTransform:"uppercase",letterSpacing:0.4}}>Anhänge ({files.length})</label>
        <Button size="sm" variant="secondary" icon="upload" onClick={()=>fileInputRef.current?.click()} disabled={uploading}>{uploading?"Lädt hoch...":"Datei hochladen"}</Button>
        <input ref={fileInputRef} type="file" multiple style={{display:"none"}} onChange={onFilePick}/>
      </div>
      {/* Dropzone */}
      <div onDragOver={e=>e.preventDefault()} onDrop={onDrop} style={{border:`2px dashed ${T.border}`,borderRadius:10,padding:files.length===0?"24px 16px":"12px 16px",background:T.bgInput,textAlign:"center",fontSize:12,color:T.textDim,marginBottom:files.length>0?10:0,transition:"border-color 0.15s"}}>
        {files.length===0 ? "Dateien hier ablegen oder oben auf Hochladen klicken (max. 20 MB pro Datei)" : "Weitere Dateien hier ablegen"}
      </div>
      {uploadError && <div style={{fontSize:12,color:T.danger,marginBottom:8}}>{uploadError}</div>}
      {/* Datei-Liste */}
      {files.length>0 && (<div style={{display:"flex",flexDirection:"column",gap:6}}>
        {files.map(f=>(<div key={f.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:10}}>
          <div style={{fontSize:18}}>{fileIcon(f.mime_type,f.filename)}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:13,fontWeight:600,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.filename}</div>
            <div style={{fontSize:11,color:T.textDim}}>{fmtSize(f.size_bytes)} · {new Date(f.created_at).toLocaleDateString("de")}</div>
          </div>
          <a href={downloadUrl(f.id)} download={f.filename} style={{padding:"4px 10px",background:T.accent,color:"#fff",borderRadius:6,fontSize:12,fontWeight:600,textDecoration:"none"}}>↓</a>
          <button onClick={()=>deleteFile(f.id)} style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4,fontSize:14}}>🗑</button>
        </div>))}
      </div>)}
    </div>
  </div>);
};


// ─── APP ───
// Error-Boundary: zeigt sichtbare Fehlermeldung statt weißer Bildschirm
// ═══════════════════════════════════════
// ANWESENHEIT / ARBEITSZEIT-TRACKING
// ═══════════════════════════════════════
// Hook der die eigene Session lädt + live mitzählt
const useAttendance = (userId) => {
  const [session,setSession] = useState(null);
  const [tick,setTick] = useState(0);

  const load = async () => {
    if(!userId) return;
    try {
      const s = await api.get("attendance", `userId=${userId}`);
      setSession(s);
    } catch(e){ /* attendance load */ }
  };
  useEffect(()=>{ load(); }, [userId]);
  // Jede Sekunde Re-Render für den Timer (nur lokal, kein API-Call)
  useEffect(()=>{
    const t = setInterval(()=>setTick(x=>x+1), 1000);
    return ()=>clearInterval(t);
  }, []);
  // Alle 30s frisch vom Server holen (Sync zwischen Geräten)
  useEffect(()=>{
    const t = setInterval(load, 30000);
    return ()=>clearInterval(t);
  }, [userId]);
  // Sofort neu laden wenn anderswo eingestempelt wurde (z.B. aus dem Login-Prompt oder durch Admin)
  useEffect(()=>{
    const handler = (e)=>{ if(!e.detail?.userId || e.detail.userId===userId) load(); };
    window.addEventListener("attendance-changed", handler);
    return ()=>window.removeEventListener("attendance-changed", handler);
  }, [userId]);

  const doAction = async (action) => {
    if(!userId) return;
    try {
      const s = await api.post("attendance", {userId, action});
      setSession(s);
    } catch(e){ /* attendance action */ }
  };

  // Live-berechnete Sekunden (clientseitig hochzählen zwischen den Server-Syncs)
  const liveWork = (()=>{
    if(!session || !session.clockIn) return 0;
    if(session.status === "offline" && session.clockOut) return session.workSeconds||0;
    // Basis vom Server + Zeit seit letztem Load
    return session.workSeconds || 0;
  })();

  return { session, doAction, reload: load, tick };
};

// Kompaktes Widget für die Sidebar
const AttendanceWidget = ({user}) => {
  const { session, doAction, tick } = useAttendance(user.id);
  // Nur für member/cutter relevant — Admin kann auch, aber optional
  if(!session) return null;

  const status = session.status || "offline";
  const isWorking = status === "working";
  const isBreak = status === "break";
  const isOffline = status === "offline";

  // Live aus Zeitstempeln rechnen — tickt jede Sekunde (tick erzwingt Re-Render)
  // und stimmt auch wenn die Seite lange zu war.
  void tick;
  const { work, currentBreak, totalBreak } = liveAttendance(session);

  const statusColor = isWorking ? T.success : isBreak ? T.warning : T.textDim;
  const statusLabel = isWorking ? "Arbeitet" : isBreak ? "In Pause" : (session.clockOut ? "Feierabend" : "Offline");

  return (<div style={{padding:"12px 14px",background:T.bgInput,borderRadius:12,border:`1px solid ${T.border}`,margin:"0 0 12px 0"}}>
    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
      <span style={{width:8,height:8,borderRadius:"50%",background:statusColor,display:"inline-block",boxShadow:isWorking?`0 0 8px ${T.success}`:"none"}}/>
      <span style={{fontSize:12,fontWeight:700,color:statusColor}}>{statusLabel}</span>
    </div>
    {/* Timer */}
    {!isOffline && <div style={{marginBottom:10}}>
      <div style={{fontSize:10,color:T.textDim,textTransform:"uppercase",letterSpacing:0.5}}>{isBreak?"Pause läuft":"Arbeitszeit heute"}</div>
      <div style={{fontSize:22,fontWeight:800,fontFamily:"'JetBrains Mono',monospace",color:isBreak?T.warning:T.text,lineHeight:1.2}}>
        {isBreak ? fmtDuration(currentBreak) : fmtDuration(work)}
      </div>
      {isBreak && <div style={{fontSize:10,color:T.textDim,marginTop:2}}>Gearbeitet: {fmtDurationShort(work)}</div>}
      {!isBreak && totalBreak>0 && <div style={{fontSize:10,color:T.textDim,marginTop:2}}>Pause gesamt: {fmtDurationShort(totalBreak)}</div>}
    </div>}
    {/* Feierabend-Anzeige */}
    {isOffline && session.clockOut && <div style={{marginBottom:10}}>
      <div style={{fontSize:10,color:T.textDim,textTransform:"uppercase",letterSpacing:0.5}}>Arbeitszeit heute</div>
      <div style={{fontSize:22,fontWeight:800,fontFamily:"'JetBrains Mono',monospace",color:T.text,lineHeight:1.2}}>{fmtDuration(work)}</div>
    </div>}
    {/* Buttons */}
    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
      {isOffline && <button onClick={()=>doAction("clock_in")} style={{flex:1,padding:"8px",background:T.success,border:"none",borderRadius:8,color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",gap:5}}><IconPlay/> {session.clockOut?"Weiter arbeiten":"Einstempeln"}</button>}
      {isWorking && <button onClick={()=>doAction("break_start")} style={{flex:1,padding:"8px",background:`${T.warning}22`,border:`1px solid ${T.warning}`,borderRadius:8,color:T.warning,fontSize:12,fontWeight:700,cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",gap:5}}><IconPause/> Pause</button>}
      {isBreak && <button onClick={()=>doAction("break_end")} style={{flex:1,padding:"8px",background:T.success,border:"none",borderRadius:8,color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",gap:5}}><IconPlay/> Weiter</button>}
      {(isWorking||isBreak) && <button onClick={()=>{if(confirm("Feierabend? Damit wird deine Arbeitszeit für heute beendet."))doAction("clock_out");}} style={{flex:1,padding:"8px",background:`${T.danger}15`,border:`1px solid ${T.danger}40`,borderRadius:8,color:T.danger,fontSize:12,fontWeight:700,cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",gap:5}}><IconStop/> Feierabend</button>}
    </div>
  </div>);
};

// ─── EINSTELLUNGEN ───
// Kleine Vorschau des CRM in einer bestimmten Farbwelt. Zeigt Seitenleiste,
// eine Karte und ein paar Balken, damit man vor dem Umschalten sieht was kommt.
const ThemeMini = ({t, hoch=false}) => (
  <div style={{display:"flex",height:hoch?104:82,borderRadius:8,overflow:"hidden",background:t.bg,border:`1px solid ${t.border}`}}>
    <div style={{width:"24%",background:t.bgSidebar,borderRight:`1px solid ${t.border}`,padding:"7px 5px",display:"flex",flexDirection:"column",gap:5}}>
      <div style={{height:5,width:"70%",borderRadius:3,background:t.text,opacity:0.75}}/>
      <div style={{height:9,borderRadius:4,background:t.accentGlow,display:"flex",alignItems:"center",padding:"0 4px"}}>
        <div style={{height:3,width:"60%",borderRadius:2,background:t.accent}}/>
      </div>
      {[0,1,2].map(i=>(<div key={i} style={{height:3,width:`${70-i*10}%`,borderRadius:2,background:t.textDim,opacity:0.5,marginLeft:4}}/>))}
    </div>
    <div style={{flex:1,padding:7,display:"flex",flexDirection:"column",gap:5,minWidth:0}}>
      <div style={{background:t.bgCard,border:`1px solid ${t.border}`,borderRadius:5,padding:"5px 6px",display:"flex",alignItems:"flex-end",gap:2,flex:1,minHeight:0}}>
        {[0.45,0.8,0.3,0.6,0.95,0.5].map((h,i)=>(
          <div key={i} style={{flex:1,display:"flex",alignItems:"flex-end",gap:1,height:"100%"}}>
            <div style={{flex:1,height:`${h*100}%`,background:t.accent,borderRadius:1}}/>
            <div style={{flex:1,height:`${Math.max(12,h*72)}%`,background:t.chart2,borderRadius:1}}/>
          </div>))}
      </div>
      <div style={{display:"flex",gap:5}}>
        {[0,1].map(i=>(<div key={i} style={{flex:1,background:t.bgCard,border:`1px solid ${t.border}`,borderRadius:5,padding:"5px 6px"}}>
          <div style={{height:3,width:"55%",borderRadius:2,background:t.textDim,opacity:0.6}}/>
          <div style={{height:6,width:"75%",borderRadius:2,background:t.text,opacity:0.85,marginTop:4}}/>
        </div>))}
        <div style={{width:26,borderRadius:5,background:t.accent}}/>
      </div>
    </div>
  </div>
);

const SettingsPage = ({user, onAttendanceChange}) => {
  // Vertriebler haben die Zeiterfassung fest an
  const isMember = user.role === "member";
  const isAdmin = user.role === "admin";
  const [attOn,setAttOn] = useState(true);
  const [loaded,setLoaded] = useState(false);
  const [saving,setSaving] = useState(false);

  // Wartungsmodus (nur Admin): global, schaltet den Wartungs-Screen für alle ein
  const [maintOn,setMaintOn] = useState(false);
  const [maintSaving,setMaintSaving] = useState(false);

  // Einmalige Verschlüsselung bestehender Kunden-Zugangsdaten (nur Admin)
  const [encRunning,setEncRunning] = useState(false);
  const [encMsg,setEncMsg] = useState(null);
  const runCredMigration = async () => {
    setEncRunning(true); setEncMsg(null);
    const res = await api.post("migrate-encrypt-credentials", {});
    setEncRunning(false);
    if(res && res.success){
      setEncMsg({ok:true, text: res.migrated>0 ? `${res.migrated} von ${res.total} Zugangsdaten verschlüsselt.` : "Alles war bereits verschlüsselt, nichts zu tun."});
    } else {
      setEncMsg({ok:false, text: res?.error || "Konnte nicht ausgeführt werden."});
    }
  };

  // Eigene Kontaktdaten (erscheinen beim Kunden als Ansprechpartner)
  const myUser = (store.get(STORAGE_KEYS.users)||[]).find(u=>u.id===user.id) || {};
  const [contact,setContact] = useState({phone:myUser.contactPhone||"",email:myUser.contactEmail||"",website:myUser.contactWebsite||""});
  const [contactSaving,setContactSaving] = useState(false);
  const [contactSaved,setContactSaved] = useState(false);

  const saveContact = async () => {
    setContactSaving(true);
    const res = await api.post("update-contact", {userId:user.id, phone:contact.phone.trim()||null, email:contact.email.trim()||null, website:contact.website.trim()||null});
    setContactSaving(false);
    if(res && !res.error){
      // Lokalen Store aktualisieren, damit die Werte beim nächsten Öffnen stimmen
      const us = store.get(STORAGE_KEYS.users)||[];
      const idx = us.findIndex(u=>u.id===user.id);
      if(idx>=0){ us[idx] = {...us[idx], contactPhone:contact.phone.trim()||null, contactEmail:contact.email.trim()||null, contactWebsite:contact.website.trim()||null}; store.set(STORAGE_KEYS.users, us); }
      setContactSaved(true); setTimeout(()=>setContactSaved(false), 2500);
    }
    else alert("Konnte die Kontaktdaten nicht speichern. Bitte erneut versuchen.");
  };

  useEffect(()=>{
    loadAttendanceSetting(user).then(v=>{ setAttOn(v); setLoaded(true); });
    if(user.role==="admin") appSettings.getBool("maintenance_mode", false).then(v=>setMaintOn(v));
  }, [user.id]);

  const toggle = async () => {
    if(isMember) return; // nicht abschaltbar
    const next = !attOn;
    setSaving(true);
    const ok = await appSettings.setBool(`attendance_enabled_${user.id}`, next, user.id);
    setSaving(false);
    if(ok){
      setAttOn(next);
      _attendanceEnabled = next;
      onAttendanceChange?.(next);
    } else {
      alert("Konnte die Einstellung nicht speichern. Bitte erneut versuchen.");
    }
  };

  const toggleMaintenance = async () => {
    const next = !maintOn;
    if(next && !confirm("Wartungsmodus einschalten? Alle Nutzer sehen dann den Wartungs-Screen statt dem CRM.")) return;
    setMaintSaving(true);
    const ok = await appSettings.setBool("maintenance_mode", next, user.id);
    setMaintSaving(false);
    if(ok) setMaintOn(next);
    else alert("Konnte den Wartungsmodus nicht umschalten. Bitte erneut versuchen.");
  };

  return (<div>
    <div style={{marginBottom:28}}>
      <h1 style={{fontSize:26,fontWeight:700,letterSpacing:"-0.02em"}}>Einstellungen</h1>
      <p style={{color:T.textDim,fontSize:14,marginTop:4}}>Verwalte deine persönlichen Optionen</p>
    </div>

    <div style={{maxWidth:820,display:"flex",flexDirection:"column",gap:16}}>
      {/* Darstellung: Modus und Farbwelt, jeweils mit einer kleinen Vorschau */}
      <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:14,boxShadow:T.shadow,overflow:"hidden"}}>
        <div style={{padding:"18px 22px 16px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:13}}>
          <div style={{width:38,height:38,borderRadius:10,background:T.accentGlow,display:"flex",alignItems:"center",justifyContent:"center",color:T.accent,flexShrink:0}}>
            <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor"><path d="M12 22C6.49 22 2 17.51 2 12S6.49 2 12 2s10 4.04 10 9c0 3.31-2.69 6-6 6h-1.77c-.28 0-.5.22-.5.5 0 .12.05.23.13.33.41.47.64 1.06.64 1.67 0 1.38-1.12 2.5-2.5 2.5zm0-18c-4.41 0-8 3.59-8 8s3.59 8 8 8c.28 0 .5-.22.5-.5a.54.54 0 00-.14-.35c-.41-.46-.63-1.05-.63-1.65 0-1.38 1.12-2.5 2.5-2.5H16c2.21 0 4-1.79 4-4 0-3.86-3.59-7-8-7zM6.5 11.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zm4-4a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zm5 1a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0z"/></svg>
          </div>
          <div style={{minWidth:0}}>
            <div style={{fontSize:15,fontWeight:600}}>Darstellung</div>
            <div style={{fontSize:12.5,color:T.textMuted,marginTop:2,lineHeight:1.45}}>Gilt nur für dich und kommt mit, wenn du dich woanders anmeldest.</div>
          </div>
        </div>

        <div style={{padding:"16px 22px 20px"}}>
          <div style={{fontSize:11,color:T.textDim,fontWeight:600,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:10}}>Modus</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:22}}>
            {[["light","Hell"],["dark","Dunkel"]].map(([m,l])=>{
              const aktiv = m===_theme;
              return (<button key={m} onClick={()=>{ if(!aktiv) darstellungAnwenden(user.id,m,_palette); }}
                style={{padding:7,borderRadius:12,cursor:"pointer",textAlign:"left",whiteSpace:"normal",
                        background:aktiv?T.accentGlow:T.bgCard2,border:`1.5px solid ${aktiv?T.accent:T.border}`,
                        display:"block",transition:"border-color .15s"}}>
                <ThemeMini t={baueTheme(_palette,m)}/>
                <div style={{display:"flex",alignItems:"center",gap:7,padding:"9px 5px 3px"}}>
                  <span style={{width:15,height:15,borderRadius:"50%",flexShrink:0,border:`1.5px solid ${aktiv?T.accent:T.borderStrong}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {aktiv && <span style={{width:7,height:7,borderRadius:"50%",background:T.accent,display:"block"}}/>}
                  </span>
                  <span style={{fontSize:13,fontWeight:600,color:aktiv?T.text:T.textMuted}}>{l}</span>
                </div>
              </button>);
            })}
          </div>

          <div style={{fontSize:11,color:T.textDim,fontWeight:600,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:10}}>Farbwelt</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:12}}>
            {PALETTEN.map(p=>{
              const aktiv = p.id===_palette;
              const vor = baueTheme(p.id,_theme);
              return (<button key={p.id} onClick={()=>{ if(!aktiv) darstellungAnwenden(user.id,_theme,p.id); }}
                style={{padding:7,borderRadius:12,cursor:"pointer",textAlign:"left",whiteSpace:"normal",minWidth:0,
                        background:aktiv?T.accentGlow:T.bgCard2,border:`1.5px solid ${aktiv?T.accent:T.border}`,
                        display:"block",transition:"border-color .15s"}}>
                <ThemeMini t={vor}/>
                <div style={{display:"flex",alignItems:"center",gap:8,padding:"9px 5px 3px",minWidth:0}}>
                  <span style={{width:13,height:13,borderRadius:4,background:vor.accent,flexShrink:0,display:"block"}}/>
                  <span style={{minWidth:0,flex:1}}>
                    <span style={{display:"block",fontSize:13,fontWeight:600,color:aktiv?T.text:T.textMuted,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.name}</span>
                    <span style={{display:"block",fontSize:11,color:T.textDim,marginTop:1,lineHeight:1.3}}>{p.info}</span>
                  </span>
                  {aktiv && <span style={{flexShrink:0,color:T.accent,display:"flex"}}><Icon name="check" size={15}/></span>}
                </div>
              </button>);
            })}
          </div>
        </div>
      </div>

      {/* Meine Kontaktdaten (erscheinen beim Kunden, wenn ich als Ansprechpartner zugewiesen bin) */}
      {user.role!=="client" && <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:"20px 24px"}}>
        <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:16}}>
          <div style={{width:42,height:42,borderRadius:11,background:T.bgHover,display:"flex",alignItems:"center",justifyContent:"center",color:T.textMuted,flexShrink:0}}>
            <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
          </div>
          <div>
            <div style={{fontSize:15,fontWeight:600}}>Meine Kontaktdaten</div>
            <div style={{fontSize:13,color:T.textMuted,marginTop:3,lineHeight:1.5}}>Diese Daten sehen Kunden, wenn du als Ansprechpartner zugewiesen bist.</div>
          </div>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Input label="Telefon" value={contact.phone} onChange={e=>setContact({...contact,phone:e.target.value})} placeholder="+49 ..."/>
          <Input label="E-Mail" value={contact.email} onChange={e=>setContact({...contact,email:e.target.value})} placeholder="name@rf-media.de"/>
          <Input label="Webseite" value={contact.website} onChange={e=>setContact({...contact,website:e.target.value})} placeholder="https://rf-media.de"/>
          <div style={{display:"flex",alignItems:"center",gap:12,marginTop:4}}>
            <Button icon="check" onClick={saveContact} disabled={contactSaving}>{contactSaving?"Speichert...":"Speichern"}</Button>
            {contactSaved && <span style={{fontSize:13,color:T.success,fontWeight:600}}>Gespeichert</span>}
          </div>
        </div>
      </div>}

      {/* Zeiterfassung */}
      <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:"20px 24px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:16,flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:14,minWidth:0}}>
            <div style={{width:42,height:42,borderRadius:11,background:T.bgHover,display:"flex",alignItems:"center",justifyContent:"center",color:T.textMuted,flexShrink:0}}>
              <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>
            </div>
            <div style={{minWidth:0}}>
              <div style={{fontSize:15,fontWeight:600}}>Zeiterfassung / Einstempeln</div>
              <div style={{fontSize:13,color:T.textMuted,marginTop:3,lineHeight:1.5}}>
                {isMember
                  ? "Für Vertriebler ist die Zeiterfassung fest aktiviert, damit Arbeitszeiten nachvollziehbar bleiben."
                  : (attOn
                    ? "Aktiv. Der Timer in der Seitenleiste und die Einstempel-Abfrage beim Login sind sichtbar."
                    : "Deaktiviert. Du siehst weder den Timer noch die Einstempel-Abfrage.")}
              </div>
            </div>
          </div>
          {isMember
            ? <span style={{fontSize:12,fontWeight:600,color:T.success,background:T.successGlow,padding:"6px 12px",borderRadius:8,whiteSpace:"nowrap"}}>Immer aktiv</span>
            : <button onClick={toggle} disabled={saving||!loaded} title={attOn?"Ausschalten":"Einschalten"} style={{position:"relative",width:52,height:30,borderRadius:15,border:"none",background:attOn?T.success:T.borderStrong,cursor:(saving||!loaded)?"wait":"pointer",transition:"background 0.2s",flexShrink:0,opacity:(saving||!loaded)?0.6:1}}>
                <span style={{position:"absolute",top:3,left:attOn?25:3,width:24,height:24,borderRadius:"50%",background:"#fff",transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.3)"}}/>
              </button>}
        </div>
      </div>

      {/* Wartungsmodus (nur Admin) */}
      {isAdmin && <div style={{background:T.bgCard,border:`1px solid ${maintOn?T.warning+"66":T.border}`,borderRadius:12,boxShadow:T.shadow,padding:"20px 24px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:16,flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:14,minWidth:0}}>
            <div style={{width:42,height:42,borderRadius:11,background:maintOn?T.warning+"22":T.bgHover,display:"flex",alignItems:"center",justifyContent:"center",color:maintOn?T.warning:T.textMuted,flexShrink:0}}>
              <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor"><path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/></svg>
            </div>
            <div style={{minWidth:0}}>
              <div style={{fontSize:15,fontWeight:600}}>Wartungsmodus</div>
              <div style={{fontSize:13,color:T.textMuted,marginTop:3,lineHeight:1.5}}>
                {maintOn
                  ? "Aktiv. Alle Nutzer sehen aktuell den Wartungs-Screen statt dem CRM. Zum Arbeiten wieder ausschalten."
                  : "Schaltet für alle einen Wartungs-Screen mit Logo ein, während du Änderungen hochlädst. Vergiss nicht, ihn danach wieder auszuschalten."}
              </div>
            </div>
          </div>
          <button onClick={toggleMaintenance} disabled={maintSaving} title={maintOn?"Ausschalten":"Einschalten"} style={{position:"relative",width:52,height:30,borderRadius:15,border:"none",background:maintOn?T.warning:T.borderStrong,cursor:maintSaving?"wait":"pointer",transition:"background 0.2s",flexShrink:0,opacity:maintSaving?0.6:1}}>
            <span style={{position:"absolute",top:3,left:maintOn?25:3,width:24,height:24,borderRadius:"50%",background:"#fff",transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.3)"}}/>
          </button>
        </div>
      </div>}

      {/* Kunden-Zugangsdaten verschlüsseln (nur Admin, einmalig) */}
      {isAdmin && <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:"20px 24px"}}>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:16,flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"flex-start",gap:14,minWidth:0}}>
            <div style={{width:42,height:42,borderRadius:11,background:T.bgHover,display:"flex",alignItems:"center",justifyContent:"center",color:T.textMuted,flexShrink:0}}>
              <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            </div>
            <div style={{minWidth:0}}>
              <div style={{fontSize:15,fontWeight:600}}>Kunden-Zugangsdaten verschlüsseln</div>
              <div style={{fontSize:13,color:T.textMuted,marginTop:3,lineHeight:1.5}}>Einmalig ausführen, nachdem die secrets.php auf dem Server liegt. Wandelt alle bereits gespeicherten Zugangsdaten in die verschlüsselte Form um. Kann gefahrlos mehrfach gedrückt werden.</div>
              {encMsg && <div style={{marginTop:10,fontSize:13,fontWeight:600,color:encMsg.ok?T.success:T.danger}}>{encMsg.text}</div>}
            </div>
          </div>
          <Button variant="secondary" onClick={runCredMigration} disabled={encRunning}>{encRunning?"Läuft...":"Jetzt verschlüsseln"}</Button>
        </div>
      </div>}
    </div>
  </div>);
};

// ─── DIENSTLEISTUNGSKATALOG ───
const fmtTargetTime = (min) => {
  if(min===null||min===undefined||min==="") return "—";
  const m = parseInt(min); if(isNaN(m)) return "—";
  if(m < 60) return `${m} Min`;
  const h = Math.floor(m/60); const r = m%60;
  if(r===0) return `${h} Std`;
  return `${h} Std ${r} Min`;
};

const ServiceCatalogPage = ({user, reload, dv}) => {
  const [services,setServices] = useState(store.get(STORAGE_KEYS.service_catalog)||[]);
  useEffect(()=>{ setServices(store.get(STORAGE_KEYS.service_catalog)||[]); }, [dv]);
  const [showAdd,setShowAdd] = useState(false);
  const [form,setForm] = useState({name:"",category:"",targetMinutes:"",notes:""});
  const [editItem,setEditItem] = useState(null);

  const addService = async () => {
    if(!form.name.trim()) return;
    await api.post("service-catalog", {name:form.name.trim(), category:form.category.trim()||null, targetMinutes:form.targetMinutes||null, notes:form.notes.trim()||null, sortOrder:services.length});
    await reload(STORAGE_KEYS.service_catalog); setServices(store.get(STORAGE_KEYS.service_catalog));
    setForm({name:"",category:"",targetMinutes:"",notes:""}); setShowAdd(false);
  };
  const saveEdit = async () => {
    if(!editItem||!editItem.name.trim()) return;
    await api.put("service-catalog", {id:editItem.id, name:editItem.name.trim(), category:(editItem.category||"").trim()||null, targetMinutes:editItem.targetMinutes||null, notes:(editItem.notes||"").trim()||null, sortOrder:editItem.sortOrder||0});
    await reload(STORAGE_KEYS.service_catalog); setServices(store.get(STORAGE_KEYS.service_catalog));
    setEditItem(null);
  };
  const delService = async (id) => {
    if(!confirm("Diese Dienstleistung wirklich löschen?")) return;
    await api.del("service-catalog", {id});
    await reload(STORAGE_KEYS.service_catalog); setServices(store.get(STORAGE_KEYS.service_catalog));
  };

  // Nach Kategorie gruppieren
  const grouped = (()=>{
    const map = {};
    for(const s of services){ const c = s.category || "Ohne Kategorie"; if(!map[c]) map[c]=[]; map[c].push(s); }
    return Object.entries(map);
  })();

  return (<div>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,flexWrap:"wrap",gap:12}}>
      <h1 style={{fontSize:26,fontWeight:700,letterSpacing:"-0.02em"}}>Dienstleistungskatalog</h1>
      <Button icon="add" onClick={()=>setShowAdd(true)}>Dienstleistung</Button>
    </div>
    <p style={{color:T.textMuted,fontSize:14,marginBottom:22}}>Lege fest, welche Leistung in welcher Zielzeit fertig sein soll. So hat jeder die Soll-Bearbeitungszeit im Blick.</p>

    <Modal open={showAdd} onClose={()=>setShowAdd(false)} title="Neue Dienstleistung" width={480}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <Input label="Bezeichnung" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="z.B. Reel schneiden"/>
        <Input label="Kategorie (optional)" value={form.category} onChange={e=>setForm({...form,category:e.target.value})} placeholder="z.B. Videoschnitt, Web, Ads"/>
        <Input label="Zielzeit in Minuten" type="number" value={form.targetMinutes} onChange={e=>setForm({...form,targetMinutes:e.target.value})} placeholder="z.B. 90"/>
        <Input label="Notiz (optional)" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="Details, Hinweise..."/>
        <Button icon="check" onClick={addService} style={{marginTop:8}}>Hinzufügen</Button>
      </div>
    </Modal>

    <Modal open={!!editItem} onClose={()=>setEditItem(null)} title="Dienstleistung bearbeiten" width={480}>
      {editItem && <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <Input label="Bezeichnung" value={editItem.name} onChange={e=>setEditItem({...editItem,name:e.target.value})}/>
        <Input label="Kategorie (optional)" value={editItem.category||""} onChange={e=>setEditItem({...editItem,category:e.target.value})}/>
        <Input label="Zielzeit in Minuten" type="number" value={editItem.targetMinutes||""} onChange={e=>setEditItem({...editItem,targetMinutes:e.target.value})}/>
        <Input label="Notiz (optional)" value={editItem.notes||""} onChange={e=>setEditItem({...editItem,notes:e.target.value})}/>
        <Button icon="check" onClick={saveEdit} style={{marginTop:8}}>Speichern</Button>
      </div>}
    </Modal>

    {services.length===0
      ? <Empty icon="clipboard" title="Noch keine Dienstleistungen" desc="Lege deine erste Dienstleistung mit Zielzeit an."/>
      : grouped.map(([cat,items])=>(
        <div key={cat} style={{marginBottom:24}}>
          <div style={{fontSize:13,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:10}}>{cat}</div>
          <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,overflow:"hidden"}}>
            {items.map((s,idx)=>(
              <div key={s.id} style={{display:"flex",alignItems:"center",gap:14,padding:"14px 20px",borderBottom:idx<items.length-1?`1px solid ${T.border}`:"none"}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:15,fontWeight:600}}>{s.name}</div>
                  {s.notes && <div style={{fontSize:12,color:T.textMuted,marginTop:2}}>{s.notes}</div>}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0,background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"6px 12px"}}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill={T.accent}><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>
                  <span style={{fontSize:14,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:T.accent}}>{fmtTargetTime(s.targetMinutes)}</span>
                </div>
                <button onClick={()=>setEditItem(s)} title="Bearbeiten" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4,flexShrink:0}}><Icon name="edit" size={15}/></button>
                <button onClick={()=>delService(s.id)} title="Löschen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4,flexShrink:0}}><Icon name="delete" size={15}/></button>
              </div>
            ))}
          </div>
        </div>
      ))}
  </div>);
};

// ═══════════════════════════════════════
// ZUGANGSDATEN-VERWALTUNG (wiederverwendbar: Kundenportal + Admin)
// clientId = user.id des Kunden. canEdit = darf anlegen/bearbeiten/löschen.
// ═══════════════════════════════════════
const CredentialsManager = ({clientId, canEdit=true}) => {
  const [creds,setCreds] = useState([]);
  const [loading,setLoading] = useState(true);
  const [showForm,setShowForm] = useState(false);
  const [editItem,setEditItem] = useState(null);
  const [reveal,setReveal] = useState({}); // {credId: true} — welches Passwort sichtbar ist
  const [copied,setCopied] = useState(null);
  const emptyForm = {label:"",username:"",password:"",url:"",notes:""};
  const [form,setForm] = useState(emptyForm);

  const load = () => {
    api.get("client-credentials", `clientId=${clientId}`)
      .then(c=>{ setCreds(Array.isArray(c)?c:[]); setLoading(false); })
      .catch(()=>setLoading(false));
  };
  useEffect(()=>{ load(); }, [clientId]);

  const add = async () => {
    if(!form.label.trim()){ alert("Bitte eine Bezeichnung angeben."); return; }
    await api.post("client-credentials", {...form, clientId, sortOrder:creds.length});
    setForm(emptyForm); setShowForm(false); load();
  };
  const saveEdit = async () => {
    if(!editItem||!editItem.label.trim()) return;
    await api.put("client-credentials", editItem);
    setEditItem(null); load();
  };
  const del = async (id) => {
    if(!confirm("Diesen Zugang wirklich löschen?")) return;
    await api.del("client-credentials", {id});
    load();
  };
  const copy = (text, key) => {
    try { navigator.clipboard.writeText(text); setCopied(key); setTimeout(()=>setCopied(null), 1500); } catch(e){}
  };

  const EyeIcon = ({open}) => open
    ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
    : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
  const CopyIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>;

  const fields = (obj,setObj) => (<div style={{display:"flex",flexDirection:"column",gap:12}}>
    <Input label="Bezeichnung" value={obj.label} onChange={e=>setObj({...obj,label:e.target.value})} placeholder="z.B. Meta Business Manager"/>
    <div className="grid-2col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
      <Input label="Benutzername" value={obj.username||""} onChange={e=>setObj({...obj,username:e.target.value})} placeholder="Login / E-Mail"/>
      <Input label="Passwort" value={obj.password||""} onChange={e=>setObj({...obj,password:e.target.value})} placeholder="Passwort"/>
    </div>
    <Input label="Link (optional)" value={obj.url||""} onChange={e=>setObj({...obj,url:e.target.value})} placeholder="https://..."/>
    <Input label="Notiz (optional)" value={obj.notes||""} onChange={e=>setObj({...obj,notes:e.target.value})} placeholder="Hinweise"/>
  </div>);

  return (<div>
    {canEdit && <div style={{display:"flex",justifyContent:"flex-end",marginBottom:14}}>
      <Button icon="add" size="sm" onClick={()=>{setForm(emptyForm);setShowForm(true);}}>Zugang hinzufügen</Button>
    </div>}

    {canEdit && <Modal open={showForm} onClose={()=>setShowForm(false)} title="Neuen Zugang anlegen" width={480}>
      {fields(form,setForm)}
      <Button icon="check" onClick={add} style={{marginTop:16}}>Speichern</Button>
    </Modal>}

    {canEdit && <Modal open={!!editItem} onClose={()=>setEditItem(null)} title="Zugang bearbeiten" width={480}>
      {editItem && <>{fields(editItem,setEditItem)}<Button icon="check" onClick={saveEdit} style={{marginTop:16}}>Speichern</Button></>}
    </Modal>}

    {loading
      ? <div style={{color:T.textDim,fontSize:14,padding:"20px 0"}}>Lädt...</div>
      : creds.length===0
        ? <div style={{color:T.textDim,fontSize:14,padding:"20px 0",textAlign:"center"}}>Noch keine Zugangsdaten hinterlegt.</div>
        : <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {creds.map(c=>{
              const shown = reveal[c.id];
              return (<div key={c.id} style={{border:`1px solid ${T.border}`,borderRadius:12,padding:16,background:T.bgInput}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:c.username||c.password||c.url||c.notes?12:0}}>
                  <div style={{fontSize:15,fontWeight:600,minWidth:0}}>{c.label}</div>
                  {canEdit && <div style={{display:"flex",gap:4,flexShrink:0}}>
                    <button onClick={()=>setEditItem({id:c.id,label:c.label,username:c.username||"",password:c.password||"",url:c.url||"",notes:c.notes||""})} title="Bearbeiten" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4}}><Icon name="edit" size={15}/></button>
                    <button onClick={()=>del(c.id)} title="Löschen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4}}><Icon name="delete" size={15}/></button>
                  </div>}
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {c.username && <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:11,color:T.textDim,minWidth:80,flexShrink:0}}>Benutzername</span>
                    <span style={{fontSize:13,fontFamily:"'JetBrains Mono',monospace",flex:1,wordBreak:"break-all"}}>{c.username}</span>
                    <button onClick={()=>copy(c.username,`u-${c.id}`)} title="Kopieren" style={{background:"none",border:"none",color:copied===`u-${c.id}`?T.success:T.textDim,cursor:"pointer",padding:3,flexShrink:0}}><CopyIcon/></button>
                  </div>}
                  {c.password && <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:11,color:T.textDim,minWidth:80,flexShrink:0}}>Passwort</span>
                    <span style={{fontSize:13,fontFamily:"'JetBrains Mono',monospace",flex:1,wordBreak:"break-all",letterSpacing:shown?0:2}}>{shown?c.password:"••••••••••"}</span>
                    <button onClick={()=>setReveal({...reveal,[c.id]:!shown})} title={shown?"Verbergen":"Anzeigen"} style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:3,flexShrink:0}}><EyeIcon open={shown}/></button>
                    <button onClick={()=>copy(c.password,`p-${c.id}`)} title="Kopieren" style={{background:"none",border:"none",color:copied===`p-${c.id}`?T.success:T.textDim,cursor:"pointer",padding:3,flexShrink:0}}><CopyIcon/></button>
                  </div>}
                  {c.url && <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:11,color:T.textDim,minWidth:80,flexShrink:0}}>Link</span>
                    <a href={c.url.startsWith("http")?c.url:`https://${c.url}`} target="_blank" rel="noopener noreferrer" style={{fontSize:13,color:T.accent,flex:1,wordBreak:"break-all",textDecoration:"none"}}>{c.url}</a>
                  </div>}
                  {c.notes && <div style={{fontSize:12,color:T.textMuted,fontStyle:"italic",marginTop:2}}>{c.notes}</div>}
                </div>
              </div>);
            })}
          </div>}
  </div>);
};

// ═══════════════════════════════════════
// DREH-IDEEN (Social Media) — offene Ideen + Archiv mit Performance
// Wiederverwendbar: Portal + Admin. Löschen immer mit 2. Bestätigung.
// ═══════════════════════════════════════
const ShootIdeasModule = ({clientId, canEdit=true, createdBy="admin"}) => {
  const [ideas,setIdeas] = useState([]);
  const [loading,setLoading] = useState(true);
  const [showAdd,setShowAdd] = useState(false);
  const [newIdea,setNewIdea] = useState({title:"",notes:""});
  const [editItem,setEditItem] = useState(null);     // offene Idee bearbeiten
  const [perfItem,setPerfItem] = useState(null);      // Performance einer erledigten Idee bearbeiten
  const [confirmDel,setConfirmDel] = useState(null);  // {kind, id/category, label} — 2. Bestätigung

  // ── Hooks ──
  const [hooks,setHooks] = useState([]);
  const [showAddCat,setShowAddCat] = useState(false);
  const [newCat,setNewCat] = useState("");
  const [addHookTo,setAddHookTo] = useState(null);    // Kategorie, zu der ein Hook hinzugefügt wird
  const [newHookText,setNewHookText] = useState("");
  const [editHook,setEditHook] = useState(null);      // {id,text}
  const [renameCat,setRenameCat] = useState(null);    // {old, value}

  const load = () => {
    api.get("shoot-ideas", `clientId=${clientId}`)
      .then(d=>{ setIdeas(Array.isArray(d)?d:[]); setLoading(false); })
      .catch(()=>setLoading(false));
    api.get("shoot-hooks", `clientId=${clientId}`)
      .then(d=>{ setHooks(Array.isArray(d)?d:[]); })
      .catch(()=>{});
  };
  useEffect(()=>{ load(); }, [clientId]);

  const open = ideas.filter(i=>!i.done);
  const done = ideas.filter(i=>i.done);

  // Hooks nach Kategorie gruppieren, Reihenfolge der Kategorien beibehalten
  const hookCategories = (() => {
    const map = new Map();
    hooks.forEach(h=>{
      if(!map.has(h.category)) map.set(h.category, []);
      map.get(h.category).push(h);
    });
    return Array.from(map.entries()); // [ [catName, [hooks...]], ... ]
  })();

  const addCategory = async () => {
    if(!newCat.trim()){ alert("Bitte einen Namen für die Richtung eingeben."); return; }
    // Kategorie existiert erst, sobald ein Hook drin ist — wir legen direkt einen leeren Platzhalter-Hook NICHT an,
    // sondern öffnen direkt das Hook-Hinzufügen für diese neue Kategorie.
    setShowAddCat(false);
    setAddHookTo(newCat.trim());
    setNewHookText("");
    setNewCat("");
  };
  const addHook = async () => {
    if(!newHookText.trim()){ alert("Bitte einen Hook eingeben."); return; }
    const catOrder = hookCategories.findIndex(([c])=>c===addHookTo);
    await api.post("shoot-hooks", {clientId, category:addHookTo, text:newHookText, catOrder: catOrder>=0?catOrder:hookCategories.length, sortOrder: (hookCategories.find(([c])=>c===addHookTo)?.[1].length)||0, createdBy});
    setNewHookText(""); setAddHookTo(null); load();
  };
  const saveHookEdit = async () => {
    if(!editHook?.text?.trim()) return;
    await api.put("shoot-hooks", {id:editHook.id, text:editHook.text});
    setEditHook(null); load();
  };
  const saveRenameCat = async () => {
    if(!renameCat?.value?.trim()) return;
    await api.put("shoot-hooks", {renameCategory:renameCat.value, oldCategory:renameCat.old, clientId});
    setRenameCat(null); load();
  };

  // ── Tabelle hochladen (Bulk-Import) ──
  const hookFileRef = useRef(null);
  const ideaFileRef = useRef(null);
  const [importing,setImporting] = useState(false);
  const [importMsg,setImportMsg] = useState(null);

  // Liest eine CSV- oder Excel-Datei und gibt ein Array von Zeilen-Objekten zurück
  const parseTableFile = (file) => new Promise((resolve,reject)=>{
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, {type:"array"});
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, {defval:""}); // Array von Objekten, Header = Spaltennamen
        resolve(rows);
      } catch(err){ reject(err); }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });

  // Spalte anhand mehrerer möglicher Namen finden (tolerant gegen Groß/Klein und Varianten)
  const pick = (row, names) => {
    const keys = Object.keys(row);
    for(const n of names){
      const k = keys.find(k=>k.trim().toLowerCase()===n.toLowerCase());
      if(k!==undefined) return String(row[k]??"").trim();
    }
    return "";
  };

  const onHookFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // erlaubt erneutes Hochladen derselben Datei
    if(!file) return;
    setImporting(true); setImportMsg(null);
    try {
      const rows = await parseTableFile(file);
      const payload = rows.map(r=>({
        category: pick(r, ["Kategorie","Richtung","category","Gruppe"]),
        text: pick(r, ["Hook","Text","hook","Inhalt","Spruch"]),
      })).filter(r=>r.category && r.text);
      if(payload.length===0){ setImportMsg({ok:false,text:"Keine gültigen Zeilen gefunden. Brauche die Spalten Kategorie und Hook."}); setImporting(false); return; }
      const res = await api.post("shoot-hooks-import", {clientId, createdBy, rows:payload});
      setImportMsg({ok:true,text:`${res.imported||0} Hooks importiert.`});
      load();
    } catch(err){ setImportMsg({ok:false,text:"Datei konnte nicht gelesen werden. Bitte CSV oder Excel verwenden."}); }
    setImporting(false);
  };

  const onIdeaFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if(!file) return;
    setImporting(true); setImportMsg(null);
    try {
      const rows = await parseTableFile(file);
      const payload = rows.map(r=>{
        const title = pick(r, ["Titel","Title","Überschrift","Name","Idee"]);
        const hook = pick(r, ["Hook"]);
        const text = pick(r, ["Text","Beschreibung","Inhalt"]);
        const cta = pick(r, ["CTA","Call to Action","Aufruf"]);
        // Notizen aus Hook, Text, CTA zusammenbauen (nur was da ist)
        const parts = [];
        if(hook) parts.push("Hook: "+hook);
        if(text) parts.push("Text: "+text);
        if(cta) parts.push("CTA: "+cta);
        // wenn es eine eigene Notizen-Spalte gibt, die bevorzugen
        const notesCol = pick(r, ["Notizen","Notes","Details"]);
        return { title, notes: notesCol || parts.join("\n\n") };
      }).filter(r=>r.title);
      if(payload.length===0){ setImportMsg({ok:false,text:"Keine gültigen Zeilen gefunden. Brauche mindestens die Spalte Titel."}); setImporting(false); return; }
      const res = await api.post("shoot-ideas-import", {clientId, createdBy, rows:payload});
      setImportMsg({ok:true,text:`${res.imported||0} Ideen importiert.`});
      load();
    } catch(err){ setImportMsg({ok:false,text:"Datei konnte nicht gelesen werden. Bitte CSV oder Excel verwenden."}); }
    setImporting(false);
  };

  const addIdea = async () => {
    if(!newIdea.title.trim()){ alert("Bitte eine Idee eingeben."); return; }
    await api.post("shoot-ideas", {clientId, title:newIdea.title, notes:newIdea.notes, createdBy, sortOrder:open.length});
    setNewIdea({title:"",notes:""}); setShowAdd(false); load();
  };
  const toggleDone = async (idea) => {
    await api.put("shoot-ideas", {id:idea.id, done:!idea.done});
    load();
  };
  const saveEdit = async () => {
    if(!editItem?.title?.trim()) return;
    await api.put("shoot-ideas", {id:editItem.id, title:editItem.title, notes:editItem.notes});
    setEditItem(null); load();
  };
  const savePerf = async () => {
    await api.put("shoot-ideas", {
      id:perfItem.id, videoLink:perfItem.videoLink, views:perfItem.views, likes:perfItem.likes,
      comments:perfItem.comments, shares:perfItem.shares, saves:perfItem.saves, performanceNotes:perfItem.performanceNotes,
    });
    setPerfItem(null); load();
  };
  const doDelete = async () => {
    if(!confirmDel) return;
    if(confirmDel.kind==="idea") await api.del("shoot-ideas", {id:confirmDel.id});
    else if(confirmDel.kind==="hook") await api.del("shoot-hooks", {id:confirmDel.id});
    else if(confirmDel.kind==="category") await api.del("shoot-hooks", {deleteCategory:confirmDel.category, clientId});
    setConfirmDel(null); load();
  };

  const fmtD = (d) => d ? new Date(d).toLocaleDateString("de",{day:"2-digit",month:"2-digit",year:"2-digit"}) : "";

  return (<div>
    {/* OFFENE IDEEN */}
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
      <div>
        <h3 style={{fontSize:16,fontWeight:600}}>Ideen für die Drehtage</h3>
        <p style={{fontSize:13,color:T.textMuted,marginTop:2}}>Sammle hier alle Ideen. Abgestrichene wandern ins Archiv und gehen nicht verloren.</p>
      </div>
      {canEdit && <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        <input ref={ideaFileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onIdeaFile} style={{display:"none"}}/>
        <Button icon="upload" size="sm" variant="secondary" onClick={()=>ideaFileRef.current?.click()} disabled={importing}>Tabelle hochladen</Button>
        <Button icon="add" size="sm" onClick={()=>{setNewIdea({title:"",notes:""});setShowAdd(true);}}>Idee hinzufügen</Button>
      </div>}
    </div>
    {importMsg && <div style={{marginBottom:14,padding:"10px 14px",borderRadius:10,fontSize:13,background:importMsg.ok?T.success+"22":T.danger+"22",color:importMsg.ok?T.success:T.danger,border:`1px solid ${importMsg.ok?T.success:T.danger}44`}}>{importMsg.text}</div>}
    {loading
      ? <div style={{color:T.textDim,fontSize:14,padding:"14px 0"}}>Lädt...</div>
      : open.length===0
        ? <div style={{color:T.textDim,fontSize:14,padding:"20px",textAlign:"center",border:`1px dashed ${T.border}`,borderRadius:12}}>Keine offenen Ideen. Leg die erste an.</div>
        : <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {open.map(idea=>(
              <div key={idea.id} style={{display:"flex",alignItems:"flex-start",gap:12,border:`1px solid ${T.border}`,borderRadius:12,padding:14,background:T.bgInput}}>
                <button onClick={()=>canEdit&&toggleDone(idea)} title="Als erledigt abstreichen" disabled={!canEdit} style={{flexShrink:0,marginTop:1,width:24,height:24,borderRadius:7,border:`2px solid ${T.border}`,background:"transparent",cursor:canEdit?"pointer":"default",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.textDim} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{opacity:0}}><polyline points="20 6 9 17 4 12"/></svg>
                </button>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:15,fontWeight:600}}>{idea.title}</div>
                  {idea.notes && <div style={{fontSize:13,color:T.textMuted,marginTop:4,whiteSpace:"pre-wrap"}}>{idea.notes}</div>}
                </div>
                {canEdit && <div style={{display:"flex",gap:4,flexShrink:0}}>
                  <button onClick={()=>setEditItem({id:idea.id,title:idea.title,notes:idea.notes||""})} title="Bearbeiten" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4}}><Icon name="edit" size={15}/></button>
                  <button onClick={()=>setConfirmDel({kind:"idea",id:idea.id,label:idea.title})} title="Löschen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4}}><Icon name="delete" size={15}/></button>
                </div>}
              </div>
            ))}
          </div>}

    {/* ARCHIV / ERLEDIGT MIT PERFORMANCE */}
    {done.length>0 && <div style={{marginTop:28}}>
      <h3 style={{fontSize:16,fontWeight:600,marginBottom:4}}>Umgesetzt &amp; Performance</h3>
      <p style={{fontSize:13,color:T.textMuted,marginBottom:14}}>Erledigte Ideen. Sobald das Video online ist, könnt ihr hier die Zahlen eintragen.</p>
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {done.map(idea=>{
          const hasPerf = idea.views||idea.likes||idea.comments||idea.shares||idea.saves||idea.videoLink||idea.performanceNotes;
          return (<div key={idea.id} style={{border:`1px solid ${T.border}`,borderRadius:12,padding:14,background:T.bgInput}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
              <button onClick={()=>canEdit&&toggleDone(idea)} title="Wieder öffnen" disabled={!canEdit} style={{flexShrink:0,marginTop:1,width:24,height:24,borderRadius:7,border:`2px solid ${T.success}`,background:T.success,cursor:canEdit?"pointer":"default",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </button>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:15,fontWeight:600,textDecoration:"line-through",textDecorationColor:T.textDim,color:T.textMuted}}>{idea.title}</div>
                {idea.doneAt && <div style={{fontSize:11,color:T.textDim,marginTop:2}}>erledigt am {fmtD(idea.doneAt)}</div>}
                {hasPerf && <div style={{display:"flex",flexWrap:"wrap",gap:14,marginTop:10}}>
                  {idea.videoLink && <a href={idea.videoLink.startsWith("http")?idea.videoLink:`https://${idea.videoLink}`} target="_blank" rel="noopener noreferrer" style={{fontSize:12,color:T.accent,textDecoration:"none",display:"inline-flex",alignItems:"center",gap:5,width:"100%"}}><Icon name="upload" size={13}/> Video ansehen</a>}
                  {[["Views",idea.views],["Likes",idea.likes],["Kommentare",idea.comments],["Shares",idea.shares],["Saves",idea.saves]].filter(([,v])=>v).map(([l,v])=>(
                    <div key={l} style={{minWidth:60}}><div style={{fontSize:16,fontWeight:700}}>{v}</div><div style={{fontSize:11,color:T.textDim}}>{l}</div></div>
                  ))}
                  {idea.performanceNotes && <div style={{fontSize:12,color:T.textMuted,fontStyle:"italic",width:"100%"}}>{idea.performanceNotes}</div>}
                </div>}
              </div>
              {canEdit && <div style={{display:"flex",gap:4,flexShrink:0}}>
                <button onClick={()=>setPerfItem({id:idea.id,videoLink:idea.videoLink||"",views:idea.views||"",likes:idea.likes||"",comments:idea.comments||"",shares:idea.shares||"",saves:idea.saves||"",performanceNotes:idea.performanceNotes||""})} title="Performance eintragen" style={{background:T.accentGlow,border:"none",color:T.accent,cursor:"pointer",padding:"5px 10px",borderRadius:7,fontSize:12,fontWeight:600,whiteSpace:"nowrap"}}>{hasPerf?"Zahlen":"+ Zahlen"}</button>
                <button onClick={()=>setConfirmDel({kind:"idea",id:idea.id,label:idea.title})} title="Löschen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4}}><Icon name="delete" size={15}/></button>
              </div>}
            </div>
          </div>);
        })}
      </div>
    </div>}

    {/* ─── HOOKS (nach Richtung gruppiert) ─── */}
    <div style={{marginTop:32,paddingTop:24,borderTop:`1px solid ${T.border}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
        <div>
          <h3 style={{fontSize:16,fontWeight:600}}>Hooks</h3>
          <p style={{fontSize:13,color:T.textMuted,marginTop:2}}>Sammle Aufhänger nach Richtung sortiert. Du kannst eigene Richtungen anlegen und benennen.</p>
        </div>
        {canEdit && <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <input ref={hookFileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onHookFile} style={{display:"none"}}/>
          <Button icon="upload" size="sm" variant="secondary" onClick={()=>hookFileRef.current?.click()} disabled={importing}>Tabelle hochladen</Button>
          <Button icon="add" size="sm" variant="secondary" onClick={()=>{setNewCat("");setShowAddCat(true);}}>Neue Richtung</Button>
        </div>}
      </div>

      {hookCategories.length===0
        ? <div style={{color:T.textDim,fontSize:14,padding:"20px",textAlign:"center",border:`1px dashed ${T.border}`,borderRadius:12}}>Noch keine Hooks. Leg eine Richtung an, zum Beispiel "Fehler-Hooks" oder "Zahlen-Hooks".</div>
        : <div style={{display:"flex",flexDirection:"column",gap:20}}>
            {hookCategories.map(([cat,catHooks])=>(
              <div key={cat}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,borderBottom:`2px solid ${T.accent}`,paddingBottom:6,marginBottom:10}}>
                  <h4 style={{fontSize:15,fontWeight:700,color:T.accent}}>{cat}</h4>
                  {canEdit && <div style={{display:"flex",gap:4,flexShrink:0}}>
                    <button onClick={()=>{setAddHookTo(cat);setNewHookText("");}} title="Hook hinzufügen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4,display:"flex",alignItems:"center"}}><Icon name="add" size={15}/></button>
                    <button onClick={()=>setRenameCat({old:cat,value:cat})} title="Richtung umbenennen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4}}><Icon name="edit" size={15}/></button>
                    <button onClick={()=>setConfirmDel({kind:"category",category:cat})} title="Ganze Richtung löschen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4}}><Icon name="delete" size={15}/></button>
                  </div>}
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:7}}>
                  {catHooks.map(h=>(
                    <div key={h.id} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"4px 0"}}>
                      <span style={{color:T.accent,fontWeight:700,lineHeight:1.5,flexShrink:0}}>·</span>
                      <span style={{flex:1,fontSize:14,lineHeight:1.5}}>{h.text}</span>
                      {canEdit && <div style={{display:"flex",gap:2,flexShrink:0}}>
                        <button onClick={()=>setEditHook({id:h.id,text:h.text})} title="Bearbeiten" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:3}}><Icon name="edit" size={13}/></button>
                        <button onClick={()=>setConfirmDel({kind:"hook",id:h.id})} title="Löschen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:3}}><Icon name="delete" size={13}/></button>
                      </div>}
                    </div>
                  ))}
                  {canEdit && <button onClick={()=>{setAddHookTo(cat);setNewHookText("");}} style={{alignSelf:"flex-start",marginTop:4,background:"none",border:"none",color:T.textDim,cursor:"pointer",fontSize:13,display:"flex",alignItems:"center",gap:5,padding:"3px 0"}}><Icon name="add" size={13}/> Hook hinzufügen</button>}
                </div>
              </div>
            ))}
          </div>}
    </div>

    {/* Neue Richtung anlegen */}
    <Modal open={showAddCat} onClose={()=>setShowAddCat(false)} title="Neue Richtung" width={440}>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <Input label="Name der Richtung" value={newCat} onChange={e=>setNewCat(e.target.value)} placeholder="z.B. Fehler-Hooks, Zahlen-Hooks"/>
        <Button icon="check" onClick={addCategory}>Weiter</Button>
      </div>
    </Modal>

    {/* Hook hinzufügen */}
    <Modal open={!!addHookTo} onClose={()=>setAddHookTo(null)} title={addHookTo?`Hook für "${addHookTo}"`:"Hook"} width={460}>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <div>
          <div style={{fontSize:13,fontWeight:600,color:T.textMuted,marginBottom:6}}>Hook</div>
          <textarea value={newHookText} onChange={e=>setNewHookText(e.target.value)} placeholder="z.B. Diesen einen Fehler machen fast alle beim Hausverkauf." style={{width:"100%",minHeight:70,padding:"10px 12px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,fontSize:14,fontFamily:"inherit",resize:"vertical"}}/>
        </div>
        <Button icon="check" onClick={addHook}>Speichern</Button>
      </div>
    </Modal>

    {/* Hook bearbeiten */}
    <Modal open={!!editHook} onClose={()=>setEditHook(null)} title="Hook bearbeiten" width={460}>
      {editHook && <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <textarea value={editHook.text} onChange={e=>setEditHook({...editHook,text:e.target.value})} style={{width:"100%",minHeight:70,padding:"10px 12px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,fontSize:14,fontFamily:"inherit",resize:"vertical"}}/>
        <Button icon="check" onClick={saveHookEdit}>Speichern</Button>
      </div>}
    </Modal>

    {/* Richtung umbenennen */}
    <Modal open={!!renameCat} onClose={()=>setRenameCat(null)} title="Richtung umbenennen" width={440}>
      {renameCat && <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <Input label="Name der Richtung" value={renameCat.value} onChange={e=>setRenameCat({...renameCat,value:e.target.value})}/>
        <Button icon="check" onClick={saveRenameCat}>Speichern</Button>
      </div>}
    </Modal>

    {/* Idee hinzufügen */}
    <Modal open={showAdd} onClose={()=>setShowAdd(false)} title="Neue Dreh-Idee" width={460}>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <Input label="Idee" value={newIdea.title} onChange={e=>setNewIdea({...newIdea,title:e.target.value})} placeholder="Worum geht's im Video?"/>
        <div>
          <div style={{fontSize:13,fontWeight:600,color:T.textMuted,marginBottom:6}}>Details (optional)</div>
          <textarea value={newIdea.notes} onChange={e=>setNewIdea({...newIdea,notes:e.target.value})} placeholder="Ablauf, Location, Hook..." style={{width:"100%",minHeight:80,padding:"10px 12px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,fontSize:14,fontFamily:"inherit",resize:"vertical"}}/>
        </div>
        <Button icon="check" onClick={addIdea}>Speichern</Button>
      </div>
    </Modal>

    {/* Idee bearbeiten */}
    <Modal open={!!editItem} onClose={()=>setEditItem(null)} title="Idee bearbeiten" width={460}>
      {editItem && <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <Input label="Idee" value={editItem.title} onChange={e=>setEditItem({...editItem,title:e.target.value})}/>
        <div>
          <div style={{fontSize:13,fontWeight:600,color:T.textMuted,marginBottom:6}}>Details</div>
          <textarea value={editItem.notes} onChange={e=>setEditItem({...editItem,notes:e.target.value})} style={{width:"100%",minHeight:80,padding:"10px 12px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,fontSize:14,fontFamily:"inherit",resize:"vertical"}}/>
        </div>
        <Button icon="check" onClick={saveEdit}>Speichern</Button>
      </div>}
    </Modal>

    {/* Performance eintragen */}
    <Modal open={!!perfItem} onClose={()=>setPerfItem(null)} title="Performance dokumentieren" width={500}>
      {perfItem && <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <Input label="Link zum Video" value={perfItem.videoLink} onChange={e=>setPerfItem({...perfItem,videoLink:e.target.value})} placeholder="https://..."/>
        <div className="grid-3col" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
          <Input label="Views" value={perfItem.views} onChange={e=>setPerfItem({...perfItem,views:e.target.value})}/>
          <Input label="Likes" value={perfItem.likes} onChange={e=>setPerfItem({...perfItem,likes:e.target.value})}/>
          <Input label="Kommentare" value={perfItem.comments} onChange={e=>setPerfItem({...perfItem,comments:e.target.value})}/>
          <Input label="Shares" value={perfItem.shares} onChange={e=>setPerfItem({...perfItem,shares:e.target.value})}/>
          <Input label="Saves" value={perfItem.saves} onChange={e=>setPerfItem({...perfItem,saves:e.target.value})}/>
        </div>
        <div>
          <div style={{fontSize:13,fontWeight:600,color:T.textMuted,marginBottom:6}}>Notiz zur Performance</div>
          <textarea value={perfItem.performanceNotes} onChange={e=>setPerfItem({...perfItem,performanceNotes:e.target.value})} placeholder="Was lief gut, was nicht?" style={{width:"100%",minHeight:70,padding:"10px 12px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,color:T.text,fontSize:14,fontFamily:"inherit",resize:"vertical"}}/>
        </div>
        <Button icon="check" onClick={savePerf}>Speichern</Button>
      </div>}
    </Modal>

    {/* Löschen — 2. Bestätigung */}
    <Modal open={!!confirmDel} onClose={()=>setConfirmDel(null)} title="Wirklich löschen?" width={420}>
      {confirmDel && <div>
        <p style={{fontSize:14,color:T.textMuted,lineHeight:1.5,marginBottom:20}}>
          {confirmDel.kind==="idea" && <>Die Idee "{confirmDel.label}" wird endgültig gelöscht, samt eingetragener Performance. Das lässt sich nicht rückgängig machen.</>}
          {confirmDel.kind==="hook" && <>Dieser Hook wird endgültig gelöscht. Das lässt sich nicht rückgängig machen.</>}
          {confirmDel.kind==="category" && <>Die ganze Richtung "{confirmDel.category}" wird mit allen Hooks darin endgültig gelöscht. Das lässt sich nicht rückgängig machen.</>}
        </p>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <Button variant="secondary" onClick={()=>setConfirmDel(null)}>Abbrechen</Button>
          <Button variant="danger" icon="delete" onClick={doDelete}>Ja, löschen</Button>
        </div>
      </div>}
    </Modal>
  </div>);
};

// ═══════════════════════════════════════
// DIENSTLEISTUNGS-MODUL (wiederverwendbar: Portal + Admin)
// Nimmt das Schema einer Dienstleistung und verwaltet die Einträge.
// ═══════════════════════════════════════
const ServiceModule = ({clientId, service, canEdit=true, createdBy="admin"}) => {
  const schema = SERVICE_SCHEMAS[service];
  const [items,setItems] = useState([]);
  const [loading,setLoading] = useState(true);
  const [showForm,setShowForm] = useState(null); // type-objekt für neuen Eintrag
  const [editItem,setEditItem] = useState(null);
  const emptyFor = (type) => ({entryType:type.id, title:"", url:"", url2:"", status:type.statuses?.[0]?.id||"", notes:""});
  const [form,setForm] = useState({});

  const load = () => {
    api.get("client-service-items", `clientId=${clientId}&service=${service}`)
      .then(d=>{ setItems(Array.isArray(d)?d:[]); setLoading(false); })
      .catch(()=>setLoading(false));
  };
  useEffect(()=>{ load(); }, [clientId, service]);

  const openAdd = (type) => { setForm(emptyFor(type)); setShowForm(type); };
  const add = async () => {
    if(!form.title?.trim()){ alert("Bitte eine Bezeichnung angeben."); return; }
    await api.post("client-service-items", {...form, clientId, service, createdBy, sortOrder:items.length});
    setShowForm(null); load();
  };
  const saveEdit = async () => {
    if(!editItem?.title?.trim()) return;
    await api.put("client-service-items", editItem);
    setEditItem(null); load();
  };
  const del = async (id) => {
    if(!confirm("Diesen Eintrag wirklich löschen?")) return;
    await api.del("client-service-items", {id});
    load();
  };

  const statusBadge = (type, statusId) => {
    const st = type.statuses?.find(s=>s.id===statusId);
    if(!st) return null;
    const colorMap = {success:T.success,warning:T.warning,info:T.info,danger:T.danger,accent:T.accent,muted:T.textDim};
    const col = colorMap[st.color]||T.textDim;
    return <span style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:11,fontWeight:600,color:col,background:col+"1a",padding:"3px 9px",borderRadius:7}}><span style={{width:6,height:6,borderRadius:"50%",background:col}}/>{st.label}</span>;
  };

  // Formularfelder je nach Typ
  const renderFields = (type,obj,setObj) => (<div style={{display:"flex",flexDirection:"column",gap:12}}>
    {type.fields.title && <Input label={type.fields.title} value={obj.title||""} onChange={e=>setObj({...obj,title:e.target.value})}/>}
    {type.fields.url && <Input label={type.fields.url} value={obj.url||""} onChange={e=>setObj({...obj,url:e.target.value})} placeholder="https://..."/>}
    {type.fields.url2 && <Input label={type.fields.url2} value={obj.url2||""} onChange={e=>setObj({...obj,url2:e.target.value})} placeholder="https://..."/>}
    {type.fields.status && type.statuses && <div>
      <div style={{fontSize:13,fontWeight:600,color:T.textMuted,marginBottom:8}}>Status</div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
        {type.statuses.map(st=>{
          const on = obj.status===st.id;
          const colorMap = {success:T.success,warning:T.warning,info:T.info,danger:T.danger,accent:T.accent,muted:T.textDim};
          const col = colorMap[st.color]||T.textDim;
          return <button key={st.id} type="button" onClick={()=>setObj({...obj,status:st.id})} style={{padding:"7px 13px",borderRadius:8,border:`1px solid ${on?col:T.border}`,background:on?col+"1a":T.bgInput,color:on?col:T.textDim,fontSize:13,fontWeight:600,cursor:"pointer"}}>{st.label}</button>;
        })}
      </div>
    </div>}
    {type.fields.notes && <Input label={type.fields.notes} value={obj.notes||""} onChange={e=>setObj({...obj,notes:e.target.value})}/>}
  </div>);

  if(!schema) return null;

  return (<div style={{display:"flex",flexDirection:"column",gap:24}}>
    {schema.types.map(type=>{
      const typeItems = items.filter(i=>i.entryType===type.id);
      return (<div key={type.id}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
          <h3 style={{fontSize:16,fontWeight:600}}>{type.label}</h3>
          {canEdit && <Button icon="add" size="sm" onClick={()=>openAdd(type)}>{type.addLabel}</Button>}
        </div>

        {canEdit && <Modal open={showForm?.id===type.id} onClose={()=>setShowForm(null)} title={type.addLabel} width={480}>
          {renderFields(type,form,setForm)}
          <Button icon="check" onClick={add} style={{marginTop:16}}>Speichern</Button>
        </Modal>}

        {loading
          ? <div style={{color:T.textDim,fontSize:14,padding:"12px 0"}}>Lädt...</div>
          : typeItems.length===0
            ? <div style={{color:T.textDim,fontSize:14,padding:"16px",textAlign:"center",border:`1px dashed ${T.border}`,borderRadius:12}}>Noch nichts hinterlegt.</div>
            : <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {typeItems.map(it=>(
                  <div key={it.id} style={{border:`1px solid ${T.border}`,borderRadius:12,padding:16,background:T.bgInput}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:8}}>
                      <div style={{minWidth:0,flex:1}}>
                        <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                          <span style={{fontSize:15,fontWeight:600}}>{it.title}</span>
                          {it.status && statusBadge(type,it.status)}
                        </div>
                      </div>
                      {canEdit && <div style={{display:"flex",gap:4,flexShrink:0}}>
                        <button onClick={()=>setEditItem({id:it.id,entryType:it.entryType,title:it.title||"",url:it.url||"",url2:it.url2||"",status:it.status||"",notes:it.notes||""})} title="Bearbeiten" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4}}><Icon name="edit" size={15}/></button>
                        <button onClick={()=>del(it.id)} title="Löschen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4}}><Icon name="delete" size={15}/></button>
                      </div>}
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      {it.url && <a href={it.url.startsWith("http")?it.url:`https://${it.url}`} target="_blank" rel="noopener noreferrer" style={{display:"inline-flex",alignItems:"center",gap:7,fontSize:13,color:T.accent,textDecoration:"none",wordBreak:"break-all"}}><Icon name="upload" size={14}/> {type.fields.url}: {it.url.replace(/^https?:\/\//,"")}</a>}
                      {it.url2 && <a href={it.url2.startsWith("http")?it.url2:`https://${it.url2}`} target="_blank" rel="noopener noreferrer" style={{display:"inline-flex",alignItems:"center",gap:7,fontSize:13,color:T.success,textDecoration:"none",wordBreak:"break-all"}}><Icon name="check" size={14}/> {type.fields.url2}: {it.url2.replace(/^https?:\/\//,"")}</a>}
                      {it.notes && <div style={{fontSize:12,color:T.textMuted,fontStyle:"italic"}}>{it.notes}</div>}
                    </div>
                  </div>
                ))}
              </div>}
      </div>);
    })}

    {/* Bearbeiten-Modal (gemeinsam) */}
    {canEdit && <Modal open={!!editItem} onClose={()=>setEditItem(null)} title="Eintrag bearbeiten" width={480}>
      {editItem && (()=>{ const type = schema.types.find(t=>t.id===editItem.entryType); return type?<>{renderFields(type,editItem,setEditItem)}<Button icon="check" onClick={saveEdit} style={{marginTop:16}}>Speichern</Button></>:null; })()}
    </Modal>}
  </div>);
};

// ═══════════════════════════════════════
// KUNDENPORTAL (Etappe 2: Seitenleiste + Ansprechpartner-Karten)
// Eigener Bereich für Kunden, im Look des CRM, aber getrennt.
// ═══════════════════════════════════════
// ═══════════════════════════════════════
// LEAD-KEY VERWALTUNG (Admin) — Schlüssel + Einbau-Anleitung für die Landingpage
// ═══════════════════════════════════════
const LeadKeyManager = ({clientId}) => {
  const [key,setKey] = useState(null);
  const [loading,setLoading] = useState(true);
  const [showGuide,setShowGuide] = useState(false);
  const [copied,setCopied] = useState(false);

  useEffect(()=>{
    api.get("lead-key", `userId=${clientId}`).then(r=>{ setKey(r.key||null); setLoading(false); }).catch(()=>setLoading(false));
  }, [clientId]);

  const generate = async () => {
    if(key && !confirm("Neuen Schlüssel erzeugen? Der alte funktioniert dann nicht mehr und muss in der Landingpage ersetzt werden.")) return;
    const r = await api.post("lead-key", {userId:clientId});
    if(r.key) setKey(r.key);
  };
  const copy = (text) => { try{ navigator.clipboard.writeText(text); setCopied(true); setTimeout(()=>setCopied(false),1500);}catch(e){} };

  const snippet = key ? `<script>
async function rfSubmitLead(e){
  e.preventDefault();
  const f = e.target;
  await fetch("https://login.rf-media.de/api/?action=lead-intake", {
    method:"POST",
    headers:{"Content-Type":"application/json"},
    body: JSON.stringify({
      key: "${key}",
      source: "Instagram",            // oder "Facebook"
      firstName: f.firstName.value,
      lastName:  f.lastName.value,
      phone:     f.phone.value,
      email:     f.email.value
    })
  });
  f.reset();
  alert("Danke! Wir melden uns.");
}
</script>` : "";

  return (<div style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:12,padding:16}}>
    {loading ? <div style={{fontSize:13,color:T.textDim}}>Lädt...</div> : <>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
        <div style={{minWidth:0}}>
          <div style={{fontSize:13,fontWeight:600,marginBottom:4}}>Landingpage-Schlüssel</div>
          {key
            ? <div style={{display:"flex",alignItems:"center",gap:8}}>
                <code style={{fontSize:12,fontFamily:"'JetBrains Mono',monospace",color:T.accent,wordBreak:"break-all"}}>{key}</code>
                <button onClick={()=>copy(key)} title="Kopieren" style={{background:"none",border:"none",color:copied?T.success:T.textDim,cursor:"pointer",padding:3}}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg></button>
              </div>
            : <div style={{fontSize:12,color:T.textDim}}>Noch kein Schlüssel erzeugt.</div>}
        </div>
        <div style={{display:"flex",gap:8}}>
          <Button size="sm" variant="secondary" onClick={generate}>{key?"Neu erzeugen":"Schlüssel erzeugen"}</Button>
          {key && <Button size="sm" variant="secondary" onClick={()=>setShowGuide(!showGuide)}>{showGuide?"Anleitung ausblenden":"Einbau-Anleitung"}</Button>}
        </div>
      </div>
      {showGuide && key && <div style={{marginTop:14,borderTop:`1px solid ${T.border}`,paddingTop:14}}>
        <div style={{fontSize:13,color:T.textMuted,marginBottom:10,lineHeight:1.6}}>Füge diesen Code in die Landingpage ein und ruf beim Formular <code style={{color:T.accent}}>onsubmit="rfSubmitLead(event)"</code> auf. Die Felder müssen <code style={{color:T.accent}}>firstName</code>, <code style={{color:T.accent}}>lastName</code>, <code style={{color:T.accent}}>phone</code> und <code style={{color:T.accent}}>email</code> heißen.</div>
        <div style={{position:"relative"}}>
          <pre style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:8,padding:14,fontSize:11.5,fontFamily:"'JetBrains Mono',monospace",overflow:"auto",color:T.text,lineHeight:1.5,margin:0}}>{snippet}</pre>
          <button onClick={()=>copy(snippet)} style={{position:"absolute",top:8,right:8,padding:"5px 10px",background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:7,fontSize:12,color:copied?T.success:T.textMuted,cursor:"pointer"}}>{copied?"Kopiert":"Kopieren"}</button>
        </div>
      </div>}
    </>}
  </div>);
};

// ═══════════════════════════════════════
// LANDINGPAGE-LEADS TABELLE (wiederverwendbar: Portal + Admin)
// Bearbeitbare, horizontal scrollbare Tabelle mit allen Spalten.
// ═══════════════════════════════════════
const LandingpageLeadsTable = ({clientId, canEdit=true, highlightId=null}) => {
  const [leads,setLeads] = useState([]);
  const [loading,setLoading] = useState(true);
  const [savingId,setSavingId] = useState(null);

  const load = () => {
    api.get("landingpage-leads", `clientId=${clientId}`)
      .then(d=>{ setLeads(Array.isArray(d)?d:[]); setLoading(false); })
      .catch(()=>setLoading(false));
  };
  useEffect(()=>{ load(); }, [clientId]);

  // Eine Zelle ändern → lokal sofort, ans Backend speichern
  const updateField = async (lead, key, value) => {
    setLeads(prev=>prev.map(l=>l.id===lead.id?{...l,[key]:value}:l));
    setSavingId(lead.id);
    await api.put("landingpage-leads", {id:lead.id, [key]:value});
    setSavingId(null);
  };

  const delLead = async (id) => {
    if(!confirm("Diesen Lead wirklich löschen?")) return;
    await api.del("landingpage-leads", {id});
    load();
  };

  // Zelle rendern je nach Typ
  const renderCell = (lead, col) => {
    const val = lead[col.key] ?? "";
    if(col.readOnly){
      if(col.type==="datetime" && val) return <span style={{fontSize:12,color:T.textMuted,whiteSpace:"nowrap"}}>{new Date(val).toLocaleDateString("de",{day:"2-digit",month:"2-digit",year:"2-digit"})} {new Date(val).toLocaleTimeString("de",{hour:"2-digit",minute:"2-digit"})}</span>;
      return <span style={{fontSize:12,color:T.textMuted}}>{val||"—"}</span>;
    }
    if(!canEdit){
      // Nur-Lesen-Darstellung
      const disp = col.type==="date" && val ? new Date(val).toLocaleDateString("de") : (val||"—");
      return <span style={{fontSize:13}}>{disp}</span>;
    }
    const base = {width:"100%",padding:"5px 7px",background:"transparent",border:`1px solid transparent`,borderRadius:6,color:T.text,fontSize:13,outline:"none"};
    const onFocus = e=>{e.target.style.border=`1px solid ${T.accent}`;e.target.style.background=T.bgInput;};
    const onBlurStyle = e=>{e.target.style.border="1px solid transparent";e.target.style.background="transparent";};
    if(col.type==="yesno" || col.type==="select"){
      const opts = col.type==="yesno"?YESNO:col.options;
      return <select value={val} onChange={e=>updateField(lead,col.key,e.target.value)} style={{...base,cursor:"pointer"}}>
        {opts.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
      </select>;
    }
    return <input type={col.type==="date"?"date":col.type==="number"?"number":"text"} defaultValue={col.type==="date"&&val?String(val).slice(0,10):val}
      onFocus={onFocus} onBlur={e=>{onBlurStyle(e); if(e.target.value!==String(val)) updateField(lead,col.key,e.target.value);}}
      style={base}/>;
  };

  return (<div>
    {loading
      ? <div style={{color:T.textDim,fontSize:14,padding:"30px 0",textAlign:"center"}}>Lädt...</div>
      : leads.length===0
        ? <div style={{color:T.textDim,fontSize:14,padding:"40px 20px",textAlign:"center",border:`1px dashed ${T.border}`,borderRadius:12}}>Noch keine Leads eingegangen. Sobald über die Landingpage eine Anfrage kommt, erscheint sie hier.</div>
        : <div className="table-wrap" style={{overflowX:"auto",WebkitOverflowScrolling:"touch",border:`1px solid ${T.border}`,borderRadius:12,maxWidth:"100%"}}>
            <table style={{borderCollapse:"collapse",width:"100%",minWidth:2400}}>
              <thead>
                <tr style={{background:T.bgInput}}>
                  {LANDINGPAGE_LEAD_COLUMNS.map(col=>(
                    <th key={col.key} style={{padding:"10px 10px",textAlign:"left",fontSize:11,fontWeight:700,color:T.textDim,textTransform:"uppercase",letterSpacing:0.3,whiteSpace:"nowrap",minWidth:col.w,borderBottom:`1px solid ${T.border}`}}>{col.label}</th>
                  ))}
                  {canEdit && <th style={{padding:"10px",borderBottom:`1px solid ${T.border}`,position:"sticky",right:0,background:T.bgInput}}></th>}
                </tr>
              </thead>
              <tbody>
                {leads.map(lead=>{
                  const isHl = highlightId===lead.id;
                  return (<tr key={lead.id} style={{borderBottom:`1px solid ${T.border}`,background:isHl?T.accentGlow:(lead.isNew?T.warning+"11":"transparent")}}>
                    {LANDINGPAGE_LEAD_COLUMNS.map(col=>(
                      <td key={col.key} style={{padding:"3px 4px",minWidth:col.w,verticalAlign:"middle"}}>{renderCell(lead,col)}</td>
                    ))}
                    {canEdit && <td style={{padding:"3px 8px",position:"sticky",right:0,background:isHl?T.bgCard:T.bgCard}}>
                      <button onClick={()=>delLead(lead.id)} title="Löschen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4}}><Icon name="delete" size={15}/></button>
                    </td>}
                  </tr>);
                })}
              </tbody>
            </table>
          </div>}
    {savingId && <div style={{fontSize:12,color:T.textDim,marginTop:8}}>Speichert...</div>}
  </div>);
};

const ClientPortal = ({user, onLogout}) => {
  const [profile,setProfile] = useState(null);
  const [loading,setLoading] = useState(true);
  const [activePage,setActivePage] = useState("overview");
  const [sidebarOpen,setSidebarOpen] = useState(false);

  useEffect(()=>{
    api.get("client-profile", `userId=${user.id}`)
      .then(p=>{ setProfile(p); setLoading(false); })
      .catch(()=>{ setLoading(false); });
  }, [user.id]);

  const services = (profile?.services||[]).map(serviceById).filter(Boolean);
  const hasLeadGen = (profile?.services||[]).includes("meta_ads");
  const hasSocial = (profile?.services||[]).includes("social_media");
  const nav = [
    {id:"overview",label:"Übersicht",icon:"dashboard"},
    ...(hasLeadGen?[{id:"leads",label:"Leads",icon:"leads"}]:[]),
    ...(hasSocial?[{id:"shoot_ideas",label:"Dreh-Ideen",icon:"clipboard"}]:[]),
    {id:"credentials",label:"Zugangsdaten",icon:"clipboard"},
    ...services.map(s=>({id:s.id,label:s.label,icon:s.icon}))
  ];

  // Ansprechpartner-Karte mit allen Kontaktdaten
  const ContactCard = ({a}) => (
    <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:14,boxShadow:T.shadow,padding:18}}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:a.phone||a.email||a.website?14:0}}>
        <Avatar userId={a.id} name={a.name} size={46} editable={false}/>
        <div style={{minWidth:0}}>
          <div style={{fontSize:15,fontWeight:600}}>{a.name}</div>
          <div style={{fontSize:12,color:T.textDim}}>R&F Media</div>
        </div>
      </div>
      {(a.phone||a.email||a.website) && <div style={{display:"flex",flexDirection:"column",gap:8,borderTop:`1px solid ${T.border}`,paddingTop:14}}>
        {a.phone && <a href={`tel:${a.phone.replace(/\s/g,"")}`} style={{display:"flex",alignItems:"center",gap:10,color:T.text,textDecoration:"none",fontSize:13}}>
          <span style={{width:30,height:30,borderRadius:8,background:T.bgInput,display:"flex",alignItems:"center",justifyContent:"center",color:T.accent,flexShrink:0}}><Icon name="phone" size={15}/></span>
          {a.phone}
        </a>}
        {a.email && <a href={`mailto:${a.email}`} style={{display:"flex",alignItems:"center",gap:10,color:T.text,textDecoration:"none",fontSize:13,wordBreak:"break-all"}}>
          <span style={{width:30,height:30,borderRadius:8,background:T.bgInput,display:"flex",alignItems:"center",justifyContent:"center",color:T.accent,flexShrink:0}}><svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg></span>
          {a.email}
        </a>}
        {a.website && <a href={a.website.startsWith("http")?a.website:`https://${a.website}`} target="_blank" rel="noopener noreferrer" style={{display:"flex",alignItems:"center",gap:10,color:T.text,textDecoration:"none",fontSize:13,wordBreak:"break-all"}}>
          <span style={{width:30,height:30,borderRadius:8,background:T.bgInput,display:"flex",alignItems:"center",justifyContent:"center",color:T.accent,flexShrink:0}}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg></span>
          {a.website.replace(/^https?:\/\//,"")}
        </a>}
      </div>}
    </div>
  );

  // Inhalt je nach aktiver Seite
  const renderContent = () => {
    if(activePage==="shoot_ideas"){
      return (<>
        <div style={{marginBottom:28}}>
          <h1 style={{fontSize:26,fontWeight:700,letterSpacing:"-0.02em"}}>Dreh-Ideen</h1>
          <p style={{color:T.textDim,fontSize:15,marginTop:6}}>Hier sammeln wir gemeinsam die Ideen für deine Drehtage und halten später fest, wie die Videos liefen.</p>
        </div>
        <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:24}}>
          <ShootIdeasModule clientId={user.id} canEdit={true} createdBy="kunde"/>
        </div>
      </>);
    }
    if(activePage==="leads"){
      return (<>
        <div style={{marginBottom:28}}>
          <h1 style={{fontSize:26,fontWeight:700,letterSpacing:"-0.02em"}}>Deine Leads</h1>
          <p style={{color:T.textDim,fontSize:15,marginTop:6}}>Alle Anfragen, die über deine Landingpage reingekommen sind. Du kannst die Tabelle direkt bearbeiten und den Status pflegen.</p>
        </div>
        <LandingpageLeadsTable clientId={user.id} canEdit={true}/>
      </>);
    }
    if(activePage==="credentials"){
      return (<>
        <div style={{marginBottom:28}}>
          <h1 style={{fontSize:26,fontWeight:700,letterSpacing:"-0.02em"}}>Zugangsdaten</h1>
          <p style={{color:T.textDim,fontSize:15,marginTop:6}}>Alle wichtigen Zugänge an einem sicheren Ort. Du kannst hier auch eigene Zugänge ergänzen.</p>
        </div>
        <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:24}}>
          <CredentialsManager clientId={user.id} canEdit={true}/>
        </div>
      </>);
    }
    if(activePage==="overview"){
      return (<>
        {/* Kundenlogo mittig */}
        {profile?.logo && <div style={{display:"flex",justifyContent:"center",marginBottom:28}}>
          <div style={{background:"#fff",border:`1px solid ${T.border}`,borderRadius:14,boxShadow:T.shadow,padding:"28px 40px",display:"flex",alignItems:"center",justifyContent:"center",maxWidth:"100%"}}>
            <img src={profile.logo} alt={profile.company||"Logo"} style={{maxWidth:280,maxHeight:120,objectFit:"contain"}}/>
          </div>
        </div>}
        <div style={{marginBottom:28}}>
          <h1 style={{fontSize:26,fontWeight:700,letterSpacing:"-0.02em"}}>Willkommen{profile?.company?`, ${profile.company}`:profile?.name?`, ${profile.name}`:""}!</h1>
          <p style={{color:T.textDim,fontSize:15,marginTop:6}}>Hier findest du alles rund um unsere Zusammenarbeit an einem Ort.</p>
        </div>

        {/* Erreichbarkeit */}
        <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:24,marginBottom:20}}>
          <h3 style={{fontSize:16,fontWeight:600,marginBottom:14}}>Schnell erreichbar</h3>
          <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
            {profile?.meetLink && <a href={profile.meetLink} target="_blank" rel="noopener noreferrer" style={{display:"inline-flex",alignItems:"center",gap:8,padding:"11px 18px",background:T.accent,border:"none",borderRadius:10,color:"#fff",textDecoration:"none",fontSize:14,fontWeight:600}}>
              <Icon name="calendar" size={16}/> Google Meet starten
            </a>}
            {profile?.assignedAdmins?.[0]?.phone && <a href={`tel:${profile.assignedAdmins[0].phone.replace(/\s/g,"")}`} style={{display:"inline-flex",alignItems:"center",gap:8,padding:"11px 18px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:10,color:T.text,textDecoration:"none",fontSize:14,fontWeight:600}}>
              <Icon name="phone" size={16}/> Anrufen
            </a>}
          </div>
        </div>

        {/* Freigeschaltete Bereiche als Schnellzugriff */}
        {services.length>0 && <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:24}}>
          <h3 style={{fontSize:16,fontWeight:600,marginBottom:16}}>Deine Bereiche</h3>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(min(100%, 160px),1fr))",gap:14}}>
            {services.map(s=>(
              <button key={s.id} onClick={()=>setActivePage(s.id)} style={{textAlign:"left",border:`1px solid ${T.border}`,borderRadius:12,padding:18,background:T.bgInput,cursor:"pointer"}}>
                <div style={{width:38,height:38,borderRadius:10,background:T.accentGlow,display:"flex",alignItems:"center",justifyContent:"center",color:T.accent,marginBottom:12}}><Icon name={s.icon} size={19}/></div>
                <div style={{fontSize:15,fontWeight:600}}>{s.label}</div>
                <div style={{fontSize:12,color:T.textDim,marginTop:3,lineHeight:1.5}}>{s.desc}</div>
              </button>
            ))}
          </div>
        </div>}
      </>);
    }
    // Dienstleistungs-Seiten mit echten Inhalten
    const s = serviceById(activePage);
    return (<>
      <div style={{marginBottom:28}}>
        <h1 style={{fontSize:26,fontWeight:700,letterSpacing:"-0.02em"}}>{s?.label}</h1>
        <p style={{color:T.textDim,fontSize:15,marginTop:6}}>{s?.desc}</p>
      </div>
      <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:24}}>
        <ServiceModule clientId={user.id} service={activePage} canEdit={true} createdBy="kunde"/>
      </div>
    </>);
  };

  const SidebarContent = () => (
    <div style={{display:"flex",flexDirection:"column",height:"100%"}}>
      <div style={{padding:"20px 20px 16px",display:"flex",alignItems:"center",gap:12,borderBottom:`1px solid ${T.border}`}}>
        <img src="/logo.png" alt="R&F Media" style={{height:30,filter:T.logoFilter}} onError={e=>e.target.style.display='none'}/>
        <div style={{fontSize:13,fontWeight:600,color:T.textMuted}}>Kundenportal</div>
      </div>
      <nav style={{flex:1,padding:"14px 12px",overflowY:"auto"}}>
        {nav.map(n=>{
          const on = activePage===n.id;
          return (<button key={n.id} onClick={()=>{setActivePage(n.id);setSidebarOpen(false);}} style={{display:"flex",alignItems:"center",gap:12,width:"100%",padding:"11px 14px",marginBottom:4,borderRadius:10,border:"none",background:on?T.accent:"transparent",color:on?"#fff":T.textMuted,cursor:"pointer",fontSize:14,fontWeight:600,textAlign:"left"}}>
            <Icon name={n.icon} size={17}/> {n.label}
          </button>);
        })}
      </nav>
      <div style={{padding:12,borderTop:`1px solid ${T.border}`}}>
        <button onClick={onLogout} style={{display:"flex",alignItems:"center",gap:12,width:"100%",padding:"11px 14px",borderRadius:10,border:"none",background:"transparent",color:T.textMuted,cursor:"pointer",fontSize:14,fontWeight:600,textAlign:"left"}}><Icon name="logout" size={17}/> Abmelden</button>
      </div>
    </div>
  );

  if(loading) return <div style={{minHeight:"100vh",background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",color:T.textDim}}>Lädt...</div>;

  return (<div style={{minHeight:"100vh",background:`${T.amb}, ${T.bg}`,display:"flex"}}>
    {/* Sidebar Desktop */}
    <aside className="client-sidebar" style={{width:240,flexShrink:0,background:T.glassBgSide,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,borderRight:`1px solid ${T.glassBorder}`,position:"sticky",top:0,height:"100vh"}}>
      <SidebarContent/>
    </aside>

    {/* Sidebar Mobile (Overlay) */}
    {sidebarOpen && <>
      <div onClick={()=>setSidebarOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:50}}/>
      <aside style={{position:"fixed",top:0,left:0,bottom:0,width:260,background:T.glassBgSide,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,borderRight:`1px solid ${T.glassBorder}`,zIndex:51}}><SidebarContent/></aside>
    </>}

    {/* Hauptbereich */}
    <div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column"}}>
      {/* Mobile-Topbar mit Menü-Button */}
      <div className="client-topbar" style={{display:"none",alignItems:"center",justifyContent:"space-between",padding:"12px 16px",borderBottom:`1px solid ${T.border}`,background:T.bgCard}}>
        <button onClick={()=>setSidebarOpen(true)} style={{background:"none",border:"none",color:T.text,cursor:"pointer",padding:4}}><Icon name="menu" size={22}/></button>
        <img src="/logo.png" alt="R&F Media" style={{height:26,filter:T.logoFilter}} onError={e=>e.target.style.display='none'}/>
        <div style={{width:30}}/>
      </div>

      <div style={{flex:1,padding:"32px 28px",maxWidth:1100,width:"100%",margin:"0 auto"}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr",gap:24}} className="client-grid">
          <div style={{minWidth:0}}>{renderContent()}</div>
          {/* Ansprechpartner-Spalte */}
          {profile?.assignedAdmins?.length>0 && <div className="client-contacts" style={{display:"flex",flexDirection:"column",gap:14}}>
            <h3 style={{fontSize:14,fontWeight:600,color:T.textMuted,textTransform:"uppercase",letterSpacing:0.5}}>Ansprechpartner</h3>
            {profile.assignedAdmins.map(a=><ContactCard key={a.id} a={a}/>)}
          </div>}
        </div>
        <footer style={{marginTop:40,paddingTop:20,borderTop:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          <span style={{fontSize:11,color:T.textDim}}>R&F Media UG</span>
          <LegalLinks/>
        </footer>
      </div>
    </div>
  </div>);
};

// ═══════════════════════════════════════
// KUNDEN-VERWALTUNG (Admin, Etappe 1)
// ═══════════════════════════════════════
// ═══════════════════════════════════════
// KUNDEN-PIPELINE
// ═══════════════════════════════════════
const rfEuro = (n) => (Number(n)||0).toLocaleString("de-DE",{style:"currency",currency:"EUR",minimumFractionDigits:0,maximumFractionDigits:0});
const rfEuroGenau = (n) => (Number(n)||0).toLocaleString("de-DE",{style:"currency",currency:"EUR"});

// Produktkatalog verwalten
const ProductCatalogModal = ({open, onClose, onChange}) => {
  const [produkte,setProdukte]=useState([]);
  const [name,setName]=useState("");
  const [preis,setPreis]=useState("");
  const [laden,setLaden]=useState(true);
  const load=()=>{ api.get('products').then(p=>{ setProdukte(Array.isArray(p)?p:[]); setLaden(false); }); };
  useEffect(()=>{ if(open){ setLaden(true); load(); } },[open]);
  const add=async()=>{
    if(!name.trim())return;
    await api.post('products',{name:name.trim(),price:preis===""?null:parseFloat(preis.replace(",","."))});
    setName(""); setPreis(""); load(); onChange&&onChange();
  };
  const del=async(id)=>{ await api.del('products',{id}); load(); onChange&&onChange(); };
  return (<Modal open={open} onClose={onClose} title="Produkte & Leistungen" width={560}>
    <p style={{fontSize:13.5,color:T.textMuted,lineHeight:1.6,marginTop:-2,marginBottom:16}}>Das ist eure Angebotsliste. Was ein Kunde davon noch nicht hat, schlägt das System später als Upsell vor.</p>
    <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap",alignItems:"flex-end"}}>
      <div style={{flex:"1 1 220px"}}><Input label="Produkt / Leistung" value={name} onChange={e=>setName(e.target.value)} placeholder="z.B. Meta Ads Betreuung"/></div>
      <div style={{width:130}}><Input label="Preis (optional)" value={preis} onChange={e=>setPreis(e.target.value)} placeholder="z.B. 700"/></div>
      <Button onClick={add} disabled={!name.trim()}>Hinzufügen</Button>
    </div>
    {laden ? <div style={{color:T.textDim,fontSize:13,padding:"20px 0",textAlign:"center"}}>Lädt...</div>
      : produkte.length===0 ? <div style={{color:T.textDim,fontSize:13,padding:"16px 0"}}>Noch keine Produkte. Leg oben das erste an.</div>
      : <div style={{display:"flex",flexDirection:"column"}}>
          {produkte.map((p,i)=>(<div key={p.id} style={{display:"flex",alignItems:"center",gap:12,padding:"11px 4px",borderBottom:i<produkte.length-1?`1px solid ${T.border}`:"none"}}>
            <div style={{flex:1,minWidth:0}}><div style={{fontSize:13.5,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.name}</div></div>
            {p.price!=null&&<div style={{fontSize:13,color:T.textMuted,fontFamily:"'JetBrains Mono',monospace"}}>{rfEuro(p.price)}</div>}
            <button onClick={()=>del(p.id)} title="Entfernen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4,display:"flex",flexShrink:0}}><Icon name="delete" size={15}/></button>
          </div>))}
        </div>}
  </Modal>);
};

// Käufe eines Kunden + Upsell-Vorschläge (im Bearbeiten-Fenster)
const CustomerPurchases = ({clientId}) => {
  const [kaeufe,setKaeufe]=useState([]);
  const [produkte,setProdukte]=useState([]);
  const [laden,setLaden]=useState(true);
  const [neu,setNeu]=useState({productName:"",amount:"",purchaseDate:""});
  const [rechnung,setRechnung]=useState(null); // {status, items} beim Auslesen
  const load=()=>{
    Promise.all([api.get('customer-purchases',`clientId=${clientId}`), api.get('products')]).then(([k,p])=>{
      setKaeufe(Array.isArray(k)?k:[]); setProdukte(Array.isArray(p)?p:[]); setLaden(false);
    });
  };
  useEffect(()=>{ setLaden(true); load(); },[clientId]);

  const summe = kaeufe.reduce((s,k)=>s+(Number(k.amount)||0),0);
  const gekauft = new Set(kaeufe.map(k=>String(k.productName||"").toLowerCase().trim()));
  const upsells = produkte.filter(p=>!gekauft.has(String(p.name||"").toLowerCase().trim()));

  const addManuell=async()=>{
    if(!neu.productName.trim())return;
    await api.post('customer-purchases',{clientId,items:[{productName:neu.productName.trim(),amount:neu.amount===""?0:parseFloat(String(neu.amount).replace(",",".")),purchaseDate:neu.purchaseDate||null,source:"manual"}]});
    setNeu({productName:"",amount:"",purchaseDate:""}); load();
  };
  const delKauf=async(id)=>{ await api.del('customer-purchases',{id}); load(); };
  const upsellAlsKauf=(name)=>setNeu(n=>({...n,productName:name}));

  // Rechnung hochladen und von der KI auslesen lassen
  const rechnungLesen=async(file)=>{
    if(!file)return;
    if(file.type!=="application/pdf"){ setRechnung({status:"fehler",msg:"Bitte eine PDF-Datei hochladen."}); return; }
    if(file.size>10000000){ setRechnung({status:"fehler",msg:"Die Datei ist zu groß (max 10 MB)."}); return; }
    setRechnung({status:"liest"});
    try{
      const b64 = await new Promise((res,rej)=>{ const r=new FileReader(); r.onload=()=>res(String(r.result).split(",")[1]); r.onerror=rej; r.readAsDataURL(file); });
      const prompt = "Das ist eine Rechnung. Lies die einzelnen Rechnungspositionen aus. Gib NUR ein JSON-Array zurück, ohne Text drumherum, ohne Markdown. Jede Position: {\"productName\": string, \"amount\": number (Nettobetrag der Position als Zahl, Punkt als Dezimaltrenner), \"purchaseDate\": \"YYYY-MM-DD\" (Rechnungsdatum)}. Wenn du nichts findest, gib [] zurück.";
      const resp = await fetch("/api/anthropic-proxy",{method:"POST",headers:authHeaders({"Content-Type":"application/json"}),body:JSON.stringify({
        model:"claude-sonnet-4-20250514", max_tokens:1500,
        messages:[{role:"user",content:[{type:"document",source:{type:"base64",media_type:"application/pdf",data:b64}},{type:"text",text:prompt}]}]
      })});
      const data = await resp.json();
      if(data.error){ setRechnung({status:"fehler",msg:data.error.message||data.error||"Auslesen fehlgeschlagen."}); return; }
      let txt = (data.content||[]).filter(c=>c.type==="text").map(c=>c.text).join("").trim();
      txt = txt.replace(/```json/gi,"").replace(/```/g,"").trim();
      let items=[];
      try{ items=JSON.parse(txt); }catch(e){ setRechnung({status:"fehler",msg:"Die Rechnung ließ sich nicht sauber auslesen. Trag die Positionen bitte von Hand ein."}); return; }
      if(!Array.isArray(items)||items.length===0){ setRechnung({status:"fehler",msg:"Auf der Rechnung wurden keine Positionen erkannt."}); return; }
      setRechnung({status:"pruefen",items:items.map(it=>({productName:it.productName||"",amount:it.amount||0,purchaseDate:it.purchaseDate||"",an:true}))});
    }catch(e){ setRechnung({status:"fehler",msg:"Beim Auslesen ist etwas schiefgelaufen."}); }
  };
  const rechnungSpeichern=async()=>{
    const items=(rechnung.items||[]).filter(it=>it.an&&String(it.productName).trim()).map(it=>({productName:String(it.productName).trim(),amount:parseFloat(String(it.amount).replace(",","."))||0,purchaseDate:it.purchaseDate||null,source:"invoice"}));
    if(!items.length){ setRechnung(null); return; }
    await api.post('customer-purchases',{clientId,items});
    setRechnung(null); load();
  };

  if(laden) return <div style={{color:T.textDim,fontSize:13,padding:"14px 0"}}>Lädt...</div>;

  return (<div>
    {/* Umsatz + Käufe */}
    <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",gap:12,marginBottom:14,flexWrap:"wrap"}}>
      <div><span style={{fontSize:24,fontWeight:700,fontFamily:"'JetBrains Mono',monospace"}}>{rfEuroGenau(summe)}</span> <span style={{fontSize:13,color:T.textDim}}>Umsatz · {kaeufe.length} {kaeufe.length===1?"Kauf":"Käufe"}</span></div>
      <label style={{display:"inline-flex",alignItems:"center",gap:7,padding:"8px 13px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer",color:T.text}}>
        <Icon name="upload" size={14}/> Rechnung hochladen
        <input type="file" accept="application/pdf" onChange={e=>{rechnungLesen(e.target.files[0]); e.target.value="";}} style={{display:"none"}}/>
      </label>
    </div>

    {/* Auslese-Ergebnis der Rechnung */}
    {rechnung&&rechnung.status==="liest"&&<div style={{background:T.infoGlow,border:`1px solid ${T.info}55`,borderRadius:10,padding:"12px 15px",fontSize:13,marginBottom:14}}>Die Rechnung wird ausgelesen, einen Moment...</div>}
    {rechnung&&rechnung.status==="fehler"&&<div style={{background:`${T.danger}14`,border:`1px solid ${T.danger}55`,color:T.danger,borderRadius:10,padding:"12px 15px",fontSize:13,marginBottom:14,lineHeight:1.6}}>{rechnung.msg} <button onClick={()=>setRechnung(null)} style={{background:"none",border:"none",color:T.textMuted,cursor:"pointer",textDecoration:"underline",fontFamily:"inherit",fontSize:13}}>schließen</button></div>}
    {rechnung&&rechnung.status==="pruefen"&&<div style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:10,padding:16,marginBottom:16}}>
      <div style={{fontSize:13.5,fontWeight:600,marginBottom:4}}>Das hat die KI aus der Rechnung gelesen</div>
      <div style={{fontSize:12.5,color:T.textMuted,marginBottom:12,lineHeight:1.5}}>Bitte prüf die Positionen, bevor du sie übernimmst. Du kannst sie noch anpassen oder abwählen.</div>
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {rechnung.items.map((it,i)=>(<div key={i} style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          <button onClick={()=>setRechnung(r=>({...r,items:r.items.map((x,j)=>j===i?{...x,an:!x.an}:x)}))} style={{width:22,height:22,borderRadius:6,border:`2px solid ${it.an?T.accent:T.borderStrong}`,background:it.an?T.accent:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,cursor:"pointer",padding:0}}>{it.an&&<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5"><polyline points="20 6 9 17 4 12"/></svg>}</button>
          <input value={it.productName} onChange={e=>setRechnung(r=>({...r,items:r.items.map((x,j)=>j===i?{...x,productName:e.target.value}:x)}))} placeholder="Position" style={{flex:"1 1 160px",minWidth:0,background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:7,padding:"8px 11px",color:T.text,fontSize:13,outline:"none"}}/>
          <input value={it.amount} onChange={e=>setRechnung(r=>({...r,items:r.items.map((x,j)=>j===i?{...x,amount:e.target.value}:x)}))} placeholder="0" style={{width:90,background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:7,padding:"8px 11px",color:T.text,fontSize:13,outline:"none",fontFamily:"'JetBrains Mono',monospace"}}/>
          <input value={it.purchaseDate} onChange={e=>setRechnung(r=>({...r,items:r.items.map((x,j)=>j===i?{...x,purchaseDate:e.target.value}:x)}))} placeholder="YYYY-MM-DD" style={{width:120,background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:7,padding:"8px 11px",color:T.text,fontSize:13,outline:"none",fontFamily:"'JetBrains Mono',monospace"}}/>
        </div>))}
      </div>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:14}}>
        <Button variant="ghost" onClick={()=>setRechnung(null)}>Verwerfen</Button>
        <Button onClick={rechnungSpeichern}>Positionen übernehmen</Button>
      </div>
    </div>}

    {/* Liste der Käufe */}
    {kaeufe.length>0&&<div style={{display:"flex",flexDirection:"column",marginBottom:14,border:`1px solid ${T.border}`,borderRadius:10,overflow:"hidden"}}>
      {kaeufe.map((k,i)=>(<div key={k.id} style={{display:"flex",alignItems:"center",gap:10,padding:"11px 14px",borderBottom:i<kaeufe.length-1?`1px solid ${T.border}`:"none",background:T.bgCard}}>
        <div style={{flex:1,minWidth:0}}><div style={{fontSize:13.5,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{k.productName}</div>{k.purchaseDate&&<div style={{fontSize:12,color:T.textDim}}>{k.purchaseDate}{k.source==="invoice"?" · aus Rechnung":""}</div>}</div>
        <div style={{fontSize:13.5,fontWeight:600,fontFamily:"'JetBrains Mono',monospace",whiteSpace:"nowrap"}}>{rfEuroGenau(k.amount)}</div>
        <button onClick={()=>delKauf(k.id)} title="Entfernen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4,display:"flex",flexShrink:0}}><Icon name="delete" size={15}/></button>
      </div>))}
    </div>}

    {/* Manuell hinzufügen */}
    <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"flex-end",marginBottom:18}}>
      <div style={{flex:"1 1 180px"}}><Input label="Kauf hinzufügen" value={neu.productName} onChange={e=>setNeu({...neu,productName:e.target.value})} placeholder="Produkt / Leistung"/></div>
      <div style={{width:100}}><Input label="Betrag" value={neu.amount} onChange={e=>setNeu({...neu,amount:e.target.value})} placeholder="€"/></div>
      <div style={{width:140}}><Input label="Datum" value={neu.purchaseDate} onChange={e=>setNeu({...neu,purchaseDate:e.target.value})} placeholder="YYYY-MM-DD"/></div>
      <Button onClick={addManuell} disabled={!neu.productName.trim()}>Speichern</Button>
    </div>

    {/* Upsell-Vorschläge */}
    <div style={{borderTop:`1px solid ${T.border}`,paddingTop:16}}>
      <div style={{fontSize:13.5,fontWeight:600,marginBottom:4}}>Upsell-Möglichkeiten</div>
      <div style={{fontSize:12.5,color:T.textMuted,marginBottom:12,lineHeight:1.5}}>Das hat der Kunde aus eurem Angebot noch nicht. Ein Klick trägt es oben als Kauf ein.</div>
      {produkte.length===0
        ? <div style={{fontSize:13,color:T.textDim}}>Noch keine Produkte im Katalog. Leg sie über „Produkte“ oben an.</div>
        : upsells.length===0
          ? <div style={{fontSize:13,color:T.success}}>Dieser Kunde hat schon alles aus eurem Angebot.</div>
          : <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {upsells.map(p=>(<button key={p.id} onClick={()=>upsellAlsKauf(p.name)} style={{display:"flex",alignItems:"center",gap:7,padding:"8px 13px",borderRadius:20,border:`1px solid ${T.accent}`,background:T.accentGlow,color:T.accent,cursor:"pointer",fontSize:12.5,fontWeight:600,fontFamily:"inherit"}}>
                <Icon name="add" size={13}/> {p.name}{p.price!=null?` · ${rfEuro(p.price)}`:""}</button>))}
            </div>}
    </div>
  </div>);
};

const ClientsPage = ({user, reload, dv}) => {
  const [clients,setClients] = useState([]);
  const [loading,setLoading] = useState(true);
  const [showAdd,setShowAdd] = useState(false);
  const [editClient,setEditClient] = useState(null);
  const [confirmDel,setConfirmDel] = useState(null);
  const [pipeline,setPipeline] = useState(null);
  const [showCatalog,setShowCatalog] = useState(false);
  const admins = (store.get(STORAGE_KEYS.users)||[]).filter(u=>u.role==="admin"||u.role==="member");

  const emptyForm = {name:"",username:"",password:"",company:"",contactPerson:"",phone:"",email:"",meetLink:"",services:[],assignedAdmins:[],notes:"",logo:"",portalAccess:true};
  const [form,setForm] = useState(emptyForm);

  const loadClients = () => {
    api.get("clients").then(c=>{ setClients(Array.isArray(c)?c:[]); setLoading(false); }).catch(()=>setLoading(false));
    api.get("customer-pipeline").then(p=>{ if(p&&!p.error) setPipeline(p); });
  };
  useEffect(()=>{ loadClients(); }, [dv]);

  const toggleService = (obj,setObj,sid) => {
    const has = obj.services.includes(sid);
    setObj({...obj, services: has ? obj.services.filter(x=>x!==sid) : [...obj.services, sid]});
  };
  const toggleAdmin = (obj,setObj,aid) => {
    const has = obj.assignedAdmins.includes(aid);
    setObj({...obj, assignedAdmins: has ? obj.assignedAdmins.filter(x=>x!==aid) : [...obj.assignedAdmins, aid]});
  };

  const addClient = async () => {
    if(!form.name.trim()){ alert("Bitte einen Anzeigenamen eingeben."); return; }
    if(form.portalAccess && (!form.username.trim()||!form.password.trim())){ alert("Für den Mitgliederbereich sind Benutzername und Passwort nötig."); return; }
    const res = await api.post("clients", form);
    if(res && res.error){ alert(res.error); return; }
    setForm(emptyForm); setShowAdd(false); loadClients();
  };
  const saveEdit = async () => {
    if(!editClient) return;
    await api.put("clients", editClient);
    setEditClient(null); loadClients();
  };
  const delClient = async () => {
    if(!confirmDel) return;
    await api.del("clients", {id:confirmDel.id});
    setConfirmDel(null); loadClients();
  };

  // Logo hochladen: skaliert auf max 400px Breite/Höhe, behält Seitenverhältnis
  const handleLogoUpload = (file, obj, setObj) => {
    if(!file) return;
    if(file.size > 5000000){ alert("Bild zu groß (max 5MB)"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const imgEl = new window.Image();
      imgEl.onload = () => {
        const maxDim = 400;
        let w = imgEl.width, h = imgEl.height;
        if(w>h && w>maxDim){ h = h*maxDim/w; w = maxDim; }
        else if(h>=w && h>maxDim){ w = w*maxDim/h; h = maxDim; }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(imgEl, 0, 0, w, h);
        const dataUrl = canvas.toDataURL("image/png");
        setObj({...obj, logo: dataUrl});
      };
      imgEl.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Wiederverwendbares Formular-Innenleben (als Funktion, nicht als Komponente,
  // damit die Input-Felder beim Tippen nicht den Fokus verlieren)
  const renderFormFields = (obj,setObj,isNew) => (<div style={{display:"flex",flexDirection:"column",gap:14}}>
    {/* Logo */}
    <div style={{display:"flex",alignItems:"center",gap:16}}>
      <div style={{width:72,height:72,borderRadius:12,border:`1px solid ${T.border}`,background:T.bgInput,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",flexShrink:0}}>
        {obj.logo
          ? <img src={obj.logo} alt="Logo" style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}}/>
          : <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={T.textDim} strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>}
      </div>
      <div>
        <div style={{fontSize:13,fontWeight:600,marginBottom:6}}>Kundenlogo</div>
        <div style={{display:"flex",gap:8}}>
          <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"7px 13px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer",color:T.text}}>
            <Icon name="upload" size={14}/> {obj.logo?"Ändern":"Hochladen"}
            <input type="file" accept="image/*" onChange={e=>handleLogoUpload(e.target.files[0],obj,setObj)} style={{display:"none"}}/>
          </label>
          {obj.logo && <button type="button" onClick={()=>setObj({...obj,logo:""})} style={{padding:"7px 13px",background:"transparent",border:`1px solid ${T.border}`,borderRadius:8,fontSize:13,color:T.textMuted,cursor:"pointer"}}>Entfernen</button>}
        </div>
      </div>
    </div>
    <div className="grid-2col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
      <Input label="Firma" value={obj.company||""} onChange={e=>setObj({...obj,company:e.target.value})} placeholder="z.B. Fleck Immobilien"/>
      <Input label="Anzeigename" value={obj.name||""} onChange={e=>setObj({...obj,name:e.target.value})} placeholder="z.B. Franz Fleck"/>
    </div>
    <div className="grid-2col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
      <Input label="Ansprechpartner (Kunde)" value={obj.contactPerson||""} onChange={e=>setObj({...obj,contactPerson:e.target.value})} placeholder="Name beim Kunden"/>
      <Input label="Telefon (Kunde)" value={obj.phone||""} onChange={e=>setObj({...obj,phone:e.target.value})} placeholder="+49 ..."/>
    </div>
    <div className="grid-2col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
      <Input label="E-Mail (Kunde)" value={obj.email||""} onChange={e=>setObj({...obj,email:e.target.value})} placeholder="kunde@..."/>
      <Input label="Google-Meet-Link" value={obj.meetLink||""} onChange={e=>setObj({...obj,meetLink:e.target.value})} placeholder="https://meet.google.com/..."/>
    </div>
    {isNew && <div style={{padding:14,background:T.bgInput,borderRadius:10,border:`1px solid ${T.border}`}}>
      <button type="button" onClick={()=>setObj({...obj,portalAccess:!obj.portalAccess})} style={{display:"flex",alignItems:"center",gap:12,width:"100%",background:"none",border:"none",cursor:"pointer",textAlign:"left",padding:0}}>
        <div style={{width:44,height:26,borderRadius:13,flexShrink:0,background:obj.portalAccess?T.accent:T.borderStrong,position:"relative",transition:"background .2s"}}>
          <div style={{position:"absolute",top:3,left:obj.portalAccess?21:3,width:20,height:20,borderRadius:"50%",background:"#fff",transition:"left .2s"}}/>
        </div>
        <div>
          <div style={{fontSize:13.5,fontWeight:600,color:T.text}}>Mitgliederbereich (Login fürs Kundenportal)</div>
          <div style={{fontSize:12,color:T.textMuted,marginTop:2}}>{obj.portalAccess?"Der Kunde bekommt einen Zugang und sieht sein Portal.":"Kunde wird nur intern geführt, ohne eigenen Login."}</div>
        </div>
      </button>
      {obj.portalAccess && <div className="grid-2col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginTop:14}}>
        <Input label="Benutzername (Login)" value={obj.username||""} onChange={e=>setObj({...obj,username:e.target.value})} placeholder="z.B. fleck"/>
        <Input label="Passwort" value={obj.password||""} onChange={e=>setObj({...obj,password:e.target.value})} placeholder="Startpasswort"/>
      </div>}
    </div>}

    {/* Dienstleistungen ankreuzen */}
    <div>
      <div style={{fontSize:13,fontWeight:600,color:T.textMuted,marginBottom:8}}>Dienstleistungen (schaltet Bereiche im Kundenportal frei)</div>
      <div className="grid-2col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        {CLIENT_SERVICES.map(s=>{
          const on = obj.services.includes(s.id);
          return (<button key={s.id} type="button" onClick={()=>toggleService(obj,setObj,s.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"11px 13px",borderRadius:10,border:`1px solid ${on?T.accent:T.border}`,background:on?T.accentGlow:T.bgInput,cursor:"pointer",textAlign:"left"}}>
            <div style={{width:20,height:20,borderRadius:6,border:`2px solid ${on?T.accent:T.borderStrong}`,background:on?T.accent:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              {on&&<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
            </div>
            <span style={{fontSize:13,fontWeight:600,color:on?T.accent:T.text}}>{s.label}</span>
          </button>);
        })}
      </div>
    </div>

    {/* Ansprechpartner zuweisen */}
    <div>
      <div style={{fontSize:13,fontWeight:600,color:T.textMuted,marginBottom:8}}>Eure Ansprechpartner (erscheinen beim Kunden)</div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        {admins.map(a=>{
          const on = obj.assignedAdmins.includes(a.id);
          return (<button key={a.id} type="button" onClick={()=>toggleAdmin(obj,setObj,a.id)} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderRadius:9,border:`1px solid ${on?T.info:T.border}`,background:on?T.infoGlow:T.bgInput,cursor:"pointer"}}>
            <Avatar userId={a.id} name={a.name} size={24} editable={false}/>
            <span style={{fontSize:13,fontWeight:600,color:on?T.info:T.text}}>{a.name}</span>
          </button>);
        })}
      </div>
    </div>

    <Input label="Interne Notiz (nur für euch)" value={obj.notes||""} onChange={e=>setObj({...obj,notes:e.target.value})} placeholder="Optional"/>
  </div>);

  return (<div>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,flexWrap:"wrap",gap:12}}>
      <h1 style={{fontSize:26,fontWeight:700,letterSpacing:"-0.02em"}}>Kunden</h1>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        <Button variant="secondary" icon="list" onClick={()=>setShowCatalog(true)}>Produkte</Button>
        <Button icon="add" onClick={()=>{setForm(emptyForm);setShowAdd(true);}}>Kunde anlegen</Button>
      </div>
    </div>
    <p style={{color:T.textMuted,fontSize:14,marginBottom:22}}>Lege Kundenzugänge an, verfolge Käufe und Umsatz und steuere, welche Bereiche im Kundenportal freigeschaltet sind.</p>

    {/* Pipeline-Kennzahlen */}
    {pipeline&&<div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:24}}>
      {[["Kunden",String(pipeline.kundenAnzahl),T.text],["Gesamtumsatz",rfEuro(pipeline.gesamtUmsatz),T.success],["Schnitt je Kunde",rfEuro(pipeline.schnitt),T.text],["Produkte im Angebot",String(pipeline.produktAnzahl),T.text]].map(([label,wert,farbe])=>(
        <div key={label} style={{flex:"1 1 125px",minWidth:0,background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:12,padding:"14px 16px",boxShadow:T.glassHi}}>
          <div style={{fontSize:10.5,color:T.textDim,fontWeight:700,letterSpacing:"0.01em",textTransform:"uppercase",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{label}</div>
          <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:22,fontWeight:700,marginTop:6,color:farbe}}>{wert}</div>
        </div>))}
    </div>}

    <ProductCatalogModal open={showCatalog} onClose={()=>setShowCatalog(false)} onChange={loadClients}/>

    {/* Anlegen-Modal */}
    <Modal open={showAdd} onClose={()=>setShowAdd(false)} title="Neuen Kunden anlegen" width={640}>
      {renderFormFields(form, setForm, true)}
      <Button icon="check" onClick={addClient} style={{marginTop:16}}>Kunde anlegen</Button>
    </Modal>

    {/* Bearbeiten-Modal */}
    <Modal open={!!editClient} onClose={()=>setEditClient(null)} title="Kunde bearbeiten" width={640}>
      {editClient && <>{renderFormFields(editClient, setEditClient, false)}
        <Button icon="check" onClick={saveEdit} style={{marginTop:16}}>Speichern</Button>
        <div style={{marginTop:24,paddingTop:20,borderTop:`1px solid ${T.border}`}}>
          <div style={{fontSize:15,fontWeight:600,marginBottom:4}}>Zugangsdaten</div>
          <div style={{fontSize:13,color:T.textMuted,marginBottom:14}}>Diese Zugänge sieht der Kunde in seinem Portal. Ihr und der Kunde könnt sie pflegen.</div>
          <CredentialsManager clientId={editClient.id} canEdit={true}/>
        </div>
        <div style={{marginTop:24,paddingTop:20,borderTop:`1px solid ${T.border}`}}>
          <div style={{fontSize:15,fontWeight:600,marginBottom:4}}>Käufe & Upsells</div>
          <div style={{fontSize:13,color:T.textMuted,marginBottom:14}}>Was dieser Kunde gekauft hat, wie viel Umsatz er bringt und was ihr ihm noch verkaufen könnt.</div>
          <CustomerPurchases clientId={editClient.id}/>
        </div>
        {/* Dienstleistungs-Inhalte je freigeschalteter Dienstleistung */}
        {(editClient.services||[]).map(sid=>{
          const sv = serviceById(sid);
          if(!sv || !SERVICE_SCHEMAS[sid]) return null;
          return (<div key={sid} style={{marginTop:24,paddingTop:20,borderTop:`1px solid ${T.border}`}}>
            <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:4}}>
              <span style={{width:30,height:30,borderRadius:8,background:T.accentGlow,display:"flex",alignItems:"center",justifyContent:"center",color:T.accent}}><Icon name={sv.icon} size={16}/></span>
              <div style={{fontSize:15,fontWeight:600}}>{sv.label}</div>
            </div>
            <div style={{fontSize:13,color:T.textMuted,marginBottom:14}}>{sv.desc}. Inhalte erscheinen direkt im Kundenportal.</div>
            <ServiceModule clientId={editClient.id} service={sid} canEdit={true} createdBy="admin"/>
          </div>);
        })}
        {/* Landingpage-Leads + Einbau-Schlüssel (wenn Meta Ads aktiv) */}
        {(editClient.services||[]).includes("meta_ads") && <div style={{marginTop:24,paddingTop:20,borderTop:`1px solid ${T.border}`}}>
          <div style={{fontSize:15,fontWeight:600,marginBottom:4}}>Landingpage-Leads</div>
          <div style={{fontSize:13,color:T.textMuted,marginBottom:14}}>Anfragen, die über die Landingpage reinkommen. Ihr und der Kunde könnt die Tabelle bearbeiten.</div>
          <LeadKeyManager clientId={editClient.id}/>
          <div style={{marginTop:16}}>
            <LandingpageLeadsTable clientId={editClient.id} canEdit={true}/>
          </div>
        </div>}
        {/* Dreh-Ideen (wenn Social Media aktiv) */}
        {(editClient.services||[]).includes("social_media") && <div style={{marginTop:24,paddingTop:20,borderTop:`1px solid ${T.border}`}}>
          <ShootIdeasModule clientId={editClient.id} canEdit={true} createdBy="admin"/>
        </div>}
        </>}
    </Modal>

    {/* Löschen-Bestätigung */}
    <Modal open={!!confirmDel} onClose={()=>setConfirmDel(null)} title="Kunde wirklich löschen?" width={440}>
      {confirmDel && <div>
        <p style={{fontSize:14,color:T.textMuted,lineHeight:1.6,marginBottom:18}}>Der Zugang von {confirmDel.company||confirmDel.name} und alle zugehörigen Profildaten werden gelöscht. Das lässt sich nicht rückgängig machen.</p>
        <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
          <Button variant="secondary" onClick={()=>setConfirmDel(null)}>Abbrechen</Button>
          <Button variant="danger" icon="delete" onClick={delClient}>Löschen</Button>
        </div>
      </div>}
    </Modal>

    {loading
      ? <div style={{textAlign:"center",padding:"60px 0",color:T.textDim}}>Lädt...</div>
      : clients.length===0
        ? <Empty icon="team" title="Noch keine Kunden" desc="Lege deinen ersten Kundenzugang an."/>
        : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(min(100%, 280px),1fr))",gap:16}}>
            {clients.map(c=>{
              const pk = pipeline&&pipeline.kunden ? pipeline.kunden.find(x=>x.id===c.id) : null;
              return (
              <div key={c.id} style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,boxShadow:T.shadow,padding:22}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
                  <div style={{display:"flex",alignItems:"center",gap:12,minWidth:0}}>
                    {c.logo && <div style={{width:46,height:46,borderRadius:10,border:`1px solid ${T.border}`,background:"#fff",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",flexShrink:0}}>
                      <img src={c.logo} alt="" style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}}/>
                    </div>}
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:16,fontWeight:600,letterSpacing:"-0.01em",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.company||c.name}</div>
                      <div style={{fontSize:12,color:T.textDim}}>{c.portalAccess?`@${c.username}`:"ohne Login"}{c.contactPerson?` · ${c.contactPerson}`:""}</div>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:6,flexShrink:0}}>
                    <button onClick={()=>setEditClient({id:c.id,name:c.name,company:c.company||"",contactPerson:c.contactPerson||"",phone:c.phone||"",email:c.email||"",meetLink:c.meetLink||"",services:c.services||[],assignedAdmins:c.assignedAdmins||[],notes:c.notes||"",logo:c.logo||"",portalAccess:c.portalAccess})} title="Bearbeiten" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4}}><Icon name="edit" size={16}/></button>
                    <button onClick={()=>setConfirmDel(c)} title="Löschen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4}}><Icon name="delete" size={16}/></button>
                  </div>
                </div>
                {/* Umsatz aus der Pipeline */}
                {pk&&pk.umsatz>0&&<div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:12}}>
                  <span style={{fontSize:18,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:T.success}}>{rfEuroGenau(pk.umsatz)}</span>
                  <span style={{fontSize:12,color:T.textDim}}>{pk.anzahlKaeufe} {pk.anzahlKaeufe===1?"Kauf":"Käufe"}{pk.upsells&&pk.upsells.length?` · ${pk.upsells.length} Upsell${pk.upsells.length===1?"":"s"} offen`:""}</span>
                </div>}
                {/* freigeschaltete Dienstleistungen */}
                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:12}}>
                  {(c.services||[]).length===0
                    ? <span style={{fontSize:12,color:T.textDim}}>Keine Dienstleistung gewählt</span>
                    : (c.services||[]).map(sid=>{const s=serviceById(sid);return s?<Badge key={sid} color={T.accent}>{s.label}</Badge>:null;})}
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:5,fontSize:12,color:T.textMuted,borderTop:`1px solid ${T.border}`,paddingTop:12}}>
                  {c.phone&&<div style={{display:"flex",alignItems:"center",gap:6}}><Icon name="phone" size={13}/> {c.phone}</div>}
                  {c.meetLink&&<div style={{display:"flex",alignItems:"center",gap:6}}><Icon name="calendar" size={13}/> Meet-Link hinterlegt</div>}
                  {(c.assignedAdmins||[]).length>0&&<div style={{display:"flex",alignItems:"center",gap:6}}><Icon name="team" size={13}/> {(c.assignedAdmins||[]).length} Ansprechpartner</div>}
                </div>
              </div>);
            })}
          </div>}
  </div>);
};

class ErrorBoundary extends React.Component {
  constructor(props){ super(props); this.state = {hasError:false, error:null}; }
  static getDerivedStateFromError(error){ return {hasError:true, error}; }
  componentDidCatch(error, info){ /* React Error Boundary */ }
  render(){
    if(this.state.hasError){
      return (<div style={{padding:30,maxWidth:700,margin:"40px auto",background:"#1a1a28",color:"#e8e8ef",borderRadius:14,border:"2px solid #ef4444",fontFamily:"DM Sans, sans-serif"}}>
        <h2 style={{fontSize:18,fontWeight:700,color:"#ef4444",marginBottom:12}}>⚠ Etwas ist schiefgelaufen</h2>
        <p style={{fontSize:13,marginBottom:14,lineHeight:1.5}}>Ein Fehler ist aufgetreten beim Anzeigen dieser Seite. Versuche die Seite neu zu laden — wenn der Fehler bleibt, kontaktiere den Support.</p>
        <pre style={{fontSize:11,background:"#0a0a0f",padding:12,borderRadius:8,overflow:"auto",maxHeight:200,fontFamily:"JetBrains Mono, monospace"}}>{String(this.state.error?.stack || this.state.error?.message || this.state.error)}</pre>
        <div style={{display:"flex",gap:8,marginTop:14}}>
          <button onClick={()=>{this.setState({hasError:false,error:null});}} style={{background:"#2a2a3a",color:"#e8e8ef",border:"none",borderRadius:8,padding:"8px 14px",cursor:"pointer",fontSize:13}}>Zurücksetzen</button>
          <button onClick={()=>window.location.reload()} style={{background:"#ef4444",color:"#fff",border:"none",borderRadius:8,padding:"8px 14px",cursor:"pointer",fontSize:13,fontWeight:600}}>Seite neu laden</button>
        </div>
      </div>);
    }
    return this.props.children;
  }
}

// ═══════════════════════════════════════════════════════════
// E-MAIL-KAMPAGNEN (Etappe 1: Pipeline, Vorlagen, Postfächer)
// ═══════════════════════════════════════════════════════════
// ─── Einfacher Rich-Text-Editor (dependency-frei, contentEditable) ───
// ─── Meldung bei neuer Antwort (Pop-up oben + kurzer Ton) ───
const RF_MAILTON_KEY = "crm_mailton";
const rfTonAn = () => { try { return localStorage.getItem(RF_MAILTON_KEY) !== "0"; } catch(e){ return true; } };
const rfTonSetzen = (an) => { try { localStorage.setItem(RF_MAILTON_KEY, an?"1":"0"); } catch(e){} };
// Kurzer, dezenter Ton, ohne Audiodatei
const rfPling = () => {
  if(!rfTonAn()) return;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if(!AC) return;
    const ctx = new AC();
    const spiel = (freq, start, dauer) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = "sine"; o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
      g.gain.exponentialRampToValueAtTime(0.16, ctx.currentTime + start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dauer);
      o.connect(g); g.connect(ctx.destination);
      o.start(ctx.currentTime + start); o.stop(ctx.currentTime + start + dauer + 0.02);
    };
    spiel(880, 0, 0.12);
    spiel(1170, 0.11, 0.16);
    setTimeout(()=>{ try{ctx.close();}catch(e){} }, 600);
  } catch(e){}
};

// Kurzer, freundlicher Ton fuers Einstempeln, klingt anders als der Mail-Ton
const rfPlingTeam = () => {
  if(!rfTonAn()) return;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if(!AC) return;
    const ctx = new AC();
    const spiel = (freq, start, dauer) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = "triangle"; o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
      g.gain.exponentialRampToValueAtTime(0.13, ctx.currentTime + start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dauer);
      o.connect(g); g.connect(ctx.destination);
      o.start(ctx.currentTime + start); o.stop(ctx.currentTime + start + dauer + 0.02);
    };
    spiel(587, 0, 0.10);
    spiel(784, 0.09, 0.14);
    setTimeout(()=>{ try{ctx.close();}catch(e){} }, 600);
  } catch(e){}
};

const MailToasts = ({items, onOpen, onClose}) => {
  if(!items.length) return null;
  return (<div style={{position:"fixed",top:"calc(14px + env(safe-area-inset-top))",left:"50%",transform:"translateX(-50%)",zIndex:2000,display:"flex",flexDirection:"column",gap:8,width:"min(420px, calc(100vw - 24px))"}}>
    {items.map(t=>(
      <div key={t.id} onClick={()=>onOpen(t)} style={{display:"flex",alignItems:"center",gap:12,background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:13,padding:"12px 14px",boxShadow:`${T.glassHi}, ${T.shadowLg}`,cursor:t.art==="team"?"default":"pointer",animation:"rf-toast-in .22s ease-out"}}>
        <div style={{width:32,height:32,borderRadius:9,flexShrink:0,background:t.art==="team"?`${T.success}22`:T.accentGlow,color:t.art==="team"?T.success:T.accent,display:"flex",alignItems:"center",justifyContent:"center"}}><Icon name={t.art==="team"?"team":"mail"} size={17}/></div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:13.5,fontWeight:700,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.von}</div>
          <div style={{fontSize:12.5,color:T.textDim,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.betreff||"(kein Betreff)"}</div>
        </div>
        <button onClick={e=>{e.stopPropagation();onClose(t.id);}} aria-label="Ausblenden" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4,display:"flex",flexShrink:0}}><Icon name="close" size={15}/></button>
      </div>
    ))}
  </div>);
};


const RichEditor = ({value, onChange, docKey, editorId, minHeight=200, placeholder=""}) => {
  const ref = useRef(null);
  useEffect(()=>{
    if(!ref.current) return;
    let html = value || "";
    if(html.indexOf('<')===-1){ html = html.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/\n/g,'<br>'); }
    if(ref.current.innerHTML !== html) ref.current.innerHTML = html;
    // eslint-disable-next-line
  },[docKey]);
  const fire=()=>{ if(ref.current) onChange(ref.current.innerHTML); };
  const exec=(cmd,arg=null)=>{ if(ref.current) ref.current.focus(); document.execCommand(cmd,false,arg); fire(); };
  const addLink=()=>{ const url=window.prompt("Link-Adresse (mit https://)",""); if(url){ exec('createLink',url); } };
  const Tb=({onClick,title,children,w})=>(<button type="button" title={title} onMouseDown={e=>e.preventDefault()} onClick={onClick} style={{minWidth:w||30,height:28,padding:"0 8px",background:T.bgCard2,border:`1px solid ${T.border}`,borderRadius:6,color:T.text,cursor:"pointer",fontSize:13,display:"inline-flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit"}}>{children}</button>);
  return (<div style={{border:`1px solid ${T.border}`,borderRadius:8,overflow:"hidden",background:T.bgInput}}>
    <div style={{display:"flex",gap:5,padding:"6px 8px",borderBottom:`1px solid ${T.border}`,background:T.bgCard2,flexWrap:"wrap"}}>
      <Tb title="Fett" onClick={()=>exec('bold')}><b>B</b></Tb>
      <Tb title="Kursiv" onClick={()=>exec('italic')}><i>I</i></Tb>
      <Tb title="Unterstrichen" onClick={()=>exec('underline')}><span style={{textDecoration:"underline"}}>U</span></Tb>
      <Tb title="Aufzählung" onClick={()=>exec('insertUnorderedList')} w={52}>• Liste</Tb>
      <Tb title="Link einfügen" onClick={addLink} w={44}>Link</Tb>
      <Tb title="Formatierung entfernen" onClick={()=>exec('removeFormat')}>×</Tb>
    </div>
    <div ref={ref} id={editorId} className="rf-editor" contentEditable suppressContentEditableWarning data-ph={placeholder} onInput={fire} onBlur={fire}
      style={{minHeight,padding:"12px 14px",color:T.text,fontSize:14,lineHeight:1.65,outline:"none",overflowY:"auto",maxHeight:480}}/>
  </div>);
};
// Text an der Cursorposition eines Editors einfügen
const rfInsertIntoEditor = (editorId, text, onChange) => {
  const el = document.getElementById(editorId);
  if(!el) return;
  el.focus();
  document.execCommand('insertText', false, text);
  onChange(el.innerHTML);
};


const CAMPAIGN_STATUS = {
  draft:  {label:"Entwurf",  color:T.textDim},
  active: {label:"Aktiv",    color:T.accent},
  paused: {label:"Pausiert", color:T.textMuted},
};
const ENROLL_STATUS = {
  active:      {label:"Läuft",        color:T.accent+"bb"},
  replied:     {label:"Geantwortet",  color:T.accent},
  appointment: {label:"Termin",       color:T.accent},
  customer:    {label:"Kunde",        color:T.success},
  lost:        {label:"Verloren",     color:T.danger},
  paused:      {label:"Pausiert",     color:T.textMuted},
  completed:   {label:"Abgeschlossen",color:T.textDim},
  stopped:     {label:"Gestoppt",     color:T.textMuted},
  bounced:     {label:"Unzustellbar", color:T.danger},
};
// Was man selbst setzen darf. Geantwortet, Abgeschlossen und Unzustellbar
// setzt das System selbst, die stehen deshalb nicht zur Auswahl.
const ENROLL_WAEHLBAR = ["active","appointment","customer","lost","paused","stopped"];

const EmailAccountsModal = ({open,onClose,accounts,onReload,users}) => {
  const blank={email:"",fromName:"",imapHost:"w01f0e6b.kasserver.com",imapPort:993,smtpHost:"w01f0e6b.kasserver.com",smtpPort:465,username:"",password:"",userId:"",active:1,visibility:"shared"};
  const [form,setForm]=useState(blank);
  const [editId,setEditId]=useState(null);
  const [saving,setSaving]=useState(false);
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const startEdit=(a)=>{ setEditId(a.id); setForm({email:a.email||"",fromName:a.fromName||"",imapHost:a.imapHost||"",imapPort:a.imapPort||993,smtpHost:a.smtpHost||"",smtpPort:a.smtpPort||465,username:a.username||"",password:"",userId:a.userId||"",active:a.active??1,visibility:a.visibility||"shared"}); };
  const reset=()=>{ setEditId(null); setForm(blank); };
  const [fehler,setFehler]=useState("");
  const save=async()=>{
    if(!form.email){return;}
    setSaving(true); setFehler("");
    const payload={...form, username:form.username||form.email};
    const r = editId ? await api.put('email-accounts',{id:editId,...payload}) : await api.post('email-accounts',payload);
    setSaving(false);
    if(r&&r.error){ setFehler(r.error); return; }
    reset(); onReload();
  };
  const del=async(id)=>{ if(!window.confirm("Postfach wirklich entfernen?"))return; const r=await api.del('email-accounts',{id}); if(r&&r.error){ setFehler(r.error); return; } onReload(); };
  return (<Modal open={open} onClose={onClose} title="Postfächer" width={720}>
    <p style={{fontSize:13,color:T.textMuted,marginTop:-8,marginBottom:16,lineHeight:1.6}}>Hier hinterlegst du die Absender-Postfächer. Das Passwort wird verschlüsselt gespeichert und nirgends im Klartext angezeigt. Nutze die Adressen auf rf-media-email.de, nicht die Hauptdomain.</p>
    {accounts.length>0&&<div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:20}}>
      {accounts.map(a=>(<div key={a.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap",padding:"12px 14px",background:T.bgCard2,border:`1px solid ${T.border}`,borderRadius:10}}>
        <div style={{minWidth:0,flex:"1 1 200px"}}><div style={{fontSize:14,fontWeight:600,wordBreak:"break-word",display:"flex",alignItems:"center",gap:7,flexWrap:"wrap"}}>{a.fromName?`${a.fromName} · `:""}{a.email}{a.visibility==="private"&&<span style={{fontSize:10.5,fontWeight:700,color:T.textMuted,background:T.bgCard2,border:`1px solid ${T.border}`,borderRadius:5,padding:"1px 6px"}}>NUR ICH</span>}</div><div style={{fontSize:12,color:T.textDim,wordBreak:"break-word"}}>IMAP {a.imapHost}:{a.imapPort} · SMTP {a.smtpHost}:{a.smtpPort} · {a.hasPassword?"Passwort gesetzt":"kein Passwort"}</div></div>
        <div style={{display:"flex",gap:8,flexShrink:0,marginLeft:"auto"}}><Button variant="ghost" size="sm" onClick={()=>startEdit(a)}>Bearbeiten</Button><Button variant="ghost" size="sm" onClick={()=>del(a.id)}><span style={{color:T.danger}}>Löschen</span></Button></div>
      </div>))}
    </div>}
    <div style={{background:T.bgCard2,border:`1px solid ${T.border}`,borderRadius:12,padding:18}}>
      <div style={{fontSize:14,fontWeight:600,marginBottom:14}}>{editId?"Postfach bearbeiten":"Neues Postfach"}</div>
      {fehler&&<div style={{background:`${T.danger}14`,border:`1px solid ${T.danger}55`,color:T.danger,borderRadius:9,padding:"10px 14px",fontSize:13,marginBottom:12}}>{fehler}</div>}
      <div style={{marginBottom:14}}>
        <div style={{fontSize:12,color:T.textMuted,fontWeight:500,marginBottom:6}}>Wer darf dieses Postfach sehen</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {[["shared","Alle Admins"],["private","Nur ich"]].map(([k,label])=>(
            <button key={k} onClick={()=>set('visibility',k)} style={{fontSize:12.5,fontWeight:600,cursor:"pointer",borderRadius:20,padding:"7px 14px",fontFamily:"inherit",border:`1px solid ${form.visibility===k?T.accent:T.border}`,background:form.visibility===k?T.accentGlow:T.bgCard2,color:form.visibility===k?T.accent:T.textMuted}}>{label}</button>
          ))}
        </div>
        <div style={{fontSize:12,color:T.textDim,marginTop:7,lineHeight:1.5}}>{form.visibility==="private"?"Nur du siehst dieses Postfach und seine Mails. Andere Admins nicht.":"Jeder Admin sieht dieses Postfach und die eingehenden Antworten. Passend für gemeinsame Adressen wie die fürs E-Mail-Marketing."}</div>
      </div>
      <div className="grid-2col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
        <Input label="E-Mail-Adresse" value={form.email} onChange={e=>set('email',e.target.value)} placeholder="felix.griska@rf-media-email.de"/>
        <Input label="Absendername" value={form.fromName} onChange={e=>set('fromName',e.target.value)} placeholder="Felix Griska"/>
        <Input label="IMAP-Server" value={form.imapHost} onChange={e=>set('imapHost',e.target.value)}/>
        <Input label="IMAP-Port" type="number" value={form.imapPort} onChange={e=>set('imapPort',e.target.value)}/>
        <Input label="SMTP-Server" value={form.smtpHost} onChange={e=>set('smtpHost',e.target.value)}/>
        <Input label="SMTP-Port" type="number" value={form.smtpPort} onChange={e=>set('smtpPort',e.target.value)}/>
        <Input label="Benutzername" value={form.username} onChange={e=>set('username',e.target.value)} placeholder="wie E-Mail-Adresse"/>
        <Input label={editId?"Passwort (leer = unverändert)":"Passwort"} type="password" value={form.password} onChange={e=>set('password',e.target.value)} placeholder="••••••••"/>
        {users&&users.length>0&&<Select label="Gehört zu" options={[{value:"",label:"— niemandem fest —"},...users.filter(u=>u.role!=="cutter"&&!u.isClient).map(u=>({value:u.id,label:u.name}))]} value={form.userId} onChange={e=>set('userId',e.target.value)}/>}
      </div>
      <div style={{display:"flex",gap:8}}>
        <Button onClick={save} disabled={saving||!form.email}>{saving?"Speichert...":(editId?"Speichern":"Hinzufügen")}</Button>
        {editId&&<Button variant="ghost" onClick={reset}>Abbrechen</Button>}
      </div>
    </div>
  </Modal>);
};

// Kompakter Branchen-Auswähler statt einer riesigen Chip-Wand.
// Zeigt die aktuelle Wahl als Knopf, klappt eine durchsuchbare Liste auf.
const BranchePicker = ({branchen, wahl, onWahl}) => {
  const [offen,setOffen]=useState(false);
  const [suche,setSuche]=useState("");
  const ref=useRef(null);
  useEffect(()=>{
    if(!offen)return;
    const zu=(e)=>{ if(ref.current&&!ref.current.contains(e.target)) setOffen(false); };
    document.addEventListener("mousedown",zu);
    return ()=>document.removeEventListener("mousedown",zu);
  },[offen]);
  const liste=(branchen||[]).filter(b=>b.name.toLowerCase().includes(suche.toLowerCase()));
  const gesamt=(branchen||[]).reduce((s,b)=>s+(b.count||0),0);
  return (<div ref={ref} style={{position:"relative"}}>
    <button onClick={()=>setOffen(o=>!o)} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,width:"100%",background:T.bgInput,border:`1px solid ${wahl?T.accent:T.border}`,borderRadius:9,padding:"11px 14px",cursor:"pointer",fontFamily:"inherit",color:T.text,fontSize:13.5}}>
      <span style={{fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{wahl||"Alle Branchen"}</span>
      <span style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
        <span style={{fontSize:12,color:T.textDim}}>{wahl?(branchen.find(b=>b.name===wahl)||{}).count:gesamt}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textDim} strokeWidth="2.5" style={{transform:offen?"rotate(180deg)":"none",transition:"transform .15s"}}><polyline points="6 9 12 15 18 9"/></svg>
      </span>
    </button>
    {offen&&<div style={{position:"absolute",top:"calc(100% + 6px)",left:0,right:0,zIndex:40,background:T.bgCard,border:`1px solid ${T.borderStrong}`,borderRadius:11,boxShadow:T.shadowLg,padding:8,maxHeight:"46vh",display:"flex",flexDirection:"column"}}>
      <input autoFocus value={suche} onChange={e=>setSuche(e.target.value)} placeholder="Branche suchen..." style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"9px 12px",color:T.text,fontSize:13,outline:"none",marginBottom:8}}/>
      <div style={{overflowY:"auto",overscrollBehavior:"contain",display:"flex",flexDirection:"column",gap:2}}>
        <button onClick={()=>{onWahl("");setOffen(false);setSuche("");}} style={{display:"flex",justifyContent:"space-between",gap:10,padding:"9px 11px",borderRadius:7,border:"none",background:wahl===""?T.accentGlow:"transparent",color:wahl===""?T.accent:T.text,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:wahl===""?600:500,textAlign:"left"}}>
          <span>Alle Branchen</span><span style={{color:T.textDim}}>{gesamt}</span>
        </button>
        {liste.map(b=>(<button key={b.name} onClick={()=>{onWahl(b.name);setOffen(false);setSuche("");}} style={{display:"flex",justifyContent:"space-between",gap:10,padding:"9px 11px",borderRadius:7,border:"none",background:wahl===b.name?T.accentGlow:"transparent",color:wahl===b.name?T.accent:T.text,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:wahl===b.name?600:500,textAlign:"left"}}>
          <span style={{whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{b.name}</span><span style={{color:T.textDim,flexShrink:0}}>{b.count}</span>
        </button>))}
        {liste.length===0&&<div style={{fontSize:12.5,color:T.textDim,padding:"10px 11px"}}>Keine Branche passt zu "{suche}".</div>}
      </div>
    </div>}
  </div>);
};

// Kompakter Branchen-Auswähler statt einer riesigen Chip-Wand.
const EmailScrapeModal = ({open,onClose,branche,onDone}) => {
  const [status,setStatus]=useState(null);
  const [laufend,setLaufend]=useState(false);
  const [stop,setStop]=useState(false);
  const [verarbeitet,setVerarbeitet]=useState(0);
  const [gefunden,setGefunden]=useState(0);
  const [gesamt,setGesamt]=useState(0);
  const [treffer,setTreffer]=useState([]);
  const [fertig,setFertig]=useState(false);
  const [wahl,setWahl]=useState(branche||"");   // hier gewählte Branche fürs Scrapen
  const stopRef=useRef(false);

  const statusLaden=(br)=>{
    setStatus(null);
    api.get('lead-scrape-status',`branche=${encodeURIComponent(br||"")}`).then(r=>setStatus(r&&!r.error?r:{offen:0,ohneWebsite:0,curl:true,branchen:[]}));
  };
  useEffect(()=>{ if(open){ setLaufend(false);setStop(false);setVerarbeitet(0);setGefunden(0);setTreffer([]);setFertig(false);stopRef.current=false;
    setWahl(branche||""); statusLaden(branche||"");
  } },[open,branche]);

  const brancheWaehlen=(b)=>{ setWahl(b); setFertig(false); setTreffer([]); statusLaden(b); };

  const start=async()=>{
    if(!status||!status.offen)return;
    setLaufend(true); setFertig(false); setStop(false); stopRef.current=false;
    setGesamt(status.offen); setVerarbeitet(0); setGefunden(0); setTreffer([]);
    let v=0,g=0,fehlerAmStueck=0;
    while(!stopRef.current){
      let r=null;
      try{ r=await api.post('lead-scrape-email',{branche:wahl||""}); }
      catch(e){ r=null; }
      // Server hat sauf einem Block Zeit gebraucht oder war kurz weg:
      // nicht abbrechen, kurz warten und den nächsten Block nehmen.
      if(!r || r.error){
        fehlerAmStueck++;
        if(r&&r.error){ setStatus(s=>({...(s||{}),hinweis:r.error})); }
        if(fehlerAmStueck>=5) break; // erst nach mehreren Fehlversuchen aufgeben
        await new Promise(res=>setTimeout(res,1200));
        continue;
      }
      fehlerAmStueck=0;
      v+=r.verarbeitet||0; g+=r.gefunden||0;
      setVerarbeitet(v); setGefunden(g);
      if(Array.isArray(r.ergebnisse)) setTreffer(t=>[...r.ergebnisse.filter(x=>x.ok),...t].slice(0,60));
      // Fertig, wenn nichts mehr offen ist. Ein leerer Block (0 verarbeitet)
      // heißt nur, dass gerade nichts geladen wurde, nicht dass Schluss ist.
      if(!r.verbleibend) break;
    }
    setLaufend(false); setFertig(true); onDone&&onDone();
  };
  const stoppen=()=>{ stopRef.current=true; setStop(true); };

  const proz = gesamt? Math.min(100, Math.round((verarbeitet/gesamt)*100)) : 0;

  return (<Modal open={open} onClose={laufend?()=>{}:onClose} title="Fehlende E-Mails ergänzen" width={620}>
    {!status && <div style={{color:T.textDim,fontSize:13,padding:"20px 0",textAlign:"center"}}>Prüfe...</div>}
    {status && status.curl===false && <div style={{background:`${T.danger}14`,border:`1px solid ${T.danger}55`,borderRadius:10,padding:"12px 15px",fontSize:13,lineHeight:1.6}}>Auf dem Server ist cURL nicht aktiv, deshalb lässt sich das automatische Suchen leider nicht ausführen. Da müsste All-Inkl cURL für die PHP-Version freischalten.</div>}
    {status && status.curl!==false && <>
      {/* Branchen-Auswahl, damit gezielt gescraped wird */}
      {!laufend&&!fertig&&status.branchen&&status.branchen.length>0&&<div style={{marginBottom:14}}>
        <div style={{fontSize:12,color:T.textMuted,fontWeight:500,marginBottom:7}}>Welche Branche soll ich durchsuchen</div>
        <BranchePicker branchen={status.branchen} wahl={wahl} onWahl={brancheWaehlen}/>
      </div>}

      <p style={{fontSize:13.5,color:T.textMuted,lineHeight:1.7,marginTop:-2}}>
        {wahl?<>Für die Branche <b style={{color:T.text}}>{wahl}</b> haben </>:<>Es haben </>}
        <b style={{color:T.text}}>{status.offen}</b> Leads eine Website, aber noch keine E-Mail. Ich schaue mir deren Seite und das Impressum an und trage gefundene Adressen automatisch ein.
        {status.ohneWebsite>0&&<> <br/><span style={{color:T.textDim}}>{status.ohneWebsite} weitere Leads haben gar keine Website, die lassen sich so nicht ergänzen.</span></>}
      </p>

      {(laufend||fertig)&&<div style={{margin:"16px 0"}}>
        <div style={{height:8,borderRadius:6,background:T.bgCard2,overflow:"hidden"}}><div style={{height:"100%",width:`${proz}%`,background:T.accent,transition:"width .3s"}}/></div>
        <div style={{display:"flex",justifyContent:"space-between",marginTop:8,fontSize:12.5,color:T.textMuted}}>
          <span>{verarbeitet} von {gesamt} geprüft</span>
          <span style={{color:T.success,fontWeight:600}}>{gefunden} gefunden</span>
        </div>
      </div>}

      {treffer.length>0&&<div style={{maxHeight:"32vh",overflowY:"auto",overscrollBehavior:"contain",display:"flex",flexDirection:"column",gap:6,border:`1px solid ${T.border}`,borderRadius:10,padding:8,marginBottom:14}}>
        {treffer.map((t,i)=>(<div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 10px",background:T.bgCard2,borderRadius:8}}>
          <span style={{color:T.success,flexShrink:0,display:"flex"}}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg></span>
          <div style={{flex:1,minWidth:0}}><div style={{fontSize:13,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.company}</div><div style={{fontSize:12,color:T.textDim,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.email}</div></div>
        </div>))}
      </div>}

      {fertig&&<div style={{background:T.infoGlow,border:`1px solid ${T.info}55`,borderRadius:9,padding:"11px 14px",fontSize:13,marginBottom:14,lineHeight:1.6}}>Fertig. Von {verarbeitet} geprüften Websites konnte ich <b>{gefunden}</b> E-Mail-Adresse{gefunden===1?"":"n"} eintragen.{stop?" (vorzeitig gestoppt)":""}</div>}

      <div style={{display:"flex",justifyContent:"flex-end",gap:8,flexWrap:"wrap"}}>
        {laufend
          ? <Button variant="ghost" onClick={stoppen}>Stoppen</Button>
          : <>
              <Button variant="ghost" onClick={onClose}>{fertig?"Schließen":"Abbrechen"}</Button>
              {!fertig&&<Button onClick={start} disabled={!status.offen}>{status.offen?`Suche starten (${status.offen})`:"Nichts zu ergänzen"}</Button>}
            </>}
      </div>
    </>}
  </Modal>);
};

const EmailEnrollModal = ({open,onClose,campaignId,onDone}) => {
  const [q,setQ]=useState("");
  const [branche,setBranche]=useState("");
  const [branchen,setBranchen]=useState([]);
  const [leads,setLeads]=useState([]);
  const [total,setTotal]=useState(0);
  const [page,setPage]=useState(1);
  const [pages,setPages]=useState(1);
  const [picked,setPicked]=useState({});
  const [loading,setLoading]=useState(false);
  const [saving,setSaving]=useState(false);
  const [meldung,setMeldung]=useState("");
  const [scrapeOffen,setScrapeOffen]=useState(false);

  const laden=async(p=1,suche=q,br=branche)=>{
    setLoading(true);
    const res=await api.get('email-leads-available',`campaignId=${campaignId}&page=${p}&search=${encodeURIComponent(suche)}&branche=${encodeURIComponent(br)}`);
    setLeads(Array.isArray(res.leads)?res.leads:[]);
    setTotal(res.total||0); setPages(res.pages||1); setPage(res.page||p);
    if(Array.isArray(res.branchen)) setBranchen(res.branchen);
    setLoading(false);
  };
  useEffect(()=>{ if(open){ setQ(""); setBranche(""); setPicked({}); setMeldung(""); laden(1,"",""); } },[open,campaignId]);

  const toggle=(l)=>setPicked(p=>{ const n={...p}; if(n[l.id])delete n[l.id]; else n[l.id]=l; return n; });
  const alleAufSeite=()=>{ const alleDa=leads.every(l=>picked[l.id]); setPicked(p=>{ const n={...p}; leads.forEach(l=>{ if(alleDa) delete n[l.id]; else n[l.id]=l; }); return n; }); };
  const add=async()=>{
    const ids=Object.keys(picked); if(!ids.length)return;
    setSaving(true); setMeldung("");
    const r=await api.post('email-enroll',{campaignId,leadIds:ids});
    setSaving(false);
    if(r&&r.error){ setMeldung(r.error); return; }
    setPicked({});
    setMeldung(`${r.enrolled||0} Lead${(r.enrolled||0)===1?"":"s"} aufgenommen.`);
    onDone(); laden(page,q,branche);
  };
  // Direkt: alle auf dieser Seite, ohne vorher anzuhaken
  const seiteHinzu=async()=>{
    if(!leads.length)return;
    setSaving(true); setMeldung("");
    const r=await api.post('email-enroll',{campaignId,leadIds:leads.map(l=>l.id)});
    setSaving(false);
    if(r&&r.error){ setMeldung(r.error); return; }
    setMeldung(`${r.enrolled||0} Lead${(r.enrolled||0)===1?"":"s"} aufgenommen.`);
    onDone(); laden(1,q,branche);
  };
  // Direkt: alle Treffer über alle Seiten hinweg
  const alleHinzu=async()=>{
    if(!window.confirm(`Wirklich alle ${total} Leads${(q||branche)?" aus dieser Auswahl":""} in die Kampagne aufnehmen?`))return;
    setSaving(true); setMeldung("");
    let gesamt=0, runden=0;
    while(runden<40){
      runden++;
      const r=await api.post('email-enroll-all',{campaignId,search:q,branche});
      if(r&&r.error){ setSaving(false); setMeldung(r.error); return; }
      gesamt += (r.enrolled||0);
      setMeldung(`${gesamt} aufgenommen${r.offen?`, noch ${r.offen} offen...`:""}`);
      if(r.fertig || (r.enrolled===0 && r.offen===0)) break;
      // Sicherheitsnetz: wenn eine Runde nichts mehr bringt, nicht endlos drehen
      if(r.enrolled===0) break;
    }
    setSaving(false);
    setMeldung(`Fertig. ${gesamt} Lead${gesamt===1?"":"s"} aufgenommen.`);
    onDone();
    setTimeout(()=>{ laden(1,q,branche); }, 300);
  };
  const count=Object.keys(picked).length;
  const alleAufSeiteGewaehlt = leads.length>0 && leads.every(l=>picked[l.id]);

  return (<Modal open={open} onClose={onClose} title="Leads zur Kampagne hinzufügen" width={680}>
    <p style={{fontSize:13,color:T.textMuted,marginTop:-4,marginBottom:14,lineHeight:1.6}}>Es werden nur Leads mit E-Mail-Adresse gezeigt. Bereits aufgenommene und abgemeldete sind schon rausgefiltert.</p>
    {branchen.length>0&&<div style={{marginBottom:12}}>
      <div style={{fontSize:12,color:T.textDim,marginBottom:6}}>Branche</div>
      <BranchePicker branchen={branchen} wahl={branche} onWahl={(b)=>{setBranche(b);laden(1,q,b);}}/>
    </div>}
    <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
      <input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==="Enter"&&laden(1,q,branche)} placeholder="Nach Firma, Name oder Stadt filtern..." style={{flex:"1 1 200px",minWidth:0,background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 14px",color:T.text,fontSize:14,outline:"none"}}/>
      <Button onClick={()=>laden(1,q,branche)} disabled={loading}>{loading?"Lädt...":"Filtern"}</Button>
      {(q||branche)&&<Button variant="ghost" onClick={()=>{setQ("");setBranche("");laden(1,"","");}}>Zurücksetzen</Button>}
    </div>
    <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:12,padding:"9px 12px",background:T.bgCard2,border:`1px solid ${T.border}`,borderRadius:9}}>
      <Icon name="mail" size={15}/>
      <span style={{fontSize:12.5,color:T.textMuted,flex:1,minWidth:0}}>Fehlen bei vielen Leads noch E-Mail-Adressen? Ich kann sie von deren Webseiten holen.</span>
      <Button size="sm" variant="ghost" onClick={()=>setScrapeOffen(true)}>E-Mails ergänzen</Button>
    </div>
    <EmailScrapeModal open={scrapeOffen} onClose={()=>setScrapeOffen(false)} branche={branche} onDone={()=>laden(page,q,branche)}/>
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap",marginBottom:8}}>
      <div style={{fontSize:12.5,color:T.textDim}}>{total} Lead{total===1?"":"s"} verfügbar{pages>1?` · Seite ${page} von ${pages}`:""}</div>
      {leads.length>0&&<button onClick={alleAufSeite} style={{background:"none",border:"none",color:T.accent,fontSize:12.5,fontWeight:600,cursor:"pointer",padding:0,fontFamily:"inherit"}}>{alleAufSeiteGewaehlt?"Auswahl dieser Seite aufheben":"Alle auf dieser Seite wählen"}</button>}
    </div>
    <div style={{maxHeight:"46vh",overflowY:"auto",overscrollBehavior:"contain",display:"flex",flexDirection:"column",gap:6,border:`1px solid ${T.border}`,borderRadius:10,padding:8}}>
      {loading&&<div style={{color:T.textDim,fontSize:13,padding:"18px 0",textAlign:"center"}}>Lädt...</div>}
      {!loading&&leads.length===0&&<div style={{color:T.textDim,fontSize:13,padding:"18px 0",textAlign:"center"}}>Keine passenden Leads mit E-Mail-Adresse gefunden.</div>}
      {!loading&&leads.map(l=>(<div key={l.id} onClick={()=>toggle(l)} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:9,cursor:"pointer",background:picked[l.id]?T.accentGlow:T.bgCard2,border:`1px solid ${picked[l.id]?T.accent:T.border}`}}>
        <div style={{width:18,height:18,borderRadius:5,border:`2px solid ${picked[l.id]?T.accent:T.borderStrong}`,background:picked[l.id]?T.accent:"transparent",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>{picked[l.id]&&<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>}</div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:13.5,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{l.company||l.contactName||"—"}</div>
          <div style={{fontSize:12,color:T.textDim,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{l.email}{l.city?` · ${l.city}`:""}</div>
        </div>
      </div>))}
    </div>
    {pages>1&&<div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10,marginTop:12}}>
      <Button variant="ghost" size="sm" onClick={()=>laden(page-1,q,branche)} disabled={page<=1||loading}>← Zurück</Button>
      <span style={{fontSize:12.5,color:T.textDim,fontFamily:"'JetBrains Mono',monospace"}}>{page} / {pages}</span>
      <Button variant="ghost" size="sm" onClick={()=>laden(page+1,q,branche)} disabled={page>=pages||loading}>Weiter →</Button>
    </div>}
    {meldung&&<div style={{marginTop:14,background:T.infoGlow,border:`1px solid ${T.info}55`,borderRadius:9,padding:"10px 14px",fontSize:13,lineHeight:1.5}}>{meldung}</div>}
    <div style={{display:"flex",justifyContent:"space-between",gap:8,marginTop:16,flexWrap:"wrap",alignItems:"center"}}>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        {leads.length>0&&<Button variant="secondary" onClick={seiteHinzu} disabled={saving}>Alle {leads.length} auf dieser Seite hinzufügen</Button>}
        {total>leads.length&&<Button variant="secondary" onClick={alleHinzu} disabled={saving}>Alle {total} hinzufügen</Button>}
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginLeft:"auto"}}>
        <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
        <Button onClick={add} disabled={saving||count===0}>{saving?"Fügt hinzu...":`${count} ausgewählte hinzufügen`}</Button>
      </div>
    </div>
  </Modal>);
};

// Wie viele Tage liegt die letzte Mail an diesen Lead zurueck
const rfTageSeit = (s) => {
  if(!s) return null;
  const d = new Date(String(s).replace(' ','T'));
  if(isNaN(d)) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};
const rfSeitText = (tage) => tage===0 ? "heute" : tage===1 ? "gestern" : `vor ${tage} Tagen`;

const EmailReviewQueue = ({campaignId, campaignName, onBack}) => {
  const [drafts,setDrafts]=useState([]);
  const [phase,setPhase]=useState(0);     // 0 = alle Phasen
  const [curId,setCurId]=useState(null);
  const [loading,setLoading]=useState(true);
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState("");
  const load=async()=>{
    setLoading(true);
    const d=await api.get('email-drafts',`campaignId=${campaignId}`);
    const list=Array.isArray(d)?d.slice():[];
    list.sort((a,b)=>((a.stepOrder||1)-(b.stepOrder||1)) || String(a.company||"").localeCompare(String(b.company||"")));
    setDrafts(list); setCurId(null); setLoading(false);
  };
  useEffect(()=>{ load(); },[campaignId]);

  // Wie viele Entwürfe liegen je Phase an
  const phasen=(()=>{
    const m={};
    drafts.forEach(d=>{ const p=d.stepOrder||1; m[p]=(m[p]||0)+1; });
    return Object.keys(m).map(Number).sort((a,b)=>a-b).map(p=>({p,n:m[p]}));
  })();

  const sichtbar = phase ? drafts.filter(d=>(d.stepOrder||1)===phase) : drafts;
  const cur = sichtbar.find(d=>d.id===curId) || sichtbar[0];
  const pos = cur ? sichtbar.findIndex(d=>d.id===cur.id) : -1;

  const setField=(k,v)=>{ if(!cur)return; setDrafts(ds=>ds.map(d=>d.id===cur.id?{...d,[k]:v}:d)); };
  const advance=()=>{
    if(!cur)return;
    const naechster = sichtbar[pos+1] || sichtbar[pos-1] || null;
    const weg=cur.id;
    setDrafts(ds=>ds.filter(d=>d.id!==weg));
    setCurId(naechster?naechster.id:null);
  };
  const send=async()=>{ if(!cur)return; setBusy(true); setErr(""); const res=await api.post('email-draft-send',{id:cur.id,subject:cur.subject,body:cur.body}); setBusy(false); if(res&&res.error){ setErr(res.error); return; } advance(); };
  const skip=async()=>{ if(!cur)return; setBusy(true); await api.post('email-draft-skip',{id:cur.id}); setBusy(false); advance(); };
  const blaettern=(n)=>{ const z=sichtbar[n]; if(z){ setCurId(z.id); setErr(""); } };
  const insertBody=(text)=>rfInsertIntoEditor('rq-editor', text, v=>setField('body',v));
  const insertLine=(text)=>rfInsertIntoEditor('rq-editor', text, v=>setField('body',v));

  if(loading) return <div style={{padding:"60px 0",textAlign:"center",color:T.textDim}}>Lädt Entwürfe...</div>;

  const chip=(aktiv)=>({fontSize:12.5,fontWeight:600,cursor:"pointer",borderRadius:20,padding:"6px 13px",fontFamily:"inherit",whiteSpace:"nowrap",border:`1px solid ${aktiv?T.accent:T.border}`,background:aktiv?T.accentGlow:T.bgCard2,color:aktiv?T.accent:T.textMuted});

  return (<div>
    <div className="page-head" style={{marginBottom:14}}>
      <Button variant="ghost" size="sm" onClick={onBack}>← Zurück</Button>
      <h1 style={{fontSize:20,fontWeight:700,letterSpacing:"-0.02em"}}>Freigabe · {campaignName}</h1>
    </div>

    {drafts.length===0
      ? <div style={{textAlign:"center",padding:"70px 20px",color:T.textDim}}><div style={{fontSize:38,marginBottom:12,color:T.success}}>✓</div><h2 style={{fontSize:19,fontWeight:700,color:T.text,marginBottom:8}}>Nichts zu prüfen</h2><p style={{fontSize:14,lineHeight:1.6,maxWidth:440,margin:"0 auto"}}>Aktuell warten keine Entwürfe. Neue erscheinen hier, sobald weitere Leads oder Follow-up-Phasen fällig sind.</p><div style={{marginTop:18}}><Button variant="ghost" onClick={onBack}>Zurück zur Kampagne</Button></div></div>
      : <>
        {/* Phasenauswahl */}
        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:14}}>
          <span style={{fontSize:12,color:T.textDim,marginRight:2}}>Phase</span>
          <button onClick={()=>{setPhase(0);setCurId(null);}} style={chip(phase===0)}>Alle · {drafts.length}</button>
          {phasen.map(({p,n})=>(
            <button key={p} onClick={()=>{setPhase(p);setCurId(null);}} style={chip(phase===p)}>Phase {p} · {n}</button>
          ))}
        </div>

        {sichtbar.length===0
          ? <div style={{textAlign:"center",padding:"56px 20px",color:T.textDim,background:T.glassBg,border:`1px solid ${T.glassBorder}`,borderRadius:14}}>
              <h2 style={{fontSize:17,fontWeight:700,color:T.text,marginBottom:8}}>In Phase {phase} ist nichts mehr offen</h2>
              <p style={{fontSize:13.5,lineHeight:1.6,maxWidth:420,margin:"0 auto"}}>In den anderen Phasen warten aber noch {drafts.length} Entwürfe.</p>
              <div style={{marginTop:16}}><Button variant="ghost" onClick={()=>{setPhase(0);setCurId(null);}}>Alle Phasen zeigen</Button></div>
            </div>
          : <>
            {/* Blättern innerhalb der gewählten Phase */}
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap",marginBottom:12}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <Button variant="ghost" size="sm" onClick={()=>blaettern(pos-1)} disabled={pos<=0}>← Vorheriger</Button>
                <Button variant="ghost" size="sm" onClick={()=>blaettern(pos+1)} disabled={pos>=sichtbar.length-1}>Nächster →</Button>
              </div>
              <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:13,color:T.textMuted,background:T.bgCard2,border:`1px solid ${T.border}`,borderRadius:20,padding:"6px 14px"}}>
                {pos+1} von {sichtbar.length}{phase?"":` · Phase ${cur.stepOrder||1}`}
              </span>
            </div>

            <div className="grid-2col" style={{display:"grid",gridTemplateColumns:"1fr 300px",gap:16,alignItems:"start"}}>
              <div style={{background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:14,padding:20,boxShadow:T.glassHi}}>
                {err&&<div style={{background:T.dangerGlow||"rgba(239,90,76,.12)",border:`1px solid ${T.danger}55`,color:T.danger,borderRadius:9,padding:"10px 14px",fontSize:13,marginBottom:14}}>{err}</div>}
                {(()=>{ const t=rfTageSeit(cur.lastSentAt); if(t===null||(cur.stepOrder||1)<2) return null;
                  const eng = t<2;
                  return (<div style={{background:eng?`${T.warning||"#e6a33a"}14`:T.bgCard2,border:`1px solid ${eng?(T.warning||"#e6a33a")+"66":T.border}`,borderRadius:9,padding:"10px 14px",fontSize:12.5,marginBottom:14,lineHeight:1.55,color:eng?T.text:T.textMuted}}>
                    Die letzte Mail an diesen Lead ging <b style={{color:T.text}}>{rfSeitText(t)}</b> raus.{eng?" Das ist noch frisch, ein Nachfassen wirkt schnell aufdringlich.":""}
                  </div>);
                })()}
                <div style={{marginBottom:12}}><span style={{fontSize:12,color:T.textMuted,fontWeight:500,display:"block",marginBottom:5}}>An</span><div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:13,color:T.textDim,background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 14px"}}>{cur.toAddr||cur.email}</div></div>
                <div style={{marginBottom:12}}><span style={{fontSize:12,color:T.textMuted,fontWeight:500,display:"block",marginBottom:5}}>Betreff</span><input value={cur.subject||""} onChange={e=>setField('subject',e.target.value)} style={{width:"100%",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"11px 14px",color:T.text,fontSize:14,fontWeight:600,outline:"none"}}/></div>
                <div><span style={{fontSize:12,color:T.textMuted,fontWeight:500,display:"block",marginBottom:5}}>Nachricht</span><RichEditor editorId="rq-editor" docKey={cur.id} value={cur.body||""} onChange={v=>setField('body',v)} minHeight={260} placeholder="Nachricht..."/></div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:12,alignItems:"center"}}>
                  {[["Vorname",(cur.contactName||"").trim().split(/\s+/)[0]],["Firma",cur.company],["Stadt",cur.city]]
                    .filter(([,v])=>v)
                    .map(([k,v])=>(<span key={k} title={`${k} einfügen`} onClick={()=>insertBody(v)} style={{fontSize:12,fontWeight:500,color:T.textMuted,background:T.bgCard2,border:`1px solid ${T.border}`,borderRadius:8,padding:"7px 11px",cursor:"pointer"}}>+ {v}</span>))}
                </div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginTop:18,paddingTop:16,borderTop:`1px solid ${T.border}`}}>
                  <Button variant="ghost" onClick={skip} disabled={busy}>Überspringen</Button>
                  <Button onClick={send} disabled={busy} icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>}>{busy?"Sendet...":"Absenden"}</Button>
                </div>
              </div>
              <div style={{background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:14,padding:18,boxShadow:T.glassHi}}>
                <h3 style={{fontSize:11,textTransform:"uppercase",letterSpacing:"0.1em",color:T.textDim,fontWeight:600,marginBottom:12}}>Lead-Infos</h3>
                {[["Firma",cur.company],["Ansprechpartner",cur.contactName],["Stadt",cur.city],["Website",cur.website],["Telefon",cur.phone]].map(([k,v])=>(<div key={k} style={{display:"flex",justifyContent:"space-between",gap:10,padding:"8px 0",borderBottom:`1px solid ${T.border}`,fontSize:13}}><span style={{color:T.textDim}}>{k}</span><span style={{fontWeight:500,textAlign:"right",wordBreak:"break-word"}}>{v||"—"}</span></div>))}
                <h3 style={{fontSize:11,textTransform:"uppercase",letterSpacing:"0.1em",color:T.textDim,fontWeight:600,margin:"16px 0 10px"}}>Schnell persönlich machen</h3>
                {cur.website&&<button onClick={()=>insertLine(`Ich hab kurz auf ${cur.website} geschaut, macht einen guten Eindruck. `)} style={{display:"block",width:"100%",textAlign:"left",background:T.bgCard2,border:`1px solid ${T.border}`,color:T.text,borderRadius:9,padding:"10px 12px",fontSize:12.5,cursor:"pointer",marginBottom:8,fontFamily:"inherit"}}>Website erwähnen</button>}
                {cur.city&&<button onClick={()=>insertLine(`Gerade in ${cur.city} ist bei Verkäufern aktuell einiges an Bewegung. `)} style={{display:"block",width:"100%",textAlign:"left",background:T.bgCard2,border:`1px solid ${T.border}`,color:T.text,borderRadius:9,padding:"10px 12px",fontSize:12.5,cursor:"pointer",fontFamily:"inherit"}}>Region erwähnen</button>}
              </div>
            </div>
          </>}
      </>}
  </div>);
};

// ─── Posteingang (Antworten) ───
// Hilfsfunktionen: Klartext aus HTML, Vorschauzeile, Zitat abtrennen
const rfPlain = (html) => (html||'')
  .replace(/<br\s*\/?>/gi,'\n').replace(/<\/(p|div|li|tr)>/gi,'\n')
  .replace(/<[^>]+>/g,'')
  .replace(/&nbsp;/g,' ').replace(/&amp;/g,'&').replace(/&lt;/g,'<').replace(/&gt;/g,'>').replace(/&quot;/g,'"').replace(/&#39;/g,"'");
const RF_QUOTE = /^(>|Am .+schrieb|Von meinem iPhone gesendet|Gesendet von|-{2,}\s*Urspr)/i;
const rfSplitQuote = (body) => {
  const lines = rfPlain(body).split('\n');
  let cut = lines.findIndex(l => RF_QUOTE.test(l.trim()));
  if (cut < 0) cut = lines.length;
  return { text: lines.slice(0,cut).join('\n').trim(), quote: lines.slice(cut).join('\n').trim() };
};
const rfPreview = (body) => {
  const t = rfSplitQuote(body).text;
  return t.split('\n').map(l=>l.trim()).filter(Boolean).join(' ').slice(0,160);
};
const rfDate = (s) => {
  if(!s) return "";
  const d = new Date((s||'').replace(' ','T'));
  if(isNaN(d)) return (s||'').slice(0,16).replace('T',' ');
  const heute = new Date();
  const gleich = d.toDateString()===heute.toDateString();
  return gleich ? d.toLocaleTimeString('de',{hour:'2-digit',minute:'2-digit'})
                : d.toLocaleDateString('de',{day:'2-digit',month:'2-digit',year:'2-digit'});
};

const rfEsc = (s) => (s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
const rfDateLang = (s) => {
  const d = new Date((s||'').replace(' ','T'));
  if(isNaN(d)) return (s||'');
  return d.toLocaleDateString('de',{day:'2-digit',month:'2-digit',year:'numeric'}) + ' um ' + d.toLocaleTimeString('de',{hour:'2-digit',minute:'2-digit'});
};
// Baut den zitierten Teil, so wie es normale Mail-Programme machen
const rfZitat = (m) => {
  const wer  = m.company || m.contactName || m.fromAddr;
  const kopf = `Am ${rfDateLang(m.createdAt)} schrieb ${rfEsc(wer)} &lt;${rfEsc(m.fromAddr)}&gt;:`;
  const orig = rfEsc(rfPlain(m.body).trim()).replace(/\n/g,'<br>');
  return `<div><br></div><div><br></div><div style="color:#888;font-size:13px;">${kopf}</div>`
       + `<blockquote style="margin:6px 0 0 0;padding-left:12px;border-left:2px solid #bbb;color:#888;">${orig}</blockquote>`;
};

const MAIL_KIND = {
  bounce:    {label:"Unzustellbar", color:"#ef5a4c"},
  autoreply: {label:"Automatisch",  color:"#8a9098"},
};
const EmailInbox = ({accounts, onBack}) => {
  const [msgs,setMsgs]=useState([]);
  const [loading,setLoading]=useState(true);
  const [fetching,setFetching]=useState(false);
  const [info,setInfo]=useState("");
  const [openMsg,setOpenMsg]=useState(null);
  const [reply,setReply]=useState("");
  const [basis,setBasis]=useState("");
  const [sending,setSending]=useState(false);
  const [showQuote,setShowQuote]=useState(false);
  const [gelesen,setGelesen]=useState({});
  const [ton,setTon]=useState(rfTonAn());
  const [neueMail,setNeueMail]=useState(null);   // {von, an, betreff, text}
  const [sendeNeu,setSendeNeu]=useState(false);
  const load=async()=>{ setLoading(true); const m=await api.get('email-inbox'); setMsgs(Array.isArray(m)?m:[]); setLoading(false); };
  useEffect(()=>{ load(); },[]);
  const fetchReplies=async()=>{
    setFetching(true); setInfo("");
    const res=await api.post('email-fetch-replies',{});
    setFetching(false);
    if(res&&res.error){ setInfo(res.error); return; }
    let txt=`${res.replies||0} neue Antwort(en) abgerufen.`;
    if(Array.isArray(res.report)){
      const fehler=res.report.filter(r=>!r.ok).map(r=>`${r.email}: ${r.error||"Fehler"}`);
      if(fehler.length) txt += " · " + fehler.join(" · ");
    }
    setInfo(txt); load();
  };
  const oeffne=async(m)=>{
    const zitat=rfZitat(m);
    setOpenMsg(m); setReply(zitat); setBasis(zitat); setShowQuote(false);
    if(!m.readAt&&!gelesen[m.id]){
      setGelesen(g=>({...g,[m.id]:true}));
      setMsgs(ms=>ms.map(x=>x.id===m.id?{...x,readAt:new Date().toISOString()}:x));
      api.post('email-mark-read',{id:m.id});
    }
  };
  // Eigener Text = alles, was über dem Zitat steht
  const eigenerText = () => {
    const a = rfPlain(reply).trim();
    const b = rfPlain(basis).trim();
    return a === b ? "" : a.replace(b,"").trim();
  };
  const neueOeffnen=()=>{
    const acc=(accounts||[])[0];
    setNeueMail({von:acc?acc.id:"", an:"", betreff:"", text:""});
  };
  const neueSenden=async()=>{
    if(!neueMail)return;
    setSendeNeu(true); setInfo("");
    const res=await api.post('email-compose-send',{accountId:neueMail.von,to:neueMail.an,subject:neueMail.betreff,body:neueMail.text});
    setSendeNeu(false);
    if(res&&res.error){ setInfo(res.error); return; }
    setNeueMail(null); setInfo("Mail gesendet."); load();
  };
  const sendReply=async()=>{
    if(!openMsg||!eigenerText())return;
    setSending(true);
    // Antwort geht aus dem Postfach raus, in dem die Mail angekommen ist
    const ziel=(openMsg.toAddr||"").toLowerCase();
    const acc=(accounts||[]).find(a=>(a.email||"").toLowerCase()===ziel) || (accounts&&accounts.length?accounts[0]:null);
    const res=await api.post('email-reply-send',{accountId:acc?acc.id:"",leadId:openMsg.leadId,to:openMsg.fromAddr,subject:(openMsg.subject||"").toLowerCase().startsWith("re:")?openMsg.subject:("Re: "+(openMsg.subject||"")),body:reply,inReplyTo:openMsg.message_id});
    setSending(false);
    if(res&&res.error){ setInfo(res.error); return; }
    setReply(""); setBasis(""); setOpenMsg(null); setInfo("Antwort gesendet.");
  };
  const loeschen=async(m,frag=true)=>{
    if(frag&&!window.confirm("Diese Mail wirklich aus dem Posteingang löschen?"))return;
    setMsgs(ms=>ms.filter(x=>x.id!==m.id));
    if(openMsg&&openMsg.id===m.id) setOpenMsg(null);
    const r=await api.post('email-message-delete',{id:m.id});
    if(r&&r.error){ setInfo(r.error); load(); }
  };

  // ─── EINZELNE MAIL ───
  if(openMsg){
    const m=openMsg; const {text,quote}=rfSplitQuote(m.body);
    return (<div>
      <div className="page-head" style={{marginBottom:18}}>
        <Button variant="ghost" size="sm" onClick={()=>setOpenMsg(null)}>← Posteingang</Button>
        <h1 style={{fontSize:19,fontWeight:700,letterSpacing:"-0.02em"}}>{m.subject||"(kein Betreff)"}</h1>
      </div>
      {info&&<div style={{background:T.infoGlow,border:`1px solid ${T.info}55`,color:T.text,borderRadius:9,padding:"10px 14px",fontSize:13,marginBottom:14}}>{info}</div>}
      {m.kind==="bounce"&&<div style={{background:`${T.danger}14`,border:`1px solid ${T.danger}55`,borderRadius:10,padding:"11px 15px",fontSize:13,marginBottom:14,lineHeight:1.6}}>Diese Mail ist ein <b>Rückläufer</b>. Die Adresse war nicht erreichbar. Der Lead wurde als unzustellbar markiert und bekommt keine weiteren Mails mehr.</div>}
      {m.kind==="autoreply"&&<div style={{background:T.bgCard2,border:`1px solid ${T.border}`,borderRadius:10,padding:"11px 15px",fontSize:13,marginBottom:14,lineHeight:1.6,color:T.textMuted}}>Das sieht nach einer <b style={{color:T.text}}>automatischen Antwort</b> aus, etwa einer Abwesenheitsnotiz. Die Sequenz für diesen Lead läuft deshalb ganz normal weiter.</div>}
      <div style={{background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:14,boxShadow:T.glassHi,overflow:"hidden",marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 18px",borderBottom:`1px solid ${T.border}`}}>
          <div style={{width:34,height:34,borderRadius:"50%",flexShrink:0,background:T.accentGlow,color:T.accent,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700}}>{((m.company||m.contactName||m.fromAddr||"?").trim()[0]||"?").toUpperCase()}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:14,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{m.company||m.contactName||m.fromAddr}</div>
            <div style={{fontSize:12,color:T.textDim,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{m.fromAddr}</div>
          </div>
          <div style={{fontSize:12,color:T.textDim,fontFamily:"'JetBrains Mono',monospace",whiteSpace:"nowrap"}}>{rfDate(m.createdAt)}</div>
        </div>
        <div style={{padding:"18px",fontSize:14,lineHeight:1.65,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{text||rfPlain(m.body)}</div>
        {quote&&<div style={{padding:"0 18px 16px"}}>
          <button onClick={()=>setShowQuote(q=>!q)} style={{background:T.bgCard2,border:`1px solid ${T.border}`,color:T.textDim,borderRadius:20,padding:"4px 12px",fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>{showQuote?"Zitat ausblenden":"··· Zitierten Text anzeigen"}</button>
          {showQuote&&<div style={{marginTop:12,paddingLeft:12,borderLeft:`2px solid ${T.border}`,fontSize:13,lineHeight:1.6,color:T.textDim,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{quote}</div>}
        </div>}
      </div>
      <div style={{background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:14,boxShadow:T.glassHi,padding:18}}>
        <div style={{fontSize:13,color:T.textMuted,marginBottom:10}}>Antwort an <b style={{color:T.text}}>{m.fromAddr}</b>{m.toAddr?<span> · von <b style={{color:T.text}}>{m.toAddr}</b></span>:null}</div>
        <RichEditor editorId="reply-editor" docKey={m.id} value={reply} onChange={setReply} minHeight={180} placeholder="Deine Antwort..."/>
        <div style={{marginTop:12,display:"flex",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
          <Button variant="ghost" onClick={()=>loeschen(m)}><span style={{color:T.danger}}>Löschen</span></Button>
          <div style={{display:"flex",gap:8}}>
            <Button variant="ghost" onClick={()=>setOpenMsg(null)}>Abbrechen</Button>
            <Button onClick={sendReply} disabled={sending||!eigenerText()}>{sending?"Sendet...":"Antwort senden"}</Button>
          </div>
        </div>
      </div>
    </div>);
  }

  // ─── LISTE ───
  const gruppen = (()=>{
    const map = {};
    msgs.forEach(m=>{ const k=(m.toAddr||"unbekannt").toLowerCase(); (map[k]=map[k]||[]).push(m); });
    const out = [];
    (accounts||[]).forEach(a=>{
      const k=(a.email||"").toLowerCase();
      out.push({key:k, titel:a.fromName||a.email, adresse:a.email, msgs:map[k]||[]});
      delete map[k];
    });
    Object.keys(map).forEach(k=>out.push({key:k, titel:k, adresse:k, msgs:map[k]}));
    return out;
  })();
  const zeile=(m,i,letzte)=>{ const neu=!m.readAt&&!gelesen[m.id]; return (
    <div key={m.id} onClick={()=>oeffne(m)} className="inbox-row" style={{display:"flex",alignItems:"center",gap:12,padding:"13px 16px",borderBottom:letzte?"none":`1px solid ${T.border}`,cursor:"pointer"}}>
      <div style={{width:7,flexShrink:0,display:"flex",justifyContent:"center"}}>{neu&&<span style={{width:7,height:7,borderRadius:"50%",background:T.accent,display:"block"}}/>}</div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:12,color:T.textDim,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{m.company||m.contactName||m.fromAddr}</div>
        <div style={{display:"flex",alignItems:"center",gap:7,minWidth:0}}>
          {MAIL_KIND[m.kind]&&<span style={{flexShrink:0,fontSize:10.5,fontWeight:700,color:MAIL_KIND[m.kind].color,background:`${MAIL_KIND[m.kind].color}1f`,borderRadius:5,padding:"1px 6px",letterSpacing:"0.02em"}}>{MAIL_KIND[m.kind].label}</span>}
          <div style={{fontSize:13.5,fontWeight:neu?700:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",minWidth:0}}>{m.subject||"(kein Betreff)"}</div>
        </div>
        <div style={{fontSize:12.5,color:T.textDim,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",marginTop:1}}>{rfPreview(m.body)||"(kein Text)"}</div>
      </div>
      <div style={{fontSize:11.5,color:T.textDim,fontFamily:"'JetBrains Mono',monospace",whiteSpace:"nowrap",flexShrink:0}}>{rfDate(m.createdAt)}</div>
      <button onClick={ev=>{ev.stopPropagation();loeschen(m);}} title="Mail löschen" className="inbox-del" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:5,display:"flex",flexShrink:0,borderRadius:6}}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/></svg>
      </button>
    </div>);
  };
  return (<div>
    <div className="page-head" style={{marginBottom:18}}>
      <Button variant="ghost" size="sm" onClick={onBack}>← Zurück</Button>
      <h1 style={{fontSize:20,fontWeight:700,letterSpacing:"-0.02em"}}>Posteingang <span style={{color:T.textDim,fontWeight:500}}>{msgs.length?`(${msgs.length})`:""}</span></h1>
      <div className="head-actions">
        <Button variant="ghost" onClick={()=>{const an=!ton; setTon(an); rfTonSetzen(an); if(an) rfPling();}}>{ton?"Ton an":"Ton aus"}</Button>
        <Button variant="ghost" onClick={neueOeffnen} disabled={!(accounts||[]).length}>Neue E-Mail</Button>
        <Button onClick={fetchReplies} disabled={fetching}>{fetching?"Ruft ab...":"Antworten abrufen"}</Button>
      </div>
    </div>
    {info&&<div style={{background:T.infoGlow,border:`1px solid ${T.info}55`,color:T.text,borderRadius:9,padding:"10px 14px",fontSize:13,marginBottom:14,lineHeight:1.5}}>{info}</div>}
    {neueMail&&<div style={{background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:14,boxShadow:T.glassHi,padding:18,marginBottom:16}}>
      <div style={{fontSize:14,fontWeight:700,marginBottom:14}}>Neue E-Mail</div>
      <div className="grid-2col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
        <div>
          <div style={{fontSize:12,color:T.textMuted,fontWeight:500,marginBottom:5}}>Von</div>
          <select value={neueMail.von} onChange={e=>setNeueMail(m=>({...m,von:e.target.value}))} style={{width:"100%",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"11px 14px",color:T.text,fontSize:14,outline:"none",fontFamily:"inherit"}}>
            {(accounts||[]).map(a=>(<option key={a.id} value={a.id}>{a.fromName?`${a.fromName} · `:""}{a.email}</option>))}
          </select>
        </div>
        <div>
          <div style={{fontSize:12,color:T.textMuted,fontWeight:500,marginBottom:5}}>An</div>
          <input value={neueMail.an} onChange={e=>setNeueMail(m=>({...m,an:e.target.value}))} placeholder="empfaenger@firma.de" style={{width:"100%",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"11px 14px",color:T.text,fontSize:14,outline:"none",fontFamily:"'JetBrains Mono',monospace"}}/>
        </div>
      </div>
      <div style={{marginBottom:12}}>
        <div style={{fontSize:12,color:T.textMuted,fontWeight:500,marginBottom:5}}>Betreff</div>
        <input value={neueMail.betreff} onChange={e=>setNeueMail(m=>({...m,betreff:e.target.value}))} style={{width:"100%",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"11px 14px",color:T.text,fontSize:14,fontWeight:600,outline:"none"}}/>
      </div>
      <RichEditor editorId="neu-editor" docKey="neu" value={neueMail.text} onChange={v=>setNeueMail(m=>({...m,text:v}))} minHeight={200} placeholder="Deine Nachricht..."/>
      <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:12,flexWrap:"wrap"}}>
        <Button variant="ghost" onClick={()=>setNeueMail(null)}>Abbrechen</Button>
        <Button onClick={neueSenden} disabled={sendeNeu||!neueMail.an.trim()||!rfPlain(neueMail.text).trim()}>{sendeNeu?"Sendet...":"Senden"}</Button>
      </div>
    </div>}
    {loading?<div style={{padding:"40px 0",textAlign:"center",color:T.textDim}}>Lädt...</div>:
      gruppen.length===0?<div style={{color:T.textDim,fontSize:14,padding:"20px 0"}}>Noch kein Postfach hinterlegt.</div>:
      <div style={{display:"flex",flexDirection:"column",gap:22}}>
        {gruppen.map(g=>(<div key={g.key}>
          <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:9,padding:"0 2px"}}>
            <Icon name="mail" size={15}/>
            <span style={{fontSize:13.5,fontWeight:700,letterSpacing:"-0.01em"}}>{g.titel}</span>
            <span style={{fontSize:12,color:T.textDim,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",minWidth:0}}>{g.titel!==g.adresse?g.adresse:""}</span>
            <span style={{marginLeft:"auto",fontSize:11.5,fontWeight:600,color:T.textMuted,background:T.bgCard2,border:`1px solid ${T.border}`,borderRadius:20,padding:"2px 9px",fontFamily:"'JetBrains Mono',monospace"}}>{g.msgs.length}</span>
          </div>
          {g.msgs.length===0
            ? <div style={{background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:14,boxShadow:T.glassHi,padding:"18px 16px",color:T.textDim,fontSize:13}}>Noch keine Antworten in diesem Postfach.</div>
            : <div style={{background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:14,boxShadow:T.glassHi,overflow:"hidden"}}>
                {g.msgs.map((m,i)=>zeile(m,i,i===g.msgs.length-1))}
              </div>}
        </div>))}
      </div>}
  </div>);
};

// ─── Abgemeldet-Bereich ───
const EmailUnsubscribed = ({onBack}) => {
  const [rows,setRows]=useState([]);
  const [loading,setLoading]=useState(true);
  const load=async()=>{ setLoading(true); const r=await api.get('email-unsubscribes'); setRows(Array.isArray(r)?r:[]); setLoading(false); };
  useEffect(()=>{ load(); },[]);
  const allow=async(id)=>{ if(!window.confirm("Diese Adresse wieder für E-Mails zulassen?"))return; await api.del('email-unsubscribes',{id}); load(); };
  return (<div>
    <div className="page-head" style={{marginBottom:16}}>
      <Button variant="ghost" size="sm" onClick={onBack}>← Zurück</Button>
      <h1 style={{fontSize:20,fontWeight:700,letterSpacing:"-0.02em"}}>Abgemeldet <span style={{color:T.textDim,fontWeight:500}}>({rows.length})</span></h1>
    </div>
    <p style={{fontSize:13,color:T.textMuted,marginBottom:16,lineHeight:1.6,maxWidth:640}}>Diese Kontakte haben sich über den Abmeldelink ausgetragen. Sie bekommen keine weiteren Mails, und laufende Sequenzen für sie sind automatisch gestoppt. Du kannst eine Adresse bei Bedarf wieder zulassen.</p>
    {loading?<div style={{padding:"40px 0",textAlign:"center",color:T.textDim}}>Lädt...</div>:
      rows.length===0?<div style={{color:T.textDim,fontSize:14,padding:"20px 0"}}>Noch niemand abgemeldet.</div>:
      <div style={{background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:14,boxShadow:T.glassHi,overflow:"hidden"}}>
        {rows.map((r,i)=>(<div key={r.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 18px",borderBottom:i<rows.length-1?`1px solid ${T.border}`:"none"}}>
          <div style={{flex:1,minWidth:0}}><div style={{fontSize:13.5,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.company||r.contactName||r.email}</div><div style={{fontSize:12,color:T.textDim}}>{r.email}</div></div>
          <span style={{fontSize:12,color:T.textMuted,fontFamily:"'JetBrains Mono',monospace",whiteSpace:"nowrap"}}>{(r.createdAt||"").slice(0,10)}</span>
          <Button variant="ghost" size="sm" onClick={()=>allow(r.id)}>Wieder zulassen</Button>
        </div>))}
      </div>}
  </div>);
};


const EmailCampaignsPage = ({user,reload,dv}) => {
  const [campaigns,setCampaigns]=useState([]);
  const [accounts,setAccounts]=useState([]);
  const [users,setUsers]=useState([]);
  const [sel,setSel]=useState(null);
  const [screen,setScreen]=useState('list');
  const [genMsg,setGenMsg]=useState('');
  const [steps,setSteps]=useState([]);
  const [enroll,setEnroll]=useState([]);
  const [enrollFehler,setEnrollFehler]=useState("");
  const [stats,setStats]=useState(null);
  const [leadSuche,setLeadSuche]=useState("");
  const [gruppenAuf,setGruppenAuf]=useState({});
  const [dupInfo,setDupInfo]=useState(null);
  const [dupLaeuft,setDupLaeuft]=useState(false);
  const [loading,setLoading]=useState(true);
  const [showAccounts,setShowAccounts]=useState(false);
  const [menuOffen,setMenuOffen]=useState(false);
  const [menuLinks,setMenuLinks]=useState(true);
  const menuBtnRef=useRef(null);
  const menuUmschalten=()=>{
    // Vor dem Öffnen schauen, wohin das Menü passt
    if(!menuOffen&&menuBtnRef.current){
      const r=menuBtnRef.current.getBoundingClientRect();
      setMenuLinks((window.innerWidth-r.left) < 215);
    }
    setMenuOffen(o=>!o);
  };
  const [showEnroll,setShowEnroll]=useState(false);
  const [cForm,setCForm]=useState(null);
  const [savingC,setSavingC]=useState(false);
  const [cronUrl,setCronUrl]=useState("");
  const [verarbeite,setVerarbeite]=useState(false);
  const [procMsg,setProcMsg]=useState("");
  const cronHolen=async()=>{ const r=await api.get('email-cron-url'); if(r&&r.url){ setCronUrl(r.url); setProcMsg(""); } else { setProcMsg("Cron-Adresse konnte nicht geladen werden."); } };
  const jetztVerarbeiten=async()=>{
    setVerarbeite(true); setProcMsg("");
    const r=await api.post('email-process',{});
    setVerarbeite(false);
    if(r&&r.error){ setProcMsg(r.error); return; }
    let txt=`${r.sent||0} Mail(s) gesendet.`;
    if(Array.isArray(r.report)){
      const hinweise=r.report.filter(x=>x.hinweis).map(x=>`${x.kampagne}: ${x.hinweis}`);
      if(hinweise.length) txt += " · " + hinweise.join(" · ");
    }
    setProcMsg(txt); reloadDetail();
  };

  const loadCampaigns=async()=>{ const c=await api.get('email-campaigns'); setCampaigns(Array.isArray(c)?c:[]); };
  const loadAccounts=async()=>{ const a=await api.get('email-accounts'); setAccounts(Array.isArray(a)?a:[]); };
  const didInit=useRef(false);
  useEffect(()=>{ (async()=>{ if(!didInit.current) setLoading(true); await loadCampaigns(); await loadAccounts(); const u=await api.get('users'); setUsers(Array.isArray(u)?u:[]); setLoading(false); didInit.current=true; })(); },[dv]);

  const openCampaign=async(c)=>{
    setSel(c.id); setCForm({...c}); setScreen('detail'); setStats(null);
    const s=await api.get('email-campaign-steps',`campaignId=${c.id}`); setSteps(Array.isArray(s)?s:[]);
    const e=await api.get('email-enrollments',`campaignId=${c.id}`); setEnroll(Array.isArray(e)?e:[]);
    const st=await api.get('email-campaign-stats',`campaignId=${c.id}`); setStats(st&&!st.error?st:null);
    const dp=await api.get('email-enrollments-dedupe',`campaignId=${c.id}`); setDupInfo(dp&&!dp.error?dp:null);
  };
  const reloadDetail=async()=>{
    const e=await api.get('email-enrollments',`campaignId=${sel}`);
    setEnroll(Array.isArray(e)?e:[]);
    const st=await api.get('email-campaign-stats',`campaignId=${sel}`);
    setStats(st&&!st.error?st:null);
    const dp=await api.get('email-enrollments-dedupe',`campaignId=${sel}`);
    setDupInfo(dp&&!dp.error?dp:null);
    await loadCampaigns();
  };
  const back=()=>{ setSel(null); setCForm(null); setSteps([]); setEnroll([]); setScreen('list'); };
  const startReview=async()=>{ setGenMsg('Erzeuge Entwürfe...'); const res=await api.post('email-generate-drafts',{campaignId:sel}); setGenMsg(''); setScreen('review'); };

  const createCampaign=async()=>{
    const res=await api.post('email-campaigns',{name:"Neue Kampagne",status:"draft"});
    if(res&&res.id){ await loadCampaigns(); const c={id:res.id,name:"Neue Kampagne",status:"draft",dailyLimit:40,sendStart:8,sendEnd:18}; openCampaign(c); }
  };
  const saveCampaign=async()=>{ setSavingC(true); await api.put('email-campaigns',cForm); setSavingC(false); await loadCampaigns(); };
  const deleteCampaign=async()=>{ if(!window.confirm("Kampagne mit allen Phasen und Zuordnungen löschen?"))return; await api.del('email-campaigns',{id:sel}); back(); await loadCampaigns(); };

  const addStep=async()=>{
    const order=(steps.length?Math.max(...steps.map(s=>s.stepOrder)):0)+1;
    const delay=steps.length===0?0:3;
    const res=await api.post('email-campaign-steps',{campaignId:sel,stepOrder:order,delayDays:delay,subject:"",body:""});
    if(res&&res.id){ const s=await api.get('email-campaign-steps',`campaignId=${sel}`); setSteps(Array.isArray(s)?s:[]); }
  };
  const updateStep=(id,patch)=>setSteps(st=>st.map(s=>s.id===id?{...s,...patch}:s));
  const saveStep=async(s)=>{ await api.put('email-campaign-steps',{id:s.id,stepOrder:s.stepOrder,delayDays:s.delayDays,subject:s.subject,body:s.body}); };
  const deleteStep=async(id)=>{ if(!window.confirm("Phase löschen?"))return; await api.del('email-campaign-steps',{id}); setSteps(st=>st.filter(s=>s.id!==id)); };

  const removeEnroll=async(id)=>{ await api.del('email-enrollments',{id}); reloadDetail(); };
  const dupAufraeumen=async()=>{
    if(!window.confirm(`${dupInfo.doppelt} doppelte Einträge entfernen? Es bleibt jeweils der Eintrag, der am weitesten ist.`))return;
    setDupLaeuft(true);
    const r=await api.post('email-enrollments-dedupe',{campaignId:sel});
    setDupLaeuft(false);
    if(r&&r.error){ setEnrollFehler(r.error); return; }
    setEnrollFehler("");
    reloadDetail();
  };
  const setEnrollStatus=async(id,status)=>{
    // sofort anzeigen, damit es sich flott anfühlt
    setEnroll(es=>es.map(e=>e.id===id?{...e,status}:e));
    const r=await api.post('email-enrollment-status',{id,status});
    if(r&&r.error){ setEnrollFehler(r.error); reloadDetail(); return; }
    setEnrollFehler("");
    reloadDetail();
  };

  if(loading) return <div style={{padding:"60px 0",textAlign:"center",color:T.textDim}}>Lädt...</div>;

  if(screen==='inbox') return <EmailInbox accounts={accounts} onBack={()=>setScreen('list')}/>;
  if(screen==='unsubscribed') return <EmailUnsubscribed onBack={()=>setScreen('list')}/>;
  if(screen==='review'&&sel) return <EmailReviewQueue campaignId={sel} campaignName={(cForm||{}).name||''} onBack={()=>{setScreen('detail');reloadDetail();}}/>;
  if(screen==='leads'&&sel){
    const q=leadSuche.trim().toLowerCase();
    const sichtbar = q ? enroll.filter(e=>[e.company,e.contactName,e.email].some(v=>String(v||"").toLowerCase().includes(q))) : enroll;
    return (<div>
      <div className="page-head" style={{marginBottom:14}}>
        <Button variant="ghost" size="sm" onClick={()=>setScreen('detail')}>← Zurück</Button>
        <h1 style={{fontSize:20,fontWeight:700,letterSpacing:"-0.02em"}}>Leads · {(cForm||{}).name||''}</h1>
      </div>
      {enrollFehler&&<div style={{background:`${T.danger}14`,border:`1px solid ${T.danger}55`,color:T.danger,borderRadius:9,padding:"10px 14px",fontSize:13,marginBottom:12}}>{enrollFehler}</div>}
      {dupInfo&&dupInfo.doppelt>0&&<div style={{background:`${T.warning||"#e6a33a"}14`,border:`1px solid ${(T.warning||"#e6a33a")}66`,borderRadius:10,padding:"12px 15px",fontSize:13,marginBottom:14,lineHeight:1.6}}>
        <b>{dupInfo.doppelt} doppelte {dupInfo.doppelt===1?"Adresse":"Adressen"}</b> in dieser Kampagne. Die würden dieselbe Mail mehrfach bekommen.
        {dupInfo.firmen&&dupInfo.firmen.length>0&&<div style={{fontSize:12.5,color:T.textMuted,marginTop:5}}>Betrifft {dupInfo.firmen.slice(0,4).join(", ")}{dupInfo.firmen.length>4?" und weitere":""}.</div>}
        <div style={{marginTop:10}}><Button size="sm" onClick={dupAufraeumen} disabled={dupLaeuft}>{dupLaeuft?"Räumt auf...":"Doppelte entfernen"}</Button></div>
      </div>}
      <div style={{background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:14,padding:20,boxShadow:T.glassHi}}>
        <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
          <input value={leadSuche} onChange={e=>setLeadSuche(e.target.value)} placeholder="Firma, Name oder Adresse suchen..."
            style={{flex:"1 1 240px",minWidth:0,background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"11px 14px",color:T.text,fontSize:13.5,outline:"none"}}/>
          {leadSuche&&<Button variant="ghost" size="sm" onClick={()=>setLeadSuche("")}>Zurücksetzen</Button>}
          <Button size="sm" icon={<Icon name="add" size={15}/>} onClick={()=>setShowEnroll(true)}>Leads hinzufügen</Button>
        </div>
        <div style={{fontSize:12.5,color:T.textDim,marginBottom:10}}>{q?`${sichtbar.length} von ${enroll.length} Leads`:`${enroll.length} Leads`}</div>
        {enroll.length===0
          ? <div style={{color:T.textDim,fontSize:13,padding:"10px 0"}}>Noch keine Leads. Füg welche hinzu, um die Sequenz zu füllen.</div>
          : sichtbar.length===0
            ? <div style={{color:T.textDim,fontSize:13,padding:"14px 0"}}>Kein Lead passt zu "{leadSuche}".</div>
            : (()=>{
                // Nach Status in Gruppen sortieren, damit nicht alles als eine
                // endlose Liste untereinander steht.
                const reihenfolge=["active","replied","appointment","customer","paused","stopped","completed","lost","bounced"];
                const titel={active:"Läuft",replied:"Geantwortet",appointment:"Termin",customer:"Kunde",paused:"Pausiert",stopped:"Gestoppt",completed:"Abgeschlossen",lost:"Verloren",bounced:"Unzustellbar"};
                const gruppen={};
                sichtbar.forEach(e=>{ const k=reihenfolge.includes(e.status)?e.status:"active"; (gruppen[k]=gruppen[k]||[]).push(e); });
                const vorhanden=reihenfolge.filter(k=>gruppen[k]&&gruppen[k].length);
                // Beim Suchen alles offen, sonst „Läuft“ offen und der Rest zu
                const istAuf=(k)=> q ? true : (k in gruppenAuf ? gruppenAuf[k] : k==="active");
                return (<div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {vorhanden.map(k=>{ const farbe=(ENROLL_STATUS[k]||{}).color||T.textDim; const auf=istAuf(k); const leute=gruppen[k]; return (
                    <div key={k} style={{border:`1px solid ${T.border}`,borderRadius:11,overflow:"hidden"}}>
                      <button onClick={()=>setGruppenAuf(g=>({...g,[k]:!auf}))} style={{display:"flex",alignItems:"center",gap:10,width:"100%",background:T.bgCard2,border:"none",padding:"12px 14px",cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                        <span style={{width:9,height:9,borderRadius:"50%",background:farbe,flexShrink:0}}/>
                        <span style={{fontSize:13.5,fontWeight:600,color:T.text}}>{titel[k]}</span>
                        <span style={{fontSize:12.5,color:T.textDim}}>{leute.length}</span>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.textDim} strokeWidth="2.5" style={{marginLeft:"auto",transform:auf?"rotate(180deg)":"none",transition:"transform .15s"}}><polyline points="6 9 12 15 18 9"/></svg>
                      </button>
                      {auf&&<div style={{display:"flex",flexDirection:"column",padding:"0 14px"}}>
                        {leute.map((e,i)=>{ const st=ENROLL_STATUS[e.status]||ENROLL_STATUS.active; const fremd=!ENROLL_WAEHLBAR.includes(e.status); return (
                          <div key={e.id} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 0",borderBottom:i<leute.length-1?`1px solid ${T.border}`:"none",flexWrap:"wrap"}}>
                            <div style={{flex:"1 1 180px",minWidth:0}}><div style={{fontSize:13.5,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{e.company||e.contactName||"—"}</div><div style={{fontSize:12,color:T.textDim,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{e.email}</div></div>
                            <span style={{fontSize:12,color:T.textDim,whiteSpace:"nowrap"}}>{e.currentStep===0?"vor erster Mail":`Phase ${e.currentStep}`}</span>
                            <select value={e.status} onChange={ev=>setEnrollStatus(e.id, ev.target.value)} title="Status ändern"
                              style={{fontSize:12,fontWeight:600,color:st.color,background:`${st.color}1a`,border:`1px solid ${st.color}66`,borderRadius:20,padding:"5px 10px",cursor:"pointer",fontFamily:"inherit",outline:"none",appearance:"none",WebkitAppearance:"none",minWidth:112,textAlign:"center"}}>
                              {fremd&&<option value={e.status}>{st.label}</option>}
                              {ENROLL_WAEHLBAR.map(x=>(<option key={x} value={x}>{ENROLL_STATUS[x].label}</option>))}
                            </select>
                            <button onClick={()=>removeEnroll(e.id)} title="Aus der Kampagne entfernen" style={{background:"none",border:"none",color:T.textDim,cursor:"pointer",padding:4,display:"flex",flexShrink:0}}><Icon name="close" size={16}/></button>
                          </div>);
                        })}
                      </div>}
                    </div>);
                  })}
                </div>);
              })()}
      </div>
      <EmailEnrollModal open={showEnroll} onClose={()=>setShowEnroll(false)} campaignId={sel} onDone={reloadDetail}/>
    </div>);
  }

  // ─── LISTENANSICHT ───
  if(!sel){
    return (<div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,marginBottom:8,flexWrap:"wrap"}}>
        <div><h1 style={{fontSize:24,fontWeight:700,letterSpacing:"-0.02em"}}>E-Mail Kampagnen</h1><p style={{color:T.textDim,fontSize:13,marginTop:3}}>Erstanschreiben und Follow-ups an deine Makler-Leads</p></div>
        <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
          <Button variant="ghost" onClick={()=>setScreen('inbox')}>Posteingang</Button>
          <Button icon={<Icon name="add" size={16}/>} onClick={createCampaign}>Neue Kampagne</Button>
          <div style={{position:"relative"}}>
            <button ref={menuBtnRef} onClick={menuUmschalten} title="Mehr" aria-label="Mehr" style={{background:T.bgCard2,border:`1px solid ${T.border}`,color:T.textMuted,borderRadius:9,width:38,height:38,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:18,lineHeight:1,fontWeight:700,letterSpacing:"1px",paddingBottom:6}}>···</button>
            {menuOffen&&<>
              <div onClick={()=>setMenuOffen(false)} style={{position:"fixed",inset:0,zIndex:60}}/>
              <div style={{position:"absolute",...(menuLinks?{right:0}:{left:0}),top:44,zIndex:61,minWidth:200,maxWidth:"calc(100vw - 24px)",background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:12,boxShadow:`${T.glassHi}, ${T.shadowLg}`,overflow:"hidden",padding:5}}>
                {[
                  ["Postfächer"+(accounts.length?` (${accounts.length})`:""), ()=>setShowAccounts(true)],
                  ["Abgemeldet", ()=>setScreen('unsubscribed')],
                ].map(([label,fn])=>(
                  <button key={label} onClick={()=>{setMenuOffen(false);fn();}} style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",color:T.text,fontSize:13.5,padding:"10px 12px",borderRadius:8,cursor:"pointer",fontFamily:"inherit"}}
                    onMouseEnter={e=>e.currentTarget.style.background=T.bgCard2}
                    onMouseLeave={e=>e.currentTarget.style.background="none"}>{label}</button>
                ))}
              </div>
            </>}
          </div>
        </div>
      </div>
      {accounts.length===0&&<div style={{background:T.warningGlow,border:`1px solid ${T.warning}55`,borderRadius:12,padding:"14px 18px",marginTop:16,fontSize:13.5,color:T.text,lineHeight:1.6}}>Noch kein Postfach hinterlegt. Leg zuerst über die drei Punkte oben rechts unter Postfächer die Absender-Adressen an, dann kannst du Kampagnen darüber laufen lassen.</div>}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:16,marginTop:20}}>
        {campaigns.length===0&&<div style={{color:T.textDim,fontSize:14,padding:"20px 0"}}>Noch keine Kampagne. Leg deine erste an.</div>}
        {campaigns.map(c=>{ const st=CAMPAIGN_STATUS[c.status]||CAMPAIGN_STATUS.draft; return (
          <div key={c.id} onClick={()=>openCampaign(c)} style={{background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:14,padding:20,boxShadow:T.glassHi,cursor:"pointer"}}>
            <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10}}>
              <div style={{fontSize:16,fontWeight:700,letterSpacing:"-0.01em"}}>{c.name}</div>
              <Badge color={st.color}>{st.label}</Badge>
            </div>
            {c.description&&<div style={{fontSize:13,color:T.textDim,marginTop:6,lineHeight:1.5}}>{c.description}</div>}
            <div style={{display:"flex",gap:18,marginTop:16,paddingTop:14,borderTop:`1px solid ${T.border}`}}>
              <div><div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:20,fontWeight:600}}>{c.totalLeads||0}</div><div style={{fontSize:11,color:T.textDim}}>Leads</div></div>
              <div><div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:20,fontWeight:600,color:T.info}}>{c.counts?.active||0}</div><div style={{fontSize:11,color:T.textDim}}>laufend</div></div>
              <div><div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:20,fontWeight:600,color:T.success}}>{c.counts?.replied||0}</div><div style={{fontSize:11,color:T.textDim}}>geantwortet</div></div>
            </div>
          </div>);
        })}
      </div>
      <EmailAccountsModal open={showAccounts} onClose={()=>setShowAccounts(false)} accounts={accounts} users={users} onReload={loadAccounts}/>
    </div>);
  }

  // ─── DETAILANSICHT ───
  const c=cForm||{};
  const phaseCount=steps.length;
  return (<div>
    <div className="page-head" style={{marginBottom:20}}>
      <Button variant="ghost" size="sm" onClick={back}>← Zurück</Button>
      <h1 style={{fontSize:22,fontWeight:700,letterSpacing:"-0.02em"}}>{c.name}</h1>
      <div className="head-actions">
        <Button size="sm" onClick={startReview} icon={<Icon name="mail" size={15}/>}>{genMsg||"Zur Freigabe →"}</Button>
        <Button variant="ghost" size="sm" onClick={deleteCampaign}><span style={{color:T.danger}}>Kampagne löschen</span></Button>
      </div>
    </div>

    {/* Zahlen zur Kampagne */}
    {stats&&<div style={{background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:14,padding:22,boxShadow:T.glassHi,marginBottom:16}}>
      <div style={{fontSize:15,fontWeight:600,marginBottom:16}}>Wie läuft die Kampagne</div>
      <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:14}}>
        <div style={{flex:"1 1 180px",background:`${T.success}12`,border:`1px solid ${T.success}44`,borderRadius:12,padding:"16px 18px"}}>
          <div style={{fontSize:11.5,color:T.success,fontWeight:700,letterSpacing:"0.02em"}}>ANTWORTRATE</div>
          <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:32,fontWeight:600,marginTop:4,color:T.success,lineHeight:1.1}}>{stats.antwortrate}%</div>
          <div style={{fontSize:12,color:T.textDim,marginTop:4}}>{stats.antworten+stats.termine+stats.kunden} von {stats.angeschrieben} angeschriebenen</div>
        </div>
        {[
          ["Angeschrieben", stats.angeschrieben, T.text, `${stats.leads} Leads in der Kampagne`],
          ["Mails raus", stats.gesendet, T.text, stats.heute?`${stats.heute} heute`:"heute noch keine"],
          ["Antworten", stats.antworten, T.success, stats.autoreplies?`${stats.autoreplies} automatisch`:null],
        ].map(([label,wert,farbe,unten])=>(
          <div key={label} style={{flex:"1 1 125px",minWidth:0,background:T.bgCard2,border:`1px solid ${T.border}`,borderRadius:12,padding:"14px 15px"}}>
            <div style={{fontSize:10.5,color:T.textDim,fontWeight:700,letterSpacing:"0.01em",textTransform:"uppercase",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{label}</div>
            <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:26,fontWeight:600,marginTop:4,color:farbe,lineHeight:1.2}}>{wert}</div>
            {unten&&<div style={{fontSize:12,color:T.textDim,marginTop:4}}>{unten}</div>}
          </div>
        ))}
      </div>
      <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
        {[
          ["Termine", stats.termine, ENROLL_STATUS.appointment.color],
          ["Kunden", stats.kunden, ENROLL_STATUS.customer.color],
          ["Verloren", stats.verloren, ENROLL_STATUS.lost.color],
          ["Unzustellbar", stats.unzustellbar, T.danger],
          ["Wartet auf Freigabe", stats.entwuerfe, T.warning||"#e6a33a"],
        ].filter(([,w])=>w>0).map(([label,wert,farbe])=>(
          <div key={label} style={{display:"flex",alignItems:"center",gap:8,background:T.bgCard2,border:`1px solid ${T.border}`,borderRadius:20,padding:"7px 14px"}}>
            <span style={{width:8,height:8,borderRadius:"50%",background:farbe,flexShrink:0}}/>
            <span style={{fontSize:12.5,color:T.textMuted}}>{label}</span>
            <span style={{fontSize:13,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:farbe}}>{wert}</span>
          </div>
        ))}
        {stats.unzustellbar>0&&stats.bounceRate>5&&<div style={{flexBasis:"100%",fontSize:12.5,color:T.danger,marginTop:4,lineHeight:1.5}}>Die Rückläuferquote liegt bei {stats.bounceRate}%. Ab etwa 5% leidet der Ruf der Absender-Domain, da lohnt ein Blick auf die Adressqualität.</div>}
      </div>
      {stats.proPhase&&stats.proPhase.length>0&&<div style={{marginTop:16,paddingTop:14,borderTop:`1px solid ${T.border}`}}>
        <div style={{fontSize:11.5,color:T.textDim,fontWeight:700,letterSpacing:"0.02em",textTransform:"uppercase",marginBottom:10}}>Versand je Phase</div>
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          {stats.proPhase.map(p=>(
            <div key={p.phase} style={{flex:"1 1 110px",background:T.bgCard2,border:`1px solid ${T.border}`,borderRadius:10,padding:"11px 13px"}}>
              <div style={{fontSize:11.5,color:T.textDim,fontWeight:600}}>Phase {p.phase}</div>
              <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:19,fontWeight:600,marginTop:3}}>{p.gesendet}</div>
            </div>
          ))}
        </div>
      </div>}
    </div>}

    {/* Einstellungen */}
    <div style={{background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:14,padding:22,boxShadow:T.glassHi,marginBottom:18}}>
      <div style={{fontSize:15,fontWeight:600,marginBottom:16}}>Einstellungen</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12}}>
        <Input label="Name" value={c.name||""} onChange={e=>setCForm({...c,name:e.target.value})}/>
        <Select label="Status" options={[{value:"draft",label:"Entwurf"},{value:"active",label:"Aktiv"},{value:"paused",label:"Pausiert"}]} value={c.status||"draft"} onChange={e=>setCForm({...c,status:e.target.value})}/>
        <Select label="Modus" options={[{value:"review",label:"Freigabe (jede Mail prüfen)"},{value:"auto",label:"Automatisch (gedrosselt)"}]} value={c.mode||"review"} onChange={e=>setCForm({...c,mode:e.target.value})}/>
        <Select label="Absender-Postfach" options={[{value:"",label:accounts.length?"— wählen —":"kein Postfach angelegt"},...accounts.map(a=>({value:a.id,label:a.fromName?`${a.fromName} (${a.email})`:a.email}))]} value={c.accountId||""} onChange={e=>setCForm({...c,accountId:e.target.value})}/>
        <Input label="Max. Mails pro Tag" type="number" value={c.dailyLimit??40} onChange={e=>setCForm({...c,dailyLimit:e.target.value})}/>
        <Input label="Sendefenster von (Uhr)" type="number" value={c.sendStart??8} onChange={e=>setCForm({...c,sendStart:e.target.value})}/>
        <Input label="bis (Uhr)" type="number" value={c.sendEnd??18} onChange={e=>setCForm({...c,sendEnd:e.target.value})}/>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:4,marginTop:12}}><label style={{fontSize:12,color:T.textMuted,fontWeight:500}}>Beschreibung</label><textarea value={c.description||""} onChange={e=>setCForm({...c,description:e.target.value})} rows={2} style={{background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 14px",color:T.text,fontSize:14,outline:"none",resize:"vertical",fontFamily:"inherit"}}/></div>
      <div style={{marginTop:14,display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
        <Button onClick={saveCampaign} disabled={savingC}>{savingC?"Speichert...":"Einstellungen speichern"}</Button>
        {c.mode==="auto"&&<Button variant="ghost" onClick={jetztVerarbeiten} disabled={verarbeite}>{verarbeite?"Sendet...":"Jetzt fällige Mails senden"}</Button>}
      </div>
      {procMsg&&<div style={{marginTop:12,background:T.infoGlow,border:`1px solid ${T.info}55`,borderRadius:9,padding:"10px 14px",fontSize:13,lineHeight:1.5}}>{procMsg}</div>}
      {c.mode==="auto"&&<div style={{marginTop:12,background:T.warningGlow,border:`1px solid ${T.warning}55`,borderRadius:10,padding:"12px 16px",fontSize:13,lineHeight:1.6}}>
        <b>Damit die Automatik von allein läuft</b>, braucht es bei All-Inkl einen Zeitplan (Cronjob), der regelmäßig diese Adresse aufruft. Ohne den passiert nichts von selbst, du kannst aber jederzeit den Knopf oben nutzen.
        <div style={{marginTop:8,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          <Button size="sm" variant="ghost" onClick={cronHolen}>{cronUrl?"Adresse neu laden":"Cron-Adresse anzeigen"}</Button>
          {cronUrl&&<Button size="sm" variant="ghost" onClick={()=>{navigator.clipboard&&navigator.clipboard.writeText(cronUrl);setProcMsg("Adresse kopiert.");}}>Kopieren</Button>}
        </div>
        {cronUrl&&<div style={{marginTop:8,fontFamily:"'JetBrains Mono',monospace",fontSize:11.5,wordBreak:"break-all",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:8,padding:"9px 11px"}}>{cronUrl}</div>}
      </div>}
    </div>

    {/* Phasen & Vorlagen */}
    <div style={{background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:14,padding:22,boxShadow:T.glassHi,marginBottom:18}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
        <div style={{fontSize:15,fontWeight:600}}>Phasen & Vorlagen</div>
        <Button variant="ghost" size="sm" icon={<Icon name="add" size={15}/>} onClick={addStep}>Phase hinzufügen</Button>
      </div>
      <p style={{fontSize:12.5,color:T.textDim,marginBottom:16,lineHeight:1.6}}>Platzhalter, die beim Versand pro Lead ersetzt werden: {"{{Vorname}}"}, {"{{Firma}}"}, {"{{Anrede}}"}. Die erste Phase geht sofort raus, jede weitere nach der eingestellten Wartezeit.</p>
      {steps.length===0&&<div style={{color:T.textDim,fontSize:13,padding:"10px 0"}}>Noch keine Phase. Füg die erste Mail hinzu.</div>}
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        {steps.map((s,i)=>(<div key={s.id} style={{background:T.bgCard2,border:`1px solid ${T.border}`,borderRadius:12,padding:16}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginBottom:12}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <span style={{width:26,height:26,borderRadius:7,background:T.accentGlow,color:T.accent,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,fontFamily:"'JetBrains Mono',monospace"}}>{i+1}</span>
              <span style={{fontSize:14,fontWeight:600}}>Phase {i+1}</span>
              <span style={{fontSize:12,color:T.textDim}}>{i===0?"sofort":`nach ${s.delayDays} Tagen`}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={()=>deleteStep(s.id)}><span style={{color:T.danger}}>entfernen</span></Button>
          </div>
          {i>0&&<div style={{maxWidth:220,marginBottom:10}}><Input label="Wartezeit (Tage nach vorheriger Mail)" type="number" value={s.delayDays} onChange={e=>updateStep(s.id,{delayDays:e.target.value})}/></div>}
          <div style={{marginBottom:10}}><Input label="Betreff" value={s.subject||""} onChange={e=>updateStep(s.id,{subject:e.target.value})} placeholder="z.B. kurze Frage zu deiner Verkäufer-Akquise"/></div>
          <div style={{display:"flex",flexDirection:"column",gap:4}}><label style={{fontSize:12,color:T.textMuted,fontWeight:500}}>Text</label><RichEditor docKey={s.id} value={s.body||""} onChange={v=>updateStep(s.id,{body:v})} minHeight={200} placeholder={"Hallo {{Vorname}}, ..."}/></div>
          <div style={{marginTop:12}}><Button size="sm" onClick={()=>saveStep(s)}>Phase speichern</Button></div>
        </div>))}
      </div>
    </div>

    {/* Leads / Pipeline */}
    <div style={{background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,border:`1px solid ${T.glassBorder}`,borderRadius:14,padding:22,boxShadow:T.glassHi}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
        <div style={{fontSize:15,fontWeight:600}}>Leads in dieser Kampagne <span style={{color:T.textDim,fontWeight:500}}>({enroll.length})</span></div>
        <Button variant="ghost" size="sm" icon={<Icon name="add" size={15}/>} onClick={()=>setShowEnroll(true)}>Leads hinzufügen</Button>
      </div>
      {/* Phasen-Überblick */}
      <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:18}}>
        {Array.from({length:Math.max(phaseCount,1)}).map((_,i)=>{ const n=enroll.filter(e=>e.status==="active"&&e.currentStep===i).length; return (
          <div key={i} style={{flex:"1 1 120px",background:T.bgCard2,border:`1px solid ${T.border}`,borderRadius:10,padding:"12px 14px"}}>
            <div style={{fontSize:11,color:T.textDim,fontWeight:600}}>{i===0?"Noch nicht angeschrieben":`Nach Phase ${i}`}</div>
            <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:22,fontWeight:600,marginTop:6}}>{n}</div>
          </div>);
        })}
        <div style={{flex:"1 1 120px",background:T.bgCard2,border:`1px solid ${T.border}`,borderRadius:10,padding:"12px 14px"}}>
          <div style={{fontSize:11,color:T.success,fontWeight:600}}>Geantwortet</div>
          <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:22,fontWeight:600,marginTop:6,color:T.success}}>{enroll.filter(e=>e.status==="replied").length}</div>
        </div>
        {["appointment","customer","lost"].map(k=>{ const n=enroll.filter(e=>e.status===k).length; if(!n) return null; const s=ENROLL_STATUS[k]; return (
          <div key={k} style={{flex:"1 1 120px",background:T.bgCard2,border:`1px solid ${T.border}`,borderRadius:10,padding:"12px 14px"}}>
            <div style={{fontSize:11,color:s.color,fontWeight:600}}>{s.label}</div>
            <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:22,fontWeight:600,marginTop:6,color:s.color}}>{n}</div>
          </div>);
        })}
        {enroll.filter(e=>e.status==="bounced").length>0&&<div style={{flex:"1 1 120px",background:T.bgCard2,border:`1px solid ${T.border}`,borderRadius:10,padding:"12px 14px"}}>
          <div style={{fontSize:11,color:T.danger,fontWeight:600}}>Unzustellbar</div>
          <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:22,fontWeight:600,marginTop:6,color:T.danger}}>{enroll.filter(e=>e.status==="bounced").length}</div>
        </div>}
      </div>
      {/* Lead-Liste liegt auf einem eigenen Bildschirm, damit es hier übersichtlich bleibt */}
      {enrollFehler&&<div style={{background:`${T.danger}14`,border:`1px solid ${T.danger}55`,color:T.danger,borderRadius:9,padding:"10px 14px",fontSize:13,marginBottom:10}}>{enrollFehler}</div>}
      {dupInfo&&dupInfo.doppelt>0&&<div style={{background:`${T.warning||"#e6a33a"}14`,border:`1px solid ${(T.warning||"#e6a33a")}66`,borderRadius:10,padding:"11px 15px",fontSize:13,marginBottom:12,lineHeight:1.6}}>
        <b>{dupInfo.doppelt} doppelte {dupInfo.doppelt===1?"Adresse":"Adressen"}</b> in dieser Kampagne. Öffne die Lead-Übersicht, um sie zu entfernen.
      </div>}
      {enroll.length===0
        ? <div style={{color:T.textDim,fontSize:13,padding:"6px 0"}}>Noch keine Leads. Füg welche hinzu, um die Sequenz zu füllen.</div>
        : <Button variant="secondary" onClick={()=>setScreen('leads')} icon={<Icon name="leads" size={16}/>}>Alle {enroll.length} Leads ansehen und verwalten</Button>}
    </div>

    <EmailEnrollModal open={showEnroll} onClose={()=>setShowEnroll(false)} campaignId={sel} onDone={reloadDetail}/>
  </div>);
};


export default function App(){
  const [user,setUser]=useState(null);
  const [page,setPage]=useState(null);
  const [sidebarOpen,setSidebarOpen]=useState(false);
  const [loading,setLoading]=useState(true);
  const [dataVersion,setDataVersion]=useState(0);
  const [clockInPrompt,setClockInPrompt]=useState(false); // Einstempel-Vorschlag nach Login
  const [attendanceOn,setAttendanceOn]=useState(_attendanceEnabled); // Feature-Toggle Zeiterfassung
  const [leadsBlocked,setLeadsBlocked]=useState(_leadsBlocked); // Lead-Sperre (ausscheidende Mitarbeiter)
  const [newLeads,setNewLeads]=useState([]); // neue Landingpage-Leads für Admin-Popup
  const [mailToasts,setMailToasts]=useState([]); // Meldungen bei neuen E-Mail-Antworten

  // Holt im Hintergrund neue Antworten und meldet sie. Alle 60 Sekunden, damit
  // der Mailserver nicht überrannt wird. Pausiert, wenn der Tab im Hintergrund liegt.
  useEffect(()=>{
    if(!user || user.role==="cutter") return;
    let laeuft=false, gestoppt=false;
    const tick=async()=>{
      if(gestoppt||laeuft||document.hidden) return;
      laeuft=true;
      try{
        const r=await api.post('email-fetch-replies',{});
        if(r&&!r.error&&Array.isArray(r.neue)&&r.neue.length){
          const neu=r.neue.slice(0,3).map((n,i)=>({id:Date.now()+"-"+i,von:n.von,betreff:n.betreff}));
          setMailToasts(t=>[...neu,...t].slice(0,3));
          rfPling();
          setDataVersion(v=>v+1);
          neu.forEach(n=>setTimeout(()=>setMailToasts(t=>t.filter(x=>x.id!==n.id)), 9000));
        }
      }catch(e){}
      laeuft=false;
    };
    const ersteRunde=setTimeout(tick, 4000);
    const iv=setInterval(tick, 60000);
    const beiSichtbar=()=>{ if(!document.hidden) tick(); };
    document.addEventListener("visibilitychange", beiSichtbar);
    return ()=>{ gestoppt=true; clearTimeout(ersteRunde); clearInterval(iv); document.removeEventListener("visibilitychange", beiSichtbar); };
  },[user]);

  // Meldet, wenn sich jemand aus dem Team einstempelt. Laeuft fuer alle Rollen.
  useEffect(()=>{
    if(!user) return;
    let stand=null, laeuft=false, gestoppt=false;
    const tick=async()=>{
      if(gestoppt||laeuft||document.hidden) return;
      laeuft=true;
      try{
        const r=await api.get('attendance-neu', stand?`seit=${encodeURIComponent(stand)}`:'');
        if(r&&!r.error){
          if(Array.isArray(r.neu)&&r.neu.length){
            const neu=r.neu.slice(0,3).map((n,i)=>({id:"team-"+Date.now()+"-"+i, art:"team", von:n.name, betreff:"hat sich eingestempelt"}));
            setMailToasts(t=>[...neu,...t].slice(0,4));
            rfPlingTeam();
            neu.forEach(n=>setTimeout(()=>setMailToasts(t=>t.filter(x=>x.id!==n.id)), 8000));
          }
          if(r.jetzt) stand=r.jetzt;
        }
      }catch(e){}
      laeuft=false;
    };
    const start=setTimeout(tick, 1500);
    const iv=setInterval(tick, 30000);
    const beiSichtbar=()=>{ if(!document.hidden) tick(); };
    document.addEventListener("visibilitychange", beiSichtbar);
    return ()=>{ gestoppt=true; clearTimeout(start); clearInterval(iv); document.removeEventListener("visibilitychange", beiSichtbar); };
  },[user]);
  const [showLeadPopup,setShowLeadPopup]=useState(false);

  // Zahlenfelder dürfen sich beim Scrollen mit dem Mausrad NICHT verstellen.
  // Wenn ein number-Feld den Fokus hat und gescrollt wird, melden wir es kurz ab,
  // damit nur die Seite scrollt und der Wert unangetastet bleibt.
  useEffect(()=>{
    const onWheel = (e) => {
      const el = document.activeElement;
      if(el && el.tagName === "INPUT" && el.type === "number" && el === e.target){
        el.blur();
      }
    };
    document.addEventListener("wheel", onWheel, {passive:true});
    return ()=>document.removeEventListener("wheel", onWheel);
  },[]);

  // Reload data from API - call this after any write operation
  const reload = async (key) => {
    if(key) await refreshData(key);
    else await loadAllData();
    setDataVersion(v=>v+1); // trigger re-render
  };

  // Initial load
  useEffect(()=>{
    const init = async () => {
      const s = session.get();
      // Ohne gültiges Token gibt es keinen automatischen Wiedereinstieg mehr (echte Anmeldung nötig)
      if(s?.userId && authToken.get()) {
        await loadAllData();
        const users = store.get(STORAGE_KEYS.users)||[];
        const u = users.find(x=>x.id===s.userId);
        if(u){
          setUser(u);
          // Persönliches Zeiterfassungs-Setting laden
          const attOn = await loadAttendanceSetting(u);
          _attendanceEnabled = attOn; setAttendanceOn(attOn);
          // Lead-Sperre laden
          const lb = await loadLeadsBlocked(u);
          _leadsBlocked = lb; setLeadsBlocked(lb);
          // Farben und Modus dieses Nutzers
          loadDarstellung(u);
        }
      }
      setLoading(false);
    };
    init();
  },[]);

  // Auto-refresh every 30 seconds when logged in
  useEffect(()=>{
    if(!user) return;
    const iv = setInterval(()=>reload(), 30000);
    return ()=>clearInterval(iv);
  },[user]);

  // Neue Landingpage-Leads prüfen (nur Admins) — beim Login + alle 45s
  useEffect(()=>{
    if(!user || user.role!=="admin") return;
    const check = async () => {
      try {
        const nl = await api.get("landingpage-leads-new");
        if(Array.isArray(nl) && nl.length>0){ setNewLeads(nl); setShowLeadPopup(true); }
      } catch(e){}
    };
    check();
    const iv = setInterval(check, 45000);
    return ()=>clearInterval(iv);
  },[user]);

  const dismissLeadPopup = async () => {
    setShowLeadPopup(false);
    try { await api.post("landingpage-leads-seen", {ids:newLeads.map(l=>l.id)}); } catch(e){}
    setNewLeads([]);
  };

  useEffect(()=>{if(user&&!page){setPage(user.role==="admin"?"dashboard":user.role==="cutter"?"cutter-dashboard":"member-dashboard");}},[user,page]);

  const logout=()=>{ try{ api.post("logout",{}); }catch(e){} authToken.clear(); session.clear(); setUser(null); setPage(null); setSidebarOpen(false); };
  // Bei abgelaufener Session (401) sauber zum Login zurück
  useEffect(()=>{ _onAuthLost = ()=>{ authToken.clear(); session.clear(); setUser(null); setPage(null); setSidebarOpen(false); }; return ()=>{ _onAuthLost = null; }; },[]);

  const handleLogin = async (u) => { 
    session.set({userId:u.id}); 
    await loadAllData(); 
    setUser(u); 
    // Persönliches Zeiterfassungs-Setting laden (Vertriebler immer an)
    const attOn = await loadAttendanceSetting(u);
    _attendanceEnabled = attOn; setAttendanceOn(attOn);
    // Lead-Sperre laden
    const lb = await loadLeadsBlocked(u);
    _leadsBlocked = lb; setLeadsBlocked(lb);
    // Prüfen ob heute schon eingestempelt — wenn nicht, Vorschlag zeigen (nur wenn Feature an)
    // Kunden ("client") sind hier komplett ausgeschlossen.
    if(attOn && u.role !== "client"){
      try {
        const att = await api.get("attendance", `userId=${u.id}`);
        if(att && att.status === "offline" && !att.clockOut) setClockInPrompt({userId:u.id, name:u.name});
      } catch(e){}
    }
  };

  // Einstempeln aus dem Prompt — nimmt die userId direkt aus dem Prompt-State (nicht aus user, das könnte stale sein)
  const confirmClockIn = async () => {
    const uid = (clockInPrompt && clockInPrompt.userId) || user?.id;
    if(!uid){ setClockInPrompt(false); return; }
    try {
      const res = await api.post("attendance", {userId:uid, action:"clock_in"});
      if(res && res.error){ alert("Einstempeln fehlgeschlagen: "+res.error); return; }
      // Widget aktualisieren: globaler Event den useAttendance abhört
      window.dispatchEvent(new CustomEvent("attendance-changed", {detail:{userId:uid}}));
      setClockInPrompt(false);
    } catch(e){
      alert("Einstempeln fehlgeschlagen: "+(e?.message||e));
    }
  };

  if(loading) return (<div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:T.bg}}>
    <div style={{textAlign:"center"}}><img src="/logo.png" alt="R&F Media" style={{height:50,marginBottom:16,opacity:0.8,filter:T.logoFilter}}/><div style={{color:T.textDim,fontSize:14}}>Lade CRM...</div></div>
  </div>);

  if(!user) return <LoginScreen onLogin={handleLogin}/>;

  // Kunden bekommen einen komplett eigenen Bereich (kein CRM, keine Sidebar)
  if(user.role === "client") return <ClientPortal user={user} onLogout={logout}/>;

  const pages={dashboard:<Dashboard user={user} reload={reload} dv={dataVersion}/>,"member-dashboard":<MemberDashboard user={user} reload={reload} dv={dataVersion}/>,"cutter-dashboard":<CutterDashboard user={user} reload={reload} dv={dataVersion}/>,"cutter-projects":<CutterProjectsView user={user} reload={reload} dv={dataVersion}/>,"cutter-invoices":<CutterInvoicesView user={user} reload={reload} dv={dataVersion}/>,"cutter-goals":<CutterGoalsView user={user} reload={reload} dv={dataVersion}/>,leads:(leadsBlocked&&user.role!=="admin")?<div style={{maxWidth:520,margin:"60px auto",textAlign:"center"}}><div style={{width:60,height:60,borderRadius:12,background:T.danger+"22",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 20px",color:T.danger}}><svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></div><h2 style={{fontSize:20,fontWeight:700,marginBottom:8}}>Kein Zugriff auf Leads</h2><p style={{color:T.textMuted,fontSize:14,lineHeight:1.6}}>Der Zugriff auf die Leads wurde für deinen Account deaktiviert. Wende dich an die Geschäftsführung, wenn du denkst, dass das ein Fehler ist.</p></div>:<LeadsPage user={user} reload={reload} dv={dataVersion}/>,calls:<CallsPage user={user} reload={reload} dv={dataVersion}/>,appointments:<AppointmentsPage user={user} reload={reload} dv={dataVersion}/>,whiteboard:<WhiteboardPage user={user}/>,email:<EmailCampaignsPage user={user} reload={reload} dv={dataVersion}/>,cutter:<CutterPage reload={reload} dv={dataVersion}/>,finances:<FinancesPage reload={reload} dv={dataVersion}/>,sevdesk:<SevDeskPage/>,checklist:<ChecklistPage reload={reload} dv={dataVersion}/>,payroll:<PayrollPage user={user}/>,team:<TeamPage user={user} reload={reload} dv={dataVersion}/>,clients:<ClientsPage user={user} reload={reload} dv={dataVersion}/>,services:<ServiceCatalogPage user={user} reload={reload} dv={dataVersion}/>,settings:<SettingsPage user={user} onAttendanceChange={(v)=>setAttendanceOn(v)}/>};
  return (<>
    <MailToasts items={mailToasts} onOpen={t=>{ if(t&&t.art==="team")return; setPage("email"); setMailToasts(x=>x.filter(y=>y.art==="team")); }} onClose={id=>setMailToasts(t=>t.filter(x=>x.id!==id))}/>
    {/* Nachrichtenfenster: Preisvorschlaege, von ueberall erreichbar */}
    {user&&(user.role==="admin"||user.role==="cutter")&&<NachrichtenFenster user={user}/>}
    {/* Umgebungslicht als Ebene hinter allem, damit Glas wirken kann */}
    <div aria-hidden="true" style={{position:"fixed",inset:0,zIndex:0,background:T.bg,pointerEvents:"none"}}/>
    <div aria-hidden="true" style={{position:"fixed",inset:0,zIndex:0,background:T.amb,pointerEvents:"none"}}/>
    <div className="mobile-header" style={{display:"none",position:"fixed",top:0,left:0,right:0,height:"calc(56px + env(safe-area-inset-top))",boxSizing:"border-box",background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,borderBottom:`1px solid ${T.glassBorder}`,zIndex:90,alignItems:"center",justifyContent:"space-between",padding:"env(safe-area-inset-top) 16px 0 16px"}}>
      <button onClick={()=>setSidebarOpen(!sidebarOpen)} style={{background:"none",border:"none",color:T.text,cursor:"pointer",padding:4}}><Icon name="menu" size={24}/></button>
      <img src="/logo.png" alt="R&F Media" style={{height:28,opacity:0.95,filter:T.logoFilter}}/>
      <div style={{width:32}}/>
    </div>
    <Sidebar currentPage={page} setPage={setPage} user={user} onLogout={logout} isOpen={sidebarOpen} setIsOpen={setSidebarOpen} onUserUpdate={u=>setUser(u)} attendanceOn={attendanceOn} leadsBlocked={leadsBlocked}/>
    {/* Admin-Benachrichtigung: neue Landingpage-Leads (kurz, ohne Details) */}
    {user && user.role==="admin" && showLeadPopup && newLeads.length>0 && <div style={{position:"fixed",bottom:24,right:24,zIndex:9998,width:340,maxWidth:"calc(100vw - 48px)",background:T.bgCard,border:`1px solid ${T.accent}55`,borderRadius:12,boxShadow:T.shadowLg,overflow:"hidden"}}>
      <div style={{background:T.accent,padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,color:"#fff"}}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/></svg>
          <span style={{fontSize:14,fontWeight:700}}>{newLeads.length} neue{newLeads.length===1?"r":""} Lead{newLeads.length===1?"":"s"}</span>
        </div>
        <button onClick={dismissLeadPopup} style={{background:"none",border:"none",color:"#fff",cursor:"pointer",padding:2,opacity:0.85}}><Icon name="close" size={18}/></button>
      </div>
      <div style={{maxHeight:280,overflowY:"auto"}}>
        {newLeads.slice(0,8).map(l=>(
          <div key={l.id} style={{padding:"11px 16px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:34,height:34,borderRadius:9,background:T.accentGlow,display:"flex",alignItems:"center",justifyContent:"center",color:T.accent,flexShrink:0,fontSize:13,fontWeight:700}}>{(l.firstName||"?").charAt(0).toUpperCase()}</div>
            <div style={{minWidth:0,flex:1}}>
              <div style={{fontSize:13,fontWeight:600}}>{l.firstName||"Neue Anfrage"}{l.source?` · ${l.source}`:""}</div>
              <div style={{fontSize:12,color:T.textMuted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.client||"Kunde"}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{padding:"10px 16px",borderTop:`1px solid ${T.border}`}}>
        <button onClick={()=>{setPage("clients");dismissLeadPopup();}} style={{width:"100%",padding:"9px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:9,color:T.text,fontSize:13,fontWeight:600,cursor:"pointer"}}>Zu den Kunden</button>
      </div>
    </div>}
    {attendanceOn && clockInPrompt && user && user.role !== "client" && clockInPrompt.userId === user.id && <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:12,padding:28,maxWidth:380,width:"100%",textAlign:"center",boxShadow:T.shadowLg}}>
        <div style={{display:"flex",justifyContent:"center",marginBottom:14}}>
          <div style={{width:52,height:52,borderRadius:"50%",background:T.successGlow,display:"flex",alignItems:"center",justifyContent:"center",color:T.success}}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>
          </div>
        </div>
        <div style={{fontSize:18,fontWeight:700,marginBottom:8,letterSpacing:"-0.01em"}}>Guten Morgen, {(user.name||"").split(" ")[0]}!</div>
        <div style={{fontSize:14,color:T.textMuted,marginBottom:20,lineHeight:1.5}}>Möchtest du jetzt deine Arbeitszeit starten? Der Timer läuft dann bis zum Feierabend.</div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={()=>setClockInPrompt(false)} style={{flex:1,padding:"12px",background:T.bgInput,border:`1px solid ${T.border}`,borderRadius:10,color:T.textMuted,fontSize:14,fontWeight:600,cursor:"pointer"}}>Später</button>
          <button onClick={confirmClockIn} style={{flex:2,padding:"12px",background:T.success,border:"none",borderRadius:10,color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6}}><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg> Einstempeln</button>
        </div>
      </div>
    </div>}
    <main className="main-content" style={{marginLeft:240,padding:"32px 36px",minHeight:"100vh",paddingTop:32,background:"transparent",position:"relative",zIndex:1,display:"flex",flexDirection:"column"}}>
      <div style={{paddingTop:0,flex:1}}><ErrorBoundary key={page}>{pages[page]||pages["dashboard"]}</ErrorBoundary></div>
      <footer style={{marginTop:40,paddingTop:20,borderTop:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <span style={{fontSize:11,color:T.textDim}}>R&F Media UG</span>
        <LegalLinks/>
      </footer>
    </main>
    <style>{`@media(max-width:1024px){.main-content{padding-top:calc(72px + env(safe-area-inset-top))!important;padding-bottom:calc(96px + env(safe-area-inset-bottom))!important;}.mobile-botnav{display:flex!important;}}`}</style>
    {/* Mobile Glas-Navigationsleiste unten */}
    <nav className="mobile-botnav" style={{display:"none",position:"fixed",bottom:0,left:0,right:0,zIndex:95,justifyContent:"space-around",alignItems:"center",padding:"9px 6px calc(16px + env(safe-area-inset-bottom))",background:T.glassBg,backdropFilter:T.glassBlur,WebkitBackdropFilter:T.glassBlur,borderTop:`1px solid ${T.glassBorder}`,boxShadow:T.glassHi}}>
      {[{id:"dashboard",label:"Start",icon:"dashboard"},...(user.role!=="cutter"?[{id:"leads",label:"Leads",icon:"leads"},{id:"calls",label:"Anrufe",icon:"phone"}]:[]),{id:"appointments",label:"Termine",icon:"calendar"}].map(it=>(
        <button key={it.id} onClick={()=>{setPage(it.id);setSidebarOpen(false);}} style={{background:"none",border:"none",display:"flex",flexDirection:"column",alignItems:"center",gap:3,color:page===it.id?T.accent:T.textDim,fontSize:10,fontWeight:500,cursor:"pointer",flex:1,padding:0}}><Icon name={it.icon} size={21}/>{it.label}</button>
      ))}
      {user.role!=="cutter"
        ? <button onClick={()=>{setPage("email");setSidebarOpen(false);}} style={{background:"none",border:"none",display:"flex",flexDirection:"column",alignItems:"center",gap:3,color:page==="email"?T.accent:T.textDim,fontSize:10,fontWeight:500,cursor:"pointer",flex:1,padding:0}}><Icon name="mail" size={21}/>E-Mail</button>
        : <button onClick={()=>setSidebarOpen(true)} style={{background:"none",border:"none",display:"flex",flexDirection:"column",alignItems:"center",gap:3,color:T.textDim,fontSize:10,fontWeight:500,cursor:"pointer",flex:1,padding:0}}><Icon name="menu" size={21}/>Mehr</button>}
    </nav>
  </>);
}
